import { createContext, useContext } from "react";

export const BankLangCtx = createContext({ t: (k) => k });
export const useLang = () => useContext(BankLangCtx);

/* ─────────────────────────────────────────────────────────────────────────── */
/* TRANSLATIONS                                                                */
/* ─────────────────────────────────────────────────────────────────────────── */
const T = {
  fr: {
    "notif.transfer_code.title": "Code de validation virement",
    "notif.transfer_code.body": "Copiez ce code pour valider votre virement.",
    "notif.transfer_code.expires_in": "Disparaît dans {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mois",
    "credits.n_months": "{n} mois",
    "msg.read": "Lu",
    "msg.sent": "Envoyé",
    "msg.attach_aria": "Joindre un fichier",
    "login.device.unknown": "Navigateur inconnu",
    "login.device.mobile": "Mobile",
    "login.device.tablet": "Tablette",
    "login.device.desktop": "Ordinateur",
    "login.recent": "Récent",
    "err.invalid_credentials": "Identifiants invalides",
    "err.bad_pin": "Code PIN incorrect",
    "err.account_blocked": "Compte bloqué",
    "err.bad_current_pw": "Mot de passe actuel incorrect",
    "err.insufficient_funds": "Fonds insuffisants, veuillez créditer votre compte.",
    "err.amount_above_savings": "Montant supérieur à l'épargne disponible",
    "err.must_be_positive": "La valeur doit être positive",
    "err.invalid_format": "Format invalide",
    "err.email_taken": "Email déjà utilisé",
    "err.card_not_found": "Carte introuvable",
    "err.physical_card_already_ordered": "Carte physique déjà commandée",
    "err.beneficiary_not_found": "Bénéficiaire introuvable",
    "err.invalid_code": "Code invalide",
    "err.no_transfer_code": "Aucun code pour ce virement",
    "err.link_expired": "Lien invalide ou expiré",
    "err.thread_not_found": "Conversation introuvable",
    "err.empty_message": "Message vide",
    "err.file_too_large": "Fichier trop volumineux (max 20 Mo)",
    "err.forbidden": "Accès interdit",
    "err.bank_not_found": "Banque introuvable",
    "err.signups_closed": "Les inscriptions sont fermées pour cette banque.",
    "err.access_suspended": "Accès temporairement suspendu. Veuillez contacter votre banque.",
    "err.notif_not_closable": "Cette notification ne peut pas être fermée.",
    /* ── Auth ── */
    "auth.signup.tagline": "Rejoignez-nous aujourd'hui",
    "auth.login.tagline": "Bienvenue !",
    "auth.signup.tagline_sub": "Ouvrez votre compte en 2 minutes et accédez à tous nos services bancaires premium.",
    "auth.login.tagline_sub": "Accédez à votre espace personnel et gérez vos finances en toute sécurité.",
    "auth.back": "Retour à l'accueil",
    "auth.bullet.security": "Sécurité bancaire de niveau militaire",
    "auth.bullet.transfers": "Virements instantanés 24h/24",
    "auth.bullet.investments": "Investissements et épargne intégrés",
    "auth.bullet.advisor": "Conseiller personnel disponible 7j/7",
    "auth.footer": "Propulsé par {bank}",
    "auth.signup.title": "Créer un compte",
    "auth.login.title": "Connexion",
    "auth.field.lastName": "Nom",
    "auth.field.firstName": "Prénoms",
    "auth.field.country": "Pays",
    "auth.field.country.placeholder": "— Sélectionner votre pays —",
    "auth.field.phone": "Téléphone",
    "auth.field.address": "Adresse",
    "auth.field.address.placeholder": "123 Rue de la Paix, Paris",
    "auth.field.email": "Email",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Mot de passe",
    "auth.submit.signup": "Créer mon compte",
    "auth.submit.login": "Se connecter",
    "auth.login.badge": "Connectez-vous à votre compte",
    "auth.card.tagline": "Votre espace client",
    "auth.submit.loading": "Chargement…",
    "auth.pin.title": "Code PIN",
    "auth.pin.subtitle": "Entrez votre Code PIN à 4 chiffres",
    "auth.pin.label": "Code PIN",
    "auth.pin.confirm": "Confirmer le Code PIN",
    "auth.pin.submit": "Confirmer",
    "auth.pin.back": "Retour à la connexion",
    "auth.pin.length_error": "Le Code PIN doit contenir 4 chiffres",
    "auth.switch.have_account": "Déjà un compte ?",
    "auth.switch.login_link": "Connexion",
    "auth.switch.no_account": "Pas encore client ?",
    "auth.error.select_country": "Sélectionnez votre pays",
    /* ── KYC ── */
    "kyc.title": "Vérification d'identité (KYC)",
    "kyc.sub": "Téléversez vos documents pour valider votre compte. Tous les documents sont chiffrés et confidentiels.",
    "kyc.id_type": "Type de pièce d'identité *",
    "kyc.id_card": "Carte d'identité",
    "kyc.passport": "Passeport",
    "kyc.driver_license": "Permis de conduire",
    "kyc.recto_passport": "Recto du passeport *",
    "kyc.recto_id": "Recto de la pièce *",
    "kyc.verso": "Verso de la pièce *",
    "kyc.selfie": "Selfie avec la pièce d'identité en main *",
    "kyc.address_proof": "Justificatif de domicile *",
    "kyc.hint_doc": "Image ou PDF, max 5 Mo",
    "kyc.hint_selfie": "Photo claire, visage et document visibles",
    "kyc.hint_address": "Facture, relevé, attestation… (max 5 Mo)",
    "kyc.upload": "Téléverser",
    "kyc.replace": "Remplacer",
    /* ── Pending ── */
    "pending.title": "Compte en attente",
    "pending.body": "Votre demande a bien été reçue. Un administrateur va vérifier votre dossier et activer votre compte sous peu.",
    "pending.back": "Retour à la connexion",
    /* ── Home (landing) ── */
    "home.nav.features": "Fonctionnalités",
    "home.nav.security": "Sécurité",
    "home.nav.invest": "Investissement",
    "home.nav.credits": "Crédits",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Se connecter",
    "home.hero.tagline": "Banque en ligne nouvelle génération",
    "home.hero.title1": "Gérez votre argent",
    "home.hero.title2": "avec intelligence",
    "home.hero.sub": "Une plateforme bancaire complète — virements, épargne, investissements et crédits depuis votre smartphone.",
    "home.hero.login": "Se connecter",
    "home.hero.trust.secure": "100% Sécurisé",
    "home.hero.trust.fast": "Ouverture rapide",
    "home.hero.trust.encrypted": "Données chiffrées",
    "home.hero.trust.support": "Service 24/7",
    "home.mockup.balance": "Solde disponible",
    "home.mockup.currency": "EUR · Compte principal",
    "home.mockup.recharge": "Recharger",
    "home.mockup.send": "Envoyer",
    "home.mockup.save": "Épargner",
    "home.mockup.salary": "Salaire Mars",
    "home.mockup.rent": "Loyer Avril",
    "home.mockup.investment": "Investissement",
    "home.mockup.days2": "il y a 2 jours",
    "home.mockup.days3": "il y a 3 jours",
    "home.mockup.days5": "il y a 5 jours",
    "home.stats.accounts": "Comptes actifs",
    "home.stats.uptime": "Disponibilité",
    "home.stats.transfers": "Virements",
    "home.stats.support": "Support",
    "home.features.label": "Fonctionnalités",
    "home.features.title": "Tout ce dont vous avez besoin",
    "home.features.sub": "Une plateforme complète conçue pour simplifier la gestion de vos finances au quotidien.",
    "home.feature.security": "Sécurité militaire",
    "home.feature.security_body": "Chiffrement AES-256, authentification double facteur et surveillance 24/7 de votre compte.",
    "home.feature.cards": "Cartes virtuelle & physique",
    "home.feature.cards_body": "Émettez des cartes en quelques secondes ou commandez une vraie carte physique livrée chez vous.",
    "home.feature.transfers": "Virements express",
    "home.feature.transfers_body": "Envoyez et recevez de l'argent partout dans le monde en quelques secondes.",
    "home.feature.analytics": "Analyse en temps réel",
    "home.feature.analytics_body": "Tableaux de bord intelligents pour suivre vos dépenses et anticiper vos finances.",
    "home.feature.investments": "Investissements intégrés",
    "home.feature.investments_body": "Accédez à des produits d'investissement sélectionnés directement depuis votre compte.",
    "home.feature.support": "Support dédié 24/7",
    "home.feature.support_body": "Un conseiller bancaire personnel disponible à tout moment via messagerie sécurisée.",
    "home.tpl.label": "Confiance",
    "home.tpl.title": "Ils nous font confiance",
    "home.mockup.balance_label": "Solde",
    "home.mockup.virement": "Virement",
    "home.mockup.salaire": "Salaire",
    "home.security.label": "Sécurité",
    "home.security.title1": "Vos finances",
    "home.security.title2": "en sécurité absolue",
    "home.security.sub": "Nous appliquons les standards de sécurité bancaire les plus stricts pour protéger chaque centime.",
    "home.security.aes.title": "Chiffrement AES-256",
    "home.security.aes.body": "Toutes les données sensibles sont chiffrées avec les mêmes algorithmes utilisés par les grandes banques internationales.",
    "home.security.auth.title": "Authentification renforcée",
    "home.security.auth.body": "Double vérification d'identité à chaque connexion et pour chaque opération sensible sur votre compte.",
    "home.security.compliance.title": "Conformité DSP2/RGPD",
    "home.security.compliance.body": "Nous respectons toutes les réglementations européennes en matière de services de paiement et de protection des données.",
    "home.invest.label": "Investissements",
    "home.invest.title": "Faites fructifier votre argent",
    "home.invest.sub": "6 catégories d'investissement sélectionnées pour leur potentiel et leur fiabilité.",
    "home.invest.yield": "Rendement",
    "home.credits.label": "Crédits",
    "home.credits.title": "Des solutions adaptées à chaque projet",
    "home.credits.sub": "Des taux compétitifs, des réponses rapides et un accompagnement personnalisé pour tous vos projets.",
    "home.credits.rate": "Taux",
    "home.faq.label": "FAQ",
    "home.faq.title": "Questions fréquentes",
    "home.faq.q1": "Comment ouvrir un compte ?",
    "home.faq.a1": "L'inscription prend moins de 2 minutes. Il vous suffit de renseigner vos informations personnelles, choisir un mot de passe et votre compte sera activé après validation par notre équipe.",
    "home.faq.q2": "Mes données sont-elles sécurisées ?",
    "home.faq.a2": "Oui. Toutes les données sont chiffrées avec AES-256 au repos et TLS 1.3 en transit. Nous respectons les normes bancaires internationales et les réglementations RGPD/DSP2.",
    "home.faq.q3": "Quels types d'investissements proposez-vous ?",
    "home.faq.a3": "Nous proposons 6 catégories : Immobilier, Crowdfunding, Énergies renouvelables, Bourse, Participatif et Crypto. Chaque produit est sélectionné pour son rapport risque/rendement.",
    "home.faq.q4": "Comment demander un crédit ?",
    "home.faq.a4": "Rendez-vous dans la section Crédit de votre espace client, choisissez le type de prêt, renseignez le montant et la durée. Votre dossier est traité sous 24h par nos conseillers.",
    "home.faq.q5": "Y a-t-il des frais cachés ?",
    "home.faq.a5": "Aucun frais caché. Tous les tarifs sont affichés clairement lors de chaque opération. Les conditions sont accessibles dans votre espace client à tout moment.",
    "home.faq.q6": "Comment contacter le support ?",
    "home.faq.a6": "Utilisez la messagerie intégrée dans votre espace client pour contacter directement votre conseiller bancaire personnel, disponible 24h/24 et 7j/7.",
    "home.cta.title": "Prêt à commencer ?",
    "home.cta.sub": "Rejoignez des milliers d'utilisateurs qui gèrent leurs finances avec {bank}. L'inscription prend moins de 2 minutes.",
    "home.footer.products": "Produits",
    "home.footer.services": "Services",
    "home.footer.access": "Accès rapide",
    "home.footer.accounts": "Comptes",
    "home.footer.transfers": "Virements",
    "home.footer.cards": "Cartes",
    "home.footer.savings": "Épargne",
    "home.footer.investments": "Investissements",
    "home.footer.credits": "Crédits",
    "home.footer.financing": "Financement",
    "home.footer.messages": "Messages",
    "home.footer.login": "Connexion",
    "home.footer.signup": "Ouvrir un compte",
    "home.footer.support": "Support 24/7",
    "home.footer.tagline": "Votre banque en ligne, disponible partout et à tout moment.",
    "home.footer.copyright": "© {year} {bank} — Tous droits réservés",
    "home.footer.powered": "Propulsé par {bank}",
    /* ── App shell ── */
    "app.menu_label": "Menu principal",
    "app.nav.dashboard": "Tableau de bord",
    "app.nav.accounts": "Comptes",
    "app.nav.transactions": "Transactions",
    "app.nav.transfers": "Virements",
    "app.nav.cards": "Cartes",
    "app.nav.savings": "Épargne",
    "app.nav.investments": "Investissement",
    "app.nav.credit": "Crédit",
    "app.nav.financing": "Financement",
    "app.nav.messages": "Messages",
    "app.nav.card_tracking": "Suivi de carte",
    "app.nav.notifications": "Notifications",
    "app.nav.settings": "Paramètres",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Accueil",
    "app.nav.mobile.transfers": "Virements",
    "app.nav.mobile.cards": "Cartes",
    "app.nav.mobile.transactions": "Transactions",
    "app.nav.more.invest": "Investir",
    "app.user_role": "Client",
    "app.logout": "Déconnexion",
    "app.search": "Rechercher…",
    "app.more_title": "Plus de fonctionnalités",
    "app.more_btn": "Plus",
    "app.loading": "Chargement…",
    "app.error_back": "Retour à la connexion",
    "app.see_more_notifs": "Voir les {count} autres notifications →",
    /* ── Page headings ── */
    "page.accounts": "Mes comptes",
    "page.transactions": "Transactions",
    "page.transfers": "Nouveau virement",
    "page.deposit": "Dépôt",
    "page.cards": "Mes cartes",
    "page.savings": "Épargne",
    "page.investments": "Investissement",
    "page.credits": "Crédit",
    "page.financing": "Financement de projets",
    "page.messages": "Messages",
    "page.settings": "Paramètres",
    "page.notifications": "Notifications importantes",
    "page.profile": "Mon profil",
    /* ── Dashboard ── */
    "dash.greeting": "Bienvenue, {name}",
    "dash.overview": "Aperçu de vos finances — {date}",
    "dash.total_balance": "Solde total",
    "dash.this_month": "+{amount} ce mois",
    "dash.view_iban": "Voir mon IBAN",
    "dash.my_accounts": "Mes comptes",
    "dash.primary_account": "Compte principal",
    "dash.savings_account": "Compte épargne",
    "dash.remunerated": "Rémunéré",
    "dash.details": "Détails",
    "dash.transfer_btn": "Virement",
    "dash.quick_actions": "Actions rapides",
    "dash.quick.deposit": "Dépôt",
    "deposit.title": "Informations de dépôt",
    "dash.quick.transfer": "Virement",
    "dash.quick.history": "Historique",
    "dash.quick.cards": "Mes cartes",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Budget mensuel",
    "dash.income": "Entrées",
    "dash.expenses": "Dépenses",
    "dash.spending_cat": "Dépenses par catégorie",
    "dash.no_spending": "Aucune dépense enregistrée.",
    "dash.recent_tx": "Transactions récentes",
    "dash.see_all": "Voir tout",
    "dash.no_tx": "Aucune opération.",
    "dash.main_card": "Ma carte principale",
    "dash.manage": "Gérer",
    "dash.block": "Bloquer",
    "dash.pin": "Code PIN",
    "dash.security": "Sécurité",
    "dash.more": "Plus",
    "dash.no_card": "Aucune carte émise",
    "dash.monthly_activity": "Activité du mois",
    "dash.operations": "Opérations",
    "dash.holder": "Titulaire",
    "dash.expires": "Exp.",
    "dash.visa_label": "VISA",
    "dash.balance": "Solde disponible",
    /* ── Transactions ── */
    "tx.all": "Toutes",
    "tx.income": "Entrées",
    "tx.expenses": "Sorties",
    "tx.today": "Aujourd'hui",
    "tx.yesterday": "Hier",
    "tx.none": "Aucune transaction.",
    /* ── Transfers ── */
    "transfer.title": "Virement rapide",
    "transfer.recipient": "Destinataire",
    "transfer.recipient_ph": "Nom, IBAN, numéro de téléphone…",
    "transfer.amount": "Montant",
    "transfer.label": "Libellé",
    "transfer.reason": "Motif (optionnel)",
    "transfer.reason_ph": "Ex : loyer, remboursement…",
    "transfer.execution": "Exécution",
    "transfer.immediate": "Immédiat",
    "transfer.delayed": "Différé",
    "transfer.balance": "Solde disponible :",
    "transfer.continue": "Continuer",
    "transfer.confirm": "Confirmer",
    "transfer.cancel": "Annuler",
    "transfer.code": "Code de validation",
    "transfer.code.received": "Votre code de validation",
    "transfer.code.copy": "Copier",
    "transfer.code.copied": "Copié",
    "common.loading": "Chargement…",
    "common.search": "Rechercher",
    "common.other": "Autre",
    "error.bank_not_found": "Banque introuvable",
    "error.back_home": "Retour à l'accueil",
    "error.unknown": "Erreur inconnue",
    "kyc.file_too_large": "Fichier trop volumineux (max 5 Mo)",
    "kyc.file_read_error": "Lecture du fichier impossible",
    "tracking.label": "Suivi",
    "tracking.status.preparation": "En préparation",
    "tracking.status.shipped": "Expédié",
    "tracking.status.transit": "En transit",
    "tracking.status.delivered": "Livré",
    "tracking.error.not_found": "Numéro de suivi introuvable. Vérifiez et réessayez.",
    "deposit.default_instructions": "Contactez votre gestionnaire pour les modalités de dépôt.",
    "card.color.night": "Nuit",
    "card.color.slate": "Ardoise",
    "card.color.steel": "Acier",
    "card.color.royal": "Bleu royal",
    "card.color.cyan": "Cyan",
    "card.color.violet": "Violet",
    "card.color.lavender": "Lavande",
    "card.color.emerald": "Émeraude",
    "card.color.forest": "Forêt",
    "card.color.gold": "Or",
    "card.color.amber": "Ambre",
    "card.color.orange": "Orange",
    "card.color.pink": "Rose",
    "card.color.ruby": "Rubis",
    "card.color.terracotta": "Terre cuite",
    "admin.wallet.title": "Mon portefeuille",
    "admin.wallet.balance": "Solde disponible",
    "admin.wallet.topup": "Recharger",
    "admin.wallet.history": "Historique",
    "admin.wallet.no_history": "Aucune transaction",
    "demo.banner": "Mode démonstration — aucune action réelle",
    "demo.disabled": "Action non disponible en mode démo",
    "content.translated": "Contenu traduit automatiquement",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Entrez le code reçu pour valider le virement",
    "transfer.pending.title": "Virement en attente de validation",
    "transfer.pending.amount": "Montant :",
    "transfer.pending.to": "Vers :",
    "transfer.pending.hint": "Votre code de validation sera envoyé par e-mail ou via la cloche notification. Le code de validation est composé de 6 chiffres. Si vous ne le recevez pas, veuillez contacter le support.",
    "transfer.pending.validate": "Valider le virement",
    "transfer.saved": "Virement enregistré. Demande le code à ton administrateur pour valider.",
    "transfer.done": "Virement effectué",
    "transfer.validated": "Virement validé !",
    "transfer.progress.processing": "Traitement du virement…",
    "transfer.progress.completed": "Virement effectué !",
    "transfer.progress.default_message": "Votre virement est en cours de traitement. Veuillez patienter.",
    /* ── Cards ── */
    "cards.none": "Aucune carte émise.\nContactez votre administrateur.",
    "cards.holder": "Titulaire",
    "cards.expires": "Exp.",
    "cards.block": "Verrouiller",
    "cards.pin": "Code PIN",
    "cards.more": "Plus",
    "cards.limits": "Plafonds de la carte",
    "cards.payment": "Paiement",
    "cards.withdrawal": "Retrait",
        "cards.daily": "Journalier",
    "cards.weekly": "Hebdomadaire",
    "cards.monthly": "Mensuel",
    "cards.blocked_label": "Bloquée",
    "cards.status_active": "Active",
    "cards.toast.blocked": "Carte bloquée",
    "cards.toast.unblocked": "Carte débloquée",
    "cards.toast.physical_ordered": "Carte physique commandée ! Livraison sous 5–10 jours ouvrés.",
    "cards.toast.created": "Carte générée avec succès !",
    "cards.none_sub": "Aucune carte émise. Générez votre carte virtuelle dès maintenant.",
    "cards.generate_btn": "Générer ma carte",
    "cards.create.title": "Générer ma carte virtuelle",
    "cards.create.network": "Réseau de paiement",
    "cards.create.color": "Couleur de la carte",
    "cards.create.preview_name": "VOTRE NOM",
    "cards.create.preview_expiry": "MM/AA",
    "cards.create.loading": "Génération…",
    "cards.create.submit": "Générer la carte",
    "cards.btn.unblock": "Débloquer",
    "cards.btn.block": "Bloquer",
    "cards.btn.info": "Infos",
    "cards.btn.physical": "Physique",
    "cards.btn.physical_done": "Commandée",
    "cards.limits_title": "Plafonds",
    "cards.reveal.title": "Informations de la carte",
    "cards.reveal.number": "Numéro complet",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Expiration",
    "cards.reveal.warning": "Ne partagez jamais ces informations. Fermez cette fenêtre après usage.",
    "cards.reveal.pin_prompt": "Entrez votre code PIN à 4 chiffres pour afficher les informations de la carte.",
    "cards.reveal.show": "Afficher",
    "cards.pin.title": "Code PIN",
    "cards.pin.message": "Ce code PIN est utilisé pour tous vos paiements en ligne et retraits. Ne le communiquez jamais à personne.",
    "cards.physical.title": "Commander la carte physique",
    "cards.physical.fee_label": "Frais de livraison",
    "cards.physical.balance_label": "Votre solde",
    "cards.physical.insufficient": "Solde insuffisant. Veuillez recharger votre compte avant de commander.",
    "cards.physical.info": "Les frais seront débités de votre compte. Livraison sous 5–10 jours ouvrés.",
    "cards.physical.order_btn": "Commander ({fee})",
    "cards.physical.ordering": "Commande…",
    "cards.physical.order_cta": "Commander une carte physique",
    "cards.physical.choose_type": "Choisissez votre carte",
    "cards.physical.features": "Avantages",
    "cards.buy_virtual_cta": "Acheter une carte virtuelle",
    /* ── Notifications ── */
    "notif.none": "Aucune notification pour le moment.",
    "notif.contact_support": "Contacter le support",
    "notif.dismiss": "Fermer",
    "notif.locked": "Message de la banque",
    /* ── Profile ── */
    "profile.change_photo": "Changer la photo",
    "profile.remove_photo": "Retirer la photo",
    "profile.email": "Email",
    "profile.phone": "Téléphone",
    "profile.country": "Pays",
    "profile.address": "Adresse",
    "profile.notifs_title": "Notifications ({count})",
    "profile.no_notifs": "Aucune notification.",
    "profile.logout": "Déconnexion",
    "profile.photo_updated": "Photo de profil mise à jour",
    "profile.photo_removed": "Photo retirée",
    "profile.photo_too_large": "Image trop volumineuse (max 1 Mo)",
    "profile.powered": "Propulsé par {bank}",
    /* ── IBAN ── */
    "iban.title": "Mon IBAN",
    "iban.loading": "Chargement…",
    "iban.bic": "Code BIC / SWIFT",
    "iban.share_info": "Partagez ces coordonnées pour recevoir un virement sur votre compte.",
    "iban.copy": "Copier",
    "iban.copied": "Copié dans le presse-papier",
    "iban.copy_fail": "Impossible de copier",
    "iban.close": "Fermer",
    /* ── Accounts page ── */
    "accounts.all": "Tous vos comptes ouverts chez {bank}.",
    "accounts.primary": "Compte principal",
    "accounts.savings": "Compte épargne",
    "accounts.primary_tag": "Principal",
    "accounts.secondary_tag": "Secondaire",
    "accounts.objective": "Objectif : {amount} · {pct}%",
    "accounts.copy": "Copier",
    "accounts.loading": "Chargement…",
    "accounts.iban_copied": "IBAN copié",
    "accounts.copy_fail": "Impossible de copier",
    /* ── Savings ── */
    "savings.total": "Total épargné",
    "savings.target_total": "Objectif total",
    "savings.progress": "Progression",
    "savings.active": "Objectifs actifs",
    "savings.new_goal": "Nouvel objectif",
    "savings.none": "Aucun objectif d'épargne pour l'instant.",
    "savings.none_sub": "Créez votre premier objectif pour commencer.",
    "savings.saved": "Épargné :",
    "savings.goal": "Objectif :",
    "savings.deadline": "Échéance : {date}",
    "savings.deposit": "Déposer",
    "savings.withdraw": "Retirer",
    "savings.loading": "Chargement…",
    "savings.form.title": "Nouvel objectif d'épargne",
    "savings.form.cancel": "Annuler",
    "savings.form.create": "Créer",
    "savings.form.name": "Nom de l'objectif",
    "savings.form.name_ph": "Voyage Japon, Voiture, …",
    "savings.form.target": "Montant cible",
    "savings.form.initial": "Dépôt initial",
    "savings.form.deadline": "Date limite (optionnel)",
    "savings.form.color": "Couleur",
    "savings.deposit_title": "Déposer sur \"{name}\"",
    "savings.deposit_amount": "Montant à déposer",
    "savings.balance_hint": "Solde principal : {amount}",
    "savings.withdraw_prompt": "Retirer combien de \"{name}\" ?",
    "savings.close_confirm": "Clôturer l'objectif \"{name}\" ? L'épargne sera reversée sur votre compte principal.",
    "savings.toast.created": "Objectif créé",
    "savings.toast.deposited": "Dépôt effectué",
    "savings.toast.withdrawn": "Retrait effectué",
    "savings.toast.closed": "Objectif clôturé",
    /* ── Investment ── */
    "invest.total": "Total investi",
    "invest.profits": "Bénéfices cumulés",
    "invest.count": "Investissements",
    "invest.products": "Produits disponibles",
    "invest.invest_btn": "Investir",
    "invest.my": "Mes investissements",
    "invest.pending": "En attente",
    "invest.validated": "Validé",
    "invest.refused": "Refusé",
    "invest.withdraw.btn": "Récupérer",
    "invest.withdraw.title": "Récupérer mon investissement",
    "invest.withdraw.intro": "Vous êtes sur le point de récupérer votre investissement « {product} ». Le capital initial et les bénéfices accumulés seront crédités sur votre solde principal.",
    "invest.withdraw.principal": "Capital initial",
    "invest.withdraw.profit": "Bénéfices",
    "invest.withdraw.total": "Total à recevoir",
    "invest.withdraw.confirm": "Confirmer la récupération",
    "invest.withdraw.warning": "Cette action est irréversible. L'investissement sera clôturé.",
    "invest.withdraw.success": "Investissement récupéré — {amount} crédités sur votre solde.",
    "invest.withdrawn": "Récupéré",
    "invest.cta_sub": "Choisissez le type d'investissement qui vous convient. Un conseiller vous accompagnera dès votre demande.",
    "invest.form.amount": "Montant à investir",
    "invest.form.balance": "Solde disponible : {amount}",
    "invest.form.note": "Note pour votre conseiller (optionnel)",
    "invest.form.note_ph": "Précisez vos attentes…",
    "invest.form.advisor_info": "Une conversation sera automatiquement ouverte avec votre conseiller.",
    "invest.form.cancel": "Annuler",
    "invest.form.submit": "Envoyer la demande",
    "invest.toast.requested": "Demande enregistrée — une conversation a été ouverte avec votre conseiller.",
    "invest.p1.name": "Immobilier",
    "invest.p1.desc": "SCPI, pierre-papier, crowdfunding immobilier",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Financement participatif de projets sélectionnés",
    "invest.p3.name": "Énergies renouv.",
    "invest.p3.desc": "Solaire, éolien et projets verts",
    "invest.p4.name": "Bourse",
    "invest.p4.desc": "Actions, ETF et fonds diversifiés",
    "invest.p5.name": "Participatif",
    "invest.p5.desc": "Prêts aux PME et entrepreneurs locaux",
    "invest.p6.name": "Crypto",
    "invest.p6.desc": "Bitcoin, Ethereum et portefeuille multi-devises",
    /* ── Messages ── */
    "msg.online": "En ligne",
    "msg.loading": "Chargement…",
    "msg.none": "Aucun message. Démarrez la conversation avec votre conseiller.",
    "msg.placeholder": "Écrivez votre message…",
    "msg.attach.image": "Image",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Fichier / Document",
    "msg.pdf_doc": "Document PDF",
    "msg.file": "Fichier",
    "msg.too_large": "Fichier trop volumineux (max {size}).",
    "msg.withdraw": "Retirer",
    /* ── Credits ── */
    "credits.cta_sub": "Effectuez votre demande directement en ligne — réponse sous 24h.",
    "credits.learn_more": "En savoir plus",
    "credits.request": "Demander",
    "credits.history": "Mes demandes",
    "credits.status.pending": "En attente",
    "credits.status.accepted": "Accepté",
    "credits.status.refused": "Refusé",
    "credits.chars": "Caractéristiques",
    "credits.amount_range": "Montant : {min}€ – {max}€",
    "credits.rate_range": "Taux d'intérêt : {min}% – {max}% par an",
    "credits.duration_range": "Durée : {min} à {max} mois",
    "credits.deposit_min": "Dépôt initial : {amount}€ minimum",
    "credits.garantie": "Garantie : {garantie}",
    "credits.eligibility": "Conditions d'éligibilité",
    "credits.elig.income": "Justifier de revenus réguliers et stables",
    "credits.elig.deposit": "Effectuer un dépôt initial de {amount}€",
    "credits.elig.garantie": "Présenter la garantie : {garantie}",
    "credits.form.title": "Demande — {name}",
    "credits.form.cancel": "Annuler",
    "credits.form.submit": "Envoyer la demande",
    "credits.form.amount": "Montant souhaité (€)",
    "credits.form.duration": "Durée (mois)",
    "credits.form.reason": "Motif (optionnel)",
    "credits.form.reason_ph": "Précisez votre projet…",
    "credits.form.close": "Fermer",
    "credits.toast.submitted": "Demande de crédit envoyée — vous recevrez une réponse sous 24h.",
    "credits.p1.name": "Prêt Personnel",
    "credits.p1.desc": "Pour financer les besoins du quotidien ou les projets personnels.",
    "credits.p2.name": "Prêt Immobilier",
    "credits.p2.desc": "Pour financer l'achat ou la construction d'un bien immobilier.",
    "credits.p3.name": "Prêt Auto / Moto",
    "credits.p3.desc": "Achetez un véhicule en toute sérénité.",
    "credits.p4.name": "Prêt Étudiant",
    "credits.p4.desc": "Une aide financière pour les études supérieures.",
    "credits.p5.name": "Prêt Professionnel",
    "credits.p5.desc": "Pour soutenir la croissance d'une activité professionnelle.",
    "credits.p6.name": "Prêt Express",
    "credits.p6.desc": "Une solution rapide pour les besoins urgents.",
    /* ── Financing ── */
    "financing.reimbursable": "Financement remboursable",
    "financing.reimbursable_desc": "Un appui financier avec des conditions fixes pour lancer ou développer une activité.",
    "financing.non_reimbursable": "Financement non remboursable",
    "financing.non_reimbursable_desc": "Pour les projets à impact social, éducatif ou humanitaire (ONG, associations).",
    "financing.cta_sub": "Des solutions adaptées pour concrétiser vos ambitions.",
    "financing.request": "Demander un financement",
    "financing.history": "Mes demandes",
    "financing.reimbursable_label": "Remboursable",
    "financing.non_reimbursable_label": "Non remboursable",
    "financing.status.pending": "En attente",
    "financing.status.granted": "Accordé",
    "financing.status.refused": "Refusé",
    "financing.form.title": "Demande — {name}",
    "financing.form.cancel": "Annuler",
    "financing.form.submit": "Envoyer la demande",
    "financing.form.project": "Nom du projet",
    "financing.form.project_ph": "Mon projet",
    "financing.form.amount": "Montant demandé (€)",
    "financing.form.sector": "Secteur",
    "financing.form.sector_ph": "Tech, agriculture, …",
    "financing.form.description": "Description du projet",
    "financing.form.description_ph": "Présentez votre projet, ses objectifs et son impact attendu…",
    /* ── Settings ── */
    "settings.security": "Sécurité",
    "settings.current_pw": "Mot de passe actuel",
    "settings.new_pw": "Nouveau mot de passe",
    "settings.confirm_pw": "Confirmer",
    "settings.submit": "Changer le mot de passe",
    "settings.no_match": "Les mots de passe ne correspondent pas",
    "settings.changed": "Mot de passe modifié",
    /* ── Login History ── */
    "login.history": "Historique de connexions",
    "login.history.empty": "Aucune connexion enregistrée.",
    /* ── Beneficiaries ── */
    "beneficiary.saved": "Bénéficiaires enregistrés",
    "beneficiary.pick": "Sélectionner",
    "beneficiary.save_this": "Enregistrer ce bénéficiaire",
    "beneficiary.saved_ok": "Bénéficiaire enregistré",
    /* ── Credits simulation ── */
    "credits.simulation": "Simulation de mensualité",
    "credits.monthly_payment": "Mensualité estimée",
    "credits.total_cost": "Coût total estimé",
    "credits.rate_used": "Taux estimatif :",
    "credits.rate_annual": "/ an",
    /* ── Common ── */
    "common.cancel": "Annuler",
    "common.confirm": "Confirmer",
    "common.close": "Fermer",
    "common.save": "Enregistrer",
    "common.loading": "Chargement…",
    "common.send": "Envoyer",
    "common.total": "Total",
    "dash.welcome": "Bienvenue",
    "common.delete": "Supprimer",
    "cards.delete.title": "Supprimer la carte",
    "cards.delete.confirm_msg": "Cette action est irréversible. La carte sera définitivement supprimée.",
    "cards.delete.physical_tooltip": "Carte physique commandée — suppression impossible",
    "cards.order.confirmed": "Commande confirmée !",
    "cards.order.confirmed_body": "Votre carte physique est en cours de préparation. Utilisez ce numéro pour suivre votre livraison.",
    "cards.order.tracking_no": "N° de suivi",
    "cards.fee_deducted": "{amount} sera déduit de votre solde",
    "cards.insufficient_short": "Solde insuffisant ({available} disponible)",
    "transfer.section.beneficiary": "Bénéficiaire",
    "transfer.section.details": "Virement",
    "transfer.field.iban": "IBAN",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banque",
    "transfer.field.city": "Ville",
    "transfer.iban_label": "IBAN :",
    "transfer.bank_label": "Banque :",
    "transfer.progress.paused": "En attente de validation",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Immobilier",
    "home.invest.p.realestate_desc": "SCPI, pierre-papier, crowdfunding immobilier",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Financement participatif de projets sélectionnés",
    "home.invest.p.renewable": "Énergies renouv.",
    "home.invest.p.renewable_desc": "Solaire, éolien et projets verts",
    "home.invest.p.stocks": "Bourse",
    "home.invest.p.stocks_desc": "Actions, ETF et fonds diversifiés",
    "home.invest.p.participatory": "Participatif",
    "home.invest.p.participatory_desc": "Prêts aux PME et entrepreneurs locaux",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum et portefeuille multi-devises",
    "home.invest.p.variable": "Variable",
    "home.credits.p.personal": "Prêt Personnel",
    "home.credits.p.realestate": "Prêt Immobilier",
    "home.credits.p.auto": "Prêt Auto/Moto",
    "home.credits.p.student": "Prêt Étudiant",
    "home.credits.p.pro": "Prêt Professionnel",
    "home.credits.p.express": "Prêt Express",
    "home.credits.rate.from": "dès {rate}/an",
    "cards.deleted": "Carte supprimée",
    "cards.feat.gold_exclusive": "Carte dorée exclusive",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Protection acheteur",
    "cards.feat.support_standard": "Support standard",
    "cards.feat.plastic_premium": "Carte plastique premium",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Assurance voyage standard",
    "cards.feat.support_priority": "Support prioritaire",
    "cards.feat.metal_brushed": "Carte en métal brossé",
    "cards.feat.cashback_2_unlimited": "Cashback 2% illimité",
    "cards.feat.travel_premium": "Assurance voyage premium",
    "cards.feat.vip_lounge": "Accès salons VIP",
    "cards.feat.support_247": "Assistance 24/7",
    "auth.demo.title": "Connexion administrateur",
    "auth.demo.subtitle": "Entrez vos identifiants admin pour prévisualiser la démo",
    "credits.garantie.none": "Aucune",
    "credits.garantie.mortgage": "Hypothèque sur le bien financé",
    "credits.garantie.vehicle": "Véhicule financé",
    "credits.garantie.parental": "Parentale ou bancaire",
    "credits.garantie.professional": "Actifs professionnels ou caution personnelle",
    "auth.pin.mismatch": "Les deux codes PIN ne correspondent pas",
    "auth.pin.digit_hint": "4 chiffres",
  
  },

  /* ═══════════════════════════ ENGLISH ═══════════════════════════ */
  en: {
    "notif.transfer_code.title": "Transfer validation code",
    "notif.transfer_code.body": "Copy this code to validate your transfer.",
    "notif.transfer_code.expires_in": "Disappears in {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} months",
    "credits.n_months": "{n} months",
    "msg.read": "Read",
    "msg.sent": "Sent",
    "msg.attach_aria": "Attach a file",
    "login.device.unknown": "Unknown browser",
    "login.device.mobile": "Mobile",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Desktop",
    "login.recent": "Recent",
    "err.invalid_credentials": "Invalid credentials",
    "err.bad_pin": "Incorrect PIN",
    "err.account_blocked": "Account blocked",
    "err.bad_current_pw": "Current password is incorrect",
    "err.insufficient_funds": "Insufficient funds, please top up your account.",
    "err.amount_above_savings": "Amount exceeds available savings",
    "err.must_be_positive": "Value must be positive",
    "err.invalid_format": "Invalid format",
    "err.email_taken": "Email already in use",
    "err.card_not_found": "Card not found",
    "err.physical_card_already_ordered": "Physical card already ordered",
    "err.beneficiary_not_found": "Beneficiary not found",
    "err.invalid_code": "Invalid code",
    "err.no_transfer_code": "No code for this transfer",
    "err.link_expired": "Link invalid or expired",
    "err.thread_not_found": "Conversation not found",
    "err.empty_message": "Empty message",
    "err.file_too_large": "File too large (max 20 MB)",
    "err.forbidden": "Access denied",
    "err.bank_not_found": "Bank not found",
    "err.signups_closed": "Sign-ups are closed for this bank.",
    "err.access_suspended": "Access temporarily suspended. Please contact your bank.",
    "err.notif_not_closable": "This notification cannot be dismissed.",
    "auth.signup.tagline": "Join us today",
    "auth.login.tagline": "Welcome back!",
    "auth.signup.tagline_sub": "Open your account in 2 minutes and access all our premium banking services.",
    "auth.login.tagline_sub": "Access your personal space and manage your finances securely.",
    "auth.back": "Back to home",
    "auth.bullet.security": "Military-grade banking security",
    "auth.bullet.transfers": "Instant transfers 24/7",
    "auth.bullet.investments": "Integrated investments and savings",
    "auth.bullet.advisor": "Personal advisor available 7 days/7",
    "auth.footer": "Powered by {bank}",
    "auth.signup.title": "Create an account",
    "auth.login.title": "Login",
    "auth.field.lastName": "Last name",
    "auth.field.firstName": "First name(s)",
    "auth.field.country": "Country",
    "auth.field.country.placeholder": "— Select your country —",
    "auth.field.phone": "Phone",
    "auth.field.address": "Address",
    "auth.field.address.placeholder": "123 Main St, London",
    "auth.field.email": "Email",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Password",
    "auth.submit.signup": "Create my account",
    "auth.submit.login": "Log in",
    "auth.login.badge": "Connect to your account",
    "auth.card.tagline": "Your client area",
    "auth.submit.loading": "Loading…",
    "auth.pin.title": "PIN Code",
    "auth.pin.subtitle": "Enter your 4-digit PIN Code",
    "auth.pin.label": "PIN Code",
    "auth.pin.confirm": "Confirm PIN Code",
    "auth.pin.submit": "Confirm",
    "auth.pin.back": "Back to login",
    "auth.pin.length_error": "PIN Code must be 4 digits",
    "auth.switch.have_account": "Already have an account?",
    "auth.switch.login_link": "Login",
    "auth.switch.no_account": "Not a client yet?",
    "auth.error.select_country": "Please select your country",
    "kyc.title": "Identity Verification (KYC)",
    "kyc.sub": "Upload your documents to validate your account. All documents are encrypted and confidential.",
    "kyc.id_type": "Identity document type *",
    "kyc.id_card": "ID Card",
    "kyc.passport": "Passport",
    "kyc.driver_license": "Driver's license",
    "kyc.recto_passport": "Passport front *",
    "kyc.recto_id": "ID front *",
    "kyc.verso": "ID back *",
    "kyc.selfie": "Selfie holding your ID *",
    "kyc.address_proof": "Proof of address *",
    "kyc.hint_doc": "Image or PDF, max 5 MB",
    "kyc.hint_selfie": "Clear photo, face and document visible",
    "kyc.hint_address": "Bill, statement, certificate… (max 5 MB)",
    "kyc.upload": "Upload",
    "kyc.replace": "Replace",
    "pending.title": "Account pending",
    "pending.body": "Your request has been received. An administrator will verify your file and activate your account shortly.",
    "pending.back": "Back to login",
    "home.nav.features": "Features",
    "home.nav.security": "Security",
    "home.nav.invest": "Investment",
    "home.nav.credits": "Credits",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Log in",
    "home.hero.tagline": "Next-generation online banking",
    "home.hero.title1": "Manage your money",
    "home.hero.title2": "with intelligence",
    "home.hero.sub": "A complete banking platform — transfers, savings, investments and credits from your smartphone.",
    "home.hero.login": "Log in",
    "home.hero.trust.secure": "100% Secure",
    "home.hero.trust.fast": "Quick setup",
    "home.hero.trust.encrypted": "Encrypted data",
    "home.hero.trust.support": "24/7 Service",
    "home.mockup.balance": "Available balance",
    "home.mockup.currency": "EUR · Main account",
    "home.mockup.recharge": "Top up",
    "home.mockup.send": "Send",
    "home.mockup.save": "Save",
    "home.mockup.salary": "March Salary",
    "home.mockup.rent": "April Rent",
    "home.mockup.investment": "Investment",
    "home.mockup.days2": "2 days ago",
    "home.mockup.days3": "3 days ago",
    "home.mockup.days5": "5 days ago",
    "home.stats.accounts": "Active accounts",
    "home.stats.uptime": "Uptime",
    "home.stats.transfers": "Transfers",
    "home.stats.support": "Support",
    "home.features.label": "Features",
    "home.features.title": "Everything you need",
    "home.features.sub": "A complete platform designed to simplify the management of your finances every day.",
    "home.feature.security": "Military security",
    "home.feature.security_body": "AES-256 encryption, two-factor authentication and 24/7 account monitoring.",
    "home.feature.cards": "Virtual & physical cards",
    "home.feature.cards_body": "Issue cards in seconds or order a real physical card delivered to you.",
    "home.feature.transfers": "Express transfers",
    "home.feature.transfers_body": "Send and receive money anywhere in the world in seconds.",
    "home.feature.analytics": "Real-time analysis",
    "home.feature.analytics_body": "Smart dashboards to track your spending and anticipate your finances.",
    "home.feature.investments": "Integrated investments",
    "home.feature.investments_body": "Access selected investment products directly from your account.",
    "home.feature.support": "Dedicated 24/7 support",
    "home.feature.support_body": "A personal banking advisor available at any time via secure messaging.",
    "home.tpl.label": "Trust",
    "home.tpl.title": "They trust us",
    "home.mockup.balance_label": "Balance",
    "home.mockup.virement": "Transfer",
    "home.mockup.salaire": "Salary",
    "home.security.label": "Security",
    "home.security.title1": "Your finances",
    "home.security.title2": "in absolute safety",
    "home.security.sub": "We apply the strictest banking security standards to protect every cent.",
    "home.security.aes.title": "AES-256 Encryption",
    "home.security.aes.body": "All sensitive data is encrypted with the same algorithms used by major international banks.",
    "home.security.auth.title": "Enhanced authentication",
    "home.security.auth.body": "Double identity verification at each login and for each sensitive operation on your account.",
    "home.security.compliance.title": "PSD2/GDPR Compliance",
    "home.security.compliance.body": "We comply with all European regulations on payment services and data protection.",
    "home.invest.label": "Investments",
    "home.invest.title": "Grow your money",
    "home.invest.sub": "6 investment categories selected for their potential and reliability.",
    "home.invest.yield": "Yield",
    "home.credits.label": "Credits",
    "home.credits.title": "Solutions for every project",
    "home.credits.sub": "Competitive rates, fast responses and personalized support for all your projects.",
    "home.credits.rate": "Rate",
    "home.faq.label": "FAQ",
    "home.faq.title": "Frequently asked questions",
    "home.faq.q1": "How do I open an account?",
    "home.faq.a1": "Registration takes less than 2 minutes. Simply fill in your personal information, choose a password and your account will be activated after validation by our team.",
    "home.faq.q2": "Is my data secure?",
    "home.faq.a2": "Yes. All data is encrypted with AES-256 at rest and TLS 1.3 in transit. We comply with international banking standards and GDPR/PSD2 regulations.",
    "home.faq.q3": "What types of investments do you offer?",
    "home.faq.a3": "We offer 6 categories: Real Estate, Crowdfunding, Renewable Energy, Stock Market, Participatory and Crypto. Each product is selected for its risk/return ratio.",
    "home.faq.q4": "How do I apply for credit?",
    "home.faq.a4": "Go to the Credit section of your client area, choose the type of loan, enter the amount and duration. Your application is processed within 24h by our advisors.",
    "home.faq.q5": "Are there hidden fees?",
    "home.faq.a5": "No hidden fees. All rates are clearly displayed with each operation. Terms are accessible in your client area at any time.",
    "home.faq.q6": "How do I contact support?",
    "home.faq.a6": "Use the integrated messaging in your client area to directly contact your personal banking advisor, available 24/7.",
    "home.cta.title": "Ready to start?",
    "home.cta.sub": "Join thousands of users managing their finances with {bank}. Registration takes less than 2 minutes.",
    "home.footer.products": "Products",
    "home.footer.services": "Services",
    "home.footer.access": "Quick access",
    "home.footer.accounts": "Accounts",
    "home.footer.transfers": "Transfers",
    "home.footer.cards": "Cards",
    "home.footer.savings": "Savings",
    "home.footer.investments": "Investments",
    "home.footer.credits": "Credits",
    "home.footer.financing": "Financing",
    "home.footer.messages": "Messages",
    "home.footer.login": "Login",
    "home.footer.signup": "Open an account",
    "home.footer.support": "24/7 Support",
    "home.footer.tagline": "Your online bank, available everywhere and at any time.",
    "home.footer.copyright": "© {year} {bank} — All rights reserved",
    "home.footer.powered": "Powered by {bank}",
    "app.menu_label": "Main menu",
    "app.nav.dashboard": "Dashboard",
    "app.nav.accounts": "Accounts",
    "app.nav.transactions": "Transactions",
    "app.nav.transfers": "Transfers",
    "app.nav.cards": "Cards",
    "app.nav.savings": "Savings",
    "app.nav.investments": "Investment",
    "app.nav.credit": "Credit",
    "app.nav.financing": "Financing",
    "app.nav.messages": "Messages",
    "app.nav.card_tracking": "Card tracking",
    "app.nav.notifications": "Notifications",
    "app.nav.settings": "Settings",
    "app.nav.profile": "Profile",
    "app.nav.mobile.home": "Home",
    "app.nav.mobile.transfers": "Transfers",
    "app.nav.mobile.cards": "Cards",
    "app.nav.mobile.transactions": "Transactions",
    "app.nav.more.invest": "Invest",
    "app.user_role": "Client",
    "app.logout": "Log out",
    "app.search": "Search…",
    "app.more_title": "More features",
    "app.more_btn": "More",
    "app.loading": "Loading…",
    "app.error_back": "Back to login",
    "app.see_more_notifs": "See {count} more notifications →",
    "page.accounts": "My accounts",
    "page.transactions": "Transactions",
    "page.transfers": "New transfer",
    "page.deposit": "Deposit",
    "page.cards": "My cards",
    "page.savings": "Savings",
    "page.investments": "Investment",
    "page.credits": "Credit",
    "page.financing": "Project financing",
    "page.messages": "Messages",
    "page.settings": "Settings",
    "page.notifications": "Important notifications",
    "page.profile": "My profile",
    "dash.greeting": "Welcome, {name}",
    "dash.overview": "Your finances overview — {date}",
    "dash.total_balance": "Total balance",
    "dash.this_month": "+{amount} this month",
    "dash.view_iban": "View my IBAN",
    "dash.my_accounts": "My accounts",
    "dash.primary_account": "Main account",
    "dash.savings_account": "Savings account",
    "dash.remunerated": "Interest-bearing",
    "dash.details": "Details",
    "dash.transfer_btn": "Transfer",
    "dash.quick_actions": "Quick actions",
    "dash.quick.deposit": "Deposit",
    "deposit.title": "Deposit information",
    "dash.quick.transfer": "Transfer",
    "dash.quick.history": "History",
    "dash.quick.cards": "My cards",
    "dash.quick.profile": "Profile",
    "dash.monthly_budget": "Monthly budget",
    "dash.income": "Income",
    "dash.expenses": "Expenses",
    "dash.spending_cat": "Spending by category",
    "dash.no_spending": "No spending recorded.",
    "dash.recent_tx": "Recent transactions",
    "dash.see_all": "See all",
    "dash.no_tx": "No transactions.",
    "dash.main_card": "My main card",
    "dash.manage": "Manage",
    "dash.block": "Block",
    "dash.pin": "PIN code",
    "dash.security": "Security",
    "dash.more": "More",
    "dash.no_card": "No card issued",
    "dash.monthly_activity": "Monthly activity",
    "dash.operations": "Operations",
    "dash.holder": "Holder",
    "dash.expires": "Exp.",
    "dash.visa_label": "VISA",
    "dash.balance": "Available balance",
    "tx.all": "All",
    "tx.income": "Income",
    "tx.expenses": "Expenses",
    "tx.today": "Today",
    "tx.yesterday": "Yesterday",
    "tx.none": "No transactions.",
    "transfer.title": "Quick transfer",
    "transfer.recipient": "Recipient",
    "transfer.recipient_ph": "Name, IBAN, phone number…",
    "transfer.amount": "Amount",
    "transfer.label": "Label",
    "transfer.reason": "Reason (optional)",
    "transfer.reason_ph": "Ex: rent, reimbursement…",
    "transfer.execution": "Execution",
    "transfer.immediate": "Immediate",
    "transfer.delayed": "Delayed",
    "transfer.balance": "Available balance:",
    "transfer.continue": "Continue",
    "transfer.confirm": "Confirm",
    "transfer.cancel": "Cancel",
    "transfer.code": "Validation code",
    "transfer.code.received": "Your validation code",
    "transfer.code.copy": "Copy",
    "transfer.code.copied": "Copied",
    "common.loading": "Loading…",
    "common.search": "Search",
    "common.other": "Other",
    "error.bank_not_found": "Bank not found",
    "error.back_home": "Back to home",
    "error.unknown": "Unknown error",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "kyc.file_read_error": "Unable to read file",
    "tracking.label": "Tracking",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "tracking.status.delivered": "Delivered",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "card.color.night": "Night",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.royal": "Royal Blue",
    "card.color.cyan": "Cyan",
    "card.color.violet": "Violet",
    "card.color.lavender": "Lavender",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.amber": "Amber",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.ruby": "Ruby",
    "card.color.terracotta": "Terracotta",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.topup": "Top up",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "content.translated": "Auto-translated content",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Enter the code received to validate the transfer",
    "transfer.pending.title": "Transfer pending validation",
    "transfer.pending.amount": "Amount:",
    "transfer.pending.to": "To:",
    "transfer.pending.hint": "Your validation code will be sent by email or via the notification bell. The validation code consists of 6 digits. If you do not receive it, please contact support.",
    "transfer.pending.validate": "Validate transfer",
    "transfer.saved": "Transfer saved. Ask your administrator for the validation code.",
    "transfer.done": "Transfer completed",
    "transfer.validated": "Transfer validated!",
    "transfer.progress.processing": "Processing transfer…",
    "transfer.progress.completed": "Transfer completed!",
    "transfer.progress.default_message": "Your transfer is being processed. Please wait.",
    "cards.none": "No card issued.\nContact your administrator.",
    "cards.holder": "Holder",
    "cards.expires": "Exp.",
    "cards.block": "Lock",
    "cards.pin": "PIN code",
    "cards.more": "More",
    "cards.limits": "Card limits",
    "cards.payment": "Payment",
    "cards.withdrawal": "Withdrawal",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Blocked",
    "cards.status_active": "Active",
    "cards.toast.blocked": "Card blocked",
    "cards.toast.unblocked": "Card unblocked",
    "cards.toast.physical_ordered": "Physical card ordered! Delivery in 5–10 business days.",
    "cards.toast.created": "Card generated successfully!",
    "cards.none_sub": "No card issued. Generate your virtual card now.",
    "cards.generate_btn": "Generate my card",
    "cards.create.title": "Generate my virtual card",
    "cards.create.network": "Payment network",
    "cards.create.color": "Card colour",
    "cards.create.preview_name": "YOUR NAME",
    "cards.create.preview_expiry": "MM/YY",
    "cards.create.loading": "Generating…",
    "cards.create.submit": "Generate card",
    "cards.btn.unblock": "Unblock",
    "cards.btn.block": "Block",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Physical",
    "cards.btn.physical_done": "Ordered",
    "cards.limits_title": "Limits",
    "cards.reveal.title": "Card information",
    "cards.reveal.number": "Full number",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Expiry",
    "cards.reveal.warning": "Never share this information. Close this window after use.",
    "cards.reveal.pin_prompt": "Enter your 4-digit PIN to display the card details.",
    "cards.reveal.show": "Show",
    "cards.pin.title": "PIN code",
    "cards.pin.message": "This PIN code is used for all your online payments and withdrawals. Never share it with anyone.",
    "cards.physical.title": "Order physical card",
    "cards.physical.fee_label": "Delivery fee",
    "cards.physical.balance_label": "Your balance",
    "cards.physical.insufficient": "Insufficient balance. Please top up your account before ordering.",
    "cards.physical.info": "The fee will be debited from your account. Delivery in 5–10 business days.",
    "cards.physical.order_btn": "Order ({fee})",
    "cards.physical.ordering": "Ordering…",
    "cards.physical.order_cta": "Order a physical card",
    "cards.physical.choose_type": "Choose your card",
    "cards.physical.features": "Features",
    "cards.buy_virtual_cta": "Buy a virtual card",
    "notif.none": "No notifications at the moment.",
    "notif.contact_support": "Contact support",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "profile.change_photo": "Change photo",
    "profile.remove_photo": "Remove photo",
    "profile.email": "Email",
    "profile.phone": "Phone",
    "profile.country": "Country",
    "profile.address": "Address",
    "profile.notifs_title": "Notifications ({count})",
    "profile.no_notifs": "No notifications.",
    "profile.logout": "Log out",
    "profile.photo_updated": "Profile photo updated",
    "profile.photo_removed": "Photo removed",
    "profile.photo_too_large": "Image too large (max 1 MB)",
    "profile.powered": "Powered by {bank}",
    "iban.title": "My IBAN",
    "iban.loading": "Loading…",
    "iban.bic": "BIC / SWIFT code",
    "iban.share_info": "Share these details to receive a transfer to your account.",
    "iban.copy": "Copy",
    "iban.copied": "Copied to clipboard",
    "iban.copy_fail": "Unable to copy",
    "iban.close": "Close",
    "accounts.all": "All your accounts with {bank}.",
    "accounts.primary": "Main account",
    "accounts.savings": "Savings account",
    "accounts.primary_tag": "Primary",
    "accounts.secondary_tag": "Secondary",
    "accounts.objective": "Target: {amount} · {pct}%",
    "accounts.copy": "Copy",
    "accounts.loading": "Loading…",
    "accounts.iban_copied": "IBAN copied",
    "accounts.copy_fail": "Unable to copy",
    "savings.total": "Total saved",
    "savings.target_total": "Total target",
    "savings.progress": "Progress",
    "savings.active": "Active goals",
    "savings.new_goal": "New goal",
    "savings.none": "No savings goals yet.",
    "savings.none_sub": "Create your first goal to get started.",
    "savings.saved": "Saved:",
    "savings.goal": "Target:",
    "savings.deadline": "Deadline: {date}",
    "savings.deposit": "Deposit",
    "savings.withdraw": "Withdraw",
    "savings.loading": "Loading…",
    "savings.form.title": "New savings goal",
    "savings.form.cancel": "Cancel",
    "savings.form.create": "Create",
    "savings.form.name": "Goal name",
    "savings.form.name_ph": "Japan trip, Car, …",
    "savings.form.target": "Target amount",
    "savings.form.initial": "Initial deposit",
    "savings.form.deadline": "Deadline (optional)",
    "savings.form.color": "Color",
    "savings.deposit_title": "Deposit to \"{name}\"",
    "savings.deposit_amount": "Amount to deposit",
    "savings.balance_hint": "Main balance: {amount}",
    "savings.withdraw_prompt": "How much to withdraw from \"{name}\"?",
    "savings.close_confirm": "Close goal \"{name}\"? Savings will be returned to your main account.",
    "savings.toast.created": "Goal created",
    "savings.toast.deposited": "Deposit made",
    "savings.toast.withdrawn": "Withdrawal made",
    "savings.toast.closed": "Goal closed",
    "invest.total": "Total invested",
    "invest.profits": "Cumulative profits",
    "invest.count": "Investments",
    "invest.products": "Available products",
    "invest.invest_btn": "Invest",
    "invest.my": "My investments",
    "invest.pending": "Pending",
    "invest.validated": "Validated",
    "invest.refused": "Refused",
    "invest.withdraw.btn": "Withdraw",
    "invest.withdraw.title": "Withdraw my investment",
    "invest.withdraw.intro": "You are about to withdraw your investment \"{product}\". The principal and accrued profit will be credited to your main balance.",
    "invest.withdraw.principal": "Principal",
    "invest.withdraw.profit": "Profit",
    "invest.withdraw.total": "Total to receive",
    "invest.withdraw.confirm": "Confirm withdrawal",
    "invest.withdraw.warning": "This action is irreversible. The investment will be closed.",
    "invest.withdraw.success": "Investment withdrawn — {amount} credited to your balance.",
    "invest.withdrawn": "Withdrawn",
    "invest.cta_sub": "Choose the type of investment that suits you. An advisor will assist you from your first request.",
    "invest.form.amount": "Amount to invest",
    "invest.form.balance": "Available balance: {amount}",
    "invest.form.note": "Note for your advisor (optional)",
    "invest.form.note_ph": "Specify your expectations…",
    "invest.form.advisor_info": "A conversation will automatically be opened with your advisor.",
    "invest.form.cancel": "Cancel",
    "invest.form.submit": "Send request",
    "invest.toast.requested": "Request registered — a conversation has been opened with your advisor.",
    "invest.p1.name": "Real Estate",
    "invest.p1.desc": "REITs, stone-paper, real estate crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Participatory financing of selected projects",
    "invest.p3.name": "Renewables",
    "invest.p3.desc": "Solar, wind and green projects",
    "invest.p4.name": "Stock Market",
    "invest.p4.desc": "Stocks, ETFs and diversified funds",
    "invest.p5.name": "Participatory",
    "invest.p5.desc": "Loans to SMEs and local entrepreneurs",
    "invest.p6.name": "Crypto",
    "invest.p6.desc": "Bitcoin, Ethereum and multi-currency wallet",
    "msg.online": "Online",
    "msg.loading": "Loading…",
    "msg.none": "No messages. Start the conversation with your advisor.",
    "msg.placeholder": "Write your message…",
    "msg.attach.image": "Image",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "File / Document",
    "msg.pdf_doc": "PDF Document",
    "msg.file": "File",
    "msg.too_large": "File too large (max {size}).",
    "msg.withdraw": "Delete",
    "credits.cta_sub": "Apply directly online — response within 24h.",
    "credits.learn_more": "Learn more",
    "credits.request": "Apply",
    "credits.history": "My applications",
    "credits.status.pending": "Pending",
    "credits.status.accepted": "Accepted",
    "credits.status.refused": "Refused",
    "credits.chars": "Characteristics",
    "credits.amount_range": "Amount: {min}€ – {max}€",
    "credits.rate_range": "Interest rate: {min}% – {max}% per year",
    "credits.duration_range": "Duration: {min} to {max} months",
    "credits.deposit_min": "Initial deposit: {amount}€ minimum",
    "credits.garantie": "Guarantee: {garantie}",
    "credits.eligibility": "Eligibility criteria",
    "credits.elig.income": "Prove regular and stable income",
    "credits.elig.deposit": "Make an initial deposit of {amount}€",
    "credits.elig.garantie": "Present the guarantee: {garantie}",
    "credits.form.title": "Application — {name}",
    "credits.form.cancel": "Cancel",
    "credits.form.submit": "Send application",
    "credits.form.amount": "Desired amount (€)",
    "credits.form.duration": "Duration (months)",
    "credits.form.reason": "Reason (optional)",
    "credits.form.reason_ph": "Describe your project…",
    "credits.form.close": "Close",
    "credits.toast.submitted": "Credit application sent — you will receive a response within 24h.",
    "credits.p1.name": "Personal Loan",
    "credits.p1.desc": "To finance everyday needs or personal projects.",
    "credits.p2.name": "Mortgage",
    "credits.p2.desc": "To finance the purchase or construction of real estate.",
    "credits.p3.name": "Auto / Moto Loan",
    "credits.p3.desc": "Buy a vehicle with peace of mind.",
    "credits.p4.name": "Student Loan",
    "credits.p4.desc": "Financial assistance for higher education.",
    "credits.p5.name": "Business Loan",
    "credits.p5.desc": "To support the growth of a professional activity.",
    "credits.p6.name": "Express Loan",
    "credits.p6.desc": "A quick solution for urgent needs.",
    "financing.reimbursable": "Repayable financing",
    "financing.reimbursable_desc": "Financial support with fixed conditions to launch or develop an activity.",
    "financing.non_reimbursable": "Non-repayable financing",
    "financing.non_reimbursable_desc": "For projects with social, educational or humanitarian impact (NGOs, associations).",
    "financing.cta_sub": "Tailored solutions to make your ambitions a reality.",
    "financing.request": "Request financing",
    "financing.history": "My applications",
    "financing.reimbursable_label": "Repayable",
    "financing.non_reimbursable_label": "Non-repayable",
    "financing.status.pending": "Pending",
    "financing.status.granted": "Granted",
    "financing.status.refused": "Refused",
    "financing.form.title": "Application — {name}",
    "financing.form.cancel": "Cancel",
    "financing.form.submit": "Send application",
    "financing.form.project": "Project name",
    "financing.form.project_ph": "My project",
    "financing.form.amount": "Requested amount (€)",
    "financing.form.sector": "Sector",
    "financing.form.sector_ph": "Tech, agriculture, …",
    "financing.form.description": "Project description",
    "financing.form.description_ph": "Describe your project, its objectives and expected impact…",
    "settings.security": "Security",
    "settings.current_pw": "Current password",
    "settings.new_pw": "New password",
    "settings.confirm_pw": "Confirm",
    "settings.submit": "Change password",
    "settings.no_match": "Passwords do not match",
    "settings.changed": "Password changed",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved_ok": "Beneficiary saved",
    "credits.simulation": "Monthly payment estimate",
    "credits.monthly_payment": "Est. monthly payment",
    "credits.total_cost": "Est. total cost",
    "credits.rate_used": "Estimated rate:",
    "credits.rate_annual": "/ year",
    "common.cancel": "Cancel",
    "common.confirm": "Confirm",
    "common.close": "Close",
    "common.save": "Save",
    "common.loading": "Loading…",
    "common.send": "Send",
    "common.total": "Total",
    "dash.welcome": "Welcome",
    "common.delete": "Delete",
    "cards.delete.title": "Delete card",
    "cards.delete.confirm_msg": "This action is irreversible. The card will be permanently deleted.",
    "cards.delete.physical_tooltip": "Physical card ordered — deletion impossible",
    "cards.order.confirmed": "Order confirmed!",
    "cards.order.confirmed_body": "Your physical card is being prepared. Use this number to track your delivery.",
    "cards.order.tracking_no": "Tracking #",
    "cards.fee_deducted": "{amount} will be deducted from your balance",
    "cards.insufficient_short": "Insufficient balance ({available} available)",
    "transfer.section.beneficiary": "Beneficiary",
    "transfer.section.details": "Transfer",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bank",
    "transfer.field.city": "City",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bank:",
    "transfer.progress.paused": "Pending validation",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Variable",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Card deleted",
    "cards.feat.gold_exclusive": "Exclusive gold card",
    "cards.feat.cashback_05": "Cashback 0.5%",
    "cards.feat.buyer_protection": "Buyer protection",
    "cards.feat.support_standard": "Standard support",
    "cards.feat.plastic_premium": "Premium plastic card",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Standard travel insurance",
    "cards.feat.support_priority": "Priority support",
    "cards.feat.metal_brushed": "Brushed metal card",
    "cards.feat.cashback_2_unlimited": "Unlimited 2% cashback",
    "cards.feat.travel_premium": "Premium travel insurance",
    "cards.feat.vip_lounge": "VIP lounge access",
    "cards.feat.support_247": "24/7 assistance",
    "auth.demo.title": "Administrator login",
    "auth.demo.subtitle": "Enter your admin credentials to preview the demo",
    "credits.garantie.none": "None",
    "credits.garantie.mortgage": "Mortgage on the financed property",
    "credits.garantie.vehicle": "Financed vehicle",
    "credits.garantie.parental": "Parental or bank guarantee",
    "credits.garantie.professional": "Professional assets or personal guarantee",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "The two PINs do not match",
    "auth.pin.digit_hint": "4 digits",
  
  },

  es: {
    "notif.transfer_code.title": "Código de validación de transferencia",
    "notif.transfer_code.body": "Copia este código para validar tu transferencia.",
    "notif.transfer_code.expires_in": "Desaparece en {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} meses",
    "credits.n_months": "{n} meses",
    "msg.read": "Leído",
    "msg.sent": "Enviado",
    "msg.attach_aria": "Adjuntar archivo",
    "login.device.unknown": "Navegador desconocido",
    "login.device.mobile": "Móvil",
    "login.device.tablet": "Tableta",
    "login.device.desktop": "Ordenador",
    "login.recent": "Reciente",
    "err.invalid_credentials": "Credenciales inválidas",
    "err.bad_pin": "PIN incorrecto",
    "err.account_blocked": "Cuenta bloqueada",
    "err.bad_current_pw": "La contraseña actual es incorrecta",
    "err.insufficient_funds": "Fondos insuficientes, recargue su cuenta.",
    "err.amount_above_savings": "Importe superior al ahorro disponible",
    "err.must_be_positive": "El valor debe ser positivo",
    "err.invalid_format": "Formato inválido",
    "err.email_taken": "Email ya en uso",
    "err.card_not_found": "Tarjeta no encontrada",
    "err.physical_card_already_ordered": "Tarjeta física ya solicitada",
    "err.beneficiary_not_found": "Beneficiario no encontrado",
    "err.invalid_code": "Código inválido",
    "err.no_transfer_code": "No hay código para esta transferencia",
    "err.link_expired": "Enlace inválido o caducado",
    "err.thread_not_found": "Conversación no encontrada",
    "err.empty_message": "Mensaje vacío",
    "err.file_too_large": "Archivo demasiado grande (máx 20 MB)",
    "err.forbidden": "Acceso denegado",
    "err.bank_not_found": "Banco no encontrado",
    "err.signups_closed": "Los registros están cerrados para este banco.",
    "err.access_suspended": "Acceso temporalmente suspendido. Por favor, contacte con su banco.",
    "err.notif_not_closable": "Esta notificación no se puede cerrar.",
    "auth.signup.tagline": "Únete a nosotros hoy",
    "auth.login.tagline": "¡Bienvenido!",
    "auth.signup.tagline_sub": "Abra su cuenta en 2 minutos y acceda a todos nuestros servicios bancarios premium.",
    "auth.login.tagline_sub": "Acceda a su espacio personal y gestione sus finanzas con total seguridad.",
    "auth.back": "Volver al inicio",
    "auth.bullet.security": "Seguridad bancaria de nivel militar",
    "auth.bullet.transfers": "Transferencias instantáneas 24/7",
    "auth.bullet.investments": "Inversiones y ahorro integrados",
    "auth.bullet.advisor": "Asesor personal disponible los 7 días",
    "auth.footer": "Impulsado por {bank}",
    "auth.signup.title": "Crear una cuenta",
    "auth.login.title": "Iniciar sesión",
    "auth.field.lastName": "Apellido",
    "auth.field.firstName": "Nombres",
    "auth.field.country": "País",
    "auth.field.country.placeholder": "— Seleccione su país —",
    "auth.field.phone": "Teléfono",
    "auth.field.address": "Dirección",
    "auth.field.address.placeholder": "Calle Mayor 123, Madrid",
    "auth.field.email": "Correo electrónico",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Contraseña",
    "auth.submit.signup": "Crear mi cuenta",
    "auth.submit.login": "Conectarse",
    "auth.login.badge": "Conéctese a su cuenta",
    "auth.card.tagline": "Su espacio de cliente",
    "auth.submit.loading": "Cargando…",
    "auth.pin.title": "Código PIN",
    "auth.pin.subtitle": "Introduzca su Código PIN de 4 dígitos",
    "auth.pin.label": "Código PIN",
    "auth.pin.confirm": "Confirmar Código PIN",
    "auth.pin.submit": "Confirmar",
    "auth.pin.back": "Volver al inicio de sesión",
    "auth.pin.length_error": "El Código PIN debe tener 4 dígitos",
    "auth.switch.have_account": "¿Ya tiene cuenta?",
    "auth.switch.login_link": "Iniciar sesión",
    "auth.switch.no_account": "¿Aún no es cliente?",
    "auth.error.select_country": "Seleccione su país",
    "kyc.title": "Verificación de identidad (KYC)",
    "kyc.sub": "Suba sus documentos para validar su cuenta. Todos los documentos están cifrados y son confidenciales.",
    "kyc.id_type": "Tipo de documento de identidad *",
    "kyc.id_card": "DNI",
    "kyc.passport": "Pasaporte",
    "kyc.driver_license": "Carnet de conducir",
    "kyc.recto_passport": "Anverso del pasaporte *",
    "kyc.recto_id": "Anverso del documento *",
    "kyc.verso": "Reverso del documento *",
    "kyc.selfie": "Selfie con el documento de identidad en mano *",
    "kyc.address_proof": "Justificante de domicilio *",
    "kyc.hint_doc": "Imagen o PDF, máx. 5 MB",
    "kyc.hint_selfie": "Foto clara, cara y documento visibles",
    "kyc.hint_address": "Factura, extracto, certificado… (máx. 5 MB)",
    "kyc.upload": "Subir",
    "kyc.replace": "Reemplazar",
    "pending.title": "Cuenta pendiente",
    "pending.body": "Hemos recibido su solicitud. Un administrador revisará su expediente y activará su cuenta en breve.",
    "pending.back": "Volver al inicio de sesión",
    "home.nav.features": "Funcionalidades",
    "home.nav.security": "Seguridad",
    "home.nav.invest": "Inversión",
    "home.nav.credits": "Créditos",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Iniciar sesión",
    "home.hero.tagline": "Banca en línea de nueva generación",
    "home.hero.title1": "Gestione su dinero",
    "home.hero.title2": "con inteligencia",
    "home.hero.sub": "Una plataforma bancaria completa: transferencias, ahorro, inversiones y créditos desde su smartphone.",
    "home.hero.login": "Iniciar sesión",
    "home.hero.trust.secure": "100% Seguro",
    "home.hero.trust.fast": "Apertura rápida",
    "home.hero.trust.encrypted": "Datos cifrados",
    "home.hero.trust.support": "Servicio 24/7",
    "home.mockup.balance": "Saldo disponible",
    "home.mockup.currency": "EUR · Cuenta principal",
    "home.mockup.recharge": "Recargar",
    "home.mockup.send": "Enviar",
    "home.mockup.save": "Ahorrar",
    "home.mockup.salary": "Sueldo de marzo",
    "home.mockup.rent": "Alquiler de abril",
    "home.mockup.investment": "Inversión",
    "home.mockup.days2": "hace 2 días",
    "home.mockup.days3": "hace 3 días",
    "home.mockup.days5": "hace 5 días",
    "home.stats.accounts": "Cuentas activas",
    "home.stats.uptime": "Disponibilidad",
    "home.stats.transfers": "Transferencias",
    "home.stats.support": "Soporte",
    "home.features.label": "Funcionalidades",
    "home.features.title": "Todo lo que necesita",
    "home.features.sub": "Una plataforma completa diseñada para simplificar la gestión de sus finanzas a diario.",
    "home.feature.security": "Seguridad militar",
    "home.feature.security_body": "Cifrado AES-256, autenticación de doble factor y vigilancia 24/7 de su cuenta.",
    "home.feature.cards": "Tarjetas virtual y física",
    "home.feature.cards_body": "Emita tarjetas en segundos o solicite una tarjeta física real entregada en su domicilio.",
    "home.feature.transfers": "Transferencias exprés",
    "home.feature.transfers_body": "Envíe y reciba dinero en cualquier parte del mundo en segundos.",
    "home.feature.analytics": "Análisis en tiempo real",
    "home.feature.analytics_body": "Cuadros de mando inteligentes para seguir sus gastos y anticipar sus finanzas.",
    "home.feature.investments": "Inversiones integradas",
    "home.feature.investments_body": "Acceda a productos de inversión seleccionados directamente desde su cuenta.",
    "home.feature.support": "Soporte dedicado 24/7",
    "home.feature.support_body": "Un asesor bancario personal disponible en todo momento mediante mensajería segura.",
    "home.tpl.label": "Confianza",
    "home.tpl.title": "Confían en nosotros",
    "home.mockup.balance_label": "Saldo",
    "home.mockup.virement": "Transferencia",
    "home.mockup.salaire": "Sueldo",
    "home.security.label": "Seguridad",
    "home.security.title1": "Sus finanzas",
    "home.security.title2": "con seguridad absoluta",
    "home.security.sub": "Aplicamos los estándares de seguridad bancaria más estrictos para proteger cada céntimo.",
    "home.security.aes.title": "Cifrado AES-256",
    "home.security.aes.body": "Todos los datos sensibles se cifran con los mismos algoritmos utilizados por los grandes bancos internacionales.",
    "home.security.auth.title": "Autenticación reforzada",
    "home.security.auth.body": "Doble verificación de identidad en cada conexión y para cada operación sensible en su cuenta.",
    "home.security.compliance.title": "Conformidad DSP2/RGPD",
    "home.security.compliance.body": "Cumplimos con todas las normativas europeas en materia de servicios de pago y protección de datos.",
    "home.invest.label": "Inversiones",
    "home.invest.title": "Haga crecer su dinero",
    "home.invest.sub": "6 categorías de inversión seleccionadas por su potencial y su fiabilidad.",
    "home.invest.yield": "Rentabilidad",
    "home.credits.label": "Créditos",
    "home.credits.title": "Soluciones adaptadas a cada proyecto",
    "home.credits.sub": "Tasas competitivas, respuestas rápidas y acompañamiento personalizado para todos sus proyectos.",
    "home.credits.rate": "Tasa",
    "home.faq.label": "FAQ",
    "home.faq.title": "Preguntas frecuentes",
    "home.faq.q1": "¿Cómo abrir una cuenta?",
    "home.faq.a1": "La inscripción dura menos de 2 minutos. Basta con introducir sus datos personales, elegir una contraseña y su cuenta se activará tras la validación de nuestro equipo.",
    "home.faq.q2": "¿Mis datos están seguros?",
    "home.faq.a2": "Sí. Todos los datos se cifran con AES-256 en reposo y TLS 1.3 en tránsito. Cumplimos con las normas bancarias internacionales y las normativas RGPD/DSP2.",
    "home.faq.q3": "¿Qué tipos de inversiones ofrecen?",
    "home.faq.a3": "Ofrecemos 6 categorías: Inmobiliario, Crowdfunding, Energías renovables, Bolsa, Participativo y Cripto. Cada producto se selecciona por su relación riesgo/rentabilidad.",
    "home.faq.q4": "¿Cómo solicitar un crédito?",
    "home.faq.a4": "Acceda a la sección Crédito de su área de cliente, elija el tipo de préstamo e indique el importe y la duración. Su expediente se tramita en 24 h por nuestros asesores.",
    "home.faq.q5": "¿Hay comisiones ocultas?",
    "home.faq.a5": "Ninguna comisión oculta. Todas las tarifas se muestran con claridad en cada operación. Las condiciones están disponibles en su área de cliente en todo momento.",
    "home.faq.q6": "¿Cómo contactar con el soporte?",
    "home.faq.a6": "Utilice la mensajería integrada en su área de cliente para contactar directamente con su asesor bancario personal, disponible 24/7.",
    "home.cta.title": "¿Listo para empezar?",
    "home.cta.sub": "Únase a miles de usuarios que gestionan sus finanzas con {bank}. La inscripción dura menos de 2 minutos.",
    "home.footer.products": "Productos",
    "home.footer.services": "Servicios",
    "home.footer.access": "Acceso rápido",
    "home.footer.accounts": "Cuentas",
    "home.footer.transfers": "Transferencias",
    "home.footer.cards": "Tarjetas",
    "home.footer.savings": "Ahorro",
    "home.footer.investments": "Inversiones",
    "home.footer.credits": "Créditos",
    "home.footer.financing": "Financiación",
    "home.footer.messages": "Mensajes",
    "home.footer.login": "Iniciar sesión",
    "home.footer.signup": "Abrir una cuenta",
    "home.footer.support": "Soporte 24/7",
    "home.footer.tagline": "Su banca en línea, disponible en todas partes y a cualquier hora.",
    "home.footer.copyright": "© {year} {bank} — Todos los derechos reservados",
    "home.footer.powered": "Impulsado por {bank}",
    "app.menu_label": "Menú principal",
    "app.nav.dashboard": "Panel de control",
    "app.nav.accounts": "Cuentas",
    "app.nav.transactions": "Transacciones",
    "app.nav.transfers": "Transferencias",
    "app.nav.cards": "Tarjetas",
    "app.nav.savings": "Ahorro",
    "app.nav.investments": "Inversión",
    "app.nav.credit": "Crédito",
    "app.nav.financing": "Financiación",
    "app.nav.messages": "Mensajes",
    "app.nav.card_tracking": "Seguimiento de tarjeta",
    "app.nav.notifications": "Notificaciones",
    "app.nav.settings": "Ajustes",
    "app.nav.profile": "Perfil",
    "app.nav.mobile.home": "Inicio",
    "app.nav.mobile.transfers": "Transferencias",
    "app.nav.mobile.cards": "Tarjetas",
    "app.nav.mobile.transactions": "Transacciones",
    "app.nav.more.invest": "Invertir",
    "app.user_role": "Cliente",
    "app.logout": "Cerrar sesión",
    "app.search": "Buscar…",
    "app.more_title": "Más funcionalidades",
    "app.more_btn": "Más",
    "app.loading": "Cargando…",
    "app.error_back": "Volver al inicio de sesión",
    "app.see_more_notifs": "Ver las {count} notificaciones restantes →",
    "page.accounts": "Mis cuentas",
    "page.transactions": "Transacciones",
    "page.transfers": "Nueva transferencia",
    "page.deposit": "Depósito",
    "page.cards": "Mis tarjetas",
    "page.savings": "Ahorro",
    "page.investments": "Inversión",
    "page.credits": "Crédito",
    "page.financing": "Financiación de proyectos",
    "page.messages": "Mensajes",
    "page.settings": "Ajustes",
    "page.notifications": "Notificaciones importantes",
    "page.profile": "Mi perfil",
    "dash.greeting": "Hola, {name}",
    "dash.overview": "Resumen de sus finanzas — {date}",
    "dash.total_balance": "Saldo total",
    "dash.this_month": "+{amount} este mes",
    "dash.view_iban": "Ver mi IBAN",
    "dash.my_accounts": "Mis cuentas",
    "dash.primary_account": "Cuenta principal",
    "dash.savings_account": "Cuenta de ahorro",
    "dash.remunerated": "Remunerada",
    "dash.details": "Detalles",
    "dash.transfer_btn": "Transferencia",
    "dash.quick_actions": "Acciones rápidas",
    "dash.quick.deposit": "Depósito",
    "deposit.title": "Información de depósito",
    "dash.quick.transfer": "Transferencia",
    "dash.quick.history": "Historial",
    "dash.quick.cards": "Mis tarjetas",
    "dash.quick.profile": "Perfil",
    "dash.monthly_budget": "Presupuesto mensual",
    "dash.income": "Ingresos",
    "dash.expenses": "Gastos",
    "dash.spending_cat": "Gastos por categoría",
    "dash.no_spending": "Ningún gasto registrado.",
    "dash.recent_tx": "Transacciones recientes",
    "dash.see_all": "Ver todo",
    "dash.no_tx": "Ninguna operación.",
    "dash.main_card": "Mi tarjeta principal",
    "dash.manage": "Gestionar",
    "dash.block": "Bloquear",
    "dash.pin": "Código PIN",
    "dash.security": "Seguridad",
    "dash.more": "Más",
    "dash.no_card": "Ninguna tarjeta emitida",
    "dash.monthly_activity": "Actividad del mes",
    "dash.operations": "Operaciones",
    "dash.holder": "Titular",
    "dash.expires": "Cad.",
    "dash.visa_label": "VISA",
    "dash.balance": "Saldo disponible",
    "tx.all": "Todas",
    "tx.income": "Ingresos",
    "tx.expenses": "Gastos",
    "tx.today": "Hoy",
    "tx.yesterday": "Ayer",
    "tx.none": "Ninguna transacción.",
    "transfer.title": "Transferencia rápida",
    "transfer.recipient": "Destinatario",
    "transfer.recipient_ph": "Nombre, IBAN, número de teléfono…",
    "transfer.amount": "Importe",
    "transfer.label": "Concepto",
    "transfer.reason": "Motivo (opcional)",
    "transfer.reason_ph": "Ej.: alquiler, reembolso…",
    "transfer.execution": "Ejecución",
    "transfer.immediate": "Inmediata",
    "transfer.delayed": "Diferida",
    "transfer.balance": "Saldo disponible:",
    "transfer.continue": "Continuar",
    "transfer.confirm": "Confirmar",
    "transfer.cancel": "Cancelar",
    "transfer.code": "Código de validación",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Introduzca el código recibido para validar la transferencia",
    "transfer.pending.title": "Transferencia pendiente de validación",
    "transfer.pending.amount": "Importe:",
    "transfer.pending.to": "Para:",
    "transfer.pending.hint": "Su código de validación será enviado por correo electrónico o mediante la campana de notificaciones. El código consta de 6 dígitos. Si no lo recibe, contacte con el soporte.",
    "transfer.pending.validate": "Validar la transferencia",
    "transfer.saved": "Transferencia registrada. Solicita el código a tu administrador para validar.",
    "transfer.done": "Transferencia realizada",
    "transfer.validated": "¡Transferencia validada!",
    "transfer.progress.processing": "Procesando transferencia…",
    "transfer.progress.completed": "¡Transferencia realizada!",
    "transfer.progress.default_message": "Su transferencia se está procesando. Por favor espere.",
    "cards.none": "Ninguna tarjeta emitida.\nContacte con su administrador.",
    "cards.holder": "Titular",
    "cards.expires": "Cad.",
    "cards.block": "Bloquear",
    "cards.pin": "Código PIN",
    "cards.more": "Más",
    "cards.limits": "Límites de la tarjeta",
    "cards.payment": "Pago",
    "cards.withdrawal": "Retirada",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Bloqueada",
    "cards.status_active": "Activa",
    "cards.toast.blocked": "Tarjeta bloqueada",
    "cards.toast.unblocked": "Tarjeta desbloqueada",
    "cards.toast.physical_ordered": "¡Tarjeta física pedida! Entrega en 5–10 días hábiles.",
    "cards.toast.created": "¡Tarjeta generada con éxito!",
    "cards.none_sub": "Ninguna tarjeta emitida. Genera tu tarjeta virtual ahora.",
    "cards.generate_btn": "Generar mi tarjeta",
    "cards.create.title": "Generar mi tarjeta virtual",
    "cards.create.network": "Red de pago",
    "cards.create.color": "Color de la tarjeta",
    "cards.create.preview_name": "TU NOMBRE",
    "cards.create.preview_expiry": "MM/AA",
    "cards.create.loading": "Generando…",
    "cards.create.submit": "Generar tarjeta",
    "cards.btn.unblock": "Desbloquear",
    "cards.btn.block": "Bloquear",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Física",
    "cards.btn.physical_done": "Pedida",
    "cards.limits_title": "Límites",
    "cards.reveal.title": "Información de la tarjeta",
    "cards.reveal.number": "Número completo",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Vencimiento",
    "cards.reveal.warning": "Nunca compartas esta información. Cierra esta ventana después de usarla.",
    "cards.pin.title": "Código PIN",
    "cards.pin.message": "Este código PIN se usa para todos tus pagos en línea y retiradas. Nunca lo compartas con nadie.",
    "cards.physical.title": "Pedir tarjeta física",
    "cards.physical.fee_label": "Gastos de envío",
    "cards.physical.balance_label": "Tu saldo",
    "cards.physical.insufficient": "Saldo insuficiente. Por favor, recarga tu cuenta antes de pedir.",
    "cards.physical.info": "Los gastos se debitarán de tu cuenta. Entrega en 5–10 días hábiles.",
    "cards.physical.order_btn": "Pedir ({fee})",
    "cards.physical.ordering": "Pidiendo…",
    "cards.physical.order_cta": "Pedir una tarjeta física",
    "cards.physical.choose_type": "Elige tu tarjeta",
    "cards.physical.features": "Ventajas",
    "cards.buy_virtual_cta": "Comprar una tarjeta virtual",
    "notif.none": "Ninguna notificación por el momento.",
    "notif.contact_support": "Contactar soporte",
    "profile.change_photo": "Cambiar la foto",
    "profile.remove_photo": "Quitar la foto",
    "profile.email": "Correo electrónico",
    "profile.phone": "Teléfono",
    "profile.country": "País",
    "profile.address": "Dirección",
    "profile.notifs_title": "Notificaciones ({count})",
    "profile.no_notifs": "Ninguna notificación.",
    "profile.logout": "Cerrar sesión",
    "profile.photo_updated": "Foto de perfil actualizada",
    "profile.photo_removed": "Foto eliminada",
    "profile.photo_too_large": "Imagen demasiado grande (máx. 1 MB)",
    "profile.powered": "Impulsado por {bank}",
    "iban.title": "Mi IBAN",
    "iban.loading": "Cargando…",
    "iban.bic": "Código BIC / SWIFT",
    "iban.share_info": "Comparta estos datos para recibir una transferencia en su cuenta.",
    "iban.copy": "Copiar",
    "iban.copied": "Copiado al portapapeles",
    "iban.copy_fail": "Imposible copiar",
    "iban.close": "Cerrar",
    "accounts.all": "Todas sus cuentas abiertas en {bank}.",
    "accounts.primary": "Cuenta principal",
    "accounts.savings": "Cuenta de ahorro",
    "accounts.primary_tag": "Principal",
    "accounts.secondary_tag": "Secundaria",
    "accounts.objective": "Objetivo: {amount} · {pct}%",
    "accounts.copy": "Copiar",
    "accounts.loading": "Cargando…",
    "accounts.iban_copied": "IBAN copiado",
    "accounts.copy_fail": "Imposible copiar",
    "savings.total": "Total ahorrado",
    "savings.target_total": "Objetivo total",
    "savings.progress": "Progreso",
    "savings.active": "Objetivos activos",
    "savings.new_goal": "Nuevo objetivo",
    "savings.none": "Ningún objetivo de ahorro por ahora.",
    "savings.none_sub": "Cree su primer objetivo para empezar.",
    "savings.saved": "Ahorrado:",
    "savings.goal": "Objetivo:",
    "savings.deadline": "Vencimiento: {date}",
    "savings.deposit": "Depositar",
    "savings.withdraw": "Retirar",
    "savings.loading": "Cargando…",
    "savings.form.title": "Nuevo objetivo de ahorro",
    "savings.form.cancel": "Cancelar",
    "savings.form.create": "Crear",
    "savings.form.name": "Nombre del objetivo",
    "savings.form.name_ph": "Viaje a Japón, Coche, …",
    "savings.form.target": "Importe objetivo",
    "savings.form.initial": "Depósito inicial",
    "savings.form.deadline": "Fecha límite (opcional)",
    "savings.form.color": "Color",
    "savings.deposit_title": "Depositar en \"{name}\"",
    "savings.deposit_amount": "Importe a depositar",
    "savings.balance_hint": "Saldo principal: {amount}",
    "savings.withdraw_prompt": "¿Cuánto retirar de \"{name}\"?",
    "savings.close_confirm": "¿Cerrar el objetivo \"{name}\"? El ahorro se transferirá a su cuenta principal.",
    "savings.toast.created": "Objetivo creado",
    "savings.toast.deposited": "Depósito realizado",
    "savings.toast.withdrawn": "Retirada realizada",
    "savings.toast.closed": "Objetivo cerrado",
    "invest.total": "Total invertido",
    "invest.profits": "Beneficios acumulados",
    "invest.count": "Inversiones",
    "invest.products": "Productos disponibles",
    "invest.invest_btn": "Invertir",
    "invest.my": "Mis inversiones",
    "invest.pending": "Pendiente",
    "invest.validated": "Validado",
    "invest.refused": "Rechazado",
    "invest.withdraw.btn": "Retirar",
    "invest.withdraw.title": "Retirar mi inversión",
    "invest.withdraw.intro": "Está a punto de retirar su inversión «{product}». El capital inicial y los beneficios acumulados se acreditarán en su saldo principal.",
    "invest.withdraw.principal": "Capital inicial",
    "invest.withdraw.profit": "Beneficios",
    "invest.withdraw.total": "Total a recibir",
    "invest.withdraw.confirm": "Confirmar retiro",
    "invest.withdraw.warning": "Esta acción es irreversible. La inversión se cerrará.",
    "invest.withdraw.success": "Inversión retirada — {amount} acreditados en su saldo.",
    "invest.withdrawn": "Retirado",
    "invest.cta_sub": "Elija el tipo de inversión que más le convenga. Un asesor le acompañará desde su solicitud.",
    "invest.form.amount": "Importe a invertir",
    "invest.form.balance": "Saldo disponible: {amount}",
    "invest.form.note": "Nota para su asesor (opcional)",
    "invest.form.note_ph": "Especifique sus expectativas…",
    "invest.form.advisor_info": "Se abrirá automáticamente una conversación con su asesor.",
    "invest.form.cancel": "Cancelar",
    "invest.form.submit": "Enviar la solicitud",
    "invest.toast.requested": "Solicitud registrada — se ha abierto una conversación con su asesor.",
    "invest.p1.name": "Inmobiliario",
    "invest.p1.desc": "SCPI, papel-ladrillo, crowdfunding inmobiliario",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Financiación participativa de proyectos seleccionados",
    "invest.p3.name": "Energ. renovables",
    "invest.p3.desc": "Solar, eólica y proyectos verdes",
    "invest.p4.name": "Bolsa",
    "invest.p4.desc": "Acciones, ETF y fondos diversificados",
    "invest.p5.name": "Participativo",
    "invest.p5.desc": "Préstamos a PME y emprendedores locales",
    "invest.p6.name": "Cripto",
    "invest.p6.desc": "Bitcoin, Ethereum y cartera multidivisa",
    "msg.online": "En línea",
    "msg.loading": "Cargando…",
    "msg.none": "Ningún mensaje. Inicie la conversación con su asesor.",
    "msg.placeholder": "Escriba su mensaje…",
    "msg.attach.image": "Imagen",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Archivo / Documento",
    "msg.pdf_doc": "Documento PDF",
    "msg.file": "Archivo",
    "msg.too_large": "Archivo demasiado grande (máx. {size}).",
    "msg.withdraw": "Retirar",
    "credits.cta_sub": "Realice su solicitud directamente en línea — respuesta en 24 h.",
    "credits.learn_more": "Saber más",
    "credits.request": "Solicitar",
    "credits.history": "Mis solicitudes",
    "credits.status.pending": "Pendiente",
    "credits.status.accepted": "Aceptado",
    "credits.status.refused": "Rechazado",
    "credits.chars": "Características",
    "credits.amount_range": "Importe: {min}€ – {max}€",
    "credits.rate_range": "Tipo de interés: {min}% – {max}% al año",
    "credits.duration_range": "Duración: {min} a {max} meses",
    "credits.deposit_min": "Depósito inicial: {amount}€ mínimo",
    "credits.garantie": "Garantía: {garantie}",
    "credits.eligibility": "Condiciones de elegibilidad",
    "credits.elig.income": "Justificar ingresos regulares y estables",
    "credits.elig.deposit": "Realizar un depósito inicial de {amount}€",
    "credits.elig.garantie": "Presentar la garantía: {garantie}",
    "credits.form.title": "Solicitud — {name}",
    "credits.form.cancel": "Cancelar",
    "credits.form.submit": "Enviar la solicitud",
    "credits.form.amount": "Importe deseado (€)",
    "credits.form.duration": "Duración (meses)",
    "credits.form.reason": "Motivo (opcional)",
    "credits.form.reason_ph": "Especifique su proyecto…",
    "credits.form.close": "Cerrar",
    "credits.toast.submitted": "Solicitud de crédito enviada — recibirá una respuesta en 24 h.",
    "credits.p1.name": "Préstamo Personal",
    "credits.p1.desc": "Para financiar las necesidades cotidianas o los proyectos personales.",
    "credits.p2.name": "Préstamo Hipotecario",
    "credits.p2.desc": "Para financiar la compra o la construcción de un bien inmobiliario.",
    "credits.p3.name": "Préstamo Auto / Moto",
    "credits.p3.desc": "Compre un vehículo con total tranquilidad.",
    "credits.p4.name": "Préstamo Estudiante",
    "credits.p4.desc": "Una ayuda financiera para los estudios superiores.",
    "credits.p5.name": "Préstamo Profesional",
    "credits.p5.desc": "Para apoyar el crecimiento de una actividad profesional.",
    "credits.p6.name": "Préstamo Exprés",
    "credits.p6.desc": "Una solución rápida para las necesidades urgentes.",
    "financing.reimbursable": "Financiación reembolsable",
    "financing.reimbursable_desc": "Un apoyo financiero con condiciones fijas para lanzar o desarrollar una actividad.",
    "financing.non_reimbursable": "Financiación no reembolsable",
    "financing.non_reimbursable_desc": "Para proyectos con impacto social, educativo o humanitario (ONG, asociaciones).",
    "financing.cta_sub": "Soluciones adaptadas para hacer realidad sus ambiciones.",
    "financing.request": "Solicitar una financiación",
    "financing.history": "Mis solicitudes",
    "financing.reimbursable_label": "Reembolsable",
    "financing.non_reimbursable_label": "No reembolsable",
    "financing.status.pending": "Pendiente",
    "financing.status.granted": "Concedida",
    "financing.status.refused": "Rechazada",
    "financing.form.title": "Solicitud — {name}",
    "financing.form.cancel": "Cancelar",
    "financing.form.submit": "Enviar la solicitud",
    "financing.form.project": "Nombre del proyecto",
    "financing.form.project_ph": "Mi proyecto",
    "financing.form.amount": "Importe solicitado (€)",
    "financing.form.sector": "Sector",
    "financing.form.sector_ph": "Tecnología, agricultura, …",
    "financing.form.description": "Descripción del proyecto",
    "financing.form.description_ph": "Presente su proyecto, sus objetivos y su impacto esperado…",
    "settings.security": "Seguridad",
    "settings.current_pw": "Contraseña actual",
    "settings.new_pw": "Nueva contraseña",
    "settings.confirm_pw": "Confirmar",
    "settings.submit": "Cambiar la contraseña",
    "settings.no_match": "Las contraseñas no coinciden",
    "settings.changed": "Contraseña modificada",
    "common.cancel": "Cancelar",
    "common.confirm": "Confirmar",
    "common.close": "Cerrar",
    "common.save": "Guardar",
    "common.loading": "Cargando…",
    "common.send": "Enviar",
    "common.total": "Total",
    "admin.wallet.balance": "Saldo disponible",
    "admin.wallet.history": "Historial",
    "admin.wallet.no_history": "Sin transacciones",
    "admin.wallet.title": "Mi billetera",
    "admin.wallet.topup": "Recargar",
    "beneficiary.pick": "Seleccionar",
    "beneficiary.save_this": "Guardar este beneficiario",
    "beneficiary.saved": "Beneficiarios guardados",
    "beneficiary.saved_ok": "Beneficiario guardado",
    "card.color.amber": "Ámbar",
    "card.color.cyan": "Cian",
    "card.color.emerald": "Esmeralda",
    "card.color.forest": "Bosque",
    "card.color.gold": "Oro",
    "card.color.lavender": "Lavanda",
    "card.color.night": "Noche",
    "card.color.orange": "Naranja",
    "card.color.pink": "Rosa",
    "card.color.royal": "Azul real",
    "card.color.ruby": "Rubí",
    "card.color.slate": "Pizarra",
    "card.color.steel": "Acero",
    "card.color.terracotta": "Terracota",
    "card.color.violet": "Violeta",
    "common.other": "Otro",
    "common.search": "Buscar",
    "content.translated": "Contenido traducido automáticamente",
    "credits.monthly_payment": "Cuota mensual estimada",
    "credits.rate_annual": "/ año",
    "credits.rate_used": "Tasa estimada:",
    "credits.simulation": "Simulación de cuota mensual",
    "credits.total_cost": "Coste total estimado",
    "demo.banner": "Modo demostración — ninguna acción real",
    "demo.disabled": "Acción no disponible en modo demo",
    "deposit.default_instructions": "Contacte a su gestor para los detalles del depósito.",
    "error.back_home": "Volver al inicio",
    "error.bank_not_found": "Banco no encontrado",
    "error.unknown": "Error desconocido",
    "kyc.file_read_error": "No se puede leer el archivo",
    "kyc.file_too_large": "Archivo demasiado grande (máx. 5 MB)",
    "login.history": "Historial de conexiones",
    "login.history.empty": "Ninguna conexión registrada.",
    "notif.dismiss": "Cerrar",
    "notif.locked": "Mensaje del banco",
    "tracking.error.not_found": "Número de seguimiento no encontrado. Verifique e intente de nuevo.",
    "tracking.label": "Seguimiento",
    "tracking.status.delivered": "Entregado",
    "tracking.status.preparation": "En preparación",
    "tracking.status.shipped": "Enviado",
    "tracking.status.transit": "En tránsito",
    "transfer.code.copied": "Copiado",
    "transfer.code.copy": "Copiar",
    "transfer.code.received": "Su código de validación",
    "dash.welcome": "Bienvenido",
    "common.delete": "Eliminar",
    "cards.delete.title": "Eliminar tarjeta",
    "cards.delete.confirm_msg": "Esta acción es irreversible. La tarjeta será eliminada permanentemente.",
    "cards.delete.physical_tooltip": "Tarjeta física pedida — eliminación imposible",
    "cards.order.confirmed": "¡Pedido confirmado!",
    "cards.order.confirmed_body": "Su tarjeta física está siendo preparada. Use este número para rastrear su entrega.",
    "cards.order.tracking_no": "N° de seguimiento",
    "cards.fee_deducted": "{amount} será deducido de su saldo",
    "cards.insufficient_short": "Saldo insuficiente ({available} disponible)",
    "transfer.section.beneficiary": "Beneficiario",
    "transfer.section.details": "Transferencia",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banco",
    "transfer.field.city": "Ciudad",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banco:",
    "transfer.progress.paused": "Pendiente de validación",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Inmobiliario",
    "home.invest.p.realestate_desc": "SCPI, crowdfunding inmobiliario, papel-piedra",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Financiación participativa de proyectos seleccionados",
    "home.invest.p.renewable": "Energías renov.",
    "home.invest.p.renewable_desc": "Solar, eólica y proyectos verdes",
    "home.invest.p.stocks": "Bolsa",
    "home.invest.p.stocks_desc": "Acciones, ETF y fondos diversificados",
    "home.invest.p.participatory": "Participativo",
    "home.invest.p.participatory_desc": "Préstamos a PYMES y emprendedores locales",
    "home.invest.p.crypto": "Cripto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum y cartera multidivisa",
    "home.invest.p.variable": "Variable",
    "home.credits.p.personal": "Préstamo Personal",
    "home.credits.p.realestate": "Préstamo Hipotecario",
    "home.credits.p.auto": "Préstamo Auto/Moto",
    "home.credits.p.student": "Préstamo Estudiante",
    "home.credits.p.pro": "Préstamo Profesional",
    "home.credits.p.express": "Préstamo Express",
    "home.credits.rate.from": "desde {rate}/año",

    "cards.deleted": "Tarjeta eliminada",

    "cards.feat.gold_exclusive": "Tarjeta dorada exclusiva",

    "cards.feat.cashback_05": "Cashback 0,5%",

    "cards.feat.buyer_protection": "Protección del comprador",

    "cards.feat.support_standard": "Soporte estándar",

    "cards.feat.plastic_premium": "Tarjeta plástica premium",

    "cards.feat.cashback_1": "Cashback 1%",

    "cards.feat.travel_standard": "Seguro de viaje estándar",

    "cards.feat.support_priority": "Soporte prioritario",

    "cards.feat.metal_brushed": "Tarjeta de metal cepillado",

    "cards.feat.cashback_2_unlimited": "Cashback 2% ilimitado",

    "cards.feat.travel_premium": "Seguro de viaje premium",

    "cards.feat.vip_lounge": "Acceso a salones VIP",

    "cards.feat.support_247": "Asistencia 24/7",

    "auth.demo.title": "Inicio de sesión de administrador",

    "auth.demo.subtitle": "Ingresa tus credenciales de admin para ver la demo",
    "credits.garantie.none": "Ninguna",
    "credits.garantie.mortgage": "Hipoteca sobre el bien financiado",
    "credits.garantie.vehicle": "Vehículo financiado",
    "credits.garantie.parental": "Parental o bancaria",
    "credits.garantie.professional": "Activos profesionales o aval personal",
    "cards.reveal.pin_prompt": "Introduzca su código PIN de 4 dígitos para mostrar la información de la tarjeta.",
    "cards.reveal.show": "Mostrar",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Los dos códigos PIN no coinciden",
    "auth.pin.digit_hint": "4 dígitos",
  
  },

  pt: {
    "notif.transfer_code.title": "Código de validação de transferência",
    "notif.transfer_code.body": "Copie este código para validar a sua transferência.",
    "notif.transfer_code.expires_in": "Desaparece em {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} meses",
    "credits.n_months": "{n} meses",
    "msg.read": "Lido",
    "msg.sent": "Enviado",
    "msg.attach_aria": "Anexar ficheiro",
    "login.device.unknown": "Navegador desconhecido",
    "login.device.mobile": "Telemóvel",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Computador",
    "login.recent": "Recente",
    "err.invalid_credentials": "Credenciais inválidas",
    "err.bad_pin": "PIN incorreto",
    "err.account_blocked": "Conta bloqueada",
    "err.bad_current_pw": "A palavra-passe atual está incorreta",
    "err.insufficient_funds": "Fundos insuficientes, recarregue a sua conta.",
    "err.amount_above_savings": "Montante superior à poupança disponível",
    "err.must_be_positive": "O valor deve ser positivo",
    "err.invalid_format": "Formato inválido",
    "err.email_taken": "Email já em uso",
    "err.card_not_found": "Cartão não encontrado",
    "err.physical_card_already_ordered": "Cartão físico já solicitado",
    "err.beneficiary_not_found": "Beneficiário não encontrado",
    "err.invalid_code": "Código inválido",
    "err.no_transfer_code": "Sem código para esta transferência",
    "err.link_expired": "Link inválido ou expirado",
    "err.thread_not_found": "Conversa não encontrada",
    "err.empty_message": "Mensagem vazia",
    "err.file_too_large": "Ficheiro muito grande (máx 20 MB)",
    "err.forbidden": "Acesso negado",
    "err.bank_not_found": "Banco não encontrado",
    "err.signups_closed": "Os registos estão fechados para este banco.",
    "err.access_suspended": "Acesso temporariamente suspenso. Contacte o seu banco.",
    "err.notif_not_closable": "Esta notificação não pode ser fechada.",
    "auth.signup.tagline": "Junte-se a nós hoje",
    "auth.login.tagline": "Bem-vindo!",
    "auth.signup.tagline_sub": "Abra a sua conta em 2 minutos e aceda a todos os nossos serviços bancários premium.",
    "auth.login.tagline_sub": "Aceda ao seu espaço pessoal e faça a gestão das suas finanças com total segurança.",
    "auth.back": "Voltar ao início",
    "auth.bullet.security": "Segurança bancária de nível militar",
    "auth.bullet.transfers": "Transferências instantâneas 24h/24",
    "auth.bullet.investments": "Investimentos e poupança integrados",
    "auth.bullet.advisor": "Consultor pessoal disponível 7 dias por semana",
    "auth.footer": "Desenvolvido por {bank}",
    "auth.signup.title": "Criar uma conta",
    "auth.login.title": "Iniciar sessão",
    "auth.field.lastName": "Apelido",
    "auth.field.firstName": "Nomes próprios",
    "auth.field.country": "País",
    "auth.field.country.placeholder": "— Selecione o seu país —",
    "auth.field.phone": "Telefone",
    "auth.field.address": "Morada",
    "auth.field.address.placeholder": "Rua da Liberdade 123, Lisboa",
    "auth.field.email": "E-mail",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Palavra-passe",
    "auth.submit.signup": "Criar a minha conta",
    "auth.submit.login": "Iniciar sessão",
    "auth.login.badge": "Acesse a sua conta",
    "auth.card.tagline": "O seu espaço de cliente",
    "auth.submit.loading": "A carregar…",
    "auth.pin.title": "Código PIN",
    "auth.pin.subtitle": "Introduza o seu Código PIN de 4 dígitos",
    "auth.pin.label": "Código PIN",
    "auth.pin.confirm": "Confirmar Código PIN",
    "auth.pin.submit": "Confirmar",
    "auth.pin.back": "Voltar ao início de sessão",
    "auth.pin.length_error": "O Código PIN deve ter 4 dígitos",
    "auth.switch.have_account": "Já tem conta?",
    "auth.switch.login_link": "Iniciar sessão",
    "auth.switch.no_account": "Ainda não é cliente?",
    "auth.error.select_country": "Selecione o seu país",
    "kyc.title": "Verificação de identidade (KYC)",
    "kyc.sub": "Carregue os seus documentos para validar a sua conta. Todos os documentos são cifrados e confidenciais.",
    "kyc.id_type": "Tipo de documento de identificação *",
    "kyc.id_card": "Cartão de cidadão",
    "kyc.passport": "Passaporte",
    "kyc.driver_license": "Carta de condução",
    "kyc.recto_passport": "Frente do passaporte *",
    "kyc.recto_id": "Frente do documento *",
    "kyc.verso": "Verso do documento *",
    "kyc.selfie": "Selfie com o documento de identificação na mão *",
    "kyc.address_proof": "Comprovativo de morada *",
    "kyc.hint_doc": "Imagem ou PDF, máx. 5 MB",
    "kyc.hint_selfie": "Foto nítida, rosto e documento visíveis",
    "kyc.hint_address": "Fatura, extrato, declaração… (máx. 5 MB)",
    "kyc.upload": "Carregar",
    "kyc.replace": "Substituir",
    "pending.title": "Conta pendente",
    "pending.body": "O seu pedido foi recebido com sucesso. Um administrador irá verificar o seu processo e ativar a sua conta em breve.",
    "pending.back": "Voltar ao início de sessão",
    "home.nav.features": "Funcionalidades",
    "home.nav.security": "Segurança",
    "home.nav.invest": "Investimento",
    "home.nav.credits": "Créditos",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Iniciar sessão",
    "home.hero.tagline": "Banca em linha de nova geração",
    "home.hero.title1": "Faça a gestão do seu dinheiro",
    "home.hero.title2": "com inteligência",
    "home.hero.sub": "Uma plataforma bancária completa — transferências, poupança, investimentos e créditos a partir do seu smartphone.",
    "home.hero.login": "Iniciar sessão",
    "home.hero.trust.secure": "100% Seguro",
    "home.hero.trust.fast": "Abertura rápida",
    "home.hero.trust.encrypted": "Dados cifrados",
    "home.hero.trust.support": "Serviço 24/7",
    "home.mockup.balance": "Saldo disponível",
    "home.mockup.currency": "EUR · Conta principal",
    "home.mockup.recharge": "Carregar",
    "home.mockup.send": "Enviar",
    "home.mockup.save": "Poupar",
    "home.mockup.salary": "Salário de março",
    "home.mockup.rent": "Renda de abril",
    "home.mockup.investment": "Investimento",
    "home.mockup.days2": "há 2 dias",
    "home.mockup.days3": "há 3 dias",
    "home.mockup.days5": "há 5 dias",
    "home.stats.accounts": "Contas ativas",
    "home.stats.uptime": "Disponibilidade",
    "home.stats.transfers": "Transferências",
    "home.stats.support": "Apoio",
    "home.features.label": "Funcionalidades",
    "home.features.title": "Tudo o que precisa",
    "home.features.sub": "Uma plataforma completa concebida para simplificar a gestão das suas finanças no dia a dia.",
    "home.feature.security": "Segurança militar",
    "home.feature.security_body": "Cifragem AES-256, autenticação de duplo fator e vigilância 24/7 da sua conta.",
    "home.feature.cards": "Cartões virtual e físico",
    "home.feature.cards_body": "Emita cartões em segundos ou peça um verdadeiro cartão físico entregue em sua casa.",
    "home.feature.transfers": "Transferências expresso",
    "home.feature.transfers_body": "Envie e receba dinheiro em qualquer parte do mundo em segundos.",
    "home.feature.analytics": "Análise em tempo real",
    "home.feature.analytics_body": "Painéis inteligentes para acompanhar as suas despesas e antecipar as suas finanças.",
    "home.feature.investments": "Investimentos integrados",
    "home.feature.investments_body": "Aceda a produtos de investimento selecionados diretamente a partir da sua conta.",
    "home.feature.support": "Apoio dedicado 24/7",
    "home.feature.support_body": "Um consultor bancário pessoal disponível a qualquer momento através de mensagens seguras.",
    "home.tpl.label": "Confiança",
    "home.tpl.title": "Confiam em nós",
    "home.mockup.balance_label": "Saldo",
    "home.mockup.virement": "Transferência",
    "home.mockup.salaire": "Salário",
    "home.security.label": "Segurança",
    "home.security.title1": "As suas finanças",
    "home.security.title2": "em segurança absoluta",
    "home.security.sub": "Aplicamos as normas de segurança bancária mais rigorosas para proteger cada cêntimo.",
    "home.security.aes.title": "Cifragem AES-256",
    "home.security.aes.body": "Todos os dados sensíveis são cifrados com os mesmos algoritmos utilizados pelos grandes bancos internacionais.",
    "home.security.auth.title": "Autenticação reforçada",
    "home.security.auth.body": "Dupla verificação de identidade a cada início de sessão e para cada operação sensível na sua conta.",
    "home.security.compliance.title": "Conformidade DSP2/RGPD",
    "home.security.compliance.body": "Cumprimos toda a regulamentação europeia em matéria de serviços de pagamento e proteção de dados.",
    "home.invest.label": "Investimentos",
    "home.invest.title": "Faça o seu dinheiro crescer",
    "home.invest.sub": "6 categorias de investimento selecionadas pelo seu potencial e fiabilidade.",
    "home.invest.yield": "Rentabilidade",
    "home.credits.label": "Créditos",
    "home.credits.title": "Soluções adaptadas a cada projeto",
    "home.credits.sub": "Taxas competitivas, respostas rápidas e acompanhamento personalizado para todos os seus projetos.",
    "home.credits.rate": "Taxa",
    "home.faq.label": "FAQ",
    "home.faq.title": "Perguntas frequentes",
    "home.faq.q1": "Como abrir uma conta?",
    "home.faq.a1": "A inscrição demora menos de 2 minutos. Basta indicar os seus dados pessoais, escolher uma palavra-passe e a sua conta será ativada após validação pela nossa equipa.",
    "home.faq.q2": "Os meus dados estão seguros?",
    "home.faq.a2": "Sim. Todos os dados são cifrados com AES-256 em repouso e TLS 1.3 em trânsito. Cumprimos as normas bancárias internacionais e a regulamentação RGPD/DSP2.",
    "home.faq.q3": "Que tipos de investimentos propõem?",
    "home.faq.a3": "Propomos 6 categorias: Imobiliário, Crowdfunding, Energias renováveis, Bolsa, Participativo e Cripto. Cada produto é selecionado pela sua relação risco/rentabilidade.",
    "home.faq.q4": "Como pedir um crédito?",
    "home.faq.a4": "Aceda à secção Crédito do seu espaço de cliente, escolha o tipo de empréstimo e indique o montante e a duração. O seu pedido é tratado em 24h pelos nossos consultores.",
    "home.faq.q5": "Há custos ocultos?",
    "home.faq.a5": "Nenhum custo oculto. Todos os preços são apresentados de forma clara em cada operação. As condições estão acessíveis no seu espaço de cliente a qualquer momento.",
    "home.faq.q6": "Como contactar o apoio?",
    "home.faq.a6": "Utilize as mensagens integradas no seu espaço de cliente para contactar diretamente o seu consultor bancário pessoal, disponível 24h/24 e 7 dias por semana.",
    "home.cta.title": "Pronto para começar?",
    "home.cta.sub": "Junte-se a milhares de utilizadores que gerem as suas finanças com {bank}. A inscrição demora menos de 2 minutos.",
    "home.footer.products": "Produtos",
    "home.footer.services": "Serviços",
    "home.footer.access": "Acesso rápido",
    "home.footer.accounts": "Contas",
    "home.footer.transfers": "Transferências",
    "home.footer.cards": "Cartões",
    "home.footer.savings": "Poupança",
    "home.footer.investments": "Investimentos",
    "home.footer.credits": "Créditos",
    "home.footer.financing": "Financiamento",
    "home.footer.messages": "Mensagens",
    "home.footer.login": "Iniciar sessão",
    "home.footer.signup": "Abrir uma conta",
    "home.footer.support": "Apoio 24/7",
    "home.footer.tagline": "A sua banca em linha, disponível em qualquer lugar e a qualquer momento.",
    "home.footer.copyright": "© {year} {bank} — Todos os direitos reservados",
    "home.footer.powered": "Desenvolvido por {bank}",
    "app.menu_label": "Menu principal",
    "app.nav.dashboard": "Painel",
    "app.nav.accounts": "Contas",
    "app.nav.transactions": "Transações",
    "app.nav.transfers": "Transferências",
    "app.nav.cards": "Cartões",
    "app.nav.savings": "Poupança",
    "app.nav.investments": "Investimento",
    "app.nav.credit": "Crédito",
    "app.nav.financing": "Financiamento",
    "app.nav.messages": "Mensagens",
    "app.nav.card_tracking": "Rastreamento de cartão",
    "app.nav.notifications": "Notificações",
    "app.nav.settings": "Definições",
    "app.nav.profile": "Perfil",
    "app.nav.mobile.home": "Início",
    "app.nav.mobile.transfers": "Transferências",
    "app.nav.mobile.cards": "Cartões",
    "app.nav.mobile.transactions": "Transações",
    "app.nav.more.invest": "Investir",
    "app.user_role": "Cliente",
    "app.logout": "Terminar sessão",
    "app.search": "Pesquisar…",
    "app.more_title": "Mais funcionalidades",
    "app.more_btn": "Mais",
    "app.loading": "A carregar…",
    "app.error_back": "Voltar ao início de sessão",
    "app.see_more_notifs": "Ver as outras {count} notificações →",
    "page.accounts": "As minhas contas",
    "page.transactions": "Transações",
    "page.transfers": "Nova transferência",
    "page.deposit": "Depósito",
    "page.cards": "Os meus cartões",
    "page.savings": "Poupança",
    "page.investments": "Investimento",
    "page.credits": "Crédito",
    "page.financing": "Financiamento de projetos",
    "page.messages": "Mensagens",
    "page.settings": "Definições",
    "page.notifications": "Notificações importantes",
    "page.profile": "O meu perfil",
    "dash.greeting": "Olá, {name}",
    "dash.overview": "Resumo das suas finanças — {date}",
    "dash.total_balance": "Saldo total",
    "dash.this_month": "+{amount} este mês",
    "dash.view_iban": "Ver o meu IBAN",
    "dash.my_accounts": "As minhas contas",
    "dash.primary_account": "Conta principal",
    "dash.savings_account": "Conta poupança",
    "dash.remunerated": "Remunerada",
    "dash.details": "Detalhes",
    "dash.transfer_btn": "Transferência",
    "dash.quick_actions": "Ações rápidas",
    "dash.quick.deposit": "Depósito",
    "deposit.title": "Informações de depósito",
    "dash.quick.transfer": "Transferência",
    "dash.quick.history": "Histórico",
    "dash.quick.cards": "Os meus cartões",
    "dash.quick.profile": "Perfil",
    "dash.monthly_budget": "Orçamento mensal",
    "dash.income": "Entradas",
    "dash.expenses": "Despesas",
    "dash.spending_cat": "Despesas por categoria",
    "dash.no_spending": "Nenhuma despesa registada.",
    "dash.recent_tx": "Transações recentes",
    "dash.see_all": "Ver tudo",
    "dash.no_tx": "Nenhuma operação.",
    "dash.main_card": "O meu cartão principal",
    "dash.manage": "Gerir",
    "dash.block": "Bloquear",
    "dash.pin": "Código PIN",
    "dash.security": "Segurança",
    "dash.more": "Mais",
    "dash.no_card": "Nenhum cartão emitido",
    "dash.monthly_activity": "Atividade do mês",
    "dash.operations": "Operações",
    "dash.holder": "Titular",
    "dash.expires": "Val.",
    "dash.visa_label": "VISA",
    "dash.balance": "Saldo disponível",
    "tx.all": "Todas",
    "tx.income": "Entradas",
    "tx.expenses": "Saídas",
    "tx.today": "Hoje",
    "tx.yesterday": "Ontem",
    "tx.none": "Nenhuma transação.",
    "transfer.title": "Transferência rápida",
    "transfer.recipient": "Destinatário",
    "transfer.recipient_ph": "Nome, IBAN, número de telefone…",
    "transfer.amount": "Montante",
    "transfer.label": "Descritivo",
    "transfer.reason": "Motivo (opcional)",
    "transfer.reason_ph": "Ex.: renda, reembolso…",
    "transfer.execution": "Execução",
    "transfer.immediate": "Imediata",
    "transfer.delayed": "Diferida",
    "transfer.balance": "Saldo disponível:",
    "transfer.continue": "Continuar",
    "transfer.confirm": "Confirmar",
    "transfer.cancel": "Cancelar",
    "transfer.code": "Código de validação",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Introduza o código recebido para validar a transferência",
    "transfer.pending.title": "Transferência pendente de validação",
    "transfer.pending.amount": "Montante:",
    "transfer.pending.to": "Para:",
    "transfer.pending.hint": "O seu código de validação será enviado por e-mail ou através do sino de notificações. O código é composto por 6 dígitos. Se não o receber, contacte o suporte.",
    "transfer.pending.validate": "Validar a transferência",
    "transfer.saved": "Transferência registada. Solicita o código ao teu administrador para validar.",
    "transfer.done": "Transferência efetuada",
    "transfer.validated": "Transferência validada!",
    "transfer.progress.processing": "A processar transferência…",
    "transfer.progress.completed": "Transferência efetuada!",
    "transfer.progress.default_message": "A sua transferência está a ser processada. Por favor aguarde.",
    "cards.none": "Nenhum cartão emitido.\nContacte o seu administrador.",
    "cards.holder": "Titular",
    "cards.expires": "Val.",
    "cards.block": "Bloquear",
    "cards.pin": "Código PIN",
    "cards.more": "Mais",
    "cards.limits": "Limites do cartão",
    "cards.payment": "Pagamento",
    "cards.withdrawal": "Levantamento",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Bloqueado",
    "cards.status_active": "Ativo",
    "cards.toast.blocked": "Cartão bloqueado",
    "cards.toast.unblocked": "Cartão desbloqueado",
    "cards.toast.physical_ordered": "Cartão físico encomendado! Entrega em 5–10 dias úteis.",
    "cards.toast.created": "Cartão gerado com sucesso!",
    "cards.none_sub": "Nenhum cartão emitido. Gere o seu cartão virtual agora.",
    "cards.generate_btn": "Gerar o meu cartão",
    "cards.create.title": "Gerar o meu cartão virtual",
    "cards.create.network": "Rede de pagamento",
    "cards.create.color": "Cor do cartão",
    "cards.create.preview_name": "O SEU NOME",
    "cards.create.preview_expiry": "MM/AA",
    "cards.create.loading": "A gerar…",
    "cards.create.submit": "Gerar cartão",
    "cards.btn.unblock": "Desbloquear",
    "cards.btn.block": "Bloquear",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Físico",
    "cards.btn.physical_done": "Encomendado",
    "cards.limits_title": "Limites",
    "cards.reveal.title": "Informações do cartão",
    "cards.reveal.number": "Número completo",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Validade",
    "cards.reveal.warning": "Nunca partilhe estas informações. Feche esta janela após a utilização.",
    "cards.pin.title": "Código PIN",
    "cards.pin.message": "Este código PIN é utilizado para todos os seus pagamentos online e levantamentos. Nunca o partilhe com ninguém.",
    "cards.physical.title": "Encomendar cartão físico",
    "cards.physical.fee_label": "Taxa de entrega",
    "cards.physical.balance_label": "O seu saldo",
    "cards.physical.insufficient": "Saldo insuficiente. Por favor, recarregue a sua conta antes de encomendar.",
    "cards.physical.info": "A taxa será debitada da sua conta. Entrega em 5–10 dias úteis.",
    "cards.physical.order_btn": "Encomendar ({fee})",
    "cards.physical.ordering": "A encomendar…",
    "cards.physical.order_cta": "Pedir um cartão físico",
    "cards.physical.choose_type": "Escolha o seu cartão",
    "cards.physical.features": "Vantagens",
    "cards.buy_virtual_cta": "Comprar um cartão virtual",
    "notif.none": "Nenhuma notificação de momento.",
    "notif.contact_support": "Contactar suporte",
    "profile.change_photo": "Alterar a foto",
    "profile.remove_photo": "Remover a foto",
    "profile.email": "E-mail",
    "profile.phone": "Telefone",
    "profile.country": "País",
    "profile.address": "Morada",
    "profile.notifs_title": "Notificações ({count})",
    "profile.no_notifs": "Nenhuma notificação.",
    "profile.logout": "Terminar sessão",
    "profile.photo_updated": "Foto de perfil atualizada",
    "profile.photo_removed": "Foto removida",
    "profile.photo_too_large": "Imagem demasiado grande (máx. 1 MB)",
    "profile.powered": "Desenvolvido por {bank}",
    "iban.title": "O meu IBAN",
    "iban.loading": "A carregar…",
    "iban.bic": "Código BIC / SWIFT",
    "iban.share_info": "Partilhe estes dados para receber uma transferência na sua conta.",
    "iban.copy": "Copiar",
    "iban.copied": "Copiado para a área de transferência",
    "iban.copy_fail": "Impossível copiar",
    "iban.close": "Fechar",
    "accounts.all": "Todas as suas contas abertas no {bank}.",
    "accounts.primary": "Conta principal",
    "accounts.savings": "Conta poupança",
    "accounts.primary_tag": "Principal",
    "accounts.secondary_tag": "Secundária",
    "accounts.objective": "Objetivo: {amount} · {pct}%",
    "accounts.copy": "Copiar",
    "accounts.loading": "A carregar…",
    "accounts.iban_copied": "IBAN copiado",
    "accounts.copy_fail": "Impossível copiar",
    "savings.total": "Total poupado",
    "savings.target_total": "Objetivo total",
    "savings.progress": "Progresso",
    "savings.active": "Objetivos ativos",
    "savings.new_goal": "Novo objetivo",
    "savings.none": "Nenhum objetivo de poupança de momento.",
    "savings.none_sub": "Crie o seu primeiro objetivo para começar.",
    "savings.saved": "Poupado:",
    "savings.goal": "Objetivo:",
    "savings.deadline": "Prazo: {date}",
    "savings.deposit": "Depositar",
    "savings.withdraw": "Levantar",
    "savings.loading": "A carregar…",
    "savings.form.title": "Novo objetivo de poupança",
    "savings.form.cancel": "Cancelar",
    "savings.form.create": "Criar",
    "savings.form.name": "Nome do objetivo",
    "savings.form.name_ph": "Viagem ao Japão, Carro, …",
    "savings.form.target": "Montante alvo",
    "savings.form.initial": "Depósito inicial",
    "savings.form.deadline": "Data limite (opcional)",
    "savings.form.color": "Cor",
    "savings.deposit_title": "Depositar em \"{name}\"",
    "savings.deposit_amount": "Montante a depositar",
    "savings.balance_hint": "Saldo principal: {amount}",
    "savings.withdraw_prompt": "Levantar quanto de \"{name}\"?",
    "savings.close_confirm": "Encerrar o objetivo \"{name}\"? A poupança será transferida para a sua conta principal.",
    "savings.toast.created": "Objetivo criado",
    "savings.toast.deposited": "Depósito efetuado",
    "savings.toast.withdrawn": "Levantamento efetuado",
    "savings.toast.closed": "Objetivo encerrado",
    "invest.total": "Total investido",
    "invest.profits": "Lucros acumulados",
    "invest.count": "Investimentos",
    "invest.products": "Produtos disponíveis",
    "invest.invest_btn": "Investir",
    "invest.my": "Os meus investimentos",
    "invest.pending": "Pendente",
    "invest.validated": "Validado",
    "invest.refused": "Recusado",
    "invest.withdraw.btn": "Resgatar",
    "invest.withdraw.title": "Resgatar meu investimento",
    "invest.withdraw.intro": "Está prestes a resgatar o seu investimento \"{product}\". O capital inicial e os lucros acumulados serão creditados no seu saldo principal.",
    "invest.withdraw.principal": "Capital inicial",
    "invest.withdraw.profit": "Lucros",
    "invest.withdraw.total": "Total a receber",
    "invest.withdraw.confirm": "Confirmar resgate",
    "invest.withdraw.warning": "Esta ação é irreversível. O investimento será encerrado.",
    "invest.withdraw.success": "Investimento resgatado — {amount} creditados no saldo.",
    "invest.withdrawn": "Resgatado",
    "invest.cta_sub": "Escolha o tipo de investimento que mais lhe convém. Um consultor irá acompanhá-lo desde o seu pedido.",
    "invest.form.amount": "Montante a investir",
    "invest.form.balance": "Saldo disponível: {amount}",
    "invest.form.note": "Nota para o seu consultor (opcional)",
    "invest.form.note_ph": "Indique as suas expectativas…",
    "invest.form.advisor_info": "Será aberta automaticamente uma conversa com o seu consultor.",
    "invest.form.cancel": "Cancelar",
    "invest.form.submit": "Enviar o pedido",
    "invest.toast.requested": "Pedido registado — foi aberta uma conversa com o seu consultor.",
    "invest.p1.name": "Imobiliário",
    "invest.p1.desc": "SCPI, papel-pedra, crowdfunding imobiliário",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Financiamento participativo de projetos selecionados",
    "invest.p3.name": "Energ. renováveis",
    "invest.p3.desc": "Solar, eólica e projetos verdes",
    "invest.p4.name": "Bolsa",
    "invest.p4.desc": "Ações, ETF e fundos diversificados",
    "invest.p5.name": "Participativo",
    "invest.p5.desc": "Empréstimos a PME e empresários locais",
    "invest.p6.name": "Cripto",
    "invest.p6.desc": "Bitcoin, Ethereum e carteira multimoeda",
    "msg.online": "Em linha",
    "msg.loading": "A carregar…",
    "msg.none": "Nenhuma mensagem. Inicie a conversa com o seu consultor.",
    "msg.placeholder": "Escreva a sua mensagem…",
    "msg.attach.image": "Imagem",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Áudio",
    "msg.attach.file": "Ficheiro / Documento",
    "msg.pdf_doc": "Documento PDF",
    "msg.file": "Ficheiro",
    "msg.too_large": "Ficheiro demasiado grande (máx. {size}).",
    "msg.withdraw": "Retirar",
    "credits.cta_sub": "Faça o seu pedido diretamente em linha — resposta em 24h.",
    "credits.learn_more": "Saber mais",
    "credits.request": "Pedir",
    "credits.history": "Os meus pedidos",
    "credits.status.pending": "Pendente",
    "credits.status.accepted": "Aceite",
    "credits.status.refused": "Recusado",
    "credits.chars": "Características",
    "credits.amount_range": "Montante: {min}€ – {max}€",
    "credits.rate_range": "Taxa de juro: {min}% – {max}% ao ano",
    "credits.duration_range": "Duração: {min} a {max} meses",
    "credits.deposit_min": "Depósito inicial: {amount}€ mínimo",
    "credits.garantie": "Garantia: {garantie}",
    "credits.eligibility": "Condições de elegibilidade",
    "credits.elig.income": "Comprovar rendimentos regulares e estáveis",
    "credits.elig.deposit": "Efetuar um depósito inicial de {amount}€",
    "credits.elig.garantie": "Apresentar a garantia: {garantie}",
    "credits.form.title": "Pedido — {name}",
    "credits.form.cancel": "Cancelar",
    "credits.form.submit": "Enviar o pedido",
    "credits.form.amount": "Montante pretendido (€)",
    "credits.form.duration": "Duração (meses)",
    "credits.form.reason": "Motivo (opcional)",
    "credits.form.reason_ph": "Indique o seu projeto…",
    "credits.form.close": "Fechar",
    "credits.toast.submitted": "Pedido de crédito enviado — receberá uma resposta em 24h.",
    "credits.p1.name": "Crédito Pessoal",
    "credits.p1.desc": "Para financiar as necessidades do dia a dia ou os projetos pessoais.",
    "credits.p2.name": "Crédito Habitação",
    "credits.p2.desc": "Para financiar a compra ou a construção de um bem imobiliário.",
    "credits.p3.name": "Crédito Auto / Mota",
    "credits.p3.desc": "Compre um veículo com toda a tranquilidade.",
    "credits.p4.name": "Crédito Estudante",
    "credits.p4.desc": "Um apoio financeiro para os estudos superiores.",
    "credits.p5.name": "Crédito Profissional",
    "credits.p5.desc": "Para apoiar o crescimento de uma atividade profissional.",
    "credits.p6.name": "Crédito Expresso",
    "credits.p6.desc": "Uma solução rápida para as necessidades urgentes.",
    "financing.reimbursable": "Financiamento reembolsável",
    "financing.reimbursable_desc": "Um apoio financeiro com condições fixas para lançar ou desenvolver uma atividade.",
    "financing.non_reimbursable": "Financiamento não reembolsável",
    "financing.non_reimbursable_desc": "Para projetos de impacto social, educativo ou humanitário (ONG, associações).",
    "financing.cta_sub": "Soluções adaptadas para concretizar as suas ambições.",
    "financing.request": "Pedir um financiamento",
    "financing.history": "Os meus pedidos",
    "financing.reimbursable_label": "Reembolsável",
    "financing.non_reimbursable_label": "Não reembolsável",
    "financing.status.pending": "Pendente",
    "financing.status.granted": "Concedido",
    "financing.status.refused": "Recusado",
    "financing.form.title": "Pedido — {name}",
    "financing.form.cancel": "Cancelar",
    "financing.form.submit": "Enviar o pedido",
    "financing.form.project": "Nome do projeto",
    "financing.form.project_ph": "O meu projeto",
    "financing.form.amount": "Montante pedido (€)",
    "financing.form.sector": "Setor",
    "financing.form.sector_ph": "Tecnologia, agricultura, …",
    "financing.form.description": "Descrição do projeto",
    "financing.form.description_ph": "Apresente o seu projeto, os seus objetivos e o seu impacto esperado…",
    "settings.security": "Segurança",
    "settings.current_pw": "Palavra-passe atual",
    "settings.new_pw": "Nova palavra-passe",
    "settings.confirm_pw": "Confirmar",
    "settings.submit": "Alterar a palavra-passe",
    "settings.no_match": "As palavras-passe não coincidem",
    "settings.changed": "Palavra-passe alterada",
    "common.cancel": "Cancelar",
    "common.confirm": "Confirmar",
    "common.close": "Fechar",
    "common.save": "Guardar",
    "common.loading": "A carregar…",
    "common.send": "Enviar",
    "common.total": "Total",
    "admin.wallet.balance": "Saldo disponível",
    "admin.wallet.history": "Histórico",
    "admin.wallet.no_history": "Sem transações",
    "admin.wallet.title": "Minha carteira",
    "admin.wallet.topup": "Recarregar",
    "beneficiary.pick": "Selecionar",
    "beneficiary.save_this": "Guardar este beneficiário",
    "beneficiary.saved": "Beneficiários guardados",
    "beneficiary.saved_ok": "Beneficiário guardado",
    "card.color.amber": "Âmbar",
    "card.color.cyan": "Ciano",
    "card.color.emerald": "Esmeralda",
    "card.color.forest": "Floresta",
    "card.color.gold": "Ouro",
    "card.color.lavender": "Lavanda",
    "card.color.night": "Noite",
    "card.color.orange": "Laranja",
    "card.color.pink": "Rosa",
    "card.color.royal": "Azul real",
    "card.color.ruby": "Rubi",
    "card.color.slate": "Ardósia",
    "card.color.steel": "Aço",
    "card.color.terracotta": "Terracota",
    "card.color.violet": "Violeta",
    "common.other": "Outro",
    "common.search": "Pesquisar",
    "content.translated": "Conteúdo traduzido automaticamente",
    "credits.monthly_payment": "Prestação mensal estimada",
    "credits.rate_annual": "/ ano",
    "credits.rate_used": "Taxa estimativa:",
    "credits.simulation": "Simulação de prestação mensal",
    "credits.total_cost": "Custo total estimado",
    "demo.banner": "Modo demonstração — nenhuma ação real",
    "demo.disabled": "Ação não disponível em modo demo",
    "deposit.default_instructions": "Contacte o seu gestor para as modalidades de depósito.",
    "error.back_home": "Voltar ao início",
    "error.bank_not_found": "Banco não encontrado",
    "error.unknown": "Erro desconhecido",
    "kyc.file_read_error": "Leitura do arquivo impossível",
    "kyc.file_too_large": "Arquivo muito grande (máx. 5 MB)",
    "login.history": "Histórico de conexões",
    "login.history.empty": "Nenhuma conexão registrada.",
    "notif.dismiss": "Fechar",
    "notif.locked": "Mensagem do banco",
    "tracking.error.not_found": "Número de rastreio não encontrado. Verifique e tente novamente.",
    "tracking.label": "Rastreio",
    "tracking.status.delivered": "Entregue",
    "tracking.status.preparation": "Em preparação",
    "tracking.status.shipped": "Enviado",
    "tracking.status.transit": "Em trânsito",
    "transfer.code.copied": "Copiado",
    "transfer.code.copy": "Copiar",
    "transfer.code.received": "O seu código de validação",
    "dash.welcome": "Bem-vindo",
    "common.delete": "Excluir",
    "cards.delete.title": "Excluir cartão",
    "cards.delete.confirm_msg": "Esta ação é irreversível. O cartão será excluído permanentemente.",
    "cards.delete.physical_tooltip": "Cartão físico encomendado — exclusão impossível",
    "cards.order.confirmed": "Pedido confirmado!",
    "cards.order.confirmed_body": "Seu cartão físico está sendo preparado. Use este número para rastrear sua entrega.",
    "cards.order.tracking_no": "N° de rastreamento",
    "cards.fee_deducted": "{amount} será debitado do seu saldo",
    "cards.insufficient_short": "Saldo insuficiente ({available} disponível)",
    "transfer.section.beneficiary": "Beneficiário",
    "transfer.section.details": "Transferência",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banco",
    "transfer.field.city": "Cidade",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banco:",
    "transfer.progress.paused": "Aguardando validação",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Imobiliário",
    "home.invest.p.realestate_desc": "SCPI, crowdfunding imobiliário, papel-pedra",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Financiamento coletivo de projetos selecionados",
    "home.invest.p.renewable": "Energias renov.",
    "home.invest.p.renewable_desc": "Solar, eólica e projetos verdes",
    "home.invest.p.stocks": "Bolsa",
    "home.invest.p.stocks_desc": "Ações, ETFs e fundos diversificados",
    "home.invest.p.participatory": "Participativo",
    "home.invest.p.participatory_desc": "Empréstimos a PMEs e empreendedores locais",
    "home.invest.p.crypto": "Cripto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum e carteira multimoeda",
    "home.invest.p.variable": "Variável",
    "home.credits.p.personal": "Empréstimo Pessoal",
    "home.credits.p.realestate": "Empréstimo Imobiliário",
    "home.credits.p.auto": "Empréstimo Auto/Moto",
    "home.credits.p.student": "Empréstimo Estudante",
    "home.credits.p.pro": "Empréstimo Profissional",
    "home.credits.p.express": "Empréstimo Expresso",
    "home.credits.rate.from": "desde {rate}/ano",

    "cards.deleted": "Cartão excluído",

    "cards.feat.gold_exclusive": "Cartão dourado exclusivo",

    "cards.feat.cashback_05": "Cashback 0,5%",

    "cards.feat.buyer_protection": "Proteção do comprador",

    "cards.feat.support_standard": "Suporte padrão",

    "cards.feat.plastic_premium": "Cartão plástico premium",

    "cards.feat.cashback_1": "Cashback 1%",

    "cards.feat.travel_standard": "Seguro de viagem padrão",

    "cards.feat.support_priority": "Suporte prioritário",

    "cards.feat.metal_brushed": "Cartão em metal escovado",

    "cards.feat.cashback_2_unlimited": "Cashback 2% ilimitado",

    "cards.feat.travel_premium": "Seguro de viagem premium",

    "cards.feat.vip_lounge": "Acesso a salas VIP",

    "cards.feat.support_247": "Assistência 24/7",

    "auth.demo.title": "Login do administrador",

    "auth.demo.subtitle": "Insira suas credenciais de admin para visualizar a demonstração",
    "credits.garantie.none": "Nenhuma",
    "credits.garantie.mortgage": "Hipoteca sobre o bem financiado",
    "credits.garantie.vehicle": "Veículo financiado",
    "credits.garantie.parental": "Parental ou bancária",
    "credits.garantie.professional": "Ativos profissionais ou caução pessoal",
    "cards.reveal.pin_prompt": "Introduza o seu código PIN de 4 dígitos para apresentar as informações do cartão.",
    "cards.reveal.show": "Apresentar",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Os dois códigos PIN não coincidem",
    "auth.pin.digit_hint": "4 dígitos",
  
  },

  it: {
    "notif.transfer_code.title": "Codice di convalida del bonifico",
    "notif.transfer_code.body": "Copia questo codice per convalidare il bonifico.",
    "notif.transfer_code.expires_in": "Scompare tra {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mesi",
    "credits.n_months": "{n} mesi",
    "msg.read": "Letto",
    "msg.sent": "Inviato",
    "msg.attach_aria": "Allega file",
    "login.device.unknown": "Browser sconosciuto",
    "login.device.mobile": "Mobile",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Computer",
    "login.recent": "Recente",
    "err.invalid_credentials": "Credenziali non valide",
    "err.bad_pin": "PIN errato",
    "err.account_blocked": "Account bloccato",
    "err.bad_current_pw": "La password attuale non è corretta",
    "err.insufficient_funds": "Fondi insufficienti, ricarica il tuo conto.",
    "err.amount_above_savings": "Importo superiore al risparmio disponibile",
    "err.must_be_positive": "Il valore deve essere positivo",
    "err.invalid_format": "Formato non valido",
    "err.email_taken": "Email già in uso",
    "err.card_not_found": "Carta non trovata",
    "err.physical_card_already_ordered": "Carta fisica già ordinata",
    "err.beneficiary_not_found": "Beneficiario non trovato",
    "err.invalid_code": "Codice non valido",
    "err.no_transfer_code": "Nessun codice per questo bonifico",
    "err.link_expired": "Link non valido o scaduto",
    "err.thread_not_found": "Conversazione non trovata",
    "err.empty_message": "Messaggio vuoto",
    "err.file_too_large": "File troppo grande (max 20 MB)",
    "err.forbidden": "Accesso negato",
    "err.bank_not_found": "Banca non trovata",
    "err.signups_closed": "Le iscrizioni sono chiuse per questa banca.",
    "err.access_suspended": "Accesso temporaneamente sospeso. Contatta la tua banca.",
    "err.notif_not_closable": "Questa notifica non può essere chiusa.",
    "auth.signup.tagline": "Unisciti a noi oggi",
    "auth.login.tagline": "Benvenuto!",
    "auth.signup.tagline_sub": "Apri il tuo conto in 2 minuti e accedi a tutti i nostri servizi bancari premium.",
    "auth.login.tagline_sub": "Accedi al tuo spazio personale e gestisci le tue finanze in totale sicurezza.",
    "auth.back": "Torna alla home",
    "auth.bullet.security": "Sicurezza bancaria di livello militare",
    "auth.bullet.transfers": "Bonifici istantanei 24/24",
    "auth.bullet.investments": "Investimenti e risparmio integrati",
    "auth.bullet.advisor": "Consulente personale disponibile 7 giorni su 7",
    "auth.footer": "Powered by {bank}",
    "auth.signup.title": "Crea un account",
    "auth.login.title": "Accesso",
    "auth.field.lastName": "Cognome",
    "auth.field.firstName": "Nomi",
    "auth.field.country": "Paese",
    "auth.field.country.placeholder": "— Seleziona il tuo paese —",
    "auth.field.phone": "Telefono",
    "auth.field.address": "Indirizzo",
    "auth.field.address.placeholder": "Via Roma 123, Milano",
    "auth.field.email": "Email",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Password",
    "auth.submit.signup": "Crea il mio account",
    "auth.submit.login": "Accedi",
    "auth.login.badge": "Accedi al tuo account",
    "auth.card.tagline": "Il tuo spazio cliente",
    "auth.submit.loading": "Caricamento…",
    "auth.pin.title": "Codice PIN",
    "auth.pin.subtitle": "Inserisci il tuo Codice PIN di 4 cifre",
    "auth.pin.label": "Codice PIN",
    "auth.pin.confirm": "Conferma Codice PIN",
    "auth.pin.submit": "Conferma",
    "auth.pin.back": "Torna al login",
    "auth.pin.length_error": "Il Codice PIN deve contenere 4 cifre",
    "auth.switch.have_account": "Hai già un account?",
    "auth.switch.login_link": "Accesso",
    "auth.switch.no_account": "Non sei ancora cliente?",
    "auth.error.select_country": "Seleziona il tuo paese",
    "kyc.title": "Verifica dell'identità (KYC)",
    "kyc.sub": "Carica i tuoi documenti per convalidare il tuo conto. Tutti i documenti sono crittografati e confidenziali.",
    "kyc.id_type": "Tipo di documento d'identità *",
    "kyc.id_card": "Carta d'identità",
    "kyc.passport": "Passaporto",
    "kyc.driver_license": "Patente",
    "kyc.recto_passport": "Fronte del passaporto *",
    "kyc.recto_id": "Fronte del documento *",
    "kyc.verso": "Retro del documento *",
    "kyc.selfie": "Selfie con il documento d'identità in mano *",
    "kyc.address_proof": "Prova di residenza *",
    "kyc.hint_doc": "Immagine o PDF, max 5 MB",
    "kyc.hint_selfie": "Foto nitida, viso e documento visibili",
    "kyc.hint_address": "Fattura, estratto conto, attestato… (max 5 MB)",
    "kyc.upload": "Carica",
    "kyc.replace": "Sostituisci",
    "pending.title": "Conto in attesa",
    "pending.body": "La tua richiesta è stata ricevuta. Un amministratore verificherà il tuo dossier e attiverà il tuo conto a breve.",
    "pending.back": "Torna all'accesso",
    "home.nav.features": "Funzionalità",
    "home.nav.security": "Sicurezza",
    "home.nav.invest": "Investimento",
    "home.nav.credits": "Crediti",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Accedi",
    "home.hero.tagline": "Banca online di nuova generazione",
    "home.hero.title1": "Gestisci il tuo denaro",
    "home.hero.title2": "con intelligenza",
    "home.hero.sub": "Una piattaforma bancaria completa — bonifici, risparmio, investimenti e crediti dal tuo smartphone.",
    "home.hero.login": "Accedi",
    "home.hero.trust.secure": "100% Sicuro",
    "home.hero.trust.fast": "Apertura rapida",
    "home.hero.trust.encrypted": "Dati crittografati",
    "home.hero.trust.support": "Servizio 24/7",
    "home.mockup.balance": "Saldo disponibile",
    "home.mockup.currency": "EUR · Conto principale",
    "home.mockup.recharge": "Ricarica",
    "home.mockup.send": "Invia",
    "home.mockup.save": "Risparmia",
    "home.mockup.salary": "Stipendio di marzo",
    "home.mockup.rent": "Affitto di aprile",
    "home.mockup.investment": "Investimento",
    "home.mockup.days2": "2 giorni fa",
    "home.mockup.days3": "3 giorni fa",
    "home.mockup.days5": "5 giorni fa",
    "home.stats.accounts": "Conti attivi",
    "home.stats.uptime": "Disponibilità",
    "home.stats.transfers": "Bonifici",
    "home.stats.support": "Supporto",
    "home.features.label": "Funzionalità",
    "home.features.title": "Tutto ciò di cui hai bisogno",
    "home.features.sub": "Una piattaforma completa progettata per semplificare la gestione delle tue finanze quotidiane.",
    "home.feature.security": "Sicurezza militare",
    "home.feature.security_body": "Crittografia AES-256, autenticazione a doppio fattore e sorveglianza 24/7 del tuo conto.",
    "home.feature.cards": "Carte virtuale e fisica",
    "home.feature.cards_body": "Emetti carte in pochi secondi o ordina una vera carta fisica consegnata a casa tua.",
    "home.feature.transfers": "Bonifici express",
    "home.feature.transfers_body": "Invia e ricevi denaro in tutto il mondo in pochi secondi.",
    "home.feature.analytics": "Analisi in tempo reale",
    "home.feature.analytics_body": "Dashboard intelligenti per monitorare le tue spese e anticipare le tue finanze.",
    "home.feature.investments": "Investimenti integrati",
    "home.feature.investments_body": "Accedi a prodotti di investimento selezionati direttamente dal tuo conto.",
    "home.feature.support": "Supporto dedicato 24/7",
    "home.feature.support_body": "Un consulente bancario personale disponibile in qualsiasi momento tramite messaggistica sicura.",
    "home.tpl.label": "Fiducia",
    "home.tpl.title": "Si fidano di noi",
    "home.mockup.balance_label": "Saldo",
    "home.mockup.virement": "Bonifico",
    "home.mockup.salaire": "Stipendio",
    "home.security.label": "Sicurezza",
    "home.security.title1": "Le tue finanze",
    "home.security.title2": "in totale sicurezza",
    "home.security.sub": "Applichiamo gli standard di sicurezza bancaria più rigorosi per proteggere ogni centesimo.",
    "home.security.aes.title": "Crittografia AES-256",
    "home.security.aes.body": "Tutti i dati sensibili sono crittografati con gli stessi algoritmi utilizzati dalle grandi banche internazionali.",
    "home.security.auth.title": "Autenticazione rafforzata",
    "home.security.auth.body": "Doppia verifica dell'identità a ogni accesso e per ogni operazione sensibile sul tuo conto.",
    "home.security.compliance.title": "Conformità DSP2/RGPD",
    "home.security.compliance.body": "Rispettiamo tutte le normative europee in materia di servizi di pagamento e protezione dei dati.",
    "home.invest.label": "Investimenti",
    "home.invest.title": "Fai fruttare il tuo denaro",
    "home.invest.sub": "6 categorie di investimento selezionate per il loro potenziale e la loro affidabilità.",
    "home.invest.yield": "Rendimento",
    "home.credits.label": "Crediti",
    "home.credits.title": "Soluzioni adatte a ogni progetto",
    "home.credits.sub": "Tassi competitivi, risposte rapide e accompagnamento personalizzato per tutti i tuoi progetti.",
    "home.credits.rate": "Tasso",
    "home.faq.label": "FAQ",
    "home.faq.title": "Domande frequenti",
    "home.faq.q1": "Come aprire un conto?",
    "home.faq.a1": "L'iscrizione richiede meno di 2 minuti. Basta inserire i tuoi dati personali, scegliere una password e il tuo conto sarà attivato dopo la convalida del nostro team.",
    "home.faq.q2": "I miei dati sono sicuri?",
    "home.faq.a2": "Sì. Tutti i dati sono crittografati con AES-256 a riposo e TLS 1.3 in transito. Rispettiamo gli standard bancari internazionali e le normative RGPD/DSP2.",
    "home.faq.q3": "Quali tipi di investimenti proponete?",
    "home.faq.a3": "Proponiamo 6 categorie: Immobiliare, Crowdfunding, Energie rinnovabili, Borsa, Partecipativo e Cripto. Ogni prodotto è selezionato per il suo rapporto rischio/rendimento.",
    "home.faq.q4": "Come richiedere un credito?",
    "home.faq.a4": "Vai nella sezione Credito della tua area cliente, scegli il tipo di prestito e indica l'importo e la durata. La tua richiesta è trattata in 24h dai nostri consulenti.",
    "home.faq.q5": "Ci sono costi nascosti?",
    "home.faq.a5": "Nessun costo nascosto. Tutte le tariffe sono visualizzate chiaramente a ogni operazione. Le condizioni sono accessibili nella tua area cliente in qualsiasi momento.",
    "home.faq.q6": "Come contattare il supporto?",
    "home.faq.a6": "Usa la messaggistica integrata nella tua area cliente per contattare direttamente il tuo consulente bancario personale, disponibile 24/24 e 7/7.",
    "home.cta.title": "Pronto a iniziare?",
    "home.cta.sub": "Unisciti a migliaia di utenti che gestiscono le loro finanze con {bank}. L'iscrizione richiede meno di 2 minuti.",
    "home.footer.products": "Prodotti",
    "home.footer.services": "Servizi",
    "home.footer.access": "Accesso rapido",
    "home.footer.accounts": "Conti",
    "home.footer.transfers": "Bonifici",
    "home.footer.cards": "Carte",
    "home.footer.savings": "Risparmio",
    "home.footer.investments": "Investimenti",
    "home.footer.credits": "Crediti",
    "home.footer.financing": "Finanziamento",
    "home.footer.messages": "Messaggi",
    "home.footer.login": "Accesso",
    "home.footer.signup": "Apri un conto",
    "home.footer.support": "Supporto 24/7",
    "home.footer.tagline": "La tua banca online, disponibile ovunque e in qualsiasi momento.",
    "home.footer.copyright": "© {year} {bank} — Tutti i diritti riservati",
    "home.footer.powered": "Powered by {bank}",
    "app.menu_label": "Menu principale",
    "app.nav.dashboard": "Dashboard",
    "app.nav.accounts": "Conti",
    "app.nav.transactions": "Transazioni",
    "app.nav.transfers": "Bonifici",
    "app.nav.cards": "Carte",
    "app.nav.savings": "Risparmio",
    "app.nav.investments": "Investimento",
    "app.nav.credit": "Credito",
    "app.nav.financing": "Finanziamento",
    "app.nav.messages": "Messaggi",
    "app.nav.card_tracking": "Tracciamento carta",
    "app.nav.notifications": "Notifiche",
    "app.nav.settings": "Impostazioni",
    "app.nav.profile": "Profilo",
    "app.nav.mobile.home": "Home",
    "app.nav.mobile.transfers": "Bonifici",
    "app.nav.mobile.cards": "Carte",
    "app.nav.mobile.transactions": "Transazioni",
    "app.nav.more.invest": "Investi",
    "app.user_role": "Cliente",
    "app.logout": "Disconnessione",
    "app.search": "Cerca…",
    "app.more_title": "Altre funzionalità",
    "app.more_btn": "Altro",
    "app.loading": "Caricamento…",
    "app.error_back": "Torna all'accesso",
    "app.see_more_notifs": "Visualizza le altre {count} notifiche →",
    "page.accounts": "I miei conti",
    "page.transactions": "Transazioni",
    "page.transfers": "Nuovo bonifico",
    "page.deposit": "Deposito",
    "page.cards": "Le mie carte",
    "page.savings": "Risparmio",
    "page.investments": "Investimento",
    "page.credits": "Credito",
    "page.financing": "Finanziamento di progetti",
    "page.messages": "Messaggi",
    "page.settings": "Impostazioni",
    "page.notifications": "Notifiche importanti",
    "page.profile": "Il mio profilo",
    "dash.greeting": "Ciao, {name}",
    "dash.overview": "Panoramica delle tue finanze — {date}",
    "dash.total_balance": "Saldo totale",
    "dash.this_month": "+{amount} questo mese",
    "dash.view_iban": "Vedi il mio IBAN",
    "dash.my_accounts": "I miei conti",
    "dash.primary_account": "Conto principale",
    "dash.savings_account": "Conto di risparmio",
    "dash.remunerated": "Remunerato",
    "dash.details": "Dettagli",
    "dash.transfer_btn": "Bonifico",
    "dash.quick_actions": "Azioni rapide",
    "dash.quick.deposit": "Deposito",
    "deposit.title": "Informazioni sul deposito",
    "dash.quick.transfer": "Bonifico",
    "dash.quick.history": "Cronologia",
    "dash.quick.cards": "Le mie carte",
    "dash.quick.profile": "Profilo",
    "dash.monthly_budget": "Budget mensile",
    "dash.income": "Entrate",
    "dash.expenses": "Uscite",
    "dash.spending_cat": "Spese per categoria",
    "dash.no_spending": "Nessuna spesa registrata.",
    "dash.recent_tx": "Transazioni recenti",
    "dash.see_all": "Vedi tutto",
    "dash.no_tx": "Nessuna operazione.",
    "dash.main_card": "La mia carta principale",
    "dash.manage": "Gestisci",
    "dash.block": "Blocca",
    "dash.pin": "Codice PIN",
    "dash.security": "Sicurezza",
    "dash.more": "Altro",
    "dash.no_card": "Nessuna carta emessa",
    "dash.monthly_activity": "Attività del mese",
    "dash.operations": "Operazioni",
    "dash.holder": "Titolare",
    "dash.expires": "Scad.",
    "dash.visa_label": "VISA",
    "dash.balance": "Saldo disponibile",
    "tx.all": "Tutte",
    "tx.income": "Entrate",
    "tx.expenses": "Uscite",
    "tx.today": "Oggi",
    "tx.yesterday": "Ieri",
    "tx.none": "Nessuna transazione.",
    "transfer.title": "Bonifico rapido",
    "transfer.recipient": "Destinatario",
    "transfer.recipient_ph": "Nome, IBAN, numero di telefono…",
    "transfer.amount": "Importo",
    "transfer.label": "Causale",
    "transfer.reason": "Motivo (opzionale)",
    "transfer.reason_ph": "Es.: affitto, rimborso…",
    "transfer.execution": "Esecuzione",
    "transfer.immediate": "Immediata",
    "transfer.delayed": "Differita",
    "transfer.balance": "Saldo disponibile:",
    "transfer.continue": "Continua",
    "transfer.confirm": "Conferma",
    "transfer.cancel": "Annulla",
    "transfer.code": "Codice di convalida",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Inserisci il codice ricevuto per convalidare il bonifico",
    "transfer.pending.title": "Bonifico in attesa di convalida",
    "transfer.pending.amount": "Importo:",
    "transfer.pending.to": "Verso:",
    "transfer.pending.hint": "Il tuo codice di validazione sarà inviato via email o tramite la campanella delle notifiche. Il codice è composto da 6 cifre. Se non lo ricevi, contatta il supporto.",
    "transfer.pending.validate": "Convalida il bonifico",
    "transfer.saved": "Bonifico registrato. Richiedi il codice al tuo amministratore per convalidare.",
    "transfer.done": "Bonifico effettuato",
    "transfer.validated": "Bonifico convalidato!",
    "transfer.progress.processing": "Elaborazione del bonifico…",
    "transfer.progress.completed": "Bonifico eseguito!",
    "transfer.progress.default_message": "Il tuo bonifico è in elaborazione. Attendere prego.",
    "cards.none": "Nessuna carta emessa.\nContatta il tuo amministratore.",
    "cards.holder": "Titolare",
    "cards.expires": "Scad.",
    "cards.block": "Blocca",
    "cards.pin": "Codice PIN",
    "cards.more": "Altro",
    "cards.limits": "Massimali della carta",
    "cards.payment": "Pagamento",
    "cards.withdrawal": "Prelievo",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Bloccata",
    "cards.status_active": "Attiva",
    "cards.toast.blocked": "Carta bloccata",
    "cards.toast.unblocked": "Carta sbloccata",
    "cards.toast.physical_ordered": "Carta fisica ordinata! Consegna in 5–10 giorni lavorativi.",
    "cards.toast.created": "Carta generata con successo!",
    "cards.none_sub": "Nessuna carta emessa. Genera la tua carta virtuale ora.",
    "cards.generate_btn": "Genera la mia carta",
    "cards.create.title": "Genera la mia carta virtuale",
    "cards.create.network": "Circuito di pagamento",
    "cards.create.color": "Colore della carta",
    "cards.create.preview_name": "IL TUO NOME",
    "cards.create.preview_expiry": "MM/AA",
    "cards.create.loading": "Generazione…",
    "cards.create.submit": "Genera carta",
    "cards.btn.unblock": "Sblocca",
    "cards.btn.block": "Blocca",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fisica",
    "cards.btn.physical_done": "Ordinata",
    "cards.limits_title": "Limiti",
    "cards.reveal.title": "Informazioni della carta",
    "cards.reveal.number": "Numero completo",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Scadenza",
    "cards.reveal.warning": "Non condividere mai queste informazioni. Chiudi questa finestra dopo l'uso.",
    "cards.pin.title": "Codice PIN",
    "cards.pin.message": "Questo codice PIN è utilizzato per tutti i tuoi pagamenti online e prelievi. Non condividerlo mai con nessuno.",
    "cards.physical.title": "Ordina carta fisica",
    "cards.physical.fee_label": "Spese di spedizione",
    "cards.physical.balance_label": "Il tuo saldo",
    "cards.physical.insufficient": "Saldo insufficiente. Per favore ricarica il tuo conto prima di ordinare.",
    "cards.physical.info": "Le spese saranno addebitate dal tuo conto. Consegna in 5–10 giorni lavorativi.",
    "cards.physical.order_btn": "Ordina ({fee})",
    "cards.physical.ordering": "Ordinando…",
    "cards.physical.order_cta": "Ordina una carta fisica",
    "cards.physical.choose_type": "Scegli la tua carta",
    "cards.physical.features": "Vantaggi",
    "cards.buy_virtual_cta": "Acquista una carta virtuale",
    "notif.none": "Nessuna notifica al momento.",
    "notif.contact_support": "Contatta il supporto",
    "profile.change_photo": "Cambia foto",
    "profile.remove_photo": "Rimuovi foto",
    "profile.email": "Email",
    "profile.phone": "Telefono",
    "profile.country": "Paese",
    "profile.address": "Indirizzo",
    "profile.notifs_title": "Notifiche ({count})",
    "profile.no_notifs": "Nessuna notifica.",
    "profile.logout": "Disconnessione",
    "profile.photo_updated": "Foto del profilo aggiornata",
    "profile.photo_removed": "Foto rimossa",
    "profile.photo_too_large": "Immagine troppo grande (max 1 MB)",
    "profile.powered": "Powered by {bank}",
    "iban.title": "Il mio IBAN",
    "iban.loading": "Caricamento…",
    "iban.bic": "Codice BIC / SWIFT",
    "iban.share_info": "Condividi queste coordinate per ricevere un bonifico sul tuo conto.",
    "iban.copy": "Copia",
    "iban.copied": "Copiato negli appunti",
    "iban.copy_fail": "Impossibile copiare",
    "iban.close": "Chiudi",
    "accounts.all": "Tutti i tuoi conti aperti presso {bank}.",
    "accounts.primary": "Conto principale",
    "accounts.savings": "Conto di risparmio",
    "accounts.primary_tag": "Principale",
    "accounts.secondary_tag": "Secondario",
    "accounts.objective": "Obiettivo: {amount} · {pct}%",
    "accounts.copy": "Copia",
    "accounts.loading": "Caricamento…",
    "accounts.iban_copied": "IBAN copiato",
    "accounts.copy_fail": "Impossibile copiare",
    "savings.total": "Totale risparmiato",
    "savings.target_total": "Obiettivo totale",
    "savings.progress": "Avanzamento",
    "savings.active": "Obiettivi attivi",
    "savings.new_goal": "Nuovo obiettivo",
    "savings.none": "Nessun obiettivo di risparmio al momento.",
    "savings.none_sub": "Crea il tuo primo obiettivo per iniziare.",
    "savings.saved": "Risparmiato:",
    "savings.goal": "Obiettivo:",
    "savings.deadline": "Scadenza: {date}",
    "savings.deposit": "Deposita",
    "savings.withdraw": "Preleva",
    "savings.loading": "Caricamento…",
    "savings.form.title": "Nuovo obiettivo di risparmio",
    "savings.form.cancel": "Annulla",
    "savings.form.create": "Crea",
    "savings.form.name": "Nome dell'obiettivo",
    "savings.form.name_ph": "Viaggio in Giappone, Auto, …",
    "savings.form.target": "Importo obiettivo",
    "savings.form.initial": "Deposito iniziale",
    "savings.form.deadline": "Data limite (opzionale)",
    "savings.form.color": "Colore",
    "savings.deposit_title": "Deposita su \"{name}\"",
    "savings.deposit_amount": "Importo da depositare",
    "savings.balance_hint": "Saldo principale: {amount}",
    "savings.withdraw_prompt": "Quanto prelevare da \"{name}\"?",
    "savings.close_confirm": "Chiudere l'obiettivo \"{name}\"? Il risparmio sarà trasferito sul tuo conto principale.",
    "savings.toast.created": "Obiettivo creato",
    "savings.toast.deposited": "Deposito effettuato",
    "savings.toast.withdrawn": "Prelievo effettuato",
    "savings.toast.closed": "Obiettivo chiuso",
    "invest.total": "Totale investito",
    "invest.profits": "Guadagni cumulati",
    "invest.count": "Investimenti",
    "invest.products": "Prodotti disponibili",
    "invest.invest_btn": "Investi",
    "invest.my": "I miei investimenti",
    "invest.pending": "In attesa",
    "invest.validated": "Convalidato",
    "invest.refused": "Rifiutato",
    "invest.withdraw.btn": "Riscatta",
    "invest.withdraw.title": "Riscatta il mio investimento",
    "invest.withdraw.intro": "Stai per riscattare il tuo investimento «{product}». Il capitale iniziale e i profitti accumulati saranno accreditati sul tuo saldo principale.",
    "invest.withdraw.principal": "Capitale iniziale",
    "invest.withdraw.profit": "Profitti",
    "invest.withdraw.total": "Totale da ricevere",
    "invest.withdraw.confirm": "Conferma riscatto",
    "invest.withdraw.warning": "Questa azione è irreversibile. L'investimento sarà chiuso.",
    "invest.withdraw.success": "Investimento riscattato — {amount} accreditati sul saldo.",
    "invest.withdrawn": "Riscattato",
    "invest.cta_sub": "Scegli il tipo di investimento più adatto a te. Un consulente ti accompagnerà fin dalla tua richiesta.",
    "invest.form.amount": "Importo da investire",
    "invest.form.balance": "Saldo disponibile: {amount}",
    "invest.form.note": "Nota per il tuo consulente (opzionale)",
    "invest.form.note_ph": "Specifica le tue aspettative…",
    "invest.form.advisor_info": "Sarà aperta automaticamente una conversazione con il tuo consulente.",
    "invest.form.cancel": "Annulla",
    "invest.form.submit": "Invia la richiesta",
    "invest.toast.requested": "Richiesta registrata — è stata aperta una conversazione con il tuo consulente.",
    "invest.p1.name": "Immobiliare",
    "invest.p1.desc": "SCPI, carta-mattone, crowdfunding immobiliare",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Finanziamento partecipativo di progetti selezionati",
    "invest.p3.name": "Energie rinnov.",
    "invest.p3.desc": "Solare, eolico e progetti green",
    "invest.p4.name": "Borsa",
    "invest.p4.desc": "Azioni, ETF e fondi diversificati",
    "invest.p5.name": "Partecipativo",
    "invest.p5.desc": "Prestiti a PME e imprenditori locali",
    "invest.p6.name": "Cripto",
    "invest.p6.desc": "Bitcoin, Ethereum e portafoglio multivaluta",
    "msg.online": "Online",
    "msg.loading": "Caricamento…",
    "msg.none": "Nessun messaggio. Avvia la conversazione con il tuo consulente.",
    "msg.placeholder": "Scrivi il tuo messaggio…",
    "msg.attach.image": "Immagine",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "File / Documento",
    "msg.pdf_doc": "Documento PDF",
    "msg.file": "File",
    "msg.too_large": "File troppo grande (max {size}).",
    "msg.withdraw": "Ritira",
    "credits.cta_sub": "Effettua la tua richiesta direttamente online — risposta entro 24h.",
    "credits.learn_more": "Scopri di più",
    "credits.request": "Richiedi",
    "credits.history": "Le mie richieste",
    "credits.status.pending": "In attesa",
    "credits.status.accepted": "Accettato",
    "credits.status.refused": "Rifiutato",
    "credits.chars": "Caratteristiche",
    "credits.amount_range": "Importo: {min}€ – {max}€",
    "credits.rate_range": "Tasso d'interesse: {min}% – {max}% annuo",
    "credits.duration_range": "Durata: da {min} a {max} mesi",
    "credits.deposit_min": "Deposito iniziale: {amount}€ minimo",
    "credits.garantie": "Garanzia: {garantie}",
    "credits.eligibility": "Condizioni di ammissibilità",
    "credits.elig.income": "Dimostrare entrate regolari e stabili",
    "credits.elig.deposit": "Effettuare un deposito iniziale di {amount}€",
    "credits.elig.garantie": "Presentare la garanzia: {garantie}",
    "credits.form.title": "Richiesta — {name}",
    "credits.form.cancel": "Annulla",
    "credits.form.submit": "Invia la richiesta",
    "credits.form.amount": "Importo desiderato (€)",
    "credits.form.duration": "Durata (mesi)",
    "credits.form.reason": "Motivo (opzionale)",
    "credits.form.reason_ph": "Specifica il tuo progetto…",
    "credits.form.close": "Chiudi",
    "credits.toast.submitted": "Richiesta di credito inviata — riceverai una risposta entro 24h.",
    "credits.p1.name": "Prestito Personale",
    "credits.p1.desc": "Per finanziare le esigenze quotidiane o i progetti personali.",
    "credits.p2.name": "Mutuo Immobiliare",
    "credits.p2.desc": "Per finanziare l'acquisto o la costruzione di un bene immobiliare.",
    "credits.p3.name": "Prestito Auto / Moto",
    "credits.p3.desc": "Acquista un veicolo in tutta serenità.",
    "credits.p4.name": "Prestito Studenti",
    "credits.p4.desc": "Un aiuto finanziario per gli studi superiori.",
    "credits.p5.name": "Prestito Professionale",
    "credits.p5.desc": "Per sostenere la crescita di un'attività professionale.",
    "credits.p6.name": "Prestito Express",
    "credits.p6.desc": "Una soluzione rapida per le esigenze urgenti.",
    "financing.reimbursable": "Finanziamento rimborsabile",
    "financing.reimbursable_desc": "Un sostegno finanziario con condizioni fisse per avviare o sviluppare un'attività.",
    "financing.non_reimbursable": "Finanziamento non rimborsabile",
    "financing.non_reimbursable_desc": "Per progetti a impatto sociale, educativo o umanitario (ONG, associazioni).",
    "financing.cta_sub": "Soluzioni adatte per concretizzare le tue ambizioni.",
    "financing.request": "Richiedi un finanziamento",
    "financing.history": "Le mie richieste",
    "financing.reimbursable_label": "Rimborsabile",
    "financing.non_reimbursable_label": "Non rimborsabile",
    "financing.status.pending": "In attesa",
    "financing.status.granted": "Concesso",
    "financing.status.refused": "Rifiutato",
    "financing.form.title": "Richiesta — {name}",
    "financing.form.cancel": "Annulla",
    "financing.form.submit": "Invia la richiesta",
    "financing.form.project": "Nome del progetto",
    "financing.form.project_ph": "Il mio progetto",
    "financing.form.amount": "Importo richiesto (€)",
    "financing.form.sector": "Settore",
    "financing.form.sector_ph": "Tech, agricoltura, …",
    "financing.form.description": "Descrizione del progetto",
    "financing.form.description_ph": "Presenta il tuo progetto, i suoi obiettivi e il suo impatto previsto…",
    "settings.security": "Sicurezza",
    "settings.current_pw": "Password attuale",
    "settings.new_pw": "Nuova password",
    "settings.confirm_pw": "Conferma",
    "settings.submit": "Cambia la password",
    "settings.no_match": "Le password non corrispondono",
    "settings.changed": "Password modificata",
    "common.cancel": "Annulla",
    "common.confirm": "Conferma",
    "common.close": "Chiudi",
    "common.save": "Salva",
    "common.loading": "Caricamento…",
    "common.send": "Invia",
    "common.total": "Totale",
    "admin.wallet.balance": "Saldo disponibile",
    "admin.wallet.history": "Storico",
    "admin.wallet.no_history": "Nessuna transazione",
    "admin.wallet.title": "Il mio portafoglio",
    "admin.wallet.topup": "Ricarica",
    "beneficiary.pick": "Seleziona",
    "beneficiary.save_this": "Salva questo beneficiario",
    "beneficiary.saved": "Beneficiari salvati",
    "beneficiary.saved_ok": "Beneficiario salvato",
    "card.color.amber": "Ambra",
    "card.color.cyan": "Ciano",
    "card.color.emerald": "Smeraldo",
    "card.color.forest": "Foresta",
    "card.color.gold": "Oro",
    "card.color.lavender": "Lavanda",
    "card.color.night": "Notte",
    "card.color.orange": "Arancione",
    "card.color.pink": "Rosa",
    "card.color.royal": "Blu reale",
    "card.color.ruby": "Rubino",
    "card.color.slate": "Ardesia",
    "card.color.steel": "Acciaio",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Viola",
    "common.other": "Altro",
    "common.search": "Cerca",
    "content.translated": "Contenuto tradotto automaticamente",
    "credits.monthly_payment": "Rata mensile stimata",
    "credits.rate_annual": "/ anno",
    "credits.rate_used": "Tasso stimato:",
    "credits.simulation": "Simulazione rata mensile",
    "credits.total_cost": "Costo totale stimato",
    "demo.banner": "Modalità dimostrazione — nessuna azione reale",
    "demo.disabled": "Azione non disponibile in modalità demo",
    "deposit.default_instructions": "Contatta il tuo gestore per le modalità di deposito.",
    "error.back_home": "Torna alla home",
    "error.bank_not_found": "Banca non trovata",
    "error.unknown": "Errore sconosciuto",
    "kyc.file_read_error": "Impossibile leggere il file",
    "kyc.file_too_large": "File troppo grande (max 5 MB)",
    "login.history": "Storico delle connessioni",
    "login.history.empty": "Nessuna connessione registrata.",
    "notif.dismiss": "Chiudi",
    "notif.locked": "Messaggio della banca",
    "tracking.error.not_found": "Numero di tracciamento non trovato. Verifica e riprova.",
    "tracking.label": "Tracciamento",
    "tracking.status.delivered": "Consegnato",
    "tracking.status.preparation": "In preparazione",
    "tracking.status.shipped": "Spedito",
    "tracking.status.transit": "In transito",
    "transfer.code.copied": "Copiato",
    "transfer.code.copy": "Copia",
    "transfer.code.received": "Il tuo codice di validazione",
    "dash.welcome": "Benvenuto",
    "common.delete": "Elimina",
    "cards.delete.title": "Elimina carta",
    "cards.delete.confirm_msg": "Questa azione è irreversibile. La carta verrà eliminata definitivamente.",
    "cards.delete.physical_tooltip": "Carta fisica ordinata — eliminazione impossibile",
    "cards.order.confirmed": "Ordine confermato!",
    "cards.order.confirmed_body": "La tua carta fisica è in preparazione. Usa questo numero per tracciare la tua consegna.",
    "cards.order.tracking_no": "N° di tracciamento",
    "cards.fee_deducted": "{amount} sarà dedotto dal tuo saldo",
    "cards.insufficient_short": "Saldo insufficiente ({available} disponibile)",
    "transfer.section.beneficiary": "Beneficiario",
    "transfer.section.details": "Bonifico",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banca",
    "transfer.field.city": "Città",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banca:",
    "transfer.progress.paused": "In attesa di convalida",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Immobiliare",
    "home.invest.p.realestate_desc": "SCPI, crowdfunding immobiliare, carta-pietra",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Finanziamento partecipativo di progetti selezionati",
    "home.invest.p.renewable": "Energie rinnov.",
    "home.invest.p.renewable_desc": "Solare, eolico e progetti verdi",
    "home.invest.p.stocks": "Borsa",
    "home.invest.p.stocks_desc": "Azioni, ETF e fondi diversificati",
    "home.invest.p.participatory": "Partecipativo",
    "home.invest.p.participatory_desc": "Prestiti a PMI e imprenditori locali",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum e portafoglio multivaluta",
    "home.invest.p.variable": "Variabile",
    "home.credits.p.personal": "Prestito Personale",
    "home.credits.p.realestate": "Mutuo Casa",
    "home.credits.p.auto": "Prestito Auto/Moto",
    "home.credits.p.student": "Prestito Studenti",
    "home.credits.p.pro": "Prestito Professionale",
    "home.credits.p.express": "Prestito Express",
    "home.credits.rate.from": "da {rate}/anno",

    "cards.deleted": "Carta eliminata",

    "cards.feat.gold_exclusive": "Carta oro esclusiva",

    "cards.feat.cashback_05": "Cashback 0,5%",

    "cards.feat.buyer_protection": "Protezione acquirente",

    "cards.feat.support_standard": "Supporto standard",

    "cards.feat.plastic_premium": "Carta in plastica premium",

    "cards.feat.cashback_1": "Cashback 1%",

    "cards.feat.travel_standard": "Assicurazione viaggio standard",

    "cards.feat.support_priority": "Supporto prioritario",

    "cards.feat.metal_brushed": "Carta in metallo spazzolato",

    "cards.feat.cashback_2_unlimited": "Cashback 2% illimitato",

    "cards.feat.travel_premium": "Assicurazione viaggio premium",

    "cards.feat.vip_lounge": "Accesso a lounge VIP",

    "cards.feat.support_247": "Assistenza 24/7",

    "auth.demo.title": "Accesso amministratore",

    "auth.demo.subtitle": "Inserisci le credenziali admin per visualizzare la demo",
    "credits.garantie.none": "Nessuna",
    "credits.garantie.mortgage": "Ipoteca sul bene finanziato",
    "credits.garantie.vehicle": "Veicolo finanziato",
    "credits.garantie.parental": "Parentale o bancaria",
    "credits.garantie.professional": "Attività professionali o garanzia personale",
    "cards.reveal.pin_prompt": "Inserisci il tuo codice PIN a 4 cifre per visualizzare le informazioni della carta.",
    "cards.reveal.show": "Visualizza",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "I due codici PIN non corrispondono",
    "auth.pin.digit_hint": "4 cifre",
  
  },

  ca: {
    "notif.transfer_code.title": "Codi de validació de transferència",
    "notif.transfer_code.body": "Copia aquest codi per validar la transferència.",
    "notif.transfer_code.expires_in": "Desapareix en {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mesos",
    "credits.n_months": "{n} mesos",
    "msg.read": "Llegit",
    "msg.sent": "Enviat",
    "msg.attach_aria": "Adjuntar fitxer",
    "login.device.unknown": "Navegador desconegut",
    "login.device.mobile": "Mòbil",
    "login.device.tablet": "Tauleta",
    "login.device.desktop": "Ordinador",
    "login.recent": "Recent",
    "err.invalid_credentials": "Credencials no vàlides",
    "err.bad_pin": "PIN incorrecte",
    "err.account_blocked": "Compte bloquejat",
    "err.bad_current_pw": "La contrasenya actual és incorrecta",
    "err.insufficient_funds": "Fons insuficients, recarregueu el compte.",
    "err.amount_above_savings": "Import superior a l'estalvi disponible",
    "err.must_be_positive": "El valor ha de ser positiu",
    "err.invalid_format": "Format no vàlid",
    "err.email_taken": "Correu ja utilitzat",
    "err.card_not_found": "Targeta no trobada",
    "err.physical_card_already_ordered": "Targeta física ja demanada",
    "err.beneficiary_not_found": "Beneficiari no trobat",
    "err.invalid_code": "Codi no vàlid",
    "err.no_transfer_code": "Cap codi per a aquesta transferència",
    "err.link_expired": "Enllaç no vàlid o caducat",
    "err.thread_not_found": "Conversa no trobada",
    "err.empty_message": "Missatge buit",
    "err.file_too_large": "Fitxer massa gran (màx 20 MB)",
    "err.forbidden": "Accés denegat",
    "err.bank_not_found": "Banc no trobat",
    "err.signups_closed": "Els registres estan tancats per a aquest banc.",
    "err.access_suspended": "Accés temporalment suspès. Contacti amb el seu banc.",
    "err.notif_not_closable": "Aquesta notificació no es pot tancar.",
    "auth.signup.tagline": "Uneix-te a nosaltres avui",
    "auth.login.tagline": "Benvingut!",
    "auth.signup.tagline_sub": "Obre el teu compte en 2 minuts i accedeix a tots els nostres serveis bancaris premium.",
    "auth.login.tagline_sub": "Accedeix al teu espai personal i gestiona les teves finances amb total seguretat.",
    "auth.back": "Tornar a l'inici",
    "auth.bullet.security": "Seguretat bancària de nivell militar",
    "auth.bullet.transfers": "Transferències instantànies 24/7",
    "auth.bullet.investments": "Inversions i estalvi integrats",
    "auth.bullet.advisor": "Assessor personal disponible 7 dies a la setmana",
    "auth.footer": "Impulsat per {bank}",
    "auth.signup.title": "Crear un compte",
    "auth.login.title": "Iniciar sessió",
    "auth.field.lastName": "Cognom",
    "auth.field.firstName": "Noms",
    "auth.field.country": "País",
    "auth.field.country.placeholder": "— Selecciona el teu país —",
    "auth.field.phone": "Telèfon",
    "auth.field.address": "Adreça",
    "auth.field.address.placeholder": "Carrer Major 123, Barcelona",
    "auth.field.email": "Correu electrònic",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Contrasenya",
    "auth.submit.signup": "Crear el meu compte",
    "auth.submit.login": "Connectar-se",
    "auth.login.badge": "Connecteu-vos al vostre compte",
    "auth.card.tagline": "El vostre espai de client",
    "auth.submit.loading": "Carregant…",
    "auth.pin.title": "Codi PIN",
    "auth.pin.subtitle": "Introduïu el vostre Codi PIN de 4 xifres",
    "auth.pin.label": "Codi PIN",
    "auth.pin.confirm": "Confirmar Codi PIN",
    "auth.pin.submit": "Confirmar",
    "auth.pin.back": "Tornar a l’inici de sessió",
    "auth.pin.length_error": "El Codi PIN ha de tenir 4 xifres",
    "auth.switch.have_account": "Ja tens compte?",
    "auth.switch.login_link": "Iniciar sessió",
    "auth.switch.no_account": "Encara no ets client?",
    "auth.error.select_country": "Selecciona el teu país",
    "kyc.title": "Verificació d'identitat (KYC)",
    "kyc.sub": "Puja els teus documents per validar el teu compte. Tots els documents estan xifrats i són confidencials.",
    "kyc.id_type": "Tipus de document d'identitat *",
    "kyc.id_card": "DNI",
    "kyc.passport": "Passaport",
    "kyc.driver_license": "Carnet de conduir",
    "kyc.recto_passport": "Anvers del passaport *",
    "kyc.recto_id": "Anvers del document *",
    "kyc.verso": "Revers del document *",
    "kyc.selfie": "Selfie amb el document d'identitat a la mà *",
    "kyc.address_proof": "Justificant de domicili *",
    "kyc.hint_doc": "Imatge o PDF, màx. 5 MB",
    "kyc.hint_selfie": "Foto clara, cara i document visibles",
    "kyc.hint_address": "Factura, extracte, certificat… (màx. 5 MB)",
    "kyc.upload": "Pujar",
    "kyc.replace": "Substituir",
    "pending.title": "Compte pendent",
    "pending.body": "Hem rebut la teva sol·licitud. Un administrador revisarà el teu expedient i activarà el teu compte aviat.",
    "pending.back": "Tornar a l'inici de sessió",
    "home.nav.features": "Funcionalitats",
    "home.nav.security": "Seguretat",
    "home.nav.invest": "Inversió",
    "home.nav.credits": "Crèdits",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Iniciar sessió",
    "home.hero.tagline": "Banca en línia de nova generació",
    "home.hero.title1": "Gestiona els teus diners",
    "home.hero.title2": "amb intel·ligència",
    "home.hero.sub": "Una plataforma bancària completa — transferències, estalvi, inversions i crèdits des del teu smartphone.",
    "home.hero.login": "Iniciar sessió",
    "home.hero.trust.secure": "100% Segur",
    "home.hero.trust.fast": "Obertura ràpida",
    "home.hero.trust.encrypted": "Dades xifrades",
    "home.hero.trust.support": "Servei 24/7",
    "home.mockup.balance": "Saldo disponible",
    "home.mockup.currency": "EUR · Compte principal",
    "home.mockup.recharge": "Recarregar",
    "home.mockup.send": "Enviar",
    "home.mockup.save": "Estalviar",
    "home.mockup.salary": "Sou de març",
    "home.mockup.rent": "Lloguer d'abril",
    "home.mockup.investment": "Inversió",
    "home.mockup.days2": "fa 2 dies",
    "home.mockup.days3": "fa 3 dies",
    "home.mockup.days5": "fa 5 dies",
    "home.stats.accounts": "Comptes actius",
    "home.stats.uptime": "Disponibilitat",
    "home.stats.transfers": "Transferències",
    "home.stats.support": "Suport",
    "home.features.label": "Funcionalitats",
    "home.features.title": "Tot el que necessites",
    "home.features.sub": "Una plataforma completa dissenyada per simplificar la gestió de les teves finances cada dia.",
    "home.feature.security": "Seguretat militar",
    "home.feature.security_body": "Xifratge AES-256, autenticació de doble factor i vigilància 24/7 del teu compte.",
    "home.feature.cards": "Targetes virtual i física",
    "home.feature.cards_body": "Emet targetes en segons o demana una targeta física real lliurada a casa teva.",
    "home.feature.transfers": "Transferències exprés",
    "home.feature.transfers_body": "Envia i rep diners a qualsevol lloc del món en segons.",
    "home.feature.analytics": "Anàlisi en temps real",
    "home.feature.analytics_body": "Taulers intel·ligents per fer el seguiment de les teves despeses i anticipar les teves finances.",
    "home.feature.investments": "Inversions integrades",
    "home.feature.investments_body": "Accedeix a productes d'inversió seleccionats directament des del teu compte.",
    "home.feature.support": "Suport dedicat 24/7",
    "home.feature.support_body": "Un assessor bancari personal disponible en qualsevol moment mitjançant missatgeria segura.",
    "home.tpl.label": "Confiança",
    "home.tpl.title": "Confien en nosaltres",
    "home.mockup.balance_label": "Saldo",
    "home.mockup.virement": "Transferència",
    "home.mockup.salaire": "Sou",
    "home.security.label": "Seguretat",
    "home.security.title1": "Les teves finances",
    "home.security.title2": "amb seguretat absoluta",
    "home.security.sub": "Apliquem els estàndards de seguretat bancària més estrictes per protegir cada cèntim.",
    "home.security.aes.title": "Xifratge AES-256",
    "home.security.aes.body": "Totes les dades sensibles es xifren amb els mateixos algorismes utilitzats pels grans bancs internacionals.",
    "home.security.auth.title": "Autenticació reforçada",
    "home.security.auth.body": "Doble verificació d'identitat a cada connexió i per a cada operació sensible al teu compte.",
    "home.security.compliance.title": "Conformitat DSP2/RGPD",
    "home.security.compliance.body": "Complim totes les normatives europees en matèria de serveis de pagament i protecció de dades.",
    "home.invest.label": "Inversions",
    "home.invest.title": "Fes créixer els teus diners",
    "home.invest.sub": "6 categories d'inversió seleccionades pel seu potencial i la seva fiabilitat.",
    "home.invest.yield": "Rendiment",
    "home.credits.label": "Crèdits",
    "home.credits.title": "Solucions adaptades a cada projecte",
    "home.credits.sub": "Tipus competitius, respostes ràpides i acompanyament personalitzat per a tots els teus projectes.",
    "home.credits.rate": "Tipus",
    "home.faq.label": "FAQ",
    "home.faq.title": "Preguntes freqüents",
    "home.faq.q1": "Com obrir un compte?",
    "home.faq.a1": "El registre triga menys de 2 minuts. Només cal introduir les teves dades personals, triar una contrasenya i el teu compte s'activarà després de la validació del nostre equip.",
    "home.faq.q2": "Les meves dades estan segures?",
    "home.faq.a2": "Sí. Totes les dades es xifren amb AES-256 en repòs i TLS 1.3 en trànsit. Complim les normes bancàries internacionals i les normatives RGPD/DSP2.",
    "home.faq.q3": "Quins tipus d'inversions oferiu?",
    "home.faq.a3": "Oferim 6 categories: Immobiliari, Crowdfunding, Energies renovables, Borsa, Participatiu i Cripto. Cada producte es selecciona per la seva relació risc/rendiment.",
    "home.faq.q4": "Com sol·licitar un crèdit?",
    "home.faq.a4": "Ves a la secció Crèdit de la teva àrea de client, tria el tipus de préstec i indica l'import i la durada. El teu expedient es tramita en 24h pels nostres assessors.",
    "home.faq.q5": "Hi ha comissions ocultes?",
    "home.faq.a5": "Cap comissió oculta. Tots els preus es mostren clarament a cada operació. Les condicions són accessibles a la teva àrea de client en qualsevol moment.",
    "home.faq.q6": "Com contactar el suport?",
    "home.faq.a6": "Utilitza la missatgeria integrada a la teva àrea de client per contactar directament amb el teu assessor bancari personal, disponible 24h/24 i 7 dies a la setmana.",
    "home.cta.title": "Preparat per començar?",
    "home.cta.sub": "Uneix-te a milers d'usuaris que gestionen les seves finances amb {bank}. El registre triga menys de 2 minuts.",
    "home.footer.products": "Productes",
    "home.footer.services": "Serveis",
    "home.footer.access": "Accés ràpid",
    "home.footer.accounts": "Comptes",
    "home.footer.transfers": "Transferències",
    "home.footer.cards": "Targetes",
    "home.footer.savings": "Estalvi",
    "home.footer.investments": "Inversions",
    "home.footer.credits": "Crèdits",
    "home.footer.financing": "Finançament",
    "home.footer.messages": "Missatges",
    "home.footer.login": "Iniciar sessió",
    "home.footer.signup": "Obrir un compte",
    "home.footer.support": "Suport 24/7",
    "home.footer.tagline": "La teva banca en línia, disponible a tot arreu i en qualsevol moment.",
    "home.footer.copyright": "© {year} {bank} — Tots els drets reservats",
    "home.footer.powered": "Impulsat per {bank}",
    "app.menu_label": "Menú principal",
    "app.nav.dashboard": "Tauler de control",
    "app.nav.accounts": "Comptes",
    "app.nav.transactions": "Transaccions",
    "app.nav.transfers": "Transferències",
    "app.nav.cards": "Targetes",
    "app.nav.savings": "Estalvi",
    "app.nav.investments": "Inversió",
    "app.nav.credit": "Crèdit",
    "app.nav.financing": "Finançament",
    "app.nav.messages": "Missatges",
    "app.nav.card_tracking": "Seguiment de targeta",
    "app.nav.notifications": "Notificacions",
    "app.nav.settings": "Configuració",
    "app.nav.profile": "Perfil",
    "app.nav.mobile.home": "Inici",
    "app.nav.mobile.transfers": "Transferències",
    "app.nav.mobile.cards": "Targetes",
    "app.nav.mobile.transactions": "Transaccions",
    "app.nav.more.invest": "Invertir",
    "app.user_role": "Client",
    "app.logout": "Tancar sessió",
    "app.search": "Cercar…",
    "app.more_title": "Més funcionalitats",
    "app.more_btn": "Més",
    "app.loading": "Carregant…",
    "app.error_back": "Tornar a l'inici de sessió",
    "app.see_more_notifs": "Veure les {count} notificacions restants →",
    "page.accounts": "Els meus comptes",
    "page.transactions": "Transaccions",
    "page.transfers": "Nova transferència",
    "page.deposit": "Dipòsit",
    "page.cards": "Les meves targetes",
    "page.savings": "Estalvi",
    "page.investments": "Inversió",
    "page.credits": "Crèdit",
    "page.financing": "Finançament de projectes",
    "page.messages": "Missatges",
    "page.settings": "Configuració",
    "page.notifications": "Notificacions importants",
    "page.profile": "El meu perfil",
    "dash.greeting": "Hola, {name}",
    "dash.overview": "Resum de les teves finances — {date}",
    "dash.total_balance": "Saldo total",
    "dash.this_month": "+{amount} aquest mes",
    "dash.view_iban": "Veure el meu IBAN",
    "dash.my_accounts": "Els meus comptes",
    "dash.primary_account": "Compte principal",
    "dash.savings_account": "Compte d'estalvi",
    "dash.remunerated": "Remunerat",
    "dash.details": "Detalls",
    "dash.transfer_btn": "Transferència",
    "dash.quick_actions": "Accions ràpides",
    "dash.quick.deposit": "Dipòsit",
    "deposit.title": "Informació del dipòsit",
    "dash.quick.transfer": "Transferència",
    "dash.quick.history": "Historial",
    "dash.quick.cards": "Les meves targetes",
    "dash.quick.profile": "Perfil",
    "dash.monthly_budget": "Pressupost mensual",
    "dash.income": "Entrades",
    "dash.expenses": "Despeses",
    "dash.spending_cat": "Despeses per categoria",
    "dash.no_spending": "Cap despesa registrada.",
    "dash.recent_tx": "Transaccions recents",
    "dash.see_all": "Veure-ho tot",
    "dash.no_tx": "Cap operació.",
    "dash.main_card": "La meva targeta principal",
    "dash.manage": "Gestionar",
    "dash.block": "Bloquejar",
    "dash.pin": "Codi PIN",
    "dash.security": "Seguretat",
    "dash.more": "Més",
    "dash.no_card": "Cap targeta emesa",
    "dash.monthly_activity": "Activitat del mes",
    "dash.operations": "Operacions",
    "dash.holder": "Titular",
    "dash.expires": "Cad.",
    "dash.visa_label": "VISA",
    "dash.balance": "Saldo disponible",
    "tx.all": "Totes",
    "tx.income": "Entrades",
    "tx.expenses": "Sortides",
    "tx.today": "Avui",
    "tx.yesterday": "Ahir",
    "tx.none": "Cap transacció.",
    "transfer.title": "Transferència ràpida",
    "transfer.recipient": "Destinatari",
    "transfer.recipient_ph": "Nom, IBAN, número de telèfon…",
    "transfer.amount": "Import",
    "transfer.label": "Concepte",
    "transfer.reason": "Motiu (opcional)",
    "transfer.reason_ph": "Ex.: lloguer, reemborsament…",
    "transfer.execution": "Execució",
    "transfer.immediate": "Immediata",
    "transfer.delayed": "Diferida",
    "transfer.balance": "Saldo disponible:",
    "transfer.continue": "Continuar",
    "transfer.confirm": "Confirmar",
    "transfer.cancel": "Cancel·lar",
    "transfer.code": "Codi de validació",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Introdueix el codi rebut per validar la transferència",
    "transfer.pending.title": "Transferència pendent de validació",
    "transfer.pending.amount": "Import:",
    "transfer.pending.to": "Cap a:",
    "transfer.pending.hint": "El teu codi de validació s'enviarà per correu electrònic o mitjançant la campana de notificacions. El codi consta de 6 dígits. Si no el reps, contacta el suport.",
    "transfer.pending.validate": "Validar la transferència",
    "transfer.saved": "Transferència registrada. Demana el codi al teu administrador per validar.",
    "transfer.done": "Transferència efectuada",
    "transfer.validated": "Transferència validada!",
    "transfer.progress.processing": "Processant la transferència…",
    "transfer.progress.completed": "Transferència realitzada!",
    "transfer.progress.default_message": "La teva transferència s'està processant. Si us plau, espera.",
    "cards.none": "Cap targeta emesa.\nContacta el teu administrador.",
    "cards.holder": "Titular",
    "cards.expires": "Cad.",
    "cards.block": "Bloquejar",
    "cards.pin": "Codi PIN",
    "cards.more": "Més",
    "cards.limits": "Límits de la targeta",
    "cards.payment": "Pagament",
    "cards.withdrawal": "Retirada",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Bloquejada",
    "cards.status_active": "Activa",
    "cards.toast.blocked": "Targeta bloquejada",
    "cards.toast.unblocked": "Targeta desbloqueijada",
    "cards.toast.physical_ordered": "Targeta física demanada! Lliurament en 5–10 dies hàbils.",
    "cards.toast.created": "Targeta generada amb èxit!",
    "cards.none_sub": "Cap targeta emesa. Genera la teva targeta virtual ara.",
    "cards.generate_btn": "Genera la meva targeta",
    "cards.create.title": "Genera la meva targeta virtual",
    "cards.create.network": "Xarxa de pagament",
    "cards.create.color": "Color de la targeta",
    "cards.create.preview_name": "EL TEU NOM",
    "cards.create.preview_expiry": "MM/AA",
    "cards.create.loading": "Generant…",
    "cards.create.submit": "Genera la targeta",
    "cards.btn.unblock": "Desbloquejar",
    "cards.btn.block": "Bloquejar",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Física",
    "cards.btn.physical_done": "Demanada",
    "cards.limits_title": "Límits",
    "cards.reveal.title": "Informació de la targeta",
    "cards.reveal.number": "Número complet",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Venciment",
    "cards.reveal.warning": "Mai comparteixis aquesta informació. Tanca aquesta finestra després d'usar-la.",
    "cards.pin.title": "Codi PIN",
    "cards.pin.message": "Aquest codi PIN s'utilitza per a tots els teus pagaments en línia i retirades. Mai el comparteixis amb ningú.",
    "cards.physical.title": "Demanar targeta física",
    "cards.physical.fee_label": "Despeses d'enviament",
    "cards.physical.balance_label": "El teu saldo",
    "cards.physical.insufficient": "Saldo insuficient. Si us plau, recarrega el teu compte abans de demanar.",
    "cards.physical.info": "Les despeses es debitaran del teu compte. Lliurament en 5–10 dies hàbils.",
    "cards.physical.order_btn": "Demanar ({fee})",
    "cards.physical.ordering": "Demanant…",
    "cards.physical.order_cta": "Demanar una targeta física",
    "cards.physical.choose_type": "Tria la teva targeta",
    "cards.physical.features": "Avantatges",
    "cards.buy_virtual_cta": "Comprar una targeta virtual",
    "notif.none": "Cap notificació de moment.",
    "notif.contact_support": "Contactar el suport",
    "profile.change_photo": "Canviar la foto",
    "profile.remove_photo": "Treure la foto",
    "profile.email": "Correu electrònic",
    "profile.phone": "Telèfon",
    "profile.country": "País",
    "profile.address": "Adreça",
    "profile.notifs_title": "Notificacions ({count})",
    "profile.no_notifs": "Cap notificació.",
    "profile.logout": "Tancar sessió",
    "profile.photo_updated": "Foto de perfil actualitzada",
    "profile.photo_removed": "Foto eliminada",
    "profile.photo_too_large": "Imatge massa gran (màx. 1 MB)",
    "profile.powered": "Impulsat per {bank}",
    "iban.title": "El meu IBAN",
    "iban.loading": "Carregant…",
    "iban.bic": "Codi BIC / SWIFT",
    "iban.share_info": "Comparteix aquestes dades per rebre una transferència al teu compte.",
    "iban.copy": "Copiar",
    "iban.copied": "Copiat al porta-retalls",
    "iban.copy_fail": "Impossible copiar",
    "iban.close": "Tancar",
    "accounts.all": "Tots els teus comptes oberts a {bank}.",
    "accounts.primary": "Compte principal",
    "accounts.savings": "Compte d'estalvi",
    "accounts.primary_tag": "Principal",
    "accounts.secondary_tag": "Secundari",
    "accounts.objective": "Objectiu: {amount} · {pct}%",
    "accounts.copy": "Copiar",
    "accounts.loading": "Carregant…",
    "accounts.iban_copied": "IBAN copiat",
    "accounts.copy_fail": "Impossible copiar",
    "savings.total": "Total estalviat",
    "savings.target_total": "Objectiu total",
    "savings.progress": "Progrés",
    "savings.active": "Objectius actius",
    "savings.new_goal": "Nou objectiu",
    "savings.none": "Cap objectiu d'estalvi de moment.",
    "savings.none_sub": "Crea el teu primer objectiu per començar.",
    "savings.saved": "Estalviat:",
    "savings.goal": "Objectiu:",
    "savings.deadline": "Venciment: {date}",
    "savings.deposit": "Dipositar",
    "savings.withdraw": "Retirar",
    "savings.loading": "Carregant…",
    "savings.form.title": "Nou objectiu d'estalvi",
    "savings.form.cancel": "Cancel·lar",
    "savings.form.create": "Crear",
    "savings.form.name": "Nom de l'objectiu",
    "savings.form.name_ph": "Viatge al Japó, Cotxe, …",
    "savings.form.target": "Import objectiu",
    "savings.form.initial": "Dipòsit inicial",
    "savings.form.deadline": "Data límit (opcional)",
    "savings.form.color": "Color",
    "savings.deposit_title": "Dipositar a \"{name}\"",
    "savings.deposit_amount": "Import a dipositar",
    "savings.balance_hint": "Saldo principal: {amount}",
    "savings.withdraw_prompt": "Quant retirar de \"{name}\"?",
    "savings.close_confirm": "Tancar l'objectiu \"{name}\"? L'estalvi es transferirà al teu compte principal.",
    "savings.toast.created": "Objectiu creat",
    "savings.toast.deposited": "Dipòsit efectuat",
    "savings.toast.withdrawn": "Retirada efectuada",
    "savings.toast.closed": "Objectiu tancat",
    "invest.total": "Total invertit",
    "invest.profits": "Beneficis acumulats",
    "invest.count": "Inversions",
    "invest.products": "Productes disponibles",
    "invest.invest_btn": "Invertir",
    "invest.my": "Les meves inversions",
    "invest.pending": "Pendent",
    "invest.validated": "Validat",
    "invest.refused": "Rebutjat",
    "invest.withdraw.btn": "Retirar",
    "invest.withdraw.title": "Retirar la meva inversió",
    "invest.withdraw.intro": "Esteu a punt de retirar la vostra inversió «{product}». El capital inicial i els beneficis acumulats s'abonaran al vostre saldo principal.",
    "invest.withdraw.principal": "Capital inicial",
    "invest.withdraw.profit": "Beneficis",
    "invest.withdraw.total": "Total a rebre",
    "invest.withdraw.confirm": "Confirma la retirada",
    "invest.withdraw.warning": "Aquesta acció és irreversible. La inversió es tancarà.",
    "invest.withdraw.success": "Inversió retirada — {amount} abonats al saldo.",
    "invest.withdrawn": "Retirada",
    "invest.cta_sub": "Tria el tipus d'inversió que més et convingui. Un assessor t'acompanyarà des de la teva sol·licitud.",
    "invest.form.amount": "Import a invertir",
    "invest.form.balance": "Saldo disponible: {amount}",
    "invest.form.note": "Nota per al teu assessor (opcional)",
    "invest.form.note_ph": "Especifica les teves expectatives…",
    "invest.form.advisor_info": "S'obrirà automàticament una conversa amb el teu assessor.",
    "invest.form.cancel": "Cancel·lar",
    "invest.form.submit": "Enviar la sol·licitud",
    "invest.toast.requested": "Sol·licitud registrada — s'ha obert una conversa amb el teu assessor.",
    "invest.p1.name": "Immobiliari",
    "invest.p1.desc": "SCPI, paper-pedra, crowdfunding immobiliari",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Finançament participatiu de projectes seleccionats",
    "invest.p3.name": "Energ. renovables",
    "invest.p3.desc": "Solar, eòlica i projectes verds",
    "invest.p4.name": "Borsa",
    "invest.p4.desc": "Accions, ETF i fons diversificats",
    "invest.p5.name": "Participatiu",
    "invest.p5.desc": "Préstecs a PME i emprenedors locals",
    "invest.p6.name": "Cripto",
    "invest.p6.desc": "Bitcoin, Ethereum i cartera multidivisa",
    "msg.online": "En línia",
    "msg.loading": "Carregant…",
    "msg.none": "Cap missatge. Inicia la conversa amb el teu assessor.",
    "msg.placeholder": "Escriu el teu missatge…",
    "msg.attach.image": "Imatge",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Àudio",
    "msg.attach.file": "Fitxer / Document",
    "msg.pdf_doc": "Document PDF",
    "msg.file": "Fitxer",
    "msg.too_large": "Fitxer massa gran (màx. {size}).",
    "msg.withdraw": "Retirar",
    "credits.cta_sub": "Fes la teva sol·licitud directament en línia — resposta en 24h.",
    "credits.learn_more": "Saber-ne més",
    "credits.request": "Sol·licitar",
    "credits.history": "Les meves sol·licituds",
    "credits.status.pending": "Pendent",
    "credits.status.accepted": "Acceptat",
    "credits.status.refused": "Rebutjat",
    "credits.chars": "Característiques",
    "credits.amount_range": "Import: {min}€ – {max}€",
    "credits.rate_range": "Tipus d'interès: {min}% – {max}% anual",
    "credits.duration_range": "Durada: {min} a {max} mesos",
    "credits.deposit_min": "Dipòsit inicial: {amount}€ mínim",
    "credits.garantie": "Garantia: {garantie}",
    "credits.eligibility": "Condicions d'elegibilitat",
    "credits.elig.income": "Justificar ingressos regulars i estables",
    "credits.elig.deposit": "Efectuar un dipòsit inicial de {amount}€",
    "credits.elig.garantie": "Presentar la garantia: {garantie}",
    "credits.form.title": "Sol·licitud — {name}",
    "credits.form.cancel": "Cancel·lar",
    "credits.form.submit": "Enviar la sol·licitud",
    "credits.form.amount": "Import desitjat (€)",
    "credits.form.duration": "Durada (mesos)",
    "credits.form.reason": "Motiu (opcional)",
    "credits.form.reason_ph": "Especifica el teu projecte…",
    "credits.form.close": "Tancar",
    "credits.toast.submitted": "Sol·licitud de crèdit enviada — rebràs una resposta en 24h.",
    "credits.p1.name": "Préstec Personal",
    "credits.p1.desc": "Per finançar les necessitats del dia a dia o els projectes personals.",
    "credits.p2.name": "Préstec Hipotecari",
    "credits.p2.desc": "Per finançar la compra o la construcció d'un bé immobiliari.",
    "credits.p3.name": "Préstec Auto / Moto",
    "credits.p3.desc": "Compra un vehicle amb tota tranquil·litat.",
    "credits.p4.name": "Préstec Estudiant",
    "credits.p4.desc": "Una ajuda financera per als estudis superiors.",
    "credits.p5.name": "Préstec Professional",
    "credits.p5.desc": "Per donar suport al creixement d'una activitat professional.",
    "credits.p6.name": "Préstec Exprés",
    "credits.p6.desc": "Una solució ràpida per a les necessitats urgents.",
    "financing.reimbursable": "Finançament reemborsable",
    "financing.reimbursable_desc": "Un suport financer amb condicions fixes per llançar o desenvolupar una activitat.",
    "financing.non_reimbursable": "Finançament no reemborsable",
    "financing.non_reimbursable_desc": "Per a projectes d'impacte social, educatiu o humanitari (ONG, associacions).",
    "financing.cta_sub": "Solucions adaptades per concretar les teves ambicions.",
    "financing.request": "Sol·licitar un finançament",
    "financing.history": "Les meves sol·licituds",
    "financing.reimbursable_label": "Reemborsable",
    "financing.non_reimbursable_label": "No reemborsable",
    "financing.status.pending": "Pendent",
    "financing.status.granted": "Concedit",
    "financing.status.refused": "Rebutjat",
    "financing.form.title": "Sol·licitud — {name}",
    "financing.form.cancel": "Cancel·lar",
    "financing.form.submit": "Enviar la sol·licitud",
    "financing.form.project": "Nom del projecte",
    "financing.form.project_ph": "El meu projecte",
    "financing.form.amount": "Import sol·licitat (€)",
    "financing.form.sector": "Sector",
    "financing.form.sector_ph": "Tecnologia, agricultura, …",
    "financing.form.description": "Descripció del projecte",
    "financing.form.description_ph": "Presenta el teu projecte, els seus objectius i el seu impacte esperat…",
    "settings.security": "Seguretat",
    "settings.current_pw": "Contrasenya actual",
    "settings.new_pw": "Nova contrasenya",
    "settings.confirm_pw": "Confirmar",
    "settings.submit": "Canviar la contrasenya",
    "settings.no_match": "Les contrasenyes no coincideixen",
    "settings.changed": "Contrasenya modificada",
    "common.cancel": "Cancel·lar",
    "common.confirm": "Confirmar",
    "common.close": "Tancar",
    "common.save": "Desar",
    "common.loading": "Carregant…",
    "common.send": "Enviar",
    "common.total": "Total",
    "admin.wallet.balance": "Saldo disponible",
    "admin.wallet.history": "Historial",
    "admin.wallet.no_history": "Sin transacciones",
    "admin.wallet.title": "Mi billetera",
    "admin.wallet.topup": "Recargar",
    "beneficiary.pick": "Seleccionar",
    "beneficiary.save_this": "Guardar este beneficiario",
    "beneficiary.saved": "Beneficiarios guardados",
    "beneficiary.saved_ok": "Beneficiario guardado",
    "card.color.amber": "Ámbar",
    "card.color.cyan": "Cian",
    "card.color.emerald": "Esmeralda",
    "card.color.forest": "Bosque",
    "card.color.gold": "Oro",
    "card.color.lavender": "Lavanda",
    "card.color.night": "Noche",
    "card.color.orange": "Naranja",
    "card.color.pink": "Rosa",
    "card.color.royal": "Azul real",
    "card.color.ruby": "Rubí",
    "card.color.slate": "Pizarra",
    "card.color.steel": "Acero",
    "card.color.terracotta": "Terracota",
    "card.color.violet": "Violeta",
    "common.other": "Otro",
    "common.search": "Buscar",
    "content.translated": "Contenido traducido automáticamente",
    "credits.monthly_payment": "Cuota mensual estimada",
    "credits.rate_annual": "/ año",
    "credits.rate_used": "Tasa estimada:",
    "credits.simulation": "Simulación de cuota mensual",
    "credits.total_cost": "Coste total estimado",
    "demo.banner": "Modo demostración — ninguna acción real",
    "demo.disabled": "Acción no disponible en modo demo",
    "deposit.default_instructions": "Contacte a su gestor para los detalles del depósito.",
    "error.back_home": "Volver al inicio",
    "error.bank_not_found": "Banco no encontrado",
    "error.unknown": "Error desconocido",
    "kyc.file_read_error": "No se puede leer el archivo",
    "kyc.file_too_large": "Archivo demasiado grande (máx. 5 MB)",
    "login.history": "Historial de conexiones",
    "login.history.empty": "Ninguna conexión registrada.",
    "notif.dismiss": "Cerrar",
    "notif.locked": "Mensaje del banco",
    "tracking.error.not_found": "Número de seguimiento no encontrado. Verifique e intente de nuevo.",
    "tracking.label": "Seguimiento",
    "tracking.status.delivered": "Entregado",
    "tracking.status.preparation": "En preparación",
    "tracking.status.shipped": "Enviado",
    "tracking.status.transit": "En tránsito",
    "transfer.code.copied": "Copiado",
    "transfer.code.copy": "Copiar",
    "transfer.code.received": "Su código de validación",
    "dash.welcome": "Benvingut",
    "common.delete": "Eliminar",
    "cards.delete.title": "Eliminar targeta",
    "cards.delete.confirm_msg": "Aquesta acció és irreversible. La targeta serà eliminada definitivament.",
    "cards.delete.physical_tooltip": "Targeta física demanada — eliminació impossible",
    "cards.order.confirmed": "Comanda confirmada!",
    "cards.order.confirmed_body": "La seva targeta física s'està preparant. Usi aquest número per seguir el seu lliurament.",
    "cards.order.tracking_no": "Núm. de seguiment",
    "cards.fee_deducted": "{amount} es deduirà del seu saldo",
    "cards.insufficient_short": "Saldo insuficient ({available} disponible)",
    "transfer.section.beneficiary": "Beneficiari",
    "transfer.section.details": "Transferència",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banc",
    "transfer.field.city": "Ciutat",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banc:",
    "transfer.progress.paused": "Pendent de validació",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Immobiliari",
    "home.invest.p.realestate_desc": "SCPI, crowdfunding immobiliari, paper-pedra",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Finançament participatiu de projectes seleccionats",
    "home.invest.p.renewable": "Energies renov.",
    "home.invest.p.renewable_desc": "Solar, eòlica i projectes verds",
    "home.invest.p.stocks": "Borsa",
    "home.invest.p.stocks_desc": "Accions, ETF i fons diversificats",
    "home.invest.p.participatory": "Participatiu",
    "home.invest.p.participatory_desc": "Préstecs a PIMES i emprenedors locals",
    "home.invest.p.crypto": "Cripto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum i cartera multidivisa",
    "home.invest.p.variable": "Variable",
    "home.credits.p.personal": "Préstec Personal",
    "home.credits.p.realestate": "Préstec Hipotecari",
    "home.credits.p.auto": "Préstec Auto/Moto",
    "home.credits.p.student": "Préstec Estudiant",
    "home.credits.p.pro": "Préstec Professional",
    "home.credits.p.express": "Préstec Express",
    "home.credits.rate.from": "des de {rate}/any",

    "cards.deleted": "Targeta eliminada",

    "cards.feat.gold_exclusive": "Targeta daurada exclusiva",

    "cards.feat.cashback_05": "Cashback 0,5%",

    "cards.feat.buyer_protection": "Protecció del comprador",

    "cards.feat.support_standard": "Suport estàndard",

    "cards.feat.plastic_premium": "Targeta plàstica premium",

    "cards.feat.cashback_1": "Cashback 1%",

    "cards.feat.travel_standard": "Assegurança de viatge estàndard",

    "cards.feat.support_priority": "Suport prioritari",

    "cards.feat.metal_brushed": "Targeta de metall raspallat",

    "cards.feat.cashback_2_unlimited": "Cashback 2% il·limitat",

    "cards.feat.travel_premium": "Assegurança de viatge premium",

    "cards.feat.vip_lounge": "Accés a sales VIP",

    "cards.feat.support_247": "Assistència 24/7",

    "auth.demo.title": "Inici de sessió d'administrador",

    "auth.demo.subtitle": "Introduïu les credencials d'administrador per veure la demostració",
    "credits.garantie.none": "Cap",
    "credits.garantie.mortgage": "Hipoteca sobre el bé finançat",
    "credits.garantie.vehicle": "Vehicle finançat",
    "credits.garantie.parental": "Parental o bancària",
    "credits.garantie.professional": "Actius professionals o aval personal",
    "cards.reveal.pin_prompt": "Introduïu el vostre codi PIN de 4 xifres per mostrar la informació de la targeta.",
    "cards.reveal.show": "Mostrar",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Els dos codis PIN no coincideixen",
    "auth.pin.digit_hint": "4 dígits",
  
  },

  eu: {
    "notif.transfer_code.title": "Transferentzia balioztatzeko kodea",
    "notif.transfer_code.body": "Kopiatu kode hau transferentzia balioztatzeko.",
    "notif.transfer_code.expires_in": "{sec}s barru desagertzen da",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} hilabete",
    "credits.n_months": "{n} hilabete",
    "msg.read": "Irakurrita",
    "msg.sent": "Bidalita",
    "msg.attach_aria": "Fitxategia erantsi",
    "login.device.unknown": "Nabigatzaile ezezaguna",
    "login.device.mobile": "Mugikorra",
    "login.device.tablet": "Tableta",
    "login.device.desktop": "Ordenagailua",
    "login.recent": "Berriena",
    "err.invalid_credentials": "Kredentzial baliogabeak",
    "err.bad_pin": "PIN okerra",
    "err.account_blocked": "Kontua blokeatuta",
    "err.bad_current_pw": "Uneko pasahitza okerra da",
    "err.insufficient_funds": "Funts nahikorik ez, kontua kargatu.",
    "err.amount_above_savings": "Zenbatekoa eskuragarri dagoen aurrezpenaren gainetik dago",
    "err.must_be_positive": "Balioak positiboa izan behar du",
    "err.invalid_format": "Formatu baliogabea",
    "err.email_taken": "Email-a erabilita dago",
    "err.card_not_found": "Txartela ez da aurkitu",
    "err.physical_card_already_ordered": "Txartel fisikoa eskatuta dago jada",
    "err.beneficiary_not_found": "Onuraduna ez da aurkitu",
    "err.invalid_code": "Kode baliogabea",
    "err.no_transfer_code": "Transferentzia honetarako koderik ez",
    "err.link_expired": "Esteka baliogabea edo iraungia",
    "err.thread_not_found": "Elkarrizketa ez da aurkitu",
    "err.empty_message": "Mezu hutsa",
    "err.file_too_large": "Fitxategia handiegia (geh. 20 MB)",
    "err.forbidden": "Sarbidea ukatua",
    "err.bank_not_found": "Bankua ez da aurkitu",
    "err.signups_closed": "Banku honetan izen-emateak itxita daude.",
    "err.access_suspended": "Sarbidea aldi baterako etenda. Jarri harremanetan zure bankuarekin.",
    "err.notif_not_closable": "Jakinarazpen hau ezin da itxi.",
    "auth.signup.tagline": "Elkartu zaitez gurekin gaur",
    "auth.login.tagline": "Ongi etorri!",
    "auth.signup.tagline_sub": "Ireki zure kontua 2 minututan eta sartu gure banku-zerbitzu premium guztietara.",
    "auth.login.tagline_sub": "Sartu zure eremu pertsonalera eta kudeatu zure finantzak erabateko segurtasunez.",
    "auth.back": "Itzuli hasierara",
    "auth.bullet.security": "Maila militarreko banku-segurtasuna",
    "auth.bullet.transfers": "Berehalako transferentziak 24/7",
    "auth.bullet.investments": "Inbertsioak eta aurrezpenak integratuta",
    "auth.bullet.advisor": "Aholkulari pertsonala 7/7 eskuragarri",
    "auth.footer": "{bank}-k bultzatuta",
    "auth.signup.title": "Kontua sortu",
    "auth.login.title": "Saio-hasiera",
    "auth.field.lastName": "Abizena",
    "auth.field.firstName": "Izenak",
    "auth.field.country": "Herrialdea",
    "auth.field.country.placeholder": "— Hautatu zure herrialdea —",
    "auth.field.phone": "Telefonoa",
    "auth.field.address": "Helbidea",
    "auth.field.address.placeholder": "Errege kalea 123, Bilbo",
    "auth.field.email": "Posta elektronikoa",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Pasahitza",
    "auth.submit.signup": "Sortu nire kontua",
    "auth.submit.login": "Hasi saioa",
    "auth.login.badge": "Sartu zure kontuan",
    "auth.card.tagline": "Zure bezeroen eremua",
    "auth.submit.loading": "Kargatzen…",
    "auth.pin.title": "PIN kodea",
    "auth.pin.subtitle": "Sartu zure 4 digituko PIN kodea",
    "auth.pin.label": "PIN kodea",
    "auth.pin.confirm": "PIN Kodea Berretsi",
    "auth.pin.submit": "Berretsi",
    "auth.pin.back": "Itzuli saio-hasierara",
    "auth.pin.length_error": "PIN kodeak 4 digitu izan behar ditu",
    "auth.switch.have_account": "Baduzu kontua?",
    "auth.switch.login_link": "Saio-hasiera",
    "auth.switch.no_account": "Ez zara bezeroa oraindik?",
    "auth.error.select_country": "Hautatu zure herrialdea",
    "kyc.title": "Nortasunaren egiaztapena (KYC)",
    "kyc.sub": "Igo zure dokumentuak kontua baliozkotzeko. Dokumentu guztiak enkriptatuta eta konfidentzialak dira.",
    "kyc.id_type": "Nortasun-agiri mota *",
    "kyc.id_card": "NAN",
    "kyc.passport": "Pasaportea",
    "kyc.driver_license": "Gidabaimena",
    "kyc.recto_passport": "Pasaportearen aurrealdea *",
    "kyc.recto_id": "Agiriaren aurrealdea *",
    "kyc.verso": "Agiriaren atzealdea *",
    "kyc.selfie": "Selfie nortasun-agiria eskuan duzula *",
    "kyc.address_proof": "Bizilekuaren egiaztagiria *",
    "kyc.hint_doc": "Irudia edo PDFa, geh. 5 MB",
    "kyc.hint_selfie": "Argazki argia, aurpegia eta agiria ikusgai",
    "kyc.hint_address": "Faktura, laburpena, agiria… (geh. 5 MB)",
    "kyc.upload": "Igo",
    "kyc.replace": "Ordeztu",
    "pending.title": "Kontua zain",
    "pending.body": "Zure eskaera jaso dugu. Administratzaile batek zure espedientea egiaztatuko du eta zure kontua laster aktibatuko du.",
    "pending.back": "Itzuli saio-hasierara",
    "home.nav.features": "Funtzionalitateak",
    "home.nav.security": "Segurtasuna",
    "home.nav.invest": "Inbertsioa",
    "home.nav.credits": "Kredituak",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Hasi saioa",
    "home.hero.tagline": "Belaunaldi berriko lineako banka",
    "home.hero.title1": "Kudeatu zure dirua",
    "home.hero.title2": "adimenez",
    "home.hero.sub": "Banku-plataforma osoa — transferentziak, aurrezpenak, inbertsioak eta kredituak zure smartphonetik.",
    "home.hero.login": "Hasi saioa",
    "home.hero.trust.secure": "% 100 segurua",
    "home.hero.trust.fast": "Irekiera azkarra",
    "home.hero.trust.encrypted": "Enkriptatutako datuak",
    "home.hero.trust.support": "Zerbitzua 24/7",
    "home.mockup.balance": "Saldo erabilgarria",
    "home.mockup.currency": "EUR · Kontu nagusia",
    "home.mockup.recharge": "Berkargatu",
    "home.mockup.send": "Bidali",
    "home.mockup.save": "Aurreztu",
    "home.mockup.salary": "Martxoko soldata",
    "home.mockup.rent": "Apirileko alokairua",
    "home.mockup.investment": "Inbertsioa",
    "home.mockup.days2": "duela 2 egun",
    "home.mockup.days3": "duela 3 egun",
    "home.mockup.days5": "duela 5 egun",
    "home.stats.accounts": "Kontu aktiboak",
    "home.stats.uptime": "Erabilgarritasuna",
    "home.stats.transfers": "Transferentziak",
    "home.stats.support": "Laguntza",
    "home.features.label": "Funtzionalitateak",
    "home.features.title": "Behar duzun guztia",
    "home.features.sub": "Plataforma osoa zure finantzen eguneroko kudeaketa sinplifikatzeko diseinatua.",
    "home.feature.security": "Segurtasun militarra",
    "home.feature.security_body": "AES-256 enkriptatzea, bi faktoreen autentifikazioa eta zure kontuaren 24/7 zaintza.",
    "home.feature.cards": "Birtualak eta fisikoak diren txartelak",
    "home.feature.cards_body": "Igorri txartelak segundo gutxitan edo eskatu benetako txartel fisikoa zure etxera bidalita.",
    "home.feature.transfers": "Transferentzia azkarrak",
    "home.feature.transfers_body": "Bidali eta jaso dirua munduko edozein lekutan segundo gutxitan.",
    "home.feature.analytics": "Denbora errealeko analisia",
    "home.feature.analytics_body": "Aginte-koadro adimentsuak zure gastuak jarraitzeko eta zure finantzak aurreikusteko.",
    "home.feature.investments": "Inbertsio integratuak",
    "home.feature.investments_body": "Sartu hautatutako inbertsio-produktuetara zuzenean zure kontutik.",
    "home.feature.support": "24/7 laguntza eskainia",
    "home.feature.support_body": "Banku-aholkulari pertsonala edozein unetan eskuragarri mezularitza seguruaren bidez.",
    "home.tpl.label": "Konfiantza",
    "home.tpl.title": "Gugan konfiantza dute",
    "home.mockup.balance_label": "Saldoa",
    "home.mockup.virement": "Transferentzia",
    "home.mockup.salaire": "Soldata",
    "home.security.label": "Segurtasuna",
    "home.security.title1": "Zure finantzak",
    "home.security.title2": "erabateko segurtasunean",
    "home.security.sub": "Banku-segurtasunaren estandar zorrotzenak aplikatzen ditugu zentimo bakoitza babesteko.",
    "home.security.aes.title": "AES-256 enkriptatzea",
    "home.security.aes.body": "Datu sentikor guztiak nazioarteko banku handiek erabiltzen dituzten algoritmo berberekin enkriptatzen dira.",
    "home.security.auth.title": "Autentifikazio sendotua",
    "home.security.auth.body": "Nortasunaren egiaztapen bikoitza saio bakoitzean eta zure kontuko eragiketa sentikor bakoitzeko.",
    "home.security.compliance.title": "DSP2/RGPD betetzea",
    "home.security.compliance.body": "Ordainketa-zerbitzuei eta datuen babesari buruzko europar arautegi guztiak betetzen ditugu.",
    "home.invest.label": "Inbertsioak",
    "home.invest.title": "Hazi ezazu zure dirua",
    "home.invest.sub": "6 inbertsio-kategoria hautatuak beren potentzialagatik eta fidagarritasunagatik.",
    "home.invest.yield": "Errentagarritasuna",
    "home.credits.label": "Kredituak",
    "home.credits.title": "Proiektu bakoitzera egokitutako soluzioak",
    "home.credits.sub": "Tasa lehiakorrak, erantzun azkarrak eta laguntza pertsonalizatua zure proiektu guztietarako.",
    "home.credits.rate": "Tasa",
    "home.faq.label": "FAQ",
    "home.faq.title": "Ohiko galderak",
    "home.faq.q1": "Nola ireki kontu bat?",
    "home.faq.a1": "Erregistroak 2 minutu baino gutxiago irauten du. Nahikoa da zure datu pertsonalak sartzea, pasahitza aukeratzea eta zure kontua gure taldearen baliozkotzearen ondoren aktibatuko da.",
    "home.faq.q2": "Nire datuak seguruak al dira?",
    "home.faq.a2": "Bai. Datu guztiak AES-256rekin enkriptatuta daude geldirik eta TLS 1.3rekin igorpenean. Nazioarteko banku-arauak eta RGPD/DSP2 arautegiak betetzen ditugu.",
    "home.faq.q3": "Zer inbertsio mota eskaintzen dituzue?",
    "home.faq.a3": "6 kategoria eskaintzen ditugu: Higiezinak, Crowdfunding, Energia berriztagarriak, Burtsa, Partaidetzazkoa eta Kripto. Produktu bakoitza arrisku/errentagarritasun erlazioagatik hautatzen da.",
    "home.faq.q4": "Nola eskatu kreditua?",
    "home.faq.a4": "Joan zure bezero-eremuko Kreditu atalera, aukeratu mailegu mota eta adierazi zenbatekoa eta iraupena. Zure espedientea 24 ordutan tramitatzen dute gure aholkulariek.",
    "home.faq.q5": "Ba al daude ezkutuko komisiorik?",
    "home.faq.a5": "Ezkutuko komisiorik ez. Tarifa guztiak argi eta garbi erakusten dira eragiketa bakoitzean. Baldintzak zure bezero-eremuan eskuragarri daude edozein unetan.",
    "home.faq.q6": "Nola jarri harremanetan laguntzarekin?",
    "home.faq.a6": "Erabili zure bezero-eremuan integratutako mezularitza zure banku-aholkulari pertsonalarekin zuzenean harremanetan jartzeko, 24/7 eskuragarri.",
    "home.cta.title": "Hasteko prest?",
    "home.cta.sub": "Elkartu zaitez milaka erabiltzailerekin, beren finantzak {bank}-ekin kudeatzen dituztenak. Erregistroak 2 minutu baino gutxiago irauten du.",
    "home.footer.products": "Produktuak",
    "home.footer.services": "Zerbitzuak",
    "home.footer.access": "Sarbide azkarra",
    "home.footer.accounts": "Kontuak",
    "home.footer.transfers": "Transferentziak",
    "home.footer.cards": "Txartelak",
    "home.footer.savings": "Aurrezpena",
    "home.footer.investments": "Inbertsioak",
    "home.footer.credits": "Kredituak",
    "home.footer.financing": "Finantzaketa",
    "home.footer.messages": "Mezuak",
    "home.footer.login": "Saio-hasiera",
    "home.footer.signup": "Kontua ireki",
    "home.footer.support": "24/7 laguntza",
    "home.footer.tagline": "Zure lineako banka, edozein lekutan eta edozein unetan eskuragarri.",
    "home.footer.copyright": "© {year} {bank} — Eskubide guztiak erreserbatuta",
    "home.footer.powered": "{bank}-k bultzatuta",
    "app.menu_label": "Menu nagusia",
    "app.nav.dashboard": "Aginte-panela",
    "app.nav.accounts": "Kontuak",
    "app.nav.transactions": "Transakzioak",
    "app.nav.transfers": "Transferentziak",
    "app.nav.cards": "Txartelak",
    "app.nav.savings": "Aurrezpena",
    "app.nav.investments": "Inbertsioa",
    "app.nav.credit": "Kreditua",
    "app.nav.financing": "Finantzaketa",
    "app.nav.messages": "Mezuak",
    "app.nav.card_tracking": "Txartelaren jarraipena",
    "app.nav.notifications": "Jakinarazpenak",
    "app.nav.settings": "Ezarpenak",
    "app.nav.profile": "Profila",
    "app.nav.mobile.home": "Hasiera",
    "app.nav.mobile.transfers": "Transferentziak",
    "app.nav.mobile.cards": "Txartelak",
    "app.nav.mobile.transactions": "Transakzioak",
    "app.nav.more.invest": "Inbertitu",
    "app.user_role": "Bezeroa",
    "app.logout": "Saioa itxi",
    "app.search": "Bilatu…",
    "app.more_title": "Funtzionalitate gehiago",
    "app.more_btn": "Gehiago",
    "app.loading": "Kargatzen…",
    "app.error_back": "Itzuli saio-hasierara",
    "app.see_more_notifs": "Ikusi gainerako {count} jakinarazpenak →",
    "page.accounts": "Nire kontuak",
    "page.transactions": "Transakzioak",
    "page.transfers": "Transferentzia berria",
    "page.deposit": "Gordailua",
    "page.cards": "Nire txartelak",
    "page.savings": "Aurrezpena",
    "page.investments": "Inbertsioa",
    "page.credits": "Kreditua",
    "page.financing": "Proiektuen finantzaketa",
    "page.messages": "Mezuak",
    "page.settings": "Ezarpenak",
    "page.notifications": "Jakinarazpen garrantzitsuak",
    "page.profile": "Nire profila",
    "dash.greeting": "Kaixo, {name}",
    "dash.overview": "Zure finantzen ikuspegi orokorra — {date}",
    "dash.total_balance": "Saldo osoa",
    "dash.this_month": "+{amount} hil honetan",
    "dash.view_iban": "Ikusi nire IBANa",
    "dash.my_accounts": "Nire kontuak",
    "dash.primary_account": "Kontu nagusia",
    "dash.savings_account": "Aurrezpen-kontua",
    "dash.remunerated": "Ordaindua",
    "dash.details": "Xehetasunak",
    "dash.transfer_btn": "Transferentzia",
    "dash.quick_actions": "Ekintza azkarrak",
    "dash.quick.deposit": "Gordailua",
    "deposit.title": "Gordailuaren informazioa",
    "dash.quick.transfer": "Transferentzia",
    "dash.quick.history": "Historia",
    "dash.quick.cards": "Nire txartelak",
    "dash.quick.profile": "Profila",
    "dash.monthly_budget": "Hileko aurrekontua",
    "dash.income": "Sarrerak",
    "dash.expenses": "Gastuak",
    "dash.spending_cat": "Gastuak kategoriaka",
    "dash.no_spending": "Erregistratutako gasturik ez.",
    "dash.recent_tx": "Azken transakzioak",
    "dash.see_all": "Ikusi guztiak",
    "dash.no_tx": "Eragiketarik ez.",
    "dash.main_card": "Nire txartel nagusia",
    "dash.manage": "Kudeatu",
    "dash.block": "Blokeatu",
    "dash.pin": "PIN kodea",
    "dash.security": "Segurtasuna",
    "dash.more": "Gehiago",
    "dash.no_card": "Igorritako txartelik ez",
    "dash.monthly_activity": "Hilabeteko jarduera",
    "dash.operations": "Eragiketak",
    "dash.holder": "Titularra",
    "dash.expires": "Iraung.",
    "dash.visa_label": "VISA",
    "dash.balance": "Saldo erabilgarria",
    "tx.all": "Guztiak",
    "tx.income": "Sarrerak",
    "tx.expenses": "Irteerak",
    "tx.today": "Gaur",
    "tx.yesterday": "Atzo",
    "tx.none": "Transakziorik ez.",
    "transfer.title": "Transferentzia azkarra",
    "transfer.recipient": "Hartzailea",
    "transfer.recipient_ph": "Izena, IBANa, telefono-zenbakia…",
    "transfer.amount": "Zenbatekoa",
    "transfer.label": "Kontzeptua",
    "transfer.reason": "Arrazoia (aukerakoa)",
    "transfer.reason_ph": "Adib.: alokairua, itzulketa…",
    "transfer.execution": "Egikaritzea",
    "transfer.immediate": "Berehala",
    "transfer.delayed": "Geroratua",
    "transfer.balance": "Saldo erabilgarria:",
    "transfer.continue": "Jarraitu",
    "transfer.confirm": "Berretsi",
    "transfer.cancel": "Utzi",
    "transfer.code": "Baliozkotze-kodea",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Sartu jasotako kodea transferentzia baliozkotzeko",
    "transfer.pending.title": "Transferentzia baliozkotzeko zain",
    "transfer.pending.amount": "Zenbatekoa:",
    "transfer.pending.to": "Nora:",
    "transfer.pending.hint": "Zure balidazio-kodea posta elektronikoz edo jakinarazpen-txilinaren bidez bidaliko da. Kodea 6 zifrako da. Ez baduzu jasotzen, jarri harremanetan laguntzarekin.",
    "transfer.pending.validate": "Baliozkotu transferentzia",
    "transfer.saved": "Transferentzia erregistratuta. Eskatu kodea administratzaileari baliozkotzeko.",
    "transfer.done": "Transferentzia egina",
    "transfer.validated": "Transferentzia baliozkotuta!",
    "transfer.progress.processing": "Transferentzia prozesatzen…",
    "transfer.progress.completed": "Transferentzia eginda!",
    "transfer.progress.default_message": "Zure transferentzia prozesatzen ari da. Mesedez, itxaron.",
    "cards.none": "Igorritako txartelik ez.\nJarri harremanetan zure administratzailearekin.",
    "cards.holder": "Titularra",
    "cards.expires": "Iraung.",
    "cards.block": "Blokeatu",
    "cards.pin": "PIN kodea",
    "cards.more": "Gehiago",
    "cards.limits": "Txartelaren mugak",
    "cards.payment": "Ordainketa",
    "cards.withdrawal": "Ateratzea",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Blokeatuta",
    "cards.status_active": "Aktibo",
    "cards.toast.blocked": "Txartela blokeatuta",
    "cards.toast.unblocked": "Txartela desblokeatu da",
    "cards.toast.physical_ordered": "Txartel fisikoa eskatuta! 5–10 egun baliodunetan entregatuko da.",
    "cards.toast.created": "Txartela arrakastaz sortuta!",
    "cards.none_sub": "Ez dago txartelik jaulkita. Sortu zure txartel birtuala orain.",
    "cards.generate_btn": "Nire txartela sortu",
    "cards.create.title": "Nire txartel birtuala sortu",
    "cards.create.network": "Ordainketa sarea",
    "cards.create.color": "Txartelaren kolorea",
    "cards.create.preview_name": "ZURE IZENA",
    "cards.create.preview_expiry": "HH/UU",
    "cards.create.loading": "Sortzen…",
    "cards.create.submit": "Txartela sortu",
    "cards.btn.unblock": "Desblokeatu",
    "cards.btn.block": "Blokeatu",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fisikoa",
    "cards.btn.physical_done": "Eskatuta",
    "cards.limits_title": "Mugak",
    "cards.reveal.title": "Txartelaren informazioa",
    "cards.reveal.number": "Zenbaki osoa",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Iraungitze data",
    "cards.reveal.warning": "Ez partekatu inoiz informazio hau. Itxi leiho hau erabili ondoren.",
    "cards.pin.title": "PIN kodea",
    "cards.pin.message": "PIN kode hau zure sareko ordainketa eta erretiratzaile guztietarako erabiltzen da. Ez esan inori.",
    "cards.physical.title": "Txartel fisikoa eskatu",
    "cards.physical.fee_label": "Bidalketaren kostua",
    "cards.physical.balance_label": "Zure saldoa",
    "cards.physical.insufficient": "Saldo eskasa. Mesedez, kargatu zure kontua eskatu aurretik.",
    "cards.physical.info": "Kostua zure kontutik kobratuko da. 5–10 egun baliodunetan entregatuko da.",
    "cards.physical.order_btn": "Eskatu ({fee})",
    "cards.physical.ordering": "Eskatzen…",
    "cards.physical.order_cta": "Txartel fisiko bat eskatu",
    "cards.physical.choose_type": "Aukeratu zure txartela",
    "cards.physical.features": "Abantailak",
    "cards.buy_virtual_cta": "Erosi txartel birtuala",
    "notif.none": "Jakinarazpenik ez momentuz.",
    "notif.contact_support": "Laguntza kontaktatu",
    "profile.change_photo": "Aldatu argazkia",
    "profile.remove_photo": "Kendu argazkia",
    "profile.email": "Posta elektronikoa",
    "profile.phone": "Telefonoa",
    "profile.country": "Herrialdea",
    "profile.address": "Helbidea",
    "profile.notifs_title": "Jakinarazpenak ({count})",
    "profile.no_notifs": "Jakinarazpenik ez.",
    "profile.logout": "Saioa itxi",
    "profile.photo_updated": "Profileko argazkia eguneratuta",
    "profile.photo_removed": "Argazkia kenduta",
    "profile.photo_too_large": "Irudia handiegia (geh. 1 MB)",
    "profile.powered": "{bank}-k bultzatuta",
    "iban.title": "Nire IBANa",
    "iban.loading": "Kargatzen…",
    "iban.bic": "BIC / SWIFT kodea",
    "iban.share_info": "Partekatu datu hauek zure kontuan transferentzia bat jasotzeko.",
    "iban.copy": "Kopiatu",
    "iban.copied": "Arbelean kopiatuta",
    "iban.copy_fail": "Ezin da kopiatu",
    "iban.close": "Itxi",
    "accounts.all": "{bank}-en irekita dituzun kontu guztiak.",
    "accounts.primary": "Kontu nagusia",
    "accounts.savings": "Aurrezpen-kontua",
    "accounts.primary_tag": "Nagusia",
    "accounts.secondary_tag": "Bigarrenkaria",
    "accounts.objective": "Helburua: {amount} · {pct}%",
    "accounts.copy": "Kopiatu",
    "accounts.loading": "Kargatzen…",
    "accounts.iban_copied": "IBANa kopiatuta",
    "accounts.copy_fail": "Ezin da kopiatu",
    "savings.total": "Guztira aurreztua",
    "savings.target_total": "Helburu osoa",
    "savings.progress": "Aurrerapena",
    "savings.active": "Helburu aktiboak",
    "savings.new_goal": "Helburu berria",
    "savings.none": "Aurrezpen-helbururik ez momentuz.",
    "savings.none_sub": "Sortu zure lehen helburua hasteko.",
    "savings.saved": "Aurreztuta:",
    "savings.goal": "Helburua:",
    "savings.deadline": "Epea: {date}",
    "savings.deposit": "Sartu",
    "savings.withdraw": "Atera",
    "savings.loading": "Kargatzen…",
    "savings.form.title": "Aurrezpen-helburu berria",
    "savings.form.cancel": "Utzi",
    "savings.form.create": "Sortu",
    "savings.form.name": "Helburuaren izena",
    "savings.form.name_ph": "Japoniako bidaia, Autoa, …",
    "savings.form.target": "Helburu zenbatekoa",
    "savings.form.initial": "Hasierako gordailua",
    "savings.form.deadline": "Mugaeguna (aukerakoa)",
    "savings.form.color": "Kolorea",
    "savings.deposit_title": "Sartu \"{name}\"-(e)n",
    "savings.deposit_amount": "Sartzeko zenbatekoa",
    "savings.balance_hint": "Saldo nagusia: {amount}",
    "savings.withdraw_prompt": "Zenbat atera \"{name}\"-(e)tik?",
    "savings.close_confirm": "Itxi \"{name}\" helburua? Aurrezpena zure kontu nagusira igaroko da.",
    "savings.toast.created": "Helburua sortuta",
    "savings.toast.deposited": "Gordailua egina",
    "savings.toast.withdrawn": "Ateratzea egina",
    "savings.toast.closed": "Helburua itxita",
    "invest.total": "Guztira inbertituta",
    "invest.profits": "Pilatutako etekinak",
    "invest.count": "Inbertsioak",
    "invest.products": "Produktu eskuragarriak",
    "invest.invest_btn": "Inbertitu",
    "invest.my": "Nire inbertsioak",
    "invest.pending": "Zain",
    "invest.validated": "Baliozkotua",
    "invest.refused": "Ukatua",
    "invest.withdraw.btn": "Atera",
    "invest.withdraw.title": "Nire inbertsioa atera",
    "invest.withdraw.intro": "\"{product}\" inbertsioa ateratzeko zorian zaude. Hasierako kapitala eta pilatutako etekina zure saldo nagusian sartuko dira.",
    "invest.withdraw.principal": "Hasierako kapitala",
    "invest.withdraw.profit": "Etekina",
    "invest.withdraw.total": "Guztira jasotzeko",
    "invest.withdraw.confirm": "Berretsi ateratzea",
    "invest.withdraw.warning": "Ekintza hau itzulezina da.",
    "invest.withdraw.success": "Inbertsioa atera da — {amount} zure saldoan sartu dira.",
    "invest.withdrawn": "Atera da",
    "invest.cta_sub": "Aukeratu zuretzat egokien den inbertsio mota. Aholkulari batek lagunduko dizu zure eskaeratik.",
    "invest.form.amount": "Inbertitzeko zenbatekoa",
    "invest.form.balance": "Saldo erabilgarria: {amount}",
    "invest.form.note": "Oharra zure aholkulariarentzat (aukerakoa)",
    "invest.form.note_ph": "Zehaztu zure itxaropenak…",
    "invest.form.advisor_info": "Elkarrizketa bat automatikoki irekiko da zure aholkulariarekin.",
    "invest.form.cancel": "Utzi",
    "invest.form.submit": "Bidali eskaera",
    "invest.toast.requested": "Eskaera erregistratuta — elkarrizketa bat ireki da zure aholkulariarekin.",
    "invest.p1.name": "Higiezinak",
    "invest.p1.desc": "SCPI, paper-harria, higiezinen crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Hautatutako proiektuen finantzaketa partaidetzazkoa",
    "invest.p3.name": "Energ. berriztag.",
    "invest.p3.desc": "Eguzki-energia, eolikoa eta proiektu berdeak",
    "invest.p4.name": "Burtsa",
    "invest.p4.desc": "Akzioak, ETF eta funts dibertsifikatuak",
    "invest.p5.name": "Partaidetzazkoa",
    "invest.p5.desc": "Maileguak PME eta tokiko ekintzaileei",
    "invest.p6.name": "Kripto",
    "invest.p6.desc": "Bitcoin, Ethereum eta moneta anitzeko zorroa",
    "msg.online": "Konektatuta",
    "msg.loading": "Kargatzen…",
    "msg.none": "Mezurik ez. Hasi elkarrizketa zure aholkulariarekin.",
    "msg.placeholder": "Idatzi zure mezua…",
    "msg.attach.image": "Irudia",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audioa",
    "msg.attach.file": "Fitxategia / Dokumentua",
    "msg.pdf_doc": "PDF dokumentua",
    "msg.file": "Fitxategia",
    "msg.too_large": "Fitxategi handiegia (geh. {size}).",
    "msg.withdraw": "Atera",
    "credits.cta_sub": "Egin zure eskaera zuzenean linean — erantzuna 24 ordutan.",
    "credits.learn_more": "Gehiago jakin",
    "credits.request": "Eskatu",
    "credits.history": "Nire eskaerak",
    "credits.status.pending": "Zain",
    "credits.status.accepted": "Onartua",
    "credits.status.refused": "Ukatua",
    "credits.chars": "Ezaugarriak",
    "credits.amount_range": "Zenbatekoa: {min}€ – {max}€",
    "credits.rate_range": "Interes-tasa: {min}% – {max}% urtean",
    "credits.duration_range": "Iraupena: {min}-{max} hilabete",
    "credits.deposit_min": "Hasierako gordailua: {amount}€ gutxienez",
    "credits.garantie": "Bermea: {garantie}",
    "credits.eligibility": "Hautagarritasun-baldintzak",
    "credits.elig.income": "Diru-sarrera erregularrak eta egonkorrak justifikatu",
    "credits.elig.deposit": "Egin hasierako {amount}€-ko gordailua",
    "credits.elig.garantie": "Aurkeztu bermea: {garantie}",
    "credits.form.title": "Eskaera — {name}",
    "credits.form.cancel": "Utzi",
    "credits.form.submit": "Bidali eskaera",
    "credits.form.amount": "Nahi den zenbatekoa (€)",
    "credits.form.duration": "Iraupena (hilabete)",
    "credits.form.reason": "Arrazoia (aukerakoa)",
    "credits.form.reason_ph": "Zehaztu zure proiektua…",
    "credits.form.close": "Itxi",
    "credits.toast.submitted": "Kreditu-eskaera bidalita — erantzuna 24 ordutan jasoko duzu.",
    "credits.p1.name": "Mailegu Pertsonala",
    "credits.p1.desc": "Eguneroko beharrak edo proiektu pertsonalak finantzatzeko.",
    "credits.p2.name": "Mailegu Hipotekarioa",
    "credits.p2.desc": "Higiezin baten erosketa edo eraikuntza finantzatzeko.",
    "credits.p3.name": "Auto / Moto Mailegua",
    "credits.p3.desc": "Erosi ibilgailua erabateko lasaitasunez.",
    "credits.p4.name": "Ikasle Mailegua",
    "credits.p4.desc": "Goi-mailako ikasketetarako diru-laguntza.",
    "credits.p5.name": "Mailegu Profesionala",
    "credits.p5.desc": "Jarduera profesional baten hazkundeari laguntzeko.",
    "credits.p6.name": "Mailegu Azkarra",
    "credits.p6.desc": "Premiazko beharretarako konponbide azkarra.",
    "financing.reimbursable": "Itzulgarria den finantzaketa",
    "financing.reimbursable_desc": "Baldintza finkoekin egindako laguntza finantzarioa jarduera bat abian jartzeko edo garatzeko.",
    "financing.non_reimbursable": "Itzulezina den finantzaketa",
    "financing.non_reimbursable_desc": "Eragin sozial, hezigarri edo humanitarioa duten proiektuetarako (GKE, elkarteak).",
    "financing.cta_sub": "Egokitutako soluzioak zure asmoak gauzatzeko.",
    "financing.request": "Eskatu finantzaketa",
    "financing.history": "Nire eskaerak",
    "financing.reimbursable_label": "Itzulgarria",
    "financing.non_reimbursable_label": "Itzulezina",
    "financing.status.pending": "Zain",
    "financing.status.granted": "Emana",
    "financing.status.refused": "Ukatua",
    "financing.form.title": "Eskaera — {name}",
    "financing.form.cancel": "Utzi",
    "financing.form.submit": "Bidali eskaera",
    "financing.form.project": "Proiektuaren izena",
    "financing.form.project_ph": "Nire proiektua",
    "financing.form.amount": "Eskatutako zenbatekoa (€)",
    "financing.form.sector": "Sektorea",
    "financing.form.sector_ph": "Tek., nekazaritza, …",
    "financing.form.description": "Proiektuaren deskribapena",
    "financing.form.description_ph": "Aurkeztu zure proiektua, bere helburuak eta espero den eragina…",
    "settings.security": "Segurtasuna",
    "settings.current_pw": "Egungo pasahitza",
    "settings.new_pw": "Pasahitz berria",
    "settings.confirm_pw": "Berretsi",
    "settings.submit": "Aldatu pasahitza",
    "settings.no_match": "Pasahitzak ez datoz bat",
    "settings.changed": "Pasahitza aldatuta",
    "common.cancel": "Utzi",
    "common.confirm": "Berretsi",
    "common.close": "Itxi",
    "common.save": "Gorde",
    "common.loading": "Kargatzen…",
    "common.send": "Bidali",
    "common.total": "Guztira",
    "admin.wallet.balance": "Saldo disponible",
    "admin.wallet.history": "Historial",
    "admin.wallet.no_history": "Sin transacciones",
    "admin.wallet.title": "Mi billetera",
    "admin.wallet.topup": "Recargar",
    "beneficiary.pick": "Seleccionar",
    "beneficiary.save_this": "Guardar este beneficiario",
    "beneficiary.saved": "Beneficiarios guardados",
    "beneficiary.saved_ok": "Beneficiario guardado",
    "card.color.amber": "Ámbar",
    "card.color.cyan": "Cian",
    "card.color.emerald": "Esmeralda",
    "card.color.forest": "Bosque",
    "card.color.gold": "Oro",
    "card.color.lavender": "Lavanda",
    "card.color.night": "Noche",
    "card.color.orange": "Naranja",
    "card.color.pink": "Rosa",
    "card.color.royal": "Azul real",
    "card.color.ruby": "Rubí",
    "card.color.slate": "Pizarra",
    "card.color.steel": "Acero",
    "card.color.terracotta": "Terracota",
    "card.color.violet": "Violeta",
    "common.other": "Otro",
    "common.search": "Buscar",
    "content.translated": "Contenido traducido automáticamente",
    "credits.monthly_payment": "Cuota mensual estimada",
    "credits.rate_annual": "/ año",
    "credits.rate_used": "Tasa estimada:",
    "credits.simulation": "Simulación de cuota mensual",
    "credits.total_cost": "Coste total estimado",
    "demo.banner": "Modo demostración — ninguna acción real",
    "demo.disabled": "Acción no disponible en modo demo",
    "deposit.default_instructions": "Contacte a su gestor para los detalles del depósito.",
    "error.back_home": "Volver al inicio",
    "error.bank_not_found": "Banco no encontrado",
    "error.unknown": "Error desconocido",
    "kyc.file_read_error": "No se puede leer el archivo",
    "kyc.file_too_large": "Archivo demasiado grande (máx. 5 MB)",
    "login.history": "Historial de conexiones",
    "login.history.empty": "Ninguna conexión registrada.",
    "notif.dismiss": "Cerrar",
    "notif.locked": "Mensaje del banco",
    "tracking.error.not_found": "Número de seguimiento no encontrado. Verifique e intente de nuevo.",
    "tracking.label": "Seguimiento",
    "tracking.status.delivered": "Entregado",
    "tracking.status.preparation": "En preparación",
    "tracking.status.shipped": "Enviado",
    "tracking.status.transit": "En tránsito",
    "transfer.code.copied": "Copiado",
    "transfer.code.copy": "Copiar",
    "transfer.code.received": "Su código de validación",
    "dash.welcome": "Ongi etorri",
    "common.delete": "Ezabatu",
    "cards.delete.title": "Txartela ezabatu",
    "cards.delete.confirm_msg": "Ekintza hau itzulezina da. Txartela betirako ezabatuko da.",
    "cards.delete.physical_tooltip": "Txartel fisikoa eskatuta — ezin da ezabatu",
    "cards.order.confirmed": "Eskaera baieztatuta!",
    "cards.order.confirmed_body": "Zure txartel fisikoa prestatzen ari da. Erabili zenbaki hau zure entrega jarraitzeko.",
    "cards.order.tracking_no": "Jarraipena zenbakia",
    "cards.fee_deducted": "{amount} zure saldotik kendu egingo da",
    "cards.insufficient_short": "Saldo ez nahikoa ({available} erabilgarri)",
    "transfer.section.beneficiary": "Onuraduna",
    "transfer.section.details": "Transferentzia",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bankua",
    "transfer.field.city": "Hiria",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bankua:",
    "transfer.progress.paused": "Balioztatzearen zain",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Aldakorra",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",

    "cards.deleted": "Txartela ezabatua",

    "cards.feat.gold_exclusive": "Urrezko txartel esklusiboa",

    "cards.feat.cashback_05": "Cashback %0,5",

    "cards.feat.buyer_protection": "Erosle babesa",

    "cards.feat.support_standard": "Laguntza estandarra",

    "cards.feat.plastic_premium": "Plastikozko txartel premium",

    "cards.feat.cashback_1": "Cashback %1",

    "cards.feat.travel_standard": "Bidaia-aseguru estandarra",

    "cards.feat.support_priority": "Lehentasunezko laguntza",

    "cards.feat.metal_brushed": "Metal eskuilatutako txartela",

    "cards.feat.cashback_2_unlimited": "%2 cashback mugagabea",

    "cards.feat.travel_premium": "Bidaia-aseguru premium",

    "cards.feat.vip_lounge": "VIP areto sarbidea",

    "cards.feat.support_247": "24/7 laguntza",

    "auth.demo.title": "Administratzaile saioa",

    "auth.demo.subtitle": "Sartu administratzaile-kredentzialak demoaren aurrebistarako",
    "credits.garantie.none": "Bat ere ez",
    "credits.garantie.mortgage": "Finantzatutako ondasunaren gaineko hipoteka",
    "credits.garantie.vehicle": "Finantzatutako ibilgailua",
    "credits.garantie.parental": "Gurasoen edo bankuaren bermea",
    "credits.garantie.professional": "Aktibo profesionalak edo berme pertsonala",
    "cards.reveal.pin_prompt": "Sartu zure 4 zifrako PIN kodea txartelaren informazioa bistaratzeko.",
    "cards.reveal.show": "Erakutsi",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Bi PIN kodeak ez datoz bat",
    "auth.pin.digit_hint": "4 digitu",
  
  },

  ro: {
    "notif.transfer_code.title": "Cod de validare a transferului",
    "notif.transfer_code.body": "Copiați acest cod pentru a valida transferul.",
    "notif.transfer_code.expires_in": "Dispare în {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} luni",
    "credits.n_months": "{n} luni",
    "msg.read": "Citit",
    "msg.sent": "Trimis",
    "msg.attach_aria": "Atașează fișier",
    "login.device.unknown": "Browser necunoscut",
    "login.device.mobile": "Mobil",
    "login.device.tablet": "Tabletă",
    "login.device.desktop": "Calculator",
    "login.recent": "Recent",
    "err.invalid_credentials": "Date de autentificare invalide",
    "err.bad_pin": "PIN incorect",
    "err.account_blocked": "Cont blocat",
    "err.bad_current_pw": "Parola actuală este incorectă",
    "err.insufficient_funds": "Fonduri insuficiente, vă rugăm să alimentați contul.",
    "err.amount_above_savings": "Suma depășește economiile disponibile",
    "err.must_be_positive": "Valoarea trebuie să fie pozitivă",
    "err.invalid_format": "Format invalid",
    "err.email_taken": "Email deja folosit",
    "err.card_not_found": "Card negăsit",
    "err.physical_card_already_ordered": "Card fizic deja comandat",
    "err.beneficiary_not_found": "Beneficiar negăsit",
    "err.invalid_code": "Cod invalid",
    "err.no_transfer_code": "Niciun cod pentru acest transfer",
    "err.link_expired": "Link invalid sau expirat",
    "err.thread_not_found": "Conversație negăsită",
    "err.empty_message": "Mesaj gol",
    "err.file_too_large": "Fișier prea mare (max 20 MB)",
    "err.forbidden": "Acces interzis",
    "err.bank_not_found": "Bancă negăsită",
    "err.signups_closed": "Înregistrările sunt închise pentru această bancă.",
    "err.access_suspended": "Acces temporar suspendat. Vă rugăm să contactați banca dumneavoastră.",
    "err.notif_not_closable": "Această notificare nu poate fi închisă.",
    "auth.signup.tagline": "Alătură-te nouă astăzi",
    "auth.login.tagline": "Bine ai venit!",
    "auth.signup.tagline_sub": "Deschide-ți contul în 2 minute și accesează toate serviciile noastre bancare premium.",
    "auth.login.tagline_sub": "Accesează-ți spațiul personal și gestionează-ți finanțele în deplină siguranță.",
    "auth.back": "Înapoi la pagina principală",
    "auth.bullet.security": "Securitate bancară de nivel militar",
    "auth.bullet.transfers": "Transferuri instantanee 24/7",
    "auth.bullet.investments": "Investiții și economii integrate",
    "auth.bullet.advisor": "Consilier personal disponibil 7 zile pe săptămână",
    "auth.footer": "Susținut de {bank}",
    "auth.signup.title": "Creează un cont",
    "auth.login.title": "Autentificare",
    "auth.field.lastName": "Nume",
    "auth.field.firstName": "Prenume",
    "auth.field.country": "Țară",
    "auth.field.country.placeholder": "— Selectează țara ta —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Adresă",
    "auth.field.address.placeholder": "Strada Păcii 123, București",
    "auth.field.email": "Email",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Parolă",
    "auth.submit.signup": "Creează-mi contul",
    "auth.submit.login": "Conectare",
    "auth.login.badge": "Conectați-vă la contul dvs.",
    "auth.card.tagline": "Spațiul dvs. de client",
    "auth.submit.loading": "Se încarcă…",
    "auth.pin.title": "Cod PIN",
    "auth.pin.subtitle": "Introduceți codul PIN din 4 cifre",
    "auth.pin.label": "Cod PIN",
    "auth.pin.confirm": "Confirmă Codul PIN",
    "auth.pin.submit": "Confirmare",
    "auth.pin.back": "Înapoi la autentificare",
    "auth.pin.length_error": "Codul PIN trebuie să aibă 4 cifre",
    "auth.switch.have_account": "Ai deja un cont?",
    "auth.switch.login_link": "Autentificare",
    "auth.switch.no_account": "Nu ești încă client?",
    "auth.error.select_country": "Selectează țara ta",
    "kyc.title": "Verificare identitate (KYC)",
    "kyc.sub": "Încarcă documentele tale pentru a-ți valida contul. Toate documentele sunt criptate și confidențiale.",
    "kyc.id_type": "Tip de act de identitate *",
    "kyc.id_card": "Carte de identitate",
    "kyc.passport": "Pașaport",
    "kyc.driver_license": "Permis de conducere",
    "kyc.recto_passport": "Față pașaport *",
    "kyc.recto_id": "Față act *",
    "kyc.verso": "Verso act *",
    "kyc.selfie": "Selfie cu actul de identitate în mână *",
    "kyc.address_proof": "Dovadă de domiciliu *",
    "kyc.hint_doc": "Imagine sau PDF, max. 5 MB",
    "kyc.hint_selfie": "Fotografie clară, față și document vizibile",
    "kyc.hint_address": "Factură, extras, atestat… (max. 5 MB)",
    "kyc.upload": "Încarcă",
    "kyc.replace": "Înlocuiește",
    "pending.title": "Cont în așteptare",
    "pending.body": "Cererea ta a fost primită. Un administrator îți va verifica dosarul și îți va activa contul în scurt timp.",
    "pending.back": "Înapoi la autentificare",
    "home.nav.features": "Funcționalități",
    "home.nav.security": "Securitate",
    "home.nav.invest": "Investiții",
    "home.nav.credits": "Credite",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Conectare",
    "home.hero.tagline": "Bancă online de nouă generație",
    "home.hero.title1": "Gestionează-ți banii",
    "home.hero.title2": "cu inteligență",
    "home.hero.sub": "O platformă bancară completă — transferuri, economii, investiții și credite de pe smartphone-ul tău.",
    "home.hero.login": "Conectare",
    "home.hero.trust.secure": "100% Securizat",
    "home.hero.trust.fast": "Deschidere rapidă",
    "home.hero.trust.encrypted": "Date criptate",
    "home.hero.trust.support": "Serviciu 24/7",
    "home.mockup.balance": "Sold disponibil",
    "home.mockup.currency": "EUR · Cont principal",
    "home.mockup.recharge": "Reîncărcare",
    "home.mockup.send": "Trimite",
    "home.mockup.save": "Economisește",
    "home.mockup.salary": "Salariu martie",
    "home.mockup.rent": "Chirie aprilie",
    "home.mockup.investment": "Investiție",
    "home.mockup.days2": "acum 2 zile",
    "home.mockup.days3": "acum 3 zile",
    "home.mockup.days5": "acum 5 zile",
    "home.stats.accounts": "Conturi active",
    "home.stats.uptime": "Disponibilitate",
    "home.stats.transfers": "Transferuri",
    "home.stats.support": "Suport",
    "home.features.label": "Funcționalități",
    "home.features.title": "Tot ce ai nevoie",
    "home.features.sub": "O platformă completă concepută pentru a simplifica gestionarea finanțelor tale zilnice.",
    "home.feature.security": "Securitate militară",
    "home.feature.security_body": "Criptare AES-256, autentificare cu doi factori și supraveghere 24/7 a contului tău.",
    "home.feature.cards": "Carduri virtual și fizic",
    "home.feature.cards_body": "Emite carduri în câteva secunde sau comandă un card fizic real livrat la domiciliu.",
    "home.feature.transfers": "Transferuri express",
    "home.feature.transfers_body": "Trimite și primește bani oriunde în lume în câteva secunde.",
    "home.feature.analytics": "Analiză în timp real",
    "home.feature.analytics_body": "Tablouri de bord inteligente pentru a-ți urmări cheltuielile și a-ți anticipa finanțele.",
    "home.feature.investments": "Investiții integrate",
    "home.feature.investments_body": "Accesează produse de investiții selectate direct din contul tău.",
    "home.feature.support": "Suport dedicat 24/7",
    "home.feature.support_body": "Un consilier bancar personal disponibil oricând prin mesagerie securizată.",
    "home.tpl.label": "Încredere",
    "home.tpl.title": "Au încredere în noi",
    "home.mockup.balance_label": "Sold",
    "home.mockup.virement": "Transfer",
    "home.mockup.salaire": "Salariu",
    "home.security.label": "Securitate",
    "home.security.title1": "Finanțele tale",
    "home.security.title2": "în siguranță absolută",
    "home.security.sub": "Aplicăm cele mai stricte standarde de securitate bancară pentru a proteja fiecare ban.",
    "home.security.aes.title": "Criptare AES-256",
    "home.security.aes.body": "Toate datele sensibile sunt criptate cu aceiași algoritmi utilizați de marile bănci internaționale.",
    "home.security.auth.title": "Autentificare consolidată",
    "home.security.auth.body": "Dublă verificare a identității la fiecare conectare și pentru fiecare operațiune sensibilă din contul tău.",
    "home.security.compliance.title": "Conformitate DSP2/RGPD",
    "home.security.compliance.body": "Respectăm toate reglementările europene în materie de servicii de plată și protecție a datelor.",
    "home.invest.label": "Investiții",
    "home.invest.title": "Fă-ți banii să crească",
    "home.invest.sub": "6 categorii de investiții selectate pentru potențialul și fiabilitatea lor.",
    "home.invest.yield": "Randament",
    "home.credits.label": "Credite",
    "home.credits.title": "Soluții adaptate fiecărui proiect",
    "home.credits.sub": "Rate competitive, răspunsuri rapide și consiliere personalizată pentru toate proiectele tale.",
    "home.credits.rate": "Rată",
    "home.faq.label": "FAQ",
    "home.faq.title": "Întrebări frecvente",
    "home.faq.q1": "Cum deschid un cont?",
    "home.faq.a1": "Înscrierea durează mai puțin de 2 minute. Trebuie doar să-ți completezi datele personale, să alegi o parolă și contul tău va fi activat după validarea echipei noastre.",
    "home.faq.q2": "Datele mele sunt securizate?",
    "home.faq.a2": "Da. Toate datele sunt criptate cu AES-256 în repaus și TLS 1.3 în tranzit. Respectăm standardele bancare internaționale și reglementările RGPD/DSP2.",
    "home.faq.q3": "Ce tipuri de investiții oferiți?",
    "home.faq.a3": "Oferim 6 categorii: Imobiliar, Crowdfunding, Energii regenerabile, Bursă, Participativ și Cripto. Fiecare produs este selectat pentru raportul său risc/randament.",
    "home.faq.q4": "Cum solicit un credit?",
    "home.faq.a4": "Accesează secțiunea Credit din zona ta de client, alege tipul de împrumut și indică suma și durata. Dosarul tău este procesat în 24h de consilierii noștri.",
    "home.faq.q5": "Există taxe ascunse?",
    "home.faq.a5": "Nicio taxă ascunsă. Toate tarifele sunt afișate clar la fiecare operațiune. Condițiile sunt accesibile în zona ta de client oricând.",
    "home.faq.q6": "Cum contactez suportul?",
    "home.faq.a6": "Utilizează mesageria integrată în zona ta de client pentru a contacta direct consilierul tău bancar personal, disponibil 24/7.",
    "home.cta.title": "Gata să începi?",
    "home.cta.sub": "Alătură-te miilor de utilizatori care își gestionează finanțele cu {bank}. Înscrierea durează mai puțin de 2 minute.",
    "home.footer.products": "Produse",
    "home.footer.services": "Servicii",
    "home.footer.access": "Acces rapid",
    "home.footer.accounts": "Conturi",
    "home.footer.transfers": "Transferuri",
    "home.footer.cards": "Carduri",
    "home.footer.savings": "Economii",
    "home.footer.investments": "Investiții",
    "home.footer.credits": "Credite",
    "home.footer.financing": "Finanțare",
    "home.footer.messages": "Mesaje",
    "home.footer.login": "Autentificare",
    "home.footer.signup": "Deschide un cont",
    "home.footer.support": "Suport 24/7",
    "home.footer.tagline": "Banca ta online, disponibilă oriunde și oricând.",
    "home.footer.copyright": "© {year} {bank} — Toate drepturile rezervate",
    "home.footer.powered": "Susținut de {bank}",
    "app.menu_label": "Meniu principal",
    "app.nav.dashboard": "Panou de bord",
    "app.nav.accounts": "Conturi",
    "app.nav.transactions": "Tranzacții",
    "app.nav.transfers": "Transferuri",
    "app.nav.cards": "Carduri",
    "app.nav.savings": "Economii",
    "app.nav.investments": "Investiții",
    "app.nav.credit": "Credit",
    "app.nav.financing": "Finanțare",
    "app.nav.messages": "Mesaje",
    "app.nav.card_tracking": "Urmărire card",
    "app.nav.notifications": "Notificări",
    "app.nav.settings": "Setări",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Acasă",
    "app.nav.mobile.transfers": "Transferuri",
    "app.nav.mobile.cards": "Carduri",
    "app.nav.mobile.transactions": "Tranzacții",
    "app.nav.more.invest": "Investește",
    "app.user_role": "Client",
    "app.logout": "Deconectare",
    "app.search": "Caută…",
    "app.more_title": "Mai multe funcționalități",
    "app.more_btn": "Mai mult",
    "app.loading": "Se încarcă…",
    "app.error_back": "Înapoi la autentificare",
    "app.see_more_notifs": "Vezi celelalte {count} notificări →",
    "page.accounts": "Conturile mele",
    "page.transactions": "Tranzacții",
    "page.transfers": "Transfer nou",
    "page.deposit": "Depunere",
    "page.cards": "Cardurile mele",
    "page.savings": "Economii",
    "page.investments": "Investiții",
    "page.credits": "Credit",
    "page.financing": "Finanțare proiecte",
    "page.messages": "Mesaje",
    "page.settings": "Setări",
    "page.notifications": "Notificări importante",
    "page.profile": "Profilul meu",
    "dash.greeting": "Bună, {name}",
    "dash.overview": "Privire de ansamblu asupra finanțelor tale — {date}",
    "dash.total_balance": "Sold total",
    "dash.this_month": "+{amount} luna aceasta",
    "dash.view_iban": "Vezi IBAN-ul meu",
    "dash.my_accounts": "Conturile mele",
    "dash.primary_account": "Cont principal",
    "dash.savings_account": "Cont de economii",
    "dash.remunerated": "Remunerat",
    "dash.details": "Detalii",
    "dash.transfer_btn": "Transfer",
    "dash.quick_actions": "Acțiuni rapide",
    "dash.quick.deposit": "Depunere",
    "deposit.title": "Informații despre depunere",
    "dash.quick.transfer": "Transfer",
    "dash.quick.history": "Istoric",
    "dash.quick.cards": "Cardurile mele",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Buget lunar",
    "dash.income": "Intrări",
    "dash.expenses": "Cheltuieli",
    "dash.spending_cat": "Cheltuieli pe categorii",
    "dash.no_spending": "Nicio cheltuială înregistrată.",
    "dash.recent_tx": "Tranzacții recente",
    "dash.see_all": "Vezi tot",
    "dash.no_tx": "Nicio operațiune.",
    "dash.main_card": "Cardul meu principal",
    "dash.manage": "Gestionează",
    "dash.block": "Blochează",
    "dash.pin": "Cod PIN",
    "dash.security": "Securitate",
    "dash.more": "Mai mult",
    "dash.no_card": "Niciun card emis",
    "dash.monthly_activity": "Activitatea lunii",
    "dash.operations": "Operațiuni",
    "dash.holder": "Titular",
    "dash.expires": "Exp.",
    "dash.visa_label": "VISA",
    "dash.balance": "Sold disponibil",
    "tx.all": "Toate",
    "tx.income": "Intrări",
    "tx.expenses": "Ieșiri",
    "tx.today": "Astăzi",
    "tx.yesterday": "Ieri",
    "tx.none": "Nicio tranzacție.",
    "transfer.title": "Transfer rapid",
    "transfer.recipient": "Destinatar",
    "transfer.recipient_ph": "Nume, IBAN, număr de telefon…",
    "transfer.amount": "Sumă",
    "transfer.label": "Descriere",
    "transfer.reason": "Motiv (opțional)",
    "transfer.reason_ph": "Ex.: chirie, rambursare…",
    "transfer.execution": "Executare",
    "transfer.immediate": "Imediată",
    "transfer.delayed": "Amânată",
    "transfer.balance": "Sold disponibil:",
    "transfer.continue": "Continuă",
    "transfer.confirm": "Confirmă",
    "transfer.cancel": "Anulează",
    "transfer.code": "Cod de validare",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Introdu codul primit pentru a valida transferul",
    "transfer.pending.title": "Transfer în așteptare de validare",
    "transfer.pending.amount": "Sumă:",
    "transfer.pending.to": "Către:",
    "transfer.pending.hint": "Codul dvs. de validare va fi trimis prin e-mail sau prin clopotul de notificări. Codul este format din 6 cifre. Dacă nu îl primiți, contactați suportul.",
    "transfer.pending.validate": "Validează transferul",
    "transfer.saved": "Transfer înregistrat. Cere codul administratorului tău pentru a valida.",
    "transfer.done": "Transfer efectuat",
    "transfer.validated": "Transfer validat!",
    "transfer.progress.processing": "Se procesează transferul…",
    "transfer.progress.completed": "Transfer efectuat!",
    "transfer.progress.default_message": "Transferul dumneavoastră este în curs de procesare. Vă rugăm să așteptați.",
    "cards.none": "Niciun card emis.\nContactează administratorul tău.",
    "cards.holder": "Titular",
    "cards.expires": "Exp.",
    "cards.block": "Blochează",
    "cards.pin": "Cod PIN",
    "cards.more": "Mai mult",
    "cards.limits": "Plafoane card",
    "cards.payment": "Plată",
    "cards.withdrawal": "Retragere",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Blocată",
    "cards.status_active": "Activă",
    "cards.toast.blocked": "Card blocat",
    "cards.toast.unblocked": "Card deblocat",
    "cards.toast.physical_ordered": "Card fizic comandat! Livrare în 5–10 zile lucrătoare.",
    "cards.toast.created": "Card generat cu succes!",
    "cards.none_sub": "Niciun card emis. Generează-ți cardul virtual acum.",
    "cards.generate_btn": "Generează cardul meu",
    "cards.create.title": "Generează cardul meu virtual",
    "cards.create.network": "Rețea de plată",
    "cards.create.color": "Culoarea cardului",
    "cards.create.preview_name": "NUMELE TĂU",
    "cards.create.preview_expiry": "LL/AA",
    "cards.create.loading": "Se generează…",
    "cards.create.submit": "Generează cardul",
    "cards.btn.unblock": "Deblochează",
    "cards.btn.block": "Blochează",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fizic",
    "cards.btn.physical_done": "Comandat",
    "cards.limits_title": "Plafoane",
    "cards.reveal.title": "Informații card",
    "cards.reveal.number": "Număr complet",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Expirare",
    "cards.reveal.warning": "Nu partaja niciodată aceste informații. Închide această fereastră după utilizare.",
    "cards.pin.title": "Cod PIN",
    "cards.pin.message": "Acest cod PIN este folosit pentru toate plățile tale online și retragerile. Nu îl comunica niciodată nimănui.",
    "cards.physical.title": "Comandă cardul fizic",
    "cards.physical.fee_label": "Taxă de livrare",
    "cards.physical.balance_label": "Soldul tău",
    "cards.physical.insufficient": "Sold insuficient. Te rugăm să reîncarci contul înainte de a comanda.",
    "cards.physical.info": "Taxa va fi debitată din contul tău. Livrare în 5–10 zile lucrătoare.",
    "cards.physical.order_btn": "Comandă ({fee})",
    "cards.physical.ordering": "Se comandă…",
    "cards.physical.order_cta": "Comandați un card fizic",
    "cards.physical.choose_type": "Alegeți cardul dvs.",
    "cards.physical.features": "Avantaje",
    "cards.buy_virtual_cta": "Cumpără un card virtual",
    "notif.none": "Nicio notificare pentru moment.",
    "notif.contact_support": "Contactați suportul",
    "profile.change_photo": "Schimbă fotografia",
    "profile.remove_photo": "Elimină fotografia",
    "profile.email": "Email",
    "profile.phone": "Telefon",
    "profile.country": "Țară",
    "profile.address": "Adresă",
    "profile.notifs_title": "Notificări ({count})",
    "profile.no_notifs": "Nicio notificare.",
    "profile.logout": "Deconectare",
    "profile.photo_updated": "Fotografie de profil actualizată",
    "profile.photo_removed": "Fotografie eliminată",
    "profile.photo_too_large": "Imagine prea mare (max. 1 MB)",
    "profile.powered": "Susținut de {bank}",
    "iban.title": "IBAN-ul meu",
    "iban.loading": "Se încarcă…",
    "iban.bic": "Cod BIC / SWIFT",
    "iban.share_info": "Partajează aceste date pentru a primi un transfer în contul tău.",
    "iban.copy": "Copiază",
    "iban.copied": "Copiat în clipboard",
    "iban.copy_fail": "Imposibil de copiat",
    "iban.close": "Închide",
    "accounts.all": "Toate conturile tale deschise la {bank}.",
    "accounts.primary": "Cont principal",
    "accounts.savings": "Cont de economii",
    "accounts.primary_tag": "Principal",
    "accounts.secondary_tag": "Secundar",
    "accounts.objective": "Obiectiv: {amount} · {pct}%",
    "accounts.copy": "Copiază",
    "accounts.loading": "Se încarcă…",
    "accounts.iban_copied": "IBAN copiat",
    "accounts.copy_fail": "Imposibil de copiat",
    "savings.total": "Total economisit",
    "savings.target_total": "Obiectiv total",
    "savings.progress": "Progres",
    "savings.active": "Obiective active",
    "savings.new_goal": "Obiectiv nou",
    "savings.none": "Niciun obiectiv de economisire deocamdată.",
    "savings.none_sub": "Creează-ți primul obiectiv pentru a începe.",
    "savings.saved": "Economisit:",
    "savings.goal": "Obiectiv:",
    "savings.deadline": "Scadență: {date}",
    "savings.deposit": "Depune",
    "savings.withdraw": "Retrage",
    "savings.loading": "Se încarcă…",
    "savings.form.title": "Nou obiectiv de economisire",
    "savings.form.cancel": "Anulează",
    "savings.form.create": "Creează",
    "savings.form.name": "Numele obiectivului",
    "savings.form.name_ph": "Călătorie în Japonia, Mașină, …",
    "savings.form.target": "Sumă țintă",
    "savings.form.initial": "Depunere inițială",
    "savings.form.deadline": "Dată limită (opțional)",
    "savings.form.color": "Culoare",
    "savings.deposit_title": "Depune în \"{name}\"",
    "savings.deposit_amount": "Sumă de depus",
    "savings.balance_hint": "Sold principal: {amount}",
    "savings.withdraw_prompt": "Cât retragi din \"{name}\"?",
    "savings.close_confirm": "Închizi obiectivul \"{name}\"? Economiile vor fi transferate în contul tău principal.",
    "savings.toast.created": "Obiectiv creat",
    "savings.toast.deposited": "Depunere efectuată",
    "savings.toast.withdrawn": "Retragere efectuată",
    "savings.toast.closed": "Obiectiv închis",
    "invest.total": "Total investit",
    "invest.profits": "Profituri cumulate",
    "invest.count": "Investiții",
    "invest.products": "Produse disponibile",
    "invest.invest_btn": "Investește",
    "invest.my": "Investițiile mele",
    "invest.pending": "În așteptare",
    "invest.validated": "Validat",
    "invest.refused": "Refuzat",
    "invest.withdraw.btn": "Retrage",
    "invest.withdraw.title": "Retrage investiția mea",
    "invest.withdraw.intro": "Sunteți pe cale să retrageți investiția „{product}\". Capitalul inițial și profitul acumulat vor fi creditate în soldul principal.",
    "invest.withdraw.principal": "Capital inițial",
    "invest.withdraw.profit": "Profit",
    "invest.withdraw.total": "Total de primit",
    "invest.withdraw.confirm": "Confirmă retragerea",
    "invest.withdraw.warning": "Această acțiune este ireversibilă. Investiția va fi închisă.",
    "invest.withdraw.success": "Investiție retrasă — {amount} creditați în sold.",
    "invest.withdrawn": "Retrasă",
    "invest.cta_sub": "Alege tipul de investiție care ți se potrivește. Un consilier te va însoți de la cererea ta.",
    "invest.form.amount": "Sumă de investit",
    "invest.form.balance": "Sold disponibil: {amount}",
    "invest.form.note": "Notă pentru consilierul tău (opțional)",
    "invest.form.note_ph": "Specifică așteptările tale…",
    "invest.form.advisor_info": "O conversație va fi deschisă automat cu consilierul tău.",
    "invest.form.cancel": "Anulează",
    "invest.form.submit": "Trimite cererea",
    "invest.toast.requested": "Cerere înregistrată — o conversație a fost deschisă cu consilierul tău.",
    "invest.p1.name": "Imobiliar",
    "invest.p1.desc": "SCPI, hârtie-piatră, crowdfunding imobiliar",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Finanțare participativă a proiectelor selectate",
    "invest.p3.name": "Energ. regenerab.",
    "invest.p3.desc": "Solară, eoliană și proiecte verzi",
    "invest.p4.name": "Bursă",
    "invest.p4.desc": "Acțiuni, ETF și fonduri diversificate",
    "invest.p5.name": "Participativ",
    "invest.p5.desc": "Împrumuturi pentru PME și antreprenori locali",
    "invest.p6.name": "Cripto",
    "invest.p6.desc": "Bitcoin, Ethereum și portofel multi-valută",
    "msg.online": "Online",
    "msg.loading": "Se încarcă…",
    "msg.none": "Niciun mesaj. Începe conversația cu consilierul tău.",
    "msg.placeholder": "Scrie mesajul tău…",
    "msg.attach.image": "Imagine",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Fișier / Document",
    "msg.pdf_doc": "Document PDF",
    "msg.file": "Fișier",
    "msg.too_large": "Fișier prea mare (max. {size}).",
    "msg.withdraw": "Retrage",
    "credits.cta_sub": "Efectuează cererea ta direct online — răspuns în 24h.",
    "credits.learn_more": "Află mai mult",
    "credits.request": "Solicită",
    "credits.history": "Cererile mele",
    "credits.status.pending": "În așteptare",
    "credits.status.accepted": "Acceptat",
    "credits.status.refused": "Refuzat",
    "credits.chars": "Caracteristici",
    "credits.amount_range": "Sumă: {min}€ – {max}€",
    "credits.rate_range": "Rata dobânzii: {min}% – {max}% pe an",
    "credits.duration_range": "Durată: {min} - {max} luni",
    "credits.deposit_min": "Depunere inițială: {amount}€ minim",
    "credits.garantie": "Garanție: {garantie}",
    "credits.eligibility": "Condiții de eligibilitate",
    "credits.elig.income": "Justifică venituri regulate și stabile",
    "credits.elig.deposit": "Efectuează o depunere inițială de {amount}€",
    "credits.elig.garantie": "Prezintă garanția: {garantie}",
    "credits.form.title": "Cerere — {name}",
    "credits.form.cancel": "Anulează",
    "credits.form.submit": "Trimite cererea",
    "credits.form.amount": "Sumă dorită (€)",
    "credits.form.duration": "Durată (luni)",
    "credits.form.reason": "Motiv (opțional)",
    "credits.form.reason_ph": "Specifică proiectul tău…",
    "credits.form.close": "Închide",
    "credits.toast.submitted": "Cerere de credit trimisă — vei primi un răspuns în 24h.",
    "credits.p1.name": "Împrumut Personal",
    "credits.p1.desc": "Pentru a finanța nevoile cotidiene sau proiectele personale.",
    "credits.p2.name": "Împrumut Imobiliar",
    "credits.p2.desc": "Pentru a finanța achiziția sau construcția unui bun imobiliar.",
    "credits.p3.name": "Împrumut Auto / Moto",
    "credits.p3.desc": "Cumpără un vehicul în toată liniștea.",
    "credits.p4.name": "Împrumut Student",
    "credits.p4.desc": "Un ajutor financiar pentru studiile superioare.",
    "credits.p5.name": "Împrumut Profesional",
    "credits.p5.desc": "Pentru a susține creșterea unei activități profesionale.",
    "credits.p6.name": "Împrumut Express",
    "credits.p6.desc": "O soluție rapidă pentru nevoile urgente.",
    "financing.reimbursable": "Finanțare rambursabilă",
    "financing.reimbursable_desc": "Un sprijin financiar cu condiții fixe pentru a lansa sau dezvolta o activitate.",
    "financing.non_reimbursable": "Finanțare nerambursabilă",
    "financing.non_reimbursable_desc": "Pentru proiecte cu impact social, educațional sau umanitar (ONG, asociații).",
    "financing.cta_sub": "Soluții adaptate pentru a-ți concretiza ambițiile.",
    "financing.request": "Solicită o finanțare",
    "financing.history": "Cererile mele",
    "financing.reimbursable_label": "Rambursabilă",
    "financing.non_reimbursable_label": "Nerambursabilă",
    "financing.status.pending": "În așteptare",
    "financing.status.granted": "Acordată",
    "financing.status.refused": "Refuzată",
    "financing.form.title": "Cerere — {name}",
    "financing.form.cancel": "Anulează",
    "financing.form.submit": "Trimite cererea",
    "financing.form.project": "Numele proiectului",
    "financing.form.project_ph": "Proiectul meu",
    "financing.form.amount": "Sumă solicitată (€)",
    "financing.form.sector": "Sector",
    "financing.form.sector_ph": "Tehnologie, agricultură, …",
    "financing.form.description": "Descrierea proiectului",
    "financing.form.description_ph": "Prezintă proiectul tău, obiectivele și impactul așteptat…",
    "settings.security": "Securitate",
    "settings.current_pw": "Parolă actuală",
    "settings.new_pw": "Parolă nouă",
    "settings.confirm_pw": "Confirmă",
    "settings.submit": "Schimbă parola",
    "settings.no_match": "Parolele nu se potrivesc",
    "settings.changed": "Parolă modificată",
    "common.cancel": "Anulează",
    "common.confirm": "Confirmă",
    "common.close": "Închide",
    "common.save": "Salvează",
    "common.loading": "Se încarcă…",
    "common.send": "Trimite",
    "common.total": "Total",
    "admin.wallet.balance": "Sold disponibil",
    "admin.wallet.history": "Istoric",
    "admin.wallet.no_history": "Nicio tranzacție",
    "admin.wallet.title": "Portofelul meu",
    "admin.wallet.topup": "Reîncarcă",
    "beneficiary.pick": "Selectează",
    "beneficiary.save_this": "Salvează acest beneficiar",
    "beneficiary.saved": "Beneficiari salvați",
    "beneficiary.saved_ok": "Beneficiar salvat",
    "card.color.amber": "Chihlimbar",
    "card.color.cyan": "Cian",
    "card.color.emerald": "Smarald",
    "card.color.forest": "Verde pădure",
    "card.color.gold": "Auriu",
    "card.color.lavender": "Lavandă",
    "card.color.night": "Noapte",
    "card.color.orange": "Portocaliu",
    "card.color.pink": "Roz",
    "card.color.royal": "Albastru regal",
    "card.color.ruby": "Rubin",
    "card.color.slate": "Ardezie",
    "card.color.steel": "Oțel",
    "card.color.terracotta": "Teracotă",
    "card.color.violet": "Violet",
    "common.other": "Altul",
    "common.search": "Caută",
    "content.translated": "Conținut tradus automat",
    "credits.monthly_payment": "Rată lunară estimată",
    "credits.rate_annual": "/ an",
    "credits.rate_used": "Rată estimativă:",
    "credits.simulation": "Simulare rată lunară",
    "credits.total_cost": "Cost total estimat",
    "demo.banner": "Mod demonstrație — nicio acțiune reală",
    "demo.disabled": "Acțiune indisponibilă în modul demo",
    "deposit.default_instructions": "Contactați managerul pentru detalii despre depozit.",
    "error.back_home": "Înapoi la pagina principală",
    "error.bank_not_found": "Bancă negăsită",
    "error.unknown": "Eroare necunoscută",
    "kyc.file_read_error": "Fișierul nu poate fi citit",
    "kyc.file_too_large": "Fișier prea mare (max 5 MB)",
    "login.history": "Istoricul autentificărilor",
    "login.history.empty": "Nicio autentificare înregistrată.",
    "notif.dismiss": "Închide",
    "notif.locked": "Mesaj de la bancă",
    "tracking.error.not_found": "Numărul de urmărire nu a fost găsit. Verificați și încercați din nou.",
    "tracking.label": "Urmărire",
    "tracking.status.delivered": "Livrat",
    "tracking.status.preparation": "În pregătire",
    "tracking.status.shipped": "Expediat",
    "tracking.status.transit": "În tranzit",
    "transfer.code.copied": "Copiat",
    "transfer.code.copy": "Copiați",
    "transfer.code.received": "Codul dvs. de validare",
    "dash.welcome": "Bun venit",
    "common.delete": "Șterge",
    "cards.delete.title": "Șterge cardul",
    "cards.delete.confirm_msg": "Această acțiune este ireversibilă. Cardul va fi șters definitiv.",
    "cards.delete.physical_tooltip": "Card fizic comandat — ștergere imposibilă",
    "cards.order.confirmed": "Comandă confirmată!",
    "cards.order.confirmed_body": "Cardul dvs. fizic este în curs de pregătire. Folosiți acest număr pentru a urmări livrarea.",
    "cards.order.tracking_no": "Nr. urmărire",
    "cards.fee_deducted": "{amount} va fi dedus din soldul dvs.",
    "cards.insufficient_short": "Sold insuficient ({available} disponibil)",
    "transfer.section.beneficiary": "Beneficiar",
    "transfer.section.details": "Transfer",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bancă",
    "transfer.field.city": "Oraș",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bancă:",
    "transfer.progress.paused": "În așteptarea validării",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Variabil",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",

    "cards.deleted": "Card șters",

    "cards.feat.gold_exclusive": "Card de aur exclusiv",

    "cards.feat.cashback_05": "Cashback 0,5%",

    "cards.feat.buyer_protection": "Protecția cumpărătorului",

    "cards.feat.support_standard": "Suport standard",

    "cards.feat.plastic_premium": "Card de plastic premium",

    "cards.feat.cashback_1": "Cashback 1%",

    "cards.feat.travel_standard": "Asigurare de călătorie standard",

    "cards.feat.support_priority": "Suport prioritar",

    "cards.feat.metal_brushed": "Card din metal periat",

    "cards.feat.cashback_2_unlimited": "Cashback 2% nelimitat",

    "cards.feat.travel_premium": "Asigurare de călătorie premium",

    "cards.feat.vip_lounge": "Acces la saloane VIP",

    "cards.feat.support_247": "Asistență 24/7",

    "auth.demo.title": "Autentificare administrator",

    "auth.demo.subtitle": "Introduceți datele de administrator pentru a previzualiza demo-ul",
    "credits.garantie.none": "Niciuna",
    "credits.garantie.mortgage": "Ipotecă asupra bunului finanțat",
    "credits.garantie.vehicle": "Vehicul finanțat",
    "credits.garantie.parental": "Parentală sau bancară",
    "credits.garantie.professional": "Active profesionale sau garanție personală",
    "cards.reveal.pin_prompt": "Introduceți codul PIN de 4 cifre pentru a afișa informațiile cardului.",
    "cards.reveal.show": "Afișare",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Cele două coduri PIN nu se potrivesc",
    "auth.pin.digit_hint": "4 cifre",
  
  },
  de: {
    "notif.transfer_code.title": "Überweisungs-Bestätigungscode",
    "notif.transfer_code.body": "Kopieren Sie diesen Code, um die Überweisung zu bestätigen.",
    "notif.transfer_code.expires_in": "Verschwindet in {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} Monate",
    "credits.n_months": "{n} Monate",
    "msg.read": "Gelesen",
    "msg.sent": "Gesendet",
    "msg.attach_aria": "Datei anhängen",
    "login.device.unknown": "Unbekannter Browser",
    "login.device.mobile": "Mobil",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Computer",
    "login.recent": "Kürzlich",
    "err.invalid_credentials": "Ungültige Anmeldedaten",
    "err.bad_pin": "Falsche PIN",
    "err.account_blocked": "Konto gesperrt",
    "err.bad_current_pw": "Aktuelles Passwort ist falsch",
    "err.insufficient_funds": "Nicht genügend Guthaben, bitte aufladen.",
    "err.amount_above_savings": "Betrag übersteigt verfügbare Ersparnisse",
    "err.must_be_positive": "Wert muss positiv sein",
    "err.invalid_format": "Ungültiges Format",
    "err.email_taken": "E-Mail bereits in Verwendung",
    "err.card_not_found": "Karte nicht gefunden",
    "err.physical_card_already_ordered": "Physische Karte bereits bestellt",
    "err.beneficiary_not_found": "Empfänger nicht gefunden",
    "err.invalid_code": "Ungültiger Code",
    "err.no_transfer_code": "Kein Code für diese Überweisung",
    "err.link_expired": "Link ungültig oder abgelaufen",
    "err.thread_not_found": "Konversation nicht gefunden",
    "err.empty_message": "Leere Nachricht",
    "err.file_too_large": "Datei zu groß (max 20 MB)",
    "err.forbidden": "Zugriff verweigert",
    "err.bank_not_found": "Bank nicht gefunden",
    "err.signups_closed": "Anmeldungen für diese Bank sind geschlossen.",
    "err.access_suspended": "Zugriff vorübergehend gesperrt. Bitte kontaktieren Sie Ihre Bank.",
    "err.notif_not_closable": "Diese Benachrichtigung kann nicht geschlossen werden.",
    "auth.signup.tagline": "Werden Sie noch heute Mitglied",
    "auth.login.tagline": "Willkommen zurück!",
    "auth.signup.tagline_sub": "Eröffnen Sie Ihr Konto in 2 Minuten und nutzen Sie alle unsere Premium-Banking-Dienste.",
    "auth.login.tagline_sub": "Zugang zu Ihrem persönlichen Bereich und sichere Verwaltung Ihrer Finanzen.",
    "auth.back": "Zurück zur Startseite",
    "auth.bullet.security": "Bankensicherheit auf militärischem Niveau",
    "auth.bullet.transfers": "Sofortüberweisungen rund um die Uhr",
    "auth.bullet.investments": "Integrierte Investments und Ersparnisse",
    "auth.bullet.advisor": "Persönlicher Berater 7 Tage/7 verfügbar",
    "auth.footer": "Unterstützt von {bank}",
    "auth.signup.title": "Konto erstellen",
    "auth.login.title": "Anmelden",
    "auth.field.lastName": "Nachname",
    "auth.field.firstName": "Vorname(n)",
    "auth.field.country": "Land",
    "auth.field.country.placeholder": "— Land auswählen —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Adresse",
    "auth.field.address.placeholder": "Musterstraße 123, Berlin",
    "auth.field.email": "E-Mail",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Passwort",
    "auth.submit.signup": "Mein Konto erstellen",
    "auth.submit.login": "Anmelden",
    "auth.login.badge": "Melden Sie sich bei Ihrem Konto an",
    "auth.card.tagline": "Ihr Kundenbereich",
    "auth.submit.loading": "Wird geladen…",
    "auth.pin.title": "PIN-Code",
    "auth.pin.subtitle": "Geben Sie Ihren 4-stelligen PIN-Code ein",
    "auth.pin.label": "PIN-Code",
    "auth.pin.confirm": "PIN-Code bestätigen",
    "auth.pin.submit": "Bestätigen",
    "auth.pin.back": "Zurück zur Anmeldung",
    "auth.pin.length_error": "Der PIN-Code muss 4 Ziffern haben",
    "auth.switch.have_account": "Bereits ein Konto?",
    "auth.switch.login_link": "Anmelden",
    "auth.switch.no_account": "Noch kein Kunde?",
    "auth.error.select_country": "Bitte wählen Sie Ihr Land",
    "kyc.title": "Identitätsprüfung (KYC)",
    "kyc.sub": "Laden Sie Ihre Dokumente hoch, um Ihr Konto zu validieren. Alle Dokumente sind verschlüsselt und vertraulich.",
    "kyc.id_type": "Art des Ausweisdokuments *",
    "kyc.id_card": "Personalausweis",
    "kyc.passport": "Reisepass",
    "kyc.driver_license": "Führerschein",
    "kyc.recto_passport": "Vorderseite des Reisepasses *",
    "kyc.recto_id": "Vorderseite des Dokuments *",
    "kyc.verso": "Rückseite des Dokuments *",
    "kyc.selfie": "Selfie mit dem Ausweis in der Hand *",
    "kyc.address_proof": "Adressnachweis *",
    "kyc.hint_doc": "Bild oder PDF, max. 5 MB",
    "kyc.hint_selfie": "Klares Foto, Gesicht und Dokument sichtbar",
    "kyc.hint_address": "Rechnung, Kontoauszug, Bescheinigung… (max. 5 MB)",
    "kyc.upload": "Hochladen",
    "kyc.replace": "Ersetzen",
    "pending.title": "Konto ausstehend",
    "pending.body": "Ihre Anfrage wurde erfolgreich empfangen. Ein Administrator wird Ihre Unterlagen prüfen und Ihr Konto in Kürze aktivieren.",
    "pending.back": "Zurück zur Anmeldung",
    "home.nav.features": "Funktionen",
    "home.nav.security": "Sicherheit",
    "home.nav.invest": "Investment",
    "home.nav.credits": "Kredite",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Anmelden",
    "home.hero.tagline": "Online-Banking der nächsten Generation",
    "home.hero.title1": "Verwalten Sie Ihr Geld",
    "home.hero.title2": "mit Intelligenz",
    "home.hero.sub": "Eine vollständige Banking-Plattform — Überweisungen, Ersparnisse, Investments und Kredite von Ihrem Smartphone.",
    "home.hero.login": "Anmelden",
    "home.hero.trust.secure": "100% Sicher",
    "home.hero.trust.fast": "Schnelle Eröffnung",
    "home.hero.trust.encrypted": "Verschlüsselte Daten",
    "home.hero.trust.support": "24/7-Service",
    "home.mockup.balance": "Verfügbares Guthaben",
    "home.mockup.currency": "EUR · Hauptkonto",
    "home.mockup.recharge": "Aufladen",
    "home.mockup.send": "Senden",
    "home.mockup.save": "Sparen",
    "home.mockup.salary": "Gehalt März",
    "home.mockup.rent": "Miete April",
    "home.mockup.investment": "Investment",
    "home.mockup.days2": "vor 2 Tagen",
    "home.mockup.days3": "vor 3 Tagen",
    "home.mockup.days5": "vor 5 Tagen",
    "home.stats.accounts": "Aktive Konten",
    "home.stats.uptime": "Verfügbarkeit",
    "home.stats.transfers": "Überweisungen",
    "home.stats.support": "Support",
    "home.features.label": "Funktionen",
    "home.features.title": "Alles, was Sie brauchen",
    "home.features.sub": "Eine vollständige Plattform, die die tägliche Verwaltung Ihrer Finanzen vereinfacht.",
    "home.feature.security": "Militärische Sicherheit",
    "home.feature.security_body": "AES-256-Verschlüsselung, Zwei-Faktor-Authentifizierung und 24/7-Kontoüberwachung.",
    "home.feature.cards": "Virtuelle & physische Karten",
    "home.feature.cards_body": "Karten in Sekunden ausstellen oder eine echte physische Karte bestellen, die zu Ihnen geliefert wird.",
    "home.feature.transfers": "Express-Überweisungen",
    "home.feature.transfers_body": "Senden und empfangen Sie Geld überall auf der Welt in Sekunden.",
    "home.feature.analytics": "Echtzeit-Analyse",
    "home.feature.analytics_body": "Intelligente Dashboards zur Verfolgung Ihrer Ausgaben und zur Vorausplanung Ihrer Finanzen.",
    "home.feature.investments": "Integrierte Investments",
    "home.feature.investments_body": "Zugang zu ausgewählten Investmentprodukten direkt von Ihrem Konto.",
    "home.feature.support": "Dedizierter 24/7-Support",
    "home.feature.support_body": "Ein persönlicher Bankberater jederzeit über sicheres Messaging verfügbar.",
    "home.tpl.label": "Vertrauen",
    "home.tpl.title": "Sie vertrauen uns",
    "home.mockup.balance_label": "Guthaben",
    "home.mockup.virement": "Überweisung",
    "home.mockup.salaire": "Gehalt",
    "home.security.label": "Sicherheit",
    "home.security.title1": "Ihre Finanzen",
    "home.security.title2": "in absoluter Sicherheit",
    "home.security.sub": "Wir wenden die strengsten Banksicherheitsstandards an, um jeden Cent zu schützen.",
    "home.security.aes.title": "AES-256-Verschlüsselung",
    "home.security.aes.body": "Alle sensiblen Daten sind mit denselben Algorithmen verschlüsselt, die von großen internationalen Banken verwendet werden.",
    "home.security.auth.title": "Erweiterte Authentifizierung",
    "home.security.auth.body": "Doppelte Identitätsprüfung bei jeder Anmeldung und bei jedem sensiblen Vorgang auf Ihrem Konto.",
    "home.security.compliance.title": "PSD2/DSGVO-Konformität",
    "home.security.compliance.body": "Wir halten alle europäischen Vorschriften zu Zahlungsdiensten und Datenschutz ein.",
    "home.invest.label": "Investments",
    "home.invest.title": "Lassen Sie Ihr Geld wachsen",
    "home.invest.sub": "6 Investmentkategorien, ausgewählt nach Potenzial und Zuverlässigkeit.",
    "home.invest.yield": "Rendite",
    "home.credits.label": "Kredite",
    "home.credits.title": "Lösungen für jedes Projekt",
    "home.credits.sub": "Wettbewerbsfähige Zinsen, schnelle Antworten und persönliche Begleitung für alle Ihre Projekte.",
    "home.credits.rate": "Zinssatz",
    "home.faq.label": "FAQ",
    "home.faq.title": "Häufig gestellte Fragen",
    "home.faq.q1": "Wie eröffne ich ein Konto?",
    "home.faq.a1": "Die Registrierung dauert weniger als 2 Minuten. Geben Sie einfach Ihre persönlichen Daten ein, wählen Sie ein Passwort und Ihr Konto wird nach der Prüfung durch unser Team aktiviert.",
    "home.faq.q2": "Sind meine Daten sicher?",
    "home.faq.a2": "Ja. Alle Daten werden mit AES-256 im Ruhezustand und TLS 1.3 bei der Übertragung verschlüsselt. Wir halten internationale Bankenstandards und DSGVO/PSD2-Vorschriften ein.",
    "home.faq.q3": "Welche Investmentarten bieten Sie an?",
    "home.faq.a3": "Wir bieten 6 Kategorien an: Immobilien, Crowdfunding, Erneuerbare Energien, Börse, Partizipativ und Crypto. Jedes Produkt wird nach seinem Risiko-Rendite-Verhältnis ausgewählt.",
    "home.faq.q4": "Wie beantrage ich einen Kredit?",
    "home.faq.a4": "Gehen Sie zum Bereich Kredit in Ihrem Kundenbereich, wählen Sie die Darlehensart und geben Sie Betrag und Laufzeit ein. Ihr Antrag wird innerhalb von 24 Stunden von unseren Beratern bearbeitet.",
    "home.faq.q5": "Gibt es versteckte Gebühren?",
    "home.faq.a5": "Keine versteckten Gebühren. Alle Tarife werden bei jeder Transaktion klar angezeigt. Die Konditionen sind jederzeit in Ihrem Kundenbereich einsehbar.",
    "home.faq.q6": "Wie kontaktiere ich den Support?",
    "home.faq.a6": "Nutzen Sie das integrierte Messaging in Ihrem Kundenbereich, um direkt Ihren persönlichen Bankberater zu kontaktieren, der 24/7 erreichbar ist.",
    "home.cta.title": "Bereit loszulegen?",
    "home.cta.sub": "Schließen Sie sich Tausenden von Nutzern an, die ihre Finanzen mit {bank} verwalten. Die Registrierung dauert weniger als 2 Minuten.",
    "home.footer.products": "Produkte",
    "home.footer.services": "Dienste",
    "home.footer.access": "Schnellzugriff",
    "home.footer.accounts": "Konten",
    "home.footer.transfers": "Überweisungen",
    "home.footer.cards": "Karten",
    "home.footer.savings": "Ersparnisse",
    "home.footer.investments": "Investments",
    "home.footer.credits": "Kredite",
    "home.footer.financing": "Finanzierung",
    "home.footer.messages": "Nachrichten",
    "home.footer.login": "Anmelden",
    "home.footer.signup": "Konto eröffnen",
    "home.footer.support": "24/7-Support",
    "home.footer.tagline": "Ihre Online-Bank, überall und jederzeit verfügbar.",
    "home.footer.copyright": "© {year} {bank} — Alle Rechte vorbehalten",
    "home.footer.powered": "Unterstützt von {bank}",
    "app.menu_label": "Hauptmenü",
    "app.nav.dashboard": "Dashboard",
    "app.nav.accounts": "Konten",
    "app.nav.transactions": "Transaktionen",
    "app.nav.transfers": "Überweisungen",
    "app.nav.cards": "Karten",
    "app.nav.savings": "Ersparnisse",
    "app.nav.investments": "Investment",
    "app.nav.credit": "Kredit",
    "app.nav.financing": "Finanzierung",
    "app.nav.messages": "Nachrichten",
    "app.nav.card_tracking": "Kartenverfolgung",
    "app.nav.notifications": "Benachrichtigungen",
    "app.nav.settings": "Einstellungen",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Startseite",
    "app.nav.mobile.transfers": "Überweisungen",
    "app.nav.mobile.cards": "Karten",
    "app.nav.mobile.transactions": "Transaktionen",
    "app.nav.more.invest": "Investieren",
    "app.user_role": "Kunde",
    "app.logout": "Abmelden",
    "app.search": "Suchen…",
    "app.more_title": "Weitere Funktionen",
    "app.more_btn": "Mehr",
    "app.loading": "Wird geladen…",
    "app.error_back": "Zurück zur Anmeldung",
    "app.see_more_notifs": "{count} weitere Benachrichtigungen anzeigen →",
    "page.accounts": "Meine Konten",
    "page.transactions": "Transaktionen",
    "page.transfers": "Neue Überweisung",
    "page.deposit": "Einzahlung",
    "page.cards": "Meine Karten",
    "page.savings": "Ersparnisse",
    "page.investments": "Investment",
    "page.credits": "Kredit",
    "page.financing": "Projektfinanzierung",
    "page.messages": "Nachrichten",
    "page.settings": "Einstellungen",
    "page.notifications": "Wichtige Benachrichtigungen",
    "page.profile": "Mein Profil",
    "dash.greeting": "Hallo, {name}",
    "dash.overview": "Übersicht Ihrer Finanzen — {date}",
    "dash.total_balance": "Gesamtguthaben",
    "dash.this_month": "+{amount} diesen Monat",
    "dash.view_iban": "Meine IBAN anzeigen",
    "dash.my_accounts": "Meine Konten",
    "dash.primary_account": "Hauptkonto",
    "dash.savings_account": "Sparkonto",
    "dash.remunerated": "Verzinst",
    "dash.details": "Details",
    "dash.transfer_btn": "Überweisung",
    "dash.quick_actions": "Schnellaktionen",
    "dash.quick.deposit": "Einzahlung",
    "deposit.title": "Einzahlungsinformationen",
    "dash.quick.transfer": "Überweisung",
    "dash.quick.history": "Verlauf",
    "dash.quick.cards": "Meine Karten",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Monatsbudget",
    "dash.income": "Einnahmen",
    "dash.expenses": "Ausgaben",
    "dash.spending_cat": "Ausgaben nach Kategorie",
    "dash.no_spending": "Keine Ausgaben erfasst.",
    "dash.recent_tx": "Letzte Transaktionen",
    "dash.see_all": "Alle anzeigen",
    "dash.no_tx": "Keine Transaktionen.",
    "dash.main_card": "Meine Hauptkarte",
    "dash.manage": "Verwalten",
    "dash.block": "Sperren",
    "dash.pin": "PIN-Code",
    "dash.security": "Sicherheit",
    "dash.more": "Mehr",
    "dash.no_card": "Keine Karte ausgestellt",
    "dash.monthly_activity": "Monatsaktivität",
    "dash.operations": "Vorgänge",
    "dash.holder": "Inhaber",
    "dash.expires": "Läuft ab",
    "dash.visa_label": "VISA",
    "dash.balance": "Verfügbares Guthaben",
    "tx.all": "Alle",
    "tx.income": "Einnahmen",
    "tx.expenses": "Ausgaben",
    "tx.today": "Heute",
    "tx.yesterday": "Gestern",
    "tx.none": "Keine Transaktionen.",
    "transfer.title": "Schnellüberweisung",
    "transfer.recipient": "Empfänger",
    "transfer.recipient_ph": "Name, IBAN, Telefonnummer…",
    "transfer.amount": "Betrag",
    "transfer.label": "Bezeichnung",
    "transfer.reason": "Verwendungszweck (optional)",
    "transfer.reason_ph": "z.B.: Miete, Rückzahlung…",
    "transfer.execution": "Ausführung",
    "transfer.immediate": "Sofort",
    "transfer.delayed": "Verzögert",
    "transfer.balance": "Verfügbares Guthaben:",
    "transfer.continue": "Weiter",
    "transfer.confirm": "Bestätigen",
    "transfer.cancel": "Abbrechen",
    "transfer.code": "Validierungscode",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Geben Sie den erhaltenen Code ein, um die Überweisung zu bestätigen",
    "transfer.pending.title": "Überweisung wartet auf Bestätigung",
    "transfer.pending.amount": "Betrag:",
    "transfer.pending.to": "An:",
    "transfer.pending.hint": "Ihr Validierungscode wird per E-Mail oder über die Benachrichtigungsglocke gesendet. Der Code besteht aus 6 Ziffern. Falls Sie ihn nicht erhalten, kontaktieren Sie den Support.",
    "transfer.pending.validate": "Überweisung bestätigen",
    "transfer.saved": "Überweisung gespeichert. Fragen Sie Ihren Administrator nach dem Validierungscode.",
    "transfer.done": "Überweisung abgeschlossen",
    "transfer.validated": "Überweisung bestätigt!",
    "transfer.progress.processing": "Überweisung wird verarbeitet…",
    "transfer.progress.completed": "Überweisung ausgeführt!",
    "transfer.progress.default_message": "Ihre Überweisung wird bearbeitet. Bitte warten Sie.",
    "cards.none": "Keine Karte ausgestellt.\nKontaktieren Sie Ihren Administrator.",
    "cards.holder": "Inhaber",
    "cards.expires": "Läuft ab",
    "cards.block": "Sperren",
    "cards.pin": "PIN-Code",
    "cards.more": "Mehr",
    "cards.limits": "Kartenlimits",
    "cards.payment": "Zahlung",
    "cards.withdrawal": "Abhebung",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Gesperrt",
    "cards.status_active": "Aktiv",
    "cards.toast.blocked": "Karte gesperrt",
    "cards.toast.unblocked": "Karte entsperrt",
    "cards.toast.physical_ordered": "Physische Karte bestellt! Lieferung in 5–10 Werktagen.",
    "cards.toast.created": "Karte erfolgreich erstellt!",
    "cards.none_sub": "Keine Karte ausgestellt. Erstelle jetzt deine virtuelle Karte.",
    "cards.generate_btn": "Meine Karte erstellen",
    "cards.create.title": "Meine virtuelle Karte erstellen",
    "cards.create.network": "Zahlungsnetzwerk",
    "cards.create.color": "Kartenfarbe",
    "cards.create.preview_name": "DEIN NAME",
    "cards.create.preview_expiry": "MM/JJ",
    "cards.create.loading": "Wird erstellt…",
    "cards.create.submit": "Karte erstellen",
    "cards.btn.unblock": "Entsperren",
    "cards.btn.block": "Sperren",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Physisch",
    "cards.btn.physical_done": "Bestellt",
    "cards.limits_title": "Limits",
    "cards.reveal.title": "Karteninformationen",
    "cards.reveal.number": "Vollständige Nummer",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Ablaufdatum",
    "cards.reveal.warning": "Teile diese Informationen niemals. Schließe dieses Fenster nach der Nutzung.",
    "cards.pin.title": "PIN-Code",
    "cards.pin.message": "Dieser PIN-Code wird für alle deine Online-Zahlungen und Abhebungen verwendet. Teile ihn niemals mit jemandem.",
    "cards.physical.title": "Physische Karte bestellen",
    "cards.physical.fee_label": "Liefergebühr",
    "cards.physical.balance_label": "Dein Guthaben",
    "cards.physical.insufficient": "Unzureichendes Guthaben. Bitte lade dein Konto auf, bevor du bestellst.",
    "cards.physical.info": "Die Gebühr wird von deinem Konto abgebucht. Lieferung in 5–10 Werktagen.",
    "cards.physical.order_btn": "Bestellen ({fee})",
    "cards.physical.ordering": "Wird bestellt…",
    "cards.physical.order_cta": "Physische Karte bestellen",
    "cards.physical.choose_type": "Wähle deine Karte",
    "cards.physical.features": "Vorteile",
    "cards.buy_virtual_cta": "Virtuelle Karte kaufen",
    "notif.none": "Keine Benachrichtigungen im Moment.",
    "notif.contact_support": "Support kontaktieren",
    "profile.change_photo": "Foto ändern",
    "profile.remove_photo": "Foto entfernen",
    "profile.email": "E-Mail",
    "profile.phone": "Telefon",
    "profile.country": "Land",
    "profile.address": "Adresse",
    "profile.notifs_title": "Benachrichtigungen ({count})",
    "profile.no_notifs": "Keine Benachrichtigungen.",
    "profile.logout": "Abmelden",
    "profile.photo_updated": "Profilfoto aktualisiert",
    "profile.photo_removed": "Foto entfernt",
    "profile.photo_too_large": "Bild zu groß (max. 1 MB)",
    "profile.powered": "Unterstützt von {bank}",
    "iban.title": "Meine IBAN",
    "iban.loading": "Wird geladen…",
    "iban.bic": "BIC / SWIFT-Code",
    "iban.share_info": "Teilen Sie diese Daten, um eine Überweisung auf Ihr Konto zu erhalten.",
    "iban.copy": "Kopieren",
    "iban.copied": "In die Zwischenablage kopiert",
    "iban.copy_fail": "Kopieren nicht möglich",
    "iban.close": "Schließen",
    "accounts.all": "Alle Ihre Konten bei {bank}.",
    "accounts.primary": "Hauptkonto",
    "accounts.savings": "Sparkonto",
    "accounts.primary_tag": "Hauptkonto",
    "accounts.secondary_tag": "Nebenkonto",
    "accounts.objective": "Ziel: {amount} · {pct}%",
    "accounts.copy": "Kopieren",
    "accounts.loading": "Wird geladen…",
    "accounts.iban_copied": "IBAN kopiert",
    "accounts.copy_fail": "Kopieren nicht möglich",
    "savings.total": "Gesamt gespart",
    "savings.target_total": "Gesamtziel",
    "savings.progress": "Fortschritt",
    "savings.active": "Aktive Ziele",
    "savings.new_goal": "Neues Ziel",
    "savings.none": "Noch keine Sparziele.",
    "savings.none_sub": "Erstellen Sie Ihr erstes Ziel, um loszulegen.",
    "savings.saved": "Gespart:",
    "savings.goal": "Ziel:",
    "savings.deadline": "Frist: {date}",
    "savings.deposit": "Einzahlen",
    "savings.withdraw": "Abheben",
    "savings.loading": "Wird geladen…",
    "savings.form.title": "Neues Sparziel",
    "savings.form.cancel": "Abbrechen",
    "savings.form.create": "Erstellen",
    "savings.form.name": "Zielname",
    "savings.form.name_ph": "Japan-Reise, Auto, …",
    "savings.form.target": "Zielbetrag",
    "savings.form.initial": "Ersteinzahlung",
    "savings.form.deadline": "Frist (optional)",
    "savings.form.color": "Farbe",
    "savings.deposit_title": "Einzahlen auf \"{name}\"",
    "savings.deposit_amount": "Einzuzahlender Betrag",
    "savings.balance_hint": "Hauptguthaben: {amount}",
    "savings.withdraw_prompt": "Wie viel von \"{name}\" abheben?",
    "savings.close_confirm": "Ziel \"{name}\" schließen? Das Guthaben wird auf Ihr Hauptkonto zurückgebucht.",
    "savings.toast.created": "Ziel erstellt",
    "savings.toast.deposited": "Einzahlung erfolgt",
    "savings.toast.withdrawn": "Abhebung erfolgt",
    "savings.toast.closed": "Ziel geschlossen",
    "invest.total": "Gesamt investiert",
    "invest.profits": "Kumulierte Gewinne",
    "invest.count": "Investments",
    "invest.products": "Verfügbare Produkte",
    "invest.invest_btn": "Investieren",
    "invest.my": "Meine Investments",
    "invest.pending": "Ausstehend",
    "invest.validated": "Bestätigt",
    "invest.refused": "Abgelehnt",
    "invest.withdraw.btn": "Auszahlen",
    "invest.withdraw.title": "Investition zurückholen",
    "invest.withdraw.intro": "Sie sind dabei, Ihre Investition „{product}\" auszuzahlen. Das eingesetzte Kapital und die aufgelaufenen Gewinne werden Ihrem Hauptkonto gutgeschrieben.",
    "invest.withdraw.principal": "Eingesetztes Kapital",
    "invest.withdraw.profit": "Gewinne",
    "invest.withdraw.total": "Gesamtbetrag",
    "invest.withdraw.confirm": "Auszahlung bestätigen",
    "invest.withdraw.warning": "Diese Aktion ist unwiderruflich. Die Investition wird geschlossen.",
    "invest.withdraw.success": "Investition ausgezahlt — {amount} Ihrem Saldo gutgeschrieben.",
    "invest.withdrawn": "Ausgezahlt",
    "invest.cta_sub": "Wählen Sie die Art des Investments, die zu Ihnen passt. Ein Berater begleitet Sie ab Ihrer ersten Anfrage.",
    "invest.form.amount": "Zu investierender Betrag",
    "invest.form.balance": "Verfügbares Guthaben: {amount}",
    "invest.form.note": "Hinweis für Ihren Berater (optional)",
    "invest.form.note_ph": "Geben Sie Ihre Erwartungen an…",
    "invest.form.advisor_info": "Ein Gespräch wird automatisch mit Ihrem Berater geöffnet.",
    "invest.form.cancel": "Abbrechen",
    "invest.form.submit": "Anfrage senden",
    "invest.toast.requested": "Anfrage gespeichert — ein Gespräch mit Ihrem Berater wurde geöffnet.",
    "invest.p1.name": "Immobilien",
    "invest.p1.desc": "SCPI, Immobilienfonds, Immobilien-Crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Partizipative Finanzierung ausgewählter Projekte",
    "invest.p3.name": "Erneuerbare Energien",
    "invest.p3.desc": "Solar-, Wind- und Grünprojekte",
    "invest.p4.name": "Börse",
    "invest.p4.desc": "Aktien, ETF und diversifizierte Fonds",
    "invest.p5.name": "Partizipativ",
    "invest.p5.desc": "Darlehen an PME und lokale Unternehmer",
    "invest.p6.name": "Crypto",
    "invest.p6.desc": "Bitcoin, Ethereum und Multi-Währungs-Wallet",
    "msg.online": "Online",
    "msg.loading": "Wird geladen…",
    "msg.none": "Keine Nachrichten. Beginnen Sie das Gespräch mit Ihrem Berater.",
    "msg.placeholder": "Schreiben Sie Ihre Nachricht…",
    "msg.attach.image": "Bild",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Datei / Dokument",
    "msg.pdf_doc": "PDF-Dokument",
    "msg.file": "Datei",
    "msg.too_large": "Datei zu groß (max. {size}).",
    "msg.withdraw": "Zurückziehen",
    "credits.cta_sub": "Stellen Sie Ihren Antrag direkt online — Antwort innerhalb von 24 Stunden.",
    "credits.learn_more": "Mehr erfahren",
    "credits.request": "Beantragen",
    "credits.history": "Meine Anträge",
    "credits.status.pending": "Ausstehend",
    "credits.status.accepted": "Akzeptiert",
    "credits.status.refused": "Abgelehnt",
    "credits.chars": "Merkmale",
    "credits.amount_range": "Betrag: {min}€ – {max}€",
    "credits.rate_range": "Zinssatz: {min}% – {max}% pro Jahr",
    "credits.duration_range": "Laufzeit: {min} bis {max} Monate",
    "credits.deposit_min": "Ersteinzahlung: mindestens {amount}€",
    "credits.garantie": "Sicherheit: {garantie}",
    "credits.eligibility": "Anspruchsvoraussetzungen",
    "credits.elig.income": "Nachweis regelmäßiger und stabiler Einkünfte",
    "credits.elig.deposit": "Ersteinzahlung von {amount}€ leisten",
    "credits.elig.garantie": "Sicherheit vorlegen: {garantie}",
    "credits.form.title": "Antrag — {name}",
    "credits.form.cancel": "Abbrechen",
    "credits.form.submit": "Antrag senden",
    "credits.form.amount": "Gewünschter Betrag (€)",
    "credits.form.duration": "Laufzeit (Monate)",
    "credits.form.reason": "Verwendungszweck (optional)",
    "credits.form.reason_ph": "Beschreiben Sie Ihr Vorhaben…",
    "credits.form.close": "Schließen",
    "credits.toast.submitted": "Kreditantrag gesendet — Sie erhalten innerhalb von 24 Stunden eine Antwort.",
    "credits.p1.name": "Privatkredit",
    "credits.p1.desc": "Zur Finanzierung alltäglicher Bedürfnisse oder persönlicher Projekte.",
    "credits.p2.name": "Immobilienkredit",
    "credits.p2.desc": "Zur Finanzierung des Kaufs oder Baus einer Immobilie.",
    "credits.p3.name": "Auto-/Motorradkredit",
    "credits.p3.desc": "Kaufen Sie ein Fahrzeug sorgenfrei.",
    "credits.p4.name": "Studienkredit",
    "credits.p4.desc": "Finanzielle Unterstützung für das Hochschulstudium.",
    "credits.p5.name": "Unternehmenskredit",
    "credits.p5.desc": "Zur Unterstützung des Wachstums einer beruflichen Tätigkeit.",
    "credits.p6.name": "Express-Kredit",
    "credits.p6.desc": "Eine schnelle Lösung für dringende Bedürfnisse.",
    "financing.reimbursable": "Rückzahlbare Finanzierung",
    "financing.reimbursable_desc": "Finanzielle Unterstützung mit festen Bedingungen zur Gründung oder Entwicklung einer Tätigkeit.",
    "financing.non_reimbursable": "Nicht rückzahlbare Finanzierung",
    "financing.non_reimbursable_desc": "Für Projekte mit sozialem, bildungsbezogenem oder humanitärem Einfluss (ONG, Verbände).",
    "financing.cta_sub": "Maßgeschneiderte Lösungen, um Ihre Ambitionen zu verwirklichen.",
    "financing.request": "Finanzierung beantragen",
    "financing.history": "Meine Anträge",
    "financing.reimbursable_label": "Rückzahlbar",
    "financing.non_reimbursable_label": "Nicht rückzahlbar",
    "financing.status.pending": "Ausstehend",
    "financing.status.granted": "Gewährt",
    "financing.status.refused": "Abgelehnt",
    "financing.form.title": "Antrag — {name}",
    "financing.form.cancel": "Abbrechen",
    "financing.form.submit": "Antrag senden",
    "financing.form.project": "Projektname",
    "financing.form.project_ph": "Mein Projekt",
    "financing.form.amount": "Beantragter Betrag (€)",
    "financing.form.sector": "Sektor",
    "financing.form.sector_ph": "Tech, Landwirtschaft, …",
    "financing.form.description": "Projektbeschreibung",
    "financing.form.description_ph": "Stellen Sie Ihr Projekt, seine Ziele und den erwarteten Einfluss vor…",
    "settings.security": "Sicherheit",
    "settings.current_pw": "Aktuelles Passwort",
    "settings.new_pw": "Neues Passwort",
    "settings.confirm_pw": "Bestätigen",
    "settings.submit": "Passwort ändern",
    "settings.no_match": "Die Passwörter stimmen nicht überein",
    "settings.changed": "Passwort geändert",
    "common.cancel": "Abbrechen",
    "common.confirm": "Bestätigen",
    "common.close": "Schließen",
    "common.save": "Speichern",
    "common.loading": "Wird geladen…",
    "common.send": "Senden",
    "common.total": "Gesamt",
    "admin.wallet.balance": "Verfügbares Guthaben",
    "admin.wallet.history": "Verlauf",
    "admin.wallet.no_history": "Keine Transaktionen",
    "admin.wallet.title": "Meine Brieftasche",
    "admin.wallet.topup": "Aufladen",
    "beneficiary.pick": "Auswählen",
    "beneficiary.save_this": "Diesen Begünstigten speichern",
    "beneficiary.saved": "Gespeicherte Begünstigte",
    "beneficiary.saved_ok": "Begünstigter gespeichert",
    "card.color.amber": "Bernstein",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Smaragd",
    "card.color.forest": "Waldgrün",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavendel",
    "card.color.night": "Nacht",
    "card.color.orange": "Orange",
    "card.color.pink": "Rosa",
    "card.color.royal": "Königsblau",
    "card.color.ruby": "Rubin",
    "card.color.slate": "Schiefer",
    "card.color.steel": "Stahl",
    "card.color.terracotta": "Terrakotta",
    "card.color.violet": "Violett",
    "common.other": "Sonstiges",
    "common.search": "Suchen",
    "content.translated": "Automatisch übersetzter Inhalt",
    "credits.monthly_payment": "Geschätzte Monatsrate",
    "credits.rate_annual": "/ Jahr",
    "credits.rate_used": "Geschätzter Zinssatz:",
    "credits.simulation": "Simulation der Monatsrate",
    "credits.total_cost": "Geschätzte Gesamtkosten",
    "demo.banner": "Demonstrationsmodus — keine echten Aktionen",
    "demo.disabled": "Aktion im Demo-Modus nicht verfügbar",
    "deposit.default_instructions": "Kontaktieren Sie Ihren Berater für die Einzahlungsmodalitäten.",
    "error.back_home": "Zurück zur Startseite",
    "error.bank_not_found": "Bank nicht gefunden",
    "error.unknown": "Unbekannter Fehler",
    "kyc.file_read_error": "Datei kann nicht gelesen werden",
    "kyc.file_too_large": "Datei zu groß (max. 5 MB)",
    "login.history": "Anmeldungshistorie",
    "login.history.empty": "Keine Anmeldung aufgezeichnet.",
    "notif.dismiss": "Schließen",
    "notif.locked": "Nachricht der Bank",
    "tracking.error.not_found": "Sendungsnummer nicht gefunden. Prüfen und erneut versuchen.",
    "tracking.label": "Sendungsverfolgung",
    "tracking.status.delivered": "Geliefert",
    "tracking.status.preparation": "In Vorbereitung",
    "tracking.status.shipped": "Versandt",
    "tracking.status.transit": "Im Transit",
    "transfer.code.copied": "Kopiert",
    "transfer.code.copy": "Kopieren",
    "transfer.code.received": "Ihr Validierungscode",
    "dash.welcome": "Willkommen",
    "common.delete": "Löschen",
    "cards.delete.title": "Karte löschen",
    "cards.delete.confirm_msg": "Diese Aktion ist unumkehrbar. Die Karte wird dauerhaft gelöscht.",
    "cards.delete.physical_tooltip": "Physische Karte bestellt — Löschen nicht möglich",
    "cards.order.confirmed": "Bestellung bestätigt!",
    "cards.order.confirmed_body": "Ihre physische Karte wird vorbereitet. Verwenden Sie diese Nummer, um Ihre Lieferung zu verfolgen.",
    "cards.order.tracking_no": "Tracking-Nr.",
    "cards.fee_deducted": "{amount} wird von Ihrem Guthaben abgezogen",
    "cards.insufficient_short": "Unzureichendes Guthaben ({available} verfügbar)",
    "transfer.section.beneficiary": "Begünstigter",
    "transfer.section.details": "Überweisung",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bank",
    "transfer.field.city": "Stadt",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bank:",
    "transfer.progress.paused": "Warte auf Bestätigung",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Immobilien",
    "home.invest.p.realestate_desc": "SCPI, Immobilien-Crowdfunding, Papier-Stein",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Schwarmfinanzierung ausgewählter Projekte",
    "home.invest.p.renewable": "Erneuerbare En.",
    "home.invest.p.renewable_desc": "Solar, Wind und grüne Projekte",
    "home.invest.p.stocks": "Börse",
    "home.invest.p.stocks_desc": "Aktien, ETFs und diversifizierte Fonds",
    "home.invest.p.participatory": "P2P-Kredite",
    "home.invest.p.participatory_desc": "Darlehen an KMU und lokale Unternehmer",
    "home.invest.p.crypto": "Krypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum und Multi-Währungs-Wallet",
    "home.invest.p.variable": "Variabel",
    "home.credits.p.personal": "Privatkredit",
    "home.credits.p.realestate": "Immobilienkredit",
    "home.credits.p.auto": "Auto-/Motorradkredit",
    "home.credits.p.student": "Studienkredit",
    "home.credits.p.pro": "Geschäftskredit",
    "home.credits.p.express": "Sofortkredit",
    "home.credits.rate.from": "ab {rate}/Jahr",
    "cards.deleted": "Karte gelöscht",
    "cards.feat.gold_exclusive": "Exklusive Goldkarte",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Käuferschutz",
    "cards.feat.support_standard": "Standard-Support",
    "cards.feat.plastic_premium": "Premium-Plastikkarte",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Standard-Reiseversicherung",
    "cards.feat.support_priority": "Priorität-Support",
    "cards.feat.metal_brushed": "Karte aus gebürstetem Metall",
    "cards.feat.cashback_2_unlimited": "Unbegrenzter 2% Cashback",
    "cards.feat.travel_premium": "Premium-Reiseversicherung",
    "cards.feat.vip_lounge": "VIP-Lounge-Zugang",
    "cards.feat.support_247": "24/7 Unterstützung",
    "auth.demo.title": "Administrator-Anmeldung",
    "auth.demo.subtitle": "Geben Sie Ihre Admin-Anmeldedaten ein, um die Demo anzuzeigen",
    "credits.garantie.none": "Keine",
    "credits.garantie.mortgage": "Hypothek auf die finanzierte Immobilie",
    "credits.garantie.vehicle": "Finanziertes Fahrzeug",
    "credits.garantie.parental": "Elterliche oder bankübliche Bürgschaft",
    "credits.garantie.professional": "Betriebsvermögen oder persönliche Bürgschaft",
    "cards.reveal.pin_prompt": "Geben Sie Ihre 4-stellige PIN ein, um die Karteninformationen anzuzeigen.",
    "cards.reveal.show": "Anzeigen",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Die beiden PINs stimmen nicht überein",
    "auth.pin.digit_hint": "4 Ziffern",
  
  },
  nl: {
    "notif.transfer_code.title": "Bevestigingscode voor overschrijving",
    "notif.transfer_code.body": "Kopieer deze code om uw overschrijving te valideren.",
    "notif.transfer_code.expires_in": "Verdwijnt over {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} maanden",
    "credits.n_months": "{n} maanden",
    "msg.read": "Gelezen",
    "msg.sent": "Verzonden",
    "msg.attach_aria": "Bestand toevoegen",
    "login.device.unknown": "Onbekende browser",
    "login.device.mobile": "Mobiel",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Computer",
    "login.recent": "Recent",
    "err.invalid_credentials": "Ongeldige inloggegevens",
    "err.bad_pin": "Onjuiste PIN",
    "err.account_blocked": "Account geblokkeerd",
    "err.bad_current_pw": "Huidig wachtwoord is onjuist",
    "err.insufficient_funds": "Onvoldoende saldo, laad uw account op.",
    "err.amount_above_savings": "Bedrag overschrijdt beschikbaar spaargeld",
    "err.must_be_positive": "Waarde moet positief zijn",
    "err.invalid_format": "Ongeldig formaat",
    "err.email_taken": "E-mail al in gebruik",
    "err.card_not_found": "Kaart niet gevonden",
    "err.physical_card_already_ordered": "Fysieke kaart al besteld",
    "err.beneficiary_not_found": "Begunstigde niet gevonden",
    "err.invalid_code": "Ongeldige code",
    "err.no_transfer_code": "Geen code voor deze overboeking",
    "err.link_expired": "Link ongeldig of verlopen",
    "err.thread_not_found": "Gesprek niet gevonden",
    "err.empty_message": "Leeg bericht",
    "err.file_too_large": "Bestand te groot (max 20 MB)",
    "err.forbidden": "Toegang geweigerd",
    "err.bank_not_found": "Bank niet gevonden",
    "err.signups_closed": "Registraties zijn gesloten voor deze bank.",
    "err.access_suspended": "Toegang tijdelijk opgeschort. Neem contact op met uw bank.",
    "err.notif_not_closable": "Deze melding kan niet worden gesloten.",
    "auth.signup.tagline": "Word vandaag lid",
    "auth.login.tagline": "Welkom terug!",
    "auth.signup.tagline_sub": "Open uw rekening in 2 minuten en krijg toegang tot al onze premium bankdiensten.",
    "auth.login.tagline_sub": "Toegang tot uw persoonlijke ruimte en beheer uw financiën veilig.",
    "auth.back": "Terug naar de startpagina",
    "auth.bullet.security": "Bankbeveiliging op militair niveau",
    "auth.bullet.transfers": "Directe overboekingen 24/7",
    "auth.bullet.investments": "Geïntegreerde beleggingen en spaargeld",
    "auth.bullet.advisor": "Persoonlijk adviseur beschikbaar 7 dagen/7",
    "auth.footer": "Aangedreven door {bank}",
    "auth.signup.title": "Account aanmaken",
    "auth.login.title": "Inloggen",
    "auth.field.lastName": "Achternaam",
    "auth.field.firstName": "Voornaam/namen",
    "auth.field.country": "Land",
    "auth.field.country.placeholder": "— Selecteer uw land —",
    "auth.field.phone": "Telefoon",
    "auth.field.address": "Adres",
    "auth.field.address.placeholder": "Kerkstraat 123, Amsterdam",
    "auth.field.email": "E-mail",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Wachtwoord",
    "auth.submit.signup": "Mijn account aanmaken",
    "auth.submit.login": "Inloggen",
    "auth.login.badge": "Log in op uw account",
    "auth.card.tagline": "Uw klantenruimte",
    "auth.submit.loading": "Laden…",
    "auth.pin.title": "Pincode",
    "auth.pin.subtitle": "Voer uw 4-cijferige pincode in",
    "auth.pin.label": "Pincode",
    "auth.pin.confirm": "Bevestig PIN-code",
    "auth.pin.submit": "Bevestigen",
    "auth.pin.back": "Terug naar inloggen",
    "auth.pin.length_error": "De pincode moet 4 cijfers bevatten",
    "auth.switch.have_account": "Al een account?",
    "auth.switch.login_link": "Inloggen",
    "auth.switch.no_account": "Nog geen klant?",
    "auth.error.select_country": "Selecteer uw land",
    "kyc.title": "Identiteitsverificatie (KYC)",
    "kyc.sub": "Upload uw documenten om uw account te valideren. Alle documenten zijn versleuteld en vertrouwelijk.",
    "kyc.id_type": "Type identiteitsdocument *",
    "kyc.id_card": "Identiteitskaart",
    "kyc.passport": "Paspoort",
    "kyc.driver_license": "Rijbewijs",
    "kyc.recto_passport": "Voorkant paspoort *",
    "kyc.recto_id": "Voorkant document *",
    "kyc.verso": "Achterkant document *",
    "kyc.selfie": "Selfie met identiteitsbewijs in de hand *",
    "kyc.address_proof": "Adresbewijs *",
    "kyc.hint_doc": "Afbeelding of PDF, max. 5 MB",
    "kyc.hint_selfie": "Duidelijke foto, gezicht en document zichtbaar",
    "kyc.hint_address": "Rekening, afschrift, attest… (max. 5 MB)",
    "kyc.upload": "Uploaden",
    "kyc.replace": "Vervangen",
    "pending.title": "Account in behandeling",
    "pending.body": "Uw aanvraag is goed ontvangen. Een beheerder zal uw dossier controleren en uw account binnenkort activeren.",
    "pending.back": "Terug naar inloggen",
    "home.nav.features": "Functies",
    "home.nav.security": "Beveiliging",
    "home.nav.invest": "Belegging",
    "home.nav.credits": "Kredieten",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Inloggen",
    "home.hero.tagline": "Online bankieren van de volgende generatie",
    "home.hero.title1": "Beheer uw geld",
    "home.hero.title2": "met intelligentie",
    "home.hero.sub": "Een compleet bankplatform — overboekingen, spaargeld, beleggingen en kredieten vanaf uw smartphone.",
    "home.hero.login": "Inloggen",
    "home.hero.trust.secure": "100% Veilig",
    "home.hero.trust.fast": "Snelle opening",
    "home.hero.trust.encrypted": "Versleutelde gegevens",
    "home.hero.trust.support": "24/7-service",
    "home.mockup.balance": "Beschikbaar saldo",
    "home.mockup.currency": "EUR · Hoofdrekening",
    "home.mockup.recharge": "Opladen",
    "home.mockup.send": "Versturen",
    "home.mockup.save": "Sparen",
    "home.mockup.salary": "Salaris maart",
    "home.mockup.rent": "Huur april",
    "home.mockup.investment": "Belegging",
    "home.mockup.days2": "2 dagen geleden",
    "home.mockup.days3": "3 dagen geleden",
    "home.mockup.days5": "5 dagen geleden",
    "home.stats.accounts": "Actieve rekeningen",
    "home.stats.uptime": "Beschikbaarheid",
    "home.stats.transfers": "Overboekingen",
    "home.stats.support": "Ondersteuning",
    "home.features.label": "Functies",
    "home.features.title": "Alles wat u nodig heeft",
    "home.features.sub": "Een compleet platform ontworpen om het dagelijks beheer van uw financiën te vereenvoudigen.",
    "home.feature.security": "Militaire beveiliging",
    "home.feature.security_body": "AES-256-versleuteling, twee-factorauthenticatie en 24/7-accountbewaking.",
    "home.feature.cards": "Virtuele & fysieke kaarten",
    "home.feature.cards_body": "Geef in seconden kaarten uit of bestel een echte fysieke kaart die bij u thuis wordt bezorgd.",
    "home.feature.transfers": "Snelle overboekingen",
    "home.feature.transfers_body": "Stuur en ontvang geld overal ter wereld in seconden.",
    "home.feature.analytics": "Realtime analyse",
    "home.feature.analytics_body": "Slimme dashboards om uw uitgaven te volgen en uw financiën te anticiperen.",
    "home.feature.investments": "Geïntegreerde beleggingen",
    "home.feature.investments_body": "Toegang tot geselecteerde beleggingsproducten rechtstreeks vanuit uw rekening.",
    "home.feature.support": "Toegewijde 24/7-support",
    "home.feature.support_body": "Een persoonlijk bankadviseur altijd beschikbaar via beveiligde berichten.",
    "home.tpl.label": "Vertrouwen",
    "home.tpl.title": "Zij vertrouwen ons",
    "home.mockup.balance_label": "Saldo",
    "home.mockup.virement": "Overboeking",
    "home.mockup.salaire": "Salaris",
    "home.security.label": "Beveiliging",
    "home.security.title1": "Uw financiën",
    "home.security.title2": "in absolute veiligheid",
    "home.security.sub": "Wij passen de strengste bankbeveiligingsnormen toe om elke cent te beschermen.",
    "home.security.aes.title": "AES-256-versleuteling",
    "home.security.aes.body": "Alle gevoelige gegevens zijn versleuteld met dezelfde algoritmen als die van grote internationale banken.",
    "home.security.auth.title": "Versterkte authenticatie",
    "home.security.auth.body": "Dubbele identiteitsverificatie bij elke aanmelding en bij elke gevoelige handeling op uw rekening.",
    "home.security.compliance.title": "PSD2/AVG-conformiteit",
    "home.security.compliance.body": "Wij voldoen aan alle Europese regelgeving inzake betalingsdiensten en gegevensbescherming.",
    "home.invest.label": "Beleggingen",
    "home.invest.title": "Laat uw geld groeien",
    "home.invest.sub": "6 beleggingscategorieën geselecteerd op potentieel en betrouwbaarheid.",
    "home.invest.yield": "Rendement",
    "home.credits.label": "Kredieten",
    "home.credits.title": "Oplossingen voor elk project",
    "home.credits.sub": "Concurrerende tarieven, snelle antwoorden en persoonlijke begeleiding voor al uw projecten.",
    "home.credits.rate": "Rente",
    "home.faq.label": "FAQ",
    "home.faq.title": "Veelgestelde vragen",
    "home.faq.q1": "Hoe open ik een rekening?",
    "home.faq.a1": "Registratie duurt minder dan 2 minuten. Vul gewoon uw persoonlijke gegevens in, kies een wachtwoord en uw rekening wordt geactiveerd na validatie door ons team.",
    "home.faq.q2": "Zijn mijn gegevens veilig?",
    "home.faq.a2": "Ja. Alle gegevens worden versleuteld met AES-256 in rust en TLS 1.3 in transit. Wij voldoen aan internationale banknormen en AVG/PSD2-regelgeving.",
    "home.faq.q3": "Welke soorten beleggingen biedt u aan?",
    "home.faq.a3": "Wij bieden 6 categorieën: Vastgoed, Crowdfunding, Hernieuwbare energie, Beurs, Participatief en Crypto. Elk product wordt geselecteerd op zijn risico-rendementsverhouding.",
    "home.faq.q4": "Hoe vraag ik een krediet aan?",
    "home.faq.a4": "Ga naar de sectie Krediet in uw klantenruimte, kies het type lening en vul het bedrag en de looptijd in. Uw aanvraag wordt binnen 24 uur behandeld door onze adviseurs.",
    "home.faq.q5": "Zijn er verborgen kosten?",
    "home.faq.a5": "Geen verborgen kosten. Alle tarieven worden duidelijk weergegeven bij elke transactie. De voorwaarden zijn op elk moment beschikbaar in uw klantenruimte.",
    "home.faq.q6": "Hoe neem ik contact op met support?",
    "home.faq.a6": "Gebruik de geïntegreerde berichten in uw klantenruimte om rechtstreeks contact op te nemen met uw persoonlijk bankadviseur, 24/7 beschikbaar.",
    "home.cta.title": "Klaar om te beginnen?",
    "home.cta.sub": "Sluit u aan bij duizenden gebruikers die hun financiën beheren met {bank}. Registratie duurt minder dan 2 minuten.",
    "home.footer.products": "Producten",
    "home.footer.services": "Diensten",
    "home.footer.access": "Snelle toegang",
    "home.footer.accounts": "Rekeningen",
    "home.footer.transfers": "Overboekingen",
    "home.footer.cards": "Kaarten",
    "home.footer.savings": "Spaargeld",
    "home.footer.investments": "Beleggingen",
    "home.footer.credits": "Kredieten",
    "home.footer.financing": "Financiering",
    "home.footer.messages": "Berichten",
    "home.footer.login": "Inloggen",
    "home.footer.signup": "Rekening openen",
    "home.footer.support": "24/7-support",
    "home.footer.tagline": "Uw online bank, overal en altijd beschikbaar.",
    "home.footer.copyright": "© {year} {bank} — Alle rechten voorbehouden",
    "home.footer.powered": "Aangedreven door {bank}",
    "app.menu_label": "Hoofdmenu",
    "app.nav.dashboard": "Dashboard",
    "app.nav.accounts": "Rekeningen",
    "app.nav.transactions": "Transacties",
    "app.nav.transfers": "Overboekingen",
    "app.nav.cards": "Kaarten",
    "app.nav.savings": "Spaargeld",
    "app.nav.investments": "Belegging",
    "app.nav.credit": "Krediet",
    "app.nav.financing": "Financiering",
    "app.nav.messages": "Berichten",
    "app.nav.card_tracking": "Kaart volgen",
    "app.nav.notifications": "Meldingen",
    "app.nav.settings": "Instellingen",
    "app.nav.profile": "Profiel",
    "app.nav.mobile.home": "Startpagina",
    "app.nav.mobile.transfers": "Overboekingen",
    "app.nav.mobile.cards": "Kaarten",
    "app.nav.mobile.transactions": "Transacties",
    "app.nav.more.invest": "Beleggen",
    "app.user_role": "Klant",
    "app.logout": "Uitloggen",
    "app.search": "Zoeken…",
    "app.more_title": "Meer functies",
    "app.more_btn": "Meer",
    "app.loading": "Laden…",
    "app.error_back": "Terug naar inloggen",
    "app.see_more_notifs": "{count} meer meldingen bekijken →",
    "page.accounts": "Mijn rekeningen",
    "page.transactions": "Transacties",
    "page.transfers": "Nieuwe overboeking",
    "page.deposit": "Storting",
    "page.cards": "Mijn kaarten",
    "page.savings": "Spaargeld",
    "page.investments": "Belegging",
    "page.credits": "Krediet",
    "page.financing": "Projectfinanciering",
    "page.messages": "Berichten",
    "page.settings": "Instellingen",
    "page.notifications": "Belangrijke meldingen",
    "page.profile": "Mijn profiel",
    "dash.greeting": "Hallo, {name}",
    "dash.overview": "Overzicht van uw financiën — {date}",
    "dash.total_balance": "Totaal saldo",
    "dash.this_month": "+{amount} deze maand",
    "dash.view_iban": "Mijn IBAN bekijken",
    "dash.my_accounts": "Mijn rekeningen",
    "dash.primary_account": "Hoofdrekening",
    "dash.savings_account": "Spaarrekening",
    "dash.remunerated": "Rentedragend",
    "dash.details": "Details",
    "dash.transfer_btn": "Overboeking",
    "dash.quick_actions": "Snelle acties",
    "dash.quick.deposit": "Storting",
    "deposit.title": "Stortingsinformatie",
    "dash.quick.transfer": "Overboeking",
    "dash.quick.history": "Geschiedenis",
    "dash.quick.cards": "Mijn kaarten",
    "dash.quick.profile": "Profiel",
    "dash.monthly_budget": "Maandbudget",
    "dash.income": "Inkomsten",
    "dash.expenses": "Uitgaven",
    "dash.spending_cat": "Uitgaven per categorie",
    "dash.no_spending": "Geen uitgaven geregistreerd.",
    "dash.recent_tx": "Recente transacties",
    "dash.see_all": "Alles bekijken",
    "dash.no_tx": "Geen transacties.",
    "dash.main_card": "Mijn hoofdkaart",
    "dash.manage": "Beheren",
    "dash.block": "Blokkeren",
    "dash.pin": "PIN-code",
    "dash.security": "Beveiliging",
    "dash.more": "Meer",
    "dash.no_card": "Geen kaart uitgegeven",
    "dash.monthly_activity": "Maandactiviteit",
    "dash.operations": "Handelingen",
    "dash.holder": "Houder",
    "dash.expires": "Verloopt",
    "dash.visa_label": "VISA",
    "dash.balance": "Beschikbaar saldo",
    "tx.all": "Alle",
    "tx.income": "Inkomsten",
    "tx.expenses": "Uitgaven",
    "tx.today": "Vandaag",
    "tx.yesterday": "Gisteren",
    "tx.none": "Geen transacties.",
    "transfer.title": "Snelle overboeking",
    "transfer.recipient": "Ontvanger",
    "transfer.recipient_ph": "Naam, IBAN, telefoonnummer…",
    "transfer.amount": "Bedrag",
    "transfer.label": "Omschrijving",
    "transfer.reason": "Reden (optioneel)",
    "transfer.reason_ph": "Bijv.: huur, terugbetaling…",
    "transfer.execution": "Uitvoering",
    "transfer.immediate": "Direct",
    "transfer.delayed": "Uitgesteld",
    "transfer.balance": "Beschikbaar saldo:",
    "transfer.continue": "Doorgaan",
    "transfer.confirm": "Bevestigen",
    "transfer.cancel": "Annuleren",
    "transfer.code": "Validatiecode",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Voer de ontvangen code in om de overboeking te bevestigen",
    "transfer.pending.title": "Overboeking wacht op bevestiging",
    "transfer.pending.amount": "Bedrag:",
    "transfer.pending.to": "Aan:",
    "transfer.pending.hint": "Uw validatiecode wordt verzonden via e-mail of via de notificatiebel. De code bestaat uit 6 cijfers. Als u hem niet ontvangt, neem dan contact op met de ondersteuning.",
    "transfer.pending.validate": "Overboeking bevestigen",
    "transfer.saved": "Overboeking opgeslagen. Vraag uw beheerder om de validatiecode.",
    "transfer.done": "Overboeking voltooid",
    "transfer.validated": "Overboeking bevestigd!",
    "transfer.progress.processing": "Overboeking wordt verwerkt…",
    "transfer.progress.completed": "Overboeking voltooid!",
    "transfer.progress.default_message": "Uw overboeking wordt verwerkt. Even geduld alstublieft.",
    "cards.none": "Geen kaart uitgegeven.\nNeem contact op met uw beheerder.",
    "cards.holder": "Houder",
    "cards.expires": "Verloopt",
    "cards.block": "Vergrendelen",
    "cards.pin": "PIN-code",
    "cards.more": "Meer",
    "cards.limits": "Kaartlimieten",
    "cards.payment": "Betaling",
    "cards.withdrawal": "Opname",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Geblokkeerd",
    "cards.status_active": "Actief",
    "cards.toast.blocked": "Kaart geblokkeerd",
    "cards.toast.unblocked": "Kaart gedeblokkeerd",
    "cards.toast.physical_ordered": "Fysieke kaart besteld! Levering in 5–10 werkdagen.",
    "cards.toast.created": "Kaart succesvol aangemaakt!",
    "cards.none_sub": "Geen kaart uitgegeven. Genereer nu je virtuele kaart.",
    "cards.generate_btn": "Mijn kaart genereren",
    "cards.create.title": "Mijn virtuele kaart genereren",
    "cards.create.network": "Betalingsnetwerk",
    "cards.create.color": "Kaartkleur",
    "cards.create.preview_name": "UW NAAM",
    "cards.create.preview_expiry": "MM/JJ",
    "cards.create.loading": "Wordt gegenereerd…",
    "cards.create.submit": "Kaart genereren",
    "cards.btn.unblock": "Deblokkeren",
    "cards.btn.block": "Blokkeren",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fysiek",
    "cards.btn.physical_done": "Besteld",
    "cards.limits_title": "Limieten",
    "cards.reveal.title": "Kaartinformatie",
    "cards.reveal.number": "Volledig nummer",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Vervaldatum",
    "cards.reveal.warning": "Deel deze informatie nooit. Sluit dit venster na gebruik.",
    "cards.pin.title": "PIN-code",
    "cards.pin.message": "Deze PIN-code wordt gebruikt voor al uw online betalingen en opnames. Deel hem nooit met iemand.",
    "cards.physical.title": "Fysieke kaart bestellen",
    "cards.physical.fee_label": "Leveringskosten",
    "cards.physical.balance_label": "Uw saldo",
    "cards.physical.insufficient": "Onvoldoende saldo. Laad uw rekening op voordat u bestelt.",
    "cards.physical.info": "De kosten worden van uw rekening afgeschreven. Levering in 5–10 werkdagen.",
    "cards.physical.order_btn": "Bestellen ({fee})",
    "cards.physical.ordering": "Wordt besteld…",
    "cards.physical.order_cta": "Fysieke kaart bestellen",
    "cards.physical.choose_type": "Kies je kaart",
    "cards.physical.features": "Voordelen",
    "cards.buy_virtual_cta": "Virtuele kaart kopen",
    "notif.none": "Geen meldingen op dit moment.",
    "notif.contact_support": "Contact opnemen met support",
    "profile.change_photo": "Foto wijzigen",
    "profile.remove_photo": "Foto verwijderen",
    "profile.email": "E-mail",
    "profile.phone": "Telefoon",
    "profile.country": "Land",
    "profile.address": "Adres",
    "profile.notifs_title": "Meldingen ({count})",
    "profile.no_notifs": "Geen meldingen.",
    "profile.logout": "Uitloggen",
    "profile.photo_updated": "Profielfoto bijgewerkt",
    "profile.photo_removed": "Foto verwijderd",
    "profile.photo_too_large": "Afbeelding te groot (max. 1 MB)",
    "profile.powered": "Aangedreven door {bank}",
    "iban.title": "Mijn IBAN",
    "iban.loading": "Laden…",
    "iban.bic": "BIC / SWIFT-code",
    "iban.share_info": "Deel deze gegevens om een overboeking naar uw rekening te ontvangen.",
    "iban.copy": "Kopiëren",
    "iban.copied": "Gekopieerd naar klembord",
    "iban.copy_fail": "Kopiëren niet mogelijk",
    "iban.close": "Sluiten",
    "accounts.all": "Al uw rekeningen bij {bank}.",
    "accounts.primary": "Hoofdrekening",
    "accounts.savings": "Spaarrekening",
    "accounts.primary_tag": "Primair",
    "accounts.secondary_tag": "Secundair",
    "accounts.objective": "Doel: {amount} · {pct}%",
    "accounts.copy": "Kopiëren",
    "accounts.loading": "Laden…",
    "accounts.iban_copied": "IBAN gekopieerd",
    "accounts.copy_fail": "Kopiëren niet mogelijk",
    "savings.total": "Totaal gespaard",
    "savings.target_total": "Totaal doel",
    "savings.progress": "Voortgang",
    "savings.active": "Actieve doelen",
    "savings.new_goal": "Nieuw doel",
    "savings.none": "Nog geen spaardoelen.",
    "savings.none_sub": "Maak uw eerste doel aan om te beginnen.",
    "savings.saved": "Gespaard:",
    "savings.goal": "Doel:",
    "savings.deadline": "Deadline: {date}",
    "savings.deposit": "Storten",
    "savings.withdraw": "Opnemen",
    "savings.loading": "Laden…",
    "savings.form.title": "Nieuw spaardoel",
    "savings.form.cancel": "Annuleren",
    "savings.form.create": "Aanmaken",
    "savings.form.name": "Naam van het doel",
    "savings.form.name_ph": "Japan-reis, Auto, …",
    "savings.form.target": "Doelbedrag",
    "savings.form.initial": "Eerste storting",
    "savings.form.deadline": "Deadline (optioneel)",
    "savings.form.color": "Kleur",
    "savings.deposit_title": "Storten op \"{name}\"",
    "savings.deposit_amount": "Te storten bedrag",
    "savings.balance_hint": "Hoofdsaldo: {amount}",
    "savings.withdraw_prompt": "Hoeveel opnemen van \"{name}\"?",
    "savings.close_confirm": "Doel \"{name}\" sluiten? Het spaargeld wordt teruggestort op uw hoofdrekening.",
    "savings.toast.created": "Doel aangemaakt",
    "savings.toast.deposited": "Storting uitgevoerd",
    "savings.toast.withdrawn": "Opname uitgevoerd",
    "savings.toast.closed": "Doel gesloten",
    "invest.total": "Totaal belegd",
    "invest.profits": "Gecumuleerde winsten",
    "invest.count": "Beleggingen",
    "invest.products": "Beschikbare producten",
    "invest.invest_btn": "Beleggen",
    "invest.my": "Mijn beleggingen",
    "invest.pending": "In behandeling",
    "invest.validated": "Bevestigd",
    "invest.refused": "Geweigerd",
    "invest.withdraw.btn": "Uitbetalen",
    "invest.withdraw.title": "Mijn investering opvragen",
    "invest.withdraw.intro": "U staat op het punt uw investering \"{product}\" op te vragen. Het oorspronkelijke kapitaal en de opgebouwde winst worden bijgeschreven op uw hoofdsaldo.",
    "invest.withdraw.principal": "Hoofdsom",
    "invest.withdraw.profit": "Winst",
    "invest.withdraw.total": "Totaal te ontvangen",
    "invest.withdraw.confirm": "Uitbetaling bevestigen",
    "invest.withdraw.warning": "Deze actie is onomkeerbaar. De investering wordt gesloten.",
    "invest.withdraw.success": "Investering uitbetaald — {amount} bijgeschreven op uw saldo.",
    "invest.withdrawn": "Uitbetaald",
    "invest.cta_sub": "Kies het type belegging dat bij u past. Een adviseur begeleidt u vanaf uw eerste aanvraag.",
    "invest.form.amount": "Te beleggen bedrag",
    "invest.form.balance": "Beschikbaar saldo: {amount}",
    "invest.form.note": "Opmerking voor uw adviseur (optioneel)",
    "invest.form.note_ph": "Geef uw verwachtingen aan…",
    "invest.form.advisor_info": "Er wordt automatisch een gesprek geopend met uw adviseur.",
    "invest.form.cancel": "Annuleren",
    "invest.form.submit": "Aanvraag versturen",
    "invest.toast.requested": "Aanvraag geregistreerd — er is een gesprek geopend met uw adviseur.",
    "invest.p1.name": "Vastgoed",
    "invest.p1.desc": "SCPI, vastgoedfondsen, vastgoed-crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Participatieve financiering van geselecteerde projecten",
    "invest.p3.name": "Hernieuwbare energie",
    "invest.p3.desc": "Zonne-, wind- en groene projecten",
    "invest.p4.name": "Beurs",
    "invest.p4.desc": "Aandelen, ETF en gediversifieerde fondsen",
    "invest.p5.name": "Participatief",
    "invest.p5.desc": "Leningen aan PME en lokale ondernemers",
    "invest.p6.name": "Crypto",
    "invest.p6.desc": "Bitcoin, Ethereum en multi-valuta-portefeuille",
    "msg.online": "Online",
    "msg.loading": "Laden…",
    "msg.none": "Geen berichten. Start het gesprek met uw adviseur.",
    "msg.placeholder": "Schrijf uw bericht…",
    "msg.attach.image": "Afbeelding",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Bestand / Document",
    "msg.pdf_doc": "PDF-document",
    "msg.file": "Bestand",
    "msg.too_large": "Bestand te groot (max. {size}).",
    "msg.withdraw": "Terugtrekken",
    "credits.cta_sub": "Dien uw aanvraag rechtstreeks online in — antwoord binnen 24 uur.",
    "credits.learn_more": "Meer informatie",
    "credits.request": "Aanvragen",
    "credits.history": "Mijn aanvragen",
    "credits.status.pending": "In behandeling",
    "credits.status.accepted": "Geaccepteerd",
    "credits.status.refused": "Geweigerd",
    "credits.chars": "Kenmerken",
    "credits.amount_range": "Bedrag: {min}€ – {max}€",
    "credits.rate_range": "Rentevoet: {min}% – {max}% per jaar",
    "credits.duration_range": "Looptijd: {min} tot {max} maanden",
    "credits.deposit_min": "Eerste storting: minimaal {amount}€",
    "credits.garantie": "Garantie: {garantie}",
    "credits.eligibility": "Toelatingsvoorwaarden",
    "credits.elig.income": "Aantonen van regelmatige en stabiele inkomsten",
    "credits.elig.deposit": "Een eerste storting van {amount}€ doen",
    "credits.elig.garantie": "De garantie voorleggen: {garantie}",
    "credits.form.title": "Aanvraag — {name}",
    "credits.form.cancel": "Annuleren",
    "credits.form.submit": "Aanvraag versturen",
    "credits.form.amount": "Gewenst bedrag (€)",
    "credits.form.duration": "Looptijd (maanden)",
    "credits.form.reason": "Reden (optioneel)",
    "credits.form.reason_ph": "Beschrijf uw project…",
    "credits.form.close": "Sluiten",
    "credits.toast.submitted": "Kredietaanvraag verzonden — u ontvangt binnen 24 uur een antwoord.",
    "credits.p1.name": "Persoonlijke lening",
    "credits.p1.desc": "Voor de financiering van dagelijkse behoeften of persoonlijke projecten.",
    "credits.p2.name": "Hypotheeklening",
    "credits.p2.desc": "Voor de financiering van de aankoop of bouw van onroerend goed.",
    "credits.p3.name": "Auto- / Motolening",
    "credits.p3.desc": "Koop een voertuig zonder zorgen.",
    "credits.p4.name": "Studielening",
    "credits.p4.desc": "Financiële steun voor het hoger onderwijs.",
    "credits.p5.name": "Zakelijke lening",
    "credits.p5.desc": "Ter ondersteuning van de groei van een professionele activiteit.",
    "credits.p6.name": "Express-lening",
    "credits.p6.desc": "Een snelle oplossing voor dringende behoeften.",
    "financing.reimbursable": "Terugbetaalbare financiering",
    "financing.reimbursable_desc": "Financiële ondersteuning met vaste voorwaarden om een activiteit op te starten of te ontwikkelen.",
    "financing.non_reimbursable": "Niet-terugbetaalbare financiering",
    "financing.non_reimbursable_desc": "Voor projecten met sociale, educatieve of humanitaire impact (ONG, verenigingen).",
    "financing.cta_sub": "Oplossingen op maat om uw ambities werkelijkheid te maken.",
    "financing.request": "Financiering aanvragen",
    "financing.history": "Mijn aanvragen",
    "financing.reimbursable_label": "Terugbetaalbaar",
    "financing.non_reimbursable_label": "Niet-terugbetaalbaar",
    "financing.status.pending": "In behandeling",
    "financing.status.granted": "Toegekend",
    "financing.status.refused": "Geweigerd",
    "financing.form.title": "Aanvraag — {name}",
    "financing.form.cancel": "Annuleren",
    "financing.form.submit": "Aanvraag versturen",
    "financing.form.project": "Projectnaam",
    "financing.form.project_ph": "Mijn project",
    "financing.form.amount": "Gevraagd bedrag (€)",
    "financing.form.sector": "Sector",
    "financing.form.sector_ph": "Tech, landbouw, …",
    "financing.form.description": "Projectbeschrijving",
    "financing.form.description_ph": "Presenteer uw project, de doelstellingen en de verwachte impact…",
    "settings.security": "Beveiliging",
    "settings.current_pw": "Huidig wachtwoord",
    "settings.new_pw": "Nieuw wachtwoord",
    "settings.confirm_pw": "Bevestigen",
    "settings.submit": "Wachtwoord wijzigen",
    "settings.no_match": "Wachtwoorden komen niet overeen",
    "settings.changed": "Wachtwoord gewijzigd",
    "common.cancel": "Annuleren",
    "common.confirm": "Bevestigen",
    "common.close": "Sluiten",
    "common.save": "Opslaan",
    "common.loading": "Laden…",
    "common.send": "Versturen",
    "common.total": "Totaal",
    "admin.wallet.balance": "Beschikbaar saldo",
    "admin.wallet.history": "Geschiedenis",
    "admin.wallet.no_history": "Geen transacties",
    "admin.wallet.title": "Mijn portemonnee",
    "admin.wallet.topup": "Opladen",
    "beneficiary.pick": "Selecteren",
    "beneficiary.save_this": "Deze begunstigde opslaan",
    "beneficiary.saved": "Opgeslagen begunstigden",
    "beneficiary.saved_ok": "Begunstigde opgeslagen",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyaan",
    "card.color.emerald": "Smaragd",
    "card.color.forest": "Bos",
    "card.color.gold": "Goud",
    "card.color.lavender": "Lavendel",
    "card.color.night": "Nacht",
    "card.color.orange": "Oranje",
    "card.color.pink": "Roze",
    "card.color.royal": "Koningsblauw",
    "card.color.ruby": "Robijn",
    "card.color.slate": "Leisteen",
    "card.color.steel": "Staal",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Overig",
    "common.search": "Zoeken",
    "content.translated": "Automatisch vertaalde inhoud",
    "credits.monthly_payment": "Geschatte maandbetaling",
    "credits.rate_annual": "/ jaar",
    "credits.rate_used": "Geschatte rente:",
    "credits.simulation": "Simulatie maandbetaling",
    "credits.total_cost": "Geschatte totaalkosten",
    "demo.banner": "Demonstratiemodus — geen echte acties",
    "demo.disabled": "Actie niet beschikbaar in demo-modus",
    "deposit.default_instructions": "Neem contact op met uw beheerder voor de stortingsinstructies.",
    "error.back_home": "Terug naar home",
    "error.bank_not_found": "Bank niet gevonden",
    "error.unknown": "Onbekende fout",
    "kyc.file_read_error": "Bestand kan niet worden gelezen",
    "kyc.file_too_large": "Bestand te groot (max. 5 MB)",
    "login.history": "Aanmeldingsgeschiedenis",
    "login.history.empty": "Geen aanmelding geregistreerd.",
    "notif.dismiss": "Sluiten",
    "notif.locked": "Bericht van de bank",
    "tracking.error.not_found": "Trackingnummer niet gevonden. Controleer en probeer opnieuw.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Geleverd",
    "tracking.status.preparation": "In voorbereiding",
    "tracking.status.shipped": "Verzonden",
    "tracking.status.transit": "Onderweg",
    "transfer.code.copied": "Gekopieerd",
    "transfer.code.copy": "Kopiëren",
    "transfer.code.received": "Uw validatiecode",
    "dash.welcome": "Welkom",
    "common.delete": "Verwijderen",
    "cards.delete.title": "Kaart verwijderen",
    "cards.delete.confirm_msg": "Deze actie is onomkeerbaar. De kaart wordt permanent verwijderd.",
    "cards.delete.physical_tooltip": "Fysieke kaart besteld — verwijdering onmogelijk",
    "cards.order.confirmed": "Bestelling bevestigd!",
    "cards.order.confirmed_body": "Uw fysieke kaart wordt voorbereid. Gebruik dit nummer om uw levering te volgen.",
    "cards.order.tracking_no": "Trackingnummer",
    "cards.fee_deducted": "{amount} wordt van uw saldo afgetrokken",
    "cards.insufficient_short": "Onvoldoende saldo ({available} beschikbaar)",
    "transfer.section.beneficiary": "Begunstigde",
    "transfer.section.details": "Overboeking",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bank",
    "transfer.field.city": "Stad",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bank:",
    "transfer.progress.paused": "Wachten op validatie",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Vastgoed",
    "home.invest.p.realestate_desc": "SCPI, vastgoed-crowdfunding, papier-steen",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participatieve financiering van geselecteerde projecten",
    "home.invest.p.renewable": "Hernieuwbare en.",
    "home.invest.p.renewable_desc": "Zonne-, windenergie en groene projecten",
    "home.invest.p.stocks": "Beurs",
    "home.invest.p.stocks_desc": "Aandelen, ETF's en gespreide fondsen",
    "home.invest.p.participatory": "Participatief",
    "home.invest.p.participatory_desc": "Leningen aan KMO's en lokale ondernemers",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum en multi-valuta wallet",
    "home.invest.p.variable": "Variabel",
    "home.credits.p.personal": "Persoonlijke Lening",
    "home.credits.p.realestate": "Hypotheek",
    "home.credits.p.auto": "Auto-/Motorlening",
    "home.credits.p.student": "Studielening",
    "home.credits.p.pro": "Zakelijke Lening",
    "home.credits.p.express": "Express Lening",
    "home.credits.rate.from": "vanaf {rate}/jaar",
    "cards.deleted": "Kaart verwijderd",
    "cards.feat.gold_exclusive": "Exclusieve gouden kaart",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Kopersbescherming",
    "cards.feat.support_standard": "Standaard ondersteuning",
    "cards.feat.plastic_premium": "Premium plastic kaart",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Standaard reisverzekering",
    "cards.feat.support_priority": "Prioritaire ondersteuning",
    "cards.feat.metal_brushed": "Geborstelde metalen kaart",
    "cards.feat.cashback_2_unlimited": "Onbeperkt 2% cashback",
    "cards.feat.travel_premium": "Premium reisverzekering",
    "cards.feat.vip_lounge": "Toegang tot VIP-lounges",
    "cards.feat.support_247": "24/7 ondersteuning",
    "auth.demo.title": "Beheerderslogin",
    "auth.demo.subtitle": "Voer uw beheerdersgegevens in om de demo te bekijken",
    "credits.garantie.none": "Geen",
    "credits.garantie.mortgage": "Hypotheek op het gefinancierde goed",
    "credits.garantie.vehicle": "Gefinancierd voertuig",
    "credits.garantie.parental": "Ouderlijke of bancaire garantie",
    "credits.garantie.professional": "Professionele activa of persoonlijke borgstelling",
    "cards.reveal.pin_prompt": "Voer uw 4-cijferige PIN-code in om de kaartgegevens weer te geven.",
    "cards.reveal.show": "Weergeven",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "De twee PIN-codes komen niet overeen",
    "auth.pin.digit_hint": "4 cijfers",
  
  },
  lb: {
    "notif.transfer_code.title": "Iwwerweisungs-Validatiouns-Code",
    "notif.transfer_code.body": "Kopéiert dëse Code fir Är Iwwerweisung ze validéieren.",
    "notif.transfer_code.expires_in": "Verschwënnt an {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} Méint",
    "credits.n_months": "{n} Méint",
    "msg.read": "Gelies",
    "msg.sent": "Geschéckt",
    "msg.attach_aria": "Datei bäifügen",
    "login.device.unknown": "Onbekannte Browser",
    "login.device.mobile": "Mobil",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Computer",
    "login.recent": "Rezent",
    "err.invalid_credentials": "Ongëlteg Umelldungsdaten",
    "err.bad_pin": "Falschen PIN",
    "err.account_blocked": "Kont gespaart",
    "err.bad_current_pw": "Aktuellt Passwuert ass falsch",
    "err.insufficient_funds": "Net genuch Guthaben, lued Äre Kont op.",
    "err.amount_above_savings": "Betrag iwwerschreit déi disponibel Eparen",
    "err.must_be_positive": "Wäert muss positiv sinn",
    "err.invalid_format": "Ongëltegt Format",
    "err.email_taken": "E-Mail schonn benotzt",
    "err.card_not_found": "Kaart net fonnt",
    "err.physical_card_already_ordered": "Physesch Kaart schonn bestallt",
    "err.beneficiary_not_found": "Empfänger net fonnt",
    "err.invalid_code": "Ongëltege Code",
    "err.no_transfer_code": "Kee Code fir dës Iwwerweisung",
    "err.link_expired": "Link ongëlteg oder ofgelaaf",
    "err.thread_not_found": "Gespréich net fonnt",
    "err.empty_message": "Eidel Noriicht",
    "err.file_too_large": "Datei ze grouss (max 20 MB)",
    "err.forbidden": "Zougrëff verweigert",
    "err.bank_not_found": "Bank net fonnt",
    "err.signups_closed": "Aschreiwung ass fir dës Bank zou.",
    "err.access_suspended": "Zougrëff temporär suspendéiert. Kontaktéiert w.e.g. Är Bank.",
    "err.notif_not_closable": "Dës Notifikatioun kann net zougemaach ginn.",
    "auth.signup.tagline": "Gitt haut Member",
    "auth.login.tagline": "Wëllkomm zeréck!",
    "auth.signup.tagline_sub": "Maacht Äre Kont an 2 Minutten op a kritt Zougang zu all eise Premium-Bankingdéngschtleeschtungen.",
    "auth.login.tagline_sub": "Zougang zu Ärem perséinleche Beräich a séchert Verwaltung vun Ären Finanze.",
    "auth.back": "Zréck zur Startseite",
    "auth.bullet.security": "Bankesécherheet op militäreschem Niveau",
    "auth.bullet.transfers": "Sofortiwwerwaisungen 24/7",
    "auth.bullet.investments": "Intégréiert Investissementer an Erspuernisser",
    "auth.bullet.advisor": "Perséinleche Beroder 7 Deeg/7 disponibel",
    "auth.footer": "Ugedriwwe vu {bank}",
    "auth.signup.title": "Kont uleeën",
    "auth.login.title": "Aloggen",
    "auth.field.lastName": "Familljenumm",
    "auth.field.firstName": "Virnumm(en)",
    "auth.field.country": "Land",
    "auth.field.country.placeholder": "— Land wielen —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Adress",
    "auth.field.address.placeholder": "Haaptstroos 123, Lëtzebuerg",
    "auth.field.email": "E-Mail",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Passwuert",
    "auth.submit.signup": "Mäi Kont uleeën",
    "auth.submit.login": "Aloggen",
    "auth.login.badge": "Log in op jouw account",
    "auth.card.tagline": "Är Clientberäich",
    "auth.submit.loading": "Lueden…",
    "auth.pin.title": "PIN-Code",
    "auth.pin.subtitle": "Gitt Äre 4-stellege PIN-Code an",
    "auth.pin.label": "PIN-Code",
    "auth.pin.confirm": "PIN-Code bestätegen",
    "auth.pin.submit": "Bestätegen",
    "auth.pin.back": "Zréck bei d’Umeldung",
    "auth.pin.length_error": "De PIN-Code muss 4 Zifferen hunn",
    "auth.switch.have_account": "Schonns e Kont?",
    "auth.switch.login_link": "Aloggen",
    "auth.switch.no_account": "Nach keen Client?",
    "auth.error.select_country": "Wielt Äre Land",
    "kyc.title": "Identitéitsprüfung (KYC)",
    "kyc.sub": "Luet Är Dokumenter erop fir Äre Kont ze validéieren. All Dokumenter si verschlësselt a vertraulech.",
    "kyc.id_type": "Aart vum Auswiesdokument *",
    "kyc.id_card": "Personalausweis",
    "kyc.passport": "Reespass",
    "kyc.driver_license": "Féierschäin",
    "kyc.recto_passport": "Virdersäit vum Reespass *",
    "kyc.recto_id": "Virdersäit vum Dokument *",
    "kyc.verso": "Réckersäit vum Dokument *",
    "kyc.selfie": "Selfie mam Ausweis an der Hand *",
    "kyc.address_proof": "Adressnoweis *",
    "kyc.hint_doc": "Bild oder PDF, max. 5 MB",
    "kyc.hint_selfie": "Klore Foto, Gesiicht a Dokument siichtbar",
    "kyc.hint_address": "Rechnung, Kontoauszuch, Attest… (max. 5 MB)",
    "kyc.upload": "Eroplueden",
    "kyc.replace": "Ersetzen",
    "pending.title": "Kont ofhängeg",
    "pending.body": "Är Ufro gouf gutt empfaangen. En Administrateur wäert Är Dossier kontrolléieren a Äre Kont geschwënn aktivéieren.",
    "pending.back": "Zréck zum Aloggen",
    "home.nav.features": "Funktionalitéiten",
    "home.nav.security": "Sécherheet",
    "home.nav.invest": "Investitioun",
    "home.nav.credits": "Kreditter",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Aloggen",
    "home.hero.tagline": "Online-Banking vun der nächster Generatioun",
    "home.hero.title1": "Verwaltt Äert Geld",
    "home.hero.title2": "mat Intelligenz",
    "home.hero.sub": "Eng komplett Bankplattform — Iwwerwaisungen, Erspuernisser, Investissementer a Kreditter vun Ärem Smartphone.",
    "home.hero.login": "Aloggen",
    "home.hero.trust.secure": "100% Sécher",
    "home.hero.trust.fast": "Séier Eröffnung",
    "home.hero.trust.encrypted": "Verschlësselt Donnéeën",
    "home.hero.trust.support": "24/7-Service",
    "home.mockup.balance": "Verfügbaren Solde",
    "home.mockup.currency": "EUR · Haaptkont",
    "home.mockup.recharge": "Oplueden",
    "home.mockup.send": "Schécken",
    "home.mockup.save": "Spueren",
    "home.mockup.salary": "Gehalt Mäerz",
    "home.mockup.rent": "Loyer Abrëll",
    "home.mockup.investment": "Investitioun",
    "home.mockup.days2": "virun 2 Deeg",
    "home.mockup.days3": "virun 3 Deeg",
    "home.mockup.days5": "virun 5 Deeg",
    "home.stats.accounts": "Aktiv Konten",
    "home.stats.uptime": "Disponibilitéit",
    "home.stats.transfers": "Iwwerwaisungen",
    "home.stats.support": "Support",
    "home.features.label": "Funktionalitéiten",
    "home.features.title": "Alles wat Dir braucht",
    "home.features.sub": "Eng komplett Plattform entworf fir d'Daagsverwaltung vun Ären Finanze ze vereinfachen.",
    "home.feature.security": "Militäresch Sécherheet",
    "home.feature.security_body": "AES-256-Verschlësselung, Zweefaktors-Authentifikatioun an 24/7-Kontoiwwerwaachung.",
    "home.feature.cards": "Virtuell a physesch Kaarten",
    "home.feature.cards_body": "Gitt Kaarten a Sekonnen eraus oder bestellt eng richteg physesch Kaart déi bei Iech geliwwert gëtt.",
    "home.feature.transfers": "Express-Iwwerwaisungen",
    "home.feature.transfers_body": "Schéckt a kritt Geld iwwerall op der Welt a Sekonnen.",
    "home.feature.analytics": "Echtzäit-Analyse",
    "home.feature.analytics_body": "Intelligent Dashboards fir Är Ausgaben ze suivéieren a Är Finanze viraus ze plangen.",
    "home.feature.investments": "Intégréiert Investissementer",
    "home.feature.investments_body": "Zougang zu ausgewielten Investissemantsproduiten direkt vun Ärem Kont.",
    "home.feature.support": "Dedizéierten 24/7-Support",
    "home.feature.support_body": "E perséinleche Bankberoder ëmmer disponibel via séchert Messaging.",
    "home.tpl.label": "Vertrauen",
    "home.tpl.title": "Si vertrauen eis",
    "home.mockup.balance_label": "Saldo",
    "home.mockup.virement": "Iwwerwaisung",
    "home.mockup.salaire": "Gehalt",
    "home.security.label": "Sécherheet",
    "home.security.title1": "Är Finanze",
    "home.security.title2": "an absoluter Sécherheet",
    "home.security.sub": "Mir applizéieren déi striktste Bankesécherheetsstandarden fir all Cent ze schützen.",
    "home.security.aes.title": "AES-256-Verschlësselung",
    "home.security.aes.body": "All sensibel Donnéeën si verschlësselt mat deenselwechten Algorithmen déi vun de grousse internationale Banken benotzt ginn.",
    "home.security.auth.title": "Verstärkte Authentifikatioun",
    "home.security.auth.body": "Duebel Identitéitsprüfung bei all Aloggen a bei all sensibel Operatioun op Ärem Kont.",
    "home.security.compliance.title": "PSD2/DSGVO-Konformitéit",
    "home.security.compliance.body": "Mir halen all europäesch Reglementéierungen zu Bezuelungsdéngschtleeschtungen a Dateschutz an.",
    "home.invest.label": "Investissementer",
    "home.invest.title": "Loosst Äert Geld wuessen",
    "home.invest.sub": "6 Investissemantskatégorien ausgewielte fir hire Potenzial a Zouverlässegkeet.",
    "home.invest.yield": "Rendement",
    "home.credits.label": "Kreditter",
    "home.credits.title": "Léisungen fir all Projet",
    "home.credits.sub": "Kompetitiv Zënsen, séier Äntwerten a perséinlech Begleedung fir all Är Projeten.",
    "home.credits.rate": "Zënssaz",
    "home.faq.label": "FAQ",
    "home.faq.title": "Heefeg gestallt Froen",
    "home.faq.q1": "Wéi maachen ech e Kont op?",
    "home.faq.a1": "D'Aschreiwung dauert manner wéi 2 Minutten. Gitt einfach Är perséinlech Informatiounen an, wielet e Passwuert a Äre Kont gëtt nom Check duerch eist Team aktivéiert.",
    "home.faq.q2": "Sinn meng Donnéeën sécher?",
    "home.faq.a2": "Jo. All Donnéeën gi mat AES-256 am Restzoustand a TLS 1.3 am Transit verschlësselt. Mir halen international Bankesstandarden an DSGVO/PSD2-Reglementéierungen an.",
    "home.faq.q3": "Wéi eng Investissemantsaarten bitt Dir un?",
    "home.faq.a3": "Mir bidden 6 Katégorien: Immobilien, Crowdfunding, Erneierbar Energien, Bourse, Participatif a Crypto. All Produkt gëtt no sengem Risiko-Rendement-Verhältnis ausgewielt.",
    "home.faq.q4": "Wéi beantrag ech e Kredit?",
    "home.faq.a4": "Gitt an de Beräich Kredit vun Ärem Clientberäich, wielet d'Darlehensaart a gitt de Betrag an d'Lafzäit an. Är Ufro gëtt bannent 24 Stonnen vun eise Beroder behandelt.",
    "home.faq.q5": "Gi et verstoppte Käschten?",
    "home.faq.a5": "Keng verstoppte Käschten. All Tarifer ginn kloer bei all Transaktioun ugewisen. D'Konditiounen sinn zu all Zäit an Ärem Clientberäich disponibel.",
    "home.faq.q6": "Wéi kontaktéieren ech de Support?",
    "home.faq.a6": "Benotzt de integréierten Messaging an Ärem Clientberäich fir direkt Äre perséinleche Bankberoder ze kontaktéieren, 24/7 disponibel.",
    "home.cta.title": "Prett fir unzefänken?",
    "home.cta.sub": "Schliesst Iech Dausende vu Benotzer un, déi hir Finanze mat {bank} verwalten. D'Aschreiwung dauert manner wéi 2 Minutten.",
    "home.footer.products": "Produiten",
    "home.footer.services": "Déngschtleeschtungen",
    "home.footer.access": "Séieren Zougang",
    "home.footer.accounts": "Konten",
    "home.footer.transfers": "Iwwerwaisungen",
    "home.footer.cards": "Kaarten",
    "home.footer.savings": "Erspuernisser",
    "home.footer.investments": "Investissementer",
    "home.footer.credits": "Kreditter",
    "home.footer.financing": "Finanzéierung",
    "home.footer.messages": "Messagen",
    "home.footer.login": "Aloggen",
    "home.footer.signup": "Kont opmaachen",
    "home.footer.support": "24/7-Support",
    "home.footer.tagline": "Är Online-Bank, iwwerall a jidderzäit disponibel.",
    "home.footer.copyright": "© {year} {bank} — All Rechter virbehal",
    "home.footer.powered": "Ugedriwwe vu {bank}",
    "app.menu_label": "Haaptmenü",
    "app.nav.dashboard": "Dashboard",
    "app.nav.accounts": "Konten",
    "app.nav.transactions": "Transaktioune",
    "app.nav.transfers": "Iwwerwaisungen",
    "app.nav.cards": "Kaarten",
    "app.nav.savings": "Erspuernisser",
    "app.nav.investments": "Investitioun",
    "app.nav.credit": "Kredit",
    "app.nav.financing": "Finanzéierung",
    "app.nav.messages": "Messagen",
    "app.nav.card_tracking": "Kaart verfollegen",
    "app.nav.notifications": "Notifikatiounen",
    "app.nav.settings": "Astellungen",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Startseite",
    "app.nav.mobile.transfers": "Iwwerwaisungen",
    "app.nav.mobile.cards": "Kaarten",
    "app.nav.mobile.transactions": "Transaktioune",
    "app.nav.more.invest": "Investéieren",
    "app.user_role": "Client",
    "app.logout": "Ofloggen",
    "app.search": "Sichen…",
    "app.more_title": "Méi Funktionalitéiten",
    "app.more_btn": "Méi",
    "app.loading": "Lueden…",
    "app.error_back": "Zréck zum Aloggen",
    "app.see_more_notifs": "{count} weider Notifikatiounen uweisen →",
    "page.accounts": "Meng Konten",
    "page.transactions": "Transaktioune",
    "page.transfers": "Nei Iwwerwaisung",
    "page.deposit": "Abotzung",
    "page.cards": "Meng Kaarten",
    "page.savings": "Erspuernisser",
    "page.investments": "Investitioun",
    "page.credits": "Kredit",
    "page.financing": "Projettsfinanzéierung",
    "page.messages": "Messagen",
    "page.settings": "Astellungen",
    "page.notifications": "Wichteg Notifikatiounen",
    "page.profile": "Mäi Profil",
    "dash.greeting": "Moien, {name}",
    "dash.overview": "Iwwerbléck vun Ären Finanze — {date}",
    "dash.total_balance": "Gesamtsolde",
    "dash.this_month": "+{amount} dëse Mount",
    "dash.view_iban": "Mäin IBAN weisen",
    "dash.my_accounts": "Meng Konten",
    "dash.primary_account": "Haaptkont",
    "dash.savings_account": "Spuerkont",
    "dash.remunerated": "Verzënst",
    "dash.details": "Detailer",
    "dash.transfer_btn": "Iwwerwaisung",
    "dash.quick_actions": "Séier Aktiounen",
    "dash.quick.deposit": "Abotzung",
    "deposit.title": "Abotzungsinformatiounen",
    "dash.quick.transfer": "Iwwerwaisung",
    "dash.quick.history": "Geschicht",
    "dash.quick.cards": "Meng Kaarten",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Méinlechebudget",
    "dash.income": "Akommessen",
    "dash.expenses": "Ausgaben",
    "dash.spending_cat": "Ausgaben no Kategorie",
    "dash.no_spending": "Keng Ausgaben erfaasst.",
    "dash.recent_tx": "Lescht Transaktioune",
    "dash.see_all": "Alles weisen",
    "dash.no_tx": "Keng Transaktioune.",
    "dash.main_card": "Meng Haaptkaard",
    "dash.manage": "Verwalten",
    "dash.block": "Spären",
    "dash.pin": "PIN-Code",
    "dash.security": "Sécherheet",
    "dash.more": "Méi",
    "dash.no_card": "Keng Kaart ausginn",
    "dash.monthly_activity": "Méinleche Aktivitéit",
    "dash.operations": "Operatiounen",
    "dash.holder": "Inhaber",
    "dash.expires": "Leeft of",
    "dash.visa_label": "VISA",
    "dash.balance": "Verfügbaren Solde",
    "tx.all": "All",
    "tx.income": "Akommessen",
    "tx.expenses": "Ausgaben",
    "tx.today": "Haut",
    "tx.yesterday": "Gëschter",
    "tx.none": "Keng Transaktioune.",
    "transfer.title": "Séier Iwwerwaisung",
    "transfer.recipient": "Empfänger",
    "transfer.recipient_ph": "Numm, IBAN, Telefonnummer…",
    "transfer.amount": "Betrag",
    "transfer.label": "Bezeechnung",
    "transfer.reason": "Grond (optional)",
    "transfer.reason_ph": "z.B.: Loyer, Réckzuelung…",
    "transfer.execution": "Ausféierung",
    "transfer.immediate": "Direkt",
    "transfer.delayed": "Verréckelt",
    "transfer.balance": "Verfügbaren Solde:",
    "transfer.continue": "Weidergoen",
    "transfer.confirm": "Bestätegen",
    "transfer.cancel": "Ofbriechen",
    "transfer.code": "Validéierungscode",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Gitt de kréiten Code an fir d'Iwwerwaisung ze bestätegen",
    "transfer.pending.title": "Iwwerwaisung waart op Bestätegung",
    "transfer.pending.amount": "Betrag:",
    "transfer.pending.to": "Un:",
    "transfer.pending.hint": "Äre Validéierungscode gëtt per E-Mail oder iwwer d'Notifikatiounsglock geschéckt. De Code besteet aus 6 Zifferen. Wann Dir en net kritt, kontaktéiert de Support.",
    "transfer.pending.validate": "Iwwerwaisung bestätegen",
    "transfer.saved": "Iwwerwaisung gespäichert. Freet Äre Administrateur nom Validéierungscode.",
    "transfer.done": "Iwwerwaisung ofgeschloss",
    "transfer.validated": "Iwwerwaisung bestätegt!",
    "transfer.progress.processing": "Iwwerwaisung gëtt veraarbecht…",
    "transfer.progress.completed": "Iwwerwaisung gemaach!",
    "transfer.progress.default_message": "Är Iwwerwaisung gëtt veraarbecht. Wannechgelift waart.",
    "cards.none": "Keng Kaart ausginn.\nKontaktéiert Äre Administrateur.",
    "cards.holder": "Inhaber",
    "cards.expires": "Leeft of",
    "cards.block": "Spären",
    "cards.pin": "PIN-Code",
    "cards.more": "Méi",
    "cards.limits": "Kaartlimiten",
    "cards.payment": "Bezuelung",
    "cards.withdrawal": "Ofhiewung",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Gespaart",
    "cards.status_active": "Aktief",
    "cards.toast.blocked": "Kaart gespaart",
    "cards.toast.unblocked": "Kaart entspaart",
    "cards.toast.physical_ordered": "Physesch Kaart bestallt! Liwwerung an 5–10 Aarbechtsdäg.",
    "cards.toast.created": "Kaart erfollegräich erstellt!",
    "cards.none_sub": "Keng Kaart ausginn. Erstellt elo är virtuell Kaart.",
    "cards.generate_btn": "Meng Kaart erstellen",
    "cards.create.title": "Meng virtuell Kaart erstellen",
    "cards.create.network": "Bezuelungsnetz",
    "cards.create.color": "Kaartfaarf",
    "cards.create.preview_name": "ÄERT NUMM",
    "cards.create.preview_expiry": "MM/JJ",
    "cards.create.loading": "Gëtt erstellt…",
    "cards.create.submit": "Kaart erstellen",
    "cards.btn.unblock": "Entspaaren",
    "cards.btn.block": "Spaaren",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Physesch",
    "cards.btn.physical_done": "Bestallt",
    "cards.limits_title": "Limite",
    "cards.reveal.title": "Kaartinformatiounen",
    "cards.reveal.number": "Vollständeg Nummer",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Verfallsdatum",
    "cards.reveal.warning": "Deelt dës Informatiounen ni. Zoumaachen dëst Fenster no der Benotzung.",
    "cards.pin.title": "PIN-Code",
    "cards.pin.message": "Dëse PIN-Code gëtt fir all Är Online-Bezuelungen an Ofhiewunge benotzt. Deelt en ni mat jeemandem.",
    "cards.physical.title": "Physesch Kaart bestellen",
    "cards.physical.fee_label": "Liwwerungskäschten",
    "cards.physical.balance_label": "Äert Guthaben",
    "cards.physical.insufficient": "Onzuräichend Guthaben. Lueft Äert Kont op ier Dir bestellt.",
    "cards.physical.info": "D'Käschten gi vun Ärem Kont ofgebucht. Liwwerung an 5–10 Aarbechtsdäg.",
    "cards.physical.order_btn": "Bestellen ({fee})",
    "cards.physical.ordering": "Gëtt bestallt…",
    "cards.physical.order_cta": "Physesch Kaart bestellen",
    "cards.physical.choose_type": "Wiel deng Kaart",
    "cards.physical.features": "Virdeeler",
    "cards.buy_virtual_cta": "Virtuell Kaart kafen",
    "notif.none": "Keng Notifikatiounen am Moment.",
    "notif.contact_support": "Support kontaktéieren",
    "profile.change_photo": "Foto änneren",
    "profile.remove_photo": "Foto entfernen",
    "profile.email": "E-Mail",
    "profile.phone": "Telefon",
    "profile.country": "Land",
    "profile.address": "Adress",
    "profile.notifs_title": "Notifikatiounen ({count})",
    "profile.no_notifs": "Keng Notifikatiounen.",
    "profile.logout": "Ofloggen",
    "profile.photo_updated": "Profilfoto aktualiséiert",
    "profile.photo_removed": "Foto entfernt",
    "profile.photo_too_large": "Bild ze grouss (max. 1 MB)",
    "profile.powered": "Ugedriwwe vu {bank}",
    "iban.title": "Mäin IBAN",
    "iban.loading": "Lueden…",
    "iban.bic": "BIC / SWIFT-Code",
    "iban.share_info": "Deelt dës Donnéeën fir eng Iwwerwaisung op Äre Kont ze kréien.",
    "iban.copy": "Kopéieren",
    "iban.copied": "An d'Tëschenspeicher kopéiert",
    "iban.copy_fail": "Kopéiere net méiglech",
    "iban.close": "Zoumaachen",
    "accounts.all": "All Är Konten bei {bank}.",
    "accounts.primary": "Haaptkont",
    "accounts.savings": "Spuerkont",
    "accounts.primary_tag": "Haaptkont",
    "accounts.secondary_tag": "Niewekont",
    "accounts.objective": "Zil: {amount} · {pct}%",
    "accounts.copy": "Kopéieren",
    "accounts.loading": "Lueden…",
    "accounts.iban_copied": "IBAN kopéiert",
    "accounts.copy_fail": "Kopéiere net méiglech",
    "savings.total": "Total gespuert",
    "savings.target_total": "Gesamtzil",
    "savings.progress": "Fortschrëtt",
    "savings.active": "Aktiv Ziler",
    "savings.new_goal": "Neit Zil",
    "savings.none": "Nach keng Spuerziler.",
    "savings.none_sub": "Creéiert Äert éischt Zil fir unzefänken.",
    "savings.saved": "Gespuert:",
    "savings.goal": "Zil:",
    "savings.deadline": "Frist: {date}",
    "savings.deposit": "Anzuelen",
    "savings.withdraw": "Ofhiwen",
    "savings.loading": "Lueden…",
    "savings.form.title": "Neit Spuerzil",
    "savings.form.cancel": "Ofbriechen",
    "savings.form.create": "Erstellen",
    "savings.form.name": "Zilnumm",
    "savings.form.name_ph": "Japan-Rees, Auto, …",
    "savings.form.target": "Zilbetrag",
    "savings.form.initial": "Éischt Anzuelung",
    "savings.form.deadline": "Frist (optional)",
    "savings.form.color": "Faarf",
    "savings.deposit_title": "Anzuele fir \"{name}\"",
    "savings.deposit_amount": "Anzezuelende Betrag",
    "savings.balance_hint": "Haaptsolde: {amount}",
    "savings.withdraw_prompt": "Wéivill vun \"{name}\" ofhiwen?",
    "savings.close_confirm": "Zil \"{name}\" zoumaachen? D'Spuergeld gëtt op Äre Haaptkont zréckgeboocht.",
    "savings.toast.created": "Zil erstallt",
    "savings.toast.deposited": "Anzuelung erfolgt",
    "savings.toast.withdrawn": "Ofhiewung erfolgt",
    "savings.toast.closed": "Zil zougemaach",
    "invest.total": "Total investéiert",
    "invest.profits": "Kumuléiert Gewënner",
    "invest.count": "Investissementer",
    "invest.products": "Disponibel Produiten",
    "invest.invest_btn": "Investéieren",
    "invest.my": "Meng Investissementer",
    "invest.pending": "Ofhängeg",
    "invest.validated": "Validéiert",
    "invest.refused": "Refuséiert",
    "invest.withdraw.btn": "Auszuelen",
    "invest.withdraw.title": "Investitioun zréckhuelen",
    "invest.withdraw.intro": "Dir sidd op der Punkt Är Investitioun \"{product}\" zréckzehuelen. De Kapital an d'Gewënner ginn op Äert Haaptkont gebucht.",
    "invest.withdraw.principal": "Kapital",
    "invest.withdraw.profit": "Gewënner",
    "invest.withdraw.total": "Total ze kréien",
    "invest.withdraw.confirm": "Bestätegen",
    "invest.withdraw.warning": "Dës Aktioun ass net ëmkéierbar.",
    "invest.withdraw.success": "Investitioun zréckgeholl — {amount} op Äert Salde gebucht.",
    "invest.withdrawn": "Zréckgeholl",
    "invest.cta_sub": "Wielet d'Aart vun Investissement déi zu Iech passt. E Beroder begleet Iech ab Är éischt Ufro.",
    "invest.form.amount": "Unzeinvestéierenden Betrag",
    "invest.form.balance": "Verfügbaren Solde: {amount}",
    "invest.form.note": "Notiz fir Äre Beroder (optional)",
    "invest.form.note_ph": "Gitt Är Erwaardungen un…",
    "invest.form.advisor_info": "E Gespréich gëtt automatesch mat Ärem Beroder opgemaach.",
    "invest.form.cancel": "Ofbriechen",
    "invest.form.submit": "Ufro schécken",
    "invest.toast.requested": "Ufro gespäichert — e Gespréich mat Ärem Beroder gouf opgemaach.",
    "invest.p1.name": "Immobilien",
    "invest.p1.desc": "SCPI, Immobiliefong, Immobilien-Crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Participativ Finanzéierung vun ausgewielten Projeten",
    "invest.p3.name": "Erneierbar Energien",
    "invest.p3.desc": "Solar-, Wand- a Grénge Projeten",
    "invest.p4.name": "Bourse",
    "invest.p4.desc": "Aktien, ETF a diversifizéiert Fong",
    "invest.p5.name": "Participativ",
    "invest.p5.desc": "Prêten un PME a lokal Entrepreneuren",
    "invest.p6.name": "Crypto",
    "invest.p6.desc": "Bitcoin, Ethereum a Multi-Währungs-Wallet",
    "msg.online": "Online",
    "msg.loading": "Lueden…",
    "msg.none": "Keng Messagen. Start d'Gespréich mat Ärem Beroder.",
    "msg.placeholder": "Schreift Är Message…",
    "msg.attach.image": "Bild",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Fichier / Dokument",
    "msg.pdf_doc": "PDF-Dokument",
    "msg.file": "Fichier",
    "msg.too_large": "Fichier ze grouss (max. {size}).",
    "msg.withdraw": "Zréckzéien",
    "credits.cta_sub": "Stellt Är Ufro direkt online — Äntwert bannent 24 Stonnen.",
    "credits.learn_more": "Méi wëssen",
    "credits.request": "Beantragen",
    "credits.history": "Meng Ufroe",
    "credits.status.pending": "Ofhängeg",
    "credits.status.accepted": "Acceptéiert",
    "credits.status.refused": "Refuséiert",
    "credits.chars": "Charakteristiken",
    "credits.amount_range": "Betrag: {min}€ – {max}€",
    "credits.rate_range": "Zënssaz: {min}% – {max}% pro Joer",
    "credits.duration_range": "Lafzäit: {min} bis {max} Méint",
    "credits.deposit_min": "Éischt Anzuelung: mindestens {amount}€",
    "credits.garantie": "Garantie: {garantie}",
    "credits.eligibility": "Eligibilitéitsbedéngungen",
    "credits.elig.income": "Reegelmässeg a stabil Akommessen noweisen",
    "credits.elig.deposit": "Eng éischt Anzuelung vun {amount}€ maachen",
    "credits.elig.garantie": "D'Garantie virleeën: {garantie}",
    "credits.form.title": "Ufro — {name}",
    "credits.form.cancel": "Ofbriechen",
    "credits.form.submit": "Ufro schécken",
    "credits.form.amount": "Gewënschten Betrag (€)",
    "credits.form.duration": "Lafzäit (Méint)",
    "credits.form.reason": "Grond (optional)",
    "credits.form.reason_ph": "Beschreift Äre Projet…",
    "credits.form.close": "Zoumaachen",
    "credits.toast.submitted": "Kreditufro geschéckt — Dir kritt bannent 24 Stonnen eng Äntwert.",
    "credits.p1.name": "Perséinleche Prêt",
    "credits.p1.desc": "Fir d'Finanzéierung vun alldeegleche Besoinen oder perséinleche Projeten.",
    "credits.p2.name": "Immobiliëprêt",
    "credits.p2.desc": "Fir d'Finanzéierung vum Kaf oder Bau vun engem Immobil.",
    "credits.p3.name": "Auto- / Moto-Prêt",
    "credits.p3.desc": "Kaaft e Gefier ouni Suergen.",
    "credits.p4.name": "Studienprêt",
    "credits.p4.desc": "Finanziell Ënnerstëtzung fir de Héichschoulstudium.",
    "credits.p5.name": "Professionnelle Prêt",
    "credits.p5.desc": "Fir d'Wuesstem vun enger professionneller Aktivitéit z'ënnerstëtzen.",
    "credits.p6.name": "Express-Prêt",
    "credits.p6.desc": "Eng séier Léisung fir dringend Besoinen.",
    "financing.reimbursable": "Zréckzuelbar Finanzéierung",
    "financing.reimbursable_desc": "Finanziell Ënnerstëtzung mat feste Konditiounen fir eng Aktivitéit ze lancéieren oder ze entwéckelen.",
    "financing.non_reimbursable": "Net zréckzuelbar Finanzéierung",
    "financing.non_reimbursable_desc": "Fir Projete mat sozialem, educativem oder humanitärem Afloss (ONG, Associatiounen).",
    "financing.cta_sub": "Maatgeschneidert Léisungen fir Är Ambitiounen wierklech ze maachen.",
    "financing.request": "Finanzéierung beantragen",
    "financing.history": "Meng Ufroe",
    "financing.reimbursable_label": "Zréckzuelbar",
    "financing.non_reimbursable_label": "Net zréckzuelbar",
    "financing.status.pending": "Ofhängeg",
    "financing.status.granted": "Accordéiert",
    "financing.status.refused": "Refuséiert",
    "financing.form.title": "Ufro — {name}",
    "financing.form.cancel": "Ofbriechen",
    "financing.form.submit": "Ufro schécken",
    "financing.form.project": "Projektnumm",
    "financing.form.project_ph": "Mäi Projet",
    "financing.form.amount": "Ugefrote Betrag (€)",
    "financing.form.sector": "Secteur",
    "financing.form.sector_ph": "Tech, Landwirtschaft, …",
    "financing.form.description": "Projetsbeschreiwung",
    "financing.form.description_ph": "Stellt Äre Projet, seng Ziler an den erwaarte Afloss vir…",
    "settings.security": "Sécherheet",
    "settings.current_pw": "Aktuellt Passwuert",
    "settings.new_pw": "Neist Passwuert",
    "settings.confirm_pw": "Bestätegen",
    "settings.submit": "Passwuert änneren",
    "settings.no_match": "D'Passwierder stëmmen net iwweren",
    "settings.changed": "Passwuert geännert",
    "common.cancel": "Ofbriechen",
    "common.confirm": "Bestätegen",
    "common.close": "Zoumaachen",
    "common.save": "Späicheren",
    "common.loading": "Lueden…",
    "common.send": "Schécken",
    "common.total": "Total",
    "admin.wallet.balance": "Verfügbares Guthaben",
    "admin.wallet.history": "Verlauf",
    "admin.wallet.no_history": "Keine Transaktionen",
    "admin.wallet.title": "Meine Brieftasche",
    "admin.wallet.topup": "Aufladen",
    "beneficiary.pick": "Auswählen",
    "beneficiary.save_this": "Diesen Begünstigten speichern",
    "beneficiary.saved": "Gespeicherte Begünstigte",
    "beneficiary.saved_ok": "Begünstigter gespeichert",
    "card.color.amber": "Bernstein",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Smaragd",
    "card.color.forest": "Waldgrün",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavendel",
    "card.color.night": "Nacht",
    "card.color.orange": "Orange",
    "card.color.pink": "Rosa",
    "card.color.royal": "Königsblau",
    "card.color.ruby": "Rubin",
    "card.color.slate": "Schiefer",
    "card.color.steel": "Stahl",
    "card.color.terracotta": "Terrakotta",
    "card.color.violet": "Violett",
    "common.other": "Sonstiges",
    "common.search": "Suchen",
    "content.translated": "Automatisch übersetzter Inhalt",
    "credits.monthly_payment": "Geschätzte Monatsrate",
    "credits.rate_annual": "/ Jahr",
    "credits.rate_used": "Geschätzter Zinssatz:",
    "credits.simulation": "Simulation der Monatsrate",
    "credits.total_cost": "Geschätzte Gesamtkosten",
    "demo.banner": "Demonstrationsmodus — keine echten Aktionen",
    "demo.disabled": "Aktion im Demo-Modus nicht verfügbar",
    "deposit.default_instructions": "Kontaktieren Sie Ihren Berater für die Einzahlungsmodalitäten.",
    "error.back_home": "Zurück zur Startseite",
    "error.bank_not_found": "Bank nicht gefunden",
    "error.unknown": "Unbekannter Fehler",
    "kyc.file_read_error": "Datei kann nicht gelesen werden",
    "kyc.file_too_large": "Datei zu groß (max. 5 MB)",
    "login.history": "Anmeldungshistorie",
    "login.history.empty": "Keine Anmeldung aufgezeichnet.",
    "notif.dismiss": "Schließen",
    "notif.locked": "Nachricht der Bank",
    "tracking.error.not_found": "Sendungsnummer nicht gefunden. Prüfen und erneut versuchen.",
    "tracking.label": "Sendungsverfolgung",
    "tracking.status.delivered": "Geliefert",
    "tracking.status.preparation": "In Vorbereitung",
    "tracking.status.shipped": "Versandt",
    "tracking.status.transit": "Im Transit",
    "transfer.code.copied": "Kopiert",
    "transfer.code.copy": "Kopieren",
    "transfer.code.received": "Ihr Validierungscode",
    "dash.welcome": "Wëllkomm",
    "common.delete": "Läschen",
    "cards.delete.title": "Kaart läschen",
    "cards.delete.confirm_msg": "Dës Aktioun ass irreversibel. D'Kaart gëtt definitiv geläscht.",
    "cards.delete.physical_tooltip": "Physesch Kaart bestellt — Läschen onméiglech",
    "cards.order.confirmed": "Bestellung bestätegt!",
    "cards.order.confirmed_body": "Är physesch Kaart gëtt virbereet. Benotzt dës Nummer fir Är Liwwerung ze verfolgen.",
    "cards.order.tracking_no": "Tracking-Nr.",
    "cards.fee_deducted": "{amount} gëtt vun Ärem Saldo ofgezunn",
    "cards.insufficient_short": "Net genuch Saldo ({available} verfügbar)",
    "transfer.section.beneficiary": "Beneficiär",
    "transfer.section.details": "Iwwerweisung",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bank",
    "transfer.field.city": "Stad",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bank:",
    "transfer.progress.paused": "Waarden op Validatioun",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Variabel",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Kaart geläscht",
    "cards.feat.gold_exclusive": "Exklusiv Gold Kaart",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Keeferschutz",
    "cards.feat.support_standard": "Standard Support",
    "cards.feat.plastic_premium": "Premium Plastikkaart",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Standard Reesversécherung",
    "cards.feat.support_priority": "Prioritéitssupport",
    "cards.feat.metal_brushed": "Gebierschte Metallkaart",
    "cards.feat.cashback_2_unlimited": "Onlimitéiert 2% Cashback",
    "cards.feat.travel_premium": "Premium Reesversécherung",
    "cards.feat.vip_lounge": "VIP-Lounge Zougang",
    "cards.feat.support_247": "24/7 Hëllef",
    "auth.demo.title": "Administrator Login",
    "auth.demo.subtitle": "Gitt Är Admin Login an fir d'Demo ze gesinn",
    "credits.garantie.none": "Keng",
    "credits.garantie.mortgage": "Hypothéik op déi finanzéiert Immobilie",
    "credits.garantie.vehicle": "Finanzéiert Gefier",
    "credits.garantie.parental": "Eltererlech oder bankesch",
    "credits.garantie.professional": "Berufflech Aktiven oder perséinlech Garantie",
    "cards.reveal.pin_prompt": "Gitt Äre 4-stellege PIN-Code an fir d'Kaartinformatiounen unzeweisen.",
    "cards.reveal.show": "Weisen",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Déi zwee PINen stëmmen net iwwerein",
    "auth.pin.digit_hint": "4 Zifferen",
  
  },
  sv: {
    "notif.transfer_code.title": "Bekräftelsekod för överföring",
    "notif.transfer_code.body": "Kopiera den här koden för att bekräfta din överföring.",
    "notif.transfer_code.expires_in": "Försvinner om {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} månader",
    "credits.n_months": "{n} månader",
    "msg.read": "Läst",
    "msg.sent": "Skickat",
    "msg.attach_aria": "Bifoga fil",
    "login.device.unknown": "Okänd webbläsare",
    "login.device.mobile": "Mobil",
    "login.device.tablet": "Surfplatta",
    "login.device.desktop": "Dator",
    "login.recent": "Nyligen",
    "err.invalid_credentials": "Ogiltiga inloggningsuppgifter",
    "err.bad_pin": "Felaktig PIN",
    "err.account_blocked": "Konto blockerat",
    "err.bad_current_pw": "Nuvarande lösenord är felaktigt",
    "err.insufficient_funds": "Otillräckliga medel, fyll på kontot.",
    "err.amount_above_savings": "Beloppet överstiger tillgängligt sparande",
    "err.must_be_positive": "Värdet måste vara positivt",
    "err.invalid_format": "Ogiltigt format",
    "err.email_taken": "E-postadress används redan",
    "err.card_not_found": "Kort hittades inte",
    "err.physical_card_already_ordered": "Fysiskt kort redan beställt",
    "err.beneficiary_not_found": "Mottagare hittades inte",
    "err.invalid_code": "Ogiltig kod",
    "err.no_transfer_code": "Ingen kod för denna överföring",
    "err.link_expired": "Länken är ogiltig eller har gått ut",
    "err.thread_not_found": "Konversation hittades inte",
    "err.empty_message": "Tomt meddelande",
    "err.file_too_large": "Filen är för stor (max 20 MB)",
    "err.forbidden": "Åtkomst nekad",
    "err.bank_not_found": "Bank hittades inte",
    "err.signups_closed": "Registreringar är stängda för denna bank.",
    "err.access_suspended": "Åtkomst tillfälligt pausad. Kontakta din bank.",
    "err.notif_not_closable": "Denna avisering kan inte stängas.",
    "auth.signup.tagline": "Gå med oss idag",
    "auth.login.tagline": "Välkommen tillbaka!",
    "auth.signup.tagline_sub": "Öppna ditt konto på 2 minuter och få tillgång till alla våra premiumbankstjänster.",
    "auth.login.tagline_sub": "Få tillgång till ditt personliga utrymme och hantera dina finanser på ett säkert sätt.",
    "auth.back": "Tillbaka till startsidan",
    "auth.bullet.security": "Militärklassad banksäkerhet",
    "auth.bullet.transfers": "Omedelbara överföringar dygnet runt",
    "auth.bullet.investments": "Integrerade investeringar och sparande",
    "auth.bullet.advisor": "Personlig rådgivare tillgänglig 7 dagar/7",
    "auth.footer": "Drivs av {bank}",
    "auth.signup.title": "Skapa ett konto",
    "auth.login.title": "Logga in",
    "auth.field.lastName": "Efternamn",
    "auth.field.firstName": "Förnamn",
    "auth.field.country": "Land",
    "auth.field.country.placeholder": "— Välj ditt land —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Adress",
    "auth.field.address.placeholder": "Storgatan 123, Stockholm",
    "auth.field.email": "E-post",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Lösenord",
    "auth.submit.signup": "Skapa mitt konto",
    "auth.submit.login": "Logga in",
    "auth.login.badge": "Logga in på ditt konto",
    "auth.card.tagline": "Ditt kundområde",
    "auth.submit.loading": "Laddar…",
    "auth.pin.title": "PIN-kod",
    "auth.pin.subtitle": "Ange din 4-siffriga PIN-kod",
    "auth.pin.label": "PIN-kod",
    "auth.pin.confirm": "Bekräfta PIN-kod",
    "auth.pin.submit": "Bekräfta",
    "auth.pin.back": "Tillbaka till inloggning",
    "auth.pin.length_error": "PIN-koden måste vara 4 siffror",
    "auth.switch.have_account": "Har du redan ett konto?",
    "auth.switch.login_link": "Logga in",
    "auth.switch.no_account": "Inte kund ännu?",
    "auth.error.select_country": "Välj ditt land",
    "kyc.title": "Identitetsverifiering (KYC)",
    "kyc.sub": "Ladda upp dina dokument för att validera ditt konto. Alla dokument är krypterade och konfidentiella.",
    "kyc.id_type": "Typ av identitetshandling *",
    "kyc.id_card": "Nationellt ID-kort",
    "kyc.passport": "Pass",
    "kyc.driver_license": "Körkort",
    "kyc.recto_passport": "Passens framsida *",
    "kyc.recto_id": "ID-kortets framsida *",
    "kyc.verso": "ID-kortets baksida *",
    "kyc.selfie": "Selfie med ID-handlingen i handen *",
    "kyc.address_proof": "Adressbevis *",
    "kyc.hint_doc": "Bild eller PDF, max 5 MB",
    "kyc.hint_selfie": "Tydligt foto, ansikte och dokument synliga",
    "kyc.hint_address": "Räkning, kontoutdrag, intyg… (max 5 MB)",
    "kyc.upload": "Ladda upp",
    "kyc.replace": "Ersätt",
    "pending.title": "Konto väntar",
    "pending.body": "Din begäran har mottagits. En administratör kommer att granska ditt ärende och aktivera ditt konto inom kort.",
    "pending.back": "Tillbaka till inloggning",
    "home.nav.features": "Funktioner",
    "home.nav.security": "Säkerhet",
    "home.nav.invest": "Investering",
    "home.nav.credits": "Krediter",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Logga in",
    "home.hero.tagline": "Nästa generations nätbank",
    "home.hero.title1": "Hantera dina pengar",
    "home.hero.title2": "med intelligens",
    "home.hero.sub": "En komplett bankplattform — överföringar, sparande, investeringar och krediter från din smartphone.",
    "home.hero.login": "Logga in",
    "home.hero.trust.secure": "100% Säkert",
    "home.hero.trust.fast": "Snabb öppning",
    "home.hero.trust.encrypted": "Krypterad data",
    "home.hero.trust.support": "Service dygnet runt",
    "home.mockup.balance": "Tillgängligt saldo",
    "home.mockup.currency": "EUR · Huvudkonto",
    "home.mockup.recharge": "Fyll på",
    "home.mockup.send": "Skicka",
    "home.mockup.save": "Spara",
    "home.mockup.salary": "Lön mars",
    "home.mockup.rent": "Hyra april",
    "home.mockup.investment": "Investering",
    "home.mockup.days2": "för 2 dagar sedan",
    "home.mockup.days3": "för 3 dagar sedan",
    "home.mockup.days5": "för 5 dagar sedan",
    "home.stats.accounts": "Aktiva konton",
    "home.stats.uptime": "Drifttid",
    "home.stats.transfers": "Överföringar",
    "home.stats.support": "Support",
    "home.features.label": "Funktioner",
    "home.features.title": "Allt du behöver",
    "home.features.sub": "En komplett plattform utformad för att förenkla hanteringen av dina finanser i vardagen.",
    "home.feature.security": "Militärklassad säkerhet",
    "home.feature.security_body": "AES-256-kryptering, tvåfaktorsautentisering och 24/7-kontoövervakning.",
    "home.feature.cards": "Virtuellt och fysiskt kort",
    "home.feature.cards_body": "Utfärda kort på några sekunder eller beställ ett riktigt fysiskt kort levererat till dig.",
    "home.feature.transfers": "Expressöverföringar",
    "home.feature.transfers_body": "Skicka och ta emot pengar var som helst i världen på några sekunder.",
    "home.feature.analytics": "Realtidsanalys",
    "home.feature.analytics_body": "Smarta instrumentpaneler för att följa dina utgifter och förutse dina finanser.",
    "home.feature.investments": "Integrerade investeringar",
    "home.feature.investments_body": "Få tillgång till utvalda investeringsprodukter direkt från ditt konto.",
    "home.feature.support": "Dedikerad support dygnet runt",
    "home.feature.support_body": "En personlig bankrådgivare tillgänglig när som helst via säker meddelandetjänst.",
    "home.tpl.label": "Förtroende",
    "home.tpl.title": "De litar på oss",
    "home.mockup.balance_label": "Saldo",
    "home.mockup.virement": "Överföring",
    "home.mockup.salaire": "Lön",
    "home.security.label": "Säkerhet",
    "home.security.title1": "Dina finanser",
    "home.security.title2": "i absolut säkerhet",
    "home.security.sub": "Vi tillämpar de striktaste banksäkerhetsstandarderna för att skydda varje krona.",
    "home.security.aes.title": "AES-256-kryptering",
    "home.security.aes.body": "All känslig data krypteras med samma algoritmer som används av de stora internationella bankerna.",
    "home.security.auth.title": "Förstärkt autentisering",
    "home.security.auth.body": "Dubbel identitetsverifiering vid varje inloggning och för varje känslig åtgärd på ditt konto.",
    "home.security.compliance.title": "PSD2/GDPR-efterlevnad",
    "home.security.compliance.body": "Vi följer alla europeiska förordningar om betaltjänster och dataskydd.",
    "home.invest.label": "Investeringar",
    "home.invest.title": "Låt dina pengar växa",
    "home.invest.sub": "6 investeringskategorier utvalda för sin potential och tillförlitlighet.",
    "home.invest.yield": "Avkastning",
    "home.credits.label": "Krediter",
    "home.credits.title": "Lösningar för varje projekt",
    "home.credits.sub": "Konkurrenskraftiga räntor, snabba svar och personligt stöd för alla dina projekt.",
    "home.credits.rate": "Ränta",
    "home.faq.label": "FAQ",
    "home.faq.title": "Vanliga frågor",
    "home.faq.q1": "Hur öppnar jag ett konto?",
    "home.faq.a1": "Registreringen tar mindre än 2 minuter. Fyll i dina personuppgifter, välj ett lösenord och ditt konto aktiveras efter validering av vårt team.",
    "home.faq.q2": "Är mina uppgifter säkra?",
    "home.faq.a2": "Ja. All data krypteras med AES-256 i vila och TLS 1.3 under överföring. Vi följer internationella bankstandarder och GDPR/PSD2-förordningar.",
    "home.faq.q3": "Vilka typer av investeringar erbjuder ni?",
    "home.faq.a3": "Vi erbjuder 6 kategorier: Fastigheter, Crowdfunding, Förnybar energi, Börsen, Deltagande och Krypto. Varje produkt väljs ut för sin risk/avkastningsprofil.",
    "home.faq.q4": "Hur ansöker jag om kredit?",
    "home.faq.a4": "Gå till kreditavsnittet i ditt kundområde, välj lånetyp, ange belopp och löptid. Din ansökan handläggs inom 24 timmar av våra rådgivare.",
    "home.faq.q5": "Finns det dolda avgifter?",
    "home.faq.a5": "Inga dolda avgifter. Alla priser visas tydligt vid varje transaktion. Villkoren är tillgängliga i ditt kundområde när som helst.",
    "home.faq.q6": "Hur kontaktar jag supporten?",
    "home.faq.a6": "Använd den integrerade meddelandetjänsten i ditt kundområde för att direkt kontakta din personliga bankrådgivare, tillgänglig dygnet runt.",
    "home.cta.title": "Redo att börja?",
    "home.cta.sub": "Gå med tusentals användare som hanterar sina finanser med {bank}. Registreringen tar mindre än 2 minuter.",
    "home.footer.products": "Produkter",
    "home.footer.services": "Tjänster",
    "home.footer.access": "Snabbåtkomst",
    "home.footer.accounts": "Konton",
    "home.footer.transfers": "Överföringar",
    "home.footer.cards": "Kort",
    "home.footer.savings": "Sparande",
    "home.footer.investments": "Investeringar",
    "home.footer.credits": "Krediter",
    "home.footer.financing": "Finansiering",
    "home.footer.messages": "Meddelanden",
    "home.footer.login": "Logga in",
    "home.footer.signup": "Öppna ett konto",
    "home.footer.support": "Support dygnet runt",
    "home.footer.tagline": "Din nätbank, tillgänglig överallt och när som helst.",
    "home.footer.copyright": "© {year} {bank} — Alla rättigheter förbehållna",
    "home.footer.powered": "Drivs av {bank}",
    "app.menu_label": "Huvudmeny",
    "app.nav.dashboard": "Instrumentpanel",
    "app.nav.accounts": "Konton",
    "app.nav.transactions": "Transaktioner",
    "app.nav.transfers": "Överföringar",
    "app.nav.cards": "Kort",
    "app.nav.savings": "Sparande",
    "app.nav.investments": "Investering",
    "app.nav.credit": "Kredit",
    "app.nav.financing": "Finansiering",
    "app.nav.messages": "Meddelanden",
    "app.nav.card_tracking": "Kortspårning",
    "app.nav.notifications": "Aviseringar",
    "app.nav.settings": "Inställningar",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Hem",
    "app.nav.mobile.transfers": "Överföringar",
    "app.nav.mobile.cards": "Kort",
    "app.nav.mobile.transactions": "Transaktioner",
    "app.nav.more.invest": "Investera",
    "app.user_role": "Kund",
    "app.logout": "Logga ut",
    "app.search": "Sök…",
    "app.more_title": "Fler funktioner",
    "app.more_btn": "Mer",
    "app.loading": "Laddar…",
    "app.error_back": "Tillbaka till inloggning",
    "app.see_more_notifs": "Visa {count} fler aviseringar →",
    "page.accounts": "Mina konton",
    "page.transactions": "Transaktioner",
    "page.transfers": "Ny överföring",
    "page.deposit": "Insättning",
    "page.cards": "Mina kort",
    "page.savings": "Sparande",
    "page.investments": "Investering",
    "page.credits": "Kredit",
    "page.financing": "Projektfinansiering",
    "page.messages": "Meddelanden",
    "page.settings": "Inställningar",
    "page.notifications": "Viktiga aviseringar",
    "page.profile": "Min profil",
    "dash.greeting": "Hej, {name}",
    "dash.overview": "Översikt av dina finanser — {date}",
    "dash.total_balance": "Totalt saldo",
    "dash.this_month": "+{amount} denna månad",
    "dash.view_iban": "Visa mitt IBAN",
    "dash.my_accounts": "Mina konton",
    "dash.primary_account": "Huvudkonto",
    "dash.savings_account": "Sparkonto",
    "dash.remunerated": "Räntebärande",
    "dash.details": "Detaljer",
    "dash.transfer_btn": "Överföring",
    "dash.quick_actions": "Snabbåtgärder",
    "dash.quick.deposit": "Insättning",
    "deposit.title": "Insättningsinformation",
    "dash.quick.transfer": "Överföring",
    "dash.quick.history": "Historik",
    "dash.quick.cards": "Mina kort",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Månadsbudget",
    "dash.income": "Inkomster",
    "dash.expenses": "Utgifter",
    "dash.spending_cat": "Utgifter per kategori",
    "dash.no_spending": "Inga utgifter registrerade.",
    "dash.recent_tx": "Senaste transaktioner",
    "dash.see_all": "Visa alla",
    "dash.no_tx": "Inga transaktioner.",
    "dash.main_card": "Mitt huvudkort",
    "dash.manage": "Hantera",
    "dash.block": "Blockera",
    "dash.pin": "PIN-kod",
    "dash.security": "Säkerhet",
    "dash.more": "Mer",
    "dash.no_card": "Inget kort utfärdat",
    "dash.monthly_activity": "Månadsaktivitet",
    "dash.operations": "Transaktioner",
    "dash.holder": "Innehavare",
    "dash.expires": "Utg.",
    "dash.visa_label": "VISA",
    "dash.balance": "Tillgängligt saldo",
    "tx.all": "Alla",
    "tx.income": "Inkomster",
    "tx.expenses": "Utgifter",
    "tx.today": "Idag",
    "tx.yesterday": "Igår",
    "tx.none": "Inga transaktioner.",
    "transfer.title": "Snabböverföring",
    "transfer.recipient": "Mottagare",
    "transfer.recipient_ph": "Namn, IBAN, telefonnummer…",
    "transfer.amount": "Belopp",
    "transfer.label": "Etikett",
    "transfer.reason": "Orsak (valfritt)",
    "transfer.reason_ph": "T.ex. hyra, återbetalning…",
    "transfer.execution": "Utförande",
    "transfer.immediate": "Omedelbar",
    "transfer.delayed": "Fördröjd",
    "transfer.balance": "Tillgängligt saldo:",
    "transfer.continue": "Fortsätt",
    "transfer.confirm": "Bekräfta",
    "transfer.cancel": "Avbryt",
    "transfer.code": "Valideringskod",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Ange den mottagna koden för att bekräfta överföringen",
    "transfer.pending.title": "Överföring väntar på validering",
    "transfer.pending.amount": "Belopp:",
    "transfer.pending.to": "Till:",
    "transfer.pending.hint": "Din valideringskod skickas via e-post eller via aviseringsklockan. Koden består av 6 siffror. Om du inte får den, vänligen kontakta supporten.",
    "transfer.pending.validate": "Bekräfta överföringen",
    "transfer.saved": "Överföringen sparad. Be din administratör om valideringskoden.",
    "transfer.done": "Överföring genomförd",
    "transfer.validated": "Överföring validerad!",
    "transfer.progress.processing": "Bearbetar överföring…",
    "transfer.progress.completed": "Överföring genomförd!",
    "transfer.progress.default_message": "Din överföring behandlas. Vänligen vänta.",
    "cards.none": "Inget kort utfärdat.\nKontakta din administratör.",
    "cards.holder": "Innehavare",
    "cards.expires": "Utg.",
    "cards.block": "Lås",
    "cards.pin": "PIN-kod",
    "cards.more": "Mer",
    "cards.limits": "Kortgränser",
    "cards.payment": "Betalning",
    "cards.withdrawal": "Uttag",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Spärrat",
    "cards.status_active": "Aktiv",
    "cards.toast.blocked": "Kort spärrat",
    "cards.toast.unblocked": "Kort avspärrat",
    "cards.toast.physical_ordered": "Fysiskt kort beställt! Leverans inom 5–10 arbetsdagar.",
    "cards.toast.created": "Kort skapat framgångsrikt!",
    "cards.none_sub": "Inget kort utfärdat. Skapa ditt virtuella kort nu.",
    "cards.generate_btn": "Skapa mitt kort",
    "cards.create.title": "Skapa mitt virtuella kort",
    "cards.create.network": "Betalningsnätverk",
    "cards.create.color": "Kortfärg",
    "cards.create.preview_name": "DITT NAMN",
    "cards.create.preview_expiry": "MM/ÅÅ",
    "cards.create.loading": "Skapar…",
    "cards.create.submit": "Skapa kort",
    "cards.btn.unblock": "Avspärra",
    "cards.btn.block": "Spärra",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fysiskt",
    "cards.btn.physical_done": "Beställt",
    "cards.limits_title": "Gränser",
    "cards.reveal.title": "Kortinformation",
    "cards.reveal.number": "Fullständigt nummer",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Utgångsdatum",
    "cards.reveal.warning": "Dela aldrig denna information. Stäng detta fönster efter användning.",
    "cards.pin.title": "PIN-kod",
    "cards.pin.message": "Denna PIN-kod används för alla dina onlinebetalningar och uttag. Dela den aldrig med någon.",
    "cards.physical.title": "Beställ fysiskt kort",
    "cards.physical.fee_label": "Leveransavgift",
    "cards.physical.balance_label": "Ditt saldo",
    "cards.physical.insufficient": "Otillräckligt saldo. Ladda upp ditt konto innan du beställer.",
    "cards.physical.info": "Avgiften debiteras från ditt konto. Leverans inom 5–10 arbetsdagar.",
    "cards.physical.order_btn": "Beställ ({fee})",
    "cards.physical.ordering": "Beställer…",
    "cards.physical.order_cta": "Beställ ett fysiskt kort",
    "cards.physical.choose_type": "Välj ditt kort",
    "cards.physical.features": "Fördelar",
    "cards.buy_virtual_cta": "Köp ett virtuellt kort",
    "notif.none": "Inga aviseringar för tillfället.",
    "notif.contact_support": "Kontakta support",
    "profile.change_photo": "Ändra foto",
    "profile.remove_photo": "Ta bort foto",
    "profile.email": "E-post",
    "profile.phone": "Telefon",
    "profile.country": "Land",
    "profile.address": "Adress",
    "profile.notifs_title": "Aviseringar ({count})",
    "profile.no_notifs": "Inga aviseringar.",
    "profile.logout": "Logga ut",
    "profile.photo_updated": "Profilfoto uppdaterat",
    "profile.photo_removed": "Foto borttaget",
    "profile.photo_too_large": "Bilden är för stor (max 1 MB)",
    "profile.powered": "Drivs av {bank}",
    "iban.title": "Mitt IBAN",
    "iban.loading": "Laddar…",
    "iban.bic": "BIC / SWIFT-kod",
    "iban.share_info": "Dela dessa uppgifter för att ta emot en överföring till ditt konto.",
    "iban.copy": "Kopiera",
    "iban.copied": "Kopierat till urklipp",
    "iban.copy_fail": "Det gick inte att kopiera",
    "iban.close": "Stäng",
    "accounts.all": "Alla dina konton hos {bank}.",
    "accounts.primary": "Huvudkonto",
    "accounts.savings": "Sparkonto",
    "accounts.primary_tag": "Primär",
    "accounts.secondary_tag": "Sekundär",
    "accounts.objective": "Mål: {amount} · {pct}%",
    "accounts.copy": "Kopiera",
    "accounts.loading": "Laddar…",
    "accounts.iban_copied": "IBAN kopierat",
    "accounts.copy_fail": "Det gick inte att kopiera",
    "savings.total": "Totalt sparat",
    "savings.target_total": "Totalt mål",
    "savings.progress": "Framsteg",
    "savings.active": "Aktiva mål",
    "savings.new_goal": "Nytt mål",
    "savings.none": "Inga sparmål ännu.",
    "savings.none_sub": "Skapa ditt första mål för att komma igång.",
    "savings.saved": "Sparat:",
    "savings.goal": "Mål:",
    "savings.deadline": "Deadline: {date}",
    "savings.deposit": "Sätt in",
    "savings.withdraw": "Ta ut",
    "savings.loading": "Laddar…",
    "savings.form.title": "Nytt sparmål",
    "savings.form.cancel": "Avbryt",
    "savings.form.create": "Skapa",
    "savings.form.name": "Målnamn",
    "savings.form.name_ph": "Japanresa, Bil, …",
    "savings.form.target": "Målbelopp",
    "savings.form.initial": "Första insättning",
    "savings.form.deadline": "Deadline (valfritt)",
    "savings.form.color": "Färg",
    "savings.deposit_title": "Sätt in på \"{name}\"",
    "savings.deposit_amount": "Belopp att sätta in",
    "savings.balance_hint": "Huvudsaldo: {amount}",
    "savings.withdraw_prompt": "Hur mycket vill du ta ut från \"{name}\"?",
    "savings.close_confirm": "Stäng målet \"{name}\"? Sparandet återförs till ditt huvudkonto.",
    "savings.toast.created": "Mål skapat",
    "savings.toast.deposited": "Insättning gjord",
    "savings.toast.withdrawn": "Uttag gjort",
    "savings.toast.closed": "Mål stängt",
    "invest.total": "Totalt investerat",
    "invest.profits": "Ackumulerad vinst",
    "invest.count": "Investeringar",
    "invest.products": "Tillgängliga produkter",
    "invest.invest_btn": "Investera",
    "invest.my": "Mina investeringar",
    "invest.pending": "Väntar",
    "invest.validated": "Validerad",
    "invest.refused": "Avvisad",
    "invest.withdraw.btn": "Ta ut",
    "invest.withdraw.title": "Ta ut min investering",
    "invest.withdraw.intro": "Du står i begrepp att ta ut din investering \"{product}\". Det ursprungliga kapitalet och den upplupna vinsten kommer att krediteras ditt huvudsaldo.",
    "invest.withdraw.principal": "Kapital",
    "invest.withdraw.profit": "Vinst",
    "invest.withdraw.total": "Totalt att ta emot",
    "invest.withdraw.confirm": "Bekräfta uttag",
    "invest.withdraw.warning": "Denna åtgärd är oåterkallelig. Investeringen kommer att avslutas.",
    "invest.withdraw.success": "Investering uttagen — {amount} krediteras ditt saldo.",
    "invest.withdrawn": "Uttagen",
    "invest.cta_sub": "Välj den typ av investering som passar dig. En rådgivare hjälper dig från din första förfrågan.",
    "invest.form.amount": "Belopp att investera",
    "invest.form.balance": "Tillgängligt saldo: {amount}",
    "invest.form.note": "Anteckning till din rådgivare (valfritt)",
    "invest.form.note_ph": "Ange dina förväntningar…",
    "invest.form.advisor_info": "En konversation öppnas automatiskt med din rådgivare.",
    "invest.form.cancel": "Avbryt",
    "invest.form.submit": "Skicka förfrågan",
    "invest.toast.requested": "Förfrågan registrerad — en konversation har öppnats med din rådgivare.",
    "invest.p1.name": "Fastigheter",
    "invest.p1.desc": "SCPI, fastighetspapper, fastighetscrowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Deltagarfinansiering av utvalda projekt",
    "invest.p3.name": "Förnybar energi",
    "invest.p3.desc": "Sol-, vind- och gröna projekt",
    "invest.p4.name": "Börs",
    "invest.p4.desc": "Aktier, ETF och diversifierade fonder",
    "invest.p5.name": "Deltagande",
    "invest.p5.desc": "Lån till PME och lokala entreprenörer",
    "invest.p6.name": "Krypto",
    "invest.p6.desc": "Bitcoin, Ethereum och plånbok för flera valutor",
    "msg.online": "Online",
    "msg.loading": "Laddar…",
    "msg.none": "Inga meddelanden. Starta konversationen med din rådgivare.",
    "msg.placeholder": "Skriv ditt meddelande…",
    "msg.attach.image": "Bild",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Ljud",
    "msg.attach.file": "Fil / Dokument",
    "msg.pdf_doc": "PDF-dokument",
    "msg.file": "Fil",
    "msg.too_large": "Filen är för stor (max {size}).",
    "msg.withdraw": "Ta bort",
    "credits.cta_sub": "Ansök direkt online — svar inom 24 timmar.",
    "credits.learn_more": "Läs mer",
    "credits.request": "Ansök",
    "credits.history": "Mina ansökningar",
    "credits.status.pending": "Väntar",
    "credits.status.accepted": "Accepterad",
    "credits.status.refused": "Avvisad",
    "credits.chars": "Egenskaper",
    "credits.amount_range": "Belopp: {min}€ – {max}€",
    "credits.rate_range": "Ränta: {min}% – {max}% per år",
    "credits.duration_range": "Löptid: {min} till {max} månader",
    "credits.deposit_min": "Första insättning: {amount}€ minimum",
    "credits.garantie": "Garanti: {garantie}",
    "credits.eligibility": "Behörighetskrav",
    "credits.elig.income": "Styrka regelbundna och stabila inkomster",
    "credits.elig.deposit": "Göra en första insättning på {amount}€",
    "credits.elig.garantie": "Uppvisa garantin: {garantie}",
    "credits.form.title": "Ansökan — {name}",
    "credits.form.cancel": "Avbryt",
    "credits.form.submit": "Skicka ansökan",
    "credits.form.amount": "Önskat belopp (€)",
    "credits.form.duration": "Löptid (månader)",
    "credits.form.reason": "Orsak (valfritt)",
    "credits.form.reason_ph": "Beskriv ditt projekt…",
    "credits.form.close": "Stäng",
    "credits.toast.submitted": "Kreditansökan skickad — du får svar inom 24 timmar.",
    "credits.p1.name": "Personligt lån",
    "credits.p1.desc": "För att finansiera vardagliga behov eller personliga projekt.",
    "credits.p2.name": "Bolån",
    "credits.p2.desc": "För att finansiera köp eller byggnation av fastighet.",
    "credits.p3.name": "Bil- / Motorcykellån",
    "credits.p3.desc": "Köp ett fordon med sinnesro.",
    "credits.p4.name": "Studielån",
    "credits.p4.desc": "Ekonomiskt stöd för högskolestudier.",
    "credits.p5.name": "Företagslån",
    "credits.p5.desc": "För att stödja tillväxten av en yrkesverksamhet.",
    "credits.p6.name": "Expresslån",
    "credits.p6.desc": "En snabb lösning för akuta behov.",
    "financing.reimbursable": "Återbetalningspliktigt bidrag",
    "financing.reimbursable_desc": "Ekonomiskt stöd med fasta villkor för att starta eller utveckla en verksamhet.",
    "financing.non_reimbursable": "Icke återbetalningspliktigt bidrag",
    "financing.non_reimbursable_desc": "För projekt med socialt, utbildningsmässigt eller humanitärt syfte (ONG, föreningar).",
    "financing.cta_sub": "Skräddarsydda lösningar för att förverkliga dina ambitioner.",
    "financing.request": "Ansök om finansiering",
    "financing.history": "Mina ansökningar",
    "financing.reimbursable_label": "Återbetalningspliktig",
    "financing.non_reimbursable_label": "Icke återbetalningspliktig",
    "financing.status.pending": "Väntar",
    "financing.status.granted": "Beviljad",
    "financing.status.refused": "Avvisad",
    "financing.form.title": "Ansökan — {name}",
    "financing.form.cancel": "Avbryt",
    "financing.form.submit": "Skicka ansökan",
    "financing.form.project": "Projektnamn",
    "financing.form.project_ph": "Mitt projekt",
    "financing.form.amount": "Begärt belopp (€)",
    "financing.form.sector": "Sektor",
    "financing.form.sector_ph": "Tech, jordbruk, …",
    "financing.form.description": "Projektbeskrivning",
    "financing.form.description_ph": "Presentera ditt projekt, dess mål och förväntad effekt…",
    "settings.security": "Säkerhet",
    "settings.current_pw": "Nuvarande lösenord",
    "settings.new_pw": "Nytt lösenord",
    "settings.confirm_pw": "Bekräfta",
    "settings.submit": "Ändra lösenord",
    "settings.no_match": "Lösenorden stämmer inte överens",
    "settings.changed": "Lösenord ändrat",
    "common.cancel": "Avbryt",
    "common.confirm": "Bekräfta",
    "common.close": "Stäng",
    "common.save": "Spara",
    "common.loading": "Laddar…",
    "common.send": "Skicka",
    "common.total": "Totalt",
    "admin.wallet.balance": "Tillgängligt saldo",
    "admin.wallet.history": "Historik",
    "admin.wallet.no_history": "Inga transaktioner",
    "admin.wallet.title": "Min plånbok",
    "admin.wallet.topup": "Fyll på",
    "beneficiary.pick": "Välj",
    "beneficiary.save_this": "Spara denna mottagare",
    "beneficiary.saved": "Sparade mottagare",
    "beneficiary.saved_ok": "Mottagare sparad",
    "card.color.amber": "Bärnsten",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Smaragd",
    "card.color.forest": "Skog",
    "card.color.gold": "Guld",
    "card.color.lavender": "Lavendel",
    "card.color.night": "Natt",
    "card.color.orange": "Orange",
    "card.color.pink": "Rosa",
    "card.color.royal": "Kunglig blå",
    "card.color.ruby": "Rubin",
    "card.color.slate": "Skiffer",
    "card.color.steel": "Stål",
    "card.color.terracotta": "Terrakotta",
    "card.color.violet": "Violett",
    "common.other": "Övrigt",
    "common.search": "Sök",
    "content.translated": "Automatiskt översatt innehåll",
    "credits.monthly_payment": "Beräknad månadsbetalning",
    "credits.rate_annual": "/ år",
    "credits.rate_used": "Beräknad ränta:",
    "credits.simulation": "Simulering av månadsbetalning",
    "credits.total_cost": "Beräknad totalkostnad",
    "demo.banner": "Demoläge — inga riktiga åtgärder",
    "demo.disabled": "Åtgärd ej tillgänglig i demoläge",
    "deposit.default_instructions": "Kontakta din handläggare för insättningsdetaljer.",
    "error.back_home": "Tillbaka till startsidan",
    "error.bank_not_found": "Banken hittades inte",
    "error.unknown": "Okänt fel",
    "kyc.file_read_error": "Filen kan inte läsas",
    "kyc.file_too_large": "Filen är för stor (max 5 MB)",
    "login.history": "Inloggningshistorik",
    "login.history.empty": "Inga inloggningar registrerade.",
    "notif.dismiss": "Stäng",
    "notif.locked": "Bankmeddelande",
    "tracking.error.not_found": "Spårningsnummer hittades inte. Kontrollera och försök igen.",
    "tracking.label": "Spårning",
    "tracking.status.delivered": "Levererad",
    "tracking.status.preparation": "Under förberedelse",
    "tracking.status.shipped": "Skickad",
    "tracking.status.transit": "Under transport",
    "transfer.code.copied": "Kopierad",
    "transfer.code.copy": "Kopiera",
    "transfer.code.received": "Din valideringskod",
    "dash.welcome": "Välkommen",
    "common.delete": "Ta bort",
    "cards.delete.title": "Ta bort kort",
    "cards.delete.confirm_msg": "Denna åtgärd är oåterkallelig. Kortet tas bort permanent.",
    "cards.delete.physical_tooltip": "Fysiskt kort beställt — borttagning omöjlig",
    "cards.order.confirmed": "Beställning bekräftad!",
    "cards.order.confirmed_body": "Ditt fysiska kort förbereds. Använd detta nummer för att spåra din leverans.",
    "cards.order.tracking_no": "Spårningsnr.",
    "cards.fee_deducted": "{amount} dras från ditt saldo",
    "cards.insufficient_short": "Otillräckligt saldo ({available} tillgängligt)",
    "transfer.section.beneficiary": "Mottagare",
    "transfer.section.details": "Överföring",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bank",
    "transfer.field.city": "Stad",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bank:",
    "transfer.progress.paused": "Väntar på validering",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Rörlig",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Kort raderat",
    "cards.feat.gold_exclusive": "Exklusivt guldkort",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Köparskydd",
    "cards.feat.support_standard": "Standardsupport",
    "cards.feat.plastic_premium": "Premium plastkort",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Standard reseförsäkring",
    "cards.feat.support_priority": "Prioriterad support",
    "cards.feat.metal_brushed": "Borstat metallkort",
    "cards.feat.cashback_2_unlimited": "Obegränsad 2% cashback",
    "cards.feat.travel_premium": "Premium reseförsäkring",
    "cards.feat.vip_lounge": "VIP-lounge-åtkomst",
    "cards.feat.support_247": "24/7 support",
    "auth.demo.title": "Administratörsinloggning",
    "auth.demo.subtitle": "Ange dina admin-uppgifter för att förhandsgranska demon",
    "credits.garantie.none": "Ingen",
    "credits.garantie.mortgage": "Hypotekslån på den finansierade fastigheten",
    "credits.garantie.vehicle": "Finansierat fordon",
    "credits.garantie.parental": "Föräldraskap eller bankgaranti",
    "credits.garantie.professional": "Professionella tillgångar eller personlig borgen",
    "cards.reveal.pin_prompt": "Ange din 4-siffriga PIN-kod för att visa kortinformationen.",
    "cards.reveal.show": "Visa",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "De två PIN-koderna stämmer inte överens",
    "auth.pin.digit_hint": "4 siffror",
  
  },
  no: {
    "notif.transfer_code.title": "Bekreftelseskode for overføring",
    "notif.transfer_code.body": "Kopier denne koden for å bekrefte overføringen.",
    "notif.transfer_code.expires_in": "Forsvinner om {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} måneder",
    "credits.n_months": "{n} måneder",
    "msg.read": "Lest",
    "msg.sent": "Sendt",
    "msg.attach_aria": "Legg ved fil",
    "login.device.unknown": "Ukjent nettleser",
    "login.device.mobile": "Mobil",
    "login.device.tablet": "Nettbrett",
    "login.device.desktop": "Datamaskin",
    "login.recent": "Nylig",
    "err.invalid_credentials": "Ugyldige påloggingsopplysninger",
    "err.bad_pin": "Feil PIN",
    "err.account_blocked": "Konto sperret",
    "err.bad_current_pw": "Nåværende passord er feil",
    "err.insufficient_funds": "Utilstrekkelige midler, fyll på kontoen din.",
    "err.amount_above_savings": "Beløpet overstiger tilgjengelig sparing",
    "err.must_be_positive": "Verdien må være positiv",
    "err.invalid_format": "Ugyldig format",
    "err.email_taken": "E-post allerede i bruk",
    "err.card_not_found": "Kort ikke funnet",
    "err.physical_card_already_ordered": "Fysisk kort allerede bestilt",
    "err.beneficiary_not_found": "Mottaker ikke funnet",
    "err.invalid_code": "Ugyldig kode",
    "err.no_transfer_code": "Ingen kode for denne overføringen",
    "err.link_expired": "Lenke ugyldig eller utløpt",
    "err.thread_not_found": "Samtale ikke funnet",
    "err.empty_message": "Tom melding",
    "err.file_too_large": "Fil for stor (maks 20 MB)",
    "err.forbidden": "Tilgang nektet",
    "err.bank_not_found": "Bank ikke funnet",
    "err.signups_closed": "Registreringer er stengt for denne banken.",
    "err.access_suspended": "Tilgang midlertidig suspendert. Vennligst kontakt banken din.",
    "err.notif_not_closable": "Dette varselet kan ikke lukkes.",
    "auth.signup.tagline": "Bli med oss i dag",
    "auth.login.tagline": "Velkommen tilbake!",
    "auth.signup.tagline_sub": "Åpne kontoen din på 2 minutter og få tilgang til alle våre premium banktjenester.",
    "auth.login.tagline_sub": "Få tilgang til ditt personlige område og administrer finansene dine trygt.",
    "auth.back": "Tilbake til forsiden",
    "auth.bullet.security": "Militærklassifisert banksikkerhet",
    "auth.bullet.transfers": "Øyeblikkelige overføringer døgnet rundt",
    "auth.bullet.investments": "Integrerte investeringer og sparing",
    "auth.bullet.advisor": "Personlig rådgiver tilgjengelig 7 dager/7",
    "auth.footer": "Drevet av {bank}",
    "auth.signup.title": "Opprett konto",
    "auth.login.title": "Logg inn",
    "auth.field.lastName": "Etternavn",
    "auth.field.firstName": "Fornavn",
    "auth.field.country": "Land",
    "auth.field.country.placeholder": "— Velg ditt land —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Adresse",
    "auth.field.address.placeholder": "Storgata 123, Oslo",
    "auth.field.email": "E-post",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Passord",
    "auth.submit.signup": "Opprett min konto",
    "auth.submit.login": "Logg inn",
    "auth.login.badge": "Logg inn på kontoen din",
    "auth.card.tagline": "Ditt kundeområde",
    "auth.submit.loading": "Laster…",
    "auth.pin.title": "PIN-kode",
    "auth.pin.subtitle": "Skriv inn din 4-sifrede PIN-kode",
    "auth.pin.label": "PIN-kode",
    "auth.pin.confirm": "Bekreft PIN-kode",
    "auth.pin.submit": "Bekreft",
    "auth.pin.back": "Tilbake til innlogging",
    "auth.pin.length_error": "PIN-koden må være 4 sifre",
    "auth.switch.have_account": "Har du allerede en konto?",
    "auth.switch.login_link": "Logg inn",
    "auth.switch.no_account": "Ikke kunde ennå?",
    "auth.error.select_country": "Velg ditt land",
    "kyc.title": "Identitetsverifisering (KYC)",
    "kyc.sub": "Last opp dokumentene dine for å validere kontoen din. Alle dokumenter er kryptert og konfidensielle.",
    "kyc.id_type": "Type identitetsdokument *",
    "kyc.id_card": "Nasjonalt ID-kort",
    "kyc.passport": "Pass",
    "kyc.driver_license": "Førerkort",
    "kyc.recto_passport": "Passets forside *",
    "kyc.recto_id": "ID-kortets forside *",
    "kyc.verso": "ID-kortets bakside *",
    "kyc.selfie": "Selfie med ID-dokumentet i hånden *",
    "kyc.address_proof": "Adressebevis *",
    "kyc.hint_doc": "Bilde eller PDF, maks 5 MB",
    "kyc.hint_selfie": "Tydelig bilde, ansikt og dokument synlig",
    "kyc.hint_address": "Regning, kontoutskrift, attest… (maks 5 MB)",
    "kyc.upload": "Last opp",
    "kyc.replace": "Erstatt",
    "pending.title": "Konto venter",
    "pending.body": "Forespørselen din er mottatt. En administrator vil gjennomgå saken din og aktivere kontoen din om kort tid.",
    "pending.back": "Tilbake til innlogging",
    "home.nav.features": "Funksjoner",
    "home.nav.security": "Sikkerhet",
    "home.nav.invest": "Investering",
    "home.nav.credits": "Kreditter",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Logg inn",
    "home.hero.tagline": "Nettbank for neste generasjon",
    "home.hero.title1": "Administrer pengene dine",
    "home.hero.title2": "med intelligens",
    "home.hero.sub": "En komplett bankplattform — overføringer, sparing, investeringer og kreditter fra smarttelefonen din.",
    "home.hero.login": "Logg inn",
    "home.hero.trust.secure": "100% Sikker",
    "home.hero.trust.fast": "Rask åpning",
    "home.hero.trust.encrypted": "Krypterte data",
    "home.hero.trust.support": "Service døgnet rundt",
    "home.mockup.balance": "Tilgjengelig saldo",
    "home.mockup.currency": "EUR · Hovedkonto",
    "home.mockup.recharge": "Fyll på",
    "home.mockup.send": "Send",
    "home.mockup.save": "Spar",
    "home.mockup.salary": "Lønn mars",
    "home.mockup.rent": "Leie april",
    "home.mockup.investment": "Investering",
    "home.mockup.days2": "for 2 dager siden",
    "home.mockup.days3": "for 3 dager siden",
    "home.mockup.days5": "for 5 dager siden",
    "home.stats.accounts": "Aktive kontoer",
    "home.stats.uptime": "Driftstid",
    "home.stats.transfers": "Overføringer",
    "home.stats.support": "Støtte",
    "home.features.label": "Funksjoner",
    "home.features.title": "Alt du trenger",
    "home.features.sub": "En komplett plattform designet for å forenkle håndteringen av finansene dine i hverdagen.",
    "home.feature.security": "Militærklassifisert sikkerhet",
    "home.feature.security_body": "AES-256-kryptering, tofaktorautentisering og 24/7-kontoovervåking.",
    "home.feature.cards": "Virtuelt og fysisk kort",
    "home.feature.cards_body": "Utsted kort på sekunder eller bestill et ekte fysisk kort levert til deg.",
    "home.feature.transfers": "Expressoverføringer",
    "home.feature.transfers_body": "Send og motta penger overalt i verden på sekunder.",
    "home.feature.analytics": "Sanntidsanalyse",
    "home.feature.analytics_body": "Smarte dashbord for å følge utgiftene dine og forutse finansene dine.",
    "home.feature.investments": "Integrerte investeringer",
    "home.feature.investments_body": "Få tilgang til utvalgte investeringsprodukter direkte fra kontoen din.",
    "home.feature.support": "Dedikert support døgnet rundt",
    "home.feature.support_body": "En personlig bankrådgiver tilgjengelig når som helst via sikker meldingstjeneste.",
    "home.tpl.label": "Tillit",
    "home.tpl.title": "De stoler på oss",
    "home.mockup.balance_label": "Saldo",
    "home.mockup.virement": "Overføring",
    "home.mockup.salaire": "Lønn",
    "home.security.label": "Sikkerhet",
    "home.security.title1": "Finansene dine",
    "home.security.title2": "i absolutt sikkerhet",
    "home.security.sub": "Vi anvender de strengeste banksikkerhetsstandardene for å beskytte hver krone.",
    "home.security.aes.title": "AES-256-kryptering",
    "home.security.aes.body": "All sensitiv data krypteres med de samme algoritmene som brukes av de store internasjonale bankene.",
    "home.security.auth.title": "Forsterket autentisering",
    "home.security.auth.body": "Dobbel identitetsverifisering ved hver innlogging og for hver sensitiv operasjon på kontoen din.",
    "home.security.compliance.title": "PSD2/GDPR-samsvar",
    "home.security.compliance.body": "Vi overholder alle europeiske forskrifter om betalingstjenester og databeskyttelse.",
    "home.invest.label": "Investeringer",
    "home.invest.title": "La pengene dine vokse",
    "home.invest.sub": "6 investeringskategorier valgt for sitt potensial og pålitelighet.",
    "home.invest.yield": "Avkastning",
    "home.credits.label": "Kreditter",
    "home.credits.title": "Løsninger for hvert prosjekt",
    "home.credits.sub": "Konkurransedyktige renter, raske svar og personlig oppfølging for alle prosjektene dine.",
    "home.credits.rate": "Rente",
    "home.faq.label": "FAQ",
    "home.faq.title": "Vanlige spørsmål",
    "home.faq.q1": "Hvordan åpner jeg en konto?",
    "home.faq.a1": "Registreringen tar mindre enn 2 minutter. Fyll inn personopplysningene dine, velg et passord, og kontoen din aktiveres etter validering av teamet vårt.",
    "home.faq.q2": "Er dataene mine sikre?",
    "home.faq.a2": "Ja. All data krypteres med AES-256 i ro og TLS 1.3 under overføring. Vi overholder internasjonale bankstandarder og GDPR/PSD2-forskrifter.",
    "home.faq.q3": "Hvilke typer investeringer tilbyr dere?",
    "home.faq.a3": "Vi tilbyr 6 kategorier: Eiendom, Crowdfunding, Fornybar energi, Aksjemarkedet, Deltakende og Krypto. Hvert produkt er valgt ut for sitt risiko/avkastningsforhold.",
    "home.faq.q4": "Hvordan søker jeg om kreditt?",
    "home.faq.a4": "Gå til kredittseksjonen i kundeområdet ditt, velg lånetype, oppgi beløp og varighet. Søknaden din behandles innen 24 timer av rådgiverne våre.",
    "home.faq.q5": "Finnes det skjulte gebyrer?",
    "home.faq.a5": "Ingen skjulte gebyrer. Alle priser vises tydelig ved hver transaksjon. Vilkårene er tilgjengelige i kundeområdet ditt når som helst.",
    "home.faq.q6": "Hvordan kontakter jeg support?",
    "home.faq.a6": "Bruk den integrerte meldingstjenesten i kundeområdet ditt for å kontakte din personlige bankrådgiver direkte, tilgjengelig døgnet rundt.",
    "home.cta.title": "Klar til å begynne?",
    "home.cta.sub": "Bli med tusenvis av brukere som administrerer finansene sine med {bank}. Registreringen tar mindre enn 2 minutter.",
    "home.footer.products": "Produkter",
    "home.footer.services": "Tjenester",
    "home.footer.access": "Hurtigtilgang",
    "home.footer.accounts": "Kontoer",
    "home.footer.transfers": "Overføringer",
    "home.footer.cards": "Kort",
    "home.footer.savings": "Sparing",
    "home.footer.investments": "Investeringer",
    "home.footer.credits": "Kreditter",
    "home.footer.financing": "Finansiering",
    "home.footer.messages": "Meldinger",
    "home.footer.login": "Logg inn",
    "home.footer.signup": "Åpne en konto",
    "home.footer.support": "Support døgnet rundt",
    "home.footer.tagline": "Din nettbank, tilgjengelig overalt og når som helst.",
    "home.footer.copyright": "© {year} {bank} — Alle rettigheter forbeholdt",
    "home.footer.powered": "Drevet av {bank}",
    "app.menu_label": "Hovedmeny",
    "app.nav.dashboard": "Dashbord",
    "app.nav.accounts": "Kontoer",
    "app.nav.transactions": "Transaksjoner",
    "app.nav.transfers": "Overføringer",
    "app.nav.cards": "Kort",
    "app.nav.savings": "Sparing",
    "app.nav.investments": "Investering",
    "app.nav.credit": "Kreditt",
    "app.nav.financing": "Finansiering",
    "app.nav.messages": "Meldinger",
    "app.nav.card_tracking": "Kortsporing",
    "app.nav.notifications": "Varsler",
    "app.nav.settings": "Innstillinger",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Hjem",
    "app.nav.mobile.transfers": "Overføringer",
    "app.nav.mobile.cards": "Kort",
    "app.nav.mobile.transactions": "Transaksjoner",
    "app.nav.more.invest": "Invester",
    "app.user_role": "Kunde",
    "app.logout": "Logg ut",
    "app.search": "Søk…",
    "app.more_title": "Flere funksjoner",
    "app.more_btn": "Mer",
    "app.loading": "Laster…",
    "app.error_back": "Tilbake til innlogging",
    "app.see_more_notifs": "Se {count} flere varsler →",
    "page.accounts": "Mine kontoer",
    "page.transactions": "Transaksjoner",
    "page.transfers": "Ny overføring",
    "page.deposit": "Innskudd",
    "page.cards": "Mine kort",
    "page.savings": "Sparing",
    "page.investments": "Investering",
    "page.credits": "Kreditt",
    "page.financing": "Prosjektfinansiering",
    "page.messages": "Meldinger",
    "page.settings": "Innstillinger",
    "page.notifications": "Viktige varsler",
    "page.profile": "Min profil",
    "dash.greeting": "Hei, {name}",
    "dash.overview": "Oversikt over finansene dine — {date}",
    "dash.total_balance": "Total saldo",
    "dash.this_month": "+{amount} denne måneden",
    "dash.view_iban": "Se mitt IBAN",
    "dash.my_accounts": "Mine kontoer",
    "dash.primary_account": "Hovedkonto",
    "dash.savings_account": "Sparekonto",
    "dash.remunerated": "Rentebærende",
    "dash.details": "Detaljer",
    "dash.transfer_btn": "Overføring",
    "dash.quick_actions": "Hurtighandlinger",
    "dash.quick.deposit": "Innskudd",
    "deposit.title": "Innskuddsinformasjon",
    "dash.quick.transfer": "Overføring",
    "dash.quick.history": "Historikk",
    "dash.quick.cards": "Mine kort",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Månedlig budsjett",
    "dash.income": "Inntekter",
    "dash.expenses": "Utgifter",
    "dash.spending_cat": "Utgifter per kategori",
    "dash.no_spending": "Ingen utgifter registrert.",
    "dash.recent_tx": "Nylige transaksjoner",
    "dash.see_all": "Se alle",
    "dash.no_tx": "Ingen transaksjoner.",
    "dash.main_card": "Mitt hovedkort",
    "dash.manage": "Administrer",
    "dash.block": "Blokker",
    "dash.pin": "PIN-kode",
    "dash.security": "Sikkerhet",
    "dash.more": "Mer",
    "dash.no_card": "Ingen kort utstedt",
    "dash.monthly_activity": "Månedlig aktivitet",
    "dash.operations": "Transaksjoner",
    "dash.holder": "Innehaver",
    "dash.expires": "Utl.",
    "dash.visa_label": "VISA",
    "dash.balance": "Tilgjengelig saldo",
    "tx.all": "Alle",
    "tx.income": "Inntekter",
    "tx.expenses": "Utgifter",
    "tx.today": "I dag",
    "tx.yesterday": "I går",
    "tx.none": "Ingen transaksjoner.",
    "transfer.title": "Rask overføring",
    "transfer.recipient": "Mottaker",
    "transfer.recipient_ph": "Navn, IBAN, telefonnummer…",
    "transfer.amount": "Beløp",
    "transfer.label": "Etikett",
    "transfer.reason": "Årsak (valgfritt)",
    "transfer.reason_ph": "F.eks. husleie, tilbakebetaling…",
    "transfer.execution": "Utførelse",
    "transfer.immediate": "Umiddelbar",
    "transfer.delayed": "Forsinket",
    "transfer.balance": "Tilgjengelig saldo:",
    "transfer.continue": "Fortsett",
    "transfer.confirm": "Bekreft",
    "transfer.cancel": "Avbryt",
    "transfer.code": "Valideringskode",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Skriv inn koden du mottok for å bekrefte overføringen",
    "transfer.pending.title": "Overføring venter på validering",
    "transfer.pending.amount": "Beløp:",
    "transfer.pending.to": "Til:",
    "transfer.pending.hint": "Valideringskoden din vil bli sendt via e-post eller via varselklokken. Koden består av 6 sifre. Hvis du ikke mottar den, vennligst kontakt support.",
    "transfer.pending.validate": "Bekreft overføringen",
    "transfer.saved": "Overføringen er lagret. Be administratoren din om valideringskoden.",
    "transfer.done": "Overføring gjennomført",
    "transfer.validated": "Overføring validert!",
    "transfer.progress.processing": "Behandler overføring…",
    "transfer.progress.completed": "Overføring fullført!",
    "transfer.progress.default_message": "Overføringen din behandles. Vennligst vent.",
    "cards.none": "Ingen kort utstedt.\nKontakt administratoren din.",
    "cards.holder": "Innehaver",
    "cards.expires": "Utl.",
    "cards.block": "Lås",
    "cards.pin": "PIN-kode",
    "cards.more": "Mer",
    "cards.limits": "Kortgrenser",
    "cards.payment": "Betaling",
    "cards.withdrawal": "Uttak",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Sperret",
    "cards.status_active": "Aktiv",
    "cards.toast.blocked": "Kort sperret",
    "cards.toast.unblocked": "Kort låst opp",
    "cards.toast.physical_ordered": "Fysisk kort bestilt! Levering innen 5–10 virkedager.",
    "cards.toast.created": "Kort opprettet!",
    "cards.none_sub": "Ingen kort utstedt. Opprett ditt virtuelle kort nå.",
    "cards.generate_btn": "Opprett mitt kort",
    "cards.create.title": "Opprett mitt virtuelle kort",
    "cards.create.network": "Betalingsnettverk",
    "cards.create.color": "Kortfarge",
    "cards.create.preview_name": "DITT NAVN",
    "cards.create.preview_expiry": "MM/ÅÅ",
    "cards.create.loading": "Oppretter…",
    "cards.create.submit": "Opprett kort",
    "cards.btn.unblock": "Lås opp",
    "cards.btn.block": "Sperre",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fysisk",
    "cards.btn.physical_done": "Bestilt",
    "cards.limits_title": "Grenser",
    "cards.reveal.title": "Kortinformasjon",
    "cards.reveal.number": "Fullstendig nummer",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Utløpsdato",
    "cards.reveal.warning": "Del aldri denne informasjonen. Lukk dette vinduet etter bruk.",
    "cards.pin.title": "PIN-kode",
    "cards.pin.message": "Denne PIN-koden brukes for alle dine nettbetalinger og uttak. Del den aldri med noen.",
    "cards.physical.title": "Bestill fysisk kort",
    "cards.physical.fee_label": "Leveringsgebyr",
    "cards.physical.balance_label": "Din saldo",
    "cards.physical.insufficient": "Utilstrekkelig saldo. Vennligst fyll opp kontoen din før du bestiller.",
    "cards.physical.info": "Gebyret trekkes fra kontoen din. Levering innen 5–10 virkedager.",
    "cards.physical.order_btn": "Bestill ({fee})",
    "cards.physical.ordering": "Bestiller…",
    "cards.physical.order_cta": "Bestill et fysisk kort",
    "cards.physical.choose_type": "Velg ditt kort",
    "cards.physical.features": "Fordeler",
    "cards.buy_virtual_cta": "Kjøp et virtuelt kort",
    "notif.none": "Ingen varsler for øyeblikket.",
    "notif.contact_support": "Kontakt support",
    "profile.change_photo": "Endre bilde",
    "profile.remove_photo": "Fjern bilde",
    "profile.email": "E-post",
    "profile.phone": "Telefon",
    "profile.country": "Land",
    "profile.address": "Adresse",
    "profile.notifs_title": "Varsler ({count})",
    "profile.no_notifs": "Ingen varsler.",
    "profile.logout": "Logg ut",
    "profile.photo_updated": "Profilbilde oppdatert",
    "profile.photo_removed": "Bilde fjernet",
    "profile.photo_too_large": "Bildet er for stort (maks 1 MB)",
    "profile.powered": "Drevet av {bank}",
    "iban.title": "Mitt IBAN",
    "iban.loading": "Laster…",
    "iban.bic": "BIC / SWIFT-kode",
    "iban.share_info": "Del disse opplysningene for å motta en overføring til kontoen din.",
    "iban.copy": "Kopier",
    "iban.copied": "Kopiert til utklippstavlen",
    "iban.copy_fail": "Kan ikke kopiere",
    "iban.close": "Lukk",
    "accounts.all": "Alle kontoene dine hos {bank}.",
    "accounts.primary": "Hovedkonto",
    "accounts.savings": "Sparekonto",
    "accounts.primary_tag": "Primær",
    "accounts.secondary_tag": "Sekundær",
    "accounts.objective": "Mål: {amount} · {pct}%",
    "accounts.copy": "Kopier",
    "accounts.loading": "Laster…",
    "accounts.iban_copied": "IBAN kopiert",
    "accounts.copy_fail": "Kan ikke kopiere",
    "savings.total": "Totalt spart",
    "savings.target_total": "Totalt mål",
    "savings.progress": "Fremgang",
    "savings.active": "Aktive mål",
    "savings.new_goal": "Nytt mål",
    "savings.none": "Ingen sparemål ennå.",
    "savings.none_sub": "Opprett ditt første mål for å komme i gang.",
    "savings.saved": "Spart:",
    "savings.goal": "Mål:",
    "savings.deadline": "Frist: {date}",
    "savings.deposit": "Sett inn",
    "savings.withdraw": "Ta ut",
    "savings.loading": "Laster…",
    "savings.form.title": "Nytt sparemål",
    "savings.form.cancel": "Avbryt",
    "savings.form.create": "Opprett",
    "savings.form.name": "Målnavn",
    "savings.form.name_ph": "Japanreise, Bil, …",
    "savings.form.target": "Målbeløp",
    "savings.form.initial": "Første innskudd",
    "savings.form.deadline": "Frist (valgfritt)",
    "savings.form.color": "Farge",
    "savings.deposit_title": "Sett inn på \"{name}\"",
    "savings.deposit_amount": "Beløp å sette inn",
    "savings.balance_hint": "Hovedsaldo: {amount}",
    "savings.withdraw_prompt": "Hvor mye vil du ta ut fra \"{name}\"?",
    "savings.close_confirm": "Avslutt målet \"{name}\"? Sparingen returneres til hovedkontoen din.",
    "savings.toast.created": "Mål opprettet",
    "savings.toast.deposited": "Innskudd utført",
    "savings.toast.withdrawn": "Uttak utført",
    "savings.toast.closed": "Mål avsluttet",
    "invest.total": "Totalt investert",
    "invest.profits": "Akkumulert fortjeneste",
    "invest.count": "Investeringer",
    "invest.products": "Tilgjengelige produkter",
    "invest.invest_btn": "Invester",
    "invest.my": "Mine investeringer",
    "invest.pending": "Venter",
    "invest.validated": "Validert",
    "invest.refused": "Avvist",
    "invest.withdraw.btn": "Ta ut",
    "invest.withdraw.title": "Ta ut investeringen min",
    "invest.withdraw.intro": "Du er i ferd med å ta ut investeringen din \"{product}\". Den opprinnelige kapitalen og påløpt fortjeneste vil bli kreditert hovedsaldoen din.",
    "invest.withdraw.principal": "Kapital",
    "invest.withdraw.profit": "Fortjeneste",
    "invest.withdraw.total": "Totalt å motta",
    "invest.withdraw.confirm": "Bekreft uttak",
    "invest.withdraw.warning": "Denne handlingen kan ikke angres. Investeringen vil bli avsluttet.",
    "invest.withdraw.success": "Investering tatt ut — {amount} kreditert saldoen.",
    "invest.withdrawn": "Uttatt",
    "invest.cta_sub": "Velg den typen investering som passer deg. En rådgiver vil hjelpe deg fra din første forespørsel.",
    "invest.form.amount": "Beløp å investere",
    "invest.form.balance": "Tilgjengelig saldo: {amount}",
    "invest.form.note": "Merknad til rådgiveren din (valgfritt)",
    "invest.form.note_ph": "Spesifiser forventningene dine…",
    "invest.form.advisor_info": "En samtale åpnes automatisk med rådgiveren din.",
    "invest.form.cancel": "Avbryt",
    "invest.form.submit": "Send forespørsel",
    "invest.toast.requested": "Forespørsel registrert — en samtale er åpnet med rådgiveren din.",
    "invest.p1.name": "Eiendom",
    "invest.p1.desc": "SCPI, eiendomspapir, eiendomscrowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Deltakerfinansiering av utvalgte prosjekter",
    "invest.p3.name": "Fornybar energi",
    "invest.p3.desc": "Sol-, vind- og grønne prosjekter",
    "invest.p4.name": "Aksjemarkedet",
    "invest.p4.desc": "Aksjer, ETF og diversifiserte fond",
    "invest.p5.name": "Deltakende",
    "invest.p5.desc": "Lån til PME og lokale gründere",
    "invest.p6.name": "Krypto",
    "invest.p6.desc": "Bitcoin, Ethereum og lommebok for flere valutaer",
    "msg.online": "Online",
    "msg.loading": "Laster…",
    "msg.none": "Ingen meldinger. Start samtalen med rådgiveren din.",
    "msg.placeholder": "Skriv meldingen din…",
    "msg.attach.image": "Bilde",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Lyd",
    "msg.attach.file": "Fil / Dokument",
    "msg.pdf_doc": "PDF-dokument",
    "msg.file": "Fil",
    "msg.too_large": "Filen er for stor (maks {size}).",
    "msg.withdraw": "Slett",
    "credits.cta_sub": "Søk direkte online — svar innen 24 timer.",
    "credits.learn_more": "Les mer",
    "credits.request": "Søk",
    "credits.history": "Mine søknader",
    "credits.status.pending": "Venter",
    "credits.status.accepted": "Akseptert",
    "credits.status.refused": "Avvist",
    "credits.chars": "Egenskaper",
    "credits.amount_range": "Beløp: {min}€ – {max}€",
    "credits.rate_range": "Rente: {min}% – {max}% per år",
    "credits.duration_range": "Varighet: {min} til {max} måneder",
    "credits.deposit_min": "Første innskudd: {amount}€ minimum",
    "credits.garantie": "Garanti: {garantie}",
    "credits.eligibility": "Kvalifikasjonskrav",
    "credits.elig.income": "Dokumentere regelmessige og stabile inntekter",
    "credits.elig.deposit": "Foreta et første innskudd på {amount}€",
    "credits.elig.garantie": "Fremlegge garantien: {garantie}",
    "credits.form.title": "Søknad — {name}",
    "credits.form.cancel": "Avbryt",
    "credits.form.submit": "Send søknad",
    "credits.form.amount": "Ønsket beløp (€)",
    "credits.form.duration": "Varighet (måneder)",
    "credits.form.reason": "Årsak (valgfritt)",
    "credits.form.reason_ph": "Beskriv prosjektet ditt…",
    "credits.form.close": "Lukk",
    "credits.toast.submitted": "Kredittsøknad sendt — du vil motta svar innen 24 timer.",
    "credits.p1.name": "Personlig lån",
    "credits.p1.desc": "For å finansiere hverdagslige behov eller personlige prosjekter.",
    "credits.p2.name": "Boliglån",
    "credits.p2.desc": "For å finansiere kjøp eller bygging av eiendom.",
    "credits.p3.name": "Bil- / Motorsykkellån",
    "credits.p3.desc": "Kjøp et kjøretøy med trygghet.",
    "credits.p4.name": "Studielån",
    "credits.p4.desc": "Økonomisk støtte for høyere utdanning.",
    "credits.p5.name": "Næringslån",
    "credits.p5.desc": "For å støtte veksten av en yrkesaktivitet.",
    "credits.p6.name": "Ekspresslån",
    "credits.p6.desc": "En rask løsning for akutte behov.",
    "financing.reimbursable": "Tilbakebetalingspliktig finansiering",
    "financing.reimbursable_desc": "Økonomisk støtte med faste vilkår for å starte eller utvikle en virksomhet.",
    "financing.non_reimbursable": "Ikke-tilbakebetalingspliktig finansiering",
    "financing.non_reimbursable_desc": "For prosjekter med sosialt, utdanningsmessig eller humanitært formål (ONG, foreninger).",
    "financing.cta_sub": "Skreddersydde løsninger for å realisere ambisjonene dine.",
    "financing.request": "Søk om finansiering",
    "financing.history": "Mine søknader",
    "financing.reimbursable_label": "Tilbakebetalingspliktig",
    "financing.non_reimbursable_label": "Ikke-tilbakebetalingspliktig",
    "financing.status.pending": "Venter",
    "financing.status.granted": "Innvilget",
    "financing.status.refused": "Avvist",
    "financing.form.title": "Søknad — {name}",
    "financing.form.cancel": "Avbryt",
    "financing.form.submit": "Send søknad",
    "financing.form.project": "Prosjektnavn",
    "financing.form.project_ph": "Mitt prosjekt",
    "financing.form.amount": "Forespurt beløp (€)",
    "financing.form.sector": "Sektor",
    "financing.form.sector_ph": "Tech, landbruk, …",
    "financing.form.description": "Prosjektbeskrivelse",
    "financing.form.description_ph": "Presenter prosjektet ditt, dets mål og forventet effekt…",
    "settings.security": "Sikkerhet",
    "settings.current_pw": "Nåværende passord",
    "settings.new_pw": "Nytt passord",
    "settings.confirm_pw": "Bekreft",
    "settings.submit": "Endre passord",
    "settings.no_match": "Passordene stemmer ikke overens",
    "settings.changed": "Passord endret",
    "common.cancel": "Avbryt",
    "common.confirm": "Bekreft",
    "common.close": "Lukk",
    "common.save": "Lagre",
    "common.loading": "Laster…",
    "common.send": "Send",
    "common.total": "Totalt",
    "admin.wallet.balance": "Tillgängligt saldo",
    "admin.wallet.history": "Historik",
    "admin.wallet.no_history": "Inga transaktioner",
    "admin.wallet.title": "Min plånbok",
    "admin.wallet.topup": "Fyll på",
    "beneficiary.pick": "Välj",
    "beneficiary.save_this": "Spara denna mottagare",
    "beneficiary.saved": "Sparade mottagare",
    "beneficiary.saved_ok": "Mottagare sparad",
    "card.color.amber": "Bärnsten",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Smaragd",
    "card.color.forest": "Skog",
    "card.color.gold": "Guld",
    "card.color.lavender": "Lavendel",
    "card.color.night": "Natt",
    "card.color.orange": "Orange",
    "card.color.pink": "Rosa",
    "card.color.royal": "Kunglig blå",
    "card.color.ruby": "Rubin",
    "card.color.slate": "Skiffer",
    "card.color.steel": "Stål",
    "card.color.terracotta": "Terrakotta",
    "card.color.violet": "Violett",
    "common.other": "Övrigt",
    "common.search": "Sök",
    "content.translated": "Automatiskt översatt innehåll",
    "credits.monthly_payment": "Beräknad månadsbetalning",
    "credits.rate_annual": "/ år",
    "credits.rate_used": "Beräknad ränta:",
    "credits.simulation": "Simulering av månadsbetalning",
    "credits.total_cost": "Beräknad totalkostnad",
    "demo.banner": "Demoläge — inga riktiga åtgärder",
    "demo.disabled": "Åtgärd ej tillgänglig i demoläge",
    "deposit.default_instructions": "Kontakta din handläggare för insättningsdetaljer.",
    "error.back_home": "Tillbaka till startsidan",
    "error.bank_not_found": "Banken hittades inte",
    "error.unknown": "Okänt fel",
    "kyc.file_read_error": "Filen kan inte läsas",
    "kyc.file_too_large": "Filen är för stor (max 5 MB)",
    "login.history": "Inloggningshistorik",
    "login.history.empty": "Inga inloggningar registrerade.",
    "notif.dismiss": "Stäng",
    "notif.locked": "Bankmeddelande",
    "tracking.error.not_found": "Spårningsnummer hittades inte. Kontrollera och försök igen.",
    "tracking.label": "Spårning",
    "tracking.status.delivered": "Levererad",
    "tracking.status.preparation": "Under förberedelse",
    "tracking.status.shipped": "Skickad",
    "tracking.status.transit": "Under transport",
    "transfer.code.copied": "Kopierad",
    "transfer.code.copy": "Kopiera",
    "transfer.code.received": "Din valideringskod",
    "dash.welcome": "Velkommen",
    "common.delete": "Slett",
    "cards.delete.title": "Slett kort",
    "cards.delete.confirm_msg": "Denne handlingen er ugjenkallelig. Kortet vil bli slettet permanent.",
    "cards.delete.physical_tooltip": "Fysisk kort bestilt — sletting umulig",
    "cards.order.confirmed": "Bestilling bekreftet!",
    "cards.order.confirmed_body": "Det fysiske kortet ditt forberedes. Bruk dette nummeret for å spore leveransen.",
    "cards.order.tracking_no": "Sporingsnr.",
    "cards.fee_deducted": "{amount} trekkes fra saldoen din",
    "cards.insufficient_short": "Utilstrekkelig saldo ({available} tilgjengelig)",
    "transfer.section.beneficiary": "Mottaker",
    "transfer.section.details": "Overføring",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bank",
    "transfer.field.city": "By",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bank:",
    "transfer.progress.paused": "Venter på validering",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Variabel",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Kort slettet",
    "cards.feat.gold_exclusive": "Eksklusivt gullkort",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Kjøperbeskyttelse",
    "cards.feat.support_standard": "Standard støtte",
    "cards.feat.plastic_premium": "Premium plastkort",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Standard reiseforsikring",
    "cards.feat.support_priority": "Prioritert støtte",
    "cards.feat.metal_brushed": "Børstet metallkort",
    "cards.feat.cashback_2_unlimited": "Ubegrenset 2% cashback",
    "cards.feat.travel_premium": "Premium reiseforsikring",
    "cards.feat.vip_lounge": "VIP-lounge-tilgang",
    "cards.feat.support_247": "24/7 assistanse",
    "auth.demo.title": "Administratorinnlogging",
    "auth.demo.subtitle": "Skriv inn admin-legitimasjon for å forhåndsvise demoen",
    "credits.garantie.none": "Ingen",
    "credits.garantie.mortgage": "Pant i finansiert eiendom",
    "credits.garantie.vehicle": "Finansiert kjøretøy",
    "credits.garantie.parental": "Foreldre- eller bankgaranti",
    "credits.garantie.professional": "Forretningsmidler eller personlig kausjon",
    "cards.reveal.pin_prompt": "Skriv inn din 4-sifrede PIN-kode for å vise kortinformasjonen.",
    "cards.reveal.show": "Vis",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "De to PIN-kodene stemmer ikke overens",
    "auth.pin.digit_hint": "4 sifre",
  
  },
  da: {
    "notif.transfer_code.title": "Bekræftelseskode til overførsel",
    "notif.transfer_code.body": "Kopiér denne kode for at bekræfte din overførsel.",
    "notif.transfer_code.expires_in": "Forsvinder om {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} måneder",
    "credits.n_months": "{n} måneder",
    "msg.read": "Læst",
    "msg.sent": "Sendt",
    "msg.attach_aria": "Vedhæft fil",
    "login.device.unknown": "Ukendt browser",
    "login.device.mobile": "Mobil",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Computer",
    "login.recent": "Senest",
    "err.invalid_credentials": "Ugyldige loginoplysninger",
    "err.bad_pin": "Forkert PIN",
    "err.account_blocked": "Konto blokeret",
    "err.bad_current_pw": "Nuværende adgangskode er forkert",
    "err.insufficient_funds": "Utilstrækkelige midler, indsæt venligst på kontoen.",
    "err.amount_above_savings": "Beløb overstiger tilgængelig opsparing",
    "err.must_be_positive": "Værdien skal være positiv",
    "err.invalid_format": "Ugyldigt format",
    "err.email_taken": "E-mail allerede i brug",
    "err.card_not_found": "Kort ikke fundet",
    "err.physical_card_already_ordered": "Fysisk kort allerede bestilt",
    "err.beneficiary_not_found": "Modtager ikke fundet",
    "err.invalid_code": "Ugyldig kode",
    "err.no_transfer_code": "Ingen kode til denne overførsel",
    "err.link_expired": "Link ugyldigt eller udløbet",
    "err.thread_not_found": "Samtale ikke fundet",
    "err.empty_message": "Tom besked",
    "err.file_too_large": "Fil for stor (maks 20 MB)",
    "err.forbidden": "Adgang nægtet",
    "err.bank_not_found": "Bank ikke fundet",
    "err.signups_closed": "Tilmeldinger er lukkede for denne bank.",
    "err.access_suspended": "Adgang midlertidigt suspenderet. Kontakt venligst din bank.",
    "err.notif_not_closable": "Denne notifikation kan ikke lukkes.",
    "auth.signup.tagline": "Tilmeld dig i dag",
    "auth.login.tagline": "Velkommen tilbage!",
    "auth.signup.tagline_sub": "Åbn din konto på 2 minutter og få adgang til alle vores premium banktjenester.",
    "auth.login.tagline_sub": "Få adgang til dit personlige område og administrer dine finanser sikkert.",
    "auth.back": "Tilbage til forsiden",
    "auth.bullet.security": "Militær banksikkerhed",
    "auth.bullet.transfers": "Øjeblikkelige overførsler døgnet rundt",
    "auth.bullet.investments": "Integrerede investeringer og opsparing",
    "auth.bullet.advisor": "Personlig rådgiver tilgængelig 7 dage/7",
    "auth.footer": "Drevet af {bank}",
    "auth.signup.title": "Opret konto",
    "auth.login.title": "Log ind",
    "auth.field.lastName": "Efternavn",
    "auth.field.firstName": "Fornavn",
    "auth.field.country": "Land",
    "auth.field.country.placeholder": "— Vælg dit land —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Adresse",
    "auth.field.address.placeholder": "Storgade 123, København",
    "auth.field.email": "E-mail",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Adgangskode",
    "auth.submit.signup": "Opret min konto",
    "auth.submit.login": "Log ind",
    "auth.login.badge": "Log ind på din konto",
    "auth.card.tagline": "Dit kundeområde",
    "auth.submit.loading": "Indlæser…",
    "auth.pin.title": "PIN-kode",
    "auth.pin.subtitle": "Indtast din 4-cifrede PIN-kode",
    "auth.pin.label": "PIN-kode",
    "auth.pin.confirm": "Bekræft PIN-kode",
    "auth.pin.submit": "Bekræft",
    "auth.pin.back": "Tilbage til login",
    "auth.pin.length_error": "PIN-koden skal være 4 cifre",
    "auth.switch.have_account": "Har du allerede en konto?",
    "auth.switch.login_link": "Log ind",
    "auth.switch.no_account": "Ikke kunde endnu?",
    "auth.error.select_country": "Vælg dit land",
    "kyc.title": "Identitetsbekræftelse (KYC)",
    "kyc.sub": "Upload dine dokumenter for at validere din konto. Alle dokumenter er krypteret og fortrolige.",
    "kyc.id_type": "Type identitetsdokument *",
    "kyc.id_card": "Nationalt ID-kort",
    "kyc.passport": "Pas",
    "kyc.driver_license": "Kørekort",
    "kyc.recto_passport": "Passets forside *",
    "kyc.recto_id": "ID-kortets forside *",
    "kyc.verso": "ID-kortets bagside *",
    "kyc.selfie": "Selfie med ID-dokumentet i hånden *",
    "kyc.address_proof": "Adressebevis *",
    "kyc.hint_doc": "Billede eller PDF, maks 5 MB",
    "kyc.hint_selfie": "Tydeligt billede, ansigt og dokument synlige",
    "kyc.hint_address": "Regning, kontoudtog, attest… (maks 5 MB)",
    "kyc.upload": "Upload",
    "kyc.replace": "Erstat",
    "pending.title": "Konto afventer",
    "pending.body": "Din anmodning er modtaget. En administrator vil gennemgå din sag og aktivere din konto om lidt.",
    "pending.back": "Tilbage til login",
    "home.nav.features": "Funktioner",
    "home.nav.security": "Sikkerhed",
    "home.nav.invest": "Investering",
    "home.nav.credits": "Kreditter",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Log ind",
    "home.hero.tagline": "Næste generations netbank",
    "home.hero.title1": "Administrer dine penge",
    "home.hero.title2": "med intelligens",
    "home.hero.sub": "En komplet bankplatform — overførsler, opsparing, investeringer og kreditter fra din smartphone.",
    "home.hero.login": "Log ind",
    "home.hero.trust.secure": "100% Sikkert",
    "home.hero.trust.fast": "Hurtig åbning",
    "home.hero.trust.encrypted": "Krypterede data",
    "home.hero.trust.support": "Service døgnet rundt",
    "home.mockup.balance": "Tilgængeligt saldo",
    "home.mockup.currency": "EUR · Hovedkonto",
    "home.mockup.recharge": "Påfyld",
    "home.mockup.send": "Send",
    "home.mockup.save": "Spar",
    "home.mockup.salary": "Løn marts",
    "home.mockup.rent": "Husleje april",
    "home.mockup.investment": "Investering",
    "home.mockup.days2": "for 2 dage siden",
    "home.mockup.days3": "for 3 dage siden",
    "home.mockup.days5": "for 5 dage siden",
    "home.stats.accounts": "Aktive konti",
    "home.stats.uptime": "Oppetid",
    "home.stats.transfers": "Overførsler",
    "home.stats.support": "Support",
    "home.features.label": "Funktioner",
    "home.features.title": "Alt hvad du har brug for",
    "home.features.sub": "En komplet platform designet til at forenkle håndteringen af dine finanser i hverdagen.",
    "home.feature.security": "Militær sikkerhed",
    "home.feature.security_body": "AES-256-kryptering, tofaktorautentificering og 24/7-kontoovervågning.",
    "home.feature.cards": "Virtuelt og fysisk kort",
    "home.feature.cards_body": "Udsted kort på få sekunder eller bestil et rigtigt fysisk kort leveret til dig.",
    "home.feature.transfers": "Expressoverførsler",
    "home.feature.transfers_body": "Send og modtag penge overalt i verden på få sekunder.",
    "home.feature.analytics": "Realtidsanalyse",
    "home.feature.analytics_body": "Smarte dashboards til at følge dine udgifter og forudsige dine finanser.",
    "home.feature.investments": "Integrerede investeringer",
    "home.feature.investments_body": "Få adgang til udvalgte investeringsprodukter direkte fra din konto.",
    "home.feature.support": "Dedikeret support døgnet rundt",
    "home.feature.support_body": "En personlig bankrådgiver tilgængelig til enhver tid via sikker beskedtjeneste.",
    "home.tpl.label": "Tillid",
    "home.tpl.title": "De stoler på os",
    "home.mockup.balance_label": "Saldo",
    "home.mockup.virement": "Overførsel",
    "home.mockup.salaire": "Løn",
    "home.security.label": "Sikkerhed",
    "home.security.title1": "Dine finanser",
    "home.security.title2": "i absolut sikkerhed",
    "home.security.sub": "Vi anvender de strengeste banksikkerhedsstandarder for at beskytte hver øre.",
    "home.security.aes.title": "AES-256-kryptering",
    "home.security.aes.body": "Alle følsomme data krypteres med de samme algoritmer, der bruges af de store internationale banker.",
    "home.security.auth.title": "Styrket autentificering",
    "home.security.auth.body": "Dobbelt identitetsbekræftelse ved hvert login og for enhver følsom handling på din konto.",
    "home.security.compliance.title": "PSD2/GDPR-overholdelse",
    "home.security.compliance.body": "Vi overholder alle europæiske regler om betalingstjenester og databeskyttelse.",
    "home.invest.label": "Investeringer",
    "home.invest.title": "Lad dine penge vokse",
    "home.invest.sub": "6 investeringskategorier udvalgt for deres potentiale og pålidelighed.",
    "home.invest.yield": "Afkast",
    "home.credits.label": "Kreditter",
    "home.credits.title": "Løsninger til ethvert projekt",
    "home.credits.sub": "Konkurrencedygtige renter, hurtige svar og personlig vejledning til alle dine projekter.",
    "home.credits.rate": "Rente",
    "home.faq.label": "FAQ",
    "home.faq.title": "Ofte stillede spørgsmål",
    "home.faq.q1": "Hvordan åbner jeg en konto?",
    "home.faq.a1": "Registreringen tager mindre end 2 minutter. Udfyld dine personlige oplysninger, vælg en adgangskode, og din konto aktiveres efter validering af vores team.",
    "home.faq.q2": "Er mine data sikre?",
    "home.faq.a2": "Ja. Alle data krypteres med AES-256 i hvile og TLS 1.3 under overførsel. Vi overholder internationale bankstandarder og GDPR/PSD2-regler.",
    "home.faq.q3": "Hvilke typer investeringer tilbyder I?",
    "home.faq.a3": "Vi tilbyder 6 kategorier: Ejendom, Crowdfunding, Vedvarende energi, Aktiemarkedet, Deltagende og Krypto. Hvert produkt er udvalgt for sit risiko/afkastforhold.",
    "home.faq.q4": "Hvordan ansøger jeg om kredit?",
    "home.faq.a4": "Gå til kreditafsnittet i dit kundeområde, vælg lånetype, angiv beløb og varighed. Din ansøgning behandles inden for 24 timer af vores rådgivere.",
    "home.faq.q5": "Er der skjulte gebyrer?",
    "home.faq.a5": "Ingen skjulte gebyrer. Alle priser vises tydeligt ved hver transaktion. Vilkårene er tilgængelige i dit kundeområde til enhver tid.",
    "home.faq.q6": "Hvordan kontakter jeg support?",
    "home.faq.a6": "Brug den integrerede beskedtjeneste i dit kundeområde til direkte at kontakte din personlige bankrådgiver, der er tilgængelig døgnet rundt.",
    "home.cta.title": "Klar til at starte?",
    "home.cta.sub": "Bliv en del af tusindvis af brugere, der administrerer deres finanser med {bank}. Registreringen tager mindre end 2 minutter.",
    "home.footer.products": "Produkter",
    "home.footer.services": "Tjenester",
    "home.footer.access": "Hurtig adgang",
    "home.footer.accounts": "Konti",
    "home.footer.transfers": "Overførsler",
    "home.footer.cards": "Kort",
    "home.footer.savings": "Opsparing",
    "home.footer.investments": "Investeringer",
    "home.footer.credits": "Kreditter",
    "home.footer.financing": "Finansiering",
    "home.footer.messages": "Beskeder",
    "home.footer.login": "Log ind",
    "home.footer.signup": "Åbn en konto",
    "home.footer.support": "Support døgnet rundt",
    "home.footer.tagline": "Din netbank, tilgængelig overalt og til enhver tid.",
    "home.footer.copyright": "© {year} {bank} — Alle rettigheder forbeholdes",
    "home.footer.powered": "Drevet af {bank}",
    "app.menu_label": "Hovedmenu",
    "app.nav.dashboard": "Dashboard",
    "app.nav.accounts": "Konti",
    "app.nav.transactions": "Transaktioner",
    "app.nav.transfers": "Overførsler",
    "app.nav.cards": "Kort",
    "app.nav.savings": "Opsparing",
    "app.nav.investments": "Investering",
    "app.nav.credit": "Kredit",
    "app.nav.financing": "Finansiering",
    "app.nav.messages": "Beskeder",
    "app.nav.card_tracking": "Kortsporing",
    "app.nav.notifications": "Notifikationer",
    "app.nav.settings": "Indstillinger",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Hjem",
    "app.nav.mobile.transfers": "Overførsler",
    "app.nav.mobile.cards": "Kort",
    "app.nav.mobile.transactions": "Transaktioner",
    "app.nav.more.invest": "Invester",
    "app.user_role": "Kunde",
    "app.logout": "Log ud",
    "app.search": "Søg…",
    "app.more_title": "Flere funktioner",
    "app.more_btn": "Mere",
    "app.loading": "Indlæser…",
    "app.error_back": "Tilbage til login",
    "app.see_more_notifs": "Se {count} flere notifikationer →",
    "page.accounts": "Mine konti",
    "page.transactions": "Transaktioner",
    "page.transfers": "Ny overførsel",
    "page.deposit": "Indskud",
    "page.cards": "Mine kort",
    "page.savings": "Opsparing",
    "page.investments": "Investering",
    "page.credits": "Kredit",
    "page.financing": "Projektfinansiering",
    "page.messages": "Beskeder",
    "page.settings": "Indstillinger",
    "page.notifications": "Vigtige notifikationer",
    "page.profile": "Min profil",
    "dash.greeting": "Hej, {name}",
    "dash.overview": "Oversigt over dine finanser — {date}",
    "dash.total_balance": "Samlet saldo",
    "dash.this_month": "+{amount} denne måned",
    "dash.view_iban": "Se mit IBAN",
    "dash.my_accounts": "Mine konti",
    "dash.primary_account": "Hovedkonto",
    "dash.savings_account": "Opsparingskonto",
    "dash.remunerated": "Rentebærende",
    "dash.details": "Detaljer",
    "dash.transfer_btn": "Overførsel",
    "dash.quick_actions": "Hurtige handlinger",
    "dash.quick.deposit": "Indskud",
    "deposit.title": "Indskudsinformation",
    "dash.quick.transfer": "Overførsel",
    "dash.quick.history": "Historik",
    "dash.quick.cards": "Mine kort",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Månedligt budget",
    "dash.income": "Indtægter",
    "dash.expenses": "Udgifter",
    "dash.spending_cat": "Udgifter pr. kategori",
    "dash.no_spending": "Ingen udgifter registreret.",
    "dash.recent_tx": "Seneste transaktioner",
    "dash.see_all": "Se alle",
    "dash.no_tx": "Ingen transaktioner.",
    "dash.main_card": "Mit hovedkort",
    "dash.manage": "Administrer",
    "dash.block": "Bloker",
    "dash.pin": "PIN-kode",
    "dash.security": "Sikkerhed",
    "dash.more": "Mere",
    "dash.no_card": "Ingen kort udstedt",
    "dash.monthly_activity": "Månedlig aktivitet",
    "dash.operations": "Transaktioner",
    "dash.holder": "Indehaver",
    "dash.expires": "Udl.",
    "dash.visa_label": "VISA",
    "dash.balance": "Tilgængeligt saldo",
    "tx.all": "Alle",
    "tx.income": "Indtægter",
    "tx.expenses": "Udgifter",
    "tx.today": "I dag",
    "tx.yesterday": "I går",
    "tx.none": "Ingen transaktioner.",
    "transfer.title": "Hurtig overførsel",
    "transfer.recipient": "Modtager",
    "transfer.recipient_ph": "Navn, IBAN, telefonnummer…",
    "transfer.amount": "Beløb",
    "transfer.label": "Etiket",
    "transfer.reason": "Årsag (valgfrit)",
    "transfer.reason_ph": "F.eks. husleje, tilbagebetaling…",
    "transfer.execution": "Udførelse",
    "transfer.immediate": "Øjeblikkelig",
    "transfer.delayed": "Forsinket",
    "transfer.balance": "Tilgængeligt saldo:",
    "transfer.continue": "Fortsæt",
    "transfer.confirm": "Bekræft",
    "transfer.cancel": "Annuller",
    "transfer.code": "Valideringskode",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Indtast den modtagne kode for at bekræfte overførslen",
    "transfer.pending.title": "Overførsel afventer validering",
    "transfer.pending.amount": "Beløb:",
    "transfer.pending.to": "Til:",
    "transfer.pending.hint": "Din valideringskode sendes via e-mail eller via notifikationsklokken. Koden består af 6 cifre. Hvis du ikke modtager den, bedes du kontakte support.",
    "transfer.pending.validate": "Bekræft overførslen",
    "transfer.saved": "Overførslen er gemt. Bed din administrator om valideringskoden.",
    "transfer.done": "Overførsel gennemført",
    "transfer.validated": "Overførsel valideret!",
    "transfer.progress.processing": "Behandler overførsel…",
    "transfer.progress.completed": "Overførsel gennemført!",
    "transfer.progress.default_message": "Din overførsel behandles. Vent venligst.",
    "cards.none": "Ingen kort udstedt.\nKontakt din administrator.",
    "cards.holder": "Indehaver",
    "cards.expires": "Udl.",
    "cards.block": "Lås",
    "cards.pin": "PIN-kode",
    "cards.more": "Mere",
    "cards.limits": "Kortgrænser",
    "cards.payment": "Betaling",
    "cards.withdrawal": "Hævning",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Spærret",
    "cards.status_active": "Aktiv",
    "cards.toast.blocked": "Kort spærret",
    "cards.toast.unblocked": "Kort låst op",
    "cards.toast.physical_ordered": "Fysisk kort bestilt! Levering inden for 5–10 arbejdsdage.",
    "cards.toast.created": "Kort oprettet!",
    "cards.none_sub": "Intet kort udstedt. Opret dit virtuelle kort nu.",
    "cards.generate_btn": "Opret mit kort",
    "cards.create.title": "Opret mit virtuelle kort",
    "cards.create.network": "Betalingsnetværk",
    "cards.create.color": "Kortfarve",
    "cards.create.preview_name": "DIT NAVN",
    "cards.create.preview_expiry": "MM/ÅÅ",
    "cards.create.loading": "Opretter…",
    "cards.create.submit": "Opret kort",
    "cards.btn.unblock": "Lås op",
    "cards.btn.block": "Spær",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fysisk",
    "cards.btn.physical_done": "Bestilt",
    "cards.limits_title": "Grænser",
    "cards.reveal.title": "Kortoplysninger",
    "cards.reveal.number": "Fuldt nummer",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Udløbsdato",
    "cards.reveal.warning": "Del aldrig disse oplysninger. Luk dette vindue efter brug.",
    "cards.pin.title": "PIN-kode",
    "cards.pin.message": "Denne PIN-kode bruges til alle dine onlinebetalinger og hævninger. Del den aldrig med nogen.",
    "cards.physical.title": "Bestil fysisk kort",
    "cards.physical.fee_label": "Leveringsgebyr",
    "cards.physical.balance_label": "Din saldo",
    "cards.physical.insufficient": "Utilstrækkelig saldo. Genopfyld venligst din konto, inden du bestiller.",
    "cards.physical.info": "Gebyret trækkes fra din konto. Levering inden for 5–10 arbejdsdage.",
    "cards.physical.order_btn": "Bestil ({fee})",
    "cards.physical.ordering": "Bestiller…",
    "cards.physical.order_cta": "Bestil et fysisk kort",
    "cards.physical.choose_type": "Vælg dit kort",
    "cards.physical.features": "Fordele",
    "cards.buy_virtual_cta": "Køb et virtuelt kort",
    "notif.none": "Ingen notifikationer i øjeblikket.",
    "notif.contact_support": "Kontakt support",
    "profile.change_photo": "Skift foto",
    "profile.remove_photo": "Fjern foto",
    "profile.email": "E-mail",
    "profile.phone": "Telefon",
    "profile.country": "Land",
    "profile.address": "Adresse",
    "profile.notifs_title": "Notifikationer ({count})",
    "profile.no_notifs": "Ingen notifikationer.",
    "profile.logout": "Log ud",
    "profile.photo_updated": "Profilbillede opdateret",
    "profile.photo_removed": "Foto fjernet",
    "profile.photo_too_large": "Billedet er for stort (maks 1 MB)",
    "profile.powered": "Drevet af {bank}",
    "iban.title": "Mit IBAN",
    "iban.loading": "Indlæser…",
    "iban.bic": "BIC / SWIFT-kode",
    "iban.share_info": "Del disse oplysninger for at modtage en overførsel til din konto.",
    "iban.copy": "Kopiér",
    "iban.copied": "Kopieret til udklipsholder",
    "iban.copy_fail": "Kan ikke kopiere",
    "iban.close": "Luk",
    "accounts.all": "Alle dine konti hos {bank}.",
    "accounts.primary": "Hovedkonto",
    "accounts.savings": "Opsparingskonto",
    "accounts.primary_tag": "Primær",
    "accounts.secondary_tag": "Sekundær",
    "accounts.objective": "Mål: {amount} · {pct}%",
    "accounts.copy": "Kopiér",
    "accounts.loading": "Indlæser…",
    "accounts.iban_copied": "IBAN kopieret",
    "accounts.copy_fail": "Kan ikke kopiere",
    "savings.total": "Samlet opsparet",
    "savings.target_total": "Samlet mål",
    "savings.progress": "Fremgang",
    "savings.active": "Aktive mål",
    "savings.new_goal": "Nyt mål",
    "savings.none": "Ingen opsparingsmål endnu.",
    "savings.none_sub": "Opret dit første mål for at komme i gang.",
    "savings.saved": "Opsparet:",
    "savings.goal": "Mål:",
    "savings.deadline": "Frist: {date}",
    "savings.deposit": "Indsæt",
    "savings.withdraw": "Hæv",
    "savings.loading": "Indlæser…",
    "savings.form.title": "Nyt opsparingsmål",
    "savings.form.cancel": "Annuller",
    "savings.form.create": "Opret",
    "savings.form.name": "Målnavn",
    "savings.form.name_ph": "Japanrejse, Bil, …",
    "savings.form.target": "Målbeløb",
    "savings.form.initial": "Første indskud",
    "savings.form.deadline": "Frist (valgfrit)",
    "savings.form.color": "Farve",
    "savings.deposit_title": "Indsæt på \"{name}\"",
    "savings.deposit_amount": "Beløb at indsætte",
    "savings.balance_hint": "Hovedsaldo: {amount}",
    "savings.withdraw_prompt": "Hvor meget vil du hæve fra \"{name}\"?",
    "savings.close_confirm": "Afslut målet \"{name}\"? Opsparingen returneres til din hovedkonto.",
    "savings.toast.created": "Mål oprettet",
    "savings.toast.deposited": "Indskud foretaget",
    "savings.toast.withdrawn": "Hævning foretaget",
    "savings.toast.closed": "Mål afsluttet",
    "invest.total": "Samlet investeret",
    "invest.profits": "Akkumuleret fortjeneste",
    "invest.count": "Investeringer",
    "invest.products": "Tilgængelige produkter",
    "invest.invest_btn": "Invester",
    "invest.my": "Mine investeringer",
    "invest.pending": "Afventer",
    "invest.validated": "Valideret",
    "invest.refused": "Afvist",
    "invest.withdraw.btn": "Hæv",
    "invest.withdraw.title": "Hæv min investering",
    "invest.withdraw.intro": "Du er ved at hæve din investering \"{product}\". Den oprindelige kapital og den påløbne fortjeneste vil blive krediteret din hovedsaldo.",
    "invest.withdraw.principal": "Kapital",
    "invest.withdraw.profit": "Fortjeneste",
    "invest.withdraw.total": "Samlet beløb",
    "invest.withdraw.confirm": "Bekræft hævning",
    "invest.withdraw.warning": "Denne handling kan ikke fortrydes. Investeringen vil blive lukket.",
    "invest.withdraw.success": "Investering hævet — {amount} krediteret saldoen.",
    "invest.withdrawn": "Hævet",
    "invest.cta_sub": "Vælg den type investering, der passer dig. En rådgiver hjælper dig fra din første anmodning.",
    "invest.form.amount": "Beløb at investere",
    "invest.form.balance": "Tilgængeligt saldo: {amount}",
    "invest.form.note": "Note til din rådgiver (valgfrit)",
    "invest.form.note_ph": "Angiv dine forventninger…",
    "invest.form.advisor_info": "En samtale åbnes automatisk med din rådgiver.",
    "invest.form.cancel": "Annuller",
    "invest.form.submit": "Send anmodning",
    "invest.toast.requested": "Anmodning registreret — en samtale er åbnet med din rådgiver.",
    "invest.p1.name": "Ejendom",
    "invest.p1.desc": "SCPI, ejendomspapir, ejendomscrowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Deltagende finansiering af udvalgte projekter",
    "invest.p3.name": "Vedvarende energi",
    "invest.p3.desc": "Sol-, vind- og grønne projekter",
    "invest.p4.name": "Aktiemarkedet",
    "invest.p4.desc": "Aktier, ETF og diversificerede fonde",
    "invest.p5.name": "Deltagende",
    "invest.p5.desc": "Lån til PME og lokale iværksættere",
    "invest.p6.name": "Krypto",
    "invest.p6.desc": "Bitcoin, Ethereum og pung til flere valutaer",
    "msg.online": "Online",
    "msg.loading": "Indlæser…",
    "msg.none": "Ingen beskeder. Start samtalen med din rådgiver.",
    "msg.placeholder": "Skriv din besked…",
    "msg.attach.image": "Billede",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Lyd",
    "msg.attach.file": "Fil / Dokument",
    "msg.pdf_doc": "PDF-dokument",
    "msg.file": "Fil",
    "msg.too_large": "Filen er for stor (maks {size}).",
    "msg.withdraw": "Slet",
    "credits.cta_sub": "Ansøg direkte online — svar inden for 24 timer.",
    "credits.learn_more": "Læs mere",
    "credits.request": "Ansøg",
    "credits.history": "Mine ansøgninger",
    "credits.status.pending": "Afventer",
    "credits.status.accepted": "Accepteret",
    "credits.status.refused": "Afvist",
    "credits.chars": "Egenskaber",
    "credits.amount_range": "Beløb: {min}€ – {max}€",
    "credits.rate_range": "Rente: {min}% – {max}% per år",
    "credits.duration_range": "Varighed: {min} til {max} måneder",
    "credits.deposit_min": "Første indskud: {amount}€ minimum",
    "credits.garantie": "Garanti: {garantie}",
    "credits.eligibility": "Kvalifikationskrav",
    "credits.elig.income": "Dokumentere regelmæssige og stabile indtægter",
    "credits.elig.deposit": "Foretage et første indskud på {amount}€",
    "credits.elig.garantie": "Fremvise garantien: {garantie}",
    "credits.form.title": "Ansøgning — {name}",
    "credits.form.cancel": "Annuller",
    "credits.form.submit": "Send ansøgning",
    "credits.form.amount": "Ønsket beløb (€)",
    "credits.form.duration": "Varighed (måneder)",
    "credits.form.reason": "Årsag (valgfrit)",
    "credits.form.reason_ph": "Beskriv dit projekt…",
    "credits.form.close": "Luk",
    "credits.toast.submitted": "Kreditansøgning sendt — du vil modtage svar inden for 24 timer.",
    "credits.p1.name": "Personligt lån",
    "credits.p1.desc": "Til at finansiere hverdagsbehov eller personlige projekter.",
    "credits.p2.name": "Boliglån",
    "credits.p2.desc": "Til at finansiere køb eller opførelse af ejendom.",
    "credits.p3.name": "Bil- / Motorcykellån",
    "credits.p3.desc": "Køb et køretøj med ro i sindet.",
    "credits.p4.name": "Studielån",
    "credits.p4.desc": "Økonomisk støtte til videregående uddannelse.",
    "credits.p5.name": "Erhvervslån",
    "credits.p5.desc": "Til at støtte væksten af en professionel aktivitet.",
    "credits.p6.name": "Ekspresslån",
    "credits.p6.desc": "En hurtig løsning til akutte behov.",
    "financing.reimbursable": "Tilbagebetalingspligtig finansiering",
    "financing.reimbursable_desc": "Økonomisk støtte med faste betingelser til at starte eller udvikle en aktivitet.",
    "financing.non_reimbursable": "Ikke-tilbagebetalingspligtig finansiering",
    "financing.non_reimbursable_desc": "Til projekter med socialt, uddannelsesmæssigt eller humanitært formål (ONG, foreninger).",
    "financing.cta_sub": "Skræddersyede løsninger til at realisere dine ambitioner.",
    "financing.request": "Ansøg om finansiering",
    "financing.history": "Mine ansøgninger",
    "financing.reimbursable_label": "Tilbagebetalingspligtig",
    "financing.non_reimbursable_label": "Ikke-tilbagebetalingspligtig",
    "financing.status.pending": "Afventer",
    "financing.status.granted": "Bevilget",
    "financing.status.refused": "Afvist",
    "financing.form.title": "Ansøgning — {name}",
    "financing.form.cancel": "Annuller",
    "financing.form.submit": "Send ansøgning",
    "financing.form.project": "Projektnavn",
    "financing.form.project_ph": "Mit projekt",
    "financing.form.amount": "Anmodet beløb (€)",
    "financing.form.sector": "Sektor",
    "financing.form.sector_ph": "Tech, landbrug, …",
    "financing.form.description": "Projektbeskrivelse",
    "financing.form.description_ph": "Præsenter dit projekt, dets mål og forventet effekt…",
    "settings.security": "Sikkerhed",
    "settings.current_pw": "Nuværende adgangskode",
    "settings.new_pw": "Ny adgangskode",
    "settings.confirm_pw": "Bekræft",
    "settings.submit": "Skift adgangskode",
    "settings.no_match": "Adgangskoderne stemmer ikke overens",
    "settings.changed": "Adgangskode ændret",
    "common.cancel": "Annuller",
    "common.confirm": "Bekræft",
    "common.close": "Luk",
    "common.save": "Gem",
    "common.loading": "Indlæser…",
    "common.send": "Send",
    "common.total": "I alt",
    "admin.wallet.balance": "Tillgängligt saldo",
    "admin.wallet.history": "Historik",
    "admin.wallet.no_history": "Inga transaktioner",
    "admin.wallet.title": "Min plånbok",
    "admin.wallet.topup": "Fyll på",
    "beneficiary.pick": "Välj",
    "beneficiary.save_this": "Spara denna mottagare",
    "beneficiary.saved": "Sparade mottagare",
    "beneficiary.saved_ok": "Mottagare sparad",
    "card.color.amber": "Bärnsten",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Smaragd",
    "card.color.forest": "Skog",
    "card.color.gold": "Guld",
    "card.color.lavender": "Lavendel",
    "card.color.night": "Natt",
    "card.color.orange": "Orange",
    "card.color.pink": "Rosa",
    "card.color.royal": "Kunglig blå",
    "card.color.ruby": "Rubin",
    "card.color.slate": "Skiffer",
    "card.color.steel": "Stål",
    "card.color.terracotta": "Terrakotta",
    "card.color.violet": "Violett",
    "common.other": "Övrigt",
    "common.search": "Sök",
    "content.translated": "Automatiskt översatt innehåll",
    "credits.monthly_payment": "Beräknad månadsbetalning",
    "credits.rate_annual": "/ år",
    "credits.rate_used": "Beräknad ränta:",
    "credits.simulation": "Simulering av månadsbetalning",
    "credits.total_cost": "Beräknad totalkostnad",
    "demo.banner": "Demoläge — inga riktiga åtgärder",
    "demo.disabled": "Åtgärd ej tillgänglig i demoläge",
    "deposit.default_instructions": "Kontakta din handläggare för insättningsdetaljer.",
    "error.back_home": "Tillbaka till startsidan",
    "error.bank_not_found": "Banken hittades inte",
    "error.unknown": "Okänt fel",
    "kyc.file_read_error": "Filen kan inte läsas",
    "kyc.file_too_large": "Filen är för stor (max 5 MB)",
    "login.history": "Inloggningshistorik",
    "login.history.empty": "Inga inloggningar registrerade.",
    "notif.dismiss": "Stäng",
    "notif.locked": "Bankmeddelande",
    "tracking.error.not_found": "Spårningsnummer hittades inte. Kontrollera och försök igen.",
    "tracking.label": "Spårning",
    "tracking.status.delivered": "Levererad",
    "tracking.status.preparation": "Under förberedelse",
    "tracking.status.shipped": "Skickad",
    "tracking.status.transit": "Under transport",
    "transfer.code.copied": "Kopierad",
    "transfer.code.copy": "Kopiera",
    "transfer.code.received": "Din valideringskod",
    "dash.welcome": "Velkommen",
    "common.delete": "Slet",
    "cards.delete.title": "Slet kort",
    "cards.delete.confirm_msg": "Denne handling er uigenkaldelig. Kortet slettes permanent.",
    "cards.delete.physical_tooltip": "Fysisk kort bestilt — sletning umulig",
    "cards.order.confirmed": "Bestilling bekræftet!",
    "cards.order.confirmed_body": "Dit fysiske kort er ved at blive forberedt. Brug dette nummer til at spore din levering.",
    "cards.order.tracking_no": "Sporingsnr.",
    "cards.fee_deducted": "{amount} trækkes fra din saldo",
    "cards.insufficient_short": "Utilstrækkelig saldo ({available} tilgængelig)",
    "transfer.section.beneficiary": "Modtager",
    "transfer.section.details": "Overførsel",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bank",
    "transfer.field.city": "By",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bank:",
    "transfer.progress.paused": "Afventer validering",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Variabel",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Kort slettet",
    "cards.feat.gold_exclusive": "Eksklusivt guldkort",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Køberbeskyttelse",
    "cards.feat.support_standard": "Standard support",
    "cards.feat.plastic_premium": "Premium plastkort",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Standard rejseforsikring",
    "cards.feat.support_priority": "Prioritetssupport",
    "cards.feat.metal_brushed": "Børstet metalkort",
    "cards.feat.cashback_2_unlimited": "Ubegrænset 2% cashback",
    "cards.feat.travel_premium": "Premium rejseforsikring",
    "cards.feat.vip_lounge": "VIP-loungeadgang",
    "cards.feat.support_247": "24/7 assistance",
    "auth.demo.title": "Administratorlogin",
    "auth.demo.subtitle": "Indtast dine admin-legitimationsoplysninger for at se demoen",
    "credits.garantie.none": "Ingen",
    "credits.garantie.mortgage": "Pant i den finansierede ejendom",
    "credits.garantie.vehicle": "Finansieret køretøj",
    "credits.garantie.parental": "Forældre- eller banksikkerhed",
    "credits.garantie.professional": "Erhvervsaktiver eller personlig kaution",
    "cards.reveal.pin_prompt": "Indtast din 4-cifrede PIN-kode for at vise kortoplysningerne.",
    "cards.reveal.show": "Vis",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "De to PIN-koder stemmer ikke overens",
    "auth.pin.digit_hint": "4 cifre",
  
  },
  is: {
    "notif.transfer_code.title": "Staðfestingarkóði fyrir millifærslu",
    "notif.transfer_code.body": "Afritaðu þennan kóða til að staðfesta millifærsluna.",
    "notif.transfer_code.expires_in": "Hverfur eftir {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mánuðir",
    "credits.n_months": "{n} mánuðir",
    "msg.read": "Lesið",
    "msg.sent": "Sent",
    "msg.attach_aria": "Tengja skrá",
    "login.device.unknown": "Óþekkt vafri",
    "login.device.mobile": "Farsími",
    "login.device.tablet": "Spjaldtölva",
    "login.device.desktop": "Tölva",
    "login.recent": "Nýlegt",
    "err.invalid_credentials": "Ógild innskráningarauðkenni",
    "err.bad_pin": "Rangt PIN",
    "err.account_blocked": "Reikningur lokaður",
    "err.bad_current_pw": "Núverandi lykilorð er rangt",
    "err.insufficient_funds": "Ófullnægjandi fjármunir, vinsamlegast endurhlaðið reikninginn.",
    "err.amount_above_savings": "Upphæð fer yfir tiltækan sparnað",
    "err.must_be_positive": "Gildi verður að vera jákvætt",
    "err.invalid_format": "Ógilt snið",
    "err.email_taken": "Netfang þegar í notkun",
    "err.card_not_found": "Kort fannst ekki",
    "err.physical_card_already_ordered": "Líkamlegt kort þegar pantað",
    "err.beneficiary_not_found": "Móttakandi fannst ekki",
    "err.invalid_code": "Ógildur kóði",
    "err.no_transfer_code": "Enginn kóði fyrir þessa millifærslu",
    "err.link_expired": "Tengill ógildur eða útrunninn",
    "err.thread_not_found": "Samtal fannst ekki",
    "err.empty_message": "Tóm skilaboð",
    "err.file_too_large": "Skrá of stór (hám. 20 MB)",
    "err.forbidden": "Aðgangur bannaður",
    "err.bank_not_found": "Banki fannst ekki",
    "err.signups_closed": "Skráningar eru lokaðar fyrir þennan banka.",
    "err.access_suspended": "Aðgangur tímabundið stöðvaður. Hafðu samband við bankann þinn.",
    "err.notif_not_closable": "Ekki er hægt að loka þessari tilkynningu.",
    "auth.signup.tagline": "Gakktu í lið með okkur í dag",
    "auth.login.tagline": "Velkomin/n aftur!",
    "auth.signup.tagline_sub": "Opnaðu reikning þinn á 2 mínútum og fáðu aðgang að öllum okkar úrvals bankaþjónustum.",
    "auth.login.tagline_sub": "Fáðu aðgang að þínum persónulega rými og stjórnaðu fjármálum þínum á öruggan hátt.",
    "auth.back": "Til baka á forsíðu",
    "auth.bullet.security": "Hernaðarstig bankaöryggi",
    "auth.bullet.transfers": "Augnablikstilfærslur allan sólarhringinn",
    "auth.bullet.investments": "Samþætt fjárfestingar og sparnað",
    "auth.bullet.advisor": "Persónulegur ráðgjafi tiltækur alla daga",
    "auth.footer": "Knúið af {bank}",
    "auth.signup.title": "Búa til reikning",
    "auth.login.title": "Innskráning",
    "auth.field.lastName": "Eftirnafn",
    "auth.field.firstName": "Fornafn",
    "auth.field.country": "Land",
    "auth.field.country.placeholder": "— Veldu land —",
    "auth.field.phone": "Símanúmer",
    "auth.field.address": "Heimilisfang",
    "auth.field.address.placeholder": "Laugavegur 123, Reykjavík",
    "auth.field.email": "Netfang",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Lykilorð",
    "auth.submit.signup": "Búa til reikning minn",
    "auth.submit.login": "Skrá inn",
    "auth.login.badge": "Skráðu þig inn á reikninginn þinn",
    "auth.card.tagline": "Þitt viðskiptasvæði",
    "auth.submit.loading": "Hleður…",
    "auth.pin.title": "PIN-númer",
    "auth.pin.subtitle": "Sláðu inn 4 stafa PIN-númerið þitt",
    "auth.pin.label": "PIN-númer",
    "auth.pin.confirm": "Staðfesta PIN-númer",
    "auth.pin.submit": "Staðfesta",
    "auth.pin.back": "Til baka í innskráningu",
    "auth.pin.length_error": "PIN-númerið verður að vera 4 tölustafir",
    "auth.switch.have_account": "Ertu þegar með reikning?",
    "auth.switch.login_link": "Innskráning",
    "auth.switch.no_account": "Ekki enn viðskiptavinur?",
    "auth.error.select_country": "Veldu land þitt",
    "kyc.title": "Auðkenningarstaðfesting (KYC)",
    "kyc.sub": "Hlaðið upp skjölum til að staðfesta reikninginn þinn. Öll skjöl eru dulkóðuð og trúnaðarmál.",
    "kyc.id_type": "Tegund skilríkja *",
    "kyc.id_card": "Nafnskírteini",
    "kyc.passport": "Vegabréf",
    "kyc.driver_license": "Ökuskírteini",
    "kyc.recto_passport": "Framhlið vegabréfs *",
    "kyc.recto_id": "Framhlið skilríkja *",
    "kyc.verso": "Bakhlið skilríkja *",
    "kyc.selfie": "Selfie með skilríki í hendi *",
    "kyc.address_proof": "Staðfesting á búsetu *",
    "kyc.hint_doc": "Mynd eða PDF, hámark 5 MB",
    "kyc.hint_selfie": "Skýr mynd, andlit og skjal sjáanlegt",
    "kyc.hint_address": "Reikningur, yfirlit, vottorð… (hámark 5 MB)",
    "kyc.upload": "Hlaða upp",
    "kyc.replace": "Skipta út",
    "pending.title": "Reikningur í bið",
    "pending.body": "Beiðni þín hefur borist. Kerfisstjóri mun fara yfir málið og virkja reikninginn þinn fljótlega.",
    "pending.back": "Til baka í innskráningu",
    "home.nav.features": "Eiginleikar",
    "home.nav.security": "Öryggi",
    "home.nav.invest": "Fjárfesting",
    "home.nav.credits": "Lán",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Skrá inn",
    "home.hero.tagline": "Næstu kynslóðar netbanki",
    "home.hero.title1": "Stjórnaðu peningum þínum",
    "home.hero.title2": "með greind",
    "home.hero.sub": "Heildstæð bankagátt — millifærslur, sparnaður, fjárfestingar og lán í gegnum snjallsímann þinn.",
    "home.hero.login": "Skrá inn",
    "home.hero.trust.secure": "100% Öruggt",
    "home.hero.trust.fast": "Fljótleg opnun",
    "home.hero.trust.encrypted": "Dulkóðuð gögn",
    "home.hero.trust.support": "Þjónusta 24/7",
    "home.mockup.balance": "Tiltækt saldo",
    "home.mockup.currency": "EUR · Aðalreikningur",
    "home.mockup.recharge": "Hlaða upp",
    "home.mockup.send": "Senda",
    "home.mockup.save": "Spara",
    "home.mockup.salary": "Laun í mars",
    "home.mockup.rent": "Leiga í apríl",
    "home.mockup.investment": "Fjárfesting",
    "home.mockup.days2": "fyrir 2 dögum",
    "home.mockup.days3": "fyrir 3 dögum",
    "home.mockup.days5": "fyrir 5 dögum",
    "home.stats.accounts": "Virkir reikningar",
    "home.stats.uptime": "Tiltækni",
    "home.stats.transfers": "Millifærslur",
    "home.stats.support": "Þjónusta",
    "home.features.label": "Eiginleikar",
    "home.features.title": "Allt sem þú þarft",
    "home.features.sub": "Heildstæð vettvangur sem er hannaður til að einfalda fjármálastjórnun þína í daglegu lífi.",
    "home.feature.security": "Hernaðaröryggi",
    "home.feature.security_body": "AES-256 dulkóðun, tvíþætt auðkenning og 24/7 eftirlit með reikningnum þínum.",
    "home.feature.cards": "Sýndar- og líkamleg kort",
    "home.feature.cards_body": "Gefðu út kort á nokkrum sekúndum eða pantaðu alvöru líkamlegt kort sent heim til þín.",
    "home.feature.transfers": "Hraðamillifærslur",
    "home.feature.transfers_body": "Sendu og taktu við peningum hvar sem er í heiminum á nokkrum sekúndum.",
    "home.feature.analytics": "Greining í rauntíma",
    "home.feature.analytics_body": "Snjöll mælaborð til að fylgjast með gjaldeyrisnotkun þinni og sjá fyrir um fjármál þín.",
    "home.feature.investments": "Samþættar fjárfestingar",
    "home.feature.investments_body": "Fáðu aðgang að völdum fjárfestingarafurðum beint frá reikningnum þínum.",
    "home.feature.support": "Sérstök þjónusta 24/7",
    "home.feature.support_body": "Persónulegur bankafulltrúi tiltækur hvenær sem er í gegnum örugg skilaboð.",
    "home.tpl.label": "Traust",
    "home.tpl.title": "Þeir treysta okkur",
    "home.mockup.balance_label": "Saldo",
    "home.mockup.virement": "Millifærsla",
    "home.mockup.salaire": "Laun",
    "home.security.label": "Öryggi",
    "home.security.title1": "Fjármál þín",
    "home.security.title2": "í fullkomnu öryggi",
    "home.security.sub": "Við beitum strangestu bankaöryggisstöðlum til að vernda hvert einsasta eyrir.",
    "home.security.aes.title": "AES-256 dulkóðun",
    "home.security.aes.body": "Öll viðkvæm gögn eru dulkóðuð með sömu reikniritum og stóru alþjóðlegu bankarnir nota.",
    "home.security.auth.title": "Aukið auðkenningarferli",
    "home.security.auth.body": "Tvöföld auðkenning við hverja innskráningu og við hverja viðkvæma aðgerð á reikningnum þínum.",
    "home.security.compliance.title": "PSD2/GDPR samræmi",
    "home.security.compliance.body": "Við fylgjum öllum evrópskum reglugerðum um greiðsluþjónustu og persónuvernd.",
    "home.invest.label": "Fjárfestingar",
    "home.invest.title": "Láttu peninga þína vaxa",
    "home.invest.sub": "6 fjárfestingarflokkar valdir fyrir möguleika þeirra og áreiðanleika.",
    "home.invest.yield": "Ávöxtun",
    "home.credits.label": "Lán",
    "home.credits.title": "Lausnir fyrir hvert verkefni",
    "home.credits.sub": "Samkeppnishæfar vextir, skjótar svörun og persónulegt leiðsögn fyrir öll þín verkefni.",
    "home.credits.rate": "Vextir",
    "home.faq.label": "FAQ",
    "home.faq.title": "Algengar spurningar",
    "home.faq.q1": "Hvernig opna ég reikning?",
    "home.faq.a1": "Skráningin tekur minna en 2 mínútur. Fylltu einfaldlega út persónulegar upplýsingar þínar, veldu lykilorð og reikningurinn verður virkjaður eftir staðfestingu af hálfu teymis okkar.",
    "home.faq.q2": "Eru gögn mín örugg?",
    "home.faq.a2": "Já. Öll gögn eru dulkóðuð með AES-256 í hvíld og TLS 1.3 í flutningi. Við uppfyllum alþjóðlegar bankareglur og GDPR/PSD2 reglugerðir.",
    "home.faq.q3": "Hvaða tegundir fjárfestinga bjóðið þið upp á?",
    "home.faq.a3": "Við bjóðum upp á 6 flokka: Fasteignir, Fjöldafjármögnun, Endurnýjanleg orka, Hlutabréfamarkaður, Þátttaka og Dulritunarmynt. Hvert afurð er valið fyrir áhættu/ávöxtun hlutfall þess.",
    "home.faq.q4": "Hvernig sæki ég um lán?",
    "home.faq.a4": "Farðu í lánahlutann á notendagestsvæðinu þínu, veldu tegund láns, sláðu inn upphæð og lengd. Umsóknin þín er afgreidd innan 24 klst af ráðgjöfum okkar.",
    "home.faq.q5": "Eru falnar gjöld?",
    "home.faq.a5": "Engin falin gjöld. Allar gjaldskrár eru sýnilega birtar við hverja aðgerð. Skilmálar eru aðgengilegir á notendagestsvæðinu þínu hvenær sem er.",
    "home.faq.q6": "Hvernig hef ég samband við þjónustudeildina?",
    "home.faq.a6": "Notaðu samþættu skilaboðaþjónustuna á notendagestsvæðinu þínu til að hafa beint samband við persónulegan bankafulltrúa þinn, sem er tiltækur allan sólarhringinn 7 daga vikunnar.",
    "home.cta.title": "Tilbúinn/n að byrja?",
    "home.cta.sub": "Gakktu í hóp þúsunda notenda sem stjórna fjármálum sínum með {bank}. Skráningin tekur minna en 2 mínútur.",
    "home.footer.products": "Afurðir",
    "home.footer.services": "Þjónusta",
    "home.footer.access": "Skjótur aðgangur",
    "home.footer.accounts": "Reikningar",
    "home.footer.transfers": "Millifærslur",
    "home.footer.cards": "Kort",
    "home.footer.savings": "Sparnaður",
    "home.footer.investments": "Fjárfestingar",
    "home.footer.credits": "Lán",
    "home.footer.financing": "Fjármögnun",
    "home.footer.messages": "Skilaboð",
    "home.footer.login": "Innskráning",
    "home.footer.signup": "Opna reikning",
    "home.footer.support": "Þjónusta 24/7",
    "home.footer.tagline": "Netbanki þinn, tiltækur alls staðar og hvenær sem er.",
    "home.footer.copyright": "© {year} {bank} — Öll réttindi áskilin",
    "home.footer.powered": "Knúið af {bank}",
    "app.menu_label": "Aðalvalmynd",
    "app.nav.dashboard": "Mælaborð",
    "app.nav.accounts": "Reikningar",
    "app.nav.transactions": "Færslur",
    "app.nav.transfers": "Millifærslur",
    "app.nav.cards": "Kort",
    "app.nav.savings": "Sparnaður",
    "app.nav.investments": "Fjárfesting",
    "app.nav.credit": "Lán",
    "app.nav.financing": "Fjármögnun",
    "app.nav.messages": "Skilaboð",
    "app.nav.card_tracking": "Kortarakning",
    "app.nav.notifications": "Tilkynningar",
    "app.nav.settings": "Stillingar",
    "app.nav.profile": "Notandasíða",
    "app.nav.mobile.home": "Heim",
    "app.nav.mobile.transfers": "Millifærslur",
    "app.nav.mobile.cards": "Kort",
    "app.nav.mobile.transactions": "Færslur",
    "app.nav.more.invest": "Fjárfesta",
    "app.user_role": "Viðskiptavinur",
    "app.logout": "Útskrá",
    "app.search": "Leita…",
    "app.more_title": "Fleiri eiginleikar",
    "app.more_btn": "Meira",
    "app.loading": "Hleður…",
    "app.error_back": "Til baka í innskráningu",
    "app.see_more_notifs": "Sjá {count} fleiri tilkynningar →",
    "page.accounts": "Reikningar mínir",
    "page.transactions": "Færslur",
    "page.transfers": "Ný millifærsla",
    "page.deposit": "Innborgun",
    "page.cards": "Kort mín",
    "page.savings": "Sparnaður",
    "page.investments": "Fjárfesting",
    "page.credits": "Lán",
    "page.financing": "Verkefnafjármögnun",
    "page.messages": "Skilaboð",
    "page.settings": "Stillingar",
    "page.notifications": "Mikilvægar tilkynningar",
    "page.profile": "Notendasíða mín",
    "dash.greeting": "Góðan dag, {name}",
    "dash.overview": "Yfirlit yfir fjármál þín — {date}",
    "dash.total_balance": "Heildarstaða",
    "dash.this_month": "+{amount} í þessum mánuði",
    "dash.view_iban": "Sjá IBAN minn",
    "dash.my_accounts": "Reikningar mínir",
    "dash.primary_account": "Aðalreikningur",
    "dash.savings_account": "Sparnaðarreikningur",
    "dash.remunerated": "Vaxaberandi",
    "dash.details": "Upplýsingar",
    "dash.transfer_btn": "Millifærsla",
    "dash.quick_actions": "Skjótar aðgerðir",
    "dash.quick.deposit": "Innborgun",
    "deposit.title": "Upplýsingar um innborgun",
    "dash.quick.transfer": "Millifærsla",
    "dash.quick.history": "Saga",
    "dash.quick.cards": "Kort mín",
    "dash.quick.profile": "Notandasíða",
    "dash.monthly_budget": "Mánaðarlegt fjárhagsáætlun",
    "dash.income": "Tekjur",
    "dash.expenses": "Gjöld",
    "dash.spending_cat": "Útgjöld eftir flokki",
    "dash.no_spending": "Engin útgjöld skráð.",
    "dash.recent_tx": "Nýlegar færslur",
    "dash.see_all": "Sjá allt",
    "dash.no_tx": "Engar aðgerðir.",
    "dash.main_card": "Aðalkort mitt",
    "dash.manage": "Stjórna",
    "dash.block": "Loka",
    "dash.pin": "PIN-númer",
    "dash.security": "Öryggi",
    "dash.more": "Meira",
    "dash.no_card": "Ekkert kort útgefið",
    "dash.monthly_activity": "Mánaðarleg virkni",
    "dash.operations": "Aðgerðir",
    "dash.holder": "Handhafi",
    "dash.expires": "Rennur út",
    "dash.visa_label": "VISA",
    "dash.balance": "Tiltækt saldo",
    "tx.all": "Allar",
    "tx.income": "Tekjur",
    "tx.expenses": "Gjöld",
    "tx.today": "Í dag",
    "tx.yesterday": "Í gær",
    "tx.none": "Engar færslur.",
    "transfer.title": "Hraðamillifærsla",
    "transfer.recipient": "Viðtakandi",
    "transfer.recipient_ph": "Nafn, IBAN, símanúmer…",
    "transfer.amount": "Upphæð",
    "transfer.label": "Merking",
    "transfer.reason": "Ástæða (valfrjálst)",
    "transfer.reason_ph": "Dæmi: leiga, endurgreiðsla…",
    "transfer.execution": "Framkvæmd",
    "transfer.immediate": "Samstundis",
    "transfer.delayed": "Seinkað",
    "transfer.balance": "Tiltækt saldo:",
    "transfer.continue": "Halda áfram",
    "transfer.confirm": "Staðfesta",
    "transfer.cancel": "Hætta við",
    "transfer.code": "Staðfestingarkóði",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Sláðu inn kóðann sem barst til að staðfesta millifærsluna",
    "transfer.pending.title": "Millifærsla bíður staðfestingar",
    "transfer.pending.amount": "Upphæð:",
    "transfer.pending.to": "Til:",
    "transfer.pending.hint": "Staðfestingarkóðinn þinn verður sendur með tölvupósti eða í gegnum tilkynningabjölluna. Kóðinn er 6 stafir. Ef þú færð hann ekki, vinsamlegast hafðu samband við þjónustudeildina.",
    "transfer.pending.validate": "Staðfesta millifærslu",
    "transfer.saved": "Millifærsla vistuð. Biðjaðu kerfisstjóra um staðfestingarkóðann.",
    "transfer.done": "Millifærsla lokið",
    "transfer.validated": "Millifærsla staðfest!",
    "transfer.progress.processing": "Vinn úr millifærslu…",
    "transfer.progress.completed": "Millifærsla framkvæmd!",
    "transfer.progress.default_message": "Verið er að vinna úr millifærslunni þinni. Vinsamlegast bíðið.",
    "cards.none": "Ekkert kort útgefið.\nHafðu samband við kerfisstjóra þinn.",
    "cards.holder": "Handhafi",
    "cards.expires": "Rennur út",
    "cards.block": "Læsa",
    "cards.pin": "PIN-númer",
    "cards.more": "Meira",
    "cards.limits": "Kortahámark",
    "cards.payment": "Greiðsla",
    "cards.withdrawal": "Úttekt",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Lokað",
    "cards.status_active": "Virkt",
    "cards.toast.blocked": "Kort lokað",
    "cards.toast.unblocked": "Kort opnað",
    "cards.toast.physical_ordered": "Líkamlegt kort pantað! Afhending á 5–10 virkum dögum.",
    "cards.toast.created": "Kort búið til!",
    "cards.none_sub": "Ekkert kort gefið út. Búðu til sýndarkortið þitt núna.",
    "cards.generate_btn": "Búa til mitt kort",
    "cards.create.title": "Búa til mitt sýndarkort",
    "cards.create.network": "Greiðslunet",
    "cards.create.color": "Kortalitur",
    "cards.create.preview_name": "ÞITT NAFN",
    "cards.create.preview_expiry": "MM/ÁÁ",
    "cards.create.loading": "Býr til…",
    "cards.create.submit": "Búa til kort",
    "cards.btn.unblock": "Opna",
    "cards.btn.block": "Loka",
    "cards.btn.info": "Upplýsingar",
    "cards.btn.physical": "Líkamlegt",
    "cards.btn.physical_done": "Pantað",
    "cards.limits_title": "Hámark",
    "cards.reveal.title": "Kortaupplýsingar",
    "cards.reveal.number": "Fullt númer",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Gildistími",
    "cards.reveal.warning": "Deildu aldrei þessum upplýsingum. Lokaðu þessum glugga eftir notkun.",
    "cards.pin.title": "PIN-númer",
    "cards.pin.message": "Þetta PIN-númer er notað fyrir allar netgreiðslur þínar og úttektir. Deildu því aldrei með neinum.",
    "cards.physical.title": "Panta líkamlegt kort",
    "cards.physical.fee_label": "Sendingarkostnaður",
    "cards.physical.balance_label": "Staða þín",
    "cards.physical.insufficient": "Ófullnægjandi staða. Vinsamlegast fylltu upp á reikninginn þinn áður en þú pantar.",
    "cards.physical.info": "Gjaldið verður dregið af reikningi þínum. Afhending á 5–10 virkum dögum.",
    "cards.physical.order_btn": "Panta ({fee})",
    "cards.physical.ordering": "Pantar…",
    "cards.physical.order_cta": "Panta líkamlegt kort",
    "cards.physical.choose_type": "Veldu kortið þitt",
    "cards.physical.features": "Kostir",
    "cards.buy_virtual_cta": "Kaupa sýndarkort",
    "notif.none": "Engar tilkynningar að svo stöddu.",
    "notif.contact_support": "Hafa samband við þjónustuver",
    "profile.change_photo": "Skipta um mynd",
    "profile.remove_photo": "Fjarlægja mynd",
    "profile.email": "Netfang",
    "profile.phone": "Símanúmer",
    "profile.country": "Land",
    "profile.address": "Heimilisfang",
    "profile.notifs_title": "Tilkynningar ({count})",
    "profile.no_notifs": "Engar tilkynningar.",
    "profile.logout": "Útskrá",
    "profile.photo_updated": "Notendamynd uppfærð",
    "profile.photo_removed": "Mynd fjarlægð",
    "profile.photo_too_large": "Mynd of stór (hámark 1 MB)",
    "profile.powered": "Knúið af {bank}",
    "iban.title": "IBAN minn",
    "iban.loading": "Hleður…",
    "iban.bic": "BIC / SWIFT kóði",
    "iban.share_info": "Deildu þessum upplýsingum til að taka á móti millifærslu á reikninginn þinn.",
    "iban.copy": "Afrita",
    "iban.copied": "Afritað á klippiborð",
    "iban.copy_fail": "Ekki tókst að afrita",
    "iban.close": "Loka",
    "accounts.all": "Allir reikningar þínir hjá {bank}.",
    "accounts.primary": "Aðalreikningur",
    "accounts.savings": "Sparnaðarreikningur",
    "accounts.primary_tag": "Aðal",
    "accounts.secondary_tag": "Aukalegur",
    "accounts.objective": "Markmið: {amount} · {pct}%",
    "accounts.copy": "Afrita",
    "accounts.loading": "Hleður…",
    "accounts.iban_copied": "IBAN afritað",
    "accounts.copy_fail": "Ekki tókst að afrita",
    "savings.total": "Heildarsparnaður",
    "savings.target_total": "Heildarmarkmið",
    "savings.progress": "Framvinda",
    "savings.active": "Virk markmið",
    "savings.new_goal": "Nýtt markmið",
    "savings.none": "Engin sparnaðarmarkmið enn sem komið er.",
    "savings.none_sub": "Búðu til fyrsta markmiðið þitt til að byrja.",
    "savings.saved": "Sparað:",
    "savings.goal": "Markmið:",
    "savings.deadline": "Eindagi: {date}",
    "savings.deposit": "Leggja inn",
    "savings.withdraw": "Taka út",
    "savings.loading": "Hleður…",
    "savings.form.title": "Nýtt sparnaðarmarkmið",
    "savings.form.cancel": "Hætta við",
    "savings.form.create": "Búa til",
    "savings.form.name": "Nafn markmiðs",
    "savings.form.name_ph": "Japan ferð, Bíll, …",
    "savings.form.target": "Markupphæð",
    "savings.form.initial": "Upphafsinnborgun",
    "savings.form.deadline": "Eindagi (valfrjálst)",
    "savings.form.color": "Litur",
    "savings.deposit_title": "Leggja inn á \"{name}\"",
    "savings.deposit_amount": "Upphæð til að leggja inn",
    "savings.balance_hint": "Aðalstaða: {amount}",
    "savings.withdraw_prompt": "Hversu mikið að taka út frá \"{name}\"?",
    "savings.close_confirm": "Loka markmiðinu \"{name}\"? Sparnaðurinn verður endurgreiddur á aðalreikning þinn.",
    "savings.toast.created": "Markmið búið til",
    "savings.toast.deposited": "Innborgun innt af hendi",
    "savings.toast.withdrawn": "Úttekt innt af hendi",
    "savings.toast.closed": "Markmiði lokað",
    "invest.total": "Heildarfjárfesting",
    "invest.profits": "Samanlagður hagnaður",
    "invest.count": "Fjárfestingar",
    "invest.products": "Tiltækar afurðir",
    "invest.invest_btn": "Fjárfesta",
    "invest.my": "Fjárfestingar mínar",
    "invest.pending": "Í bið",
    "invest.validated": "Staðfest",
    "invest.refused": "Hafnað",
    "invest.withdraw.btn": "Taka út",
    "invest.withdraw.title": "Taka út fjárfestingu mína",
    "invest.withdraw.intro": "Þú ert að fara að taka út fjárfestingu þína \"{product}\". Höfuðstóllinn og áfallinn hagnaður verða færðir á aðalstöðu þína.",
    "invest.withdraw.principal": "Höfuðstóll",
    "invest.withdraw.profit": "Hagnaður",
    "invest.withdraw.total": "Heildarupphæð",
    "invest.withdraw.confirm": "Staðfesta úttekt",
    "invest.withdraw.warning": "Þessi aðgerð er óafturkræf.",
    "invest.withdraw.success": "Fjárfesting tekin út — {amount} færð á stöðuna.",
    "invest.withdrawn": "Tekin út",
    "invest.cta_sub": "Veldu tegund fjárfestingar sem hentar þér. Ráðgjafi mun aðstoða þig frá fyrstu beiðni.",
    "invest.form.amount": "Fjárfestingarupphæð",
    "invest.form.balance": "Tiltækt saldo: {amount}",
    "invest.form.note": "Athugasemd fyrir ráðgjafa (valfrjálst)",
    "invest.form.note_ph": "Tilgreindu væntingar þínar…",
    "invest.form.advisor_info": "Samtal verður sjálfkrafa opnað við ráðgjafann þinn.",
    "invest.form.cancel": "Hætta við",
    "invest.form.submit": "Senda beiðni",
    "invest.toast.requested": "Beiðni skráð — samtal hefur verið opnað við ráðgjafann þinn.",
    "invest.p1.name": "Fasteignir",
    "invest.p1.desc": "SCPI, pappírsfasteign, fjöldafjármögnun fasteigna",
    "invest.p2.name": "Fjöldafjármögnun",
    "invest.p2.desc": "Þátttökufjármögnun fyrir valin verkefni",
    "invest.p3.name": "Endurnýjanleg orka",
    "invest.p3.desc": "Sólar-, vinda- og grænir verkefni",
    "invest.p4.name": "Hlutabréfamarkaður",
    "invest.p4.desc": "Hlutabréf, ETF og dreifðar sjóðir",
    "invest.p5.name": "Þátttaka",
    "invest.p5.desc": "Lán til lítilla og meðalstórra fyrirtækja og staðbundinna frumkvöðla",
    "invest.p6.name": "Dulritunarmynt",
    "invest.p6.desc": "Bitcoin, Ethereum og marggjaldeyris veski",
    "msg.online": "Á netinu",
    "msg.loading": "Hleður…",
    "msg.none": "Engin skilaboð. Byrjaðu samtalið við ráðgjafann þinn.",
    "msg.placeholder": "Skrifaðu skilaboð þín…",
    "msg.attach.image": "Mynd",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Hljóð",
    "msg.attach.file": "Skrá / Skjal",
    "msg.pdf_doc": "PDF skjal",
    "msg.file": "Skrá",
    "msg.too_large": "Skrá of stór (hámark {size}).",
    "msg.withdraw": "Eyða",
    "credits.cta_sub": "Sæktu um beint á netinu — svar innan 24 klst.",
    "credits.learn_more": "Fræðast meira",
    "credits.request": "Sækja um",
    "credits.history": "Umsóknir mínar",
    "credits.status.pending": "Í bið",
    "credits.status.accepted": "Samþykkt",
    "credits.status.refused": "Hafnað",
    "credits.chars": "Eiginleikar",
    "credits.amount_range": "Upphæð: {min}€ – {max}€",
    "credits.rate_range": "Vextir: {min}% – {max}% á ári",
    "credits.duration_range": "Lengd: {min} til {max} mánuðir",
    "credits.deposit_min": "Upphafsinnborgun: {amount}€ lágmark",
    "credits.garantie": "Trygging: {garantie}",
    "credits.eligibility": "Hæfisskilyrði",
    "credits.elig.income": "Sanna reglulegar og stöðugar tekjur",
    "credits.elig.deposit": "Greiða upphafsinnborgun upp á {amount}€",
    "credits.elig.garantie": "Leggja fram trygginguna: {garantie}",
    "credits.form.title": "Umsókn — {name}",
    "credits.form.cancel": "Hætta við",
    "credits.form.submit": "Senda umsókn",
    "credits.form.amount": "Óskuð upphæð (€)",
    "credits.form.duration": "Lengd (mánuðir)",
    "credits.form.reason": "Ástæða (valfrjálst)",
    "credits.form.reason_ph": "Lýstu verkefni þínu…",
    "credits.form.close": "Loka",
    "credits.toast.submitted": "Lánasumsókn send — þú munt fá svar innan 24 klst.",
    "credits.p1.name": "Persónulegt lán",
    "credits.p1.desc": "Til að fjármagna daglegar þarfir eða persónuleg verkefni.",
    "credits.p2.name": "Fasteignalán",
    "credits.p2.desc": "Til að fjármagna kaup eða byggingu fasteignar.",
    "credits.p3.name": "Bíla- / Mótorhjólalán",
    "credits.p3.desc": "Kauptu farartæki með friði í huga.",
    "credits.p4.name": "Námslán",
    "credits.p4.desc": "Fjárhagsleg aðstoð fyrir háskólanám.",
    "credits.p5.name": "Atvinnulán",
    "credits.p5.desc": "Til að styðja vöxt í atvinnustarfsemi.",
    "credits.p6.name": "Hraðalán",
    "credits.p6.desc": "Skjót lausn fyrir brýnar þarfir.",
    "financing.reimbursable": "Endurgreiðanlegt fjármagn",
    "financing.reimbursable_desc": "Fjárhagslegur stuðningur með föstum skilmálum til að ræsa eða þróa starfsemi.",
    "financing.non_reimbursable": "Óendurgreiðanlegt fjármagn",
    "financing.non_reimbursable_desc": "Fyrir verkefni með félagslegt, menntunarlegt eða mannúðlegt markmið (frjálsar samtök).",
    "financing.cta_sub": "Sniðnar lausnir til að gera draumar þína að veruleika.",
    "financing.request": "Sækja um fjármögnun",
    "financing.history": "Umsóknir mínar",
    "financing.reimbursable_label": "Endurgreiðanlegt",
    "financing.non_reimbursable_label": "Óendurgreiðanlegt",
    "financing.status.pending": "Í bið",
    "financing.status.granted": "Veitt",
    "financing.status.refused": "Hafnað",
    "financing.form.title": "Umsókn — {name}",
    "financing.form.cancel": "Hætta við",
    "financing.form.submit": "Senda umsókn",
    "financing.form.project": "Nafn verkefnis",
    "financing.form.project_ph": "Verkefni mitt",
    "financing.form.amount": "Óskuð upphæð (€)",
    "financing.form.sector": "Geirinn",
    "financing.form.sector_ph": "Tækni, landbúnaður, …",
    "financing.form.description": "Lýsing á verkefni",
    "financing.form.description_ph": "Kynntu verkefni þitt, markmið þess og væntanlegar áhrif…",
    "settings.security": "Öryggi",
    "settings.current_pw": "Núverandi lykilorð",
    "settings.new_pw": "Nýtt lykilorð",
    "settings.confirm_pw": "Staðfesta",
    "settings.submit": "Breyta lykilorði",
    "settings.no_match": "Lykilorðin stemma ekki",
    "settings.changed": "Lykilorði breytt",
    "common.cancel": "Hætta við",
    "common.confirm": "Staðfesta",
    "common.close": "Loka",
    "common.save": "Vista",
    "common.loading": "Hleður…",
    "common.send": "Senda",
    "common.total": "Samtals",
    "admin.wallet.balance": "Tillgängligt saldo",
    "admin.wallet.history": "Historik",
    "admin.wallet.no_history": "Inga transaktioner",
    "admin.wallet.title": "Min plånbok",
    "admin.wallet.topup": "Fyll på",
    "beneficiary.pick": "Välj",
    "beneficiary.save_this": "Spara denna mottagare",
    "beneficiary.saved": "Sparade mottagare",
    "beneficiary.saved_ok": "Mottagare sparad",
    "card.color.amber": "Bärnsten",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Smaragd",
    "card.color.forest": "Skog",
    "card.color.gold": "Guld",
    "card.color.lavender": "Lavendel",
    "card.color.night": "Natt",
    "card.color.orange": "Orange",
    "card.color.pink": "Rosa",
    "card.color.royal": "Kunglig blå",
    "card.color.ruby": "Rubin",
    "card.color.slate": "Skiffer",
    "card.color.steel": "Stål",
    "card.color.terracotta": "Terrakotta",
    "card.color.violet": "Violett",
    "common.other": "Övrigt",
    "common.search": "Sök",
    "content.translated": "Automatiskt översatt innehåll",
    "credits.monthly_payment": "Beräknad månadsbetalning",
    "credits.rate_annual": "/ år",
    "credits.rate_used": "Beräknad ränta:",
    "credits.simulation": "Simulering av månadsbetalning",
    "credits.total_cost": "Beräknad totalkostnad",
    "demo.banner": "Demoläge — inga riktiga åtgärder",
    "demo.disabled": "Åtgärd ej tillgänglig i demoläge",
    "deposit.default_instructions": "Kontakta din handläggare för insättningsdetaljer.",
    "error.back_home": "Tillbaka till startsidan",
    "error.bank_not_found": "Banken hittades inte",
    "error.unknown": "Okänt fel",
    "kyc.file_read_error": "Filen kan inte läsas",
    "kyc.file_too_large": "Filen är för stor (max 5 MB)",
    "login.history": "Inloggningshistorik",
    "login.history.empty": "Inga inloggningar registrerade.",
    "notif.dismiss": "Stäng",
    "notif.locked": "Bankmeddelande",
    "tracking.error.not_found": "Spårningsnummer hittades inte. Kontrollera och försök igen.",
    "tracking.label": "Spårning",
    "tracking.status.delivered": "Levererad",
    "tracking.status.preparation": "Under förberedelse",
    "tracking.status.shipped": "Skickad",
    "tracking.status.transit": "Under transport",
    "transfer.code.copied": "Kopierad",
    "transfer.code.copy": "Kopiera",
    "transfer.code.received": "Din valideringskod",
    "dash.welcome": "Velkominn",
    "common.delete": "Eyða",
    "cards.delete.title": "Eyða korti",
    "cards.delete.confirm_msg": "Þessi aðgerð er óafturkræf. Kortið verður eytt varanlega.",
    "cards.delete.physical_tooltip": "Líkamlegt kort pantað — eyðing ómöguleg",
    "cards.order.confirmed": "Pöntun staðfest!",
    "cards.order.confirmed_body": "Líkamlegt kort þitt er í undirbúningi. Notaðu þetta númer til að fylgjast með afhendinguna.",
    "cards.order.tracking_no": "Rakningarnúmer",
    "cards.fee_deducted": "{amount} verður dregið frá stöðunni þinni",
    "cards.insufficient_short": "Ósamt saldo ({available} tiltækt)",
    "transfer.section.beneficiary": "Viðtakandi",
    "transfer.section.details": "Millifærsla",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banki",
    "transfer.field.city": "Borg",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banki:",
    "transfer.progress.paused": "Bíður eftir staðfestingu",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Breytilegt",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Korti eytt",
    "cards.feat.gold_exclusive": "Einkagullkort",
    "cards.feat.cashback_05": "Endurgreiðsla 0,5%",
    "cards.feat.buyer_protection": "Verndun kaupanda",
    "cards.feat.support_standard": "Staðlaður stuðningur",
    "cards.feat.plastic_premium": "Premium plastkort",
    "cards.feat.cashback_1": "Endurgreiðsla 1%",
    "cards.feat.travel_standard": "Stöðluð ferðatrygging",
    "cards.feat.support_priority": "Forgangsstuðningur",
    "cards.feat.metal_brushed": "Burstað málmkort",
    "cards.feat.cashback_2_unlimited": "Ótakmörkuð 2% endurgreiðsla",
    "cards.feat.travel_premium": "Premium ferðatrygging",
    "cards.feat.vip_lounge": "Aðgangur að VIP-stofum",
    "cards.feat.support_247": "24/7 aðstoð",
    "auth.demo.title": "Innskráning stjórnanda",
    "auth.demo.subtitle": "Sláðu inn stjórnandaaðgang til að skoða kynningu",
    "credits.garantie.none": "Engin",
    "credits.garantie.mortgage": "Veðréttur í fjármagnaðri eign",
    "credits.garantie.vehicle": "Fjármagnað ökutæki",
    "credits.garantie.parental": "Foreldra- eða bankaábyrgð",
    "credits.garantie.professional": "Faglegar eignir eða persónuleg ábyrgð",
    "cards.reveal.pin_prompt": "Sláðu inn 4 stafa PIN-númerið þitt til að sýna kortaupplýsingar.",
    "cards.reveal.show": "Sýna",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Þessir tveir PIN-kóðar stemma ekki",
    "auth.pin.digit_hint": "4 tölustafir",
  
  },
  pl: {
    "notif.transfer_code.title": "Kod weryfikacji przelewu",
    "notif.transfer_code.body": "Skopiuj ten kod, aby zatwierdzić przelew.",
    "notif.transfer_code.expires_in": "Znika za {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mies.",
    "credits.n_months": "{n} mies.",
    "msg.read": "Przeczytane",
    "msg.sent": "Wysłane",
    "msg.attach_aria": "Załącz plik",
    "login.device.unknown": "Nieznana przeglądarka",
    "login.device.mobile": "Mobilny",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Komputer",
    "login.recent": "Niedawne",
    "err.invalid_credentials": "Nieprawidłowe dane logowania",
    "err.bad_pin": "Nieprawidłowy PIN",
    "err.account_blocked": "Konto zablokowane",
    "err.bad_current_pw": "Bieżące hasło jest nieprawidłowe",
    "err.insufficient_funds": "Niewystarczające środki, doładuj konto.",
    "err.amount_above_savings": "Kwota przekracza dostępne oszczędności",
    "err.must_be_positive": "Wartość musi być dodatnia",
    "err.invalid_format": "Nieprawidłowy format",
    "err.email_taken": "Email już używany",
    "err.card_not_found": "Karta nie znaleziona",
    "err.physical_card_already_ordered": "Karta fizyczna już zamówiona",
    "err.beneficiary_not_found": "Odbiorca nie znaleziony",
    "err.invalid_code": "Nieprawidłowy kod",
    "err.no_transfer_code": "Brak kodu dla tego przelewu",
    "err.link_expired": "Link nieprawidłowy lub wygasł",
    "err.thread_not_found": "Konwersacja nie znaleziona",
    "err.empty_message": "Pusta wiadomość",
    "err.file_too_large": "Plik zbyt duży (maks 20 MB)",
    "err.forbidden": "Dostęp zabroniony",
    "err.bank_not_found": "Bank nie znaleziony",
    "err.signups_closed": "Rejestracje są zamknięte dla tego banku.",
    "err.access_suspended": "Dostęp tymczasowo zawieszony. Skontaktuj się ze swoim bankiem.",
    "err.notif_not_closable": "Tego powiadomienia nie można zamknąć.",
    "auth.signup.tagline": "Dołącz do nas już dziś",
    "auth.login.tagline": "Witaj ponownie!",
    "auth.signup.tagline_sub": "Otwórz konto w 2 minuty i uzyskaj dostęp do wszystkich naszych usług bankowych premium.",
    "auth.login.tagline_sub": "Uzyskaj dostęp do swojego osobistego konta i zarządzaj finansami w pełnym bezpieczeństwie.",
    "auth.back": "Powrót do strony głównej",
    "auth.bullet.security": "Bezpieczeństwo bankowe klasy wojskowej",
    "auth.bullet.transfers": "Natychmiastowe przelewy 24/7",
    "auth.bullet.investments": "Zintegrowane inwestycje i oszczędności",
    "auth.bullet.advisor": "Osobisty doradca dostępny 7 dni w tygodniu",
    "auth.footer": "Obsługiwane przez {bank}",
    "auth.signup.title": "Utwórz konto",
    "auth.login.title": "Logowanie",
    "auth.field.lastName": "Nazwisko",
    "auth.field.firstName": "Imię",
    "auth.field.country": "Kraj",
    "auth.field.country.placeholder": "— Wybierz kraj —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Adres",
    "auth.field.address.placeholder": "ul. Marszałkowska 123, Warszawa",
    "auth.field.email": "E-mail",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Hasło",
    "auth.submit.signup": "Utwórz moje konto",
    "auth.submit.login": "Zaloguj się",
    "auth.login.badge": "Zaloguj się na swoje konto",
    "auth.card.tagline": "Twoja strefa klienta",
    "auth.submit.loading": "Ładowanie…",
    "auth.pin.title": "Kod PIN",
    "auth.pin.subtitle": "Wprowadź swój 4-cyfrowy kod PIN",
    "auth.pin.label": "Kod PIN",
    "auth.pin.confirm": "Potwierdź kod PIN",
    "auth.pin.submit": "Potwierdź",
    "auth.pin.back": "Powrót do logowania",
    "auth.pin.length_error": "Kod PIN musi mieć 4 cyfry",
    "auth.switch.have_account": "Masz już konto?",
    "auth.switch.login_link": "Logowanie",
    "auth.switch.no_account": "Nie jesteś jeszcze klientem?",
    "auth.error.select_country": "Wybierz swój kraj",
    "kyc.title": "Weryfikacja tożsamości (KYC)",
    "kyc.sub": "Prześlij swoje dokumenty, aby zweryfikować konto. Wszystkie dokumenty są szyfrowane i poufne.",
    "kyc.id_type": "Rodzaj dokumentu tożsamości *",
    "kyc.id_card": "Dowód osobisty",
    "kyc.passport": "Paszport",
    "kyc.driver_license": "Prawo jazdy",
    "kyc.recto_passport": "Przód paszportu *",
    "kyc.recto_id": "Przód dokumentu *",
    "kyc.verso": "Tył dokumentu *",
    "kyc.selfie": "Selfie z dokumentem w ręku *",
    "kyc.address_proof": "Potwierdzenie adresu zamieszkania *",
    "kyc.hint_doc": "Obraz lub PDF, maks. 5 MB",
    "kyc.hint_selfie": "Wyraźne zdjęcie, twarz i dokument widoczne",
    "kyc.hint_address": "Rachunek, wyciąg, zaświadczenie… (maks. 5 MB)",
    "kyc.upload": "Prześlij",
    "kyc.replace": "Zastąp",
    "pending.title": "Konto oczekuje na aktywację",
    "pending.body": "Twoje zgłoszenie zostało przyjęte. Administrator zweryfikuje Twoją dokumentację i wkrótce aktywuje konto.",
    "pending.back": "Powrót do logowania",
    "home.nav.features": "Funkcje",
    "home.nav.security": "Bezpieczeństwo",
    "home.nav.invest": "Inwestycje",
    "home.nav.credits": "Kredyty",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Zaloguj się",
    "home.hero.tagline": "Bankowość internetowa nowej generacji",
    "home.hero.title1": "Zarządzaj swoimi pieniędzmi",
    "home.hero.title2": "z inteligencją",
    "home.hero.sub": "Kompleksowa platforma bankowa — przelewy, oszczędności, inwestycje i kredyty ze smartfona.",
    "home.hero.login": "Zaloguj się",
    "home.hero.trust.secure": "100% Bezpieczne",
    "home.hero.trust.fast": "Szybkie otwarcie",
    "home.hero.trust.encrypted": "Zaszyfrowane dane",
    "home.hero.trust.support": "Obsługa 24/7",
    "home.mockup.balance": "Dostępne saldo",
    "home.mockup.currency": "EUR · Konto główne",
    "home.mockup.recharge": "Doładuj",
    "home.mockup.send": "Wyślij",
    "home.mockup.save": "Oszczędzaj",
    "home.mockup.salary": "Wynagrodzenie marzec",
    "home.mockup.rent": "Czynsz kwiecień",
    "home.mockup.investment": "Inwestycja",
    "home.mockup.days2": "2 dni temu",
    "home.mockup.days3": "3 dni temu",
    "home.mockup.days5": "5 dni temu",
    "home.stats.accounts": "Aktywne konta",
    "home.stats.uptime": "Dostępność",
    "home.stats.transfers": "Przelewy",
    "home.stats.support": "Wsparcie",
    "home.features.label": "Funkcje",
    "home.features.title": "Wszystko, czego potrzebujesz",
    "home.features.sub": "Kompletna platforma zaprojektowana, aby uprościć zarządzanie finansami na co dzień.",
    "home.feature.security": "Bezpieczeństwo militarne",
    "home.feature.security_body": "Szyfrowanie AES-256, uwierzytelnianie dwuskładnikowe i całodobowy monitoring konta.",
    "home.feature.cards": "Karty wirtualne i fizyczne",
    "home.feature.cards_body": "Wydawaj karty w kilka sekund lub zamów prawdziwą kartę fizyczną dostarczoną do domu.",
    "home.feature.transfers": "Przelewy ekspresowe",
    "home.feature.transfers_body": "Wysyłaj i odbieraj pieniądze gdziekolwiek na świecie w kilka sekund.",
    "home.feature.analytics": "Analiza w czasie rzeczywistym",
    "home.feature.analytics_body": "Inteligentne dashboardy do śledzenia wydatków i planowania finansów.",
    "home.feature.investments": "Zintegrowane inwestycje",
    "home.feature.investments_body": "Uzyskaj dostęp do wybranych produktów inwestycyjnych bezpośrednio ze swojego konta.",
    "home.feature.support": "Dedykowane wsparcie 24/7",
    "home.feature.support_body": "Osobisty doradca bankowy dostępny w każdej chwili przez bezpieczną wiadomość.",
    "home.tpl.label": "Zaufanie",
    "home.tpl.title": "Zaufali nam",
    "home.mockup.balance_label": "Saldo",
    "home.mockup.virement": "Przelew",
    "home.mockup.salaire": "Wynagrodzenie",
    "home.security.label": "Bezpieczeństwo",
    "home.security.title1": "Twoje finanse",
    "home.security.title2": "w pełnym bezpieczeństwie",
    "home.security.sub": "Stosujemy najsurowsze standardy bezpieczeństwa bankowego, aby chronić każdy grosz.",
    "home.security.aes.title": "Szyfrowanie AES-256",
    "home.security.aes.body": "Wszystkie wrażliwe dane są szyfrowane tymi samymi algorytmami, których używają największe banki międzynarodowe.",
    "home.security.auth.title": "Wzmocnione uwierzytelnianie",
    "home.security.auth.body": "Podwójna weryfikacja tożsamości przy każdym logowaniu i każdej wrażliwej operacji na koncie.",
    "home.security.compliance.title": "Zgodność PSD2/RODO",
    "home.security.compliance.body": "Spełniamy wszystkie europejskie przepisy dotyczące usług płatniczych i ochrony danych.",
    "home.invest.label": "Inwestycje",
    "home.invest.title": "Pomnażaj swoje pieniądze",
    "home.invest.sub": "6 kategorii inwestycji wybranych ze względu na potencjał i niezawodność.",
    "home.invest.yield": "Stopa zwrotu",
    "home.credits.label": "Kredyty",
    "home.credits.title": "Rozwiązania dla każdego projektu",
    "home.credits.sub": "Konkurencyjne stawki, szybkie odpowiedzi i spersonalizowane wsparcie dla wszystkich projektów.",
    "home.credits.rate": "Oprocentowanie",
    "home.faq.label": "FAQ",
    "home.faq.title": "Często zadawane pytania",
    "home.faq.q1": "Jak otworzyć konto?",
    "home.faq.a1": "Rejestracja zajmuje mniej niż 2 minuty. Wystarczy wypełnić dane osobowe, wybrać hasło, a konto zostanie aktywowane po weryfikacji przez nasz zespół.",
    "home.faq.q2": "Czy moje dane są bezpieczne?",
    "home.faq.a2": "Tak. Wszystkie dane są szyfrowane AES-256 w spoczynku i TLS 1.3 w transmisji. Spełniamy międzynarodowe normy bankowe i przepisy RODO/PSD2.",
    "home.faq.q3": "Jakie rodzaje inwestycji oferujecie?",
    "home.faq.a3": "Oferujemy 6 kategorii: Nieruchomości, Crowdfunding, Odnawialne źródła energii, Giełda, Partycypacyjne i Kryptowaluty. Każdy produkt jest dobierany pod kątem stosunku ryzyka do zysku.",
    "home.faq.q4": "Jak złożyć wniosek o kredyt?",
    "home.faq.a4": "Przejdź do sekcji Kredyt w swoim panelu klienta, wybierz rodzaj pożyczki, wprowadź kwotę i okres spłaty. Twój wniosek jest rozpatrywany w ciągu 24h przez naszych doradców.",
    "home.faq.q5": "Czy są ukryte opłaty?",
    "home.faq.a5": "Żadnych ukrytych opłat. Wszystkie stawki są wyraźnie wyświetlane przy każdej operacji. Warunki są dostępne w panelu klienta w dowolnym momencie.",
    "home.faq.q6": "Jak skontaktować się z pomocą techniczną?",
    "home.faq.a6": "Skorzystaj ze zintegrowanego systemu wiadomości w swoim panelu klienta, aby bezpośrednio skontaktować się z osobistym doradcą bankowym, dostępnym całą dobę przez 7 dni w tygodniu.",
    "home.cta.title": "Gotowy, aby zacząć?",
    "home.cta.sub": "Dołącz do tysięcy użytkowników zarządzających finansami z {bank}. Rejestracja zajmuje mniej niż 2 minuty.",
    "home.footer.products": "Produkty",
    "home.footer.services": "Usługi",
    "home.footer.access": "Szybki dostęp",
    "home.footer.accounts": "Konta",
    "home.footer.transfers": "Przelewy",
    "home.footer.cards": "Karty",
    "home.footer.savings": "Oszczędności",
    "home.footer.investments": "Inwestycje",
    "home.footer.credits": "Kredyty",
    "home.footer.financing": "Finansowanie",
    "home.footer.messages": "Wiadomości",
    "home.footer.login": "Logowanie",
    "home.footer.signup": "Otwórz konto",
    "home.footer.support": "Wsparcie 24/7",
    "home.footer.tagline": "Twój bank internetowy dostępny wszędzie i o każdej porze.",
    "home.footer.copyright": "© {year} {bank} — Wszelkie prawa zastrzeżone",
    "home.footer.powered": "Obsługiwane przez {bank}",
    "app.menu_label": "Menu główne",
    "app.nav.dashboard": "Panel główny",
    "app.nav.accounts": "Konta",
    "app.nav.transactions": "Transakcje",
    "app.nav.transfers": "Przelewy",
    "app.nav.cards": "Karty",
    "app.nav.savings": "Oszczędności",
    "app.nav.investments": "Inwestycje",
    "app.nav.credit": "Kredyt",
    "app.nav.financing": "Finansowanie",
    "app.nav.messages": "Wiadomości",
    "app.nav.card_tracking": "Śledzenie karty",
    "app.nav.notifications": "Powiadomienia",
    "app.nav.settings": "Ustawienia",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Strona główna",
    "app.nav.mobile.transfers": "Przelewy",
    "app.nav.mobile.cards": "Karty",
    "app.nav.mobile.transactions": "Transakcje",
    "app.nav.more.invest": "Inwestuj",
    "app.user_role": "Klient",
    "app.logout": "Wyloguj się",
    "app.search": "Szukaj…",
    "app.more_title": "Więcej funkcji",
    "app.more_btn": "Więcej",
    "app.loading": "Ładowanie…",
    "app.error_back": "Powrót do logowania",
    "app.see_more_notifs": "Zobacz {count} więcej powiadomień →",
    "page.accounts": "Moje konta",
    "page.transactions": "Transakcje",
    "page.transfers": "Nowy przelew",
    "page.deposit": "Wpłata",
    "page.cards": "Moje karty",
    "page.savings": "Oszczędności",
    "page.investments": "Inwestycje",
    "page.credits": "Kredyt",
    "page.financing": "Finansowanie projektów",
    "page.messages": "Wiadomości",
    "page.settings": "Ustawienia",
    "page.notifications": "Ważne powiadomienia",
    "page.profile": "Mój profil",
    "dash.greeting": "Cześć, {name}",
    "dash.overview": "Przegląd finansów — {date}",
    "dash.total_balance": "Łączne saldo",
    "dash.this_month": "+{amount} w tym miesiącu",
    "dash.view_iban": "Zobacz mój IBAN",
    "dash.my_accounts": "Moje konta",
    "dash.primary_account": "Konto główne",
    "dash.savings_account": "Konto oszczędnościowe",
    "dash.remunerated": "Oprocentowane",
    "dash.details": "Szczegóły",
    "dash.transfer_btn": "Przelew",
    "dash.quick_actions": "Szybkie działania",
    "dash.quick.deposit": "Wpłata",
    "deposit.title": "Informacje o wpłacie",
    "dash.quick.transfer": "Przelew",
    "dash.quick.history": "Historia",
    "dash.quick.cards": "Moje karty",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Budżet miesięczny",
    "dash.income": "Przychody",
    "dash.expenses": "Wydatki",
    "dash.spending_cat": "Wydatki według kategorii",
    "dash.no_spending": "Brak zarejestrowanych wydatków.",
    "dash.recent_tx": "Ostatnie transakcje",
    "dash.see_all": "Zobacz wszystko",
    "dash.no_tx": "Brak operacji.",
    "dash.main_card": "Moja główna karta",
    "dash.manage": "Zarządzaj",
    "dash.block": "Zablokuj",
    "dash.pin": "Kod PIN",
    "dash.security": "Bezpieczeństwo",
    "dash.more": "Więcej",
    "dash.no_card": "Brak wydanej karty",
    "dash.monthly_activity": "Aktywność miesięczna",
    "dash.operations": "Operacje",
    "dash.holder": "Posiadacz",
    "dash.expires": "Ważna do",
    "dash.visa_label": "VISA",
    "dash.balance": "Dostępne saldo",
    "tx.all": "Wszystkie",
    "tx.income": "Przychody",
    "tx.expenses": "Wydatki",
    "tx.today": "Dziś",
    "tx.yesterday": "Wczoraj",
    "tx.none": "Brak transakcji.",
    "transfer.title": "Szybki przelew",
    "transfer.recipient": "Odbiorca",
    "transfer.recipient_ph": "Imię i nazwisko, IBAN, numer telefonu…",
    "transfer.amount": "Kwota",
    "transfer.label": "Tytuł",
    "transfer.reason": "Powód (opcjonalnie)",
    "transfer.reason_ph": "Np.: czynsz, zwrot kosztów…",
    "transfer.execution": "Wykonanie",
    "transfer.immediate": "Natychmiastowe",
    "transfer.delayed": "Odroczone",
    "transfer.balance": "Dostępne saldo:",
    "transfer.continue": "Kontynuuj",
    "transfer.confirm": "Potwierdź",
    "transfer.cancel": "Anuluj",
    "transfer.code": "Kod weryfikacyjny",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Wprowadź otrzymany kod, aby zatwierdzić przelew",
    "transfer.pending.title": "Przelew oczekuje na zatwierdzenie",
    "transfer.pending.amount": "Kwota:",
    "transfer.pending.to": "Do:",
    "transfer.pending.hint": "Twój kod weryfikacyjny zostanie wysłany e-mailem lub przez dzwonek powiadomień. Kod składa się z 6 cyfr. Jeśli go nie otrzymasz, skontaktuj się z pomocą techniczną.",
    "transfer.pending.validate": "Zatwierdź przelew",
    "transfer.saved": "Przelew zapisany. Poproś administratora o kod weryfikacyjny.",
    "transfer.done": "Przelew zrealizowany",
    "transfer.validated": "Przelew zatwierdzony!",
    "transfer.progress.processing": "Przetwarzanie przelewu…",
    "transfer.progress.completed": "Przelew wykonany!",
    "transfer.progress.default_message": "Twój przelew jest przetwarzany. Proszę czekać.",
    "cards.none": "Brak wydanej karty.\nSkontaktuj się z administratorem.",
    "cards.holder": "Posiadacz",
    "cards.expires": "Ważna do",
    "cards.block": "Zablokuj",
    "cards.pin": "Kod PIN",
    "cards.more": "Więcej",
    "cards.limits": "Limity karty",
    "cards.payment": "Płatność",
    "cards.withdrawal": "Wypłata",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Zablokowana",
    "cards.status_active": "Aktywna",
    "cards.toast.blocked": "Karta zablokowana",
    "cards.toast.unblocked": "Karta odblokowana",
    "cards.toast.physical_ordered": "Karta fizyczna zamówiona! Dostawa w ciągu 5–10 dni roboczych.",
    "cards.toast.created": "Karta wygenerowana pomyślnie!",
    "cards.none_sub": "Brak wydanej karty. Wygeneruj swoją wirtualną kartę teraz.",
    "cards.generate_btn": "Wygeneruj moją kartę",
    "cards.create.title": "Wygeneruj moją wirtualną kartę",
    "cards.create.network": "Sieć płatności",
    "cards.create.color": "Kolor karty",
    "cards.create.preview_name": "TWOJE IMIĘ",
    "cards.create.preview_expiry": "MM/RR",
    "cards.create.loading": "Generowanie…",
    "cards.create.submit": "Wygeneruj kartę",
    "cards.btn.unblock": "Odblokuj",
    "cards.btn.block": "Zablokuj",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fizyczna",
    "cards.btn.physical_done": "Zamówiona",
    "cards.limits_title": "Limity",
    "cards.reveal.title": "Informacje o karcie",
    "cards.reveal.number": "Pełny numer",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Data ważności",
    "cards.reveal.warning": "Nigdy nie udostępniaj tych informacji. Zamknij to okno po użyciu.",
    "cards.pin.title": "Kod PIN",
    "cards.pin.message": "Ten kod PIN jest używany do wszystkich płatności online i wypłat. Nigdy nikomu go nie podawaj.",
    "cards.physical.title": "Zamów kartę fizyczną",
    "cards.physical.fee_label": "Opłata za dostawę",
    "cards.physical.balance_label": "Twoje saldo",
    "cards.physical.insufficient": "Niewystarczające saldo. Doładuj swoje konto przed zamówieniem.",
    "cards.physical.info": "Opłata zostanie pobrana z twojego konta. Dostawa w ciągu 5–10 dni roboczych.",
    "cards.physical.order_btn": "Zamów ({fee})",
    "cards.physical.ordering": "Zamawiam…",
    "cards.physical.order_cta": "Zamów kartę fizyczną",
    "cards.physical.choose_type": "Wybierz swoją kartę",
    "cards.physical.features": "Zalety",
    "cards.buy_virtual_cta": "Kup wirtualną kartę",
    "notif.none": "Brak powiadomień w tej chwili.",
    "notif.contact_support": "Skontaktuj się z pomocą techniczną",
    "profile.change_photo": "Zmień zdjęcie",
    "profile.remove_photo": "Usuń zdjęcie",
    "profile.email": "E-mail",
    "profile.phone": "Telefon",
    "profile.country": "Kraj",
    "profile.address": "Adres",
    "profile.notifs_title": "Powiadomienia ({count})",
    "profile.no_notifs": "Brak powiadomień.",
    "profile.logout": "Wyloguj się",
    "profile.photo_updated": "Zdjęcie profilowe zaktualizowane",
    "profile.photo_removed": "Zdjęcie usunięte",
    "profile.photo_too_large": "Obraz za duży (maks. 1 MB)",
    "profile.powered": "Obsługiwane przez {bank}",
    "iban.title": "Mój IBAN",
    "iban.loading": "Ładowanie…",
    "iban.bic": "Kod BIC / SWIFT",
    "iban.share_info": "Udostępnij te dane, aby otrzymać przelew na swoje konto.",
    "iban.copy": "Kopiuj",
    "iban.copied": "Skopiowano do schowka",
    "iban.copy_fail": "Nie można skopiować",
    "iban.close": "Zamknij",
    "accounts.all": "Wszystkie Twoje konta w {bank}.",
    "accounts.primary": "Konto główne",
    "accounts.savings": "Konto oszczędnościowe",
    "accounts.primary_tag": "Główne",
    "accounts.secondary_tag": "Dodatkowe",
    "accounts.objective": "Cel: {amount} · {pct}%",
    "accounts.copy": "Kopiuj",
    "accounts.loading": "Ładowanie…",
    "accounts.iban_copied": "IBAN skopiowany",
    "accounts.copy_fail": "Nie można skopiować",
    "savings.total": "Łączne oszczędności",
    "savings.target_total": "Łączny cel",
    "savings.progress": "Postęp",
    "savings.active": "Aktywne cele",
    "savings.new_goal": "Nowy cel",
    "savings.none": "Brak celów oszczędnościowych.",
    "savings.none_sub": "Utwórz pierwszy cel, aby zacząć.",
    "savings.saved": "Zaoszczędzono:",
    "savings.goal": "Cel:",
    "savings.deadline": "Termin: {date}",
    "savings.deposit": "Wpłać",
    "savings.withdraw": "Wypłać",
    "savings.loading": "Ładowanie…",
    "savings.form.title": "Nowy cel oszczędnościowy",
    "savings.form.cancel": "Anuluj",
    "savings.form.create": "Utwórz",
    "savings.form.name": "Nazwa celu",
    "savings.form.name_ph": "Podróż do Japonii, Samochód, …",
    "savings.form.target": "Kwota docelowa",
    "savings.form.initial": "Wpłata początkowa",
    "savings.form.deadline": "Termin (opcjonalnie)",
    "savings.form.color": "Kolor",
    "savings.deposit_title": "Wpłać na \"{name}\"",
    "savings.deposit_amount": "Kwota do wpłaty",
    "savings.balance_hint": "Saldo główne: {amount}",
    "savings.withdraw_prompt": "Ile wypłacić z \"{name}\"?",
    "savings.close_confirm": "Zamknąć cel \"{name}\"? Oszczędności zostaną zwrócone na konto główne.",
    "savings.toast.created": "Cel utworzony",
    "savings.toast.deposited": "Wpłata dokonana",
    "savings.toast.withdrawn": "Wypłata dokonana",
    "savings.toast.closed": "Cel zamknięty",
    "invest.total": "Łączna inwestycja",
    "invest.profits": "Skumulowane zyski",
    "invest.count": "Inwestycje",
    "invest.products": "Dostępne produkty",
    "invest.invest_btn": "Inwestuj",
    "invest.my": "Moje inwestycje",
    "invest.pending": "Oczekujące",
    "invest.validated": "Zatwierdzone",
    "invest.refused": "Odrzucone",
    "invest.withdraw.btn": "Wypłać",
    "invest.withdraw.title": "Wypłać moją inwestycję",
    "invest.withdraw.intro": "Zamierzasz wypłacić swoją inwestycję „{product}\". Kapitał początkowy oraz naliczony zysk zostaną zaksięgowane na Twoim głównym saldzie.",
    "invest.withdraw.principal": "Kapitał początkowy",
    "invest.withdraw.profit": "Zysk",
    "invest.withdraw.total": "Razem do wypłaty",
    "invest.withdraw.confirm": "Potwierdź wypłatę",
    "invest.withdraw.warning": "Tej operacji nie można cofnąć. Inwestycja zostanie zamknięta.",
    "invest.withdraw.success": "Inwestycja wypłacona — {amount} zaksięgowane na saldzie.",
    "invest.withdrawn": "Wypłacona",
    "invest.cta_sub": "Wybierz rodzaj inwestycji odpowiedni dla siebie. Doradca będzie Ci towarzyszył od pierwszego wniosku.",
    "invest.form.amount": "Kwota do zainwestowania",
    "invest.form.balance": "Dostępne saldo: {amount}",
    "invest.form.note": "Notatka dla doradcy (opcjonalnie)",
    "invest.form.note_ph": "Określ swoje oczekiwania…",
    "invest.form.advisor_info": "Rozmowa zostanie automatycznie otwarta z Twoim doradcą.",
    "invest.form.cancel": "Anuluj",
    "invest.form.submit": "Wyślij wniosek",
    "invest.toast.requested": "Wniosek zarejestrowany — otwarto rozmowę z Twoim doradcą.",
    "invest.p1.name": "Nieruchomości",
    "invest.p1.desc": "SCPI, paper-stone, crowdfunding nieruchomości",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Finansowanie partycypacyjne wybranych projektów",
    "invest.p3.name": "Odnawialne źródła energii",
    "invest.p3.desc": "Projekty solarne, wiatrowe i ekologiczne",
    "invest.p4.name": "Giełda",
    "invest.p4.desc": "Akcje, ETF i zdywersyfikowane fundusze",
    "invest.p5.name": "Partycypacyjne",
    "invest.p5.desc": "Pożyczki dla MŚP i lokalnych przedsiębiorców",
    "invest.p6.name": "Kryptowaluty",
    "invest.p6.desc": "Bitcoin, Ethereum i portfel wielowalutowy",
    "msg.online": "Online",
    "msg.loading": "Ładowanie…",
    "msg.none": "Brak wiadomości. Rozpocznij rozmowę z doradcą.",
    "msg.placeholder": "Napisz wiadomość…",
    "msg.attach.image": "Obraz",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Plik / Dokument",
    "msg.pdf_doc": "Dokument PDF",
    "msg.file": "Plik",
    "msg.too_large": "Plik za duży (maks. {size}).",
    "msg.withdraw": "Usuń",
    "credits.cta_sub": "Złóż wniosek bezpośrednio online — odpowiedź w ciągu 24h.",
    "credits.learn_more": "Dowiedz się więcej",
    "credits.request": "Wnioskuj",
    "credits.history": "Moje wnioski",
    "credits.status.pending": "Oczekujące",
    "credits.status.accepted": "Zaakceptowane",
    "credits.status.refused": "Odrzucone",
    "credits.chars": "Charakterystyka",
    "credits.amount_range": "Kwota: {min}€ – {max}€",
    "credits.rate_range": "Oprocentowanie: {min}% – {max}% rocznie",
    "credits.duration_range": "Okres: {min} do {max} miesięcy",
    "credits.deposit_min": "Wpłata początkowa: {amount}€ minimum",
    "credits.garantie": "Gwarancja: {garantie}",
    "credits.eligibility": "Warunki kwalifikowalności",
    "credits.elig.income": "Udokumentowanie regularnych i stabilnych dochodów",
    "credits.elig.deposit": "Dokonanie wpłaty początkowej w wysokości {amount}€",
    "credits.elig.garantie": "Przedstawienie gwarancji: {garantie}",
    "credits.form.title": "Wniosek — {name}",
    "credits.form.cancel": "Anuluj",
    "credits.form.submit": "Wyślij wniosek",
    "credits.form.amount": "Żądana kwota (€)",
    "credits.form.duration": "Okres (miesiące)",
    "credits.form.reason": "Powód (opcjonalnie)",
    "credits.form.reason_ph": "Opisz swój projekt…",
    "credits.form.close": "Zamknij",
    "credits.toast.submitted": "Wniosek kredytowy wysłany — odpowiedź otrzymasz w ciągu 24h.",
    "credits.p1.name": "Pożyczka osobista",
    "credits.p1.desc": "Na finansowanie codziennych potrzeb lub projektów osobistych.",
    "credits.p2.name": "Kredyt hipoteczny",
    "credits.p2.desc": "Na finansowanie zakupu lub budowy nieruchomości.",
    "credits.p3.name": "Kredyt samochodowy / motocyklowy",
    "credits.p3.desc": "Kup pojazd bez obaw.",
    "credits.p4.name": "Kredyt studencki",
    "credits.p4.desc": "Wsparcie finansowe na studia wyższe.",
    "credits.p5.name": "Kredyt biznesowy",
    "credits.p5.desc": "Na wsparcie wzrostu działalności zawodowej.",
    "credits.p6.name": "Pożyczka ekspresowa",
    "credits.p6.desc": "Szybkie rozwiązanie dla pilnych potrzeb.",
    "financing.reimbursable": "Finansowanie zwrotne",
    "financing.reimbursable_desc": "Wsparcie finansowe z ustalonymi warunkami na uruchomienie lub rozwój działalności.",
    "financing.non_reimbursable": "Finansowanie bezzwrotne",
    "financing.non_reimbursable_desc": "Dla projektów o oddziaływaniu społecznym, edukacyjnym lub humanitarnym (NGO, stowarzyszenia).",
    "financing.cta_sub": "Dopasowane rozwiązania, aby urzeczywistnić Twoje ambicje.",
    "financing.request": "Wnioskuj o finansowanie",
    "financing.history": "Moje wnioski",
    "financing.reimbursable_label": "Zwrotne",
    "financing.non_reimbursable_label": "Bezzwrotne",
    "financing.status.pending": "Oczekujące",
    "financing.status.granted": "Przyznane",
    "financing.status.refused": "Odrzucone",
    "financing.form.title": "Wniosek — {name}",
    "financing.form.cancel": "Anuluj",
    "financing.form.submit": "Wyślij wniosek",
    "financing.form.project": "Nazwa projektu",
    "financing.form.project_ph": "Mój projekt",
    "financing.form.amount": "Wnioskowana kwota (€)",
    "financing.form.sector": "Sektor",
    "financing.form.sector_ph": "Technologia, rolnictwo, …",
    "financing.form.description": "Opis projektu",
    "financing.form.description_ph": "Przedstaw swój projekt, jego cele i oczekiwane rezultaty…",
    "settings.security": "Bezpieczeństwo",
    "settings.current_pw": "Aktualne hasło",
    "settings.new_pw": "Nowe hasło",
    "settings.confirm_pw": "Potwierdź",
    "settings.submit": "Zmień hasło",
    "settings.no_match": "Hasła nie pasują do siebie",
    "settings.changed": "Hasło zmienione",
    "common.cancel": "Anuluj",
    "common.confirm": "Potwierdź",
    "common.close": "Zamknij",
    "common.save": "Zapisz",
    "common.loading": "Ładowanie…",
    "common.send": "Wyślij",
    "common.total": "Łącznie",
    "admin.wallet.balance": "Dostępne saldo",
    "admin.wallet.history": "Historia",
    "admin.wallet.no_history": "Brak transakcji",
    "admin.wallet.title": "Mój portfel",
    "admin.wallet.topup": "Doładuj",
    "beneficiary.pick": "Wybierz",
    "beneficiary.save_this": "Zapisz tego beneficjenta",
    "beneficiary.saved": "Zapisani beneficjenci",
    "beneficiary.saved_ok": "Beneficjent zapisany",
    "card.color.amber": "Bursztynowy",
    "card.color.cyan": "Cyjanowy",
    "card.color.emerald": "Szmaragdowy",
    "card.color.forest": "Leśny",
    "card.color.gold": "Złoty",
    "card.color.lavender": "Lawendowy",
    "card.color.night": "Nocny",
    "card.color.orange": "Pomarańczowy",
    "card.color.pink": "Różowy",
    "card.color.royal": "Granat królewski",
    "card.color.ruby": "Rubinowy",
    "card.color.slate": "Łupkowy",
    "card.color.steel": "Stalowy",
    "card.color.terracotta": "Terakota",
    "card.color.violet": "Fioletowy",
    "common.other": "Inne",
    "common.search": "Szukaj",
    "content.translated": "Treść przetłumaczona automatycznie",
    "credits.monthly_payment": "Szacowana rata miesięczna",
    "credits.rate_annual": "/ rok",
    "credits.rate_used": "Szacowana stopa:",
    "credits.simulation": "Symulacja raty miesięcznej",
    "credits.total_cost": "Szacowany koszt całkowity",
    "demo.banner": "Tryb demonstracyjny — brak prawdziwych działań",
    "demo.disabled": "Działanie niedostępne w trybie demo",
    "deposit.default_instructions": "Skontaktuj się z menedżerem w sprawie szczegółów wpłaty.",
    "error.back_home": "Powrót do strony głównej",
    "error.bank_not_found": "Bank nie znaleziony",
    "error.unknown": "Nieznany błąd",
    "kyc.file_read_error": "Nie można odczytać pliku",
    "kyc.file_too_large": "Plik za duży (maks. 5 MB)",
    "login.history": "Historia logowań",
    "login.history.empty": "Brak zarejestrowanych logowań.",
    "notif.dismiss": "Zamknij",
    "notif.locked": "Wiadomość banku",
    "tracking.error.not_found": "Numer śledzenia nie znaleziony. Sprawdź i spróbuj ponownie.",
    "tracking.label": "Śledzenie",
    "tracking.status.delivered": "Dostarczono",
    "tracking.status.preparation": "W przygotowaniu",
    "tracking.status.shipped": "Wysłano",
    "tracking.status.transit": "W drodze",
    "transfer.code.copied": "Skopiowano",
    "transfer.code.copy": "Kopiuj",
    "transfer.code.received": "Twój kod walidacyjny",
    "dash.welcome": "Witaj",
    "common.delete": "Usuń",
    "cards.delete.title": "Usuń kartę",
    "cards.delete.confirm_msg": "Ta akcja jest nieodwracalna. Karta zostanie trwale usunięta.",
    "cards.delete.physical_tooltip": "Karta fizyczna zamówiona — usunięcie niemożliwe",
    "cards.order.confirmed": "Zamówienie potwierdzone!",
    "cards.order.confirmed_body": "Twoja fizyczna karta jest przygotowywana. Użyj tego numeru do śledzenia dostawy.",
    "cards.order.tracking_no": "Nr śledzenia",
    "cards.fee_deducted": "{amount} zostanie potrącone z salda",
    "cards.insufficient_short": "Niewystarczające saldo ({available} dostępne)",
    "transfer.section.beneficiary": "Odbiorca",
    "transfer.section.details": "Przelew",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bank",
    "transfer.field.city": "Miasto",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bank:",
    "transfer.progress.paused": "Oczekuje na walidację",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Zmienna",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Karta usunięta",
    "cards.feat.gold_exclusive": "Ekskluzywna złota karta",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Ochrona kupującego",
    "cards.feat.support_standard": "Standardowe wsparcie",
    "cards.feat.plastic_premium": "Plastikowa karta premium",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Standardowe ubezpieczenie podróżne",
    "cards.feat.support_priority": "Wsparcie priorytetowe",
    "cards.feat.metal_brushed": "Karta z metalu szczotkowanego",
    "cards.feat.cashback_2_unlimited": "Nieograniczony cashback 2%",
    "cards.feat.travel_premium": "Premium ubezpieczenie podróżne",
    "cards.feat.vip_lounge": "Dostęp do salonów VIP",
    "cards.feat.support_247": "Pomoc 24/7",
    "auth.demo.title": "Logowanie administratora",
    "auth.demo.subtitle": "Wprowadź dane administratora, aby zobaczyć demo",
    "credits.garantie.none": "Brak",
    "credits.garantie.mortgage": "Hipoteka na finansowanej nieruchomości",
    "credits.garantie.vehicle": "Finansowany pojazd",
    "credits.garantie.parental": "Poręczenie rodzicielskie lub bankowe",
    "credits.garantie.professional": "Aktywa zawodowe lub poręczenie osobiste",
    "cards.reveal.pin_prompt": "Wprowadź swój 4-cyfrowy kod PIN, aby wyświetlić informacje o karcie.",
    "cards.reveal.show": "Pokaż",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Dwa kody PIN nie pasują do siebie",
    "auth.pin.digit_hint": "4 cyfry",
  
  },
  cs: {
    "notif.transfer_code.title": "Ověřovací kód převodu",
    "notif.transfer_code.body": "Zkopírujte tento kód pro ověření převodu.",
    "notif.transfer_code.expires_in": "Zmizí za {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} měsíců",
    "credits.n_months": "{n} měsíců",
    "msg.read": "Přečteno",
    "msg.sent": "Odesláno",
    "msg.attach_aria": "Připojit soubor",
    "login.device.unknown": "Neznámý prohlížeč",
    "login.device.mobile": "Mobil",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Počítač",
    "login.recent": "Nedávné",
    "err.invalid_credentials": "Neplatné přihlašovací údaje",
    "err.bad_pin": "Nesprávný PIN",
    "err.account_blocked": "Účet zablokován",
    "err.bad_current_pw": "Aktuální heslo je nesprávné",
    "err.insufficient_funds": "Nedostatek prostředků, dobijte si účet.",
    "err.amount_above_savings": "Částka přesahuje dostupné úspory",
    "err.must_be_positive": "Hodnota musí být kladná",
    "err.invalid_format": "Neplatný formát",
    "err.email_taken": "E-mail již používán",
    "err.card_not_found": "Karta nenalezena",
    "err.physical_card_already_ordered": "Fyzická karta již objednána",
    "err.beneficiary_not_found": "Příjemce nenalezen",
    "err.invalid_code": "Neplatný kód",
    "err.no_transfer_code": "Žádný kód pro tento převod",
    "err.link_expired": "Odkaz neplatný nebo vypršel",
    "err.thread_not_found": "Konverzace nenalezena",
    "err.empty_message": "Prázdná zpráva",
    "err.file_too_large": "Soubor je příliš velký (max 20 MB)",
    "err.forbidden": "Přístup odepřen",
    "err.bank_not_found": "Banka nenalezena",
    "err.signups_closed": "Registrace pro tuto banku jsou uzavřeny.",
    "err.access_suspended": "Přístup dočasně pozastaven. Kontaktujte prosím svou banku.",
    "err.notif_not_closable": "Toto oznámení nelze zavřít.",
    "auth.signup.tagline": "Připojte se k nám ještě dnes",
    "auth.login.tagline": "Vítejte zpět!",
    "auth.signup.tagline_sub": "Otevřete si účet za 2 minuty a získejte přístup ke všem našim prémiovým bankovním službám.",
    "auth.login.tagline_sub": "Přihlaste se ke svému osobnímu prostoru a spravujte své finance v bezpečí.",
    "auth.back": "Zpět na úvodní stránku",
    "auth.bullet.security": "Bankovní zabezpečení vojenské třídy",
    "auth.bullet.transfers": "Okamžité převody 24/7",
    "auth.bullet.investments": "Integrované investice a spoření",
    "auth.bullet.advisor": "Osobní poradce dostupný 7 dní v týdnu",
    "auth.footer": "Technologie {bank}",
    "auth.signup.title": "Vytvořit účet",
    "auth.login.title": "Přihlášení",
    "auth.field.lastName": "Příjmení",
    "auth.field.firstName": "Jméno",
    "auth.field.country": "Země",
    "auth.field.country.placeholder": "— Vyberte zemi —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Adresa",
    "auth.field.address.placeholder": "Václavské náměstí 123, Praha",
    "auth.field.email": "E-mail",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Heslo",
    "auth.submit.signup": "Vytvořit můj účet",
    "auth.submit.login": "Přihlásit se",
    "auth.login.badge": "Přihlaste se ke svému účtu",
    "auth.card.tagline": "Váš zákaznický prostor",
    "auth.submit.loading": "Načítání…",
    "auth.pin.title": "Kód PIN",
    "auth.pin.subtitle": "Zadejte svůj 4místný kód PIN",
    "auth.pin.label": "Kód PIN",
    "auth.pin.confirm": "Potvrdit PIN kód",
    "auth.pin.submit": "Potvrdit",
    "auth.pin.back": "Zpět na přihlášení",
    "auth.pin.length_error": "Kód PIN musí mít 4 číslice",
    "auth.switch.have_account": "Již máte účet?",
    "auth.switch.login_link": "Přihlášení",
    "auth.switch.no_account": "Ještě nejste klientem?",
    "auth.error.select_country": "Vyberte svou zemi",
    "kyc.title": "Ověření totožnosti (KYC)",
    "kyc.sub": "Nahrajte své dokumenty pro ověření účtu. Všechny dokumenty jsou šifrované a důvěrné.",
    "kyc.id_type": "Typ dokladu totožnosti *",
    "kyc.id_card": "Občanský průkaz",
    "kyc.passport": "Pas",
    "kyc.driver_license": "Řidičský průkaz",
    "kyc.recto_passport": "Přední strana pasu *",
    "kyc.recto_id": "Přední strana dokladu *",
    "kyc.verso": "Zadní strana dokladu *",
    "kyc.selfie": "Selfie s dokladem v ruce *",
    "kyc.address_proof": "Doklad o bydlišti *",
    "kyc.hint_doc": "Obrázek nebo PDF, max. 5 MB",
    "kyc.hint_selfie": "Jasná fotografie, obličej a doklad viditelné",
    "kyc.hint_address": "Faktura, výpis, potvrzení… (max. 5 MB)",
    "kyc.upload": "Nahrát",
    "kyc.replace": "Nahradit",
    "pending.title": "Účet čeká na aktivaci",
    "pending.body": "Vaše žádost byla přijata. Správce ověří vaše dokumenty a brzy aktivuje váš účet.",
    "pending.back": "Zpět na přihlášení",
    "home.nav.features": "Funkce",
    "home.nav.security": "Zabezpečení",
    "home.nav.invest": "Investice",
    "home.nav.credits": "Úvěry",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Přihlásit se",
    "home.hero.tagline": "Internetové bankovnictví nové generace",
    "home.hero.title1": "Spravujte své peníze",
    "home.hero.title2": "s inteligencí",
    "home.hero.sub": "Kompletní bankovní platforma — převody, spoření, investice a úvěry z vašeho smartphonu.",
    "home.hero.login": "Přihlásit se",
    "home.hero.trust.secure": "100% Bezpečné",
    "home.hero.trust.fast": "Rychlé otevření",
    "home.hero.trust.encrypted": "Šifrovaná data",
    "home.hero.trust.support": "Služba 24/7",
    "home.mockup.balance": "Dostupný zůstatek",
    "home.mockup.currency": "EUR · Hlavní účet",
    "home.mockup.recharge": "Dobít",
    "home.mockup.send": "Odeslat",
    "home.mockup.save": "Spořit",
    "home.mockup.salary": "Mzda březen",
    "home.mockup.rent": "Nájem duben",
    "home.mockup.investment": "Investice",
    "home.mockup.days2": "před 2 dny",
    "home.mockup.days3": "před 3 dny",
    "home.mockup.days5": "před 5 dny",
    "home.stats.accounts": "Aktivní účty",
    "home.stats.uptime": "Dostupnost",
    "home.stats.transfers": "Převody",
    "home.stats.support": "Podpora",
    "home.features.label": "Funkce",
    "home.features.title": "Vše, co potřebujete",
    "home.features.sub": "Kompletní platforma navržená pro zjednodušení správy vašich financí každý den.",
    "home.feature.security": "Vojenské zabezpečení",
    "home.feature.security_body": "Šifrování AES-256, dvoufaktorové ověřování a nepřetržitý monitoring účtu.",
    "home.feature.cards": "Virtuální a fyzické karty",
    "home.feature.cards_body": "Vydávejte karty během několika sekund nebo si objednejte fyzickou kartu s doručením domů.",
    "home.feature.transfers": "Expresní převody",
    "home.feature.transfers_body": "Posílejte a přijímejte peníze kdekoli na světě během několika sekund.",
    "home.feature.analytics": "Analýza v reálném čase",
    "home.feature.analytics_body": "Chytré přehledy pro sledování výdajů a plánování financí.",
    "home.feature.investments": "Integrované investice",
    "home.feature.investments_body": "Přistupujte k vybraným investičním produktům přímo z vašeho účtu.",
    "home.feature.support": "Dedikovaná podpora 24/7",
    "home.feature.support_body": "Osobní bankovní poradce dostupný kdykoli přes zabezpečené zprávy.",
    "home.tpl.label": "Důvěra",
    "home.tpl.title": "Důvěřují nám",
    "home.mockup.balance_label": "Zůstatek",
    "home.mockup.virement": "Převod",
    "home.mockup.salaire": "Mzda",
    "home.security.label": "Zabezpečení",
    "home.security.title1": "Vaše finance",
    "home.security.title2": "v absolutním bezpečí",
    "home.security.sub": "Uplatňujeme nejpřísnější standardy bankovního zabezpečení pro ochranu každého centu.",
    "home.security.aes.title": "Šifrování AES-256",
    "home.security.aes.body": "Všechna citlivá data jsou šifrována stejnými algoritmy, které používají velké mezinárodní banky.",
    "home.security.auth.title": "Posílené ověřování",
    "home.security.auth.body": "Dvojitá ověření totožnosti při každém přihlášení a každé citlivé operaci na vašem účtu.",
    "home.security.compliance.title": "Soulad PSD2/GDPR",
    "home.security.compliance.body": "Dodržujeme všechna evropská nařízení v oblasti platebních služeb a ochrany dat.",
    "home.invest.label": "Investice",
    "home.invest.title": "Nechte své peníze růst",
    "home.invest.sub": "6 investičních kategorií vybraných pro jejich potenciál a spolehlivost.",
    "home.invest.yield": "Výnos",
    "home.credits.label": "Úvěry",
    "home.credits.title": "Řešení pro každý projekt",
    "home.credits.sub": "Konkurenční sazby, rychlé odpovědi a personalizovaná podpora pro všechny vaše projekty.",
    "home.credits.rate": "Sazba",
    "home.faq.label": "FAQ",
    "home.faq.title": "Často kladené otázky",
    "home.faq.q1": "Jak si otevřu účet?",
    "home.faq.a1": "Registrace trvá méně než 2 minuty. Stačí vyplnit osobní údaje, zvolit heslo a váš účet bude aktivován po ověření naším týmem.",
    "home.faq.q2": "Jsou moje data v bezpečí?",
    "home.faq.a2": "Ano. Všechna data jsou šifrována pomocí AES-256 v klidu a TLS 1.3 při přenosu. Dodržujeme mezinárodní bankovní standardy a nařízení GDPR/PSD2.",
    "home.faq.q3": "Jaké typy investic nabízíte?",
    "home.faq.a3": "Nabízíme 6 kategorií: Nemovitosti, Crowdfunding, Obnovitelná energie, Burza, Participativní a Krypto. Každý produkt je vybírán podle poměru rizika a výnosu.",
    "home.faq.q4": "Jak požádat o úvěr?",
    "home.faq.a4": "Přejděte do sekce Úvěr ve svém klientském prostoru, vyberte typ půjčky, zadejte částku a dobu splatnosti. Vaše žádost je zpracována do 24 hodin našimi poradci.",
    "home.faq.q5": "Existují skryté poplatky?",
    "home.faq.a5": "Žádné skryté poplatky. Všechny sazby jsou jasně zobrazeny při každé operaci. Podmínky jsou dostupné ve vašem klientském prostoru kdykoli.",
    "home.faq.q6": "Jak kontaktovat podporu?",
    "home.faq.a6": "Použijte integrované zasílání zpráv ve svém klientském prostoru pro přímý kontakt s osobním bankovním poradcem, dostupným 24 hodin denně 7 dní v týdnu.",
    "home.cta.title": "Připraveni začít?",
    "home.cta.sub": "Připojte se k tisícům uživatelů, kteří spravují své finance s {bank}. Registrace trvá méně než 2 minuty.",
    "home.footer.products": "Produkty",
    "home.footer.services": "Služby",
    "home.footer.access": "Rychlý přístup",
    "home.footer.accounts": "Účty",
    "home.footer.transfers": "Převody",
    "home.footer.cards": "Karty",
    "home.footer.savings": "Spoření",
    "home.footer.investments": "Investice",
    "home.footer.credits": "Úvěry",
    "home.footer.financing": "Financování",
    "home.footer.messages": "Zprávy",
    "home.footer.login": "Přihlášení",
    "home.footer.signup": "Otevřít účet",
    "home.footer.support": "Podpora 24/7",
    "home.footer.tagline": "Vaše internetová banka dostupná všude a kdykoli.",
    "home.footer.copyright": "© {year} {bank} — Všechna práva vyhrazena",
    "home.footer.powered": "Technologie {bank}",
    "app.menu_label": "Hlavní nabídka",
    "app.nav.dashboard": "Přehled",
    "app.nav.accounts": "Účty",
    "app.nav.transactions": "Transakce",
    "app.nav.transfers": "Převody",
    "app.nav.cards": "Karty",
    "app.nav.savings": "Spoření",
    "app.nav.investments": "Investice",
    "app.nav.credit": "Úvěr",
    "app.nav.financing": "Financování",
    "app.nav.messages": "Zprávy",
    "app.nav.card_tracking": "Sledování karty",
    "app.nav.notifications": "Oznámení",
    "app.nav.settings": "Nastavení",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Domů",
    "app.nav.mobile.transfers": "Převody",
    "app.nav.mobile.cards": "Karty",
    "app.nav.mobile.transactions": "Transakce",
    "app.nav.more.invest": "Investovat",
    "app.user_role": "Klient",
    "app.logout": "Odhlásit se",
    "app.search": "Hledat…",
    "app.more_title": "Více funkcí",
    "app.more_btn": "Více",
    "app.loading": "Načítání…",
    "app.error_back": "Zpět na přihlášení",
    "app.see_more_notifs": "Zobrazit {count} dalších oznámení →",
    "page.accounts": "Moje účty",
    "page.transactions": "Transakce",
    "page.transfers": "Nový převod",
    "page.deposit": "Vklad",
    "page.cards": "Moje karty",
    "page.savings": "Spoření",
    "page.investments": "Investice",
    "page.credits": "Úvěr",
    "page.financing": "Financování projektů",
    "page.messages": "Zprávy",
    "page.settings": "Nastavení",
    "page.notifications": "Důležitá oznámení",
    "page.profile": "Můj profil",
    "dash.greeting": "Dobrý den, {name}",
    "dash.overview": "Přehled vašich financí — {date}",
    "dash.total_balance": "Celkový zůstatek",
    "dash.this_month": "+{amount} tento měsíc",
    "dash.view_iban": "Zobrazit můj IBAN",
    "dash.my_accounts": "Moje účty",
    "dash.primary_account": "Hlavní účet",
    "dash.savings_account": "Spořicí účet",
    "dash.remunerated": "Úročený",
    "dash.details": "Podrobnosti",
    "dash.transfer_btn": "Převod",
    "dash.quick_actions": "Rychlé akce",
    "dash.quick.deposit": "Vklad",
    "deposit.title": "Informace o vkladu",
    "dash.quick.transfer": "Převod",
    "dash.quick.history": "Historie",
    "dash.quick.cards": "Moje karty",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Měsíční rozpočet",
    "dash.income": "Příjmy",
    "dash.expenses": "Výdaje",
    "dash.spending_cat": "Výdaje podle kategorie",
    "dash.no_spending": "Žádné zaznamenané výdaje.",
    "dash.recent_tx": "Nedávné transakce",
    "dash.see_all": "Zobrazit vše",
    "dash.no_tx": "Žádné operace.",
    "dash.main_card": "Moje hlavní karta",
    "dash.manage": "Spravovat",
    "dash.block": "Zablokovat",
    "dash.pin": "Kód PIN",
    "dash.security": "Zabezpečení",
    "dash.more": "Více",
    "dash.no_card": "Žádná karta nevydána",
    "dash.monthly_activity": "Měsíční aktivita",
    "dash.operations": "Operace",
    "dash.holder": "Držitel",
    "dash.expires": "Platnost",
    "dash.visa_label": "VISA",
    "dash.balance": "Dostupný zůstatek",
    "tx.all": "Všechny",
    "tx.income": "Příjmy",
    "tx.expenses": "Výdaje",
    "tx.today": "Dnes",
    "tx.yesterday": "Včera",
    "tx.none": "Žádné transakce.",
    "transfer.title": "Rychlý převod",
    "transfer.recipient": "Příjemce",
    "transfer.recipient_ph": "Jméno, IBAN, telefonní číslo…",
    "transfer.amount": "Částka",
    "transfer.label": "Popis",
    "transfer.reason": "Důvod (volitelné)",
    "transfer.reason_ph": "Např.: nájem, náhrada…",
    "transfer.execution": "Provedení",
    "transfer.immediate": "Okamžitě",
    "transfer.delayed": "Odloženo",
    "transfer.balance": "Dostupný zůstatek:",
    "transfer.continue": "Pokračovat",
    "transfer.confirm": "Potvrdit",
    "transfer.cancel": "Zrušit",
    "transfer.code": "Ověřovací kód",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Zadejte přijatý kód pro potvrzení převodu",
    "transfer.pending.title": "Převod čeká na ověření",
    "transfer.pending.amount": "Částka:",
    "transfer.pending.to": "Komu:",
    "transfer.pending.hint": "Váš ověřovací kód bude zaslán e-mailem nebo prostřednictvím zvonku upozornění. Kód se skládá z 6 číslic. Pokud ho neobdržíte, kontaktujte podporu.",
    "transfer.pending.validate": "Potvrdit převod",
    "transfer.saved": "Převod uložen. Požádejte správce o ověřovací kód.",
    "transfer.done": "Převod proveden",
    "transfer.validated": "Převod potvrzen!",
    "transfer.progress.processing": "Zpracovávání převodu…",
    "transfer.progress.completed": "Převod proveden!",
    "transfer.progress.default_message": "Váš převod se zpracovává. Prosím čekejte.",
    "cards.none": "Žádná karta nevydána.\nKontaktujte svého správce.",
    "cards.holder": "Držitel",
    "cards.expires": "Platnost",
    "cards.block": "Zablokovat",
    "cards.pin": "Kód PIN",
    "cards.more": "Více",
    "cards.limits": "Limity karty",
    "cards.payment": "Platba",
    "cards.withdrawal": "Výběr",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Zablokována",
    "cards.status_active": "Aktivní",
    "cards.toast.blocked": "Karta zablokována",
    "cards.toast.unblocked": "Karta odblokována",
    "cards.toast.physical_ordered": "Fyzická karta objednána! Doručení do 5–10 pracovních dní.",
    "cards.toast.created": "Karta úspěšně vygenerována!",
    "cards.none_sub": "Žádná karta nevydána. Vygenerujte svou virtuální kartu nyní.",
    "cards.generate_btn": "Vygenerovat mou kartu",
    "cards.create.title": "Vygenerovat mou virtuální kartu",
    "cards.create.network": "Platební síť",
    "cards.create.color": "Barva karty",
    "cards.create.preview_name": "VAŠE JMÉNO",
    "cards.create.preview_expiry": "MM/RR",
    "cards.create.loading": "Generování…",
    "cards.create.submit": "Vygenerovat kartu",
    "cards.btn.unblock": "Odblokovat",
    "cards.btn.block": "Zablokovat",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fyzická",
    "cards.btn.physical_done": "Objednána",
    "cards.limits_title": "Limity",
    "cards.reveal.title": "Informace o kartě",
    "cards.reveal.number": "Celé číslo",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Platnost",
    "cards.reveal.warning": "Nikdy tyto informace nesdílejte. Zavřete toto okno po použití.",
    "cards.pin.title": "Kód PIN",
    "cards.pin.message": "Tento kód PIN se používá pro všechny vaše online platby a výběry. Nikomu jej nikdy nesdělujte.",
    "cards.physical.title": "Objednat fyzickou kartu",
    "cards.physical.fee_label": "Poplatek za doručení",
    "cards.physical.balance_label": "Váš zůstatek",
    "cards.physical.insufficient": "Nedostatečný zůstatek. Před objednáním prosím dobijte svůj účet.",
    "cards.physical.info": "Poplatek bude stržen z vašeho účtu. Doručení do 5–10 pracovních dní.",
    "cards.physical.order_btn": "Objednat ({fee})",
    "cards.physical.ordering": "Objednávám…",
    "cards.physical.order_cta": "Objednat fyzickou kartu",
    "cards.physical.choose_type": "Vyberte si svou kartu",
    "cards.physical.features": "Výhody",
    "cards.buy_virtual_cta": "Koupit virtuální kartu",
    "notif.none": "Zatím žádná oznámení.",
    "notif.contact_support": "Kontaktovat podporu",
    "profile.change_photo": "Změnit fotografii",
    "profile.remove_photo": "Odebrat fotografii",
    "profile.email": "E-mail",
    "profile.phone": "Telefon",
    "profile.country": "Země",
    "profile.address": "Adresa",
    "profile.notifs_title": "Oznámení ({count})",
    "profile.no_notifs": "Žádná oznámení.",
    "profile.logout": "Odhlásit se",
    "profile.photo_updated": "Profilová fotografie aktualizována",
    "profile.photo_removed": "Fotografie odebrána",
    "profile.photo_too_large": "Obrázek příliš velký (max. 1 MB)",
    "profile.powered": "Technologie {bank}",
    "iban.title": "Můj IBAN",
    "iban.loading": "Načítání…",
    "iban.bic": "Kód BIC / SWIFT",
    "iban.share_info": "Sdílejte tyto údaje pro příjem převodu na váš účet.",
    "iban.copy": "Kopírovat",
    "iban.copied": "Zkopírováno do schránky",
    "iban.copy_fail": "Nelze kopírovat",
    "iban.close": "Zavřít",
    "accounts.all": "Všechny vaše účty u {bank}.",
    "accounts.primary": "Hlavní účet",
    "accounts.savings": "Spořicí účet",
    "accounts.primary_tag": "Hlavní",
    "accounts.secondary_tag": "Vedlejší",
    "accounts.objective": "Cíl: {amount} · {pct}%",
    "accounts.copy": "Kopírovat",
    "accounts.loading": "Načítání…",
    "accounts.iban_copied": "IBAN zkopírován",
    "accounts.copy_fail": "Nelze kopírovat",
    "savings.total": "Celkové úspory",
    "savings.target_total": "Celkový cíl",
    "savings.progress": "Průběh",
    "savings.active": "Aktivní cíle",
    "savings.new_goal": "Nový cíl",
    "savings.none": "Zatím žádné spořicí cíle.",
    "savings.none_sub": "Vytvořte první cíl a začněte.",
    "savings.saved": "Naspořeno:",
    "savings.goal": "Cíl:",
    "savings.deadline": "Termín: {date}",
    "savings.deposit": "Vložit",
    "savings.withdraw": "Vybrat",
    "savings.loading": "Načítání…",
    "savings.form.title": "Nový spořicí cíl",
    "savings.form.cancel": "Zrušit",
    "savings.form.create": "Vytvořit",
    "savings.form.name": "Název cíle",
    "savings.form.name_ph": "Japonsko, Auto, …",
    "savings.form.target": "Cílová částka",
    "savings.form.initial": "Počáteční vklad",
    "savings.form.deadline": "Termín (volitelné)",
    "savings.form.color": "Barva",
    "savings.deposit_title": "Vložit na \"{name}\"",
    "savings.deposit_amount": "Částka k vložení",
    "savings.balance_hint": "Hlavní zůstatek: {amount}",
    "savings.withdraw_prompt": "Kolik vybrat z \"{name}\"?",
    "savings.close_confirm": "Uzavřít cíl \"{name}\"? Úspory budou vráceny na hlavní účet.",
    "savings.toast.created": "Cíl vytvořen",
    "savings.toast.deposited": "Vklad proveden",
    "savings.toast.withdrawn": "Výběr proveden",
    "savings.toast.closed": "Cíl uzavřen",
    "invest.total": "Celkové investice",
    "invest.profits": "Kumulované zisky",
    "invest.count": "Investice",
    "invest.products": "Dostupné produkty",
    "invest.invest_btn": "Investovat",
    "invest.my": "Moje investice",
    "invest.pending": "Čekající",
    "invest.validated": "Schválené",
    "invest.refused": "Zamítnuté",
    "invest.withdraw.btn": "Vybrat",
    "invest.withdraw.title": "Vybrat moji investici",
    "invest.withdraw.intro": "Chystáte se vybrat svou investici „{product}\". Počáteční kapitál a naběhlý zisk budou připsány na váš hlavní zůstatek.",
    "invest.withdraw.principal": "Počáteční kapitál",
    "invest.withdraw.profit": "Zisk",
    "invest.withdraw.total": "Celkem k obdržení",
    "invest.withdraw.confirm": "Potvrdit výběr",
    "invest.withdraw.warning": "Tuto akci nelze vrátit zpět. Investice bude uzavřena.",
    "invest.withdraw.success": "Investice vybrána — {amount} připsáno na zůstatek.",
    "invest.withdrawn": "Vybráno",
    "invest.cta_sub": "Vyberte typ investice, který vám vyhovuje. Poradce vás bude doprovázet od první žádosti.",
    "invest.form.amount": "Částka k investování",
    "invest.form.balance": "Dostupný zůstatek: {amount}",
    "invest.form.note": "Poznámka pro poradce (volitelné)",
    "invest.form.note_ph": "Upřesněte svá očekávání…",
    "invest.form.advisor_info": "Konverzace s vaším poradcem bude automaticky otevřena.",
    "invest.form.cancel": "Zrušit",
    "invest.form.submit": "Odeslat žádost",
    "invest.toast.requested": "Žádost zaregistrována — konverzace s vaším poradcem byla otevřena.",
    "invest.p1.name": "Nemovitosti",
    "invest.p1.desc": "SCPI, papírové nemovitosti, realitní crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Participativní financování vybraných projektů",
    "invest.p3.name": "Obnovitelná energie",
    "invest.p3.desc": "Solární, větrné a zelené projekty",
    "invest.p4.name": "Burza",
    "invest.p4.desc": "Akcie, ETF a diverzifikované fondy",
    "invest.p5.name": "Participativní",
    "invest.p5.desc": "Půjčky malým a středním podnikům a místním podnikatelům",
    "invest.p6.name": "Krypto",
    "invest.p6.desc": "Bitcoin, Ethereum a víceměnová peněženka",
    "msg.online": "Online",
    "msg.loading": "Načítání…",
    "msg.none": "Žádné zprávy. Zahajte konverzaci s vaším poradcem.",
    "msg.placeholder": "Napište zprávu…",
    "msg.attach.image": "Obrázek",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Soubor / Dokument",
    "msg.pdf_doc": "Dokument PDF",
    "msg.file": "Soubor",
    "msg.too_large": "Soubor příliš velký (max. {size}).",
    "msg.withdraw": "Smazat",
    "credits.cta_sub": "Požádejte přímo online — odpověď do 24 hodin.",
    "credits.learn_more": "Zjistit více",
    "credits.request": "Požádat",
    "credits.history": "Moje žádosti",
    "credits.status.pending": "Čekající",
    "credits.status.accepted": "Přijatá",
    "credits.status.refused": "Zamítnutá",
    "credits.chars": "Charakteristika",
    "credits.amount_range": "Částka: {min}€ – {max}€",
    "credits.rate_range": "Úroková sazba: {min}% – {max}% ročně",
    "credits.duration_range": "Doba: {min} až {max} měsíců",
    "credits.deposit_min": "Počáteční vklad: {amount}€ minimum",
    "credits.garantie": "Záruka: {garantie}",
    "credits.eligibility": "Podmínky způsobilosti",
    "credits.elig.income": "Doložit pravidelné a stabilní příjmy",
    "credits.elig.deposit": "Provést počáteční vklad ve výši {amount}€",
    "credits.elig.garantie": "Předložit záruku: {garantie}",
    "credits.form.title": "Žádost — {name}",
    "credits.form.cancel": "Zrušit",
    "credits.form.submit": "Odeslat žádost",
    "credits.form.amount": "Požadovaná částka (€)",
    "credits.form.duration": "Doba (měsíce)",
    "credits.form.reason": "Důvod (volitelné)",
    "credits.form.reason_ph": "Popište svůj projekt…",
    "credits.form.close": "Zavřít",
    "credits.toast.submitted": "Žádost o úvěr odeslána — odpověď obdržíte do 24 hodin.",
    "credits.p1.name": "Osobní půjčka",
    "credits.p1.desc": "Na financování každodenních potřeb nebo osobních projektů.",
    "credits.p2.name": "Hypoteční úvěr",
    "credits.p2.desc": "Na financování koupě nebo výstavby nemovitosti.",
    "credits.p3.name": "Půjčka na auto / motocykl",
    "credits.p3.desc": "Kupte vozidlo v klidu.",
    "credits.p4.name": "Studentská půjčka",
    "credits.p4.desc": "Finanční pomoc pro vysokoškolské studium.",
    "credits.p5.name": "Podnikatelský úvěr",
    "credits.p5.desc": "Na podporu růstu profesní činnosti.",
    "credits.p6.name": "Expresní půjčka",
    "credits.p6.desc": "Rychlé řešení pro naléhavé potřeby.",
    "financing.reimbursable": "Návratné financování",
    "financing.reimbursable_desc": "Finanční podpora s pevnými podmínkami pro zahájení nebo rozvoj činnosti.",
    "financing.non_reimbursable": "Nenávratné financování",
    "financing.non_reimbursable_desc": "Pro projekty se sociálním, vzdělávacím nebo humanitárním dopadem (NGO, sdružení).",
    "financing.cta_sub": "Řešení na míru pro uskutečnění vašich ambicí.",
    "financing.request": "Požádat o financování",
    "financing.history": "Moje žádosti",
    "financing.reimbursable_label": "Návratné",
    "financing.non_reimbursable_label": "Nenávratné",
    "financing.status.pending": "Čekající",
    "financing.status.granted": "Přiznané",
    "financing.status.refused": "Zamítnuté",
    "financing.form.title": "Žádost — {name}",
    "financing.form.cancel": "Zrušit",
    "financing.form.submit": "Odeslat žádost",
    "financing.form.project": "Název projektu",
    "financing.form.project_ph": "Můj projekt",
    "financing.form.amount": "Požadovaná částka (€)",
    "financing.form.sector": "Sektor",
    "financing.form.sector_ph": "Technologie, zemědělství, …",
    "financing.form.description": "Popis projektu",
    "financing.form.description_ph": "Představte svůj projekt, jeho cíle a očekávaný dopad…",
    "settings.security": "Zabezpečení",
    "settings.current_pw": "Aktuální heslo",
    "settings.new_pw": "Nové heslo",
    "settings.confirm_pw": "Potvrdit",
    "settings.submit": "Změnit heslo",
    "settings.no_match": "Hesla se neshodují",
    "settings.changed": "Heslo změněno",
    "common.cancel": "Zrušit",
    "common.confirm": "Potvrdit",
    "common.close": "Zavřít",
    "common.save": "Uložit",
    "common.loading": "Načítání…",
    "common.send": "Odeslat",
    "common.total": "Celkem",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Odh. měsíční splátka",
    "credits.rate_annual": "/ rok",
    "credits.rate_used": "Odh. sazba:",
    "credits.simulation": "Simulace měsíční splátky",
    "credits.total_cost": "Odh. celkové náklady",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Vítejte",
    "common.delete": "Smazat",
    "cards.delete.title": "Smazat kartu",
    "cards.delete.confirm_msg": "Tato akce je nevratná. Karta bude trvale smazána.",
    "cards.delete.physical_tooltip": "Fyzická karta objednána — smazání nemožné",
    "cards.order.confirmed": "Objednávka potvrzena!",
    "cards.order.confirmed_body": "Vaše fyzická karta se připravuje. Použijte toto číslo ke sledování zásilky.",
    "cards.order.tracking_no": "Č. sledování",
    "cards.fee_deducted": "{amount} bude odečteno z vašeho zůstatku",
    "cards.insufficient_short": "Nedostatečný zůstatek ({available} dostupné)",
    "transfer.section.beneficiary": "Příjemce",
    "transfer.section.details": "Převod",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banka",
    "transfer.field.city": "Město",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banka:",
    "transfer.progress.paused": "Čeká na ověření",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Variabilní",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Karta smazána",
    "cards.feat.gold_exclusive": "Exkluzivní zlatá karta",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Ochrana kupujícího",
    "cards.feat.support_standard": "Standardní podpora",
    "cards.feat.plastic_premium": "Plastová karta premium",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Standardní cestovní pojištění",
    "cards.feat.support_priority": "Prioritní podpora",
    "cards.feat.metal_brushed": "Karta z kartáčovaného kovu",
    "cards.feat.cashback_2_unlimited": "Neomezený cashback 2%",
    "cards.feat.travel_premium": "Premium cestovní pojištění",
    "cards.feat.vip_lounge": "Přístup do VIP salónků",
    "cards.feat.support_247": "Asistence 24/7",
    "auth.demo.title": "Přihlášení správce",
    "auth.demo.subtitle": "Zadejte své přihlašovací údaje správce pro náhled dema",
    "credits.garantie.none": "Žádná",
    "credits.garantie.mortgage": "Hypotéka na financovanou nemovitost",
    "credits.garantie.vehicle": "Financované vozidlo",
    "credits.garantie.parental": "Rodičovská nebo bankovní",
    "credits.garantie.professional": "Profesionální aktiva nebo osobní záruka",
    "cards.reveal.pin_prompt": "Zadejte svůj 4místný PIN kód pro zobrazení informací o kartě.",
    "cards.reveal.show": "Zobrazit",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Dva kódy PIN se neshodují",
    "auth.pin.digit_hint": "4 číslice",
  
  },
  sk: {
    "notif.transfer_code.title": "Overovací kód prevodu",
    "notif.transfer_code.body": "Skopírujte tento kód na overenie prevodu.",
    "notif.transfer_code.expires_in": "Zmizne o {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mesiacov",
    "credits.n_months": "{n} mesiacov",
    "msg.read": "Prečítané",
    "msg.sent": "Odoslané",
    "msg.attach_aria": "Pripojiť súbor",
    "login.device.unknown": "Neznámy prehliadač",
    "login.device.mobile": "Mobil",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Počítač",
    "login.recent": "Nedávne",
    "err.invalid_credentials": "Neplatné prihlasovacie údaje",
    "err.bad_pin": "Nesprávny PIN",
    "err.account_blocked": "Účet zablokovaný",
    "err.bad_current_pw": "Aktuálne heslo je nesprávne",
    "err.insufficient_funds": "Nedostatok prostriedkov, dobite si účet.",
    "err.amount_above_savings": "Suma presahuje dostupné úspory",
    "err.must_be_positive": "Hodnota musí byť kladná",
    "err.invalid_format": "Neplatný formát",
    "err.email_taken": "E-mail už používaný",
    "err.card_not_found": "Karta nenájdená",
    "err.physical_card_already_ordered": "Fyzická karta už objednaná",
    "err.beneficiary_not_found": "Príjemca nenájdený",
    "err.invalid_code": "Neplatný kód",
    "err.no_transfer_code": "Žiadny kód pre tento prevod",
    "err.link_expired": "Odkaz neplatný alebo expirovaný",
    "err.thread_not_found": "Konverzácia nenájdená",
    "err.empty_message": "Prázdna správa",
    "err.file_too_large": "Súbor je príliš veľký (max 20 MB)",
    "err.forbidden": "Prístup zamietnutý",
    "err.bank_not_found": "Banka nenájdená",
    "err.signups_closed": "Registrácie pre túto banku sú uzavreté.",
    "err.access_suspended": "Prístup dočasne pozastavený. Kontaktujte prosím svoju banku.",
    "err.notif_not_closable": "Toto upozornenie sa nedá zatvoriť.",
    "auth.signup.tagline": "Pridajte sa k nám dnes",
    "auth.login.tagline": "Vitajte späť!",
    "auth.signup.tagline_sub": "Otvorte si účet za 2 minúty a získajte prístup ku všetkým našim prémiových bankovým službám.",
    "auth.login.tagline_sub": "Prihláste sa do svojho osobného priestoru a spravujte svoje financie v bezpečí.",
    "auth.back": "Späť na hlavnú stránku",
    "auth.bullet.security": "Banková bezpečnosť vojenskej úrovne",
    "auth.bullet.transfers": "Okamžité prevody 24/7",
    "auth.bullet.investments": "Integrované investície a sporenie",
    "auth.bullet.advisor": "Osobný poradca dostupný 7 dní v týždni",
    "auth.footer": "Powered by {bank}",
    "auth.signup.title": "Vytvoriť účet",
    "auth.login.title": "Prihlásenie",
    "auth.field.lastName": "Priezvisko",
    "auth.field.firstName": "Meno",
    "auth.field.country": "Krajina",
    "auth.field.country.placeholder": "— Vyberte svoju krajinu —",
    "auth.field.phone": "Telefón",
    "auth.field.address": "Adresa",
    "auth.field.address.placeholder": "Hlavná ulica 123, Bratislava",
    "auth.field.email": "E-mail",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Heslo",
    "auth.submit.signup": "Vytvoriť môj účet",
    "auth.submit.login": "Prihlásiť sa",
    "auth.login.badge": "Prihláste sa do svojho účtu",
    "auth.card.tagline": "Vaša zákaznícka zóna",
    "auth.submit.loading": "Načítavanie…",
    "auth.pin.title": "Kód PIN",
    "auth.pin.subtitle": "Zadajte svoj 4-miestny kód PIN",
    "auth.pin.label": "Kód PIN",
    "auth.pin.confirm": "Potvrdiť PIN kód",
    "auth.pin.submit": "Potvrdiť",
    "auth.pin.back": "Späť na prihlásenie",
    "auth.pin.length_error": "Kód PIN musí mať 4 číslice",
    "auth.switch.have_account": "Už máte účet?",
    "auth.switch.login_link": "Prihlásenie",
    "auth.switch.no_account": "Ešte nie ste klientom?",
    "auth.error.select_country": "Vyberte svoju krajinu",
    "kyc.title": "Overenie totožnosti (KYC)",
    "kyc.sub": "Nahrajte svoje dokumenty na overenie účtu. Všetky dokumenty sú šifrované a dôverné.",
    "kyc.id_type": "Typ dokladu totožnosti *",
    "kyc.id_card": "Občiansky preukaz",
    "kyc.passport": "Cestovný pas",
    "kyc.driver_license": "Vodičský preukaz",
    "kyc.recto_passport": "Predná strana pasu *",
    "kyc.recto_id": "Predná strana dokladu *",
    "kyc.verso": "Zadná strana dokladu *",
    "kyc.selfie": "Selfie s dokladom totožnosti v ruke *",
    "kyc.address_proof": "Doklad o adrese *",
    "kyc.hint_doc": "Obrázok alebo PDF, max. 5 MB",
    "kyc.hint_selfie": "Jasná fotografia, tvár a doklad viditeľné",
    "kyc.hint_address": "Faktúra, výpis, potvrdenie… (max. 5 MB)",
    "kyc.upload": "Nahrať",
    "kyc.replace": "Nahradiť",
    "pending.title": "Účet čaká na schválenie",
    "pending.body": "Vaša žiadosť bola prijatá. Správca overí váš spis a čoskoro aktivuje váš účet.",
    "pending.back": "Späť na prihlásenie",
    "home.nav.features": "Funkcie",
    "home.nav.security": "Bezpečnosť",
    "home.nav.invest": "Investície",
    "home.nav.credits": "Úvery",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Prihlásiť sa",
    "home.hero.tagline": "Online bankovníctvo novej generácie",
    "home.hero.title1": "Spravujte svoje peniaze",
    "home.hero.title2": "s rozumom",
    "home.hero.sub": "Kompletná banková platforma — prevody, sporenie, investície a úvery z vášho smartfónu.",
    "home.hero.login": "Prihlásiť sa",
    "home.hero.trust.secure": "100% Bezpečné",
    "home.hero.trust.fast": "Rýchle otvorenie",
    "home.hero.trust.encrypted": "Šifrované dáta",
    "home.hero.trust.support": "Služba 24/7",
    "home.mockup.balance": "Dostupný zostatok",
    "home.mockup.currency": "EUR · Hlavný účet",
    "home.mockup.recharge": "Dobiť",
    "home.mockup.send": "Odoslať",
    "home.mockup.save": "Sporiť",
    "home.mockup.salary": "Plat – marec",
    "home.mockup.rent": "Nájom – apríl",
    "home.mockup.investment": "Investícia",
    "home.mockup.days2": "pred 2 dňami",
    "home.mockup.days3": "pred 3 dňami",
    "home.mockup.days5": "pred 5 dňami",
    "home.stats.accounts": "Aktívne účty",
    "home.stats.uptime": "Dostupnosť",
    "home.stats.transfers": "Prevody",
    "home.stats.support": "Podpora",
    "home.features.label": "Funkcie",
    "home.features.title": "Všetko, čo potrebujete",
    "home.features.sub": "Kompletná platforma navrhnutá na zjednodušenie správy vašich financií každý deň.",
    "home.feature.security": "Vojenská bezpečnosť",
    "home.feature.security_body": "Šifrovanie AES-256, dvojfaktorová autentifikácia a sledovanie účtu 24/7.",
    "home.feature.cards": "Virtuálna a fyzická karta",
    "home.feature.cards_body": "Vydajte karty za pár sekúnd alebo si objednajte skutočnú fyzickú kartu doručenú domov.",
    "home.feature.transfers": "Expresné prevody",
    "home.feature.transfers_body": "Odosielajte a prijímajte peniaze kdekoľvek na svete za pár sekúnd.",
    "home.feature.analytics": "Analýza v reálnom čase",
    "home.feature.analytics_body": "Inteligentné dashboardy na sledovanie výdavkov a predvídanie vašich financií.",
    "home.feature.investments": "Integrované investície",
    "home.feature.investments_body": "Prístup k vybraným investičným produktom priamo z vášho účtu.",
    "home.feature.support": "Dedikovaná podpora 24/7",
    "home.feature.support_body": "Osobný bankový poradca dostupný kedykoľvek prostredníctvom zabezpečenej správy.",
    "home.tpl.label": "Dôvera",
    "home.tpl.title": "Dôverujú nám",
    "home.mockup.balance_label": "Zostatok",
    "home.mockup.virement": "Prevod",
    "home.mockup.salaire": "Plat",
    "home.security.label": "Bezpečnosť",
    "home.security.title1": "Vaše financie",
    "home.security.title2": "v absolútnej bezpečnosti",
    "home.security.sub": "Uplatňujeme najprísnejšie štandardy bankovej bezpečnosti na ochranu každého centu.",
    "home.security.aes.title": "Šifrovanie AES-256",
    "home.security.aes.body": "Všetky citlivé dáta sú šifrované rovnakými algoritmami, aké používajú veľké medzinárodné banky.",
    "home.security.auth.title": "Posilnená autentifikácia",
    "home.security.auth.body": "Dvojité overenie totožnosti pri každom prihlásení a pri každej citlivej operácii na vašom účte.",
    "home.security.compliance.title": "Súlad s PSD2/GDPR",
    "home.security.compliance.body": "Dodržiavame všetky európske predpisy v oblasti platobných služieb a ochrany údajov.",
    "home.invest.label": "Investície",
    "home.invest.title": "Nechajte svoje peniaze rásť",
    "home.invest.sub": "6 investičných kategórií vybraných pre ich potenciál a spoľahlivosť.",
    "home.invest.yield": "Výnos",
    "home.credits.label": "Úvery",
    "home.credits.title": "Riešenia pre každý projekt",
    "home.credits.sub": "Konkurenčné sadzby, rýchle odpovede a personalizovaná podpora pre všetky vaše projekty.",
    "home.credits.rate": "Sadzba",
    "home.faq.label": "FAQ",
    "home.faq.title": "Časté otázky",
    "home.faq.q1": "Ako si otvoriť účet?",
    "home.faq.a1": "Registrácia trvá menej ako 2 minúty. Jednoducho vyplňte osobné údaje, zvoľte si heslo a váš účet bude aktivovaný po overení naším tímom.",
    "home.faq.q2": "Sú moje dáta v bezpečí?",
    "home.faq.a2": "Áno. Všetky dáta sú šifrované pomocou AES-256 v pokoji a TLS 1.3 pri prenose. Dodržiavame medzinárodné bankové štandardy a predpisy GDPR/PSD2.",
    "home.faq.q3": "Aké typy investícií ponúkate?",
    "home.faq.a3": "Ponúkame 6 kategórií: Nehnuteľnosti, Crowdfunding, Obnoviteľné energie, Burza, Participatívne a Krypto. Každý produkt je vybraný pre jeho pomer rizika a výnosu.",
    "home.faq.q4": "Ako požiadať o úver?",
    "home.faq.a4": "Prejdite do sekcie Úver vo svojom klientskom priestore, vyberte typ pôžičky, zadajte sumu a dobu splácania. Vaša žiadosť je spracovaná do 24 hodín našimi poradcami.",
    "home.faq.q5": "Existujú skryté poplatky?",
    "home.faq.a5": "Žiadne skryté poplatky. Všetky sadzby sú jasne zobrazené pri každej operácii. Podmienky sú kedykoľvek dostupné vo vašom klientskom priestore.",
    "home.faq.q6": "Ako kontaktovať podporu?",
    "home.faq.a6": "Využite integrovanú správu vo vašom klientskom priestore na priame kontaktovanie vášho osobného bankového poradcu, dostupného 24/7.",
    "home.cta.title": "Pripravení začať?",
    "home.cta.sub": "Pripojte sa k tisíckam používateľov, ktorí spravujú svoje financie s {bank}. Registrácia trvá menej ako 2 minúty.",
    "home.footer.products": "Produkty",
    "home.footer.services": "Služby",
    "home.footer.access": "Rýchly prístup",
    "home.footer.accounts": "Účty",
    "home.footer.transfers": "Prevody",
    "home.footer.cards": "Karty",
    "home.footer.savings": "Sporenie",
    "home.footer.investments": "Investície",
    "home.footer.credits": "Úvery",
    "home.footer.financing": "Financovanie",
    "home.footer.messages": "Správy",
    "home.footer.login": "Prihlásenie",
    "home.footer.signup": "Otvoriť účet",
    "home.footer.support": "Podpora 24/7",
    "home.footer.tagline": "Vaša online banka, dostupná všade a kedykoľvek.",
    "home.footer.copyright": "© {year} {bank} — Všetky práva vyhradené",
    "home.footer.powered": "Powered by {bank}",
    "app.menu_label": "Hlavné menu",
    "app.nav.dashboard": "Prehľad",
    "app.nav.accounts": "Účty",
    "app.nav.transactions": "Transakcie",
    "app.nav.transfers": "Prevody",
    "app.nav.cards": "Karty",
    "app.nav.savings": "Sporenie",
    "app.nav.investments": "Investície",
    "app.nav.credit": "Úver",
    "app.nav.financing": "Financovanie",
    "app.nav.messages": "Správy",
    "app.nav.card_tracking": "Sledovanie karty",
    "app.nav.notifications": "Upozornenia",
    "app.nav.settings": "Nastavenia",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Domov",
    "app.nav.mobile.transfers": "Prevody",
    "app.nav.mobile.cards": "Karty",
    "app.nav.mobile.transactions": "Transakcie",
    "app.nav.more.invest": "Investovať",
    "app.user_role": "Klient",
    "app.logout": "Odhlásiť sa",
    "app.search": "Hľadať…",
    "app.more_title": "Ďalšie funkcie",
    "app.more_btn": "Viac",
    "app.loading": "Načítavanie…",
    "app.error_back": "Späť na prihlásenie",
    "app.see_more_notifs": "Zobraziť {count} ďalších upozornení →",
    "page.accounts": "Moje účty",
    "page.transactions": "Transakcie",
    "page.transfers": "Nový prevod",
    "page.deposit": "Vklad",
    "page.cards": "Moje karty",
    "page.savings": "Sporenie",
    "page.investments": "Investície",
    "page.credits": "Úver",
    "page.financing": "Financovanie projektov",
    "page.messages": "Správy",
    "page.settings": "Nastavenia",
    "page.notifications": "Dôležité upozornenia",
    "page.profile": "Môj profil",
    "dash.greeting": "Dobrý deň, {name}",
    "dash.overview": "Prehľad vašich financií — {date}",
    "dash.total_balance": "Celkový zostatok",
    "dash.this_month": "+{amount} tento mesiac",
    "dash.view_iban": "Zobraziť môj IBAN",
    "dash.my_accounts": "Moje účty",
    "dash.primary_account": "Hlavný účet",
    "dash.savings_account": "Sporiaci účet",
    "dash.remunerated": "Úročený",
    "dash.details": "Detaily",
    "dash.transfer_btn": "Prevod",
    "dash.quick_actions": "Rýchle akcie",
    "dash.quick.deposit": "Vklad",
    "deposit.title": "Informácie o vklade",
    "dash.quick.transfer": "Prevod",
    "dash.quick.history": "História",
    "dash.quick.cards": "Moje karty",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Mesačný rozpočet",
    "dash.income": "Príjmy",
    "dash.expenses": "Výdavky",
    "dash.spending_cat": "Výdavky podľa kategórie",
    "dash.no_spending": "Žiadne výdavky zaznamenané.",
    "dash.recent_tx": "Posledné transakcie",
    "dash.see_all": "Zobraziť všetko",
    "dash.no_tx": "Žiadne operácie.",
    "dash.main_card": "Moja hlavná karta",
    "dash.manage": "Spravovať",
    "dash.block": "Zablokovať",
    "dash.pin": "PIN kód",
    "dash.security": "Bezpečnosť",
    "dash.more": "Viac",
    "dash.no_card": "Žiadna karta nevydaná",
    "dash.monthly_activity": "Mesačná aktivita",
    "dash.operations": "Operácie",
    "dash.holder": "Držiteľ",
    "dash.expires": "Platnosť",
    "dash.visa_label": "VISA",
    "dash.balance": "Dostupný zostatok",
    "tx.all": "Všetky",
    "tx.income": "Príjmy",
    "tx.expenses": "Výdavky",
    "tx.today": "Dnes",
    "tx.yesterday": "Včera",
    "tx.none": "Žiadne transakcie.",
    "transfer.title": "Rýchly prevod",
    "transfer.recipient": "Príjemca",
    "transfer.recipient_ph": "Meno, IBAN, telefónne číslo…",
    "transfer.amount": "Suma",
    "transfer.label": "Popis",
    "transfer.reason": "Dôvod (voliteľné)",
    "transfer.reason_ph": "Napr.: nájom, splátka…",
    "transfer.execution": "Vykonanie",
    "transfer.immediate": "Okamžité",
    "transfer.delayed": "Odložené",
    "transfer.balance": "Dostupný zostatok:",
    "transfer.continue": "Pokračovať",
    "transfer.confirm": "Potvrdiť",
    "transfer.cancel": "Zrušiť",
    "transfer.code": "Overovací kód",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Zadajte prijatý kód na potvrdenie prevodu",
    "transfer.pending.title": "Prevod čaká na overenie",
    "transfer.pending.amount": "Suma:",
    "transfer.pending.to": "Komu:",
    "transfer.pending.hint": "Váš overovací kód bude zaslaný e-mailom alebo prostredníctvom zvonka upozornení. Kód sa skladá z 6 číslic. Ak ho nedostanete, kontaktujte podporu.",
    "transfer.pending.validate": "Overiť prevod",
    "transfer.saved": "Prevod uložený. Požiadajte správcu o overovací kód.",
    "transfer.done": "Prevod uskutočnený",
    "transfer.validated": "Prevod overený!",
    "transfer.progress.processing": "Spracovanie prevodu…",
    "transfer.progress.completed": "Prevod vykonaný!",
    "transfer.progress.default_message": "Váš prevod sa spracováva. Prosím čakajte.",
    "cards.none": "Žiadna karta nevydaná.\nKontaktujte svojho správcu.",
    "cards.holder": "Držiteľ",
    "cards.expires": "Platnosť",
    "cards.block": "Zablokovať",
    "cards.pin": "PIN kód",
    "cards.more": "Viac",
    "cards.limits": "Limity karty",
    "cards.payment": "Platba",
    "cards.withdrawal": "Výber",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Zablokovaná",
    "cards.status_active": "Aktívna",
    "cards.toast.blocked": "Karta zablokovaná",
    "cards.toast.unblocked": "Karta odblokovaná",
    "cards.toast.physical_ordered": "Fyzická karta objednaná! Doručenie do 5–10 pracovných dní.",
    "cards.toast.created": "Karta úspešne vygenerovaná!",
    "cards.none_sub": "Žiadna karta nevydaná. Vygenerujte svoju virtuálnu kartu teraz.",
    "cards.generate_btn": "Vygenerovať moju kartu",
    "cards.create.title": "Vygenerovať moju virtuálnu kartu",
    "cards.create.network": "Platobná sieť",
    "cards.create.color": "Farba karty",
    "cards.create.preview_name": "VAŠE MENO",
    "cards.create.preview_expiry": "MM/RR",
    "cards.create.loading": "Generovanie…",
    "cards.create.submit": "Vygenerovať kartu",
    "cards.btn.unblock": "Odblokovať",
    "cards.btn.block": "Zablokovať",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fyzická",
    "cards.btn.physical_done": "Objednaná",
    "cards.limits_title": "Limity",
    "cards.reveal.title": "Informácie o karte",
    "cards.reveal.number": "Celé číslo",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Platnosť",
    "cards.reveal.warning": "Nikdy nezdieľajte tieto informácie. Zatvorte toto okno po použití.",
    "cards.pin.title": "Kód PIN",
    "cards.pin.message": "Tento kód PIN sa používa pre všetky vaše online platby a výbery. Nikdy ho nikomu nehovorte.",
    "cards.physical.title": "Objednať fyzickú kartu",
    "cards.physical.fee_label": "Poplatok za doručenie",
    "cards.physical.balance_label": "Váš zostatok",
    "cards.physical.insufficient": "Nedostatočný zostatok. Pred objednaním prosím dobite svoj účet.",
    "cards.physical.info": "Poplatok bude stiahnutý z vášho účtu. Doručenie do 5–10 pracovných dní.",
    "cards.physical.order_btn": "Objednať ({fee})",
    "cards.physical.ordering": "Objednávam…",
    "cards.physical.order_cta": "Objednať fyzickú kartu",
    "cards.physical.choose_type": "Vyberte si svoju kartu",
    "cards.physical.features": "Výhody",
    "cards.buy_virtual_cta": "Kúpiť virtuálnu kartu",
    "notif.none": "Žiadne upozornenia v tejto chvíli.",
    "notif.contact_support": "Kontaktovať podporu",
    "profile.change_photo": "Zmeniť fotografiu",
    "profile.remove_photo": "Odstrániť fotografiu",
    "profile.email": "E-mail",
    "profile.phone": "Telefón",
    "profile.country": "Krajina",
    "profile.address": "Adresa",
    "profile.notifs_title": "Upozornenia ({count})",
    "profile.no_notifs": "Žiadne upozornenia.",
    "profile.logout": "Odhlásiť sa",
    "profile.photo_updated": "Profilová fotografia aktualizovaná",
    "profile.photo_removed": "Fotografia odstránená",
    "profile.photo_too_large": "Obrázok je príliš veľký (max. 1 MB)",
    "profile.powered": "Powered by {bank}",
    "iban.title": "Môj IBAN",
    "iban.loading": "Načítavanie…",
    "iban.bic": "Kód BIC / SWIFT",
    "iban.share_info": "Zdieľajte tieto údaje na prijatie prevodu na váš účet.",
    "iban.copy": "Kopírovať",
    "iban.copied": "Skopírované do schránky",
    "iban.copy_fail": "Kopírovanie zlyhalo",
    "iban.close": "Zatvoriť",
    "accounts.all": "Všetky vaše účty u {bank}.",
    "accounts.primary": "Hlavný účet",
    "accounts.savings": "Sporiaci účet",
    "accounts.primary_tag": "Hlavný",
    "accounts.secondary_tag": "Vedľajší",
    "accounts.objective": "Cieľ: {amount} · {pct}%",
    "accounts.copy": "Kopírovať",
    "accounts.loading": "Načítavanie…",
    "accounts.iban_copied": "IBAN skopírovaný",
    "accounts.copy_fail": "Kopírovanie zlyhalo",
    "savings.total": "Celkové úspory",
    "savings.target_total": "Celkový cieľ",
    "savings.progress": "Pokrok",
    "savings.active": "Aktívne ciele",
    "savings.new_goal": "Nový cieľ",
    "savings.none": "Zatiaľ žiadne ciele sporenia.",
    "savings.none_sub": "Vytvorte svoj prvý cieľ a začnite.",
    "savings.saved": "Ušetrené:",
    "savings.goal": "Cieľ:",
    "savings.deadline": "Termín: {date}",
    "savings.deposit": "Vložiť",
    "savings.withdraw": "Vybrať",
    "savings.loading": "Načítavanie…",
    "savings.form.title": "Nový cieľ sporenia",
    "savings.form.cancel": "Zrušiť",
    "savings.form.create": "Vytvoriť",
    "savings.form.name": "Názov cieľa",
    "savings.form.name_ph": "Výlet do Japonska, Auto, …",
    "savings.form.target": "Cieľová suma",
    "savings.form.initial": "Počiatočný vklad",
    "savings.form.deadline": "Termín (voliteľné)",
    "savings.form.color": "Farba",
    "savings.deposit_title": "Vložiť na \"{name}\"",
    "savings.deposit_amount": "Suma na vloženie",
    "savings.balance_hint": "Zostatok na hlavnom účte: {amount}",
    "savings.withdraw_prompt": "Koľko vybrať z \"{name}\"?",
    "savings.close_confirm": "Uzavrieť cieľ \"{name}\"? Úspory budú prevedené na váš hlavný účet.",
    "savings.toast.created": "Cieľ vytvorený",
    "savings.toast.deposited": "Vklad uskutočnený",
    "savings.toast.withdrawn": "Výber uskutočnený",
    "savings.toast.closed": "Cieľ uzavretý",
    "invest.total": "Celkové investície",
    "invest.profits": "Kumulatívne zisky",
    "invest.count": "Investície",
    "invest.products": "Dostupné produkty",
    "invest.invest_btn": "Investovať",
    "invest.my": "Moje investície",
    "invest.pending": "Čakajúce",
    "invest.validated": "Overené",
    "invest.refused": "Odmietnuté",
    "invest.withdraw.btn": "Vybrať",
    "invest.withdraw.title": "Vybrať moju investíciu",
    "invest.withdraw.intro": "Chystáte sa vybrať svoju investíciu „{product}\". Počiatočný kapitál a nahromadený zisk budú pripísané na váš hlavný zostatok.",
    "invest.withdraw.principal": "Počiatočný kapitál",
    "invest.withdraw.profit": "Zisk",
    "invest.withdraw.total": "Celkom na prijatie",
    "invest.withdraw.confirm": "Potvrdiť výber",
    "invest.withdraw.warning": "Túto akciu nemožno vrátiť. Investícia bude uzavretá.",
    "invest.withdraw.success": "Investícia vybratá — {amount} pripísané na zostatok.",
    "invest.withdrawn": "Vybratá",
    "invest.cta_sub": "Vyberte si typ investície, ktorá vám vyhovuje. Poradca vám pomôže od prvej žiadosti.",
    "invest.form.amount": "Suma na investovanie",
    "invest.form.balance": "Dostupný zostatok: {amount}",
    "invest.form.note": "Poznámka pre poradcu (voliteľné)",
    "invest.form.note_ph": "Uveďte svoje očakávania…",
    "invest.form.advisor_info": "Automaticky sa otvorí konverzácia s vaším poradcom.",
    "invest.form.cancel": "Zrušiť",
    "invest.form.submit": "Odoslať žiadosť",
    "invest.toast.requested": "Žiadosť zaznamenaná — konverzácia s vaším poradcom bola otvorená.",
    "invest.p1.name": "Nehnuteľnosti",
    "invest.p1.desc": "SCPI, realitné fondy, realitný crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Participatívne financovanie vybraných projektov",
    "invest.p3.name": "Obnoviteľné energie",
    "invest.p3.desc": "Solárne, veterné a zelené projekty",
    "invest.p4.name": "Burza",
    "invest.p4.desc": "Akcie, ETF a diverzifikované fondy",
    "invest.p5.name": "Participatívne",
    "invest.p5.desc": "Pôžičky malým podnikom a miestnym podnikateľom",
    "invest.p6.name": "Krypto",
    "invest.p6.desc": "Bitcoin, Ethereum a multi-menová peňaženka",
    "msg.online": "Online",
    "msg.loading": "Načítavanie…",
    "msg.none": "Žiadne správy. Začnite konverzáciu so svojím poradcom.",
    "msg.placeholder": "Napíšte správu…",
    "msg.attach.image": "Obrázok",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Zvuk",
    "msg.attach.file": "Súbor / Dokument",
    "msg.pdf_doc": "Dokument PDF",
    "msg.file": "Súbor",
    "msg.too_large": "Súbor je príliš veľký (max. {size}).",
    "msg.withdraw": "Stiahnuť",
    "credits.cta_sub": "Podajte žiadosť priamo online — odpoveď do 24 hodín.",
    "credits.learn_more": "Zistiť viac",
    "credits.request": "Požiadať",
    "credits.history": "Moje žiadosti",
    "credits.status.pending": "Čakajúce",
    "credits.status.accepted": "Prijaté",
    "credits.status.refused": "Odmietnuté",
    "credits.chars": "Charakteristiky",
    "credits.amount_range": "Suma: {min}€ – {max}€",
    "credits.rate_range": "Úroková sadzba: {min}% – {max}% ročne",
    "credits.duration_range": "Doba: {min} až {max} mesiacov",
    "credits.deposit_min": "Počiatočný vklad: {amount}€ minimum",
    "credits.garantie": "Záruka: {garantie}",
    "credits.eligibility": "Podmienky oprávnenosti",
    "credits.elig.income": "Preukázať pravidelný a stabilný príjem",
    "credits.elig.deposit": "Uskutočniť počiatočný vklad {amount}€",
    "credits.elig.garantie": "Predložiť záruku: {garantie}",
    "credits.form.title": "Žiadosť — {name}",
    "credits.form.cancel": "Zrušiť",
    "credits.form.submit": "Odoslať žiadosť",
    "credits.form.amount": "Požadovaná suma (€)",
    "credits.form.duration": "Doba (mesiace)",
    "credits.form.reason": "Dôvod (voliteľné)",
    "credits.form.reason_ph": "Opíšte svoj projekt…",
    "credits.form.close": "Zatvoriť",
    "credits.toast.submitted": "Žiadosť o úver odoslaná — odpoveď dostanete do 24 hodín.",
    "credits.p1.name": "Osobná pôžička",
    "credits.p1.desc": "Na financovanie každodenných potrieb alebo osobných projektov.",
    "credits.p2.name": "Hypotéka",
    "credits.p2.desc": "Na financovanie kúpy alebo výstavby nehnuteľnosti.",
    "credits.p3.name": "Auto / Moto pôžička",
    "credits.p3.desc": "Kúpte si vozidlo bez obáv.",
    "credits.p4.name": "Študentská pôžička",
    "credits.p4.desc": "Finančná pomoc pre vysokoškolské štúdium.",
    "credits.p5.name": "Podnikateľská pôžička",
    "credits.p5.desc": "Na podporu rastu podnikateľskej činnosti.",
    "credits.p6.name": "Expresná pôžička",
    "credits.p6.desc": "Rýchle riešenie pre naliehavé potreby.",
    "financing.reimbursable": "Splatné financovanie",
    "financing.reimbursable_desc": "Finančná podpora s pevnými podmienkami na spustenie alebo rozvoj činnosti.",
    "financing.non_reimbursable": "Nesplatné financovanie",
    "financing.non_reimbursable_desc": "Pre projekty so sociálnym, vzdelávacím alebo humanitárnym dopadom (MVO, združenia).",
    "financing.cta_sub": "Riešenia na mieru na uskutočnenie vašich ambícií.",
    "financing.request": "Požiadať o financovanie",
    "financing.history": "Moje žiadosti",
    "financing.reimbursable_label": "Splatné",
    "financing.non_reimbursable_label": "Nesplatné",
    "financing.status.pending": "Čakajúce",
    "financing.status.granted": "Schválené",
    "financing.status.refused": "Odmietnuté",
    "financing.form.title": "Žiadosť — {name}",
    "financing.form.cancel": "Zrušiť",
    "financing.form.submit": "Odoslať žiadosť",
    "financing.form.project": "Názov projektu",
    "financing.form.project_ph": "Môj projekt",
    "financing.form.amount": "Požadovaná suma (€)",
    "financing.form.sector": "Sektor",
    "financing.form.sector_ph": "Tech, poľnohospodárstvo, …",
    "financing.form.description": "Popis projektu",
    "financing.form.description_ph": "Predstavte váš projekt, jeho ciele a očakávaný dopad…",
    "settings.security": "Bezpečnosť",
    "settings.current_pw": "Aktuálne heslo",
    "settings.new_pw": "Nové heslo",
    "settings.confirm_pw": "Potvrdiť",
    "settings.submit": "Zmeniť heslo",
    "settings.no_match": "Heslá sa nezhodujú",
    "settings.changed": "Heslo zmenené",
    "common.cancel": "Zrušiť",
    "common.confirm": "Potvrdiť",
    "common.close": "Zatvoriť",
    "common.save": "Uložiť",
    "common.loading": "Načítavanie…",
    "common.send": "Odoslať",
    "common.total": "Celkom",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Odh. mesačná splátka",
    "credits.rate_annual": "/ rok",
    "credits.rate_used": "Odh. sadzba:",
    "credits.simulation": "Simulácia mesačnej splátky",
    "credits.total_cost": "Odh. celkové náklady",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Vitajte",
    "common.delete": "Odstrániť",
    "cards.delete.title": "Odstrániť kartu",
    "cards.delete.confirm_msg": "Táto akcia je nezvratná. Karta bude trvalo odstránená.",
    "cards.delete.physical_tooltip": "Fyzická karta objednaná — odstránenie nemožné",
    "cards.order.confirmed": "Objednávka potvrdená!",
    "cards.order.confirmed_body": "Vaša fyzická karta sa pripravuje. Použite toto číslo na sledovanie doručenia.",
    "cards.order.tracking_no": "Č. sledovania",
    "cards.fee_deducted": "{amount} bude odpočítané z vášho zostatku",
    "cards.insufficient_short": "Nedostatočný zostatok ({available} dostupné)",
    "transfer.section.beneficiary": "Príjemca",
    "transfer.section.details": "Prevod",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banka",
    "transfer.field.city": "Mesto",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banka:",
    "transfer.progress.paused": "Čaká na overenie",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Variabilný",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Karta zmazaná",
    "cards.feat.gold_exclusive": "Exkluzívna zlatá karta",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Ochrana kupujúceho",
    "cards.feat.support_standard": "Štandardná podpora",
    "cards.feat.plastic_premium": "Plastová karta premium",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Štandardné cestovné poistenie",
    "cards.feat.support_priority": "Prioritná podpora",
    "cards.feat.metal_brushed": "Karta z kefovaného kovu",
    "cards.feat.cashback_2_unlimited": "Neobmedzený cashback 2%",
    "cards.feat.travel_premium": "Premium cestovné poistenie",
    "cards.feat.vip_lounge": "Prístup do VIP salónikov",
    "cards.feat.support_247": "Asistencia 24/7",
    "auth.demo.title": "Prihlásenie správcu",
    "auth.demo.subtitle": "Zadajte svoje prihlasovacie údaje správcu pre náhľad ukážky",
    "credits.garantie.none": "Žiadna",
    "credits.garantie.mortgage": "Hypotéka na financovanú nehnuteľnosť",
    "credits.garantie.vehicle": "Financované vozidlo",
    "credits.garantie.parental": "Rodičovská alebo banková",
    "credits.garantie.professional": "Profesionálne aktíva alebo osobná záruka",
    "cards.reveal.pin_prompt": "Zadajte svoj 4-ciferný PIN kód pre zobrazenie informácií o karte.",
    "cards.reveal.show": "Zobraziť",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Dva PIN kódy sa nezhodujú",
    "auth.pin.digit_hint": "4 číslice",
  
  },
  sl: {
    "notif.transfer_code.title": "Potrditvena koda nakazila",
    "notif.transfer_code.body": "Kopirajte to kodo za potrditev nakazila.",
    "notif.transfer_code.expires_in": "Izgine čez {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mesecev",
    "credits.n_months": "{n} mesecev",
    "msg.read": "Prebrano",
    "msg.sent": "Poslano",
    "msg.attach_aria": "Pripni datoteko",
    "login.device.unknown": "Neznan brskalnik",
    "login.device.mobile": "Mobilni",
    "login.device.tablet": "Tablica",
    "login.device.desktop": "Računalnik",
    "login.recent": "Nedavno",
    "err.invalid_credentials": "Neveljavni podatki za prijavo",
    "err.bad_pin": "Napačen PIN",
    "err.account_blocked": "Račun je blokiran",
    "err.bad_current_pw": "Trenutno geslo je napačno",
    "err.insufficient_funds": "Nezadostna sredstva, napolnite svoj račun.",
    "err.amount_above_savings": "Znesek presega razpoložljive prihranke",
    "err.must_be_positive": "Vrednost mora biti pozitivna",
    "err.invalid_format": "Neveljaven format",
    "err.email_taken": "E-pošta je že uporabljena",
    "err.card_not_found": "Kartica ni najdena",
    "err.physical_card_already_ordered": "Fizična kartica je že naročena",
    "err.beneficiary_not_found": "Prejemnik ni najden",
    "err.invalid_code": "Neveljavna koda",
    "err.no_transfer_code": "Ni kode za ta prenos",
    "err.link_expired": "Povezava neveljavna ali potekla",
    "err.thread_not_found": "Pogovor ni najden",
    "err.empty_message": "Prazno sporočilo",
    "err.file_too_large": "Datoteka je prevelika (najv. 20 MB)",
    "err.forbidden": "Dostop zavrnjen",
    "err.bank_not_found": "Banka ni najdena",
    "err.signups_closed": "Registracije za to banko so zaprte.",
    "err.access_suspended": "Dostop začasno onemogočen. Prosimo, obrnite se na svojo banko.",
    "err.notif_not_closable": "Tega obvestila ni mogoče zapreti.",
    "auth.signup.tagline": "Pridružite se nam danes",
    "auth.login.tagline": "Dobrodošli nazaj!",
    "auth.signup.tagline_sub": "Odprite račun v 2 minutah in dostopajte do vseh naših premium bančnih storitev.",
    "auth.login.tagline_sub": "Dostopajte do svojega osebnega prostora in upravljajte finance varno.",
    "auth.back": "Nazaj na domačo stran",
    "auth.bullet.security": "Bančna varnost vojaške ravni",
    "auth.bullet.transfers": "Takojšnji prenosi 24/7",
    "auth.bullet.investments": "Integrirane naložbe in varčevanje",
    "auth.bullet.advisor": "Osebni svetovalec na voljo 7 dni v tednu",
    "auth.footer": "Powered by {bank}",
    "auth.signup.title": "Ustvarite račun",
    "auth.login.title": "Prijava",
    "auth.field.lastName": "Priimek",
    "auth.field.firstName": "Ime",
    "auth.field.country": "Država",
    "auth.field.country.placeholder": "— Izberite svojo državo —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Naslov",
    "auth.field.address.placeholder": "Glavna ulica 123, Ljubljana",
    "auth.field.email": "E-pošta",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Geslo",
    "auth.submit.signup": "Ustvarite moj račun",
    "auth.submit.login": "Prijava",
    "auth.login.badge": "Prijavite se v svoj račun",
    "auth.card.tagline": "Vaš prostor za stranke",
    "auth.submit.loading": "Nalaganje…",
    "auth.pin.title": "Koda PIN",
    "auth.pin.subtitle": "Vnesite svojo 4-mestno kodo PIN",
    "auth.pin.label": "Koda PIN",
    "auth.pin.confirm": "Potrdite PIN kodo",
    "auth.pin.submit": "Potrdi",
    "auth.pin.back": "Nazaj na prijavo",
    "auth.pin.length_error": "Koda PIN mora imeti 4 števke",
    "auth.switch.have_account": "Že imate račun?",
    "auth.switch.login_link": "Prijava",
    "auth.switch.no_account": "Še niste stranka?",
    "auth.error.select_country": "Izberite svojo državo",
    "kyc.title": "Preverjanje identitete (KYC)",
    "kyc.sub": "Naložite svoje dokumente za potrditev računa. Vsi dokumenti so šifrirani in zaupni.",
    "kyc.id_type": "Vrsta osebnega dokumenta *",
    "kyc.id_card": "Osebna izkaznica",
    "kyc.passport": "Potni list",
    "kyc.driver_license": "Vozniško dovoljenje",
    "kyc.recto_passport": "Sprednja stran potnega lista *",
    "kyc.recto_id": "Sprednja stran dokumenta *",
    "kyc.verso": "Zadnja stran dokumenta *",
    "kyc.selfie": "Selfie z osebnim dokumentom v roki *",
    "kyc.address_proof": "Dokazilo o naslovu *",
    "kyc.hint_doc": "Slika ali PDF, največ 5 MB",
    "kyc.hint_selfie": "Jasna fotografija, obraz in dokument vidna",
    "kyc.hint_address": "Račun, izpisek, potrdilo… (največ 5 MB)",
    "kyc.upload": "Naloži",
    "kyc.replace": "Zamenjaj",
    "pending.title": "Račun čaka na odobritev",
    "pending.body": "Vaša vloga je bila prejeta. Skrbnik bo preveril vaš spis in kmalu aktiviral vaš račun.",
    "pending.back": "Nazaj na prijavo",
    "home.nav.features": "Funkcije",
    "home.nav.security": "Varnost",
    "home.nav.invest": "Naložbe",
    "home.nav.credits": "Krediti",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Prijava",
    "home.hero.tagline": "Spletno bančništvo nove generacije",
    "home.hero.title1": "Upravljajte svoj denar",
    "home.hero.title2": "z inteligenco",
    "home.hero.sub": "Popolna bančna platforma — prenosi, varčevanje, naložbe in krediti iz vašega pametnega telefona.",
    "home.hero.login": "Prijava",
    "home.hero.trust.secure": "100% Varno",
    "home.hero.trust.fast": "Hitra vzpostavitev",
    "home.hero.trust.encrypted": "Šifrirani podatki",
    "home.hero.trust.support": "Storitev 24/7",
    "home.mockup.balance": "Razpoložljivo stanje",
    "home.mockup.currency": "EUR · Glavni račun",
    "home.mockup.recharge": "Napolni",
    "home.mockup.send": "Pošlji",
    "home.mockup.save": "Varčuj",
    "home.mockup.salary": "Plača – marec",
    "home.mockup.rent": "Najemnina – april",
    "home.mockup.investment": "Naložba",
    "home.mockup.days2": "pred 2 dnevi",
    "home.mockup.days3": "pred 3 dnevi",
    "home.mockup.days5": "pred 5 dnevi",
    "home.stats.accounts": "Aktivni računi",
    "home.stats.uptime": "Razpoložljivost",
    "home.stats.transfers": "Prenosi",
    "home.stats.support": "Podpora",
    "home.features.label": "Funkcije",
    "home.features.title": "Vse kar potrebujete",
    "home.features.sub": "Popolna platforma zasnovana za poenostavitev upravljanja vaših financ vsak dan.",
    "home.feature.security": "Vojaška varnost",
    "home.feature.security_body": "Šifriranje AES-256, dvofaktorska avtentikacija in spremljanje računa 24/7.",
    "home.feature.cards": "Virtualna in fizična kartica",
    "home.feature.cards_body": "Izdajte kartice v nekaj sekundah ali naročite pravo fizično kartico dostavljeno domov.",
    "home.feature.transfers": "Ekspresni prenosi",
    "home.feature.transfers_body": "Pošiljajte in prejemajte denar kjerkoli na svetu v nekaj sekundah.",
    "home.feature.analytics": "Analiza v realnem času",
    "home.feature.analytics_body": "Pametne nadzorne plošče za sledenje porabi in predvidevanje vaših financ.",
    "home.feature.investments": "Integrirane naložbe",
    "home.feature.investments_body": "Dostop do izbranih naložbenih produktov neposredno iz vašega računa.",
    "home.feature.support": "Namenjena podpora 24/7",
    "home.feature.support_body": "Osebni bančni svetovalec na voljo kadarkoli prek varnega sporočanja.",
    "home.tpl.label": "Zaupanje",
    "home.tpl.title": "Zaupajo nam",
    "home.mockup.balance_label": "Stanje",
    "home.mockup.virement": "Prenos",
    "home.mockup.salaire": "Plača",
    "home.security.label": "Varnost",
    "home.security.title1": "Vaše finance",
    "home.security.title2": "v absolutni varnosti",
    "home.security.sub": "Uporabljamo najstrožje standarde bančne varnosti za zaščito vsakega centa.",
    "home.security.aes.title": "Šifriranje AES-256",
    "home.security.aes.body": "Vsi občutljivi podatki so šifrirani z enakimi algoritmi, ki jih uporabljajo velike mednarodne banke.",
    "home.security.auth.title": "Okrepljena avtentikacija",
    "home.security.auth.body": "Dvojno preverjanje identitete ob vsaki prijavi in za vsako občutljivo operacijo na vašem računu.",
    "home.security.compliance.title": "Skladnost s PSD2/GDPR",
    "home.security.compliance.body": "Upoštevamo vse evropske predpise o plačilnih storitvah in varstvu podatkov.",
    "home.invest.label": "Naložbe",
    "home.invest.title": "Naredite, da vaš denar raste",
    "home.invest.sub": "6 naložbenih kategorij izbranih za njihov potencial in zanesljivost.",
    "home.invest.yield": "Donosnost",
    "home.credits.label": "Krediti",
    "home.credits.title": "Rešitve za vsak projekt",
    "home.credits.sub": "Konkurenčne obrestne mere, hitre odgovore in prilagojeno podporo za vse vaše projekte.",
    "home.credits.rate": "Obrestna mera",
    "home.faq.label": "FAQ",
    "home.faq.title": "Pogosta vprašanja",
    "home.faq.q1": "Kako odpreti račun?",
    "home.faq.a1": "Registracija traja manj kot 2 minuti. Preprosto izpolnite osebne podatke, izberite geslo in vaš račun bo aktiviran po potrditvi naše ekipe.",
    "home.faq.q2": "Ali so moji podatki varni?",
    "home.faq.a2": "Da. Vsi podatki so šifrirani z AES-256 v mirovanju in TLS 1.3 med prenosom. Upoštevamo mednarodne bančne standarde in predpise GDPR/PSD2.",
    "home.faq.q3": "Katere vrste naložb ponujate?",
    "home.faq.a3": "Ponujamo 6 kategorij: Nepremičnine, Crowdfunding, Obnovljiva energija, Borza, Participativno in Kripto. Vsak produkt je izbran za njegovo razmerje tveganja in donosa.",
    "home.faq.q4": "Kako zaprositi za kredit?",
    "home.faq.a4": "Pojdite v razdelek Kredit v svojem uporabniškem prostoru, izberite vrsto posojila, vnesite znesek in trajanje. Vaša vloga se obravnava v 24 urah pri naših svetovalcih.",
    "home.faq.q5": "Ali obstajajo skrite pristojbine?",
    "home.faq.a5": "Brez skritih pristojbin. Vse cene so jasno prikazane pri vsaki operaciji. Pogoji so dostopni v vašem uporabniškem prostoru kadarkoli.",
    "home.faq.q6": "Kako kontaktirati podporo?",
    "home.faq.a6": "Uporabite integrirano sporočanje v svojem uporabniškem prostoru za neposreden stik z vašim osebnim bančnim svetovalcem, ki je na voljo 24/7.",
    "home.cta.title": "Pripravljeni za začetek?",
    "home.cta.sub": "Pridružite se tisočim uporabnikov, ki upravljajo svoje finance z {bank}. Registracija traja manj kot 2 minuti.",
    "home.footer.products": "Produkti",
    "home.footer.services": "Storitve",
    "home.footer.access": "Hiter dostop",
    "home.footer.accounts": "Računi",
    "home.footer.transfers": "Prenosi",
    "home.footer.cards": "Kartice",
    "home.footer.savings": "Varčevanje",
    "home.footer.investments": "Naložbe",
    "home.footer.credits": "Krediti",
    "home.footer.financing": "Financiranje",
    "home.footer.messages": "Sporočila",
    "home.footer.login": "Prijava",
    "home.footer.signup": "Odprite račun",
    "home.footer.support": "Podpora 24/7",
    "home.footer.tagline": "Vaša spletna banka, dostopna povsod in kadarkoli.",
    "home.footer.copyright": "© {year} {bank} — Vse pravice pridržane",
    "home.footer.powered": "Powered by {bank}",
    "app.menu_label": "Glavni meni",
    "app.nav.dashboard": "Nadzorna plošča",
    "app.nav.accounts": "Računi",
    "app.nav.transactions": "Transakcije",
    "app.nav.transfers": "Prenosi",
    "app.nav.cards": "Kartice",
    "app.nav.savings": "Varčevanje",
    "app.nav.investments": "Naložbe",
    "app.nav.credit": "Kredit",
    "app.nav.financing": "Financiranje",
    "app.nav.messages": "Sporočila",
    "app.nav.card_tracking": "Sledenje kartice",
    "app.nav.notifications": "Obvestila",
    "app.nav.settings": "Nastavitve",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Domov",
    "app.nav.mobile.transfers": "Prenosi",
    "app.nav.mobile.cards": "Kartice",
    "app.nav.mobile.transactions": "Transakcije",
    "app.nav.more.invest": "Vlagaj",
    "app.user_role": "Stranka",
    "app.logout": "Odjava",
    "app.search": "Iskanje…",
    "app.more_title": "Več funkcij",
    "app.more_btn": "Več",
    "app.loading": "Nalaganje…",
    "app.error_back": "Nazaj na prijavo",
    "app.see_more_notifs": "Pokaži {count} več obvestil →",
    "page.accounts": "Moji računi",
    "page.transactions": "Transakcije",
    "page.transfers": "Nov prenos",
    "page.deposit": "Polog",
    "page.cards": "Moje kartice",
    "page.savings": "Varčevanje",
    "page.investments": "Naložbe",
    "page.credits": "Kredit",
    "page.financing": "Financiranje projektov",
    "page.messages": "Sporočila",
    "page.settings": "Nastavitve",
    "page.notifications": "Pomembna obvestila",
    "page.profile": "Moj profil",
    "dash.greeting": "Pozdravljeni, {name}",
    "dash.overview": "Pregled vaših financ — {date}",
    "dash.total_balance": "Skupno stanje",
    "dash.this_month": "+{amount} ta mesec",
    "dash.view_iban": "Poglej moj IBAN",
    "dash.my_accounts": "Moji računi",
    "dash.primary_account": "Glavni račun",
    "dash.savings_account": "Varčevalni račun",
    "dash.remunerated": "Obrestovan",
    "dash.details": "Podrobnosti",
    "dash.transfer_btn": "Prenos",
    "dash.quick_actions": "Hitre akcije",
    "dash.quick.deposit": "Polog",
    "deposit.title": "Informacije o pologu",
    "dash.quick.transfer": "Prenos",
    "dash.quick.history": "Zgodovina",
    "dash.quick.cards": "Moje kartice",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Mesečni proračun",
    "dash.income": "Prihodki",
    "dash.expenses": "Odhodki",
    "dash.spending_cat": "Poraba po kategorijah",
    "dash.no_spending": "Ni zabeležene porabe.",
    "dash.recent_tx": "Nedavne transakcije",
    "dash.see_all": "Prikaži vse",
    "dash.no_tx": "Ni operacij.",
    "dash.main_card": "Moja glavna kartica",
    "dash.manage": "Upravljaj",
    "dash.block": "Blokiraj",
    "dash.pin": "PIN koda",
    "dash.security": "Varnost",
    "dash.more": "Več",
    "dash.no_card": "Ni izdane kartice",
    "dash.monthly_activity": "Mesečna aktivnost",
    "dash.operations": "Operacije",
    "dash.holder": "Imetnik",
    "dash.expires": "Velj.",
    "dash.visa_label": "VISA",
    "dash.balance": "Razpoložljivo stanje",
    "tx.all": "Vse",
    "tx.income": "Prihodki",
    "tx.expenses": "Odhodki",
    "tx.today": "Danes",
    "tx.yesterday": "Včeraj",
    "tx.none": "Ni transakcij.",
    "transfer.title": "Hiter prenos",
    "transfer.recipient": "Prejemnik",
    "transfer.recipient_ph": "Ime, IBAN, telefonska številka…",
    "transfer.amount": "Znesek",
    "transfer.label": "Opis",
    "transfer.reason": "Razlog (neobvezno)",
    "transfer.reason_ph": "Npr.: najemnina, vračilo…",
    "transfer.execution": "Izvedba",
    "transfer.immediate": "Takojšnje",
    "transfer.delayed": "Odloženo",
    "transfer.balance": "Razpoložljivo stanje:",
    "transfer.continue": "Nadaljuj",
    "transfer.confirm": "Potrdi",
    "transfer.cancel": "Prekliči",
    "transfer.code": "Potrditvena koda",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Vnesite prejeto kodo za potrditev prenosa",
    "transfer.pending.title": "Prenos čaka na potrditev",
    "transfer.pending.amount": "Znesek:",
    "transfer.pending.to": "Za:",
    "transfer.pending.hint": "Vaša potrditvena koda bo poslana po e-pošti ali prek zvonca za obvestila. Koda je sestavljena iz 6 številk. Če je ne prejmete, se obrnite na podporo.",
    "transfer.pending.validate": "Potrdi prenos",
    "transfer.saved": "Prenos shranjen. Prosite skrbnika za potrditveno kodo.",
    "transfer.done": "Prenos izveden",
    "transfer.validated": "Prenos potrjen!",
    "transfer.progress.processing": "Obdelava prenosa…",
    "transfer.progress.completed": "Prenos opravljen!",
    "transfer.progress.default_message": "Vaš prenos se obdeluje. Prosimo počakajte.",
    "cards.none": "Ni izdane kartice.\nKontaktirajte svojega skrbnika.",
    "cards.holder": "Imetnik",
    "cards.expires": "Velj.",
    "cards.block": "Blokiraj",
    "cards.pin": "PIN koda",
    "cards.more": "Več",
    "cards.limits": "Omejitve kartice",
    "cards.payment": "Plačilo",
    "cards.withdrawal": "Dvig",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Blokirana",
    "cards.status_active": "Aktivna",
    "cards.toast.blocked": "Kartica blokirana",
    "cards.toast.unblocked": "Kartica odblokirana",
    "cards.toast.physical_ordered": "Fizična kartica naročena! Dostava v 5–10 delovnih dneh.",
    "cards.toast.created": "Kartica uspešno ustvarjena!",
    "cards.none_sub": "Nobena kartica ni bila izdana. Ustvarite svojo virtualno kartico zdaj.",
    "cards.generate_btn": "Ustvari mojo kartico",
    "cards.create.title": "Ustvari mojo virtualno kartico",
    "cards.create.network": "Plačilno omrežje",
    "cards.create.color": "Barva kartice",
    "cards.create.preview_name": "VAŠE IME",
    "cards.create.preview_expiry": "MM/LL",
    "cards.create.loading": "Ustvarjanje…",
    "cards.create.submit": "Ustvari kartico",
    "cards.btn.unblock": "Odblokiraj",
    "cards.btn.block": "Blokiraj",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fizična",
    "cards.btn.physical_done": "Naročena",
    "cards.limits_title": "Omejitve",
    "cards.reveal.title": "Informacije o kartici",
    "cards.reveal.number": "Polna številka",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Veljavnost",
    "cards.reveal.warning": "Teh informacij nikoli ne delite. Po uporabi zaprite to okno.",
    "cards.pin.title": "PIN koda",
    "cards.pin.message": "Ta PIN koda se uporablja za vsa vaša spletna plačila in dvige. Nikoli je ne sporočite nikomur.",
    "cards.physical.title": "Naroči fizično kartico",
    "cards.physical.fee_label": "Stroški dostave",
    "cards.physical.balance_label": "Vaše stanje",
    "cards.physical.insufficient": "Nezadostno stanje. Pred naročilom prosimo napolnite račun.",
    "cards.physical.info": "Stroški bodo bremenili vaš račun. Dostava v 5–10 delovnih dneh.",
    "cards.physical.order_btn": "Naroči ({fee})",
    "cards.physical.ordering": "Naročanje…",
    "cards.physical.order_cta": "Naroči fizično kartico",
    "cards.physical.choose_type": "Izberi svojo kartico",
    "cards.physical.features": "Prednosti",
    "cards.buy_virtual_cta": "Kupi virtualno kartico",
    "notif.none": "Trenutno ni obvestil.",
    "notif.contact_support": "Stik s podporo",
    "profile.change_photo": "Zamenjaj fotografijo",
    "profile.remove_photo": "Odstrani fotografijo",
    "profile.email": "E-pošta",
    "profile.phone": "Telefon",
    "profile.country": "Država",
    "profile.address": "Naslov",
    "profile.notifs_title": "Obvestila ({count})",
    "profile.no_notifs": "Ni obvestil.",
    "profile.logout": "Odjava",
    "profile.photo_updated": "Profilna fotografija posodobljena",
    "profile.photo_removed": "Fotografija odstranjena",
    "profile.photo_too_large": "Slika je prevelika (največ 1 MB)",
    "profile.powered": "Powered by {bank}",
    "iban.title": "Moj IBAN",
    "iban.loading": "Nalaganje…",
    "iban.bic": "Koda BIC / SWIFT",
    "iban.share_info": "Delite te podatke za prejem prenosa na vaš račun.",
    "iban.copy": "Kopiraj",
    "iban.copied": "Kopirano v odložišče",
    "iban.copy_fail": "Kopiranje ni uspelo",
    "iban.close": "Zapri",
    "accounts.all": "Vsi vaši računi pri {bank}.",
    "accounts.primary": "Glavni račun",
    "accounts.savings": "Varčevalni račun",
    "accounts.primary_tag": "Primarni",
    "accounts.secondary_tag": "Sekundarni",
    "accounts.objective": "Cilj: {amount} · {pct}%",
    "accounts.copy": "Kopiraj",
    "accounts.loading": "Nalaganje…",
    "accounts.iban_copied": "IBAN kopiran",
    "accounts.copy_fail": "Kopiranje ni uspelo",
    "savings.total": "Skupno prihrankov",
    "savings.target_total": "Skupni cilj",
    "savings.progress": "Napredek",
    "savings.active": "Aktivni cilji",
    "savings.new_goal": "Nov cilj",
    "savings.none": "Zaenkrat ni ciljev varčevanja.",
    "savings.none_sub": "Ustvarite svoj prvi cilj za začetek.",
    "savings.saved": "Prihranki:",
    "savings.goal": "Cilj:",
    "savings.deadline": "Rok: {date}",
    "savings.deposit": "Vloži",
    "savings.withdraw": "Dvigni",
    "savings.loading": "Nalaganje…",
    "savings.form.title": "Nov cilj varčevanja",
    "savings.form.cancel": "Prekliči",
    "savings.form.create": "Ustvari",
    "savings.form.name": "Ime cilja",
    "savings.form.name_ph": "Potovanje na Japonsko, Avto, …",
    "savings.form.target": "Ciljni znesek",
    "savings.form.initial": "Začetni vlog",
    "savings.form.deadline": "Rok (neobvezno)",
    "savings.form.color": "Barva",
    "savings.deposit_title": "Vloži na \"{name}\"",
    "savings.deposit_amount": "Znesek za vlogo",
    "savings.balance_hint": "Stanje na glavnem računu: {amount}",
    "savings.withdraw_prompt": "Koliko dvigniti iz \"{name}\"?",
    "savings.close_confirm": "Zapri cilj \"{name}\"? Prihranki bodo vrnjeni na vaš glavni račun.",
    "savings.toast.created": "Cilj ustvarjen",
    "savings.toast.deposited": "Vloga izvedena",
    "savings.toast.withdrawn": "Dvig izveden",
    "savings.toast.closed": "Cilj zaprt",
    "invest.total": "Skupne naložbe",
    "invest.profits": "Kumulativni dobički",
    "invest.count": "Naložbe",
    "invest.products": "Razpoložljivi produkti",
    "invest.invest_btn": "Vlagaj",
    "invest.my": "Moje naložbe",
    "invest.pending": "V obravnavi",
    "invest.validated": "Potrjeno",
    "invest.refused": "Zavrnjeno",
    "invest.withdraw.btn": "Dvigni",
    "invest.withdraw.title": "Dvigni mojo naložbo",
    "invest.withdraw.intro": "Pravkar boste dvignili svojo naložbo „{product}\". Začetni kapital in obračunani dobiček bosta dobropisana na vaš glavni saldo.",
    "invest.withdraw.principal": "Začetni kapital",
    "invest.withdraw.profit": "Dobiček",
    "invest.withdraw.total": "Skupaj za prejem",
    "invest.withdraw.confirm": "Potrdi dvig",
    "invest.withdraw.warning": "To dejanje je nepovratno. Naložba bo zaprta.",
    "invest.withdraw.success": "Naložba dvignjena — {amount} dobropisano na saldu.",
    "invest.withdrawn": "Dvignjeno",
    "invest.cta_sub": "Izberite vrsto naložbe, ki vam ustreza. Svetovalec vam bo pomagal od prve vloge.",
    "invest.form.amount": "Znesek za vlaganje",
    "invest.form.balance": "Razpoložljivo stanje: {amount}",
    "invest.form.note": "Opomba za svetovalca (neobvezno)",
    "invest.form.note_ph": "Navedite svoja pričakovanja…",
    "invest.form.advisor_info": "Samodejno se bo odprlo pogovor z vašim svetovalcem.",
    "invest.form.cancel": "Prekliči",
    "invest.form.submit": "Pošlji vlogo",
    "invest.toast.requested": "Vloga zabeležena — pogovor z vašim svetovalcem je bil odprt.",
    "invest.p1.name": "Nepremičnine",
    "invest.p1.desc": "SCPI, papirne nepremičnine, nepremičninski crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Participativno financiranje izbranih projektov",
    "invest.p3.name": "Obnovljivi viri",
    "invest.p3.desc": "Solarni, vetrni in zeleni projekti",
    "invest.p4.name": "Borza",
    "invest.p4.desc": "Delnice, ETF in diverzificirani skladi",
    "invest.p5.name": "Participativno",
    "invest.p5.desc": "Posojila malim podjetjem in lokalnim podjetnikom",
    "invest.p6.name": "Kripto",
    "invest.p6.desc": "Bitcoin, Ethereum in večvalutna denarnica",
    "msg.online": "Na spletu",
    "msg.loading": "Nalaganje…",
    "msg.none": "Ni sporočil. Začnite pogovor s svojim svetovalcem.",
    "msg.placeholder": "Napišite sporočilo…",
    "msg.attach.image": "Slika",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Zvok",
    "msg.attach.file": "Datoteka / Dokument",
    "msg.pdf_doc": "Dokument PDF",
    "msg.file": "Datoteka",
    "msg.too_large": "Datoteka je prevelika (največ {size}).",
    "msg.withdraw": "Umakni",
    "credits.cta_sub": "Oddajte vlogo neposredno prek spleta — odgovor v 24 urah.",
    "credits.learn_more": "Izvedi več",
    "credits.request": "Zaprosi",
    "credits.history": "Moje vloge",
    "credits.status.pending": "V obravnavi",
    "credits.status.accepted": "Sprejeto",
    "credits.status.refused": "Zavrnjeno",
    "credits.chars": "Značilnosti",
    "credits.amount_range": "Znesek: {min}€ – {max}€",
    "credits.rate_range": "Obrestna mera: {min}% – {max}% letno",
    "credits.duration_range": "Trajanje: {min} do {max} mesecev",
    "credits.deposit_min": "Začetni vlog: {amount}€ minimum",
    "credits.garantie": "Garancija: {garantie}",
    "credits.eligibility": "Pogoji upravičenosti",
    "credits.elig.income": "Izkazati reden in stabilen dohodek",
    "credits.elig.deposit": "Opraviti začetni vlog {amount}€",
    "credits.elig.garantie": "Predložiti garancijo: {garantie}",
    "credits.form.title": "Vloga — {name}",
    "credits.form.cancel": "Prekliči",
    "credits.form.submit": "Pošlji vlogo",
    "credits.form.amount": "Zahtevani znesek (€)",
    "credits.form.duration": "Trajanje (meseci)",
    "credits.form.reason": "Razlog (neobvezno)",
    "credits.form.reason_ph": "Opišite svoj projekt…",
    "credits.form.close": "Zapri",
    "credits.toast.submitted": "Vloga za kredit poslana — odgovor boste prejeli v 24 urah.",
    "credits.p1.name": "Osebno posojilo",
    "credits.p1.desc": "Za financiranje vsakodnevnih potreb ali osebnih projektov.",
    "credits.p2.name": "Hipotekarno posojilo",
    "credits.p2.desc": "Za financiranje nakupa ali gradnje nepremičnine.",
    "credits.p3.name": "Avto / Moto posojilo",
    "credits.p3.desc": "Kupite vozilo brez skrbi.",
    "credits.p4.name": "Študentsko posojilo",
    "credits.p4.desc": "Finančna pomoč za visokošolsko izobraževanje.",
    "credits.p5.name": "Poslovno posojilo",
    "credits.p5.desc": "Za podporo rasti poklicne dejavnosti.",
    "credits.p6.name": "Ekspresno posojilo",
    "credits.p6.desc": "Hitra rešitev za nujne potrebe.",
    "financing.reimbursable": "Vračljivo financiranje",
    "financing.reimbursable_desc": "Finančna podpora s fiksnimi pogoji za zagon ali razvoj dejavnosti.",
    "financing.non_reimbursable": "Nevračljivo financiranje",
    "financing.non_reimbursable_desc": "Za projekte z družbenim, izobraževalnim ali humanitarnim vplivom (NVO, združenja).",
    "financing.cta_sub": "Prilagojene rešitve za uresničitev vaših ambicij.",
    "financing.request": "Zaprosi za financiranje",
    "financing.history": "Moje vloge",
    "financing.reimbursable_label": "Vračljivo",
    "financing.non_reimbursable_label": "Nevračljivo",
    "financing.status.pending": "V obravnavi",
    "financing.status.granted": "Odobreno",
    "financing.status.refused": "Zavrnjeno",
    "financing.form.title": "Vloga — {name}",
    "financing.form.cancel": "Prekliči",
    "financing.form.submit": "Pošlji vlogo",
    "financing.form.project": "Ime projekta",
    "financing.form.project_ph": "Moj projekt",
    "financing.form.amount": "Zahtevani znesek (€)",
    "financing.form.sector": "Sektor",
    "financing.form.sector_ph": "Tech, kmetijstvo, …",
    "financing.form.description": "Opis projekta",
    "financing.form.description_ph": "Predstavite svoj projekt, njegove cilje in pričakovani vpliv…",
    "settings.security": "Varnost",
    "settings.current_pw": "Trenutno geslo",
    "settings.new_pw": "Novo geslo",
    "settings.confirm_pw": "Potrdi",
    "settings.submit": "Spremeni geslo",
    "settings.no_match": "Gesli se ne ujemata",
    "settings.changed": "Geslo spremenjeno",
    "common.cancel": "Prekliči",
    "common.confirm": "Potrdi",
    "common.close": "Zapri",
    "common.save": "Shrani",
    "common.loading": "Nalaganje…",
    "common.send": "Pošlji",
    "common.total": "Skupaj",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Ocen. mesečni obrok",
    "credits.rate_annual": "/ leto",
    "credits.rate_used": "Ocen. stopnja:",
    "credits.simulation": "Simulacija mesečnega obroka",
    "credits.total_cost": "Ocen. skupni stroški",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Dobrodošli",
    "common.delete": "Izbriši",
    "cards.delete.title": "Izbriši kartico",
    "cards.delete.confirm_msg": "To dejanje je nepopravljivo. Kartica bo trajno izbrisana.",
    "cards.delete.physical_tooltip": "Fizična kartica naročena — brisanje nemogoče",
    "cards.order.confirmed": "Naročilo potrjeno!",
    "cards.order.confirmed_body": "Vaša fizična kartica se pripravlja. Uporabite to številko za sledenje dostave.",
    "cards.order.tracking_no": "Št. sledenja",
    "cards.fee_deducted": "{amount} bo odšteto od vašega stanja",
    "cards.insufficient_short": "Nezadostno stanje ({available} na voljo)",
    "transfer.section.beneficiary": "Prejemnik",
    "transfer.section.details": "Nakazilo",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banka",
    "transfer.field.city": "Mesto",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banka:",
    "transfer.progress.paused": "Čaka na potrditev",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Spremenljivo",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Kartica izbrisana",
    "cards.feat.gold_exclusive": "Ekskluzivna zlata kartica",
    "cards.feat.cashback_05": "Vračilo 0,5%",
    "cards.feat.buyer_protection": "Zaščita kupca",
    "cards.feat.support_standard": "Standardna podpora",
    "cards.feat.plastic_premium": "Premium plastična kartica",
    "cards.feat.cashback_1": "Vračilo 1%",
    "cards.feat.travel_standard": "Standardno potovalno zavarovanje",
    "cards.feat.support_priority": "Prednostna podpora",
    "cards.feat.metal_brushed": "Brušena kovinska kartica",
    "cards.feat.cashback_2_unlimited": "Neomejen 2% vračilo",
    "cards.feat.travel_premium": "Premium potovalno zavarovanje",
    "cards.feat.vip_lounge": "Dostop do VIP salonov",
    "cards.feat.support_247": "24/7 podpora",
    "auth.demo.title": "Prijava skrbnika",
    "auth.demo.subtitle": "Vnesite skrbniške podatke za predogled dema",
    "credits.garantie.none": "Brez",
    "credits.garantie.mortgage": "Hipoteka na financirano nepremičnino",
    "credits.garantie.vehicle": "Financirano vozilo",
    "credits.garantie.parental": "Starševsko ali bančno",
    "credits.garantie.professional": "Poslovna sredstva ali osebno poroštvo",
    "cards.reveal.pin_prompt": "Vnesite svojo 4-mestno PIN kodo za prikaz podatkov kartice.",
    "cards.reveal.show": "Prikaži",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Dve PIN kodi se ne ujemata",
    "auth.pin.digit_hint": "4 številke",
  
  },
  hr: {
    "notif.transfer_code.title": "Kod za potvrdu prijenosa",
    "notif.transfer_code.body": "Kopirajte ovaj kod za potvrdu prijenosa.",
    "notif.transfer_code.expires_in": "Nestaje za {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mjeseci",
    "credits.n_months": "{n} mjeseci",
    "msg.read": "Pročitano",
    "msg.sent": "Poslano",
    "msg.attach_aria": "Priloži datoteku",
    "login.device.unknown": "Nepoznati preglednik",
    "login.device.mobile": "Mobilni",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Računalo",
    "login.recent": "Nedavno",
    "err.invalid_credentials": "Nevažeći podaci za prijavu",
    "err.bad_pin": "Pogrešan PIN",
    "err.account_blocked": "Račun blokiran",
    "err.bad_current_pw": "Trenutna lozinka je netočna",
    "err.insufficient_funds": "Nedovoljno sredstava, nadopunite svoj račun.",
    "err.amount_above_savings": "Iznos premašuje dostupnu štednju",
    "err.must_be_positive": "Vrijednost mora biti pozitivna",
    "err.invalid_format": "Nevažeći format",
    "err.email_taken": "Email već se koristi",
    "err.card_not_found": "Kartica nije pronađena",
    "err.physical_card_already_ordered": "Fizička kartica već je naručena",
    "err.beneficiary_not_found": "Primatelj nije pronađen",
    "err.invalid_code": "Nevažeći kod",
    "err.no_transfer_code": "Nema koda za ovaj prijenos",
    "err.link_expired": "Poveznica nevažeća ili istekla",
    "err.thread_not_found": "Razgovor nije pronađen",
    "err.empty_message": "Prazna poruka",
    "err.file_too_large": "Datoteka je prevelika (najviše 20 MB)",
    "err.forbidden": "Pristup odbijen",
    "err.bank_not_found": "Banka nije pronađena",
    "err.signups_closed": "Registracije su zatvorene za ovu banku.",
    "err.access_suspended": "Pristup privremeno obustavljen. Molimo kontaktirajte svoju banku.",
    "err.notif_not_closable": "Ova obavijest se ne može zatvoriti.",
    "auth.signup.tagline": "Pridružite nam se danas",
    "auth.login.tagline": "Dobrodošli natrag!",
    "auth.signup.tagline_sub": "Otvorite račun za 2 minute i pristupite svim našim premium bankarskim uslugama.",
    "auth.login.tagline_sub": "Pristupite svom osobnom prostoru i upravljajte financijama sigurno.",
    "auth.back": "Natrag na početnu stranicu",
    "auth.bullet.security": "Bankarska sigurnost vojničke razine",
    "auth.bullet.transfers": "Trenutačni prijenosi 24/7",
    "auth.bullet.investments": "Integrirane investicije i štednja",
    "auth.bullet.advisor": "Osobni savjetnik dostupan 7 dana u tjednu",
    "auth.footer": "Powered by {bank}",
    "auth.signup.title": "Izradite račun",
    "auth.login.title": "Prijava",
    "auth.field.lastName": "Prezime",
    "auth.field.firstName": "Ime",
    "auth.field.country": "Država",
    "auth.field.country.placeholder": "— Odaberite svoju državu —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Adresa",
    "auth.field.address.placeholder": "Glavna ulica 123, Zagreb",
    "auth.field.email": "E-pošta",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Lozinka",
    "auth.submit.signup": "Izradite moj račun",
    "auth.submit.login": "Prijavite se",
    "auth.login.badge": "Prijavite se na svoj račun",
    "auth.card.tagline": "Vaš korisnički prostor",
    "auth.submit.loading": "Učitavanje…",
    "auth.pin.title": "PIN kod",
    "auth.pin.subtitle": "Unesite svoj 4-znamenkasti PIN kod",
    "auth.pin.label": "PIN kod",
    "auth.pin.confirm": "Potvrdi PIN kod",
    "auth.pin.submit": "Potvrdi",
    "auth.pin.back": "Natrag na prijavu",
    "auth.pin.length_error": "PIN kod mora imati 4 znamenke",
    "auth.switch.have_account": "Već imate račun?",
    "auth.switch.login_link": "Prijava",
    "auth.switch.no_account": "Još niste klijent?",
    "auth.error.select_country": "Odaberite svoju državu",
    "kyc.title": "Provjera identiteta (KYC)",
    "kyc.sub": "Učitajte svoje dokumente za potvrdu računa. Svi dokumenti su šifrirani i povjerljivi.",
    "kyc.id_type": "Vrsta osobnog dokumenta *",
    "kyc.id_card": "Osobna iskaznica",
    "kyc.passport": "Putovnica",
    "kyc.driver_license": "Vozačka dozvola",
    "kyc.recto_passport": "Prednja strana putovnice *",
    "kyc.recto_id": "Prednja strana dokumenta *",
    "kyc.verso": "Stražnja strana dokumenta *",
    "kyc.selfie": "Selfie s osobnim dokumentom u ruci *",
    "kyc.address_proof": "Dokaz o adresi *",
    "kyc.hint_doc": "Slika ili PDF, maks. 5 MB",
    "kyc.hint_selfie": "Jasna fotografija, lice i dokument vidljivi",
    "kyc.hint_address": "Račun, izjava, potvrda… (maks. 5 MB)",
    "kyc.upload": "Učitaj",
    "kyc.replace": "Zamijeni",
    "pending.title": "Račun čeka na odobrenje",
    "pending.body": "Vaš zahtjev je primljen. Administrator će provjeriti vašu dokumentaciju i uskoro aktivirati vaš račun.",
    "pending.back": "Natrag na prijavu",
    "home.nav.features": "Značajke",
    "home.nav.security": "Sigurnost",
    "home.nav.invest": "Investicije",
    "home.nav.credits": "Krediti",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Prijavite se",
    "home.hero.tagline": "Online bankarstvo nove generacije",
    "home.hero.title1": "Upravljajte svojim novcem",
    "home.hero.title2": "s inteligencijom",
    "home.hero.sub": "Cjelovita bankarska platforma — prijenosi, štednja, investicije i krediti s vašeg pametnog telefona.",
    "home.hero.login": "Prijavite se",
    "home.hero.trust.secure": "100% Sigurno",
    "home.hero.trust.fast": "Brzo otvaranje",
    "home.hero.trust.encrypted": "Šifrirani podaci",
    "home.hero.trust.support": "Usluga 24/7",
    "home.mockup.balance": "Dostupno stanje",
    "home.mockup.currency": "EUR · Glavni račun",
    "home.mockup.recharge": "Dopuni",
    "home.mockup.send": "Pošalji",
    "home.mockup.save": "Štedi",
    "home.mockup.salary": "Plaća – ožujak",
    "home.mockup.rent": "Najam – travanj",
    "home.mockup.investment": "Investicija",
    "home.mockup.days2": "prije 2 dana",
    "home.mockup.days3": "prije 3 dana",
    "home.mockup.days5": "prije 5 dana",
    "home.stats.accounts": "Aktivni računi",
    "home.stats.uptime": "Dostupnost",
    "home.stats.transfers": "Prijenosi",
    "home.stats.support": "Podrška",
    "home.features.label": "Značajke",
    "home.features.title": "Sve što vam treba",
    "home.features.sub": "Cjelovita platforma osmišljena za pojednostavljivanje upravljanja vašim financijama svaki dan.",
    "home.feature.security": "Vojna sigurnost",
    "home.feature.security_body": "Šifriranje AES-256, dvofaktorska autentikacija i nadzor računa 24/7.",
    "home.feature.cards": "Virtualna i fizička kartica",
    "home.feature.cards_body": "Izdajte kartice za nekoliko sekundi ili naručite pravu fizičku karticu dostavljenu kući.",
    "home.feature.transfers": "Ekspresni prijenosi",
    "home.feature.transfers_body": "Šaljite i primajte novac bilo gdje na svijetu za nekoliko sekundi.",
    "home.feature.analytics": "Analiza u stvarnom vremenu",
    "home.feature.analytics_body": "Pametne nadzorne ploče za praćenje troškova i predviđanje vaših financija.",
    "home.feature.investments": "Integrirane investicije",
    "home.feature.investments_body": "Pristup odabranim investicijskim proizvodima izravno s vašeg računa.",
    "home.feature.support": "Posvećena podrška 24/7",
    "home.feature.support_body": "Osobni bankarski savjetnik dostupan u bilo koje vrijeme putem sigurnih poruka.",
    "home.tpl.label": "Povjerenje",
    "home.tpl.title": "Vjeruju nam",
    "home.mockup.balance_label": "Stanje",
    "home.mockup.virement": "Prijenos",
    "home.mockup.salaire": "Plaća",
    "home.security.label": "Sigurnost",
    "home.security.title1": "Vaše financije",
    "home.security.title2": "u apsolutnoj sigurnosti",
    "home.security.sub": "Primjenjujemo najstrože standarde bankarske sigurnosti za zaštitu svakog centa.",
    "home.security.aes.title": "Šifriranje AES-256",
    "home.security.aes.body": "Svi osjetljivi podaci su šifrirani istim algoritmima koje koriste velike međunarodne banke.",
    "home.security.auth.title": "Ojačana autentikacija",
    "home.security.auth.body": "Dvostruka provjera identiteta pri svakoj prijavi i za svaku osjetljivu operaciju na vašem računu.",
    "home.security.compliance.title": "Usklađenost s PSD2/GDPR",
    "home.security.compliance.body": "Poštujemo sve europske propise o platnim uslugama i zaštiti podataka.",
    "home.invest.label": "Investicije",
    "home.invest.title": "Neka vaš novac raste",
    "home.invest.sub": "6 investicijskih kategorija odabranih zbog njihovog potencijala i pouzdanosti.",
    "home.invest.yield": "Prinos",
    "home.credits.label": "Krediti",
    "home.credits.title": "Rješenja za svaki projekt",
    "home.credits.sub": "Konkurentne kamatne stope, brzi odgovori i personalizirana podrška za sve vaše projekte.",
    "home.credits.rate": "Kamatna stopa",
    "home.faq.label": "FAQ",
    "home.faq.title": "Česta pitanja",
    "home.faq.q1": "Kako otvoriti račun?",
    "home.faq.a1": "Registracija traje manje od 2 minute. Jednostavno unesite osobne podatke, odaberite lozinku i vaš račun će biti aktiviran nakon potvrde našeg tima.",
    "home.faq.q2": "Jesu li moji podaci sigurni?",
    "home.faq.a2": "Da. Svi podaci su šifrirani s AES-256 u mirovanju i TLS 1.3 u prijenosu. Poštujemo međunarodne bankarske standarde i propise GDPR/PSD2.",
    "home.faq.q3": "Koje vrste investicija nudite?",
    "home.faq.a3": "Nudimo 6 kategorija: Nekretnine, Crowdfunding, Obnovljive izvore energije, Burzu, Participativno i Kripto. Svaki proizvod je odabran zbog omjera rizika i prinosa.",
    "home.faq.q4": "Kako zatražiti kredit?",
    "home.faq.a4": "Idite u odjeljak Kredit u svom korisničkom prostoru, odaberite vrstu zajma, unesite iznos i trajanje. Vaš zahtjev se obrađuje u roku od 24 sata od strane naših savjetnika.",
    "home.faq.q5": "Postoje li skrivene naknade?",
    "home.faq.a5": "Bez skrivenih naknada. Sve cijene su jasno prikazane pri svakoj operaciji. Uvjeti su dostupni u vašem korisničkom prostoru u bilo koje vrijeme.",
    "home.faq.q6": "Kako kontaktirati podršku?",
    "home.faq.a6": "Koristite integriranu razmjenu poruka u svom korisničkom prostoru za izravni kontakt s vašim osobnim bankarskim savjetnikom, dostupnim 24/7.",
    "home.cta.title": "Spremni za početak?",
    "home.cta.sub": "Pridružite se tisućama korisnika koji upravljaju svojim financijama s {bank}. Registracija traje manje od 2 minute.",
    "home.footer.products": "Proizvodi",
    "home.footer.services": "Usluge",
    "home.footer.access": "Brzi pristup",
    "home.footer.accounts": "Računi",
    "home.footer.transfers": "Prijenosi",
    "home.footer.cards": "Kartice",
    "home.footer.savings": "Štednja",
    "home.footer.investments": "Investicije",
    "home.footer.credits": "Krediti",
    "home.footer.financing": "Financiranje",
    "home.footer.messages": "Poruke",
    "home.footer.login": "Prijava",
    "home.footer.signup": "Otvorite račun",
    "home.footer.support": "Podrška 24/7",
    "home.footer.tagline": "Vaša online banka, dostupna svugdje i u bilo koje vrijeme.",
    "home.footer.copyright": "© {year} {bank} — Sva prava pridržana",
    "home.footer.powered": "Powered by {bank}",
    "app.menu_label": "Glavni izbornik",
    "app.nav.dashboard": "Nadzorna ploča",
    "app.nav.accounts": "Računi",
    "app.nav.transactions": "Transakcije",
    "app.nav.transfers": "Prijenosi",
    "app.nav.cards": "Kartice",
    "app.nav.savings": "Štednja",
    "app.nav.investments": "Investicije",
    "app.nav.credit": "Kredit",
    "app.nav.financing": "Financiranje",
    "app.nav.messages": "Poruke",
    "app.nav.card_tracking": "Praćenje kartice",
    "app.nav.notifications": "Obavijesti",
    "app.nav.settings": "Postavke",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Početna",
    "app.nav.mobile.transfers": "Prijenosi",
    "app.nav.mobile.cards": "Kartice",
    "app.nav.mobile.transactions": "Transakcije",
    "app.nav.more.invest": "Investiraj",
    "app.user_role": "Klijent",
    "app.logout": "Odjava",
    "app.search": "Pretraži…",
    "app.more_title": "Više značajki",
    "app.more_btn": "Više",
    "app.loading": "Učitavanje…",
    "app.error_back": "Natrag na prijavu",
    "app.see_more_notifs": "Pogledaj još {count} obavijesti →",
    "page.accounts": "Moji računi",
    "page.transactions": "Transakcije",
    "page.transfers": "Novi prijenos",
    "page.deposit": "Polog",
    "page.cards": "Moje kartice",
    "page.savings": "Štednja",
    "page.investments": "Investicije",
    "page.credits": "Kredit",
    "page.financing": "Financiranje projekata",
    "page.messages": "Poruke",
    "page.settings": "Postavke",
    "page.notifications": "Važne obavijesti",
    "page.profile": "Moj profil",
    "dash.greeting": "Dobar dan, {name}",
    "dash.overview": "Pregled vaših financija — {date}",
    "dash.total_balance": "Ukupno stanje",
    "dash.this_month": "+{amount} ovaj mjesec",
    "dash.view_iban": "Pogledaj moj IBAN",
    "dash.my_accounts": "Moji računi",
    "dash.primary_account": "Glavni račun",
    "dash.savings_account": "Štedni račun",
    "dash.remunerated": "Kamatonosni",
    "dash.details": "Detalji",
    "dash.transfer_btn": "Prijenos",
    "dash.quick_actions": "Brze radnje",
    "dash.quick.deposit": "Polog",
    "deposit.title": "Informacije o pologu",
    "dash.quick.transfer": "Prijenos",
    "dash.quick.history": "Povijest",
    "dash.quick.cards": "Moje kartice",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Mjesečni proračun",
    "dash.income": "Prihodi",
    "dash.expenses": "Rashodi",
    "dash.spending_cat": "Rashodi po kategorijama",
    "dash.no_spending": "Nema zabilježenih rashoda.",
    "dash.recent_tx": "Nedavne transakcije",
    "dash.see_all": "Vidi sve",
    "dash.no_tx": "Nema operacija.",
    "dash.main_card": "Moja glavna kartica",
    "dash.manage": "Upravljaj",
    "dash.block": "Blokiraj",
    "dash.pin": "PIN kôd",
    "dash.security": "Sigurnost",
    "dash.more": "Više",
    "dash.no_card": "Nema izdane kartice",
    "dash.monthly_activity": "Mjesečna aktivnost",
    "dash.operations": "Operacije",
    "dash.holder": "Nositelj",
    "dash.expires": "Vrijedi do",
    "dash.visa_label": "VISA",
    "dash.balance": "Dostupno stanje",
    "tx.all": "Sve",
    "tx.income": "Prihodi",
    "tx.expenses": "Rashodi",
    "tx.today": "Danas",
    "tx.yesterday": "Jučer",
    "tx.none": "Nema transakcija.",
    "transfer.title": "Brzi prijenos",
    "transfer.recipient": "Primatelj",
    "transfer.recipient_ph": "Ime, IBAN, broj telefona…",
    "transfer.amount": "Iznos",
    "transfer.label": "Opis",
    "transfer.reason": "Razlog (neobavezno)",
    "transfer.reason_ph": "Npr.: najam, povrat…",
    "transfer.execution": "Izvršenje",
    "transfer.immediate": "Trenutačno",
    "transfer.delayed": "Odgođeno",
    "transfer.balance": "Dostupno stanje:",
    "transfer.continue": "Nastavi",
    "transfer.confirm": "Potvrdi",
    "transfer.cancel": "Odustani",
    "transfer.code": "Potvrdni kôd",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Unesite primljeni kôd za potvrdu prijenosa",
    "transfer.pending.title": "Prijenos čeka na potvrdu",
    "transfer.pending.amount": "Iznos:",
    "transfer.pending.to": "Za:",
    "transfer.pending.hint": "Vaš potvrdni kôd bit će poslan e-poštom ili putem zvona za obavijesti. Kôd se sastoji od 6 znamenki. Ako ga ne primite, kontaktirajte podršku.",
    "transfer.pending.validate": "Potvrdi prijenos",
    "transfer.saved": "Prijenos spremljen. Zatražite od administratora potvrdni kôd.",
    "transfer.done": "Prijenos izvršen",
    "transfer.validated": "Prijenos potvrđen!",
    "transfer.progress.processing": "Obrada prijenosa…",
    "transfer.progress.completed": "Prijenos izvršen!",
    "transfer.progress.default_message": "Vaš prijenos se obrađuje. Molimo pričekajte.",
    "cards.none": "Nema izdane kartice.\nKontaktirajte svog administratora.",
    "cards.holder": "Nositelj",
    "cards.expires": "Vrijedi do",
    "cards.block": "Blokiraj",
    "cards.pin": "PIN kôd",
    "cards.more": "Više",
    "cards.limits": "Ograničenja kartice",
    "cards.payment": "Plaćanje",
    "cards.withdrawal": "Podizanje",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Blokirana",
    "cards.status_active": "Aktivna",
    "cards.toast.blocked": "Kartica blokirana",
    "cards.toast.unblocked": "Kartica odblokirana",
    "cards.toast.physical_ordered": "Fizička kartica naručena! Dostava za 5–10 radnih dana.",
    "cards.toast.created": "Kartica uspješno generirana!",
    "cards.none_sub": "Nema izdane kartice. Generirajte svoju virtualnu karticu sada.",
    "cards.generate_btn": "Generiraj moju karticu",
    "cards.create.title": "Generiraj moju virtualnu karticu",
    "cards.create.network": "Platna mreža",
    "cards.create.color": "Boja kartice",
    "cards.create.preview_name": "VAŠE IME",
    "cards.create.preview_expiry": "MM/GG",
    "cards.create.loading": "Generiranje…",
    "cards.create.submit": "Generiraj karticu",
    "cards.btn.unblock": "Odblokiraj",
    "cards.btn.block": "Blokiraj",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fizička",
    "cards.btn.physical_done": "Naručena",
    "cards.limits_title": "Ograničenja",
    "cards.reveal.title": "Informacije o kartici",
    "cards.reveal.number": "Cijeli broj",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Istek",
    "cards.reveal.warning": "Nikada ne dijelite ove informacije. Zatvorite ovaj prozor nakon korištenja.",
    "cards.pin.title": "PIN kod",
    "cards.pin.message": "Ovaj PIN kod koristi se za sva vaša online plaćanja i podizanja. Nikad ga ne otkrivajte nikome.",
    "cards.physical.title": "Naruči fizičku karticu",
    "cards.physical.fee_label": "Naknada za dostavu",
    "cards.physical.balance_label": "Vaš saldo",
    "cards.physical.insufficient": "Nedovoljan saldo. Dopunite račun prije naručivanja.",
    "cards.physical.info": "Naknada će biti naplaćena s vašeg računa. Dostava za 5–10 radnih dana.",
    "cards.physical.order_btn": "Naruči ({fee})",
    "cards.physical.ordering": "Naručujem…",
    "cards.physical.order_cta": "Naruči fizičku karticu",
    "cards.physical.choose_type": "Odaberi svoju karticu",
    "cards.physical.features": "Prednosti",
    "cards.buy_virtual_cta": "Kupi virtualnu karticu",
    "notif.none": "Trenutačno nema obavijesti.",
    "notif.contact_support": "Kontaktirajte podršku",
    "profile.change_photo": "Promijeni fotografiju",
    "profile.remove_photo": "Ukloni fotografiju",
    "profile.email": "E-pošta",
    "profile.phone": "Telefon",
    "profile.country": "Država",
    "profile.address": "Adresa",
    "profile.notifs_title": "Obavijesti ({count})",
    "profile.no_notifs": "Nema obavijesti.",
    "profile.logout": "Odjava",
    "profile.photo_updated": "Profilna fotografija ažurirana",
    "profile.photo_removed": "Fotografija uklonjena",
    "profile.photo_too_large": "Slika je prevelika (maks. 1 MB)",
    "profile.powered": "Powered by {bank}",
    "iban.title": "Moj IBAN",
    "iban.loading": "Učitavanje…",
    "iban.bic": "Kôd BIC / SWIFT",
    "iban.share_info": "Podijelite ove podatke za primanje prijenosa na vaš račun.",
    "iban.copy": "Kopiraj",
    "iban.copied": "Kopirano u međuspremnik",
    "iban.copy_fail": "Kopiranje nije uspjelo",
    "iban.close": "Zatvori",
    "accounts.all": "Svi vaši računi kod {bank}.",
    "accounts.primary": "Glavni račun",
    "accounts.savings": "Štedni račun",
    "accounts.primary_tag": "Primarni",
    "accounts.secondary_tag": "Sekundarni",
    "accounts.objective": "Cilj: {amount} · {pct}%",
    "accounts.copy": "Kopiraj",
    "accounts.loading": "Učitavanje…",
    "accounts.iban_copied": "IBAN kopiran",
    "accounts.copy_fail": "Kopiranje nije uspjelo",
    "savings.total": "Ukupna uštedina",
    "savings.target_total": "Ukupni cilj",
    "savings.progress": "Napredak",
    "savings.active": "Aktivni ciljevi",
    "savings.new_goal": "Novi cilj",
    "savings.none": "Još nema ciljeva štednje.",
    "savings.none_sub": "Stvorite svoj prvi cilj za početak.",
    "savings.saved": "Uštedino:",
    "savings.goal": "Cilj:",
    "savings.deadline": "Rok: {date}",
    "savings.deposit": "Uplati",
    "savings.withdraw": "Podigne",
    "savings.loading": "Učitavanje…",
    "savings.form.title": "Novi cilj štednje",
    "savings.form.cancel": "Odustani",
    "savings.form.create": "Stvori",
    "savings.form.name": "Naziv cilja",
    "savings.form.name_ph": "Putovanje u Japan, Auto, …",
    "savings.form.target": "Ciljni iznos",
    "savings.form.initial": "Početni ulog",
    "savings.form.deadline": "Rok (neobavezno)",
    "savings.form.color": "Boja",
    "savings.deposit_title": "Uplata na \"{name}\"",
    "savings.deposit_amount": "Iznos za uplatu",
    "savings.balance_hint": "Stanje na glavnom računu: {amount}",
    "savings.withdraw_prompt": "Koliko podignuti iz \"{name}\"?",
    "savings.close_confirm": "Zatvoriti cilj \"{name}\"? Uštedina će biti vraćena na vaš glavni račun.",
    "savings.toast.created": "Cilj stvoren",
    "savings.toast.deposited": "Uplata izvršena",
    "savings.toast.withdrawn": "Podizanje izvršeno",
    "savings.toast.closed": "Cilj zatvoren",
    "invest.total": "Ukupne investicije",
    "invest.profits": "Kumulativni prihodi",
    "invest.count": "Investicije",
    "invest.products": "Dostupni proizvodi",
    "invest.invest_btn": "Investiraj",
    "invest.my": "Moje investicije",
    "invest.pending": "Na čekanju",
    "invest.validated": "Potvrđeno",
    "invest.refused": "Odbijeno",
    "invest.withdraw.btn": "Povuci",
    "invest.withdraw.title": "Povuci moju investiciju",
    "invest.withdraw.intro": "Upravo ćete povući svoju investiciju „{product}\". Početni kapital i obračunata dobit bit će uplaćeni na vaš glavni saldo.",
    "invest.withdraw.principal": "Početni kapital",
    "invest.withdraw.profit": "Dobit",
    "invest.withdraw.total": "Ukupno za primanje",
    "invest.withdraw.confirm": "Potvrdi povlačenje",
    "invest.withdraw.warning": "Ova radnja je nepovratna. Investicija će biti zatvorena.",
    "invest.withdraw.success": "Investicija povučena — {amount} uplaćeno na saldo.",
    "invest.withdrawn": "Povučeno",
    "invest.cta_sub": "Odaberite vrstu investicije koja vam odgovara. Savjetnik će vam pomoći od prvog zahtjeva.",
    "invest.form.amount": "Iznos za investiranje",
    "invest.form.balance": "Dostupno stanje: {amount}",
    "invest.form.note": "Napomena za savjetnika (neobavezno)",
    "invest.form.note_ph": "Navedite svoja očekivanja…",
    "invest.form.advisor_info": "Automatski će se otvoriti razgovor s vašim savjetnikom.",
    "invest.form.cancel": "Odustani",
    "invest.form.submit": "Pošalji zahtjev",
    "invest.toast.requested": "Zahtjev zabilježen — razgovor s vašim savjetnikom je otvoren.",
    "invest.p1.name": "Nekretnine",
    "invest.p1.desc": "SCPI, nekretninski fondovi, nekretninski crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Participativno financiranje odabranih projekata",
    "invest.p3.name": "Obnovljivi izvori",
    "invest.p3.desc": "Solarni, vjetarni i zeleni projekti",
    "invest.p4.name": "Burza",
    "invest.p4.desc": "Dionice, ETF i diverzificirani fondovi",
    "invest.p5.name": "Participativno",
    "invest.p5.desc": "Zajmovi malim poduzećima i lokalnim poduzetnicima",
    "invest.p6.name": "Kripto",
    "invest.p6.desc": "Bitcoin, Ethereum i viševalutni novčanik",
    "msg.online": "Na mreži",
    "msg.loading": "Učitavanje…",
    "msg.none": "Nema poruka. Pokrenite razgovor sa svojim savjetnikom.",
    "msg.placeholder": "Napišite poruku…",
    "msg.attach.image": "Slika",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Datoteka / Dokument",
    "msg.pdf_doc": "PDF dokument",
    "msg.file": "Datoteka",
    "msg.too_large": "Datoteka je prevelika (maks. {size}).",
    "msg.withdraw": "Povuci",
    "credits.cta_sub": "Podnesite zahtjev izravno online — odgovor u roku od 24 sata.",
    "credits.learn_more": "Saznaj više",
    "credits.request": "Zatraži",
    "credits.history": "Moji zahtjevi",
    "credits.status.pending": "Na čekanju",
    "credits.status.accepted": "Prihvaćeno",
    "credits.status.refused": "Odbijeno",
    "credits.chars": "Karakteristike",
    "credits.amount_range": "Iznos: {min}€ – {max}€",
    "credits.rate_range": "Kamatna stopa: {min}% – {max}% godišnje",
    "credits.duration_range": "Trajanje: {min} do {max} mjeseci",
    "credits.deposit_min": "Početni ulog: {amount}€ minimum",
    "credits.garantie": "Jamstvo: {garantie}",
    "credits.eligibility": "Uvjeti prihvatljivosti",
    "credits.elig.income": "Dokazati redovite i stabilne prihode",
    "credits.elig.deposit": "Izvršiti početni ulog od {amount}€",
    "credits.elig.garantie": "Predočiti jamstvo: {garantie}",
    "credits.form.title": "Zahtjev — {name}",
    "credits.form.cancel": "Odustani",
    "credits.form.submit": "Pošalji zahtjev",
    "credits.form.amount": "Željeni iznos (€)",
    "credits.form.duration": "Trajanje (mjeseci)",
    "credits.form.reason": "Razlog (neobavezno)",
    "credits.form.reason_ph": "Opišite svoj projekt…",
    "credits.form.close": "Zatvori",
    "credits.toast.submitted": "Zahtjev za kredit poslan — odgovor ćete primiti u roku od 24 sata.",
    "credits.p1.name": "Osobni zajam",
    "credits.p1.desc": "Za financiranje svakodnevnih potreba ili osobnih projekata.",
    "credits.p2.name": "Hipotekarni zajam",
    "credits.p2.desc": "Za financiranje kupnje ili izgradnje nekretnine.",
    "credits.p3.name": "Auto / Moto zajam",
    "credits.p3.desc": "Kupite vozilo bez brige.",
    "credits.p4.name": "Studentski zajam",
    "credits.p4.desc": "Financijska pomoć za visoko obrazovanje.",
    "credits.p5.name": "Poslovni zajam",
    "credits.p5.desc": "Za podršku rastu poslovne aktivnosti.",
    "credits.p6.name": "Ekspresni zajam",
    "credits.p6.desc": "Brzo rješenje za hitne potrebe.",
    "financing.reimbursable": "Povratno financiranje",
    "financing.reimbursable_desc": "Financijska podrška s fiksnim uvjetima za pokretanje ili razvoj aktivnosti.",
    "financing.non_reimbursable": "Bespovratno financiranje",
    "financing.non_reimbursable_desc": "Za projekte s društvenim, obrazovnim ili humanitarnim učinkom (NVO, udruge).",
    "financing.cta_sub": "Prilagođena rješenja za ostvarenje vaših ambicija.",
    "financing.request": "Zatraži financiranje",
    "financing.history": "Moji zahtjevi",
    "financing.reimbursable_label": "Povratno",
    "financing.non_reimbursable_label": "Bespovratno",
    "financing.status.pending": "Na čekanju",
    "financing.status.granted": "Odobreno",
    "financing.status.refused": "Odbijeno",
    "financing.form.title": "Zahtjev — {name}",
    "financing.form.cancel": "Odustani",
    "financing.form.submit": "Pošalji zahtjev",
    "financing.form.project": "Naziv projekta",
    "financing.form.project_ph": "Moj projekt",
    "financing.form.amount": "Traženi iznos (€)",
    "financing.form.sector": "Sektor",
    "financing.form.sector_ph": "Tech, poljoprivreda, …",
    "financing.form.description": "Opis projekta",
    "financing.form.description_ph": "Predstavite vaš projekt, njegove ciljeve i očekivani učinak…",
    "settings.security": "Sigurnost",
    "settings.current_pw": "Trenutna lozinka",
    "settings.new_pw": "Nova lozinka",
    "settings.confirm_pw": "Potvrdi",
    "settings.submit": "Promijeni lozinku",
    "settings.no_match": "Lozinke se ne podudaraju",
    "settings.changed": "Lozinka promijenjena",
    "common.cancel": "Odustani",
    "common.confirm": "Potvrdi",
    "common.close": "Zatvori",
    "common.save": "Spremi",
    "common.loading": "Učitavanje…",
    "common.send": "Pošalji",
    "common.total": "Ukupno",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Proc. mesečna rata",
    "credits.rate_annual": "/ god.",
    "credits.rate_used": "Proc. stopa:",
    "credits.simulation": "Simulacija mesečne rate",
    "credits.total_cost": "Proc. ukupni troškovi",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Dobrodošli",
    "common.delete": "Izbriši",
    "cards.delete.title": "Izbriši karticu",
    "cards.delete.confirm_msg": "Ova radnja je nepovratna. Kartica će biti trajno obrisana.",
    "cards.delete.physical_tooltip": "Fizička kartica naručena — brisanje nemoguće",
    "cards.order.confirmed": "Narudžba potvrđena!",
    "cards.order.confirmed_body": "Vaša fizička kartica se priprema. Koristite ovaj broj za praćenje dostave.",
    "cards.order.tracking_no": "Br. praćenja",
    "cards.fee_deducted": "{amount} bit će odbijeno od vašeg stanja",
    "cards.insufficient_short": "Nedovoljno stanje ({available} dostupno)",
    "transfer.section.beneficiary": "Primatelj",
    "transfer.section.details": "Prijenos",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banka",
    "transfer.field.city": "Grad",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banka:",
    "transfer.progress.paused": "Čeka na potvrdu",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Varijabilna",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Kartica izbrisana",
    "cards.feat.gold_exclusive": "Ekskluzivna zlatna kartica",
    "cards.feat.cashback_05": "Povrat novca 0,5%",
    "cards.feat.buyer_protection": "Zaštita kupca",
    "cards.feat.support_standard": "Standardna podrška",
    "cards.feat.plastic_premium": "Premium plastična kartica",
    "cards.feat.cashback_1": "Povrat novca 1%",
    "cards.feat.travel_standard": "Standardno putno osiguranje",
    "cards.feat.support_priority": "Prioritetna podrška",
    "cards.feat.metal_brushed": "Brušena metalna kartica",
    "cards.feat.cashback_2_unlimited": "Neograničeni 2% povrat novca",
    "cards.feat.travel_premium": "Premium putno osiguranje",
    "cards.feat.vip_lounge": "Pristup VIP salonima",
    "cards.feat.support_247": "24/7 podrška",
    "auth.demo.title": "Prijava administratora",
    "auth.demo.subtitle": "Unesite admin podatke za pregled demoa",
    "credits.garantie.none": "Bez jamstva",
    "credits.garantie.mortgage": "Hipoteka na financiranu nekretninu",
    "credits.garantie.vehicle": "Financirano vozilo",
    "credits.garantie.parental": "Roditeljsko ili bankarsko",
    "credits.garantie.professional": "Poslovna imovina ili osobno jamstvo",
    "cards.reveal.pin_prompt": "Unesite svoj 4-znamenkasti PIN kod kako biste prikazali informacije o kartici.",
    "cards.reveal.show": "Prikaži",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Dva PIN koda se ne podudaraju",
    "auth.pin.digit_hint": "4 znamenke",
  
  },
  bs: {
    "notif.transfer_code.title": "Kod za potvrdu transfera",
    "notif.transfer_code.body": "Kopirajte ovaj kod za potvrdu transfera.",
    "notif.transfer_code.expires_in": "Nestaje za {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mjeseci",
    "credits.n_months": "{n} mjeseci",
    "msg.read": "Pročitano",
    "msg.sent": "Poslano",
    "msg.attach_aria": "Priloži datoteku",
    "login.device.unknown": "Nepoznati preglednik",
    "login.device.mobile": "Mobilni",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Računar",
    "login.recent": "Nedavno",
    "err.invalid_credentials": "Nevažeći podaci za prijavu",
    "err.bad_pin": "Pogrešan PIN",
    "err.account_blocked": "Račun blokiran",
    "err.bad_current_pw": "Trenutna lozinka je netačna",
    "err.insufficient_funds": "Nedovoljno sredstava, dopunite svoj račun.",
    "err.amount_above_savings": "Iznos premašuje dostupnu uštedu",
    "err.must_be_positive": "Vrijednost mora biti pozitivna",
    "err.invalid_format": "Nevažeći format",
    "err.email_taken": "Email se već koristi",
    "err.card_not_found": "Kartica nije pronađena",
    "err.physical_card_already_ordered": "Fizička kartica već je naručena",
    "err.beneficiary_not_found": "Primalac nije pronađen",
    "err.invalid_code": "Nevažeći kod",
    "err.no_transfer_code": "Nema koda za ovaj prijenos",
    "err.link_expired": "Link nevažeći ili istekao",
    "err.thread_not_found": "Razgovor nije pronađen",
    "err.empty_message": "Prazna poruka",
    "err.file_too_large": "Datoteka je prevelika (max 20 MB)",
    "err.forbidden": "Pristup odbijen",
    "err.bank_not_found": "Banka nije pronađena",
    "err.signups_closed": "Registracije su zatvorene za ovu banku.",
    "err.access_suspended": "Pristup privremeno obustavljen. Molimo kontaktirajte vašu banku.",
    "err.notif_not_closable": "Ovo obavještenje se ne može zatvoriti.",
    "auth.signup.tagline": "Pridružite nam se danas",
    "auth.login.tagline": "Dobrodošli!",
    "auth.signup.tagline_sub": "Otvorite račun za 2 minute i pristupite svim našim premium bankarskim uslugama.",
    "auth.login.tagline_sub": "Pristupite svom ličnom prostoru i upravljajte finansijama na siguran način.",
    "auth.back": "Povratak na početnu stranicu",
    "auth.bullet.security": "Bankarska sigurnost vojnog nivoa",
    "auth.bullet.transfers": "Trenutni transferi 24h/24",
    "auth.bullet.investments": "Integrisana ulaganja i štednja",
    "auth.bullet.advisor": "Lični savjetnik dostupan 7 dana u sedmici",
    "auth.footer": "Pokreće {bank}",
    "auth.signup.title": "Kreirajte račun",
    "auth.login.title": "Prijava",
    "auth.field.lastName": "Prezime",
    "auth.field.firstName": "Ime(na)",
    "auth.field.country": "Zemlja",
    "auth.field.country.placeholder": "— Odaberite svoju zemlju —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Adresa",
    "auth.field.address.placeholder": "Ulica mira 123, Sarajevo",
    "auth.field.email": "Email",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Lozinka",
    "auth.submit.signup": "Kreiraj moj račun",
    "auth.submit.login": "Prijavite se",
    "auth.login.badge": "Prijavite se na svoj račun",
    "auth.card.tagline": "Vaš korisnički prostor",
    "auth.submit.loading": "Učitavanje…",
    "auth.pin.title": "PIN kod",
    "auth.pin.subtitle": "Unesite svoj 4-cifreni PIN kod",
    "auth.pin.label": "PIN kod",
    "auth.pin.confirm": "Potvrdi PIN kod",
    "auth.pin.submit": "Potvrdi",
    "auth.pin.back": "Nazad na prijavu",
    "auth.pin.length_error": "PIN kod mora imati 4 cifre",
    "auth.switch.have_account": "Već imate račun?",
    "auth.switch.login_link": "Prijava",
    "auth.switch.no_account": "Još niste klijent?",
    "auth.error.select_country": "Odaberite svoju zemlju",
    "kyc.title": "Provjera identiteta (KYC)",
    "kyc.sub": "Učitajte svoje dokumente za validaciju računa. Svi dokumenti su šifrirani i povjerljivi.",
    "kyc.id_type": "Vrsta identifikacionog dokumenta *",
    "kyc.id_card": "Lična karta",
    "kyc.passport": "Pasoš",
    "kyc.driver_license": "Vozačka dozvola",
    "kyc.recto_passport": "Prednja strana pasoša *",
    "kyc.recto_id": "Prednja strana dokumenta *",
    "kyc.verso": "Zadnja strana dokumenta *",
    "kyc.selfie": "Selfie s dokumentom u ruci *",
    "kyc.address_proof": "Dokaz o prebivalištu *",
    "kyc.hint_doc": "Slika ili PDF, max 5 MB",
    "kyc.hint_selfie": "Jasna fotografija, lice i dokument vidljivi",
    "kyc.hint_address": "Račun, izvod, potvrda… (max 5 MB)",
    "kyc.upload": "Učitaj",
    "kyc.replace": "Zamijeni",
    "pending.title": "Račun na čekanju",
    "pending.body": "Vaš zahtjev je primljen. Administrator će provjeriti Vaš dosje i aktivirati Vaš račun u kratkom roku.",
    "pending.back": "Povratak na prijavu",
    "home.nav.features": "Funkcionalnosti",
    "home.nav.security": "Sigurnost",
    "home.nav.invest": "Investicije",
    "home.nav.credits": "Krediti",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Prijavite se",
    "home.hero.tagline": "Online bankarstvo nove generacije",
    "home.hero.title1": "Upravljajte svojim novcem",
    "home.hero.title2": "s inteligencijom",
    "home.hero.sub": "Kompletna bankarska platforma — transferi, štednja, investicije i krediti s vašeg pametnog telefona.",
    "home.hero.login": "Prijavite se",
    "home.hero.trust.secure": "100% Sigurno",
    "home.hero.trust.fast": "Brzo otvaranje",
    "home.hero.trust.encrypted": "Šifrirani podaci",
    "home.hero.trust.support": "Servis 24/7",
    "home.mockup.balance": "Raspoloživo stanje",
    "home.mockup.currency": "EUR · Glavni račun",
    "home.mockup.recharge": "Dopuniti",
    "home.mockup.send": "Pošalji",
    "home.mockup.save": "Štednja",
    "home.mockup.salary": "Plata Mart",
    "home.mockup.rent": "Stanarina April",
    "home.mockup.investment": "Investicija",
    "home.mockup.days2": "prije 2 dana",
    "home.mockup.days3": "prije 3 dana",
    "home.mockup.days5": "prije 5 dana",
    "home.stats.accounts": "Aktivni računi",
    "home.stats.uptime": "Dostupnost",
    "home.stats.transfers": "Transferi",
    "home.stats.support": "Podrška",
    "home.features.label": "Funkcionalnosti",
    "home.features.title": "Sve što vam treba",
    "home.features.sub": "Kompletna platforma dizajnirana da pojednostavi upravljanje Vašim finansijama svakodnevno.",
    "home.feature.security": "Vojna sigurnost",
    "home.feature.security_body": "AES-256 šifriranje, dvofaktorska autentifikacija i nadzor računa 24/7.",
    "home.feature.cards": "Virtuelna i fizička kartica",
    "home.feature.cards_body": "Izdajte kartice za nekoliko sekundi ili naručite pravu fizičku karticu dostavljenu na vašu adresu.",
    "home.feature.transfers": "Brzi transferi",
    "home.feature.transfers_body": "Šaljite i primajte novac svuda u svijetu za nekoliko sekundi.",
    "home.feature.analytics": "Analiza u realnom vremenu",
    "home.feature.analytics_body": "Pametne kontrolne ploče za praćenje troškova i anticipaciju Vaših finansija.",
    "home.feature.investments": "Integrisane investicije",
    "home.feature.investments_body": "Pristupite odabranim investicionim proizvodima direktno s Vašeg računa.",
    "home.feature.support": "Dedikirana podrška 24/7",
    "home.feature.support_body": "Lični bankarski savjetnik dostupan u svakom trenutku putem sigurnog dopisivanja.",
    "home.tpl.label": "Povjerenje",
    "home.tpl.title": "Vjeruju nam",
    "home.mockup.balance_label": "Stanje",
    "home.mockup.virement": "Transfer",
    "home.mockup.salaire": "Plata",
    "home.security.label": "Sigurnost",
    "home.security.title1": "Vaše finansije",
    "home.security.title2": "u apsolutnoj sigurnosti",
    "home.security.sub": "Primjenjujemo najstrože standarde bankarske sigurnosti kako bismo zaštitili svaki cent.",
    "home.security.aes.title": "AES-256 šifriranje",
    "home.security.aes.body": "Svi osjetljivi podaci šifrirani su istim algoritmima koje koriste velike međunarodne banke.",
    "home.security.auth.title": "Pojačana autentifikacija",
    "home.security.auth.body": "Dvostruka provjera identiteta pri svakoj prijavi i svakoj osjetljivoj operaciji na Vašem računu.",
    "home.security.compliance.title": "Usklađenost PSD2/GDPR",
    "home.security.compliance.body": "Poštujemo sve europske propise o platnim uslugama i zaštiti podataka.",
    "home.invest.label": "Investicije",
    "home.invest.title": "Uvećajte svoj novac",
    "home.invest.sub": "6 investicionih kategorija odabranih po potencijalu i pouzdanosti.",
    "home.invest.yield": "Prinos",
    "home.credits.label": "Krediti",
    "home.credits.title": "Rješenja prilagođena svakom projektu",
    "home.credits.sub": "Konkurentne stope, brzi odgovori i personalizovana podrška za sve Vaše projekte.",
    "home.credits.rate": "Stopa",
    "home.faq.label": "FAQ",
    "home.faq.title": "Često postavljana pitanja",
    "home.faq.q1": "Kako otvoriti račun?",
    "home.faq.a1": "Registracija traje manje od 2 minute. Samo unesite lične podatke, odaberite lozinku i Vaš račun bit će aktiviran nakon provjere od strane našeg tima.",
    "home.faq.q2": "Jesu li moji podaci sigurni?",
    "home.faq.a2": "Da. Svi podaci su šifrirani s AES-256 u pohrani i TLS 1.3 u prijenosu. Poštujemo međunarodne bankarske standarde i propise GDPR/PSD2.",
    "home.faq.q3": "Koje vrste investicija nudite?",
    "home.faq.a3": "Nudimo 6 kategorija: Nekretnine, Crowdfunding, Obnovljive energije, Burza, Participativno i Kripto. Svaki proizvod odabran je po omjeru rizika i prinosa.",
    "home.faq.q4": "Kako podnijeti zahtjev za kredit?",
    "home.faq.a4": "Idite u odjeljak Kredit Vašeg korisničkog prostora, odaberite vrstu kredita, unesite iznos i trajanje. Vaš zahtjev obradit će naši savjetnici u roku od 24h.",
    "home.faq.q5": "Postoje li skriveni troškovi?",
    "home.faq.a5": "Nema skrivenih troškova. Sve tarife jasno su prikazane pri svakoj operaciji. Uvjeti su dostupni u Vašem korisničkom prostoru u svakom trenutku.",
    "home.faq.q6": "Kako kontaktirati podršku?",
    "home.faq.a6": "Koristite integrisanu komunikaciju u Vašem korisničkom prostoru za direktan kontakt s Vašim ličnim bankarskim savjetnikom, dostupnim 24h/24 i 7 dana u sedmici.",
    "home.cta.title": "Spremi za početak?",
    "home.cta.sub": "Pridružite se hiljadama korisnika koji upravljaju finansijama s {bank}. Registracija traje manje od 2 minute.",
    "home.footer.products": "Proizvodi",
    "home.footer.services": "Usluge",
    "home.footer.access": "Brzi pristup",
    "home.footer.accounts": "Računi",
    "home.footer.transfers": "Transferi",
    "home.footer.cards": "Kartice",
    "home.footer.savings": "Štednja",
    "home.footer.investments": "Investicije",
    "home.footer.credits": "Krediti",
    "home.footer.financing": "Finansiranje",
    "home.footer.messages": "Poruke",
    "home.footer.login": "Prijava",
    "home.footer.signup": "Otvori račun",
    "home.footer.support": "Podrška 24/7",
    "home.footer.tagline": "Vaša online banka, dostupna svugdje i u svakom trenutku.",
    "home.footer.copyright": "© {year} {bank} — Sva prava pridržana",
    "home.footer.powered": "Pokreće {bank}",
    "app.menu_label": "Glavni meni",
    "app.nav.dashboard": "Kontrolna ploča",
    "app.nav.accounts": "Računi",
    "app.nav.transactions": "Transakcije",
    "app.nav.transfers": "Transferi",
    "app.nav.cards": "Kartice",
    "app.nav.savings": "Štednja",
    "app.nav.investments": "Investicije",
    "app.nav.credit": "Kredit",
    "app.nav.financing": "Finansiranje",
    "app.nav.messages": "Poruke",
    "app.nav.card_tracking": "Praćenje kartice",
    "app.nav.notifications": "Obavještenja",
    "app.nav.settings": "Postavke",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Početna",
    "app.nav.mobile.transfers": "Transferi",
    "app.nav.mobile.cards": "Kartice",
    "app.nav.mobile.transactions": "Transakcije",
    "app.nav.more.invest": "Investirati",
    "app.user_role": "Klijent",
    "app.logout": "Odjava",
    "app.search": "Pretraži…",
    "app.more_title": "Više funkcionalnosti",
    "app.more_btn": "Više",
    "app.loading": "Učitavanje…",
    "app.error_back": "Povratak na prijavu",
    "app.see_more_notifs": "Pogledaj {count} više obavještenja →",
    "page.accounts": "Moji računi",
    "page.transactions": "Transakcije",
    "page.transfers": "Novi transfer",
    "page.deposit": "Depozit",
    "page.cards": "Moje kartice",
    "page.savings": "Štednja",
    "page.investments": "Investicije",
    "page.credits": "Kredit",
    "page.financing": "Finansiranje projekata",
    "page.messages": "Poruke",
    "page.settings": "Postavke",
    "page.notifications": "Važna obavještenja",
    "page.profile": "Moj profil",
    "dash.greeting": "Dobar dan, {name}",
    "dash.overview": "Pregled Vaših finansija — {date}",
    "dash.total_balance": "Ukupno stanje",
    "dash.this_month": "+{amount} ovog mjeseca",
    "dash.view_iban": "Pogledaj moj IBAN",
    "dash.my_accounts": "Moji računi",
    "dash.primary_account": "Glavni račun",
    "dash.savings_account": "Štedni račun",
    "dash.remunerated": "Kamatonosni",
    "dash.details": "Detalji",
    "dash.transfer_btn": "Transfer",
    "dash.quick_actions": "Brze radnje",
    "dash.quick.deposit": "Depozit",
    "deposit.title": "Informacije o depozitu",
    "dash.quick.transfer": "Transfer",
    "dash.quick.history": "Historija",
    "dash.quick.cards": "Moje kartice",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Mjesečni budžet",
    "dash.income": "Prihodi",
    "dash.expenses": "Rashodi",
    "dash.spending_cat": "Rashodi po kategoriji",
    "dash.no_spending": "Nema zabilježenih rashoda.",
    "dash.recent_tx": "Nedavne transakcije",
    "dash.see_all": "Pogledaj sve",
    "dash.no_tx": "Nema operacija.",
    "dash.main_card": "Moja glavna kartica",
    "dash.manage": "Upravljaj",
    "dash.block": "Blokiraj",
    "dash.pin": "PIN kod",
    "dash.security": "Sigurnost",
    "dash.more": "Više",
    "dash.no_card": "Nije izdata kartica",
    "dash.monthly_activity": "Aktivnost mjeseca",
    "dash.operations": "Operacije",
    "dash.holder": "Vlasnik",
    "dash.expires": "Ist.",
    "dash.visa_label": "VISA",
    "dash.balance": "Raspoloživo stanje",
    "tx.all": "Sve",
    "tx.income": "Prihodi",
    "tx.expenses": "Rashodi",
    "tx.today": "Danas",
    "tx.yesterday": "Jučer",
    "tx.none": "Nema transakcija.",
    "transfer.title": "Brzi transfer",
    "transfer.recipient": "Primalac",
    "transfer.recipient_ph": "Ime, IBAN, broj telefona…",
    "transfer.amount": "Iznos",
    "transfer.label": "Oznaka",
    "transfer.reason": "Razlog (opciono)",
    "transfer.reason_ph": "Npr.: stanarina, povrat…",
    "transfer.execution": "Izvršenje",
    "transfer.immediate": "Odmah",
    "transfer.delayed": "Odgođeno",
    "transfer.balance": "Raspoloživo stanje:",
    "transfer.continue": "Nastavi",
    "transfer.confirm": "Potvrdi",
    "transfer.cancel": "Otkaži",
    "transfer.code": "Kod za validaciju",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Unesite primljeni kod za potvrdu transfera",
    "transfer.pending.title": "Transfer čeka validaciju",
    "transfer.pending.amount": "Iznos:",
    "transfer.pending.to": "Za:",
    "transfer.pending.hint": "Vaš validacijski kod bit će poslan e-mailom ili putem zvona obavijesti. Kod se sastoji od 6 znamenki. Ako ga ne primite, kontaktirajte podršku.",
    "transfer.pending.validate": "Potvrdi transfer",
    "transfer.saved": "Transfer sačuvan. Zatražite kod od svog administratora za potvrdu.",
    "transfer.done": "Transfer izvršen",
    "transfer.validated": "Transfer potvrđen!",
    "transfer.progress.processing": "Obrada transfera…",
    "transfer.progress.completed": "Transfer izvršen!",
    "transfer.progress.default_message": "Vaš transfer se obrađuje. Molimo sačekajte.",
    "cards.none": "Nije izdata kartica.\nKontaktirajte svog administratora.",
    "cards.holder": "Vlasnik",
    "cards.expires": "Ist.",
    "cards.block": "Zaključaj",
    "cards.pin": "PIN kod",
    "cards.more": "Više",
    "cards.limits": "Limiti kartice",
    "cards.payment": "Plaćanje",
    "cards.withdrawal": "Podizanje gotovine",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Blokirana",
    "cards.status_active": "Aktivna",
    "cards.toast.blocked": "Kartica blokirana",
    "cards.toast.unblocked": "Kartica deblokirana",
    "cards.toast.physical_ordered": "Fizička kartica naručena! Dostava za 5–10 radnih dana.",
    "cards.toast.created": "Kartica uspješno generirana!",
    "cards.none_sub": "Nema izdane kartice. Generirajte svoju virtualnu karticu sada.",
    "cards.generate_btn": "Generiraj moju karticu",
    "cards.create.title": "Generiraj moju virtualnu karticu",
    "cards.create.network": "Platna mreža",
    "cards.create.color": "Boja kartice",
    "cards.create.preview_name": "VAŠE IME",
    "cards.create.preview_expiry": "MM/GG",
    "cards.create.loading": "Generiranje…",
    "cards.create.submit": "Generiraj karticu",
    "cards.btn.unblock": "Deblokiraj",
    "cards.btn.block": "Blokiraj",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fizička",
    "cards.btn.physical_done": "Naručena",
    "cards.limits_title": "Ograničenja",
    "cards.reveal.title": "Informacije o kartici",
    "cards.reveal.number": "Cijeli broj",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Istek",
    "cards.reveal.warning": "Nikada ne dijelite ove informacije. Zatvorite ovaj prozor nakon korištenja.",
    "cards.pin.title": "PIN kod",
    "cards.pin.message": "Ovaj PIN kod se koristi za sva vaša online plaćanja i podizanja. Nikad ga ne otkrivajte nikome.",
    "cards.physical.title": "Naruči fizičku karticu",
    "cards.physical.fee_label": "Naknada za dostavu",
    "cards.physical.balance_label": "Vaš saldo",
    "cards.physical.insufficient": "Nedovoljan saldo. Dopunite račun prije naručivanja.",
    "cards.physical.info": "Naknada će biti naplaćena s vašeg računa. Dostava za 5–10 radnih dana.",
    "cards.physical.order_btn": "Naruči ({fee})",
    "cards.physical.ordering": "Naručujem…",
    "cards.physical.order_cta": "Naruči fizičku karticu",
    "cards.physical.choose_type": "Odaberi svoju karticu",
    "cards.physical.features": "Prednosti",
    "cards.buy_virtual_cta": "Kupi virtuelnu karticu",
    "notif.none": "Trenutno nema obavještenja.",
    "notif.contact_support": "Kontaktirajte podršku",
    "profile.change_photo": "Promijeni fotografiju",
    "profile.remove_photo": "Ukloni fotografiju",
    "profile.email": "Email",
    "profile.phone": "Telefon",
    "profile.country": "Zemlja",
    "profile.address": "Adresa",
    "profile.notifs_title": "Obavještenja ({count})",
    "profile.no_notifs": "Nema obavještenja.",
    "profile.logout": "Odjava",
    "profile.photo_updated": "Fotografija profila ažurirana",
    "profile.photo_removed": "Fotografija uklonjena",
    "profile.photo_too_large": "Slika je prevelika (max 1 MB)",
    "profile.powered": "Pokreće {bank}",
    "iban.title": "Moj IBAN",
    "iban.loading": "Učitavanje…",
    "iban.bic": "BIC / SWIFT kod",
    "iban.share_info": "Podijelite ove podatke kako biste primili transfer na Vaš račun.",
    "iban.copy": "Kopiraj",
    "iban.copied": "Kopirano u međuspremnik",
    "iban.copy_fail": "Nije moguće kopirati",
    "iban.close": "Zatvori",
    "accounts.all": "Svi Vaši računi otvoreni kod {bank}.",
    "accounts.primary": "Glavni račun",
    "accounts.savings": "Štedni račun",
    "accounts.primary_tag": "Glavni",
    "accounts.secondary_tag": "Sekundarni",
    "accounts.objective": "Cilj: {amount} · {pct}%",
    "accounts.copy": "Kopiraj",
    "accounts.loading": "Učitavanje…",
    "accounts.iban_copied": "IBAN kopiran",
    "accounts.copy_fail": "Nije moguće kopirati",
    "savings.total": "Ukupno uštedeno",
    "savings.target_total": "Ukupni cilj",
    "savings.progress": "Napredak",
    "savings.active": "Aktivni ciljevi",
    "savings.new_goal": "Novi cilj",
    "savings.none": "Još nema ciljeva štednje.",
    "savings.none_sub": "Kreirajte prvi cilj kako biste počeli.",
    "savings.saved": "Uštedeno:",
    "savings.goal": "Cilj:",
    "savings.deadline": "Rok: {date}",
    "savings.deposit": "Uplati",
    "savings.withdraw": "Podigni",
    "savings.loading": "Učitavanje…",
    "savings.form.title": "Novi cilj štednje",
    "savings.form.cancel": "Otkaži",
    "savings.form.create": "Kreiraj",
    "savings.form.name": "Naziv cilja",
    "savings.form.name_ph": "Putovanje Japan, Auto, …",
    "savings.form.target": "Ciljani iznos",
    "savings.form.initial": "Početna uplata",
    "savings.form.deadline": "Rok (opciono)",
    "savings.form.color": "Boja",
    "savings.deposit_title": "Uplati na \"{name}\"",
    "savings.deposit_amount": "Iznos za uplatu",
    "savings.balance_hint": "Glavno stanje: {amount}",
    "savings.withdraw_prompt": "Koliko podići s \"{name}\"?",
    "savings.close_confirm": "Zatvoriti cilj \"{name}\"? Štednja će biti vraćena na Vaš glavni račun.",
    "savings.toast.created": "Cilj kreiran",
    "savings.toast.deposited": "Uplata izvršena",
    "savings.toast.withdrawn": "Podizanje izvršeno",
    "savings.toast.closed": "Cilj zatvoren",
    "invest.total": "Ukupno investirano",
    "invest.profits": "Kumulativni prihodi",
    "invest.count": "Investicije",
    "invest.products": "Dostupni proizvodi",
    "invest.invest_btn": "Investiraj",
    "invest.my": "Moje investicije",
    "invest.pending": "Na čekanju",
    "invest.validated": "Potvrđeno",
    "invest.refused": "Odbijeno",
    "invest.withdraw.btn": "Povuci",
    "invest.withdraw.title": "Povuci moju investiciju",
    "invest.withdraw.intro": "Upravo ćete povući svoju investiciju „{product}\". Početni kapital i obračunata dobit bit će uplaćeni na vaš glavni saldo.",
    "invest.withdraw.principal": "Početni kapital",
    "invest.withdraw.profit": "Dobit",
    "invest.withdraw.total": "Ukupno za primanje",
    "invest.withdraw.confirm": "Potvrdi povlačenje",
    "invest.withdraw.warning": "Ova radnja je nepovratna.",
    "invest.withdraw.success": "Investicija povučena — {amount} uplaćeno na saldo.",
    "invest.withdrawn": "Povučeno",
    "invest.cta_sub": "Odaberite vrstu investicije koja Vam odgovara. Savjetnik će Vas pratiti od prvog zahtjeva.",
    "invest.form.amount": "Iznos za investiranje",
    "invest.form.balance": "Raspoloživo stanje: {amount}",
    "invest.form.note": "Napomena za savjetnika (opciono)",
    "invest.form.note_ph": "Navedite svoja očekivanja…",
    "invest.form.advisor_info": "Razgovor s Vašim savjetnikom bit će automatski otvoren.",
    "invest.form.cancel": "Otkaži",
    "invest.form.submit": "Pošalji zahtjev",
    "invest.toast.requested": "Zahtjev zabilježen — razgovor s Vašim savjetnikom je otvoren.",
    "invest.p1.name": "Nekretnine",
    "invest.p1.desc": "SCPI, nekretninski crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Participativno finansiranje odabranih projekata",
    "invest.p3.name": "Obnovljive energije",
    "invest.p3.desc": "Solarni, vjetrovni i zeleni projekti",
    "invest.p4.name": "Burza",
    "invest.p4.desc": "Dionice, ETF i diverzifikovani fondovi",
    "invest.p5.name": "Participativno",
    "invest.p5.desc": "Krediti za MSP i lokalne poduzetnike",
    "invest.p6.name": "Kripto",
    "invest.p6.desc": "Bitcoin, Ethereum i viševalutni novčanik",
    "msg.online": "Na mreži",
    "msg.loading": "Učitavanje…",
    "msg.none": "Nema poruka. Pokrenite razgovor s Vašim savjetnikom.",
    "msg.placeholder": "Napišite svoju poruku…",
    "msg.attach.image": "Slika",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Fajl / Dokument",
    "msg.pdf_doc": "PDF dokument",
    "msg.file": "Fajl",
    "msg.too_large": "Fajl je prevelik (max {size}).",
    "msg.withdraw": "Povuci",
    "credits.cta_sub": "Podnesite zahtjev direktno online — odgovor u roku od 24h.",
    "credits.learn_more": "Saznaj više",
    "credits.request": "Zatraži",
    "credits.history": "Moji zahtjevi",
    "credits.status.pending": "Na čekanju",
    "credits.status.accepted": "Prihvaćeno",
    "credits.status.refused": "Odbijeno",
    "credits.chars": "Karakteristike",
    "credits.amount_range": "Iznos: {min}€ – {max}€",
    "credits.rate_range": "Kamatna stopa: {min}% – {max}% godišnje",
    "credits.duration_range": "Trajanje: {min} do {max} mjeseci",
    "credits.deposit_min": "Početni depozit: {amount}€ minimum",
    "credits.garantie": "Garancija: {garantie}",
    "credits.eligibility": "Uvjeti prihvatljivosti",
    "credits.elig.income": "Dokazati redovne i stabilne prihode",
    "credits.elig.deposit": "Izvršiti početni depozit od {amount}€",
    "credits.elig.garantie": "Predočiti garanciju: {garantie}",
    "credits.form.title": "Zahtjev — {name}",
    "credits.form.cancel": "Otkaži",
    "credits.form.submit": "Pošalji zahtjev",
    "credits.form.amount": "Željeni iznos (€)",
    "credits.form.duration": "Trajanje (mjeseci)",
    "credits.form.reason": "Razlog (opciono)",
    "credits.form.reason_ph": "Opišite svoj projekt…",
    "credits.form.close": "Zatvori",
    "credits.toast.submitted": "Zahtjev za kredit poslan — odgovor ćete dobiti u roku od 24h.",
    "credits.p1.name": "Lični kredit",
    "credits.p1.desc": "Za finansiranje svakodnevnih potreba ili ličnih projekata.",
    "credits.p2.name": "Stambeni kredit",
    "credits.p2.desc": "Za finansiranje kupovine ili izgradnje nekretnine.",
    "credits.p3.name": "Auto / Moto kredit",
    "credits.p3.desc": "Kupite vozilo s mirom u duši.",
    "credits.p4.name": "Studentski kredit",
    "credits.p4.desc": "Finansijska pomoć za visokoškolsko obrazovanje.",
    "credits.p5.name": "Poslovni kredit",
    "credits.p5.desc": "Za podršku rastu profesionalne djelatnosti.",
    "credits.p6.name": "Ekspres kredit",
    "credits.p6.desc": "Brzo rješenje za hitne potrebe.",
    "financing.reimbursable": "Povratno finansiranje",
    "financing.reimbursable_desc": "Finansijska podrška s fiksnim uvjetima za pokretanje ili razvoj djelatnosti.",
    "financing.non_reimbursable": "Bespovratno finansiranje",
    "financing.non_reimbursable_desc": "Za projekte društvenog, obrazovnog ili humanitarnog učinka (NVO, udruženja).",
    "financing.cta_sub": "Prilagođena rješenja za ostvarivanje Vaših ambicija.",
    "financing.request": "Zatraži finansiranje",
    "financing.history": "Moji zahtjevi",
    "financing.reimbursable_label": "Povratno",
    "financing.non_reimbursable_label": "Bespovratno",
    "financing.status.pending": "Na čekanju",
    "financing.status.granted": "Odobreno",
    "financing.status.refused": "Odbijeno",
    "financing.form.title": "Zahtjev — {name}",
    "financing.form.cancel": "Otkaži",
    "financing.form.submit": "Pošalji zahtjev",
    "financing.form.project": "Naziv projekta",
    "financing.form.project_ph": "Moj projekt",
    "financing.form.amount": "Traženi iznos (€)",
    "financing.form.sector": "Sektor",
    "financing.form.sector_ph": "Tech, poljoprivreda, …",
    "financing.form.description": "Opis projekta",
    "financing.form.description_ph": "Predstavite svoj projekt, njegove ciljeve i očekivani učinak…",
    "settings.security": "Sigurnost",
    "settings.current_pw": "Trenutna lozinka",
    "settings.new_pw": "Nova lozinka",
    "settings.confirm_pw": "Potvrdi",
    "settings.submit": "Promijeni lozinku",
    "settings.no_match": "Lozinke se ne podudaraju",
    "settings.changed": "Lozinka promijenjena",
    "common.cancel": "Otkaži",
    "common.confirm": "Potvrdi",
    "common.close": "Zatvori",
    "common.save": "Sačuvaj",
    "common.loading": "Učitavanje…",
    "common.send": "Pošalji",
    "common.total": "Ukupno",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Proc. mjesečna rata",
    "credits.rate_annual": "/ god.",
    "credits.rate_used": "Proc. stopa:",
    "credits.simulation": "Simulacija mjesečne rate",
    "credits.total_cost": "Proc. ukupni troškovi",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Dobrodošli",
    "common.delete": "Obriši",
    "cards.delete.title": "Obriši karticu",
    "cards.delete.confirm_msg": "Ova radnja je nepovratna. Kartica će biti trajno obrisana.",
    "cards.delete.physical_tooltip": "Fizička kartica naručena — brisanje nemoguće",
    "cards.order.confirmed": "Narudžba potvrđena!",
    "cards.order.confirmed_body": "Vaša fizička kartica se priprema. Koristite ovaj broj za praćenje dostave.",
    "cards.order.tracking_no": "Br. praćenja",
    "cards.fee_deducted": "{amount} bit će odbijeno od vašeg stanja",
    "cards.insufficient_short": "Nedovoljno stanje ({available} dostupno)",
    "transfer.section.beneficiary": "Primalac",
    "transfer.section.details": "Prijenos",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banka",
    "transfer.field.city": "Grad",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banka:",
    "transfer.progress.paused": "Čeka na potvrdu",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Varijabilna",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Kartica obrisana",
    "cards.feat.gold_exclusive": "Ekskluzivna zlatna kartica",
    "cards.feat.cashback_05": "Povrat novca 0,5%",
    "cards.feat.buyer_protection": "Zaštita kupca",
    "cards.feat.support_standard": "Standardna podrška",
    "cards.feat.plastic_premium": "Premium plastična kartica",
    "cards.feat.cashback_1": "Povrat novca 1%",
    "cards.feat.travel_standard": "Standardno putno osiguranje",
    "cards.feat.support_priority": "Prioritetna podrška",
    "cards.feat.metal_brushed": "Brušena metalna kartica",
    "cards.feat.cashback_2_unlimited": "Neograničeni 2% povrat",
    "cards.feat.travel_premium": "Premium putno osiguranje",
    "cards.feat.vip_lounge": "Pristup VIP salonima",
    "cards.feat.support_247": "24/7 podrška",
    "auth.demo.title": "Prijava administratora",
    "auth.demo.subtitle": "Unesite admin podatke za pregled demoa",
    "credits.garantie.none": "Nema",
    "credits.garantie.mortgage": "Hipoteka na financiranu nekretninu",
    "credits.garantie.vehicle": "Financirano vozilo",
    "credits.garantie.parental": "Roditeljsko ili bankarsko jemstvo",
    "credits.garantie.professional": "Poslovna sredstva ili lično jemstvo",
    "cards.reveal.pin_prompt": "Unesite vaš 4-cifreni PIN kod da biste prikazali informacije o kartici.",
    "cards.reveal.show": "Prikaži",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Dva PIN koda se ne podudaraju",
    "auth.pin.digit_hint": "4 cifre",
  
  },
  sr: {
    "notif.transfer_code.title": "Код за потврду преноса",
    "notif.transfer_code.body": "Копирајте овај код да бисте потврдили пренос.",
    "notif.transfer_code.expires_in": "Нестаје за {sec}с",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} месеци",
    "credits.n_months": "{n} месеци",
    "msg.read": "Прочитано",
    "msg.sent": "Послато",
    "msg.attach_aria": "Приложи датотеку",
    "login.device.unknown": "Непознат прегледач",
    "login.device.mobile": "Мобилни",
    "login.device.tablet": "Таблет",
    "login.device.desktop": "Рачунар",
    "login.recent": "Недавно",
    "err.invalid_credentials": "Неважећи подаци за пријаву",
    "err.bad_pin": "Погрешан PIN",
    "err.account_blocked": "Налог блокиран",
    "err.bad_current_pw": "Тренутна лозинка је нетачна",
    "err.insufficient_funds": "Недовољно средстава, допуните свој налог.",
    "err.amount_above_savings": "Износ премашује доступну штедњу",
    "err.must_be_positive": "Вредност мора бити позитивна",
    "err.invalid_format": "Неважећи формат",
    "err.email_taken": "Имејл се већ користи",
    "err.card_not_found": "Картица није пронађена",
    "err.physical_card_already_ordered": "Физичка картица је већ наручена",
    "err.beneficiary_not_found": "Прималац није пронађен",
    "err.invalid_code": "Неважећи код",
    "err.no_transfer_code": "Нема кода за овај пренос",
    "err.link_expired": "Линк неважећи или истекао",
    "err.thread_not_found": "Разговор није пронађен",
    "err.empty_message": "Празна порука",
    "err.file_too_large": "Датотека је превелика (макс 20 MB)",
    "err.forbidden": "Приступ одбијен",
    "err.bank_not_found": "Банка није пронађена",
    "err.signups_closed": "Регистрације су затворене за ову банку.",
    "err.access_suspended": "Приступ је привремено обустављен. Молимо контактирајте своју банку.",
    "err.notif_not_closable": "Ово обавештење се не може затворити.",
    "auth.signup.tagline": "Придружите нам се данас",
    "auth.login.tagline": "Добродошли!",
    "auth.signup.tagline_sub": "Отворите налог за 2 минута и приступите свим нашим premium банкарским услугама.",
    "auth.login.tagline_sub": "Приступите свом личном простору и управљајте финансијама на безбедан начин.",
    "auth.back": "Повратак на почетну страницу",
    "auth.bullet.security": "Банкарска сигурност војног нивоа",
    "auth.bullet.transfers": "Тренутни трансфери 24h/24",
    "auth.bullet.investments": "Интегрисана улагања и штедња",
    "auth.bullet.advisor": "Лични саветник доступан 7 дана у недељи",
    "auth.footer": "Покреће {bank}",
    "auth.signup.title": "Креирајте налог",
    "auth.login.title": "Пријава",
    "auth.field.lastName": "Презиме",
    "auth.field.firstName": "Име(на)",
    "auth.field.country": "Земља",
    "auth.field.country.placeholder": "— Одаберите своју земљу —",
    "auth.field.phone": "Телефон",
    "auth.field.address": "Адреса",
    "auth.field.address.placeholder": "Улица мира 123, Београд",
    "auth.field.email": "Имејл",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Лозинка",
    "auth.submit.signup": "Креирај мој налог",
    "auth.submit.login": "Пријавите се",
    "auth.login.badge": "Пријавите се на свој налог",
    "auth.card.tagline": "Ваш кориснички простор",
    "auth.submit.loading": "Учитавање…",
    "auth.pin.title": "PIN kôd",
    "auth.pin.subtitle": "Unesite svoj 4-cifreni PIN kôd",
    "auth.pin.label": "PIN kôd",
    "auth.pin.confirm": "Potvrdi PIN kod",
    "auth.pin.submit": "Potvrdi",
    "auth.pin.back": "Nazad na prijavu",
    "auth.pin.length_error": "PIN kôd mora imati 4 cifre",
    "auth.switch.have_account": "Већ имате налог?",
    "auth.switch.login_link": "Пријава",
    "auth.switch.no_account": "Још нисте клијент?",
    "auth.error.select_country": "Одаберите своју земљу",
    "kyc.title": "Верификација идентитета (KYC)",
    "kyc.sub": "Учитајте своје документе за валидацију налога. Сви документи су шифровани и поверљиви.",
    "kyc.id_type": "Врста идентификационог документа *",
    "kyc.id_card": "Лична карта",
    "kyc.passport": "Пасош",
    "kyc.driver_license": "Возачка дозвола",
    "kyc.recto_passport": "Предња страна пасоша *",
    "kyc.recto_id": "Предња страна документа *",
    "kyc.verso": "Задња страна документа *",
    "kyc.selfie": "Селфи са документом у руци *",
    "kyc.address_proof": "Доказ о пребивалишту *",
    "kyc.hint_doc": "Слика или PDF, max 5 MB",
    "kyc.hint_selfie": "Јасна фотографија, лице и документ видљиви",
    "kyc.hint_address": "Рачун, извод, потврда… (max 5 MB)",
    "kyc.upload": "Учитај",
    "kyc.replace": "Замени",
    "pending.title": "Налог на чекању",
    "pending.body": "Ваш захтев је примљен. Администратор ће проверити Ваш досије и активирати Ваш налог у кратком року.",
    "pending.back": "Повратак на пријаву",
    "home.nav.features": "Функционалности",
    "home.nav.security": "Безбедност",
    "home.nav.invest": "Инвестиције",
    "home.nav.credits": "Кредити",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Пријавите се",
    "home.hero.tagline": "Онлајн банкарство нове генерације",
    "home.hero.title1": "Управљајте својим новцем",
    "home.hero.title2": "са интелигенцијом",
    "home.hero.sub": "Комплетна банкарска платформа — трансфери, штедња, инвестиције и кредити са вашег паметног телефона.",
    "home.hero.login": "Пријавите се",
    "home.hero.trust.secure": "100% Безбедно",
    "home.hero.trust.fast": "Брзо отварање",
    "home.hero.trust.encrypted": "Шифровани подаци",
    "home.hero.trust.support": "Сервис 24/7",
    "home.mockup.balance": "Расположиво стање",
    "home.mockup.currency": "EUR · Главни налог",
    "home.mockup.recharge": "Допунити",
    "home.mockup.send": "Пошаљи",
    "home.mockup.save": "Штедња",
    "home.mockup.salary": "Плата Март",
    "home.mockup.rent": "Станарина Април",
    "home.mockup.investment": "Инвестиција",
    "home.mockup.days2": "пре 2 дана",
    "home.mockup.days3": "пре 3 дана",
    "home.mockup.days5": "пре 5 дана",
    "home.stats.accounts": "Активни налози",
    "home.stats.uptime": "Доступност",
    "home.stats.transfers": "Трансфери",
    "home.stats.support": "Подршка",
    "home.features.label": "Функционалности",
    "home.features.title": "Све што вам треба",
    "home.features.sub": "Комплетна платформа дизајнирана да поједностави управљање Вашим финансијама свакодневно.",
    "home.feature.security": "Војна безбедност",
    "home.feature.security_body": "AES-256 шифровање, двофакторска аутентификација и надзор налога 24/7.",
    "home.feature.cards": "Виртуелна и физичка картица",
    "home.feature.cards_body": "Издајте картице за неколико секунди или наручите праву физичку картицу достављену на вашу адресу.",
    "home.feature.transfers": "Брзи трансфери",
    "home.feature.transfers_body": "Шаљите и примајте новац свуда у свету за неколико секунди.",
    "home.feature.analytics": "Анализа у реалном времену",
    "home.feature.analytics_body": "Паметне контролне табле за праћење трошкова и антиципацију Ваших финансија.",
    "home.feature.investments": "Интегрисане инвестиције",
    "home.feature.investments_body": "Приступите одабраним инвестиционим производима директно са Вашег налога.",
    "home.feature.support": "Дедикована подршка 24/7",
    "home.feature.support_body": "Лични банкарски саветник доступан у сваком тренутку путем безбедног дописивања.",
    "home.tpl.label": "Поверење",
    "home.tpl.title": "Верују нам",
    "home.mockup.balance_label": "Стање",
    "home.mockup.virement": "Трансфер",
    "home.mockup.salaire": "Плата",
    "home.security.label": "Безбедност",
    "home.security.title1": "Ваше финансије",
    "home.security.title2": "у апсолутној безбедности",
    "home.security.sub": "Примењујемо најстроже стандарде банкарске безбедности ради заштите сваког цента.",
    "home.security.aes.title": "AES-256 шифровање",
    "home.security.aes.body": "Сви осетљиви подаци шифровани су истим алгоритмима које користе велике међународне банке.",
    "home.security.auth.title": "Појачана аутентификација",
    "home.security.auth.body": "Двострука провера идентитета при свакој пријави и свакој осетљивој операцији на Вашем налогу.",
    "home.security.compliance.title": "Усклађеност PSD2/GDPR",
    "home.security.compliance.body": "Поштујемо све европске прописе о платним услугама и заштити података.",
    "home.invest.label": "Инвестиције",
    "home.invest.title": "Увећајте свој новац",
    "home.invest.sub": "6 инвестиционих категорија одабраних по потенцијалу и поузданости.",
    "home.invest.yield": "Принос",
    "home.credits.label": "Кредити",
    "home.credits.title": "Решења прилагођена сваком пројекту",
    "home.credits.sub": "Конкурентне стопе, брзи одговори и персонализована подршка за све Ваше пројекте.",
    "home.credits.rate": "Стопа",
    "home.faq.label": "FAQ",
    "home.faq.title": "Често постављана питања",
    "home.faq.q1": "Како отворити налог?",
    "home.faq.a1": "Регистрација траје мање од 2 минута. Само унесите личне податке, одаберите лозинку и Ваш налог биће активиран након провере од стране нашег тима.",
    "home.faq.q2": "Да ли су моји подаци безбедни?",
    "home.faq.a2": "Да. Сви подаци шифровани су са AES-256 у складишту и TLS 1.3 у преносу. Поштујемо међународне банкарске стандарде и прописе GDPR/PSD2.",
    "home.faq.q3": "Које врсте инвестиција нудите?",
    "home.faq.a3": "Нудимо 6 категорија: Некретнине, Crowdfunding, Обновљиве енергије, Берза, Партиципативно и Крипто. Сваки производ одабран је по односу ризика и приноса.",
    "home.faq.q4": "Како поднети захтев за кредит?",
    "home.faq.a4": "Идите у одељак Кредит Вашег корисничког простора, одаберите врсту кредита, унесите износ и трајање. Ваш захтев обрадиће наши саветници у року од 24h.",
    "home.faq.q5": "Постоје ли скривени трошкови?",
    "home.faq.a5": "Нема скривених трошкова. Све тарифе јасно су приказане при свакој операцији. Услови су доступни у Вашем корисничком простору у сваком тренутку.",
    "home.faq.q6": "Како контактирати подршку?",
    "home.faq.a6": "Користите интегрисану комуникацију у Вашем корисничком простору за директан контакт са Вашим личним банкарским саветником, доступним 24h/24 и 7 дана у недељи.",
    "home.cta.title": "Спремни за почетак?",
    "home.cta.sub": "Придружите се хиљадама корисника који управљају финансијама са {bank}. Регистрација траје мање од 2 минута.",
    "home.footer.products": "Производи",
    "home.footer.services": "Услуге",
    "home.footer.access": "Брзи приступ",
    "home.footer.accounts": "Налози",
    "home.footer.transfers": "Трансфери",
    "home.footer.cards": "Картице",
    "home.footer.savings": "Штедња",
    "home.footer.investments": "Инвестиције",
    "home.footer.credits": "Кредити",
    "home.footer.financing": "Финансирање",
    "home.footer.messages": "Поруке",
    "home.footer.login": "Пријава",
    "home.footer.signup": "Отвори налог",
    "home.footer.support": "Подршка 24/7",
    "home.footer.tagline": "Ваша онлајн банка, доступна свуда и у сваком тренутку.",
    "home.footer.copyright": "© {year} {bank} — Сва права задржана",
    "home.footer.powered": "Покреће {bank}",
    "app.menu_label": "Главни мени",
    "app.nav.dashboard": "Контролна табла",
    "app.nav.accounts": "Налози",
    "app.nav.transactions": "Трансакције",
    "app.nav.transfers": "Трансфери",
    "app.nav.cards": "Картице",
    "app.nav.savings": "Штедња",
    "app.nav.investments": "Инвестиције",
    "app.nav.credit": "Кредит",
    "app.nav.financing": "Финансирање",
    "app.nav.messages": "Поруке",
    "app.nav.card_tracking": "Праћење картице",
    "app.nav.notifications": "Обавештења",
    "app.nav.settings": "Подешавања",
    "app.nav.profile": "Профил",
    "app.nav.mobile.home": "Почетна",
    "app.nav.mobile.transfers": "Трансфери",
    "app.nav.mobile.cards": "Картице",
    "app.nav.mobile.transactions": "Трансакције",
    "app.nav.more.invest": "Инвестирај",
    "app.user_role": "Клијент",
    "app.logout": "Одјава",
    "app.search": "Претражи…",
    "app.more_title": "Више функционалности",
    "app.more_btn": "Више",
    "app.loading": "Учитавање…",
    "app.error_back": "Повратак на пријаву",
    "app.see_more_notifs": "Погледај {count} више обавештења →",
    "page.accounts": "Моји налози",
    "page.transactions": "Трансакције",
    "page.transfers": "Нови трансфер",
    "page.deposit": "Депозит",
    "page.cards": "Моје картице",
    "page.savings": "Штедња",
    "page.investments": "Инвестиције",
    "page.credits": "Кредит",
    "page.financing": "Финансирање пројеката",
    "page.messages": "Поруке",
    "page.settings": "Подешавања",
    "page.notifications": "Важна обавештења",
    "page.profile": "Мој профил",
    "dash.greeting": "Добар дан, {name}",
    "dash.overview": "Преглед Ваших финансија — {date}",
    "dash.total_balance": "Укупно стање",
    "dash.this_month": "+{amount} овог месеца",
    "dash.view_iban": "Погледај мој IBAN",
    "dash.my_accounts": "Моји налози",
    "dash.primary_account": "Главни налог",
    "dash.savings_account": "Штедни налог",
    "dash.remunerated": "Камато­носни",
    "dash.details": "Детаљи",
    "dash.transfer_btn": "Трансфер",
    "dash.quick_actions": "Брзе радње",
    "dash.quick.deposit": "Депозит",
    "deposit.title": "Информације о депозиту",
    "dash.quick.transfer": "Трансфер",
    "dash.quick.history": "Историја",
    "dash.quick.cards": "Моје картице",
    "dash.quick.profile": "Профил",
    "dash.monthly_budget": "Месечни буџет",
    "dash.income": "Приходи",
    "dash.expenses": "Расходи",
    "dash.spending_cat": "Расходи по категорији",
    "dash.no_spending": "Нема забележених расхода.",
    "dash.recent_tx": "Недавне трансакције",
    "dash.see_all": "Погледај све",
    "dash.no_tx": "Нема операција.",
    "dash.main_card": "Моја главна картица",
    "dash.manage": "Управљај",
    "dash.block": "Блокирај",
    "dash.pin": "PIN код",
    "dash.security": "Безбедност",
    "dash.more": "Више",
    "dash.no_card": "Није издата картица",
    "dash.monthly_activity": "Активност месеца",
    "dash.operations": "Операције",
    "dash.holder": "Власник",
    "dash.expires": "Ист.",
    "dash.visa_label": "VISA",
    "dash.balance": "Расположиво стање",
    "tx.all": "Све",
    "tx.income": "Приходи",
    "tx.expenses": "Расходи",
    "tx.today": "Данас",
    "tx.yesterday": "Јуче",
    "tx.none": "Нема трансакција.",
    "transfer.title": "Брзи трансфер",
    "transfer.recipient": "Прималац",
    "transfer.recipient_ph": "Ime, IBAN, broj telefona…",
    "transfer.amount": "Износ",
    "transfer.label": "Ознака",
    "transfer.reason": "Разлог (опционо)",
    "transfer.reason_ph": "Нпр.: станарина, повраћај…",
    "transfer.execution": "Извршење",
    "transfer.immediate": "Одмах",
    "transfer.delayed": "Одложено",
    "transfer.balance": "Расположиво стање:",
    "transfer.continue": "Настави",
    "transfer.confirm": "Потврди",
    "transfer.cancel": "Откажи",
    "transfer.code": "Код за валидацију",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Унесите примљени код за потврду трансфера",
    "transfer.pending.title": "Трансфер чека валидацију",
    "transfer.pending.amount": "Износ:",
    "transfer.pending.to": "За:",
    "transfer.pending.hint": "Ваш код за валидацију биће послат е-поштом или путем звонца обавештења. Код се састоји од 6 цифара. Ако га не примите, контактирајте подршку.",
    "transfer.pending.validate": "Потврди трансфер",
    "transfer.saved": "Трансфер сачуван. Затражите код од свог администратора за потврду.",
    "transfer.done": "Трансфер извршен",
    "transfer.validated": "Трансфер потврђен!",
    "transfer.progress.processing": "Обрада трансфера…",
    "transfer.progress.completed": "Трансфер извршен!",
    "transfer.progress.default_message": "Ваш трансфер се обрађује. Молимо сачекајте.",
    "cards.none": "Није издата картица.\nКонтактирајте свог администратора.",
    "cards.holder": "Власник",
    "cards.expires": "Ист.",
    "cards.block": "Закључај",
    "cards.pin": "PIN код",
    "cards.more": "Више",
    "cards.limits": "Лимити картице",
    "cards.payment": "Плаћање",
    "cards.withdrawal": "Подизање готовине",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Блокирана",
    "cards.status_active": "Активна",
    "cards.toast.blocked": "Картица блокирана",
    "cards.toast.unblocked": "Картица деблокирана",
    "cards.toast.physical_ordered": "Физичка картица наручена! Испорука за 5–10 радних дана.",
    "cards.toast.created": "Картица успешно генерисана!",
    "cards.none_sub": "Нема издате картице. Генеришите своју виртуелну картицу сада.",
    "cards.generate_btn": "Генериши моју картицу",
    "cards.create.title": "Генериши моју виртуелну картицу",
    "cards.create.network": "Платна мрежа",
    "cards.create.color": "Боја картице",
    "cards.create.preview_name": "ВАШЕ ИМЕ",
    "cards.create.preview_expiry": "ММ/ГГ",
    "cards.create.loading": "Генерисање…",
    "cards.create.submit": "Генериши картицу",
    "cards.btn.unblock": "Деблокирај",
    "cards.btn.block": "Блокирај",
    "cards.btn.info": "Инфо",
    "cards.btn.physical": "Физичка",
    "cards.btn.physical_done": "Наручена",
    "cards.limits_title": "Ограничења",
    "cards.reveal.title": "Информације о картици",
    "cards.reveal.number": "Пун број",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Истек",
    "cards.reveal.warning": "Никада не делите ове информације. Затворите овај прозор након употребе.",
    "cards.pin.title": "PIN код",
    "cards.pin.message": "Овај PIN код се користи за сва ваша онлајн плаћања и подизања. Никад га не откривајте никоме.",
    "cards.physical.title": "Наручи физичку картицу",
    "cards.physical.fee_label": "Накнада за испоруку",
    "cards.physical.balance_label": "Ваш салдо",
    "cards.physical.insufficient": "Недовољан салдо. Допуните налог пре наручивања.",
    "cards.physical.info": "Накнада ће бити наплаћена са вашег налога. Испорука за 5–10 радних дана.",
    "cards.physical.order_btn": "Наручи ({fee})",
    "cards.physical.ordering": "Наручујем…",
    "cards.physical.order_cta": "Наручи физичку картицу",
    "cards.physical.choose_type": "Одабери своју картицу",
    "cards.physical.features": "Предности",
    "cards.buy_virtual_cta": "Купи виртуелну картицу",
    "notif.none": "Тренутно нема обавештења.",
    "notif.contact_support": "Контактирајте подршку",
    "profile.change_photo": "Промени фотографију",
    "profile.remove_photo": "Уклони фотографију",
    "profile.email": "Имејл",
    "profile.phone": "Телефон",
    "profile.country": "Земља",
    "profile.address": "Адреса",
    "profile.notifs_title": "Обавештења ({count})",
    "profile.no_notifs": "Нема обавештења.",
    "profile.logout": "Одјава",
    "profile.photo_updated": "Фотографија профила ажурирана",
    "profile.photo_removed": "Фотографија уклоњена",
    "profile.photo_too_large": "Слика је превелика (max 1 MB)",
    "profile.powered": "Покреће {bank}",
    "iban.title": "Мој IBAN",
    "iban.loading": "Учитавање…",
    "iban.bic": "BIC / SWIFT код",
    "iban.share_info": "Поделите ове податке ради примања трансфера на Ваш налог.",
    "iban.copy": "Копирај",
    "iban.copied": "Копирано у међуспремник",
    "iban.copy_fail": "Није могуће копирати",
    "iban.close": "Затвори",
    "accounts.all": "Сви Ваши налози отворени код {bank}.",
    "accounts.primary": "Главни налог",
    "accounts.savings": "Штедни налог",
    "accounts.primary_tag": "Главни",
    "accounts.secondary_tag": "Секундарни",
    "accounts.objective": "Циљ: {amount} · {pct}%",
    "accounts.copy": "Копирај",
    "accounts.loading": "Учитавање…",
    "accounts.iban_copied": "IBAN копиран",
    "accounts.copy_fail": "Није могуће копирати",
    "savings.total": "Укупно уштеђено",
    "savings.target_total": "Укупни циљ",
    "savings.progress": "Напредак",
    "savings.active": "Активни циљеви",
    "savings.new_goal": "Нови циљ",
    "savings.none": "Још нема циљева штедње.",
    "savings.none_sub": "Креирајте први циљ ради почетка.",
    "savings.saved": "Уштеђено:",
    "savings.goal": "Циљ:",
    "savings.deadline": "Рок: {date}",
    "savings.deposit": "Уплати",
    "savings.withdraw": "Подигни",
    "savings.loading": "Учитавање…",
    "savings.form.title": "Нови циљ штедње",
    "savings.form.cancel": "Откажи",
    "savings.form.create": "Креирај",
    "savings.form.name": "Назив циља",
    "savings.form.name_ph": "Путовање Јапан, Ауто, …",
    "savings.form.target": "Циљани износ",
    "savings.form.initial": "Почетна уплата",
    "savings.form.deadline": "Рок (опционо)",
    "savings.form.color": "Боја",
    "savings.deposit_title": "Уплати на \"{name}\"",
    "savings.deposit_amount": "Износ за уплату",
    "savings.balance_hint": "Главно стање: {amount}",
    "savings.withdraw_prompt": "Колико подићи са \"{name}\"?",
    "savings.close_confirm": "Затворити циљ \"{name}\"? Штедња ће бити враћена на Ваш главни налог.",
    "savings.toast.created": "Циљ креиран",
    "savings.toast.deposited": "Уплата извршена",
    "savings.toast.withdrawn": "Подизање извршено",
    "savings.toast.closed": "Циљ затворен",
    "invest.total": "Укупно инвестирано",
    "invest.profits": "Кумулативни приходи",
    "invest.count": "Инвестиције",
    "invest.products": "Доступни производи",
    "invest.invest_btn": "Инвестирај",
    "invest.my": "Моје инвестиције",
    "invest.pending": "На чекању",
    "invest.validated": "Потврђено",
    "invest.refused": "Одбијено",
    "invest.withdraw.btn": "Повуци",
    "invest.withdraw.title": "Повуци моју инвестицију",
    "invest.withdraw.intro": "Управо ћете повући своју инвестицију „{product}\". Почетни капитал и обрачуната добит биће уплаћени на ваш главни биланс.",
    "invest.withdraw.principal": "Почетни капитал",
    "invest.withdraw.profit": "Добит",
    "invest.withdraw.total": "Укупно за пријем",
    "invest.withdraw.confirm": "Потврди повлачење",
    "invest.withdraw.warning": "Ова радња је неповратна. Инвестиција ће бити затворена.",
    "invest.withdraw.success": "Инвестиција повучена — {amount} уплаћено на биланс.",
    "invest.withdrawn": "Повучено",
    "invest.cta_sub": "Одаберите врсту инвестиције која Вам одговара. Саветник ће Вас пратити од првог захтева.",
    "invest.form.amount": "Износ за инвестирање",
    "invest.form.balance": "Расположиво стање: {amount}",
    "invest.form.note": "Напомена за саветника (опционо)",
    "invest.form.note_ph": "Наведите своја очекивања…",
    "invest.form.advisor_info": "Разговор са Вашим саветником биће аутоматски отворен.",
    "invest.form.cancel": "Откажи",
    "invest.form.submit": "Пошаљи захтев",
    "invest.toast.requested": "Захтев забележен — разговор са Вашим саветником је отворен.",
    "invest.p1.name": "Некретнине",
    "invest.p1.desc": "SCPI, некретнински crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Партиципативно финансирање одабраних пројеката",
    "invest.p3.name": "Обновљиве енергије",
    "invest.p3.desc": "Соларни, ветровни и зелени пројекти",
    "invest.p4.name": "Берза",
    "invest.p4.desc": "Акције, ETF и диверзификовани фондови",
    "invest.p5.name": "Партиципативно",
    "invest.p5.desc": "Кредити за МСП и локалне предузетнике",
    "invest.p6.name": "Крипто",
    "invest.p6.desc": "Bitcoin, Ethereum и вишевалутни новчаник",
    "msg.online": "На мрежи",
    "msg.loading": "Учитавање…",
    "msg.none": "Нема порука. Покрените разговор са Вашим саветником.",
    "msg.placeholder": "Напишите своју поруку…",
    "msg.attach.image": "Слика",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Аудио",
    "msg.attach.file": "Фајл / Документ",
    "msg.pdf_doc": "PDF документ",
    "msg.file": "Фајл",
    "msg.too_large": "Фајл је превелик (max {size}).",
    "msg.withdraw": "Повуци",
    "credits.cta_sub": "Поднесите захтев директно онлајн — одговор у року од 24h.",
    "credits.learn_more": "Сазнај више",
    "credits.request": "Затражи",
    "credits.history": "Моји захтеви",
    "credits.status.pending": "На чекању",
    "credits.status.accepted": "Прихваћено",
    "credits.status.refused": "Одбијено",
    "credits.chars": "Карактеристике",
    "credits.amount_range": "Износ: {min}€ – {max}€",
    "credits.rate_range": "Каматна стопа: {min}% – {max}% годишње",
    "credits.duration_range": "Трајање: {min} до {max} месеци",
    "credits.deposit_min": "Почетни депозит: {amount}€ минимум",
    "credits.garantie": "Гаранција: {garantie}",
    "credits.eligibility": "Услови прихватљивости",
    "credits.elig.income": "Доказати редовне и стабилне приходе",
    "credits.elig.deposit": "Извршити почетни депозит од {amount}€",
    "credits.elig.garantie": "Предочити гаранцију: {garantie}",
    "credits.form.title": "Захтев — {name}",
    "credits.form.cancel": "Откажи",
    "credits.form.submit": "Пошаљи захтев",
    "credits.form.amount": "Жељени износ (€)",
    "credits.form.duration": "Трајање (месеци)",
    "credits.form.reason": "Разлог (опционо)",
    "credits.form.reason_ph": "Опишите свој пројекат…",
    "credits.form.close": "Затвори",
    "credits.toast.submitted": "Захтев за кредит послат — одговор ћете добити у року од 24h.",
    "credits.p1.name": "Лични кредит",
    "credits.p1.desc": "За финансирање свакодневних потреба или личних пројеката.",
    "credits.p2.name": "Стамбени кредит",
    "credits.p2.desc": "За финансирање куповине или изградње некретнине.",
    "credits.p3.name": "Ауто / Мото кредит",
    "credits.p3.desc": "Купите возило с миром у души.",
    "credits.p4.name": "Студентски кредит",
    "credits.p4.desc": "Финансијска помоћ за високошколско образовање.",
    "credits.p5.name": "Пословни кредит",
    "credits.p5.desc": "За подршку расту професионалне делатности.",
    "credits.p6.name": "Експрес кредит",
    "credits.p6.desc": "Брзо решење за хитне потребе.",
    "financing.reimbursable": "Повратно финансирање",
    "financing.reimbursable_desc": "Финансијска подршка с фиксним условима за покретање или развој делатности.",
    "financing.non_reimbursable": "Бесповратно финансирање",
    "financing.non_reimbursable_desc": "За пројекте друштвеног, образовног или хуманитарног утицаја (НВО, удружења).",
    "financing.cta_sub": "Прилагођена решења за остваривање Ваших амбиција.",
    "financing.request": "Затражи финансирање",
    "financing.history": "Моји захтеви",
    "financing.reimbursable_label": "Повратно",
    "financing.non_reimbursable_label": "Бесповратно",
    "financing.status.pending": "На чекању",
    "financing.status.granted": "Одобрено",
    "financing.status.refused": "Одбијено",
    "financing.form.title": "Захтев — {name}",
    "financing.form.cancel": "Откажи",
    "financing.form.submit": "Пошаљи захтев",
    "financing.form.project": "Назив пројекта",
    "financing.form.project_ph": "Мој пројекат",
    "financing.form.amount": "Тражени износ (€)",
    "financing.form.sector": "Сектор",
    "financing.form.sector_ph": "Tech, пољопривреда, …",
    "financing.form.description": "Опис пројекта",
    "financing.form.description_ph": "Представите свој пројекат, његове циљеве и очекивани утицај…",
    "settings.security": "Безбедност",
    "settings.current_pw": "Тренутна лозинка",
    "settings.new_pw": "Нова лозинка",
    "settings.confirm_pw": "Потврди",
    "settings.submit": "Промени лозинку",
    "settings.no_match": "Лозинке се не подударају",
    "settings.changed": "Лозинка промењена",
    "common.cancel": "Откажи",
    "common.confirm": "Потврди",
    "common.close": "Затвори",
    "common.save": "Сачувај",
    "common.loading": "Учитавање…",
    "common.send": "Пошаљи",
    "common.total": "Укупно",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Прибл. месечна рата",
    "credits.rate_annual": "/ год.",
    "credits.rate_used": "Прибл. стопа:",
    "credits.simulation": "Симулација месечне рате",
    "credits.total_cost": "Прибл. укупни трошкови",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Добродошли",
    "common.delete": "Обриши",
    "cards.delete.title": "Обриши картицу",
    "cards.delete.confirm_msg": "Ова радња је неповратна. Картица ће бити трајно обрисана.",
    "cards.delete.physical_tooltip": "Физичка картица наручена — брисање немогуће",
    "cards.order.confirmed": "Наруџба потврђена!",
    "cards.order.confirmed_body": "Ваша физичка картица се припрема. Користите овај број за праћење доставе.",
    "cards.order.tracking_no": "Бр. праћења",
    "cards.fee_deducted": "{amount} ће бити одбијено од вашег стања",
    "cards.insufficient_short": "Недовољно стање ({available} доступно)",
    "transfer.section.beneficiary": "Прималац",
    "transfer.section.details": "Пренос",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Банка",
    "transfer.field.city": "Град",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Банка:",
    "transfer.progress.paused": "Чека на потврду",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Promenljiva",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Kartica obrisana",
    "cards.feat.gold_exclusive": "Ексклузивна златна картица",
    "cards.feat.cashback_05": "Повраћај новца 0,5%",
    "cards.feat.buyer_protection": "Заштита купца",
    "cards.feat.support_standard": "Стандардна подршка",
    "cards.feat.plastic_premium": "Премиум пластична картица",
    "cards.feat.cashback_1": "Повраћај новца 1%",
    "cards.feat.travel_standard": "Стандардно путно осигурање",
    "cards.feat.support_priority": "Приоритетна подршка",
    "cards.feat.metal_brushed": "Брушена метална картица",
    "cards.feat.cashback_2_unlimited": "Неограничени 2% повраћај",
    "cards.feat.travel_premium": "Премиум путно осигурање",
    "cards.feat.vip_lounge": "Приступ VIP салонима",
    "cards.feat.support_247": "24/7 подршка",
    "auth.demo.title": "Пријава администратора",
    "auth.demo.subtitle": "Унесите админ податке за преглед демоа",
    "credits.garantie.none": "Nijedna",
    "credits.garantie.mortgage": "Hipoteka na finansiranu imovinu",
    "credits.garantie.vehicle": "Finansirano vozilo",
    "credits.garantie.parental": "Roditeljska ili bankarska",
    "credits.garantie.professional": "Poslovna sredstva ili lično jemstvo",
    "cards.reveal.pin_prompt": "Unesite vaš 4-cifreni PIN kod da biste prikazali informacije o kartici.",
    "cards.reveal.show": "Prikaži",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Два ПИН кода не одговарају",
    "auth.pin.digit_hint": "4 цифре",
  
  },
  bg: {
    "notif.transfer_code.title": "Код за потвърждение на превод",
    "notif.transfer_code.body": "Копирайте този код, за да потвърдите превода.",
    "notif.transfer_code.expires_in": "Изчезва след {sec}с",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} месеца",
    "credits.n_months": "{n} месеца",
    "msg.read": "Прочетено",
    "msg.sent": "Изпратено",
    "msg.attach_aria": "Прикачи файл",
    "login.device.unknown": "Непознат браузър",
    "login.device.mobile": "Мобилен",
    "login.device.tablet": "Таблет",
    "login.device.desktop": "Компютър",
    "login.recent": "Скорошен",
    "err.invalid_credentials": "Невалидни данни за вход",
    "err.bad_pin": "Грешен PIN",
    "err.account_blocked": "Акаунтът е блокиран",
    "err.bad_current_pw": "Текущата парола е грешна",
    "err.insufficient_funds": "Недостатъчни средства, моля заредете акаунта си.",
    "err.amount_above_savings": "Сумата надвишава наличните спестявания",
    "err.must_be_positive": "Стойността трябва да е положителна",
    "err.invalid_format": "Невалиден формат",
    "err.email_taken": "Имейлът вече се използва",
    "err.card_not_found": "Картата не е намерена",
    "err.physical_card_already_ordered": "Физическата карта вече е поръчана",
    "err.beneficiary_not_found": "Получателят не е намерен",
    "err.invalid_code": "Невалиден код",
    "err.no_transfer_code": "Няма код за този превод",
    "err.link_expired": "Линкът е невалиден или изтекъл",
    "err.thread_not_found": "Разговорът не е намерен",
    "err.empty_message": "Празно съобщение",
    "err.file_too_large": "Файлът е твърде голям (макс 20 MB)",
    "err.forbidden": "Достъпът е отказан",
    "err.bank_not_found": "Банката не е намерена",
    "err.signups_closed": "Регистрациите за тази банка са затворени.",
    "err.access_suspended": "Достъпът е временно спрян. Моля, свържете се с банката си.",
    "err.notif_not_closable": "Това известие не може да бъде затворено.",
    "auth.signup.tagline": "Присъединете се към нас днес",
    "auth.login.tagline": "Добре дошли!",
    "auth.signup.tagline_sub": "Отворете сметка за 2 минути и получете достъп до всички наши премиум банкови услуги.",
    "auth.login.tagline_sub": "Достъпете личното си пространство и управлявайте финансите си сигурно.",
    "auth.back": "Обратно към началната страница",
    "auth.bullet.security": "Банкова сигурност от военен клас",
    "auth.bullet.transfers": "Незабавни преводи 24h/24",
    "auth.bullet.investments": "Интегрирани инвестиции и спестявания",
    "auth.bullet.advisor": "Личен съветник, достъпен 7 дни в седмицата",
    "auth.footer": "Задвижва се от {bank}",
    "auth.signup.title": "Създайте сметка",
    "auth.login.title": "Вход",
    "auth.field.lastName": "Фамилия",
    "auth.field.firstName": "Собствено(и) име(на)",
    "auth.field.country": "Държава",
    "auth.field.country.placeholder": "— Изберете вашата държава —",
    "auth.field.phone": "Телефон",
    "auth.field.address": "Адрес",
    "auth.field.address.placeholder": "ул. Мир 123, София",
    "auth.field.email": "Имейл",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Парола",
    "auth.submit.signup": "Създай моята сметка",
    "auth.submit.login": "Влезте",
    "auth.login.badge": "Влезте в акаунта си",
    "auth.card.tagline": "Вашето клиентско пространство",
    "auth.submit.loading": "Зареждане…",
    "auth.pin.title": "ПИН код",
    "auth.pin.subtitle": "Въведете своя 4-цифрен ПИН код",
    "auth.pin.label": "ПИН код",
    "auth.pin.confirm": "Потвърдете ПИН кода",
    "auth.pin.submit": "Потвърди",
    "auth.pin.back": "Обратно към вход",
    "auth.pin.length_error": "ПИН кодът трябва да е 4 цифри",
    "auth.switch.have_account": "Вече имате сметка?",
    "auth.switch.login_link": "Вход",
    "auth.switch.no_account": "Все още не сте клиент?",
    "auth.error.select_country": "Изберете вашата държава",
    "kyc.title": "Проверка на самоличността (KYC)",
    "kyc.sub": "Качете документите си за валидиране на сметката. Всички документи са криптирани и поверителни.",
    "kyc.id_type": "Вид документ за самоличност *",
    "kyc.id_card": "Лична карта",
    "kyc.passport": "Паспорт",
    "kyc.driver_license": "Шофьорска книжка",
    "kyc.recto_passport": "Предна страна на паспорта *",
    "kyc.recto_id": "Предна страна на документа *",
    "kyc.verso": "Задна страна на документа *",
    "kyc.selfie": "Селфи с документ в ръка *",
    "kyc.address_proof": "Доказателство за адрес *",
    "kyc.hint_doc": "Изображение или PDF, макс. 5 MB",
    "kyc.hint_selfie": "Ясна снимка, лице и документ видими",
    "kyc.hint_address": "Сметка, извлечение, удостоверение… (макс. 5 MB)",
    "kyc.upload": "Качи",
    "kyc.replace": "Замени",
    "pending.title": "Сметката е в изчакване",
    "pending.body": "Вашата заявка е получена. Администратор ще провери досието ви и ще активира сметката ви в кратък срок.",
    "pending.back": "Обратно към входа",
    "home.nav.features": "Функционалности",
    "home.nav.security": "Сигурност",
    "home.nav.invest": "Инвестиции",
    "home.nav.credits": "Кредити",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Влезте",
    "home.hero.tagline": "Онлайн банкиране от ново поколение",
    "home.hero.title1": "Управлявайте парите си",
    "home.hero.title2": "с интелигентност",
    "home.hero.sub": "Пълна банкова платформа — преводи, спестявания, инвестиции и кредити от вашия смартфон.",
    "home.hero.login": "Влезте",
    "home.hero.trust.secure": "100% Сигурно",
    "home.hero.trust.fast": "Бързо отваряне",
    "home.hero.trust.encrypted": "Криптирани данни",
    "home.hero.trust.support": "Услуга 24/7",
    "home.mockup.balance": "Налично салдо",
    "home.mockup.currency": "EUR · Основна сметка",
    "home.mockup.recharge": "Зареди",
    "home.mockup.send": "Изпрати",
    "home.mockup.save": "Спести",
    "home.mockup.salary": "Заплата Март",
    "home.mockup.rent": "Наем Април",
    "home.mockup.investment": "Инвестиция",
    "home.mockup.days2": "преди 2 дни",
    "home.mockup.days3": "преди 3 дни",
    "home.mockup.days5": "преди 5 дни",
    "home.stats.accounts": "Активни сметки",
    "home.stats.uptime": "Наличност",
    "home.stats.transfers": "Преводи",
    "home.stats.support": "Поддръжка",
    "home.features.label": "Функционалности",
    "home.features.title": "Всичко, от което се нуждаете",
    "home.features.sub": "Пълна платформа, проектирана да опрости управлението на вашите финанси всеки ден.",
    "home.feature.security": "Военна сигурност",
    "home.feature.security_body": "AES-256 криптиране, двуфакторна автентикация и наблюдение на сметката 24/7.",
    "home.feature.cards": "Виртуална и физическа карта",
    "home.feature.cards_body": "Издавайте карти за секунди или поръчайте истинска физическа карта, доставена до вас.",
    "home.feature.transfers": "Бързи преводи",
    "home.feature.transfers_body": "Изпращайте и получавайте пари навсякъде по света за секунди.",
    "home.feature.analytics": "Анализ в реално време",
    "home.feature.analytics_body": "Интелигентни табла за управление за следене на разходите и предвиждане на финансите ви.",
    "home.feature.investments": "Интегрирани инвестиции",
    "home.feature.investments_body": "Достъп до избрани инвестиционни продукти директно от вашата сметка.",
    "home.feature.support": "Специализирана поддръжка 24/7",
    "home.feature.support_body": "Личен банков съветник, достъпен по всяко време чрез защитена кореспонденция.",
    "home.tpl.label": "Trust",
    "home.tpl.title": "They trust us",
    "home.mockup.balance_label": "Салдо",
    "home.mockup.virement": "Превод",
    "home.mockup.salaire": "Заплата",
    "home.security.label": "Сигурност",
    "home.security.title1": "Вашите финанси",
    "home.security.title2": "в абсолютна сигурност",
    "home.security.sub": "Прилагаме най-строгите стандарти за банкова сигурност, за да защитим всеки цент.",
    "home.security.aes.title": "AES-256 криптиране",
    "home.security.aes.body": "Всички чувствителни данни са криптирани със същите алгоритми, използвани от големите международни банки.",
    "home.security.auth.title": "Засилена автентикация",
    "home.security.auth.body": "Двойна проверка на самоличността при всяко влизане и за всяка чувствителна операция по вашата сметка.",
    "home.security.compliance.title": "Съответствие PSD2/GDPR",
    "home.security.compliance.body": "Спазваме всички европейски разпоредби относно платежните услуги и защитата на данните.",
    "home.invest.label": "Инвестиции",
    "home.invest.title": "Умножете парите си",
    "home.invest.sub": "6 инвестиционни категории, избрани за техния потенциал и надеждност.",
    "home.invest.yield": "Доходност",
    "home.credits.label": "Кредити",
    "home.credits.title": "Решения, пригодени за всеки проект",
    "home.credits.sub": "Конкурентни лихви, бързи отговори и персонализирана подкрепа за всички ваши проекти.",
    "home.credits.rate": "Лихва",
    "home.faq.label": "FAQ",
    "home.faq.title": "Често задавани въпроси",
    "home.faq.q1": "Как да открия сметка?",
    "home.faq.a1": "Регистрацията отнема по-малко от 2 минути. Просто въведете личните си данни, изберете парола и сметката ви ще бъде активирана след проверка от нашия екип.",
    "home.faq.q2": "Сигурни ли са моите данни?",
    "home.faq.a2": "Да. Всички данни са криптирани с AES-256 при съхранение и TLS 1.3 при пренос. Спазваме международните банкови стандарти и разпоредбите GDPR/PSD2.",
    "home.faq.q3": "Какви видове инвестиции предлагате?",
    "home.faq.a3": "Предлагаме 6 категории: Недвижими имоти, Crowdfunding, Възобновяема енергия, Борса, Участническо и Крипто. Всеки продукт е избран за съотношението риск/доходност.",
    "home.faq.q4": "Как да кандидатствам за кредит?",
    "home.faq.a4": "Отидете в раздел Кредити на клиентската си зона, изберете вида кредит, въведете сумата и срока. Заявката ви ще бъде обработена в рамките на 24h от нашите консултанти.",
    "home.faq.q5": "Има ли скрити такси?",
    "home.faq.a5": "Няма скрити такси. Всички тарифи са ясно показани при всяка операция. Условията са достъпни в клиентската ви зона по всяко време.",
    "home.faq.q6": "Как да се свържа с поддръжката?",
    "home.faq.a6": "Използвайте интегрираната кореспонденция в клиентската си зона за директен контакт с личния ви банков съветник, достъпен 24h/24 и 7 дни в седмицата.",
    "home.cta.title": "Готови ли сте да започнете?",
    "home.cta.sub": "Присъединете се към хиляди потребители, управляващи финансите си с {bank}. Регистрацията отнема по-малко от 2 минути.",
    "home.footer.products": "Продукти",
    "home.footer.services": "Услуги",
    "home.footer.access": "Бърз достъп",
    "home.footer.accounts": "Сметки",
    "home.footer.transfers": "Преводи",
    "home.footer.cards": "Карти",
    "home.footer.savings": "Спестявания",
    "home.footer.investments": "Инвестиции",
    "home.footer.credits": "Кредити",
    "home.footer.financing": "Финансиране",
    "home.footer.messages": "Съобщения",
    "home.footer.login": "Вход",
    "home.footer.signup": "Открий сметка",
    "home.footer.support": "Поддръжка 24/7",
    "home.footer.tagline": "Вашата онлайн банка, достъпна навсякъде и по всяко време.",
    "home.footer.copyright": "© {year} {bank} — Всички права запазени",
    "home.footer.powered": "Задвижва се от {bank}",
    "app.menu_label": "Главно меню",
    "app.nav.dashboard": "Табло за управление",
    "app.nav.accounts": "Сметки",
    "app.nav.transactions": "Транзакции",
    "app.nav.transfers": "Преводи",
    "app.nav.cards": "Карти",
    "app.nav.savings": "Спестявания",
    "app.nav.investments": "Инвестиции",
    "app.nav.credit": "Кредит",
    "app.nav.financing": "Финансиране",
    "app.nav.messages": "Съобщения",
    "app.nav.card_tracking": "Проследяване на карта",
    "app.nav.notifications": "Известия",
    "app.nav.settings": "Настройки",
    "app.nav.profile": "Профил",
    "app.nav.mobile.home": "Начало",
    "app.nav.mobile.transfers": "Преводи",
    "app.nav.mobile.cards": "Карти",
    "app.nav.mobile.transactions": "Транзакции",
    "app.nav.more.invest": "Инвестирай",
    "app.user_role": "Клиент",
    "app.logout": "Изход",
    "app.search": "Търси…",
    "app.more_title": "Още функционалности",
    "app.more_btn": "Още",
    "app.loading": "Зареждане…",
    "app.error_back": "Обратно към входа",
    "app.see_more_notifs": "Виж {count} още известия →",
    "page.accounts": "Моите сметки",
    "page.transactions": "Транзакции",
    "page.transfers": "Нов превод",
    "page.deposit": "Депозит",
    "page.cards": "Моите карти",
    "page.savings": "Спестявания",
    "page.investments": "Инвестиции",
    "page.credits": "Кредит",
    "page.financing": "Финансиране на проекти",
    "page.messages": "Съобщения",
    "page.settings": "Настройки",
    "page.notifications": "Важни известия",
    "page.profile": "Моят профил",
    "dash.greeting": "Добър ден, {name}",
    "dash.overview": "Преглед на вашите финанси — {date}",
    "dash.total_balance": "Общо салдо",
    "dash.this_month": "+{amount} този месец",
    "dash.view_iban": "Виж моя IBAN",
    "dash.my_accounts": "Моите сметки",
    "dash.primary_account": "Основна сметка",
    "dash.savings_account": "Спестовна сметка",
    "dash.remunerated": "Лихвоносна",
    "dash.details": "Детайли",
    "dash.transfer_btn": "Превод",
    "dash.quick_actions": "Бързи действия",
    "dash.quick.deposit": "Депозит",
    "deposit.title": "Информация за депозита",
    "dash.quick.transfer": "Превод",
    "dash.quick.history": "История",
    "dash.quick.cards": "Моите карти",
    "dash.quick.profile": "Профил",
    "dash.monthly_budget": "Месечен бюджет",
    "dash.income": "Приходи",
    "dash.expenses": "Разходи",
    "dash.spending_cat": "Разходи по категория",
    "dash.no_spending": "Няма записани разходи.",
    "dash.recent_tx": "Скорошни транзакции",
    "dash.see_all": "Виж всички",
    "dash.no_tx": "Няма операции.",
    "dash.main_card": "Моята основна карта",
    "dash.manage": "Управлявай",
    "dash.block": "Блокирай",
    "dash.pin": "PIN код",
    "dash.security": "Сигурност",
    "dash.more": "Още",
    "dash.no_card": "Няма издадена карта",
    "dash.monthly_activity": "Активност за месеца",
    "dash.operations": "Операции",
    "dash.holder": "Притежател",
    "dash.expires": "Изт.",
    "dash.visa_label": "VISA",
    "dash.balance": "Налично салдо",
    "tx.all": "Всички",
    "tx.income": "Приходи",
    "tx.expenses": "Разходи",
    "tx.today": "Днес",
    "tx.yesterday": "Вчера",
    "tx.none": "Няма транзакции.",
    "transfer.title": "Бърз превод",
    "transfer.recipient": "Получател",
    "transfer.recipient_ph": "Ime, IBAN, broj telefona…",
    "transfer.amount": "Сума",
    "transfer.label": "Описание",
    "transfer.reason": "Причина (незадължително)",
    "transfer.reason_ph": "Напр.: наем, връщане на сума…",
    "transfer.execution": "Изпълнение",
    "transfer.immediate": "Незабавно",
    "transfer.delayed": "Отложено",
    "transfer.balance": "Налично салдо:",
    "transfer.continue": "Продължи",
    "transfer.confirm": "Потвърди",
    "transfer.cancel": "Откажи",
    "transfer.code": "Код за валидация",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Въведете получения код за потвърждение на превода",
    "transfer.pending.title": "Преводът чака валидация",
    "transfer.pending.amount": "Сума:",
    "transfer.pending.to": "За:",
    "transfer.pending.hint": "Вашият код за валидация ще бъде изпратен по имейл или чрез камбанката за известия. Кодът се състои от 6 цифри. Ако не го получите, свържете се с поддръжката.",
    "transfer.pending.validate": "Потвърди превода",
    "transfer.saved": "Преводът е записан. Поискайте кода от администратора си за потвърждение.",
    "transfer.done": "Преводът е извършен",
    "transfer.validated": "Преводът е потвърден!",
    "transfer.progress.processing": "Обработка на превода…",
    "transfer.progress.completed": "Преводът е извършен!",
    "transfer.progress.default_message": "Вашият превод се обработва. Моля, изчакайте.",
    "cards.none": "Няма издадена карта.\nСвържете се с вашия администратор.",
    "cards.holder": "Притежател",
    "cards.expires": "Изт.",
    "cards.block": "Заключи",
    "cards.pin": "PIN код",
    "cards.more": "Още",
    "cards.limits": "Лимити на картата",
    "cards.payment": "Плащане",
    "cards.withdrawal": "Теглене на пари",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Блокирана",
    "cards.status_active": "Активна",
    "cards.toast.blocked": "Картата е блокирана",
    "cards.toast.unblocked": "Картата е деблокирана",
    "cards.toast.physical_ordered": "Физическата карта е поръчана! Доставка за 5–10 работни дни.",
    "cards.toast.created": "Картата е генерирана успешно!",
    "cards.none_sub": "Няма издадена карта. Генерирайте вашата виртуална карта сега.",
    "cards.generate_btn": "Генерирай моята карта",
    "cards.create.title": "Генерирай моята виртуална карта",
    "cards.create.network": "Платежна мрежа",
    "cards.create.color": "Цвят на картата",
    "cards.create.preview_name": "ВАШЕТО ИМЕ",
    "cards.create.preview_expiry": "ММ/ГГ",
    "cards.create.loading": "Генериране…",
    "cards.create.submit": "Генерирай карта",
    "cards.btn.unblock": "Деблокирай",
    "cards.btn.block": "Блокирай",
    "cards.btn.info": "Инфо",
    "cards.btn.physical": "Физическа",
    "cards.btn.physical_done": "Поръчана",
    "cards.limits_title": "Лимити",
    "cards.reveal.title": "Информация за картата",
    "cards.reveal.number": "Пълен номер",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Срок на валидност",
    "cards.reveal.warning": "Никога не споделяйте тази информация. Затворете прозореца след употреба.",
    "cards.pin.title": "PIN код",
    "cards.pin.message": "Този PIN код се използва за всички ваши онлайн плащания и тегления. Никога не го съобщавайте на никого.",
    "cards.physical.title": "Поръчай физическа карта",
    "cards.physical.fee_label": "Такса за доставка",
    "cards.physical.balance_label": "Вашият баланс",
    "cards.physical.insufficient": "Недостатъчен баланс. Моля, заредете сметката си преди да поръчате.",
    "cards.physical.info": "Таксата ще бъде удържана от вашата сметка. Доставка за 5–10 работни дни.",
    "cards.physical.order_btn": "Поръчай ({fee})",
    "cards.physical.ordering": "Поръчване…",
    "cards.physical.order_cta": "Поръчайте физическа карта",
    "cards.physical.choose_type": "Изберете вашата карта",
    "cards.physical.features": "Предимства",
    "cards.buy_virtual_cta": "Купи виртуална карта",
    "notif.none": "Няма известия в момента.",
    "notif.contact_support": "Свържете се с поддръжката",
    "profile.change_photo": "Смени снимката",
    "profile.remove_photo": "Премахни снимката",
    "profile.email": "Имейл",
    "profile.phone": "Телефон",
    "profile.country": "Държава",
    "profile.address": "Адрес",
    "profile.notifs_title": "Известия ({count})",
    "profile.no_notifs": "Няма известия.",
    "profile.logout": "Изход",
    "profile.photo_updated": "Профилната снимка е актуализирана",
    "profile.photo_removed": "Снимката е премахната",
    "profile.photo_too_large": "Изображението е твърде голямо (макс. 1 MB)",
    "profile.powered": "Задвижва се от {bank}",
    "iban.title": "Моят IBAN",
    "iban.loading": "Зареждане…",
    "iban.bic": "BIC / SWIFT код",
    "iban.share_info": "Споделете тези данни, за да получите превод по вашата сметка.",
    "iban.copy": "Копирай",
    "iban.copied": "Копирано в клипборда",
    "iban.copy_fail": "Невъзможно копиране",
    "iban.close": "Затвори",
    "accounts.all": "Всички ваши сметки, открити при {bank}.",
    "accounts.primary": "Основна сметка",
    "accounts.savings": "Спестовна сметка",
    "accounts.primary_tag": "Основна",
    "accounts.secondary_tag": "Вторична",
    "accounts.objective": "Цел: {amount} · {pct}%",
    "accounts.copy": "Копирай",
    "accounts.loading": "Зареждане…",
    "accounts.iban_copied": "IBAN е копиран",
    "accounts.copy_fail": "Невъзможно копиране",
    "savings.total": "Общо спестено",
    "savings.target_total": "Обща цел",
    "savings.progress": "Напредък",
    "savings.active": "Активни цели",
    "savings.new_goal": "Нова цел",
    "savings.none": "Все още няма цели за спестяване.",
    "savings.none_sub": "Създайте първата си цел, за да започнете.",
    "savings.saved": "Спестено:",
    "savings.goal": "Цел:",
    "savings.deadline": "Краен срок: {date}",
    "savings.deposit": "Внеси",
    "savings.withdraw": "Изтегли",
    "savings.loading": "Зареждане…",
    "savings.form.title": "Нова цел за спестяване",
    "savings.form.cancel": "Откажи",
    "savings.form.create": "Създай",
    "savings.form.name": "Наименование на целта",
    "savings.form.name_ph": "Пътуване до Япония, Кола, …",
    "savings.form.target": "Целева сума",
    "savings.form.initial": "Начален депозит",
    "savings.form.deadline": "Краен срок (незадължително)",
    "savings.form.color": "Цвят",
    "savings.deposit_title": "Внеси в \"{name}\"",
    "savings.deposit_amount": "Сума за внасяне",
    "savings.balance_hint": "Основно салдо: {amount}",
    "savings.withdraw_prompt": "Колко да изтеглите от \"{name}\"?",
    "savings.close_confirm": "Затворете ли целта \"{name}\"? Спестяванията ще бъдат върнати в основната ви сметка.",
    "savings.toast.created": "Целта е създадена",
    "savings.toast.deposited": "Депозитът е извършен",
    "savings.toast.withdrawn": "Тeглeнeто е извършено",
    "savings.toast.closed": "Целта е затворена",
    "invest.total": "Общо инвестирано",
    "invest.profits": "Кумулативни печалби",
    "invest.count": "Инвестиции",
    "invest.products": "Налични продукти",
    "invest.invest_btn": "Инвестирай",
    "invest.my": "Моите инвестиции",
    "invest.pending": "В изчакване",
    "invest.validated": "Потвърдено",
    "invest.refused": "Отказано",
    "invest.withdraw.btn": "Изтегли",
    "invest.withdraw.title": "Изтегли инвестицията ми",
    "invest.withdraw.intro": "На път сте да изтеглите вашата инвестиция „{product}\". Първоначалният капитал и натрупаната печалба ще бъдат кредитирани в основното ви салдо.",
    "invest.withdraw.principal": "Първоначален капитал",
    "invest.withdraw.profit": "Печалба",
    "invest.withdraw.total": "Общо за получаване",
    "invest.withdraw.confirm": "Потвърди тегленето",
    "invest.withdraw.warning": "Това действие е необратимо. Инвестицията ще бъде затворена.",
    "invest.withdraw.success": "Инвестицията е изтеглена — {amount} са кредитирани в салдото.",
    "invest.withdrawn": "Изтеглена",
    "invest.cta_sub": "Изберете вида инвестиция, която ви подхожда. Съветник ще ви придружи от първата заявка.",
    "invest.form.amount": "Сума за инвестиране",
    "invest.form.balance": "Налично салдо: {amount}",
    "invest.form.note": "Бележка за съветника (незадължително)",
    "invest.form.note_ph": "Посочете очакванията си…",
    "invest.form.advisor_info": "Разговор с вашия съветник ще бъде автоматично отворен.",
    "invest.form.cancel": "Откажи",
    "invest.form.submit": "Изпрати заявка",
    "invest.toast.requested": "Заявката е регистрирана — отворен е разговор с вашия съветник.",
    "invest.p1.name": "Недвижими имоти",
    "invest.p1.desc": "SCPI, недвижимостен crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Участническо финансиране на избрани проекти",
    "invest.p3.name": "Възобновяема енергия",
    "invest.p3.desc": "Слънчева, вятърна и зелени проекти",
    "invest.p4.name": "Борса",
    "invest.p4.desc": "Акции, ETF и диверсифицирани фондове",
    "invest.p5.name": "Участническо",
    "invest.p5.desc": "Кредити за МСП и местни предприемачи",
    "invest.p6.name": "Крипто",
    "invest.p6.desc": "Bitcoin, Ethereum и многовалутен портфейл",
    "msg.online": "Онлайн",
    "msg.loading": "Зареждане…",
    "msg.none": "Няма съобщения. Започнете разговор с вашия съветник.",
    "msg.placeholder": "Напишете вашето съобщение…",
    "msg.attach.image": "Изображение",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Аудио",
    "msg.attach.file": "Файл / Документ",
    "msg.pdf_doc": "PDF документ",
    "msg.file": "Файл",
    "msg.too_large": "Файлът е твърде голям (макс. {size}).",
    "msg.withdraw": "Изтегли",
    "credits.cta_sub": "Подайте заявката си директно онлайн — отговор в рамките на 24h.",
    "credits.learn_more": "Научи повече",
    "credits.request": "Заяви",
    "credits.history": "Моите заявки",
    "credits.status.pending": "В изчакване",
    "credits.status.accepted": "Прието",
    "credits.status.refused": "Отказано",
    "credits.chars": "Характеристики",
    "credits.amount_range": "Сума: {min}€ – {max}€",
    "credits.rate_range": "Лихвен процент: {min}% – {max}% годишно",
    "credits.duration_range": "Срок: {min} до {max} месеца",
    "credits.deposit_min": "Начален депозит: {amount}€ минимум",
    "credits.garantie": "Гаранция: {garantie}",
    "credits.eligibility": "Условия за допустимост",
    "credits.elig.income": "Доказване на редовни и стабилни доходи",
    "credits.elig.deposit": "Извършване на начален депозит от {amount}€",
    "credits.elig.garantie": "Представяне на гаранцията: {garantie}",
    "credits.form.title": "Заявка — {name}",
    "credits.form.cancel": "Откажи",
    "credits.form.submit": "Изпрати заявка",
    "credits.form.amount": "Желана сума (€)",
    "credits.form.duration": "Срок (месеци)",
    "credits.form.reason": "Причина (незадължително)",
    "credits.form.reason_ph": "Опишете проекта си…",
    "credits.form.close": "Затвори",
    "credits.toast.submitted": "Заявката за кредит е изпратена — ще получите отговор в рамките на 24h.",
    "credits.p1.name": "Потребителски кредит",
    "credits.p1.desc": "За финансиране на ежедневни нужди или лични проекти.",
    "credits.p2.name": "Ипотечен кредит",
    "credits.p2.desc": "За финансиране на покупка или строеж на недвижим имот.",
    "credits.p3.name": "Автомобилен кредит",
    "credits.p3.desc": "Купете превозно средство спокойно.",
    "credits.p4.name": "Студентски кредит",
    "credits.p4.desc": "Финансова помощ за висше образование.",
    "credits.p5.name": "Бизнес кредит",
    "credits.p5.desc": "За подкрепа на растежа на професионална дейност.",
    "credits.p6.name": "Експресен кредит",
    "credits.p6.desc": "Бързо решение за спешни нужди.",
    "financing.reimbursable": "Възстановимо финансиране",
    "financing.reimbursable_desc": "Финансова подкрепа с фиксирани условия за стартиране или развиване на дейност.",
    "financing.non_reimbursable": "Невъзстановимо финансиране",
    "financing.non_reimbursable_desc": "За проекти с обществено, образователно или хуманитарно въздействие (НПО, сдружения).",
    "financing.cta_sub": "Пригодени решения за реализиране на вашите амбиции.",
    "financing.request": "Заяви финансиране",
    "financing.history": "Моите заявки",
    "financing.reimbursable_label": "Възстановимо",
    "financing.non_reimbursable_label": "Невъзстановимо",
    "financing.status.pending": "В изчакване",
    "financing.status.granted": "Одобрено",
    "financing.status.refused": "Отказано",
    "financing.form.title": "Заявка — {name}",
    "financing.form.cancel": "Откажи",
    "financing.form.submit": "Изпрати заявка",
    "financing.form.project": "Наименование на проекта",
    "financing.form.project_ph": "Моят проект",
    "financing.form.amount": "Исканата сума (€)",
    "financing.form.sector": "Сектор",
    "financing.form.sector_ph": "Tech, земеделие, …",
    "financing.form.description": "Описание на проекта",
    "financing.form.description_ph": "Представете проекта си, неговите цели и очакваното въздействие…",
    "settings.security": "Сигурност",
    "settings.current_pw": "Текуща парола",
    "settings.new_pw": "Нова парола",
    "settings.confirm_pw": "Потвърди",
    "settings.submit": "Смени паролата",
    "settings.no_match": "Паролите не съвпадат",
    "settings.changed": "Паролата е сменена",
    "common.cancel": "Откажи",
    "common.confirm": "Потвърди",
    "common.close": "Затвори",
    "common.save": "Запази",
    "common.loading": "Зареждане…",
    "common.send": "Изпрати",
    "common.total": "Общо",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Прибл. месечна вноска",
    "credits.rate_annual": "/ год.",
    "credits.rate_used": "Прибл. лихва:",
    "credits.simulation": "Симулация на месечна вноска",
    "credits.total_cost": "Прибл. обща стойност",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Добре дошли",
    "common.delete": "Изтрий",
    "cards.delete.title": "Изтрий картата",
    "cards.delete.confirm_msg": "Тази операция е необратима. Картата ще бъде изтрита за постоянно.",
    "cards.delete.physical_tooltip": "Физическа карта поръчана — изтриване невъзможно",
    "cards.order.confirmed": "Поръчката е потвърдена!",
    "cards.order.confirmed_body": "Вашата физическа карта се подготвя. Използвайте този номер, за да проследите доставката.",
    "cards.order.tracking_no": "№ за проследяване",
    "cards.fee_deducted": "{amount} ще бъде приспаднато от салдото ви",
    "cards.insufficient_short": "Недостатъчно салдо ({available} налично)",
    "transfer.section.beneficiary": "Получател",
    "transfer.section.details": "Превод",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Банка",
    "transfer.field.city": "Град",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Банка:",
    "transfer.progress.paused": "Изчаква валидиране",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Променлива",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Картата е изтрита",
    "cards.feat.gold_exclusive": "Ексклузивна златна карта",
    "cards.feat.cashback_05": "Кешбек 0,5%",
    "cards.feat.buyer_protection": "Защита на купувача",
    "cards.feat.support_standard": "Стандартна поддръжка",
    "cards.feat.plastic_premium": "Премиум пластмасова карта",
    "cards.feat.cashback_1": "Кешбек 1%",
    "cards.feat.travel_standard": "Стандартна туристическа застраховка",
    "cards.feat.support_priority": "Приоритетна поддръжка",
    "cards.feat.metal_brushed": "Карта от четкан метал",
    "cards.feat.cashback_2_unlimited": "Неограничен 2% кешбек",
    "cards.feat.travel_premium": "Премиум туристическа застраховка",
    "cards.feat.vip_lounge": "Достъп до VIP салони",
    "cards.feat.support_247": "24/7 поддръжка",
    "auth.demo.title": "Вход на администратор",
    "auth.demo.subtitle": "Въведете администраторски данни за преглед на демото",
    "credits.garantie.none": "Няма",
    "credits.garantie.mortgage": "Ипотека върху финансирания имот",
    "credits.garantie.vehicle": "Финансирано превозно средство",
    "credits.garantie.parental": "Родителска или банкова",
    "credits.garantie.professional": "Професионални активи или лично поръчителство",
    "cards.reveal.pin_prompt": "Въведете вашия 4-цифрен PIN код, за да видите информацията за картата.",
    "cards.reveal.show": "Покажи",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Двата ПИН кода не съвпадат",
    "auth.pin.digit_hint": "4 цифри",
  
  },
  mk: {
    "notif.transfer_code.title": "Код за потврда на трансфер",
    "notif.transfer_code.body": "Копирајте го овој код за да го потврдите трансферот.",
    "notif.transfer_code.expires_in": "Исчезнува за {sec}с",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} месеци",
    "credits.n_months": "{n} месеци",
    "msg.read": "Прочитано",
    "msg.sent": "Испратено",
    "msg.attach_aria": "Прикачи датотека",
    "login.device.unknown": "Непознат прелистувач",
    "login.device.mobile": "Мобилен",
    "login.device.tablet": "Таблет",
    "login.device.desktop": "Компјутер",
    "login.recent": "Неодамна",
    "err.invalid_credentials": "Неважечки податоци за најава",
    "err.bad_pin": "Погрешен PIN",
    "err.account_blocked": "Сметката е блокирана",
    "err.bad_current_pw": "Тековната лозинка е погрешна",
    "err.insufficient_funds": "Недоволно средства, наполнете ја сметката.",
    "err.amount_above_savings": "Износот ги надминува достапните заштеди",
    "err.must_be_positive": "Вредноста мора да биде позитивна",
    "err.invalid_format": "Неважечки формат",
    "err.email_taken": "Имејлот веќе се користи",
    "err.card_not_found": "Картичката не е најдена",
    "err.physical_card_already_ordered": "Физичката картичка е веќе нарачана",
    "err.beneficiary_not_found": "Примателот не е најден",
    "err.invalid_code": "Неважечки код",
    "err.no_transfer_code": "Нема код за овој трансфер",
    "err.link_expired": "Линкот е неважечки или истечен",
    "err.thread_not_found": "Разговорот не е најден",
    "err.empty_message": "Празна порака",
    "err.file_too_large": "Датотеката е преголема (макс 20 МБ)",
    "err.forbidden": "Пристапот е одбиен",
    "err.bank_not_found": "Банката не е најдена",
    "err.signups_closed": "Регистрациите за оваа банка се затворени.",
    "err.access_suspended": "Пристапот е привремено суспендиран. Контактирајте ја вашата банка.",
    "err.notif_not_closable": "Ова известување не може да се затвори.",
    "auth.signup.tagline": "Придружете ни се денес",
    "auth.login.tagline": "Добредојдовте!",
    "auth.signup.tagline_sub": "Отворете сметка за 2 минути и пристапете до сите наши премиум банкарски услуги.",
    "auth.login.tagline_sub": "Пристапете до вашиот личен простор и управувајте со вашите финансии безбедно.",
    "auth.back": "Назад кон почетната страница",
    "auth.bullet.security": "Банкарска безбедност на воено ниво",
    "auth.bullet.transfers": "Моментални трансфери 24/7",
    "auth.bullet.investments": "Интегрирани инвестиции и штедење",
    "auth.bullet.advisor": "Личен советник достапен 7 дена/7",
    "auth.footer": "Поддржано од {bank}",
    "auth.signup.title": "Создај сметка",
    "auth.login.title": "Најава",
    "auth.field.lastName": "Презиме",
    "auth.field.firstName": "Ime(иња)",
    "auth.field.country": "Земја",
    "auth.field.country.placeholder": "— Изберете ја вашата земја —",
    "auth.field.phone": "Телефон",
    "auth.field.address": "Адреса",
    "auth.field.address.placeholder": "ул. Македонија 123, Скопје",
    "auth.field.email": "Е-пошта",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Лозинка",
    "auth.submit.signup": "Создај ја мојата сметка",
    "auth.submit.login": "Најави се",
    "auth.login.badge": "Најави се на твојот профил",
    "auth.card.tagline": "Вашиот клиентски простор",
    "auth.submit.loading": "Вчитување…",
    "auth.pin.title": "ПИН код",
    "auth.pin.subtitle": "Внесете го вашиот 4-цифрен ПИН код",
    "auth.pin.label": "ПИН код",
    "auth.pin.confirm": "Потврди ПИН код",
    "auth.pin.submit": "Потврди",
    "auth.pin.back": "Назад кон најава",
    "auth.pin.length_error": "ПИН кодот мора да има 4 цифри",
    "auth.switch.have_account": "Веќе имате сметка?",
    "auth.switch.login_link": "Најава",
    "auth.switch.no_account": "Уште немате сметка?",
    "auth.error.select_country": "Изберете ја вашата земја",
    "kyc.title": "Верификација на идентитет (KYC)",
    "kyc.sub": "Поставете ги вашите документи за да ја потврдите сметката. Сите документи се шифрирани и доверливи.",
    "kyc.id_type": "Тип на документ за идентитет *",
    "kyc.id_card": "Лична карта",
    "kyc.passport": "Пасош",
    "kyc.driver_license": "Возачка дозвола",
    "kyc.recto_passport": "Предна страна на пасошот *",
    "kyc.recto_id": "Предна страна на документот *",
    "kyc.verso": "Задна страна на документот *",
    "kyc.selfie": "Селфи со документот за идентитет во рака *",
    "kyc.address_proof": "Потврда за адреса *",
    "kyc.hint_doc": "Слика или PDF, макс 5 MB",
    "kyc.hint_selfie": "Јасна фотографија, лице и документ видливи",
    "kyc.hint_address": "Сметка, извод, потврда… (макс 5 MB)",
    "kyc.upload": "Постави",
    "kyc.replace": "Замени",
    "pending.title": "Сметката е на чекање",
    "pending.body": "Вашето барање е примено. Администраторот ќе ја провери вашата документација и ќе ја активира вашата сметка наскоро.",
    "pending.back": "Назад кон најавата",
    "home.nav.features": "Функционалности",
    "home.nav.security": "Безбедност",
    "home.nav.invest": "Инвестиции",
    "home.nav.credits": "Кредити",
    "home.nav.faq": "ЧПП",
    "home.nav.login": "Најави се",
    "home.hero.tagline": "Онлајн банкарство од следна генерација",
    "home.hero.title1": "Управувајте со вашиот пари",
    "home.hero.title2": "со интелигенција",
    "home.hero.sub": "Комплетна банкарска платформа — трансфери, штедење, инвестиции и кредити од вашиот паметен телефон.",
    "home.hero.login": "Најави се",
    "home.hero.trust.secure": "100% Безбедно",
    "home.hero.trust.fast": "Брзо отворање",
    "home.hero.trust.encrypted": "Шифрирани податоци",
    "home.hero.trust.support": "Услуга 24/7",
    "home.mockup.balance": "Расположливо салдо",
    "home.mockup.currency": "EUR · Главна сметка",
    "home.mockup.recharge": "Наполни",
    "home.mockup.send": "Испрати",
    "home.mockup.save": "Штеди",
    "home.mockup.salary": "Плата март",
    "home.mockup.rent": "Кирија април",
    "home.mockup.investment": "Инвестиција",
    "home.mockup.days2": "пред 2 дена",
    "home.mockup.days3": "пред 3 дена",
    "home.mockup.days5": "пред 5 дена",
    "home.stats.accounts": "Активни сметки",
    "home.stats.uptime": "Достапност",
    "home.stats.transfers": "Трансфери",
    "home.stats.support": "Поддршка",
    "home.features.label": "Функционалности",
    "home.features.title": "Сè што ви треба",
    "home.features.sub": "Комплетна платформа дизајнирана за поедноставување на управувањето со вашите финансии секојдневно.",
    "home.feature.security": "Воена безбедност",
    "home.feature.security_body": "Шифрирање AES-256, двофакторска автентикација и мониторинг на сметката 24/7.",
    "home.feature.cards": "Виртуелни и физички картички",
    "home.feature.cards_body": "Издавајте картички за секунди или нарачајте вистинска физичка картичка доставена до вас.",
    "home.feature.transfers": "Експресни трансфери",
    "home.feature.transfers_body": "Испраќајте и примајте пари насекаде во светот за секунди.",
    "home.feature.analytics": "Анализа во реално време",
    "home.feature.analytics_body": "Паметни контролни табли за следење на вашите трошоци и предвидување на вашите финансии.",
    "home.feature.investments": "Интегрирани инвестиции",
    "home.feature.investments_body": "Пристапете до избрани инвестициски производи директно од вашата сметка.",
    "home.feature.support": "Посветена поддршка 24/7",
    "home.feature.support_body": "Личен банкарски советник достапен во секое време преку безбедна размена на пораки.",
    "home.tpl.label": "Доверба",
    "home.tpl.title": "Ни веруваат",
    "home.mockup.balance_label": "Салдо",
    "home.mockup.virement": "Трансфер",
    "home.mockup.salaire": "Плата",
    "home.security.label": "Безбедност",
    "home.security.title1": "Вашите финансии",
    "home.security.title2": "во апсолутна безбедност",
    "home.security.sub": "Ги применуваме најстрогите стандарди за банкарска безбедност за да заштитиме секој денар.",
    "home.security.aes.title": "Шифрирање AES-256",
    "home.security.aes.body": "Сите чувствителни податоци се шифрирани со истите алгоритми кои ги користат главните меѓународни банки.",
    "home.security.auth.title": "Засилена автентикација",
    "home.security.auth.body": "Двојна верификација на идентитетот при секоја најава и за секоја чувствителна операција на вашата сметка.",
    "home.security.compliance.title": "Усогласеност со DSP2/RGPD",
    "home.security.compliance.body": "Ги почитуваме сите европски регулативи во областа на платежните услуги и заштитата на податоците.",
    "home.invest.label": "Инвестиции",
    "home.invest.title": "Направете да работат вашите пари",
    "home.invest.sub": "6 инвестициски категории избрани поради нивниот потенцијал и сигурност.",
    "home.invest.yield": "Принос",
    "home.credits.label": "Кредити",
    "home.credits.title": "Решенија прилагодени на секој проект",
    "home.credits.sub": "Конкурентни стапки, брзи одговори и персонализирана поддршка за сите ваши проекти.",
    "home.credits.rate": "Стапка",
    "home.faq.label": "ЧПП",
    "home.faq.title": "Чести прашања",
    "home.faq.q1": "Како да отворам сметка?",
    "home.faq.a1": "Регистрацијата трае помалку од 2 минути. Само внесете ги вашите лични информации, изберете лозинка и вашата сметка ќе биде активирана по потврдата од нашиот тим.",
    "home.faq.q2": "Дали моите податоци се безбедни?",
    "home.faq.a2": "Да. Сите податоци се шифрирани со AES-256 при мирување и TLS 1.3 во транзит. Ги почитуваме меѓународните банкарски стандарди и регулативите RGPD/DSP2.",
    "home.faq.q3": "Какви видови инвестиции нудите?",
    "home.faq.a3": "Нудиме 6 категории: Недвижнини, Краудфандинг, Обновливи извори на енергија, Берза, Партиципативен и Крипто. Секој производ е избран поради односот ризик/принос.",
    "home.faq.q4": "Како да побарам кредит?",
    "home.faq.a4": "Одете во делот Кредити на вашиот клиентски простор, изберете го типот на заем, внесете го износот и траењето. Вашето барање се обработува во рок од 24 часа од страна на нашите советници.",
    "home.faq.q5": "Дали има скриени трошоци?",
    "home.faq.a5": "Без скриени трошоци. Сите тарифи се јасно прикажани при секоја операција. Условите се достапни во вашиот клиентски простор во секое време.",
    "home.faq.q6": "Како да ја контактирам поддршката?",
    "home.faq.a6": "Користете ја интегрираната пораки во вашиот клиентски простор за директно да го контактирате вашиот личен банкарски советник, достапен 24/7.",
    "home.cta.title": "Подготвени да започнете?",
    "home.cta.sub": "Придружете им се на илјадници корисници кои управуваат со своите финансии со {bank}. Регистрацијата трае помалку од 2 минути.",
    "home.footer.products": "Производи",
    "home.footer.services": "Услуги",
    "home.footer.access": "Брз пристап",
    "home.footer.accounts": "Сметки",
    "home.footer.transfers": "Трансфери",
    "home.footer.cards": "Картички",
    "home.footer.savings": "Штедење",
    "home.footer.investments": "Инвестиции",
    "home.footer.credits": "Кредити",
    "home.footer.financing": "Финансирање",
    "home.footer.messages": "Пораки",
    "home.footer.login": "Најава",
    "home.footer.signup": "Отвори сметка",
    "home.footer.support": "Поддршка 24/7",
    "home.footer.tagline": "Вашата онлајн банка, достапна насекаде и во секое време.",
    "home.footer.copyright": "© {year} {bank} — Сите права се задржани",
    "home.footer.powered": "Поддржано од {bank}",
    "app.menu_label": "Главно мени",
    "app.nav.dashboard": "Контролна табла",
    "app.nav.accounts": "Сметки",
    "app.nav.transactions": "Трансакции",
    "app.nav.transfers": "Трансфери",
    "app.nav.cards": "Картички",
    "app.nav.savings": "Штедење",
    "app.nav.investments": "Инвестиции",
    "app.nav.credit": "Кредит",
    "app.nav.financing": "Финансирање",
    "app.nav.messages": "Пораки",
    "app.nav.card_tracking": "Следење картичка",
    "app.nav.notifications": "Известувања",
    "app.nav.settings": "Поставки",
    "app.nav.profile": "Профил",
    "app.nav.mobile.home": "Почетна",
    "app.nav.mobile.transfers": "Трансфери",
    "app.nav.mobile.cards": "Картички",
    "app.nav.mobile.transactions": "Трансакции",
    "app.nav.more.invest": "Инвестирај",
    "app.user_role": "Клиент",
    "app.logout": "Одјава",
    "app.search": "Пребарај…",
    "app.more_title": "Повеќе функции",
    "app.more_btn": "Повеќе",
    "app.loading": "Вчитување…",
    "app.error_back": "Назад кон најавата",
    "app.see_more_notifs": "Погледни {count} дополнителни известувања →",
    "page.accounts": "Моите сметки",
    "page.transactions": "Трансакции",
    "page.transfers": "Нов трансфер",
    "page.deposit": "Депозит",
    "page.cards": "Моите картички",
    "page.savings": "Штедење",
    "page.investments": "Инвестиции",
    "page.credits": "Кредит",
    "page.financing": "Финансирање на проекти",
    "page.messages": "Пораки",
    "page.settings": "Поставки",
    "page.notifications": "Важни известувања",
    "page.profile": "Мојот профил",
    "dash.greeting": "Здраво, {name}",
    "dash.overview": "Преглед на вашите финансии — {date}",
    "dash.total_balance": "Вкупно салдо",
    "dash.this_month": "+{amount} овој месец",
    "dash.view_iban": "Погледни го мојот IBAN",
    "dash.my_accounts": "Моите сметки",
    "dash.primary_account": "Главна сметка",
    "dash.savings_account": "Штедна сметка",
    "dash.remunerated": "Со камата",
    "dash.details": "Детали",
    "dash.transfer_btn": "Трансфер",
    "dash.quick_actions": "Брзи акции",
    "dash.quick.deposit": "Депозит",
    "deposit.title": "Информации за депозитот",
    "dash.quick.transfer": "Трансфер",
    "dash.quick.history": "Историја",
    "dash.quick.cards": "Моите картички",
    "dash.quick.profile": "Профил",
    "dash.monthly_budget": "Месечен буџет",
    "dash.income": "Приходи",
    "dash.expenses": "Расходи",
    "dash.spending_cat": "Трошоци по категорија",
    "dash.no_spending": "Нема евидентирани трошоци.",
    "dash.recent_tx": "Неодамнешни трансакции",
    "dash.see_all": "Види сè",
    "dash.no_tx": "Нема операции.",
    "dash.main_card": "Мојата главна картичка",
    "dash.manage": "Управувај",
    "dash.block": "Блокирај",
    "dash.pin": "PIN код",
    "dash.security": "Безбедност",
    "dash.more": "Повеќе",
    "dash.no_card": "Нема издадена картичка",
    "dash.monthly_activity": "Месечна активност",
    "dash.operations": "Операции",
    "dash.holder": "Носител",
    "dash.expires": "Истек.",
    "dash.visa_label": "VISA",
    "dash.balance": "Расположливо салдо",
    "tx.all": "Сите",
    "tx.income": "Приходи",
    "tx.expenses": "Расходи",
    "tx.today": "Денес",
    "tx.yesterday": "Вчера",
    "tx.none": "Нема трансакции.",
    "transfer.title": "Брз трансфер",
    "transfer.recipient": "Примач",
    "transfer.recipient_ph": "Ime, IBAN, број на телефон…",
    "transfer.amount": "Износ",
    "transfer.label": "Наслов",
    "transfer.reason": "Причина (опционално)",
    "transfer.reason_ph": "Пр.: кирија, враќање…",
    "transfer.execution": "Извршување",
    "transfer.immediate": "Моментално",
    "transfer.delayed": "Одложено",
    "transfer.balance": "Расположливо салдо:",
    "transfer.continue": "Продолжи",
    "transfer.confirm": "Потврди",
    "transfer.cancel": "Откажи",
    "transfer.code": "Код за валидација",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Внесете го добиениот код за потврда на трансферот",
    "transfer.pending.title": "Трансфер на чекање за валидација",
    "transfer.pending.amount": "Износ:",
    "transfer.pending.to": "Кон:",
    "transfer.pending.hint": "Вашиот код за валидација ќе биде испратен по е-пошта или преку ѕвончето за известувања. Кодот е составен од 6 цифри. Доколку не го добиете, контактирајте ја поддршката.",
    "transfer.pending.validate": "Потврди го трансферот",
    "transfer.saved": "Трансферот е зачуван. Побарајте го кодот од вашиот администратор за потврда.",
    "transfer.done": "Трансферот е извршен",
    "transfer.validated": "Трансферот е потврден!",
    "transfer.progress.processing": "Обработка на трансферот…",
    "transfer.progress.completed": "Трансферот е извршен!",
    "transfer.progress.default_message": "Вашиот трансфер се обработува. Ве молиме почекајте.",
    "cards.none": "Нема издадена картичка.\nКонтактирајте го вашиот администратор.",
    "cards.holder": "Носител",
    "cards.expires": "Истек.",
    "cards.block": "Заклучи",
    "cards.pin": "PIN код",
    "cards.more": "Повеќе",
    "cards.limits": "Ограничувања на картичката",
    "cards.payment": "Плаќање",
    "cards.withdrawal": "Повлекување",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Блокирана",
    "cards.status_active": "Активна",
    "cards.toast.blocked": "Картичката е блокирана",
    "cards.toast.unblocked": "Картичката е деблокирана",
    "cards.toast.physical_ordered": "Физичката картичка е нарачана! Испорака за 5–10 работни дена.",
    "cards.toast.created": "Картичката е успешно генерирана!",
    "cards.none_sub": "Нема издадена картичка. Генерирајте ја вашата виртуелна картичка сега.",
    "cards.generate_btn": "Генерирај ја мојата картичка",
    "cards.create.title": "Генерирај ја мојата виртуелна картичка",
    "cards.create.network": "Платежна мрежа",
    "cards.create.color": "Боја на картичката",
    "cards.create.preview_name": "ВАШЕТО ИМЕ",
    "cards.create.preview_expiry": "ММ/ГГ",
    "cards.create.loading": "Генерирање…",
    "cards.create.submit": "Генерирај картичка",
    "cards.btn.unblock": "Деблокирај",
    "cards.btn.block": "Блокирај",
    "cards.btn.info": "Инфо",
    "cards.btn.physical": "Физичка",
    "cards.btn.physical_done": "Нарачана",
    "cards.limits_title": "Ограничувања",
    "cards.reveal.title": "Информации за картичката",
    "cards.reveal.number": "Целосен број",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Истекување",
    "cards.reveal.warning": "Никогаш не ги споделувајте овие информации. Затворете го прозорецот по употреба.",
    "cards.pin.title": "PIN код",
    "cards.pin.message": "Овој PIN код се користи за сите ваши онлајн плаќања и повлекувања. Никогаш не го кажувајте на никого.",
    "cards.physical.title": "Нарачај физичка картичка",
    "cards.physical.fee_label": "Трошок за испорака",
    "cards.physical.balance_label": "Вашиот биланс",
    "cards.physical.insufficient": "Недоволен биланс. Ве молиме дополнете ја сметката пред да нарачате.",
    "cards.physical.info": "Трошокот ќе биде одбиен од вашата сметка. Испорака за 5–10 работни дена.",
    "cards.physical.order_btn": "Нарачај ({fee})",
    "cards.physical.ordering": "Нарачување…",
    "cards.physical.order_cta": "Нарачај физичка картичка",
    "cards.physical.choose_type": "Избери ја својата картичка",
    "cards.physical.features": "Предности",
    "cards.buy_virtual_cta": "Купи виртуелна картичка",
    "notif.none": "Нема известувања во моментов.",
    "notif.contact_support": "Контактирајте ја поддршката",
    "profile.change_photo": "Промени фотографија",
    "profile.remove_photo": "Отстрани фотографија",
    "profile.email": "Е-пошта",
    "profile.phone": "Телефон",
    "profile.country": "Земја",
    "profile.address": "Адреса",
    "profile.notifs_title": "Известувања ({count})",
    "profile.no_notifs": "Нема известувања.",
    "profile.logout": "Одјава",
    "profile.photo_updated": "Профилната фотографија е ажурирана",
    "profile.photo_removed": "Фотографијата е отстранета",
    "profile.photo_too_large": "Сликата е прегoлема (макс 1 MB)",
    "profile.powered": "Поддржано од {bank}",
    "iban.title": "Мојот IBAN",
    "iban.loading": "Вчитување…",
    "iban.bic": "BIC / SWIFT код",
    "iban.share_info": "Споделете ги овие податоци за да примите трансфер на вашата сметка.",
    "iban.copy": "Копирај",
    "iban.copied": "Копирано во клипборд",
    "iban.copy_fail": "Невозможно копирање",
    "iban.close": "Затвори",
    "accounts.all": "Сите ваши сметки отворени кај {bank}.",
    "accounts.primary": "Главна сметка",
    "accounts.savings": "Штедна сметка",
    "accounts.primary_tag": "Главна",
    "accounts.secondary_tag": "Секундарна",
    "accounts.objective": "Цел: {amount} · {pct}%",
    "accounts.copy": "Копирај",
    "accounts.loading": "Вчитување…",
    "accounts.iban_copied": "IBAN е копиран",
    "accounts.copy_fail": "Невозможно копирање",
    "savings.total": "Вкупно заштедено",
    "savings.target_total": "Вкупна цел",
    "savings.progress": "Напредок",
    "savings.active": "Активни цели",
    "savings.new_goal": "Нова цел",
    "savings.none": "Нема цели за штедење засега.",
    "savings.none_sub": "Создадете ја вашата прва цел за да започнете.",
    "savings.saved": "Заштедено:",
    "savings.goal": "Цел:",
    "savings.deadline": "Краен рок: {date}",
    "savings.deposit": "Депозит",
    "savings.withdraw": "Повлечи",
    "savings.loading": "Вчитување…",
    "savings.form.title": "Нова цел за штедење",
    "savings.form.cancel": "Откажи",
    "savings.form.create": "Создај",
    "savings.form.name": "Ime на целта",
    "savings.form.name_ph": "Патување Јапонија, Автомобил, …",
    "savings.form.target": "Целен износ",
    "savings.form.initial": "Почетен депозит",
    "savings.form.deadline": "Краен рок (опционално)",
    "savings.form.color": "Боја",
    "savings.deposit_title": "Депозит на \"{name}\"",
    "savings.deposit_amount": "Износ за депозит",
    "savings.balance_hint": "Главно салдо: {amount}",
    "savings.withdraw_prompt": "Колку да се повлече од \"{name}\"?",
    "savings.close_confirm": "Затвори ја целта \"{name}\"? Заштедата ќе биде вратена на вашата главна сметка.",
    "savings.toast.created": "Целта е создадена",
    "savings.toast.deposited": "Депозитот е извршен",
    "savings.toast.withdrawn": "Повлекувањето е извршено",
    "savings.toast.closed": "Целта е затворена",
    "invest.total": "Вкупно инвестирано",
    "invest.profits": "Кумулативни добивки",
    "invest.count": "Инвестиции",
    "invest.products": "Достапни производи",
    "invest.invest_btn": "Инвестирај",
    "invest.my": "Моите инвестиции",
    "invest.pending": "На чекање",
    "invest.validated": "Потврдено",
    "invest.refused": "Одбиено",
    "invest.withdraw.btn": "Повлечи",
    "invest.withdraw.title": "Повлечи ја мојата инвестиција",
    "invest.withdraw.intro": "Се подготвувате да ја повлечете вашата инвестиција „{product}\". Почетниот капитал и пресметаната добивка ќе бидат уплатени на вашиот главен биланс.",
    "invest.withdraw.principal": "Почетен капитал",
    "invest.withdraw.profit": "Добивка",
    "invest.withdraw.total": "Вкупно за прием",
    "invest.withdraw.confirm": "Потврди повлекување",
    "invest.withdraw.warning": "Оваа акција е неповратна.",
    "invest.withdraw.success": "Инвестицијата е повлечена — {amount} уплатено на билансот.",
    "invest.withdrawn": "Повлечено",
    "invest.cta_sub": "Изберете го типот на инвестиција која ви одговара. Советник ќе ве придружува уште од вашето барање.",
    "invest.form.amount": "Износ за инвестирање",
    "invest.form.balance": "Расположливо салдо: {amount}",
    "invest.form.note": "Белешка за вашиот советник (опционално)",
    "invest.form.note_ph": "Наведете ги вашите очекувања…",
    "invest.form.advisor_info": "Разговор ќе биде автоматски отворен со вашиот советник.",
    "invest.form.cancel": "Откажи",
    "invest.form.submit": "Испрати барање",
    "invest.toast.requested": "Барањето е регистрирано — разговор е отворен со вашиот советник.",
    "invest.p1.name": "Недвижнини",
    "invest.p1.desc": "SCPI, хартиена тула, краудфандинг во недвижнини",
    "invest.p2.name": "Краудфандинг",
    "invest.p2.desc": "Партиципативно финансирање на избрани проекти",
    "invest.p3.name": "Обновливи извори",
    "invest.p3.desc": "Соларни, ветерни и зелени проекти",
    "invest.p4.name": "Берза",
    "invest.p4.desc": "Акции, ETF и диверзифицирани фондови",
    "invest.p5.name": "Партиципативен",
    "invest.p5.desc": "Заеми за МСП и локални претприемачи",
    "invest.p6.name": "Крипто",
    "invest.p6.desc": "Bitcoin, Ethereum и повеќевалутен паричник",
    "msg.online": "Онлајн",
    "msg.loading": "Вчитување…",
    "msg.none": "Нема пораки. Започнете разговор со вашиот советник.",
    "msg.placeholder": "Напишете ја вашата порака…",
    "msg.attach.image": "Слика",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Аудио",
    "msg.attach.file": "Датотека / Документ",
    "msg.pdf_doc": "PDF документ",
    "msg.file": "Датотека",
    "msg.too_large": "Датотеката е преголема (макс {size}).",
    "msg.withdraw": "Повлечи",
    "credits.cta_sub": "Поднесете го вашето барање директно онлајн — одговор во рок од 24 часа.",
    "credits.learn_more": "Дознај повеќе",
    "credits.request": "Побарај",
    "credits.history": "Моите барања",
    "credits.status.pending": "На чекање",
    "credits.status.accepted": "Прифатено",
    "credits.status.refused": "Одбиено",
    "credits.chars": "Карактеристики",
    "credits.amount_range": "Износ: {min}€ – {max}€",
    "credits.rate_range": "Каматна стапка: {min}% – {max}% годишно",
    "credits.duration_range": "Траење: {min} до {max} месеци",
    "credits.deposit_min": "Почетен депозит: {amount}€ минимум",
    "credits.garantie": "Гаранција: {garantie}",
    "credits.eligibility": "Услови за подобност",
    "credits.elig.income": "Докажете редовни и стабилни приходи",
    "credits.elig.deposit": "Извршете почетен депозит од {amount}€",
    "credits.elig.garantie": "Обезбедете гаранција: {garantie}",
    "credits.form.title": "Барање — {name}",
    "credits.form.cancel": "Откажи",
    "credits.form.submit": "Испрати барање",
    "credits.form.amount": "Посакуван износ (€)",
    "credits.form.duration": "Траење (месеци)",
    "credits.form.reason": "Причина (опционално)",
    "credits.form.reason_ph": "Наведете го вашиот проект…",
    "credits.form.close": "Затвори",
    "credits.toast.submitted": "Барањето за кредит е испратено — ќе добиете одговор во рок од 24 часа.",
    "credits.p1.name": "Личен заем",
    "credits.p1.desc": "За финансирање на секојдневни потреби или лични проекти.",
    "credits.p2.name": "Хипотекарен заем",
    "credits.p2.desc": "За финансирање на купување или изградба на недвижнини.",
    "credits.p3.name": "Заем за авто / мото",
    "credits.p3.desc": "Купете возило без никакви грижи.",
    "credits.p4.name": "Студентски заем",
    "credits.p4.desc": "Финансиска помош за високото образование.",
    "credits.p5.name": "Деловен заем",
    "credits.p5.desc": "За поддршка на растот на деловна активност.",
    "credits.p6.name": "Експрес заем",
    "credits.p6.desc": "Брзо решение за итни потреби.",
    "financing.reimbursable": "Повратно финансирање",
    "financing.reimbursable_desc": "Финансиска поддршка со фиксни услови за стартување или развивање активност.",
    "financing.non_reimbursable": "Неповратно финансирање",
    "financing.non_reimbursable_desc": "За проекти со општествено, образовно или хуманитарно влијание (НВО, здруженија).",
    "financing.cta_sub": "Прилагодени решенија за остварување на вашите амбиции.",
    "financing.request": "Побарај финансирање",
    "financing.history": "Моите барања",
    "financing.reimbursable_label": "Повратно",
    "financing.non_reimbursable_label": "Неповратно",
    "financing.status.pending": "На чекање",
    "financing.status.granted": "Одобрено",
    "financing.status.refused": "Одбиено",
    "financing.form.title": "Барање — {name}",
    "financing.form.cancel": "Откажи",
    "financing.form.submit": "Испрати барање",
    "financing.form.project": "Ime на проектот",
    "financing.form.project_ph": "Мојот проект",
    "financing.form.amount": "Побаруван износ (€)",
    "financing.form.sector": "Сектор",
    "financing.form.sector_ph": "Технологија, земјоделство, …",
    "financing.form.description": "Опис на проектот",
    "financing.form.description_ph": "Претставете го вашиот проект, неговите цели и очекуваното влијание…",
    "settings.security": "Безбедност",
    "settings.current_pw": "Тековна лозинка",
    "settings.new_pw": "Нова лозинка",
    "settings.confirm_pw": "Потврди",
    "settings.submit": "Промени лозинка",
    "settings.no_match": "Лозинките не се совпаѓаат",
    "settings.changed": "Лозинката е променета",
    "common.cancel": "Откажи",
    "common.confirm": "Потврди",
    "common.close": "Затвори",
    "common.save": "Зачувај",
    "common.loading": "Вчитување…",
    "common.send": "Испрати",
    "common.total": "Вкупно",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Прибл. месечна рата",
    "credits.rate_annual": "/ год.",
    "credits.rate_used": "Прибл. стапка:",
    "credits.simulation": "Симулација на месечна рата",
    "credits.total_cost": "Прибл. вкупни трошоци",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Добредојде",
    "common.delete": "Избриши",
    "cards.delete.title": "Избриши картичка",
    "cards.delete.confirm_msg": "Оваа акција е неповратна. Картичката ќе биде трајно избришана.",
    "cards.delete.physical_tooltip": "Физичка картичка нарачана — бришење невозможно",
    "cards.order.confirmed": "Нарачката е потврдена!",
    "cards.order.confirmed_body": "Вашата физичка картичка се подготвува. Користете го овој број за следење на испораката.",
    "cards.order.tracking_no": "Бр. за следење",
    "cards.fee_deducted": "{amount} ќе биде одбиено од вашата состојба",
    "cards.insufficient_short": "Недоволна состојба ({available} достапно)",
    "transfer.section.beneficiary": "Корисник",
    "transfer.section.details": "Трансфер",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Банка",
    "transfer.field.city": "Град",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Банка:",
    "transfer.progress.paused": "Чека потврда",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Варијабилна",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Картичката е избришана",
    "cards.feat.gold_exclusive": "Ексклузивна златна картичка",
    "cards.feat.cashback_05": "Кешбек 0,5%",
    "cards.feat.buyer_protection": "Заштита на купувачот",
    "cards.feat.support_standard": "Стандардна поддршка",
    "cards.feat.plastic_premium": "Премиум пластична картичка",
    "cards.feat.cashback_1": "Кешбек 1%",
    "cards.feat.travel_standard": "Стандардно патничко осигурување",
    "cards.feat.support_priority": "Приоритетна поддршка",
    "cards.feat.metal_brushed": "Картичка од четкан метал",
    "cards.feat.cashback_2_unlimited": "Неограничен 2% кешбек",
    "cards.feat.travel_premium": "Премиум патничко осигурување",
    "cards.feat.vip_lounge": "Пристап до VIP салони",
    "cards.feat.support_247": "24/7 поддршка",
    "auth.demo.title": "Најава на администратор",
    "auth.demo.subtitle": "Внесете admin податоци за преглед на демото",
    "credits.garantie.none": "Нема",
    "credits.garantie.mortgage": "Хипотека врз финансираниот имот",
    "credits.garantie.vehicle": "Финансирано возило",
    "credits.garantie.parental": "Родителска или банкарска",
    "credits.garantie.professional": "Професионални средства или лична гаранција",
    "cards.reveal.pin_prompt": "Внесете го вашиот 4-цифрен PIN код за да ги прикажете информациите за картичката.",
    "cards.reveal.show": "Прикажи",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Двата ПИН кода не се совпаѓаат",
    "auth.pin.digit_hint": "4 цифри",
  
  },
  sq: {
    "notif.transfer_code.title": "Kodi i validimit të transferimit",
    "notif.transfer_code.body": "Kopjoni këtë kod për të validuar transferimin.",
    "notif.transfer_code.expires_in": "Zhduket pas {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} muaj",
    "credits.n_months": "{n} muaj",
    "msg.read": "Lexuar",
    "msg.sent": "Dërguar",
    "msg.attach_aria": "Bashkëngjit skedar",
    "login.device.unknown": "Shfletues i panjohur",
    "login.device.mobile": "Celular",
    "login.device.tablet": "Tabletë",
    "login.device.desktop": "Kompjuter",
    "login.recent": "Së fundmi",
    "err.invalid_credentials": "Kredenciale të pavlefshme",
    "err.bad_pin": "PIN i gabuar",
    "err.account_blocked": "Llogaria e bllokuar",
    "err.bad_current_pw": "Fjalëkalimi aktual është i gabuar",
    "err.insufficient_funds": "Fonde të pamjaftueshme, mbushni llogarinë tuaj.",
    "err.amount_above_savings": "Shuma tejkalon kursimet e disponueshme",
    "err.must_be_positive": "Vlera duhet të jetë pozitive",
    "err.invalid_format": "Format i pavlefshëm",
    "err.email_taken": "Emaili tashmë në përdorim",
    "err.card_not_found": "Karta nuk u gjet",
    "err.physical_card_already_ordered": "Karta fizike është porositur tashmë",
    "err.beneficiary_not_found": "Përfituesi nuk u gjet",
    "err.invalid_code": "Kod i pavlefshëm",
    "err.no_transfer_code": "Asnjë kod për këtë transfertë",
    "err.link_expired": "Lidhja e pavlefshme ose e skaduar",
    "err.thread_not_found": "Biseda nuk u gjet",
    "err.empty_message": "Mesazh bosh",
    "err.file_too_large": "Skedari shumë i madh (maks 20 MB)",
    "err.forbidden": "Aksesi i refuzuar",
    "err.bank_not_found": "Banka nuk u gjet",
    "err.signups_closed": "Regjistrimet janë mbyllur për këtë bankë.",
    "err.access_suspended": "Aksesi pezulluar përkohësisht. Ju lutemi kontaktoni bankën tuaj.",
    "err.notif_not_closable": "Ky njoftim nuk mund të mbyllet.",
    "auth.signup.tagline": "Bashkohuni me ne sot",
    "auth.login.tagline": "Mirë se keni ardhur!",
    "auth.signup.tagline_sub": "Hapni llogarinë tuaj në 2 minuta dhe aksesoni të gjitha shërbimet tona bankare premium.",
    "auth.login.tagline_sub": "Aksesoni hapësirën tuaj personale dhe menaxhoni financat tuaja me siguri.",
    "auth.back": "Kthehu në faqen kryesore",
    "auth.bullet.security": "Siguri bankare e nivelit ushtarak",
    "auth.bullet.transfers": "Transferta të menjëhershme 24/7",
    "auth.bullet.investments": "Investime dhe kursime të integruara",
    "auth.bullet.advisor": "Këshilltar personal i disponueshëm 7 ditë/7",
    "auth.footer": "Mundësuar nga {bank}",
    "auth.signup.title": "Krijo një llogari",
    "auth.login.title": "Hyrje",
    "auth.field.lastName": "Mbiemri",
    "auth.field.firstName": "Emri(et)",
    "auth.field.country": "Shteti",
    "auth.field.country.placeholder": "— Zgjidhni shtetin tuaj —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Adresa",
    "auth.field.address.placeholder": "Rruga Skënderbeu 123, Tiranë",
    "auth.field.email": "Email",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Fjalëkalimi",
    "auth.submit.signup": "Krijo llogarinë time",
    "auth.submit.login": "Hyr",
    "auth.login.badge": "Hyni në llogarinë tuaj",
    "auth.card.tagline": "Hapësira juaj e klientit",
    "auth.submit.loading": "Duke ngarkuar…",
    "auth.pin.title": "Kodi PIN",
    "auth.pin.subtitle": "Vendosni kodin tuaj PIN me 4 shifra",
    "auth.pin.label": "Kodi PIN",
    "auth.pin.confirm": "Konfirmo Kodin PIN",
    "auth.pin.submit": "Konfirmo",
    "auth.pin.back": "Kthehu te identifikimi",
    "auth.pin.length_error": "Kodi PIN duhet të ketë 4 shifra",
    "auth.switch.have_account": "Keni tashmë një llogari?",
    "auth.switch.login_link": "Hyrje",
    "auth.switch.no_account": "Nuk jeni ende klient?",
    "auth.error.select_country": "Zgjidhni shtetin tuaj",
    "kyc.title": "Verifikimi i identitetit (KYC)",
    "kyc.sub": "Ngarkoni dokumentet tuaja për të vërtetuar llogarinë. Të gjitha dokumentet janë të koduara dhe konfidenciale.",
    "kyc.id_type": "Lloji i dokumentit të identitetit *",
    "kyc.id_card": "Kartë identiteti",
    "kyc.passport": "Pasaportë",
    "kyc.driver_license": "Patentë shoferi",
    "kyc.recto_passport": "Ana e përparme e pasaportës *",
    "kyc.recto_id": "Ana e përparme e dokumentit *",
    "kyc.verso": "Ana e pasme e dokumentit *",
    "kyc.selfie": "Selfie me dokumentin e identitetit në dorë *",
    "kyc.address_proof": "Dëshmi adrese *",
    "kyc.hint_doc": "Imazh ose PDF, maks 5 MB",
    "kyc.hint_selfie": "Foto e qartë, fytyra dhe dokumenti të dukshëm",
    "kyc.hint_address": "Faturë, deklaratë, certifikatë… (maks 5 MB)",
    "kyc.upload": "Ngarko",
    "kyc.replace": "Zëvendëso",
    "pending.title": "Llogaria në pritje",
    "pending.body": "Kërkesa juaj është marrë. Një administrator do të verifikojë dosjen tuaj dhe do të aktivizojë llogarinë tuaj së shpejti.",
    "pending.back": "Kthehu te hyrja",
    "home.nav.features": "Veçoritë",
    "home.nav.security": "Siguria",
    "home.nav.invest": "Investimet",
    "home.nav.credits": "Kreditë",
    "home.nav.faq": "PBP",
    "home.nav.login": "Hyr",
    "home.hero.tagline": "Bankë online e gjeneratës tjetër",
    "home.hero.title1": "Menaxhoni paratë tuaja",
    "home.hero.title2": "me inteligjencë",
    "home.hero.sub": "Një platformë bankare e plotë — transferta, kursime, investime dhe kredi nga smartfoni juaj.",
    "home.hero.login": "Hyr",
    "home.hero.trust.secure": "100% i Sigurt",
    "home.hero.trust.fast": "Hapje e shpejtë",
    "home.hero.trust.encrypted": "Të dhëna të koduara",
    "home.hero.trust.support": "Shërbim 24/7",
    "home.mockup.balance": "Balancë e disponueshme",
    "home.mockup.currency": "EUR · Llogaria kryesore",
    "home.mockup.recharge": "Rimbush",
    "home.mockup.send": "Dërgo",
    "home.mockup.save": "Kurse",
    "home.mockup.salary": "Paga Mars",
    "home.mockup.rent": "Qiraja Prill",
    "home.mockup.investment": "Investim",
    "home.mockup.days2": "2 ditë më parë",
    "home.mockup.days3": "3 ditë më parë",
    "home.mockup.days5": "5 ditë më parë",
    "home.stats.accounts": "Llogari aktive",
    "home.stats.uptime": "Disponueshmëri",
    "home.stats.transfers": "Transferta",
    "home.stats.support": "Mbështetje",
    "home.features.label": "Veçoritë",
    "home.features.title": "Gjithçka që ju nevojitet",
    "home.features.sub": "Një platformë e plotë e projektuar për të thjeshtuar menaxhimin e financave tuaja çdo ditë.",
    "home.feature.security": "Siguri ushtarake",
    "home.feature.security_body": "Enkriptim AES-256, autentifikim me dy faktorë dhe monitorim i llogarisë 24/7.",
    "home.feature.cards": "Karta virtuale dhe fizike",
    "home.feature.cards_body": "Lëshoni karta në sekonda ose porositni një kartë të vërtetë fizike të dorëzuar te ju.",
    "home.feature.transfers": "Transferta ekspres",
    "home.feature.transfers_body": "Dërgoni dhe merrni para kudo në botë në sekonda.",
    "home.feature.analytics": "Analizë në kohë reale",
    "home.feature.analytics_body": "Panele inteligjente për të gjurmuar shpenzimet tuaja dhe për të parashikuar financat tuaja.",
    "home.feature.investments": "Investime të integruara",
    "home.feature.investments_body": "Aksesoni produkte investimi të zgjedhura direkt nga llogaria juaj.",
    "home.feature.support": "Mbështetje e dedikuar 24/7",
    "home.feature.support_body": "Një këshilltar bankar personal i disponueshëm në çdo kohë nëpërmjet mesazheve të sigurta.",
    "home.tpl.label": "Besimi",
    "home.tpl.title": "Na besojnë",
    "home.mockup.balance_label": "Balancë",
    "home.mockup.virement": "Transfertë",
    "home.mockup.salaire": "Pagë",
    "home.security.label": "Siguria",
    "home.security.title1": "Financat tuaja",
    "home.security.title2": "në siguri absolute",
    "home.security.sub": "Ne zbatojmë standardet më të rrepta të sigurisë bankare për të mbrojtur çdo cent.",
    "home.security.aes.title": "Enkriptim AES-256",
    "home.security.aes.body": "Të gjitha të dhënat e ndjeshme janë të koduara me të njëjtat algoritme të përdorura nga bankat e mëdha ndërkombëtare.",
    "home.security.auth.title": "Autentifikim i forcuar",
    "home.security.auth.body": "Verifikim i dyfishtë i identitetit në çdo hyrje dhe për çdo operacion të ndjeshëm në llogarinë tuaj.",
    "home.security.compliance.title": "Pajtueshmëri DSP2/RGPD",
    "home.security.compliance.body": "Ne respektojmë të gjitha rregulloret europiane në fushën e shërbimeve të pagesave dhe mbrojtjes së të dhënave.",
    "home.invest.label": "Investimet",
    "home.invest.title": "Bëni paratë tuaja të punojnë",
    "home.invest.sub": "6 kategori investimi të zgjedhura për potencialin dhe besueshmërinë e tyre.",
    "home.invest.yield": "Rendiment",
    "home.credits.label": "Kreditë",
    "home.credits.title": "Zgjidhje të adaptuara për çdo projekt",
    "home.credits.sub": "Norma konkurruese, përgjigje të shpejta dhe mbështetje e personalizuar për të gjitha projektet tuaja.",
    "home.credits.rate": "Norma",
    "home.faq.label": "PBP",
    "home.faq.title": "Pyetjet e shpeshta",
    "home.faq.q1": "Si të hap një llogari?",
    "home.faq.a1": "Regjistrimi zgjat më pak se 2 minuta. Thjesht plotësoni informacionet tuaja personale, zgjidhni një fjalëkalim dhe llogaria juaj do të aktivizohet pas vërtetimit nga ekipi ynë.",
    "home.faq.q2": "A janë të sigurta të dhënat e mia?",
    "home.faq.a2": "Po. Të gjitha të dhënat janë të koduara me AES-256 në pushim dhe TLS 1.3 në tranzit. Ne respektojmë standardet bankare ndërkombëtare dhe rregulloret RGPD/DSP2.",
    "home.faq.q3": "Çfarë llojesh investimesh ofroni?",
    "home.faq.a3": "Ofrojmë 6 kategori: Pasuri të paluajtshme, Crowdfunding, Energji të ripërtëritshme, Bursë, Participativë dhe Kripto. Çdo produkt zgjidhet për raportin e tij rrezik/rendiment.",
    "home.faq.q4": "Si të aplikoj për kredi?",
    "home.faq.a4": "Shkoni te seksioni Kredi i hapësirës suaj të klientit, zgjidhni llojin e huasë, shkruani shumën dhe kohëzgjatjen. Dosja juaj trajtohet brenda 24 orëve nga këshilltarët tanë.",
    "home.faq.q5": "A ka tarifa të fshehta?",
    "home.faq.a5": "Asnjë tarifë e fshehur. Të gjitha tarifat shfaqen qartë gjatë çdo operacioni. Kushtet janë të aksesueshme në hapësirën tuaj të klientit në çdo kohë.",
    "home.faq.q6": "Si të kontaktoj mbështetjen?",
    "home.faq.a6": "Përdorni mesazhet e integruara në hapësirën tuaj të klientit për të kontaktuar direkt këshilltarin tuaj bankar personal, të disponueshëm 24/7.",
    "home.cta.title": "Gati për të filluar?",
    "home.cta.sub": "Bashkohuni me mijëra përdorues që menaxhojnë financat e tyre me {bank}. Regjistrimi zgjat më pak se 2 minuta.",
    "home.footer.products": "Produktet",
    "home.footer.services": "Shërbimet",
    "home.footer.access": "Akses i shpejtë",
    "home.footer.accounts": "Llogaritë",
    "home.footer.transfers": "Transfertat",
    "home.footer.cards": "Kartat",
    "home.footer.savings": "Kursimet",
    "home.footer.investments": "Investimet",
    "home.footer.credits": "Kreditë",
    "home.footer.financing": "Financim",
    "home.footer.messages": "Mesazhet",
    "home.footer.login": "Hyrje",
    "home.footer.signup": "Hap një llogari",
    "home.footer.support": "Mbështetje 24/7",
    "home.footer.tagline": "Banka juaj online, e disponueshme kudo dhe në çdo kohë.",
    "home.footer.copyright": "© {year} {bank} — Të gjitha të drejtat e rezervuara",
    "home.footer.powered": "Mundësuar nga {bank}",
    "app.menu_label": "Menuja kryesore",
    "app.nav.dashboard": "Paneli",
    "app.nav.accounts": "Llogaritë",
    "app.nav.transactions": "Transaksionet",
    "app.nav.transfers": "Transfertat",
    "app.nav.cards": "Kartat",
    "app.nav.savings": "Kursimet",
    "app.nav.investments": "Investimet",
    "app.nav.credit": "Kredi",
    "app.nav.financing": "Financim",
    "app.nav.messages": "Mesazhet",
    "app.nav.card_tracking": "Gjurmimi i kartës",
    "app.nav.notifications": "Njoftimet",
    "app.nav.settings": "Cilësimet",
    "app.nav.profile": "Profili",
    "app.nav.mobile.home": "Kryefaqja",
    "app.nav.mobile.transfers": "Transfertat",
    "app.nav.mobile.cards": "Kartat",
    "app.nav.mobile.transactions": "Transaksionet",
    "app.nav.more.invest": "Investo",
    "app.user_role": "Klient",
    "app.logout": "Dilni",
    "app.search": "Kërko…",
    "app.more_title": "Më shumë veçori",
    "app.more_btn": "Më shumë",
    "app.loading": "Duke ngarkuar…",
    "app.error_back": "Kthehu te hyrja",
    "app.see_more_notifs": "Shiko {count} njoftime të tjera →",
    "page.accounts": "Llogaritë e mia",
    "page.transactions": "Transaksionet",
    "page.transfers": "Transfertë e re",
    "page.deposit": "Depozitë",
    "page.cards": "Kartat e mia",
    "page.savings": "Kursimet",
    "page.investments": "Investimet",
    "page.credits": "Kredi",
    "page.financing": "Financim projektesh",
    "page.messages": "Mesazhet",
    "page.settings": "Cilësimet",
    "page.notifications": "Njoftime të rëndësishme",
    "page.profile": "Profili im",
    "dash.greeting": "Mirëdita, {name}",
    "dash.overview": "Pasqyrë e financave tuaja — {date}",
    "dash.total_balance": "Balancë totale",
    "dash.this_month": "+{amount} këtë muaj",
    "dash.view_iban": "Shiko IBAN-in tim",
    "dash.my_accounts": "Llogaritë e mia",
    "dash.primary_account": "Llogaria kryesore",
    "dash.savings_account": "Llogaria e kursimeve",
    "dash.remunerated": "Me interes",
    "dash.details": "Detajet",
    "dash.transfer_btn": "Transfertë",
    "dash.quick_actions": "Veprime të shpejta",
    "dash.quick.deposit": "Depozitë",
    "deposit.title": "Informacion mbi depozitën",
    "dash.quick.transfer": "Transfertë",
    "dash.quick.history": "Historia",
    "dash.quick.cards": "Kartat e mia",
    "dash.quick.profile": "Profili",
    "dash.monthly_budget": "Buxheti mujor",
    "dash.income": "Të ardhura",
    "dash.expenses": "Shpenzime",
    "dash.spending_cat": "Shpenzime sipas kategorisë",
    "dash.no_spending": "Asnjë shpenzim i regjistruar.",
    "dash.recent_tx": "Transaksionet e fundit",
    "dash.see_all": "Shiko të gjitha",
    "dash.no_tx": "Asnjë operacion.",
    "dash.main_card": "Karta ime kryesore",
    "dash.manage": "Menaxho",
    "dash.block": "Blloko",
    "dash.pin": "Kodi PIN",
    "dash.security": "Siguria",
    "dash.more": "Më shumë",
    "dash.no_card": "Asnjë kartë e lëshuar",
    "dash.monthly_activity": "Aktiviteti mujor",
    "dash.operations": "Operacionet",
    "dash.holder": "Mbajtësi",
    "dash.expires": "Skad.",
    "dash.visa_label": "VISA",
    "dash.balance": "Balancë e disponueshme",
    "tx.all": "Të gjitha",
    "tx.income": "Të ardhura",
    "tx.expenses": "Shpenzime",
    "tx.today": "Sot",
    "tx.yesterday": "Dje",
    "tx.none": "Asnjë transaksion.",
    "transfer.title": "Transfertë e shpejtë",
    "transfer.recipient": "Marrësi",
    "transfer.recipient_ph": "Emri, IBAN, numri i telefonit…",
    "transfer.amount": "Shuma",
    "transfer.label": "Etiketë",
    "transfer.reason": "Arsyeja (opsionale)",
    "transfer.reason_ph": "Shembull: qiraja, rimbursim…",
    "transfer.execution": "Ekzekutim",
    "transfer.immediate": "Menjëherë",
    "transfer.delayed": "I shtyrë",
    "transfer.balance": "Balancë e disponueshme:",
    "transfer.continue": "Vazhdo",
    "transfer.confirm": "Konfirmo",
    "transfer.cancel": "Anulo",
    "transfer.code": "Kodi i vërtetimit",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Shkruani kodin e marrë për të vërtetuar transfertën",
    "transfer.pending.title": "Transfertë në pritje vërtetimi",
    "transfer.pending.amount": "Shuma:",
    "transfer.pending.to": "Tek:",
    "transfer.pending.hint": "Kodi juaj i validimit do të dërgohet me e-mail ose përmes ziles së njoftimeve. Kodi është i përbërë nga 6 shifra. Nëse nuk e merrni, ju lutemi kontaktoni mbështetjen.",
    "transfer.pending.validate": "Vërteto transfertën",
    "transfer.saved": "Transferta u ruajt. Kërkojini administratorit tuaj kodin e vërtetimit.",
    "transfer.done": "Transferta u krye",
    "transfer.validated": "Transferta u vërtetua!",
    "transfer.progress.processing": "Duke përpunuar transfertën…",
    "transfer.progress.completed": "Transferta u krye!",
    "transfer.progress.default_message": "Transferta juaj po përpunohet. Ju lutemi prisni.",
    "cards.none": "Asnjë kartë e lëshuar.\nKontaktoni administratorin tuaj.",
    "cards.holder": "Mbajtësi",
    "cards.expires": "Skad.",
    "cards.block": "Blloko",
    "cards.pin": "Kodi PIN",
    "cards.more": "Më shumë",
    "cards.limits": "Limitet e kartës",
    "cards.payment": "Pagesa",
    "cards.withdrawal": "Tërheqje",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Bllokuar",
    "cards.status_active": "Aktive",
    "cards.toast.blocked": "Karta e bllokuar",
    "cards.toast.unblocked": "Karta e zhbllokuar",
    "cards.toast.physical_ordered": "Karta fizike e porositur! Dorëzimi brenda 5–10 ditëve të punës.",
    "cards.toast.created": "Karta u gjenerua me sukses!",
    "cards.none_sub": "Asnjë kartë e lëshuar. Gjeneroni kartën tuaj virtuale tani.",
    "cards.generate_btn": "Gjenero kartën time",
    "cards.create.title": "Gjenero kartën time virtuale",
    "cards.create.network": "Rrjeti i pagesave",
    "cards.create.color": "Ngjyra e kartës",
    "cards.create.preview_name": "EMRI JUAJ",
    "cards.create.preview_expiry": "MM/VV",
    "cards.create.loading": "Gjenerimi…",
    "cards.create.submit": "Gjenero kartën",
    "cards.btn.unblock": "Zhblloko",
    "cards.btn.block": "Blloko",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fizike",
    "cards.btn.physical_done": "Porositur",
    "cards.limits_title": "Limitet",
    "cards.reveal.title": "Informacioni i kartës",
    "cards.reveal.number": "Numri i plotë",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Skadimi",
    "cards.reveal.warning": "Mos i ndani kurrë këto informacione. Mbyllni këtë dritare pas përdorimit.",
    "cards.pin.title": "Kodi PIN",
    "cards.pin.message": "Ky kod PIN përdoret për të gjitha pagesat tuaja online dhe tërheqjet. Mos ia tregoni kurrë askujt.",
    "cards.physical.title": "Porosit kartën fizike",
    "cards.physical.fee_label": "Tarifa e dorëzimit",
    "cards.physical.balance_label": "Bilanci juaj",
    "cards.physical.insufficient": "Bilanc i pamjaftueshëm. Ju lutemi rimbushni llogarinë tuaj para se të porosisni.",
    "cards.physical.info": "Tarifa do të debitohet nga llogaria juaj. Dorëzimi brenda 5–10 ditëve të punës.",
    "cards.physical.order_btn": "Porosit ({fee})",
    "cards.physical.ordering": "Duke porositur…",
    "cards.physical.order_cta": "Porosit një kartë fizike",
    "cards.physical.choose_type": "Zgjidhni kartën tuaj",
    "cards.physical.features": "Avantazhet",
    "cards.buy_virtual_cta": "Bli një kartë virtuale",
    "notif.none": "Asnjë njoftim për momentin.",
    "notif.contact_support": "Kontaktoni mbështetjen",
    "profile.change_photo": "Ndrysho foton",
    "profile.remove_photo": "Hiq foton",
    "profile.email": "Email",
    "profile.phone": "Telefon",
    "profile.country": "Shteti",
    "profile.address": "Adresa",
    "profile.notifs_title": "Njoftimet ({count})",
    "profile.no_notifs": "Asnjë njoftim.",
    "profile.logout": "Dilni",
    "profile.photo_updated": "Foto e profilit u përditësua",
    "profile.photo_removed": "Foto u hoq",
    "profile.photo_too_large": "Imazhi është shumë i madh (maks 1 MB)",
    "profile.powered": "Mundësuar nga {bank}",
    "iban.title": "IBAN-i im",
    "iban.loading": "Duke ngarkuar…",
    "iban.bic": "Kodi BIC / SWIFT",
    "iban.share_info": "Ndani këto të dhëna për të marrë një transfertë në llogarinë tuaj.",
    "iban.copy": "Kopjo",
    "iban.copied": "U kopjua në clipboard",
    "iban.copy_fail": "Kopjimi i pamundur",
    "iban.close": "Mbyll",
    "accounts.all": "Të gjitha llogaritë tuaja të hapura në {bank}.",
    "accounts.primary": "Llogaria kryesore",
    "accounts.savings": "Llogaria e kursimeve",
    "accounts.primary_tag": "Kryesore",
    "accounts.secondary_tag": "Dytësore",
    "accounts.objective": "Objektiv: {amount} · {pct}%",
    "accounts.copy": "Kopjo",
    "accounts.loading": "Duke ngarkuar…",
    "accounts.iban_copied": "IBAN u kopjua",
    "accounts.copy_fail": "Kopjimi i pamundur",
    "savings.total": "Totali i kursyer",
    "savings.target_total": "Objektiv total",
    "savings.progress": "Progresi",
    "savings.active": "Objektiva aktive",
    "savings.new_goal": "Objektiv i ri",
    "savings.none": "Asnjë objektiv kursimesh për momentin.",
    "savings.none_sub": "Krijoni objektivin tuaj të parë për të filluar.",
    "savings.saved": "Kursyer:",
    "savings.goal": "Objektiv:",
    "savings.deadline": "Afati: {date}",
    "savings.deposit": "Depozitë",
    "savings.withdraw": "Tërhiq",
    "savings.loading": "Duke ngarkuar…",
    "savings.form.title": "Objektiv i ri kursimesh",
    "savings.form.cancel": "Anulo",
    "savings.form.create": "Krijo",
    "savings.form.name": "Emri i objektivit",
    "savings.form.name_ph": "Udhëtim Japoni, Makinë, …",
    "savings.form.target": "Shuma e synuar",
    "savings.form.initial": "Depozita fillestare",
    "savings.form.deadline": "Data e afatit (opsionale)",
    "savings.form.color": "Ngjyra",
    "savings.deposit_title": "Depozitë te \"{name}\"",
    "savings.deposit_amount": "Shuma për depozitë",
    "savings.balance_hint": "Balancë kryesore: {amount}",
    "savings.withdraw_prompt": "Sa të tërhiqet nga \"{name}\"?",
    "savings.close_confirm": "Mbyll objektivin \"{name}\"? Kursimet do të kthehen në llogarinë tuaj kryesore.",
    "savings.toast.created": "Objektivi u krijua",
    "savings.toast.deposited": "Depozita u krye",
    "savings.toast.withdrawn": "Tërheqja u krye",
    "savings.toast.closed": "Objektivi u mbyll",
    "invest.total": "Totali i investuar",
    "invest.profits": "Fitimet kumulative",
    "invest.count": "Investimet",
    "invest.products": "Produkte të disponueshme",
    "invest.invest_btn": "Investo",
    "invest.my": "Investimet e mia",
    "invest.pending": "Në pritje",
    "invest.validated": "Vërtetuar",
    "invest.refused": "Refuzuar",
    "invest.withdraw.btn": "Tërhiq",
    "invest.withdraw.title": "Tërhiq investimin tim",
    "invest.withdraw.intro": "Po tërhiqni investimin tuaj „{product}\". Kapitali fillestar dhe fitimi i grumbulluar do të kreditohen në bilancin tuaj kryesor.",
    "invest.withdraw.principal": "Kapitali fillestar",
    "invest.withdraw.profit": "Fitimi",
    "invest.withdraw.total": "Totali për të marrë",
    "invest.withdraw.confirm": "Konfirmo tërheqjen",
    "invest.withdraw.warning": "Ky veprim është i pakthyeshëm. Investimi do të mbyllet.",
    "invest.withdraw.success": "Investimi i tërhequr — {amount} të kredituar në bilancin tuaj.",
    "invest.withdrawn": "Tërhequr",
    "invest.cta_sub": "Zgjidhni llojin e investimit që ju përshtatet. Një këshilltar do t'ju shoqërojë që nga kërkesa juaj.",
    "invest.form.amount": "Shuma për investim",
    "invest.form.balance": "Balancë e disponueshme: {amount}",
    "invest.form.note": "Shënim për këshilltarin tuaj (opsionale)",
    "invest.form.note_ph": "Specifikoni pritshmëritë tuaja…",
    "invest.form.advisor_info": "Një bisedë do të hapet automatikisht me këshilltarin tuaj.",
    "invest.form.cancel": "Anulo",
    "invest.form.submit": "Dërgo kërkesën",
    "invest.toast.requested": "Kërkesa u regjistrua — një bisedë u hap me këshilltarin tuaj.",
    "invest.p1.name": "Pasuri të paluajtshme",
    "invest.p1.desc": "SCPI, letër-tulla, crowdfunding i pasurive të paluajtshme",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Financim participativ i projekteve të zgjedhura",
    "invest.p3.name": "Energji të ripërtëritshme",
    "invest.p3.desc": "Diellore, me erë dhe projekte të gjelbra",
    "invest.p4.name": "Bursë",
    "invest.p4.desc": "Aksione, ETF dhe fonde të diversifikuara",
    "invest.p5.name": "Participativ",
    "invest.p5.desc": "Hua për NMV dhe sipërmarrës lokalë",
    "invest.p6.name": "Kripto",
    "invest.p6.desc": "Bitcoin, Ethereum dhe portofol shumë-valutor",
    "msg.online": "Online",
    "msg.loading": "Duke ngarkuar…",
    "msg.none": "Asnjë mesazh. Filloni bisedën me këshilltarin tuaj.",
    "msg.placeholder": "Shkruani mesazhin tuaj…",
    "msg.attach.image": "Imazh",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Skedar / Dokument",
    "msg.pdf_doc": "Dokument PDF",
    "msg.file": "Skedar",
    "msg.too_large": "Skedari është shumë i madh (maks {size}).",
    "msg.withdraw": "Tërhiq",
    "credits.cta_sub": "Bëni kërkesën tuaj direkt online — përgjigje brenda 24 orëve.",
    "credits.learn_more": "Mëso më shumë",
    "credits.request": "Kërko",
    "credits.history": "Kërkesat e mia",
    "credits.status.pending": "Në pritje",
    "credits.status.accepted": "Pranuar",
    "credits.status.refused": "Refuzuar",
    "credits.chars": "Karakteristikat",
    "credits.amount_range": "Shuma: {min}€ – {max}€",
    "credits.rate_range": "Norma e interesit: {min}% – {max}% në vit",
    "credits.duration_range": "Kohëzgjatja: {min} deri {max} muaj",
    "credits.deposit_min": "Depozita fillestare: {amount}€ minimum",
    "credits.garantie": "Garancia: {garantie}",
    "credits.eligibility": "Kushtet e pranueshmërisë",
    "credits.elig.income": "Të dëshmohen të ardhura të rregullta dhe të qëndrueshme",
    "credits.elig.deposit": "Të kryhet depozita fillestare prej {amount}€",
    "credits.elig.garantie": "Të paraqitet garancia: {garantie}",
    "credits.form.title": "Kërkesë — {name}",
    "credits.form.cancel": "Anulo",
    "credits.form.submit": "Dërgo kërkesën",
    "credits.form.amount": "Shuma e dëshiruar (€)",
    "credits.form.duration": "Kohëzgjatja (muaj)",
    "credits.form.reason": "Arsyeja (opsionale)",
    "credits.form.reason_ph": "Specifikoni projektin tuaj…",
    "credits.form.close": "Mbyll",
    "credits.toast.submitted": "Kërkesa për kredi u dërgua — do të merrni përgjigje brenda 24 orëve.",
    "credits.p1.name": "Kredi Personale",
    "credits.p1.desc": "Për të financuar nevojat e përditshme ose projektet personale.",
    "credits.p2.name": "Kredi Hipotekare",
    "credits.p2.desc": "Për të financuar blerjen ose ndërtimin e një prone.",
    "credits.p3.name": "Kredi Auto / Moto",
    "credits.p3.desc": "Blini një mjet transporti pa asnjë shqetësim.",
    "credits.p4.name": "Kredi Studentore",
    "credits.p4.desc": "Ndihmë financiare për studimet e larta.",
    "credits.p5.name": "Kredi Profesionale",
    "credits.p5.desc": "Për të mbështetur rritjen e një aktiviteti profesional.",
    "credits.p6.name": "Kredi Ekspres",
    "credits.p6.desc": "Zgjidhje e shpejtë për nevojat urgjente.",
    "financing.reimbursable": "Financim i rimbursueshëm",
    "financing.reimbursable_desc": "Mbështetje financiare me kushte fikse për të nisur ose zhvilluar një aktivitet.",
    "financing.non_reimbursable": "Financim i pakthyeshëm",
    "financing.non_reimbursable_desc": "Për projektet me ndikim shoqëror, arsimor ose humanitar (OJQ, shoqata).",
    "financing.cta_sub": "Zgjidhje të adaptuara për realizimin e ambicieve tuaja.",
    "financing.request": "Kërko financim",
    "financing.history": "Kërkesat e mia",
    "financing.reimbursable_label": "I rimbursueshëm",
    "financing.non_reimbursable_label": "I pakthyeshëm",
    "financing.status.pending": "Në pritje",
    "financing.status.granted": "Dhënë",
    "financing.status.refused": "Refuzuar",
    "financing.form.title": "Kërkesë — {name}",
    "financing.form.cancel": "Anulo",
    "financing.form.submit": "Dërgo kërkesën",
    "financing.form.project": "Emri i projektit",
    "financing.form.project_ph": "Projekti im",
    "financing.form.amount": "Shuma e kërkuar (€)",
    "financing.form.sector": "Sektori",
    "financing.form.sector_ph": "Teknologji, bujqësi, …",
    "financing.form.description": "Përshkrimi i projektit",
    "financing.form.description_ph": "Prezantoni projektin tuaj, objektivat e tij dhe ndikimin e pritur…",
    "settings.security": "Siguria",
    "settings.current_pw": "Fjalëkalimi aktual",
    "settings.new_pw": "Fjalëkalimi i ri",
    "settings.confirm_pw": "Konfirmo",
    "settings.submit": "Ndrysho fjalëkalimin",
    "settings.no_match": "Fjalëkalimet nuk përputhen",
    "settings.changed": "Fjalëkalimi u ndryshua",
    "common.cancel": "Anulo",
    "common.confirm": "Konfirmo",
    "common.close": "Mbyll",
    "common.save": "Ruaj",
    "common.loading": "Duke ngarkuar…",
    "common.send": "Dërgo",
    "common.total": "Totali",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Pag. mujore e vlerë.",
    "credits.rate_annual": "/ vit",
    "credits.rate_used": "Norma e vlerës.:",
    "credits.simulation": "Simulim i pagesës mujore",
    "credits.total_cost": "Kostoja totale e vlerë.",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Mirë se vini",
    "common.delete": "Fshi",
    "cards.delete.title": "Fshi kartën",
    "cards.delete.confirm_msg": "Ky veprim është i pakthyeshëm. Karta do të fshihet përgjithmonë.",
    "cards.delete.physical_tooltip": "Karta fizike e porositur — fshirja e pamundur",
    "cards.order.confirmed": "Porosia u konfirmua!",
    "cards.order.confirmed_body": "Karta juaj fizike është duke u përgatitur. Përdorni këtë numër për të gjurmuar dorëzimin.",
    "cards.order.tracking_no": "Nr. gjurmimi",
    "cards.fee_deducted": "{amount} do të zbritet nga bilanci juaj",
    "cards.insufficient_short": "Bilanc i pamjaftueshëm ({available} i disponueshëm)",
    "transfer.section.beneficiary": "Përfituesi",
    "transfer.section.details": "Transferim",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bankë",
    "transfer.field.city": "Qytet",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bankë:",
    "transfer.progress.paused": "Pret validimin",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "E ndryshueshme",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Karta u fshi",
    "cards.feat.gold_exclusive": "Kartë e arta ekskluzive",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Mbrojtja e blerësit",
    "cards.feat.support_standard": "Mbështetje standarde",
    "cards.feat.plastic_premium": "Kartë plastike premium",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Sigurim udhëtimi standard",
    "cards.feat.support_priority": "Mbështetje me prioritet",
    "cards.feat.metal_brushed": "Kartë me metal të fërkuar",
    "cards.feat.cashback_2_unlimited": "Cashback 2% i pakufizuar",
    "cards.feat.travel_premium": "Sigurim udhëtimi premium",
    "cards.feat.vip_lounge": "Akses në sallone VIP",
    "cards.feat.support_247": "Asistencë 24/7",
    "auth.demo.title": "Hyrja e administratorit",
    "auth.demo.subtitle": "Vendos kredencialet e adminit për të parë demon",
    "credits.garantie.none": "Asnjë",
    "credits.garantie.mortgage": "Hipotekë mbi pronën e financuar",
    "credits.garantie.vehicle": "Automjet i financuar",
    "credits.garantie.parental": "Prindërore ose bankare",
    "credits.garantie.professional": "Aktive profesionale ose garanci personale",
    "cards.reveal.pin_prompt": "Vendosni kodin tuaj PIN 4-shifror për të shfaqur informacionet e kartës.",
    "cards.reveal.show": "Shfaq",
    "transfer.field.iban": "IBAN",
  
  },
  cnr: {
    "notif.transfer_code.title": "Kod za potvrdu prijenosa",
    "notif.transfer_code.body": "Kopirajte ovaj kod za potvrdu prijenosa.",
    "notif.transfer_code.expires_in": "Nestaje za {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mjeseci",
    "credits.n_months": "{n} mjeseci",
    "msg.read": "Pročitano",
    "msg.sent": "Poslato",
    "msg.attach_aria": "Priloži datoteku",
    "login.device.unknown": "Nepoznati pregledač",
    "login.device.mobile": "Mobilni",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Računar",
    "login.recent": "Nedavno",
    "err.invalid_credentials": "Nevažeći podaci za prijavu",
    "err.bad_pin": "Pogrešan PIN",
    "err.account_blocked": "Nalog blokiran",
    "err.bad_current_pw": "Trenutna lozinka je netačna",
    "err.insufficient_funds": "Nedovoljno sredstava, dopunite svoj nalog.",
    "err.amount_above_savings": "Iznos premašuje dostupnu uštedu",
    "err.must_be_positive": "Vrijednost mora biti pozitivna",
    "err.invalid_format": "Nevažeći format",
    "err.email_taken": "Email se već koristi",
    "err.card_not_found": "Kartica nije pronađena",
    "err.physical_card_already_ordered": "Fizička kartica je već naručena",
    "err.beneficiary_not_found": "Primalac nije pronađen",
    "err.invalid_code": "Nevažeći kod",
    "err.no_transfer_code": "Nema koda za ovaj transfer",
    "err.link_expired": "Link nevažeći ili istekao",
    "err.thread_not_found": "Razgovor nije pronađen",
    "err.empty_message": "Prazna poruka",
    "err.file_too_large": "Datoteka je prevelika (maks 20 MB)",
    "err.forbidden": "Pristup odbijen",
    "err.bank_not_found": "Banka nije pronađena",
    "err.signups_closed": "Registracije su zatvorene za ovu banku.",
    "err.access_suspended": "Pristup privremeno obustavljen. Molimo kontaktirajte vašu banku.",
    "err.notif_not_closable": "Ovo obavještenje se ne može zatvoriti.",
    "auth.signup.tagline": "Pridružite nam se danas",
    "auth.login.tagline": "Dobrodošli!",
    "auth.signup.tagline_sub": "Otvorite račun za 2 minuta i pristupite svim našim premium bankarskim uslugama.",
    "auth.login.tagline_sub": "Pristupite svom ličnom prostoru i upravljajte finansijama bezbjednosno.",
    "auth.back": "Nazad na početnu stranicu",
    "auth.bullet.security": "Bankarska bezbjednost vojnog nivoa",
    "auth.bullet.transfers": "Trenutni transferi 24/7",
    "auth.bullet.investments": "Integrisane investicije i štednja",
    "auth.bullet.advisor": "Lični savjetnik dostupan 7 dana/7",
    "auth.footer": "Pokrenuto od {bank}",
    "auth.signup.title": "Kreiraj nalog",
    "auth.login.title": "Prijava",
    "auth.field.lastName": "Prezime",
    "auth.field.firstName": "Ime(na)",
    "auth.field.country": "Zemlja",
    "auth.field.country.placeholder": "— Izaberite svoju zemlju —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Adresa",
    "auth.field.address.placeholder": "ul. Slobode 123, Podgorica",
    "auth.field.email": "E-pošta",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Lozinka",
    "auth.submit.signup": "Kreiraj moj nalog",
    "auth.submit.login": "Prijavi se",
    "auth.login.badge": "Prijavite se na svoj račun",
    "auth.card.tagline": "Vaš korisnički prostor",
    "auth.submit.loading": "Učitavanje…",
    "auth.switch.have_account": "Već imate nalog?",
    "auth.switch.login_link": "Prijava",
    "auth.switch.no_account": "Još niste klijent?",
    "auth.error.select_country": "Izaberite svoju zemlju",
    "kyc.title": "Verifikacija identiteta (KYC)",
    "kyc.sub": "Otpremite vaše dokumente kako biste potvrdili nalog. Svi dokumenti su šifrovani i povjerljivi.",
    "kyc.id_type": "Vrsta dokumenta za identitet *",
    "kyc.id_card": "Lična karta",
    "kyc.passport": "Pasoš",
    "kyc.driver_license": "Vozačka dozvola",
    "kyc.recto_passport": "Prednja strana pasoša *",
    "kyc.recto_id": "Prednja strana dokumenta *",
    "kyc.verso": "Zadnja strana dokumenta *",
    "kyc.selfie": "Selfie s dokumentom za identitet u ruci *",
    "kyc.address_proof": "Dokaz o adresi *",
    "kyc.hint_doc": "Slika ili PDF, maks 5 MB",
    "kyc.hint_selfie": "Jasna fotografija, lice i dokument vidljivi",
    "kyc.hint_address": "Račun, izvod, potvrda… (maks 5 MB)",
    "kyc.upload": "Otpremi",
    "kyc.replace": "Zamijeni",
    "pending.title": "Nalog na čekanju",
    "pending.body": "Vaš zahtjev je primljen. Administrator će provjeriti vašu dokumentaciju i aktivirati vaš nalog uskoro.",
    "pending.back": "Nazad na prijavu",
    "home.nav.features": "Funkcionalnosti",
    "home.nav.security": "Bezbjednost",
    "home.nav.invest": "Investicije",
    "home.nav.credits": "Krediti",
    "home.nav.faq": "ČPP",
    "home.nav.login": "Prijavi se",
    "home.hero.tagline": "Online bankarstvo sljedeće generacije",
    "home.hero.title1": "Upravljajte svojim novcem",
    "home.hero.title2": "s inteligencijom",
    "home.hero.sub": "Kompletna bankarska platforma — transferi, štednja, investicije i krediti s vašeg pametnog telefona.",
    "home.hero.login": "Prijavi se",
    "home.hero.trust.secure": "100% Bezbijedno",
    "home.hero.trust.fast": "Brzo otvaranje",
    "home.hero.trust.encrypted": "Šifrovani podaci",
    "home.hero.trust.support": "Usluga 24/7",
    "home.mockup.balance": "Raspoloživo stanje",
    "home.mockup.currency": "EUR · Glavni račun",
    "home.mockup.recharge": "Napuni",
    "home.mockup.send": "Pošalji",
    "home.mockup.save": "Štedi",
    "home.mockup.salary": "Plata mart",
    "home.mockup.rent": "Kirija april",
    "home.mockup.investment": "Investicija",
    "home.mockup.days2": "prije 2 dana",
    "home.mockup.days3": "prije 3 dana",
    "home.mockup.days5": "prije 5 dana",
    "home.stats.accounts": "Aktivni računi",
    "home.stats.uptime": "Dostupnost",
    "home.stats.transfers": "Transferi",
    "home.stats.support": "Podrška",
    "home.features.label": "Funkcionalnosti",
    "home.features.title": "Sve što vam je potrebno",
    "home.features.sub": "Kompletna platforma osmišljena za pojednostavljivanje upravljanja vašim finansijama svakodnevno.",
    "home.feature.security": "Vojna bezbjednost",
    "home.feature.security_body": "Šifrovanje AES-256, dvofaktorska autentikacija i nadzor naloga 24/7.",
    "home.feature.cards": "Virtuelne i fizičke kartice",
    "home.feature.cards_body": "Izdajte kartice za sekunde ili naručite pravu fizičku karticu dostavljenu do vas.",
    "home.feature.transfers": "Ekspresni transferi",
    "home.feature.transfers_body": "Šaljite i primajte novac svugdje u svijetu za sekunde.",
    "home.feature.analytics": "Analiza u realnom vremenu",
    "home.feature.analytics_body": "Pametne kontrolne table za praćenje vaših troškova i predviđanje vaših finansija.",
    "home.feature.investments": "Integrisane investicije",
    "home.feature.investments_body": "Pristupite odabranim investicionim proizvodima direktno s vašeg naloga.",
    "home.feature.support": "Posvećena podrška 24/7",
    "home.feature.support_body": "Lični bankarski savjetnik dostupan u svako doba putem sigurnih poruka.",
    "home.tpl.label": "Trust",
    "home.tpl.title": "They trust us",
    "home.mockup.balance_label": "Stanje",
    "home.mockup.virement": "Transfer",
    "home.mockup.salaire": "Plata",
    "home.security.label": "Bezbjednost",
    "home.security.title1": "Vaše finansije",
    "home.security.title2": "u apsolutnoj bezbjednosti",
    "home.security.sub": "Primjenjujemo najstrože standarde bankarske bezbjednosti za zaštitu svakog centa.",
    "home.security.aes.title": "Šifrovanje AES-256",
    "home.security.aes.body": "Svi osjetljivi podaci su šifrovani istim algoritmima koje koriste velike međunarodne banke.",
    "home.security.auth.title": "Ojačana autentikacija",
    "home.security.auth.body": "Dvostruka verifikacija identiteta pri svakoj prijavi i za svaku osjetljivu operaciju na vašem nalogu.",
    "home.security.compliance.title": "Usklađenost DSP2/RGPD",
    "home.security.compliance.body": "Poštujemo sve evropske propise u oblasti platnih usluga i zaštite podataka.",
    "home.invest.label": "Investicije",
    "home.invest.title": "Neka vaš novac radi za vas",
    "home.invest.sub": "6 investicionih kategorija odabranih zbog potencijala i pouzdanosti.",
    "home.invest.yield": "Prinos",
    "home.credits.label": "Krediti",
    "home.credits.title": "Rješenja prilagođena svakom projektu",
    "home.credits.sub": "Konkurentne stope, brzi odgovori i personalizovana podrška za sve vaše projekte.",
    "home.credits.rate": "Stopa",
    "home.faq.label": "ČPP",
    "home.faq.title": "Često postavljana pitanja",
    "home.faq.q1": "Kako otvoriti nalog?",
    "home.faq.a1": "Registracija traje manje od 2 minuta. Samo unesite lične podatke, odaberite lozinku i vaš nalog će biti aktiviran nakon potvrde od strane našeg tima.",
    "home.faq.q2": "Da li su moji podaci bezbjedni?",
    "home.faq.a2": "Da. Svi podaci su šifrovani sa AES-256 u mirovanju i TLS 1.3 u tranzitu. Poštujemo međunarodne bankarske standarde i propise RGPD/DSP2.",
    "home.faq.q3": "Koje vrste investicija nudite?",
    "home.faq.a3": "Nudimo 6 kategorija: Nekretnine, Crowdfunding, Obnovljivi izvori energije, Berza, Participativni i Kripto. Svaki proizvod je odabran zbog odnosa rizika i prinosa.",
    "home.faq.q4": "Kako zatražiti kredit?",
    "home.faq.a4": "Idite u odjeljak Krediti u svom klijentskom prostoru, odaberite vrstu zajma, unesite iznos i trajanje. Vaš zahtjev se obrađuje u roku od 24 sata od strane naših savjetnika.",
    "home.faq.q5": "Da li postoje skrivene naknade?",
    "home.faq.a5": "Nema skrivenih naknada. Sve tarife se jasno prikazuju pri svakoj operaciji. Uslovi su dostupni u vašem klijentskom prostoru u svako doba.",
    "home.faq.q6": "Kako kontaktirati podršku?",
    "home.faq.a6": "Koristite integrisane poruke u svom klijentskom prostoru za direktno kontaktiranje vašeg ličnog bankarskog savjetnika, dostupnog 24/7.",
    "home.cta.title": "Spremni za početak?",
    "home.cta.sub": "Pridružite se hiljadama korisnika koji upravljaju finansijama s {bank}. Registracija traje manje od 2 minuta.",
    "home.footer.products": "Proizvodi",
    "home.footer.services": "Usluge",
    "home.footer.access": "Brzi pristup",
    "home.footer.accounts": "Računi",
    "home.footer.transfers": "Transferi",
    "home.footer.cards": "Kartice",
    "home.footer.savings": "Štednja",
    "home.footer.investments": "Investicije",
    "home.footer.credits": "Krediti",
    "home.footer.financing": "Finansiranje",
    "home.footer.messages": "Poruke",
    "home.footer.login": "Prijava",
    "home.footer.signup": "Otvori nalog",
    "home.footer.support": "Podrška 24/7",
    "home.footer.tagline": "Vaša online banka, dostupna svugdje i u svako doba.",
    "home.footer.copyright": "© {year} {bank} — Sva prava zadržana",
    "home.footer.powered": "Pokrenuto od {bank}",
    "app.menu_label": "Glavni meni",
    "app.nav.dashboard": "Kontrolna tabla",
    "app.nav.accounts": "Računi",
    "app.nav.transactions": "Transakcije",
    "app.nav.transfers": "Transferi",
    "app.nav.cards": "Kartice",
    "app.nav.savings": "Štednja",
    "app.nav.investments": "Investicije",
    "app.nav.credit": "Kredit",
    "app.nav.financing": "Finansiranje",
    "app.nav.messages": "Poruke",
    "app.nav.card_tracking": "Praćenje kartice",
    "app.nav.notifications": "Obavještenja",
    "app.nav.settings": "Podešavanja",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Početna",
    "app.nav.mobile.transfers": "Transferi",
    "app.nav.mobile.cards": "Kartice",
    "app.nav.mobile.transactions": "Transakcije",
    "app.nav.more.invest": "Investiraj",
    "app.user_role": "Klijent",
    "app.logout": "Odjava",
    "app.search": "Pretraži…",
    "app.more_title": "Više funkcija",
    "app.more_btn": "Više",
    "app.loading": "Učitavanje…",
    "app.error_back": "Nazad na prijavu",
    "app.see_more_notifs": "Vidi {count} dodatnih obavještenja →",
    "page.accounts": "Moji računi",
    "page.transactions": "Transakcije",
    "page.transfers": "Novi transfer",
    "page.deposit": "Depozit",
    "page.cards": "Moje kartice",
    "page.savings": "Štednja",
    "page.investments": "Investicije",
    "page.credits": "Kredit",
    "page.financing": "Finansiranje projekata",
    "page.messages": "Poruke",
    "page.settings": "Podešavanja",
    "page.notifications": "Važna obavještenja",
    "page.profile": "Moj profil",
    "dash.greeting": "Zdravo, {name}",
    "dash.overview": "Pregled vaših finansija — {date}",
    "dash.total_balance": "Ukupno stanje",
    "dash.this_month": "+{amount} ovog mjeseca",
    "dash.view_iban": "Pogledaj moj IBAN",
    "dash.my_accounts": "Moji računi",
    "dash.primary_account": "Glavni račun",
    "dash.savings_account": "Štedni račun",
    "dash.remunerated": "Sa kamatom",
    "dash.details": "Detalji",
    "dash.transfer_btn": "Transfer",
    "dash.quick_actions": "Brze akcije",
    "dash.quick.deposit": "Depozit",
    "deposit.title": "Informacije o depozitu",
    "dash.quick.transfer": "Transfer",
    "dash.quick.history": "Istorija",
    "dash.quick.cards": "Moje kartice",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Mjesečni budžet",
    "dash.income": "Prihodi",
    "dash.expenses": "Rashodi",
    "dash.spending_cat": "Troškovi po kategoriji",
    "dash.no_spending": "Nema evidentiranih troškova.",
    "dash.recent_tx": "Nedavne transakcije",
    "dash.see_all": "Vidi sve",
    "dash.no_tx": "Nema operacija.",
    "dash.main_card": "Moja glavna kartica",
    "dash.manage": "Upravljaj",
    "dash.block": "Blokiraj",
    "dash.pin": "PIN kod",
    "dash.security": "Bezbjednost",
    "dash.more": "Više",
    "dash.no_card": "Nema izdate kartice",
    "dash.monthly_activity": "Mjesečna aktivnost",
    "dash.operations": "Operacije",
    "dash.holder": "Nosilac",
    "dash.expires": "Ist.",
    "dash.visa_label": "VISA",
    "dash.balance": "Raspoloživo stanje",
    "tx.all": "Sve",
    "tx.income": "Prihodi",
    "tx.expenses": "Rashodi",
    "tx.today": "Danas",
    "tx.yesterday": "Juče",
    "tx.none": "Nema transakcija.",
    "transfer.title": "Brzi transfer",
    "transfer.recipient": "Primalac",
    "transfer.recipient_ph": "Ime, IBAN, broj telefona…",
    "transfer.amount": "Iznos",
    "transfer.label": "Opis",
    "transfer.reason": "Razlog (opcionalno)",
    "transfer.reason_ph": "Npr.: kirija, povrat…",
    "transfer.execution": "Izvršenje",
    "transfer.immediate": "Trenutno",
    "transfer.delayed": "Odloženo",
    "transfer.balance": "Raspoloživo stanje:",
    "transfer.continue": "Nastavi",
    "transfer.confirm": "Potvrdi",
    "transfer.cancel": "Otkaži",
    "transfer.code": "Kod za validaciju",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Unesite primljeni kod za potvrdu transfera",
    "transfer.pending.title": "Transfer na čekanju validacije",
    "transfer.pending.amount": "Iznos:",
    "transfer.pending.to": "Za:",
    "transfer.pending.hint": "Vaš validacioni kod bit će poslan e-poštom ili putem zvona obavještenja. Kod se sastoji od 6 cifara. Ako ga ne primite, kontaktirajte podršku.",
    "transfer.pending.validate": "Potvrdi transfer",
    "transfer.saved": "Transfer je sačuvan. Zatražite kod od administratora za potvrdu.",
    "transfer.done": "Transfer izvršen",
    "transfer.validated": "Transfer potvrđen!",
    "transfer.progress.processing": "Obrada transfera…",
    "transfer.progress.completed": "Transfer izvršen!",
    "transfer.progress.default_message": "Vaš transfer se obrađuje. Molimo sačekajte.",
    "cards.none": "Nema izdate kartice.\nKontaktirajte svog administratora.",
    "cards.holder": "Nosilac",
    "cards.expires": "Ist.",
    "cards.block": "Zaključaj",
    "cards.pin": "PIN kod",
    "cards.more": "Više",
    "cards.limits": "Ograničenja kartice",
    "cards.payment": "Plaćanje",
    "cards.withdrawal": "Podizanje",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Blokirana",
    "cards.status_active": "Aktivna",
    "cards.toast.blocked": "Kartica blokirana",
    "cards.toast.unblocked": "Kartica deblokirana",
    "cards.toast.physical_ordered": "Fizička kartica naručena! Dostava za 5–10 radnih dana.",
    "cards.toast.created": "Kartica uspješno generisana!",
    "cards.none_sub": "Nema izdate kartice. Generišite svoju virtuelnu karticu sada.",
    "cards.generate_btn": "Generiši moju karticu",
    "cards.create.title": "Generiši moju virtuelnu karticu",
    "cards.create.network": "Platna mreža",
    "cards.create.color": "Boja kartice",
    "cards.create.preview_name": "VAŠE IME",
    "cards.create.preview_expiry": "MM/GG",
    "cards.create.loading": "Generisanje…",
    "cards.create.submit": "Generiši karticu",
    "cards.btn.unblock": "Deblokiraj",
    "cards.btn.block": "Blokiraj",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fizička",
    "cards.btn.physical_done": "Naručena",
    "cards.limits_title": "Ograničenja",
    "cards.reveal.title": "Informacije o kartici",
    "cards.reveal.number": "Cijeli broj",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Istek",
    "cards.reveal.warning": "Nikada ne dijelite ove informacije. Zatvorite ovaj prozor nakon korišćenja.",
    "cards.pin.title": "PIN kod",
    "cards.pin.message": "Ovaj PIN kod se koristi za sva vaša online plaćanja i podizanja. Nikad ga ne otkrivajte nikome.",
    "cards.physical.title": "Naruči fizičku karticu",
    "cards.physical.fee_label": "Naknada za dostavu",
    "cards.physical.balance_label": "Vaš saldo",
    "cards.physical.insufficient": "Nedovoljan saldo. Dopunite račun prije naručivanja.",
    "cards.physical.info": "Naknada će biti naplaćena sa vašeg računa. Dostava za 5–10 radnih dana.",
    "cards.physical.order_btn": "Naruči ({fee})",
    "cards.physical.ordering": "Naručujem…",
    "cards.physical.order_cta": "Naruči fizičku karticu",
    "cards.physical.choose_type": "Odaberi svoju karticu",
    "cards.physical.features": "Prednosti",
    "cards.buy_virtual_cta": "Kupi virtuelnu karticu",
    "notif.none": "Nema obavještenja trenutno.",
    "notif.contact_support": "Contact support",
    "profile.change_photo": "Promijeni fotografiju",
    "profile.remove_photo": "Ukloni fotografiju",
    "profile.email": "E-pošta",
    "profile.phone": "Telefon",
    "profile.country": "Zemlja",
    "profile.address": "Adresa",
    "profile.notifs_title": "Obavještenja ({count})",
    "profile.no_notifs": "Nema obavještenja.",
    "profile.logout": "Odjava",
    "profile.photo_updated": "Profilna fotografija je ažurirana",
    "profile.photo_removed": "Fotografija je uklonjena",
    "profile.photo_too_large": "Slika je prevelika (maks 1 MB)",
    "profile.powered": "Pokrenuto od {bank}",
    "iban.title": "Moj IBAN",
    "iban.loading": "Učitavanje…",
    "iban.bic": "BIC / SWIFT kod",
    "iban.share_info": "Podijelite ove podatke kako biste primili transfer na vaš račun.",
    "iban.copy": "Kopiraj",
    "iban.copied": "Kopirano u clipboard",
    "iban.copy_fail": "Kopiranje nije moguće",
    "iban.close": "Zatvori",
    "accounts.all": "Svi vaši računi otvoreni kod {bank}.",
    "accounts.primary": "Glavni račun",
    "accounts.savings": "Štedni račun",
    "accounts.primary_tag": "Glavni",
    "accounts.secondary_tag": "Sekundarni",
    "accounts.objective": "Cilj: {amount} · {pct}%",
    "accounts.copy": "Kopiraj",
    "accounts.loading": "Učitavanje…",
    "accounts.iban_copied": "IBAN je kopiran",
    "accounts.copy_fail": "Kopiranje nije moguće",
    "savings.total": "Ukupno uštedeno",
    "savings.target_total": "Ukupni cilj",
    "savings.progress": "Napredak",
    "savings.active": "Aktivni ciljevi",
    "savings.new_goal": "Novi cilj",
    "savings.none": "Nema ciljeva štednje za sada.",
    "savings.none_sub": "Kreirajte prvi cilj da biste počeli.",
    "savings.saved": "Uštedeno:",
    "savings.goal": "Cilj:",
    "savings.deadline": "Rok: {date}",
    "savings.deposit": "Depozit",
    "savings.withdraw": "Podignite",
    "savings.loading": "Učitavanje…",
    "savings.form.title": "Novi cilj štednje",
    "savings.form.cancel": "Otkaži",
    "savings.form.create": "Kreiraj",
    "savings.form.name": "Naziv cilja",
    "savings.form.name_ph": "Putovanje Japan, Automobil, …",
    "savings.form.target": "Ciljni iznos",
    "savings.form.initial": "Početni depozit",
    "savings.form.deadline": "Rok (opcionalno)",
    "savings.form.color": "Boja",
    "savings.deposit_title": "Depozit na \"{name}\"",
    "savings.deposit_amount": "Iznos za depozit",
    "savings.balance_hint": "Glavno stanje: {amount}",
    "savings.withdraw_prompt": "Koliko podići iz \"{name}\"?",
    "savings.close_confirm": "Zatvoriti cilj \"{name}\"? Štednja će biti vraćena na vaš glavni račun.",
    "savings.toast.created": "Cilj je kreiran",
    "savings.toast.deposited": "Depozit je izvršen",
    "savings.toast.withdrawn": "Podizanje je izvršeno",
    "savings.toast.closed": "Cilj je zatvoren",
    "invest.total": "Ukupno investirano",
    "invest.profits": "Kumulativni dobici",
    "invest.count": "Investicije",
    "invest.products": "Dostupni proizvodi",
    "invest.invest_btn": "Investiraj",
    "invest.my": "Moje investicije",
    "invest.pending": "Na čekanju",
    "invest.validated": "Potvrđeno",
    "invest.refused": "Odbijeno",
    "invest.withdraw.btn": "Récupérer",
    "invest.withdraw.title": "Récupérer mon investissement",
    "invest.withdraw.intro": "Vous êtes sur le point de récupérer votre investissement « {product} ». Le capital initial et les bénéfices accumulés seront crédités sur votre solde principal.",
    "invest.withdraw.principal": "Capital initial",
    "invest.withdraw.profit": "Bénéfices",
    "invest.withdraw.total": "Total à recevoir",
    "invest.withdraw.confirm": "Confirmer la récupération",
    "invest.withdraw.warning": "Cette action est irréversible. L'investissement sera clôturé.",
    "invest.withdraw.success": "Investissement récupéré — {amount} crédités sur votre solde.",
    "invest.withdrawn": "Récupéré",
    "invest.cta_sub": "Odaberite vrstu investicije koja vam odgovara. Savjetnik će vas pratiti od samog zahtjeva.",
    "invest.form.amount": "Iznos za investiranje",
    "invest.form.balance": "Raspoloživo stanje: {amount}",
    "invest.form.note": "Napomena za savjetnika (opcionalno)",
    "invest.form.note_ph": "Navedite svoja očekivanja…",
    "invest.form.advisor_info": "Razgovor će se automatski otvoriti s vašim savjetnikom.",
    "invest.form.cancel": "Otkaži",
    "invest.form.submit": "Pošalji zahtjev",
    "invest.toast.requested": "Zahtjev je registrovan — razgovor je otvoren s vašim savjetnikom.",
    "invest.p1.name": "Nekretnine",
    "invest.p1.desc": "SCPI, papirna cigla, crowdfunding nekretnina",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Participativno finansiranje odabranih projekata",
    "invest.p3.name": "Obnovljivi izvori",
    "invest.p3.desc": "Solarna, vjetrena i zelena energetska rješenja",
    "invest.p4.name": "Berza",
    "invest.p4.desc": "Dionice, ETF i diverzifikovani fondovi",
    "invest.p5.name": "Participativni",
    "invest.p5.desc": "Zajmovi za MSP i lokalne poduzetnike",
    "invest.p6.name": "Kripto",
    "invest.p6.desc": "Bitcoin, Ethereum i viševalutni novčanik",
    "msg.online": "Online",
    "msg.loading": "Učitavanje…",
    "msg.none": "Nema poruka. Pokrenite razgovor s vašim savjetnikom.",
    "msg.placeholder": "Napišite vašu poruku…",
    "msg.attach.image": "Slika",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Fajl / Dokument",
    "msg.pdf_doc": "PDF dokument",
    "msg.file": "Fajl",
    "msg.too_large": "Fajl je prevelik (maks {size}).",
    "msg.withdraw": "Podignite",
    "credits.cta_sub": "Pošaljite zahtjev direktno online — odgovor za 24 sata.",
    "credits.learn_more": "Saznaj više",
    "credits.request": "Zatražite",
    "credits.history": "Moji zahtjevi",
    "credits.status.pending": "Na čekanju",
    "credits.status.accepted": "Prihvaćeno",
    "credits.status.refused": "Odbijeno",
    "credits.chars": "Karakteristike",
    "credits.amount_range": "Iznos: {min}€ – {max}€",
    "credits.rate_range": "Kamatna stopa: {min}% – {max}% godišnje",
    "credits.duration_range": "Trajanje: {min} do {max} mjeseci",
    "credits.deposit_min": "Početni depozit: {amount}€ minimum",
    "credits.garantie": "Garancija: {garantie}",
    "credits.eligibility": "Uslovi prihvatljivosti",
    "credits.elig.income": "Dokazati redovne i stabilne prihode",
    "credits.elig.deposit": "Izvršiti početni depozit od {amount}€",
    "credits.elig.garantie": "Predočiti garanciju: {garantie}",
    "credits.form.title": "Zahtjev — {name}",
    "credits.form.cancel": "Otkaži",
    "credits.form.submit": "Pošalji zahtjev",
    "credits.form.amount": "Željeni iznos (€)",
    "credits.form.duration": "Trajanje (mjeseci)",
    "credits.form.reason": "Razlog (opcionalno)",
    "credits.form.reason_ph": "Opišite vaš projekat…",
    "credits.form.close": "Zatvori",
    "credits.toast.submitted": "Zahtjev za kredit je poslan — odgovor ćete dobiti za 24 sata.",
    "credits.p1.name": "Lični zajam",
    "credits.p1.desc": "Za finansiranje svakodnevnih potreba ili ličnih projekata.",
    "credits.p2.name": "Hipotekarni zajam",
    "credits.p2.desc": "Za finansiranje kupovine ili izgradnje nekretnine.",
    "credits.p3.name": "Zajam za auto / moto",
    "credits.p3.desc": "Kupite vozilo bez ikakvih briga.",
    "credits.p4.name": "Studentski zajam",
    "credits.p4.desc": "Finansijska pomoć za visoko obrazovanje.",
    "credits.p5.name": "Poslovni zajam",
    "credits.p5.desc": "Za podršku rastu poslovne aktivnosti.",
    "credits.p6.name": "Ekspresni zajam",
    "credits.p6.desc": "Brzo rješenje za hitne potrebe.",
    "financing.reimbursable": "Povratno finansiranje",
    "financing.reimbursable_desc": "Finansijska podrška s fiksnim uslovima za pokretanje ili razvijanje aktivnosti.",
    "financing.non_reimbursable": "Bespovratno finansiranje",
    "financing.non_reimbursable_desc": "Za projekte s društvenim, obrazovnim ili humanitarnim uticajem (NVO, udruženja).",
    "financing.cta_sub": "Prilagođena rješenja za ostvarivanje vaših ambicija.",
    "financing.request": "Zatražite finansiranje",
    "financing.history": "Moji zahtjevi",
    "financing.reimbursable_label": "Povratno",
    "financing.non_reimbursable_label": "Bespovratno",
    "financing.status.pending": "Na čekanju",
    "financing.status.granted": "Odobreno",
    "financing.status.refused": "Odbijeno",
    "financing.form.title": "Zahtjev — {name}",
    "financing.form.cancel": "Otkaži",
    "financing.form.submit": "Pošalji zahtjev",
    "financing.form.project": "Naziv projekta",
    "financing.form.project_ph": "Moj projekat",
    "financing.form.amount": "Traženi iznos (€)",
    "financing.form.sector": "Sektor",
    "financing.form.sector_ph": "Tehnologija, poljoprivreda, …",
    "financing.form.description": "Opis projekta",
    "financing.form.description_ph": "Predstavite projekat, ciljeve i očekivani uticaj…",
    "settings.security": "Bezbjednost",
    "settings.current_pw": "Trenutna lozinka",
    "settings.new_pw": "Nova lozinka",
    "settings.confirm_pw": "Potvrdi",
    "settings.submit": "Promijeni lozinku",
    "settings.no_match": "Lozinke se ne podudaraju",
    "settings.changed": "Lozinka je promijenjena",
    "common.cancel": "Otkaži",
    "common.confirm": "Potvrdi",
    "common.close": "Zatvori",
    "common.save": "Sačuvaj",
    "common.loading": "Učitavanje…",
    "common.send": "Pošalji",
    "common.total": "Ukupno",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Pag. mujore e vlerë.",
    "credits.rate_annual": "/ vit",
    "credits.rate_used": "Norma e vlerës.:",
    "credits.simulation": "Simulim i pagesës mujore",
    "credits.total_cost": "Kostoja totale e vlerë.",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Добродошли",
    "common.delete": "Обриши",
    "cards.delete.title": "Обриши картицу",
    "cards.delete.confirm_msg": "Ова радња је неповратна. Картица ће бити трајно обрисана.",
    "cards.delete.physical_tooltip": "Физичка картица наручена — брисање немогуће",
    "cards.order.confirmed": "Наруџба потврђена!",
    "cards.order.confirmed_body": "Ваша физичка картица се припрема. Користите овај број за праћење доставе.",
    "cards.order.tracking_no": "Бр. праћења",
    "cards.fee_deducted": "{amount} ће бити одбијено од вашег стања",
    "cards.insufficient_short": "Недовољно стање ({available} доступно)",
    "transfer.section.beneficiary": "Прималац",
    "transfer.section.details": "Пренос",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Банка",
    "transfer.field.city": "Град",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Банка:",
    "transfer.progress.paused": "Чека на потврду",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Promjenljiva",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "credits.garantie.none": "Nijedna",
    "credits.garantie.mortgage": "Hipoteka na finansiranu nekretninu",
    "credits.garantie.vehicle": "Finansirano vozilo",
    "credits.garantie.parental": "Roditeljska ili bankarska",
    "credits.garantie.professional": "Profesionalna imovina ili lično jemstvo",
    "auth.pin.title": "PIN kod",
    "auth.pin.subtitle": "Unesite svoj 4-cifreni PIN kod",
    "auth.pin.label": "PIN kod",
    "auth.pin.confirm": "Potvrdi PIN kod",
    "auth.pin.submit": "Potvrdi",
    "auth.pin.back": "Nazad na prijavu",
    "auth.pin.length_error": "PIN kod mora sadržati 4 cifre",
    "cards.reveal.pin_prompt": "Unesite svoj 4-cifreni PIN kod da biste prikazali informacije o kartici.",
    "cards.reveal.show": "Prikaži",
    "transfer.field.iban": "IBAN",
    "cards.deleted": "Kartica obrisana",
    "cards.feat.gold_exclusive": "Ekskluzivna zlatna kartica",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Zaštita kupca",
    "cards.feat.support_standard": "Standardna podrška",
    "cards.feat.plastic_premium": "Premium plastična kartica",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Standardno putno osiguranje",
    "cards.feat.support_priority": "Prioritetna podrška",
    "cards.feat.metal_brushed": "Kartica od brušenog metala",
    "cards.feat.cashback_2_unlimited": "Neograničen Cashback 2%",
    "cards.feat.travel_premium": "Premium putno osiguranje",
    "cards.feat.vip_lounge": "Pristup VIP salonima",
    "cards.feat.support_247": "Podrška 24/7",
    "auth.demo.title": "Administratorska prijava",
    "auth.demo.subtitle": "Unesite svoje admin pristupne podatke za pregled demo verzije",
    "auth.pin.mismatch": "Dy kodet PIN nuk përputhen",
    "auth.pin.digit_hint": "4 shifra",
  },
  uk: {
    "notif.transfer_code.title": "Код підтвердження переказу",
    "notif.transfer_code.body": "Скопіюйте цей код, щоб підтвердити переказ.",
    "notif.transfer_code.expires_in": "Зникає через {sec}с",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} міс.",
    "credits.n_months": "{n} міс.",
    "msg.read": "Прочитано",
    "msg.sent": "Надіслано",
    "msg.attach_aria": "Прикріпити файл",
    "login.device.unknown": "Невідомий браузер",
    "login.device.mobile": "Мобільний",
    "login.device.tablet": "Планшет",
    "login.device.desktop": "Комп'ютер",
    "login.recent": "Нещодавно",
    "err.invalid_credentials": "Невірні дані для входу",
    "err.bad_pin": "Невірний PIN",
    "err.account_blocked": "Обліковий запис заблоковано",
    "err.bad_current_pw": "Поточний пароль невірний",
    "err.insufficient_funds": "Недостатньо коштів, поповніть рахунок.",
    "err.amount_above_savings": "Сума перевищує доступні заощадження",
    "err.must_be_positive": "Значення має бути позитивним",
    "err.invalid_format": "Невірний формат",
    "err.email_taken": "Email вже використовується",
    "err.card_not_found": "Картку не знайдено",
    "err.physical_card_already_ordered": "Фізична картка вже замовлена",
    "err.beneficiary_not_found": "Одержувача не знайдено",
    "err.invalid_code": "Невірний код",
    "err.no_transfer_code": "Немає коду для цього переказу",
    "err.link_expired": "Посилання невірне або застаріле",
    "err.thread_not_found": "Розмову не знайдено",
    "err.empty_message": "Порожнє повідомлення",
    "err.file_too_large": "Файл занадто великий (макс 20 МБ)",
    "err.forbidden": "Доступ заборонено",
    "err.bank_not_found": "Банк не знайдено",
    "err.signups_closed": "Реєстрація в цьому банку закрита.",
    "err.access_suspended": "Доступ тимчасово призупинено. Будь ласка, зверніться до вашого банку.",
    "err.notif_not_closable": "Це сповіщення не можна закрити.",
    "auth.signup.tagline": "Приєднуйтесь до нас сьогодні",
    "auth.login.tagline": "Ласкаво просимо!",
    "auth.signup.tagline_sub": "Відкрийте свій рахунок за 2 хвилини та отримайте доступ до всіх наших преміум-банківських послуг.",
    "auth.login.tagline_sub": "Увійдіть до свого особистого кабінету та керуйте своїми фінансами в повній безпеці.",
    "auth.back": "Повернутися на головну",
    "auth.bullet.security": "Банківська безпека військового рівня",
    "auth.bullet.transfers": "Миттєві перекази 24/7",
    "auth.bullet.investments": "Інтегровані інвестиції та заощадження",
    "auth.bullet.advisor": "Особистий консультант доступний 7 днів/7",
    "auth.footer": "Працює на {bank}",
    "auth.signup.title": "Створити рахунок",
    "auth.login.title": "Вхід",
    "auth.field.lastName": "Прізвище",
    "auth.field.firstName": "Ім'я",
    "auth.field.country": "Країна",
    "auth.field.country.placeholder": "— Оберіть вашу країну —",
    "auth.field.phone": "Телефон",
    "auth.field.address": "Адреса",
    "auth.field.address.placeholder": "вул. Хрещатик, 1, Київ",
    "auth.field.email": "Email",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Пароль",
    "auth.submit.signup": "Створити мій рахунок",
    "auth.submit.login": "Увійти",
    "auth.login.badge": "Увійдіть до свого облікового запису",
    "auth.card.tagline": "Ваш особистий кабінет",
    "auth.submit.loading": "Завантаження…",
    "auth.pin.title": "PIN-код",
    "auth.pin.subtitle": "Введіть свій 4-значний PIN-код",
    "auth.pin.label": "PIN-код",
    "auth.pin.confirm": "Підтвердити PIN-код",
    "auth.pin.submit": "Підтвердити",
    "auth.pin.back": "Назад до входу",
    "auth.pin.length_error": "PIN-код має містити 4 цифри",
    "auth.switch.have_account": "Вже маєте рахунок?",
    "auth.switch.login_link": "Вхід",
    "auth.switch.no_account": "Ще не клієнт?",
    "auth.error.select_country": "Оберіть вашу країну",
    "kyc.title": "Верифікація особи (KYC)",
    "kyc.sub": "Завантажте свої документи для підтвердження рахунку. Усі документи зашифровані та конфіденційні.",
    "kyc.id_type": "Тип документа, що посвідчує особу *",
    "kyc.id_card": "Посвідчення особи",
    "kyc.passport": "Паспорт",
    "kyc.driver_license": "Водійське посвідчення",
    "kyc.recto_passport": "Лицева сторона паспорта *",
    "kyc.recto_id": "Лицева сторона документа *",
    "kyc.verso": "Зворотна сторона документа *",
    "kyc.selfie": "Селфі з документом у руці *",
    "kyc.address_proof": "Підтвердження адреси *",
    "kyc.hint_doc": "Зображення або PDF, макс. 5 МБ",
    "kyc.hint_selfie": "Чітке фото, обличчя та документ видно",
    "kyc.hint_address": "Рахунок, виписка, довідка… (макс. 5 МБ)",
    "kyc.upload": "Завантажити",
    "kyc.replace": "Замінити",
    "pending.title": "Рахунок на розгляді",
    "pending.body": "Ваш запит отримано. Адміністратор перевірить ваші документи та активує рахунок найближчим часом.",
    "pending.back": "Повернутися до входу",
    "home.nav.features": "Можливості",
    "home.nav.security": "Безпека",
    "home.nav.invest": "Інвестиції",
    "home.nav.credits": "Кредити",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Увійти",
    "home.hero.tagline": "Онлайн-банкінг нового покоління",
    "home.hero.title1": "Керуйте своїми грошима",
    "home.hero.title2": "розумно",
    "home.hero.sub": "Повноцінна банківська платформа — перекази, заощадження, інвестиції та кредити з вашого смартфона.",
    "home.hero.login": "Увійти",
    "home.hero.trust.secure": "100% Безпечно",
    "home.hero.trust.fast": "Швидке відкриття",
    "home.hero.trust.encrypted": "Зашифровані дані",
    "home.hero.trust.support": "Підтримка 24/7",
    "home.mockup.balance": "Доступний залишок",
    "home.mockup.currency": "EUR · Основний рахунок",
    "home.mockup.recharge": "Поповнити",
    "home.mockup.send": "Надіслати",
    "home.mockup.save": "Зберегти",
    "home.mockup.salary": "Зарплата Березень",
    "home.mockup.rent": "Оренда Квітень",
    "home.mockup.investment": "Інвестиція",
    "home.mockup.days2": "2 дні тому",
    "home.mockup.days3": "3 дні тому",
    "home.mockup.days5": "5 днів тому",
    "home.stats.accounts": "Активних рахунків",
    "home.stats.uptime": "Доступність",
    "home.stats.transfers": "Переказів",
    "home.stats.support": "Підтримка",
    "home.features.label": "Можливості",
    "home.features.title": "Все, що вам потрібно",
    "home.features.sub": "Повноцінна платформа для спрощення управління вашими фінансами щодня.",
    "home.feature.security": "Військова безпека",
    "home.feature.security_body": "Шифрування AES-256, двофакторна автентифікація та цілодобовий моніторинг рахунку.",
    "home.feature.cards": "Віртуальні та фізичні картки",
    "home.feature.cards_body": "Випускайте картки за кілька секунд або замовте справжню фізичну картку з доставкою.",
    "home.feature.transfers": "Миттєві перекази",
    "home.feature.transfers_body": "Надсилайте та отримуйте гроші будь-де у світі за лічені секунди.",
    "home.feature.analytics": "Аналіз у реальному часі",
    "home.feature.analytics_body": "Розумні дашборди для відстеження витрат та планування фінансів.",
    "home.feature.investments": "Інтегровані інвестиції",
    "home.feature.investments_body": "Отримайте доступ до відібраних інвестиційних продуктів безпосередньо з вашого рахунку.",
    "home.feature.support": "Підтримка 24/7",
    "home.feature.support_body": "Особистий банківський консультант доступний у будь-який час через захищений месенджер.",
    "home.tpl.label": "Довіра",
    "home.tpl.title": "Нам довіряють",
    "home.mockup.balance_label": "Залишок",
    "home.mockup.virement": "Переказ",
    "home.mockup.salaire": "Зарплата",
    "home.security.label": "Безпека",
    "home.security.title1": "Ваші фінанси",
    "home.security.title2": "під абсолютним захистом",
    "home.security.sub": "Ми дотримуємось найсуворіших стандартів банківської безпеки для захисту кожного цента.",
    "home.security.aes.title": "Шифрування AES-256",
    "home.security.aes.body": "Усі конфіденційні дані зашифровані тими ж алгоритмами, що використовують провідні міжнародні банки.",
    "home.security.auth.title": "Посилена автентифікація",
    "home.security.auth.body": "Подвійна перевірка особи при кожному вході та для кожної чутливої операції на вашому рахунку.",
    "home.security.compliance.title": "Відповідність PSD2/GDPR",
    "home.security.compliance.body": "Ми дотримуємось усіх європейських норм щодо платіжних послуг та захисту даних.",
    "home.invest.label": "Інвестиції",
    "home.invest.title": "Примножте свої гроші",
    "home.invest.sub": "6 категорій інвестицій, відібраних за потенціалом та надійністю.",
    "home.invest.yield": "Дохідність",
    "home.credits.label": "Кредити",
    "home.credits.title": "Рішення для кожного проєкту",
    "home.credits.sub": "Конкурентні ставки, швидкі відповіді та персоналізований супровід для всіх ваших проєктів.",
    "home.credits.rate": "Ставка",
    "home.faq.label": "FAQ",
    "home.faq.title": "Поширені запитання",
    "home.faq.q1": "Як відкрити рахунок?",
    "home.faq.a1": "Реєстрація займає менше 2 хвилин. Просто введіть свої персональні дані, оберіть пароль — і ваш рахунок буде активовано після перевірки нашою командою.",
    "home.faq.q2": "Чи захищені мої дані?",
    "home.faq.a2": "Так. Усі дані зашифровані за допомогою AES-256 у стані спокою та TLS 1.3 під час передачі. Ми дотримуємось міжнародних банківських стандартів та норм GDPR/PSD2.",
    "home.faq.q3": "Які типи інвестицій ви пропонуєте?",
    "home.faq.a3": "Ми пропонуємо 6 категорій: Нерухомість, Краудфандинг, Відновлювана енергія, Фондовий ринок, Партиципативні та Крипто. Кожен продукт відбирається за співвідношенням ризику та прибутковості.",
    "home.faq.q4": "Як подати заявку на кредит?",
    "home.faq.a4": "Перейдіть до розділу Кредит у вашому особистому кабінеті, оберіть тип позики, введіть суму та строк. Ваша заявка буде розглянута протягом 24 годин нашими консультантами.",
    "home.faq.q5": "Чи є приховані комісії?",
    "home.faq.a5": "Жодних прихованих комісій. Усі тарифи чітко відображаються при кожній операції. Умови доступні у вашому особистому кабінеті у будь-який час.",
    "home.faq.q6": "Як зв'язатися зі службою підтримки?",
    "home.faq.a6": "Скористайтесь вбудованим месенджером у вашому особистому кабінеті для прямого зв'язку з вашим особистим банківським консультантом, доступним 24/7.",
    "home.cta.title": "Готові почати?",
    "home.cta.sub": "Приєднуйтесь до тисяч користувачів, які керують своїми фінансами за допомогою {bank}. Реєстрація займає менше 2 хвилин.",
    "home.footer.products": "Продукти",
    "home.footer.services": "Послуги",
    "home.footer.access": "Швидкий доступ",
    "home.footer.accounts": "Рахунки",
    "home.footer.transfers": "Перекази",
    "home.footer.cards": "Картки",
    "home.footer.savings": "Заощадження",
    "home.footer.investments": "Інвестиції",
    "home.footer.credits": "Кредити",
    "home.footer.financing": "Фінансування",
    "home.footer.messages": "Повідомлення",
    "home.footer.login": "Вхід",
    "home.footer.signup": "Відкрити рахунок",
    "home.footer.support": "Підтримка 24/7",
    "home.footer.tagline": "Ваш онлайн-банк, доступний скрізь і в будь-який час.",
    "home.footer.copyright": "© {year} {bank} — Усі права захищені",
    "home.footer.powered": "Працює на {bank}",
    "app.menu_label": "Головне меню",
    "app.nav.dashboard": "Дашборд",
    "app.nav.accounts": "Рахунки",
    "app.nav.transactions": "Транзакції",
    "app.nav.transfers": "Перекази",
    "app.nav.cards": "Картки",
    "app.nav.savings": "Заощадження",
    "app.nav.investments": "Інвестиції",
    "app.nav.credit": "Кредит",
    "app.nav.financing": "Фінансування",
    "app.nav.messages": "Повідомлення",
    "app.nav.card_tracking": "Відстеження картки",
    "app.nav.notifications": "Сповіщення",
    "app.nav.settings": "Налаштування",
    "app.nav.profile": "Профіль",
    "app.nav.mobile.home": "Головна",
    "app.nav.mobile.transfers": "Перекази",
    "app.nav.mobile.cards": "Картки",
    "app.nav.mobile.transactions": "Транзакції",
    "app.nav.more.invest": "Інвестувати",
    "app.user_role": "Клієнт",
    "app.logout": "Вийти",
    "app.search": "Пошук…",
    "app.more_title": "Більше можливостей",
    "app.more_btn": "Ще",
    "app.loading": "Завантаження…",
    "app.error_back": "Повернутися до входу",
    "app.see_more_notifs": "Переглянути ще {count} сповіщень →",
    "page.accounts": "Мої рахунки",
    "page.transactions": "Транзакції",
    "page.transfers": "Новий переказ",
    "page.deposit": "Депозит",
    "page.cards": "Мої картки",
    "page.savings": "Заощадження",
    "page.investments": "Інвестиції",
    "page.credits": "Кредит",
    "page.financing": "Фінансування проєктів",
    "page.messages": "Повідомлення",
    "page.settings": "Налаштування",
    "page.notifications": "Важливі сповіщення",
    "page.profile": "Мій профіль",
    "dash.greeting": "Привіт, {name}",
    "dash.overview": "Огляд ваших фінансів — {date}",
    "dash.total_balance": "Загальний залишок",
    "dash.this_month": "+{amount} цього місяця",
    "dash.view_iban": "Переглянути мій IBAN",
    "dash.my_accounts": "Мої рахунки",
    "dash.primary_account": "Основний рахунок",
    "dash.savings_account": "Ощадний рахунок",
    "dash.remunerated": "Відсотковий",
    "dash.details": "Деталі",
    "dash.transfer_btn": "Переказ",
    "dash.quick_actions": "Швидкі дії",
    "dash.quick.deposit": "Депозит",
    "deposit.title": "Інформація про депозит",
    "dash.quick.transfer": "Переказ",
    "dash.quick.history": "Історія",
    "dash.quick.cards": "Мої картки",
    "dash.quick.profile": "Профіль",
    "dash.monthly_budget": "Місячний бюджет",
    "dash.income": "Надходження",
    "dash.expenses": "Витрати",
    "dash.spending_cat": "Витрати за категоріями",
    "dash.no_spending": "Витрати не зареєстровані.",
    "dash.recent_tx": "Останні транзакції",
    "dash.see_all": "Переглянути всі",
    "dash.no_tx": "Операцій немає.",
    "dash.main_card": "Моя основна картка",
    "dash.manage": "Керувати",
    "dash.block": "Заблокувати",
    "dash.pin": "PIN-код",
    "dash.security": "Безпека",
    "dash.more": "Ще",
    "dash.no_card": "Картки не випущено",
    "dash.monthly_activity": "Активність за місяць",
    "dash.operations": "Операції",
    "dash.holder": "Власник",
    "dash.expires": "Дійсна до",
    "dash.visa_label": "VISA",
    "dash.balance": "Доступний залишок",
    "tx.all": "Усі",
    "tx.income": "Надходження",
    "tx.expenses": "Витрати",
    "tx.today": "Сьогодні",
    "tx.yesterday": "Вчора",
    "tx.none": "Транзакцій немає.",
    "transfer.title": "Швидкий переказ",
    "transfer.recipient": "Одержувач",
    "transfer.recipient_ph": "Ім'я, IBAN, номер телефону…",
    "transfer.amount": "Сума",
    "transfer.label": "Призначення",
    "transfer.reason": "Причина (необов'язково)",
    "transfer.reason_ph": "Напр.: оренда, повернення боргу…",
    "transfer.execution": "Виконання",
    "transfer.immediate": "Негайно",
    "transfer.delayed": "Відкладено",
    "transfer.balance": "Доступний залишок:",
    "transfer.continue": "Продовжити",
    "transfer.confirm": "Підтвердити",
    "transfer.cancel": "Скасувати",
    "transfer.code": "Код підтвердження",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Введіть отриманий код для підтвердження переказу",
    "transfer.pending.title": "Переказ очікує підтвердження",
    "transfer.pending.amount": "Сума:",
    "transfer.pending.to": "Кому:",
    "transfer.pending.hint": "Ваш код підтвердження буде надіслано електронною поштою або через дзвіночок сповіщень. Код складається з 6 цифр. Якщо ви його не отримали, зверніться до служби підтримки.",
    "transfer.pending.validate": "Підтвердити переказ",
    "transfer.saved": "Переказ збережено. Запросіть код у адміністратора для підтвердження.",
    "transfer.done": "Переказ виконано",
    "transfer.validated": "Переказ підтверджено!",
    "transfer.progress.processing": "Обробка переказу…",
    "transfer.progress.completed": "Переказ виконано!",
    "transfer.progress.default_message": "Ваш переказ обробляється. Будь ласка, зачекайте.",
    "cards.none": "Картки не випущено.\nЗверніться до адміністратора.",
    "cards.holder": "Власник",
    "cards.expires": "Дійсна до",
    "cards.block": "Заблокувати",
    "cards.pin": "PIN-код",
    "cards.more": "Ще",
    "cards.limits": "Ліміти картки",
    "cards.payment": "Оплата",
    "cards.withdrawal": "Зняття готівки",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Заблоковано",
    "cards.status_active": "Активно",
    "cards.toast.blocked": "Картку заблоковано",
    "cards.toast.unblocked": "Картку розблоковано",
    "cards.toast.physical_ordered": "Фізичну картку замовлено! Доставка за 5–10 робочих днів.",
    "cards.toast.created": "Картку успішно згенеровано!",
    "cards.none_sub": "Жодної картки не видано. Згенеруйте свою віртуальну картку зараз.",
    "cards.generate_btn": "Згенерувати мою картку",
    "cards.create.title": "Згенерувати мою віртуальну картку",
    "cards.create.network": "Платіжна мережа",
    "cards.create.color": "Колір картки",
    "cards.create.preview_name": "ВАШЕ ІМ'Я",
    "cards.create.preview_expiry": "ММ/РР",
    "cards.create.loading": "Генерування…",
    "cards.create.submit": "Згенерувати картку",
    "cards.btn.unblock": "Розблокувати",
    "cards.btn.block": "Заблокувати",
    "cards.btn.info": "Інфо",
    "cards.btn.physical": "Фізична",
    "cards.btn.physical_done": "Замовлено",
    "cards.limits_title": "Ліміти",
    "cards.reveal.title": "Інформація про картку",
    "cards.reveal.number": "Повний номер",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Термін дії",
    "cards.reveal.warning": "Ніколи не діліться цією інформацією. Закрийте це вікно після використання.",
    "cards.pin.title": "PIN-код",
    "cards.pin.message": "Цей PIN-код використовується для всіх ваших онлайн-платежів і знять. Ніколи нікому його не повідомляйте.",
    "cards.physical.title": "Замовити фізичну картку",
    "cards.physical.fee_label": "Плата за доставку",
    "cards.physical.balance_label": "Ваш баланс",
    "cards.physical.insufficient": "Недостатній баланс. Будь ласка, поповніть рахунок перед замовленням.",
    "cards.physical.info": "Плата буде знята з вашого рахунку. Доставка за 5–10 робочих днів.",
    "cards.physical.order_btn": "Замовити ({fee})",
    "cards.physical.ordering": "Замовлення…",
    "cards.physical.order_cta": "Замовте фізичну картку",
    "cards.physical.choose_type": "Виберіть свою картку",
    "cards.physical.features": "Переваги",
    "cards.buy_virtual_cta": "Купити віртуальну картку",
    "notif.none": "Наразі сповіщень немає.",
    "notif.contact_support": "Зв’яжіться з підтримкою",
    "profile.change_photo": "Змінити фото",
    "profile.remove_photo": "Видалити фото",
    "profile.email": "Email",
    "profile.phone": "Телефон",
    "profile.country": "Країна",
    "profile.address": "Адреса",
    "profile.notifs_title": "Сповіщення ({count})",
    "profile.no_notifs": "Сповіщень немає.",
    "profile.logout": "Вийти",
    "profile.photo_updated": "Фото профілю оновлено",
    "profile.photo_removed": "Фото видалено",
    "profile.photo_too_large": "Зображення надто велике (макс. 1 МБ)",
    "profile.powered": "Працює на {bank}",
    "iban.title": "Мій IBAN",
    "iban.loading": "Завантаження…",
    "iban.bic": "Код BIC / SWIFT",
    "iban.share_info": "Поділіться цими реквізитами, щоб отримати переказ на ваш рахунок.",
    "iban.copy": "Копіювати",
    "iban.copied": "Скопійовано до буфера обміну",
    "iban.copy_fail": "Не вдалося скопіювати",
    "iban.close": "Закрити",
    "accounts.all": "Усі ваші рахунки в {bank}.",
    "accounts.primary": "Основний рахунок",
    "accounts.savings": "Ощадний рахунок",
    "accounts.primary_tag": "Основний",
    "accounts.secondary_tag": "Додатковий",
    "accounts.objective": "Ціль: {amount} · {pct}%",
    "accounts.copy": "Копіювати",
    "accounts.loading": "Завантаження…",
    "accounts.iban_copied": "IBAN скопійовано",
    "accounts.copy_fail": "Не вдалося скопіювати",
    "savings.total": "Загальна сума заощаджень",
    "savings.target_total": "Загальна ціль",
    "savings.progress": "Прогрес",
    "savings.active": "Активні цілі",
    "savings.new_goal": "Нова ціль",
    "savings.none": "Цілей заощаджень ще немає.",
    "savings.none_sub": "Створіть свою першу ціль, щоб розпочати.",
    "savings.saved": "Заощаджено:",
    "savings.goal": "Ціль:",
    "savings.deadline": "Дедлайн: {date}",
    "savings.deposit": "Поповнити",
    "savings.withdraw": "Зняти",
    "savings.loading": "Завантаження…",
    "savings.form.title": "Нова ціль заощаджень",
    "savings.form.cancel": "Скасувати",
    "savings.form.create": "Створити",
    "savings.form.name": "Назва цілі",
    "savings.form.name_ph": "Подорож до Японії, Автомобіль, …",
    "savings.form.target": "Цільова сума",
    "savings.form.initial": "Початковий внесок",
    "savings.form.deadline": "Кінцева дата (необов'язково)",
    "savings.form.color": "Колір",
    "savings.deposit_title": "Поповнити \"{name}\"",
    "savings.deposit_amount": "Сума поповнення",
    "savings.balance_hint": "Залишок на основному рахунку: {amount}",
    "savings.withdraw_prompt": "Скільки зняти з \"{name}\"?",
    "savings.close_confirm": "Закрити ціль \"{name}\"? Заощадження будуть повернуті на ваш основний рахунок.",
    "savings.toast.created": "Ціль створено",
    "savings.toast.deposited": "Поповнення виконано",
    "savings.toast.withdrawn": "Зняття виконано",
    "savings.toast.closed": "Ціль закрито",
    "invest.total": "Загальна сума інвестицій",
    "invest.profits": "Накопичений прибуток",
    "invest.count": "Інвестиції",
    "invest.products": "Доступні продукти",
    "invest.invest_btn": "Інвестувати",
    "invest.my": "Мої інвестиції",
    "invest.pending": "На розгляді",
    "invest.validated": "Підтверджено",
    "invest.refused": "Відхилено",
    "invest.withdraw.btn": "Вивести",
    "invest.withdraw.title": "Вивести мою інвестицію",
    "invest.withdraw.intro": "Ви збираєтеся вивести свою інвестицію «{product}». Початковий капітал і нарахований прибуток будуть зараховані на ваш основний баланс.",
    "invest.withdraw.principal": "Початковий капітал",
    "invest.withdraw.profit": "Прибуток",
    "invest.withdraw.total": "Разом до отримання",
    "invest.withdraw.confirm": "Підтвердити виведення",
    "invest.withdraw.warning": "Цю дію не можна скасувати. Інвестицію буде закрито.",
    "invest.withdraw.success": "Інвестиція виведена — {amount} зараховано на баланс.",
    "invest.withdrawn": "Виведено",
    "invest.cta_sub": "Оберіть тип інвестиції, що вам підходить. Консультант допоможе вам з першого запиту.",
    "invest.form.amount": "Сума для інвестування",
    "invest.form.balance": "Доступний залишок: {amount}",
    "invest.form.note": "Нотатка для консультанта (необов'язково)",
    "invest.form.note_ph": "Уточніть ваші очікування…",
    "invest.form.advisor_info": "Розмову з вашим консультантом буде відкрито автоматично.",
    "invest.form.cancel": "Скасувати",
    "invest.form.submit": "Надіслати запит",
    "invest.toast.requested": "Запит зареєстровано — розмову з вашим консультантом відкрито.",
    "invest.p1.name": "Нерухомість",
    "invest.p1.desc": "SCPI, нерухомість на папері, краудфандинг нерухомості",
    "invest.p2.name": "Краудфандинг",
    "invest.p2.desc": "Партиципативне фінансування відібраних проєктів",
    "invest.p3.name": "Відновлювана енергія",
    "invest.p3.desc": "Сонячна, вітрова та зелені проєкти",
    "invest.p4.name": "Фондовий ринок",
    "invest.p4.desc": "Акції, ETF та диверсифіковані фонди",
    "invest.p5.name": "Партиципативні",
    "invest.p5.desc": "Позики для МСП та місцевих підприємців",
    "invest.p6.name": "Крипто",
    "invest.p6.desc": "Bitcoin, Ethereum та мультивалютний гаманець",
    "msg.online": "Онлайн",
    "msg.loading": "Завантаження…",
    "msg.none": "Повідомлень немає. Розпочніть розмову з вашим консультантом.",
    "msg.placeholder": "Напишіть ваше повідомлення…",
    "msg.attach.image": "Зображення",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Аудіо",
    "msg.attach.file": "Файл / Документ",
    "msg.pdf_doc": "PDF-документ",
    "msg.file": "Файл",
    "msg.too_large": "Файл надто великий (макс. {size}).",
    "msg.withdraw": "Видалити",
    "credits.cta_sub": "Подайте заявку онлайн — відповідь протягом 24 годин.",
    "credits.learn_more": "Дізнатися більше",
    "credits.request": "Подати заявку",
    "credits.history": "Мої заявки",
    "credits.status.pending": "На розгляді",
    "credits.status.accepted": "Схвалено",
    "credits.status.refused": "Відхилено",
    "credits.chars": "Характеристики",
    "credits.amount_range": "Сума: {min}€ – {max}€",
    "credits.rate_range": "Відсоткова ставка: {min}% – {max}% на рік",
    "credits.duration_range": "Строк: від {min} до {max} місяців",
    "credits.deposit_min": "Початковий внесок: {amount}€ мінімум",
    "credits.garantie": "Застава: {garantie}",
    "credits.eligibility": "Умови відповідності",
    "credits.elig.income": "Підтвердити регулярний та стабільний дохід",
    "credits.elig.deposit": "Здійснити початковий внесок у розмірі {amount}€",
    "credits.elig.garantie": "Надати заставу: {garantie}",
    "credits.form.title": "Заявка — {name}",
    "credits.form.cancel": "Скасувати",
    "credits.form.submit": "Надіслати заявку",
    "credits.form.amount": "Бажана сума (€)",
    "credits.form.duration": "Строк (місяців)",
    "credits.form.reason": "Причина (необов'язково)",
    "credits.form.reason_ph": "Опишіть ваш проєкт…",
    "credits.form.close": "Закрити",
    "credits.toast.submitted": "Заявку на кредит надіслано — ви отримаєте відповідь протягом 24 годин.",
    "credits.p1.name": "Особистий кредит",
    "credits.p1.desc": "Для фінансування повсякденних потреб або особистих проєктів.",
    "credits.p2.name": "Іпотечний кредит",
    "credits.p2.desc": "Для фінансування купівлі або будівництва нерухомості.",
    "credits.p3.name": "Автокредит / Мотокредит",
    "credits.p3.desc": "Придбайте транспортний засіб без зайвих турбот.",
    "credits.p4.name": "Студентський кредит",
    "credits.p4.desc": "Фінансова допомога для вищої освіти.",
    "credits.p5.name": "Бізнес-кредит",
    "credits.p5.desc": "Для підтримки розвитку підприємницької діяльності.",
    "credits.p6.name": "Експрес-кредит",
    "credits.p6.desc": "Швидке рішення для термінових потреб.",
    "financing.reimbursable": "Фінансування з поверненням",
    "financing.reimbursable_desc": "Фінансова підтримка з фіксованими умовами для запуску або розвитку діяльності.",
    "financing.non_reimbursable": "Безповоротне фінансування",
    "financing.non_reimbursable_desc": "Для проєктів із соціальним, освітнім або гуманітарним впливом (НКО, асоціації).",
    "financing.cta_sub": "Індивідуальні рішення для реалізації ваших амбіцій.",
    "financing.request": "Подати запит на фінансування",
    "financing.history": "Мої заявки",
    "financing.reimbursable_label": "З поверненням",
    "financing.non_reimbursable_label": "Безповоротне",
    "financing.status.pending": "На розгляді",
    "financing.status.granted": "Надано",
    "financing.status.refused": "Відхилено",
    "financing.form.title": "Заявка — {name}",
    "financing.form.cancel": "Скасувати",
    "financing.form.submit": "Надіслати заявку",
    "financing.form.project": "Назва проєкту",
    "financing.form.project_ph": "Мій проєкт",
    "financing.form.amount": "Запитувана сума (€)",
    "financing.form.sector": "Сектор",
    "financing.form.sector_ph": "Технології, сільське господарство, …",
    "financing.form.description": "Опис проєкту",
    "financing.form.description_ph": "Представте ваш проєкт, його цілі та очікуваний вплив…",
    "settings.security": "Безпека",
    "settings.current_pw": "Поточний пароль",
    "settings.new_pw": "Новий пароль",
    "settings.confirm_pw": "Підтвердити",
    "settings.submit": "Змінити пароль",
    "settings.no_match": "Паролі не збігаються",
    "settings.changed": "Пароль змінено",
    "common.cancel": "Скасувати",
    "common.confirm": "Підтвердити",
    "common.close": "Закрити",
    "common.save": "Зберегти",
    "common.loading": "Завантаження…",
    "common.send": "Надіслати",
    "common.total": "Разом",
    "admin.wallet.balance": "Доступний баланс",
    "admin.wallet.history": "Історія",
    "admin.wallet.no_history": "Немає транзакцій",
    "admin.wallet.title": "Мій гаманець",
    "admin.wallet.topup": "Поповнити",
    "beneficiary.pick": "Вибрати",
    "beneficiary.save_this": "Зберегти одержувача",
    "beneficiary.saved": "Збережені одержувачі",
    "beneficiary.saved_ok": "Одержувача збережено",
    "card.color.amber": "Бурштиновий",
    "card.color.cyan": "Блакитний",
    "card.color.emerald": "Смарагдовий",
    "card.color.forest": "Лісовий",
    "card.color.gold": "Золотий",
    "card.color.lavender": "Лавандовий",
    "card.color.night": "Нічний",
    "card.color.orange": "Помаранчевий",
    "card.color.pink": "Рожевий",
    "card.color.royal": "Королівський синій",
    "card.color.ruby": "Рубіновий",
    "card.color.slate": "Сланцевий",
    "card.color.steel": "Сталевий",
    "card.color.terracotta": "Теракотовий",
    "card.color.violet": "Фіолетовий",
    "common.other": "Інше",
    "common.search": "Пошук",
    "content.translated": "Автоматично перекладений вміст",
    "credits.monthly_payment": "Орієнтовний щомісячний платіж",
    "credits.rate_annual": "/ рік",
    "credits.rate_used": "Орієнтовна ставка:",
    "credits.simulation": "Розрахунок щомісячного платежу",
    "credits.total_cost": "Орієнтовна загальна вартість",
    "demo.banner": "Демо-режим — жодних реальних дій",
    "demo.disabled": "Дія недоступна в демо-режимі",
    "deposit.default_instructions": "Зверніться до менеджера для отримання деталей поповнення.",
    "error.back_home": "На головну",
    "error.bank_not_found": "Банк не знайдено",
    "error.unknown": "Невідома помилка",
    "kyc.file_read_error": "Не вдалося прочитати файл",
    "kyc.file_too_large": "Файл занадто великий (макс. 5 МБ)",
    "login.history": "Історія входів",
    "login.history.empty": "Немає записів про входи.",
    "notif.dismiss": "Закрити",
    "notif.locked": "Повідомлення банку",
    "tracking.error.not_found": "Номер відстеження не знайдено. Перевірте і спробуйте знову.",
    "tracking.label": "Відстеження",
    "tracking.status.delivered": "Доставлено",
    "tracking.status.preparation": "Підготовка",
    "tracking.status.shipped": "Відправлено",
    "tracking.status.transit": "В дорозі",
    "transfer.code.copied": "Скопійовано",
    "transfer.code.copy": "Копіювати",
    "transfer.code.received": "Ваш код підтвердження",
    "dash.welcome": "Ласкаво просимо",
    "common.delete": "Видалити",
    "cards.delete.title": "Видалити картку",
    "cards.delete.confirm_msg": "Ця дія є незворотною. Картка буде видалена назавжди.",
    "cards.delete.physical_tooltip": "Фізична картка замовлена — видалення неможливе",
    "cards.order.confirmed": "Замовлення підтверджено!",
    "cards.order.confirmed_body": "Ваша фізична картка готується. Використовуйте цей номер для відстеження доставки.",
    "cards.order.tracking_no": "№ відстеження",
    "cards.fee_deducted": "{amount} буде знято з вашого балансу",
    "cards.insufficient_short": "Недостатній баланс ({available} доступно)",
    "transfer.section.beneficiary": "Одержувач",
    "transfer.section.details": "Переказ",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Банк",
    "transfer.field.city": "Місто",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Банк:",
    "transfer.progress.paused": "Очікує перевірки",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Змінна",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Картку видалено",
    "cards.feat.gold_exclusive": "Ексклюзивна золота картка",
    "cards.feat.cashback_05": "Кешбек 0,5%",
    "cards.feat.buyer_protection": "Захист покупця",
    "cards.feat.support_standard": "Стандартна підтримка",
    "cards.feat.plastic_premium": "Преміум пластикова картка",
    "cards.feat.cashback_1": "Кешбек 1%",
    "cards.feat.travel_standard": "Стандартна туристична страховка",
    "cards.feat.support_priority": "Пріоритетна підтримка",
    "cards.feat.metal_brushed": "Картка з матового металу",
    "cards.feat.cashback_2_unlimited": "Безлімітний кешбек 2%",
    "cards.feat.travel_premium": "Преміум туристична страховка",
    "cards.feat.vip_lounge": "Доступ до VIP-залів",
    "cards.feat.support_247": "Підтримка 24/7",
    "auth.demo.title": "Вхід адміністратора",
    "auth.demo.subtitle": "Введіть облікові дані адміністратора для перегляду демо",
    "credits.garantie.none": "Відсутня",
    "credits.garantie.mortgage": "Іпотека на фінансовану нерухомість",
    "credits.garantie.vehicle": "Фінансований транспортний засіб",
    "credits.garantie.parental": "Батьківська або банківська",
    "credits.garantie.professional": "Професійні активи або особиста порука",
    "cards.reveal.pin_prompt": "Введіть ваш 4-значний PIN-код для відображення інформації про картку.",
    "cards.reveal.show": "Показати",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Два коди PIN не збігаються",
    "auth.pin.digit_hint": "4 цифри",
  
  },
  ru: {
    "notif.transfer_code.title": "Код подтверждения перевода",
    "notif.transfer_code.body": "Скопируйте этот код для подтверждения перевода.",
    "notif.transfer_code.expires_in": "Исчезнет через {sec}с",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} мес.",
    "credits.n_months": "{n} мес.",
    "msg.read": "Прочитано",
    "msg.sent": "Отправлено",
    "msg.attach_aria": "Прикрепить файл",
    "login.device.unknown": "Неизвестный браузер",
    "login.device.mobile": "Мобильный",
    "login.device.tablet": "Планшет",
    "login.device.desktop": "Компьютер",
    "login.recent": "Недавно",
    "err.invalid_credentials": "Неверные учётные данные",
    "err.bad_pin": "Неверный PIN",
    "err.account_blocked": "Аккаунт заблокирован",
    "err.bad_current_pw": "Текущий пароль неверный",
    "err.insufficient_funds": "Недостаточно средств, пополните счёт.",
    "err.amount_above_savings": "Сумма превышает доступные сбережения",
    "err.must_be_positive": "Значение должно быть положительным",
    "err.invalid_format": "Неверный формат",
    "err.email_taken": "Email уже используется",
    "err.card_not_found": "Карта не найдена",
    "err.physical_card_already_ordered": "Физическая карта уже заказана",
    "err.beneficiary_not_found": "Получатель не найден",
    "err.invalid_code": "Неверный код",
    "err.no_transfer_code": "Нет кода для этого перевода",
    "err.link_expired": "Ссылка недействительна или истекла",
    "err.thread_not_found": "Беседа не найдена",
    "err.empty_message": "Пустое сообщение",
    "err.file_too_large": "Файл слишком большой (макс 20 МБ)",
    "err.forbidden": "Доступ запрещён",
    "err.bank_not_found": "Банк не найден",
    "err.signups_closed": "Регистрация в этом банке закрыта.",
    "err.access_suspended": "Доступ временно приостановлен. Пожалуйста, обратитесь в ваш банк.",
    "err.notif_not_closable": "Это уведомление нельзя закрыть.",
    "auth.signup.tagline": "Присоединяйтесь к нам сегодня",
    "auth.login.tagline": "Добро пожаловать!",
    "auth.signup.tagline_sub": "Откройте счёт за 2 минуты и получите доступ ко всем нашим премиум-банковским услугам.",
    "auth.login.tagline_sub": "Войдите в личный кабинет и управляйте своими финансами в полной безопасности.",
    "auth.back": "Вернуться на главную",
    "auth.bullet.security": "Банковская безопасность военного уровня",
    "auth.bullet.transfers": "Мгновенные переводы 24/7",
    "auth.bullet.investments": "Интегрированные инвестиции и сбережения",
    "auth.bullet.advisor": "Личный консультант доступен 7 дней/7",
    "auth.footer": "Работает на {bank}",
    "auth.signup.title": "Создать счёт",
    "auth.login.title": "Вход",
    "auth.field.lastName": "Фамилия",
    "auth.field.firstName": "Имя",
    "auth.field.country": "Страна",
    "auth.field.country.placeholder": "— Выберите вашу страну —",
    "auth.field.phone": "Телефон",
    "auth.field.address": "Адрес",
    "auth.field.address.placeholder": "ул. Тверская, 1, Москва",
    "auth.field.email": "Email",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Пароль",
    "auth.submit.signup": "Создать мой счёт",
    "auth.submit.login": "Войти",
    "auth.login.badge": "Войдите в свой аккаунт",
    "auth.card.tagline": "Ваш личный кабинет",
    "auth.submit.loading": "Загрузка…",
    "auth.pin.title": "ПИН-код",
    "auth.pin.subtitle": "Введите свой 4-значный ПИН-код",
    "auth.pin.label": "ПИН-код",
    "auth.pin.confirm": "Подтвердить PIN-код",
    "auth.pin.submit": "Подтвердить",
    "auth.pin.back": "Назад ко входу",
    "auth.pin.length_error": "ПИН-код должен содержать 4 цифры",
    "auth.switch.have_account": "Уже есть счёт?",
    "auth.switch.login_link": "Вход",
    "auth.switch.no_account": "Ещё не клиент?",
    "auth.error.select_country": "Выберите вашу страну",
    "kyc.title": "Верификация личности (KYC)",
    "kyc.sub": "Загрузите документы для подтверждения счёта. Все документы зашифрованы и конфиденциальны.",
    "kyc.id_type": "Тип удостоверения личности *",
    "kyc.id_card": "Удостоверение личности",
    "kyc.passport": "Паспорт",
    "kyc.driver_license": "Водительское удостоверение",
    "kyc.recto_passport": "Лицевая сторона паспорта *",
    "kyc.recto_id": "Лицевая сторона документа *",
    "kyc.verso": "Обратная сторона документа *",
    "kyc.selfie": "Селфи с документом в руке *",
    "kyc.address_proof": "Подтверждение адреса *",
    "kyc.hint_doc": "Изображение или PDF, макс. 5 МБ",
    "kyc.hint_selfie": "Чёткое фото, лицо и документ видны",
    "kyc.hint_address": "Счёт, выписка, справка… (макс. 5 МБ)",
    "kyc.upload": "Загрузить",
    "kyc.replace": "Заменить",
    "pending.title": "Счёт на рассмотрении",
    "pending.body": "Ваш запрос получен. Администратор проверит документы и активирует счёт в ближайшее время.",
    "pending.back": "Вернуться ко входу",
    "home.nav.features": "Возможности",
    "home.nav.security": "Безопасность",
    "home.nav.invest": "Инвестиции",
    "home.nav.credits": "Кредиты",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Войти",
    "home.hero.tagline": "Онлайн-банкинг нового поколения",
    "home.hero.title1": "Управляйте своими деньгами",
    "home.hero.title2": "с умом",
    "home.hero.sub": "Полноценная банковская платформа — переводы, сбережения, инвестиции и кредиты со смартфона.",
    "home.hero.login": "Войти",
    "home.hero.trust.secure": "100% Безопасно",
    "home.hero.trust.fast": "Быстрое открытие",
    "home.hero.trust.encrypted": "Зашифрованные данные",
    "home.hero.trust.support": "Поддержка 24/7",
    "home.mockup.balance": "Доступный остаток",
    "home.mockup.currency": "EUR · Основной счёт",
    "home.mockup.recharge": "Пополнить",
    "home.mockup.send": "Отправить",
    "home.mockup.save": "Сохранить",
    "home.mockup.salary": "Зарплата Март",
    "home.mockup.rent": "Аренда Апрель",
    "home.mockup.investment": "Инвестиция",
    "home.mockup.days2": "2 дня назад",
    "home.mockup.days3": "3 дня назад",
    "home.mockup.days5": "5 дней назад",
    "home.stats.accounts": "Активных счетов",
    "home.stats.uptime": "Доступность",
    "home.stats.transfers": "Переводов",
    "home.stats.support": "Поддержка",
    "home.features.label": "Возможности",
    "home.features.title": "Всё, что вам нужно",
    "home.features.sub": "Полноценная платформа для упрощения управления финансами каждый день.",
    "home.feature.security": "Военная безопасность",
    "home.feature.security_body": "Шифрование AES-256, двухфакторная аутентификация и круглосуточный мониторинг счёта.",
    "home.feature.cards": "Виртуальные и физические карты",
    "home.feature.cards_body": "Выпускайте карты за секунды или закажите настоящую физическую карту с доставкой.",
    "home.feature.transfers": "Мгновенные переводы",
    "home.feature.transfers_body": "Отправляйте и получайте деньги в любую точку мира за секунды.",
    "home.feature.analytics": "Анализ в реальном времени",
    "home.feature.analytics_body": "Умные дашборды для отслеживания расходов и планирования финансов.",
    "home.feature.investments": "Интегрированные инвестиции",
    "home.feature.investments_body": "Получите доступ к отобранным инвестиционным продуктам прямо со счёта.",
    "home.feature.support": "Поддержка 24/7",
    "home.feature.support_body": "Личный банковский консультант доступен в любое время через защищённый мессенджер.",
    "home.tpl.label": "Доверие",
    "home.tpl.title": "Нам доверяют",
    "home.mockup.balance_label": "Остаток",
    "home.mockup.virement": "Перевод",
    "home.mockup.salaire": "Зарплата",
    "home.security.label": "Безопасность",
    "home.security.title1": "Ваши финансы",
    "home.security.title2": "под абсолютной защитой",
    "home.security.sub": "Мы применяем самые строгие стандарты банковской безопасности для защиты каждого цента.",
    "home.security.aes.title": "Шифрование AES-256",
    "home.security.aes.body": "Все конфиденциальные данные зашифрованы теми же алгоритмами, которые используют крупнейшие международные банки.",
    "home.security.auth.title": "Усиленная аутентификация",
    "home.security.auth.body": "Двойная проверка личности при каждом входе и для каждой чувствительной операции на счёте.",
    "home.security.compliance.title": "Соответствие PSD2/GDPR",
    "home.security.compliance.body": "Мы соблюдаем все европейские нормы в области платёжных услуг и защиты данных.",
    "home.invest.label": "Инвестиции",
    "home.invest.title": "Приумножьте свои деньги",
    "home.invest.sub": "6 категорий инвестиций, отобранных по потенциалу и надёжности.",
    "home.invest.yield": "Доходность",
    "home.credits.label": "Кредиты",
    "home.credits.title": "Решения для каждого проекта",
    "home.credits.sub": "Конкурентные ставки, быстрые ответы и персонализированное сопровождение для всех ваших проектов.",
    "home.credits.rate": "Ставка",
    "home.faq.label": "FAQ",
    "home.faq.title": "Часто задаваемые вопросы",
    "home.faq.q1": "Как открыть счёт?",
    "home.faq.a1": "Регистрация занимает менее 2 минут. Просто заполните личные данные, выберите пароль — и счёт будет активирован после проверки нашей командой.",
    "home.faq.q2": "Защищены ли мои данные?",
    "home.faq.a2": "Да. Все данные зашифрованы с помощью AES-256 в покое и TLS 1.3 при передаче. Мы соблюдаем международные банковские стандарты и нормы GDPR/PSD2.",
    "home.faq.q3": "Какие типы инвестиций вы предлагаете?",
    "home.faq.a3": "Мы предлагаем 6 категорий: Недвижимость, Краудфандинг, Возобновляемая энергия, Фондовый рынок, Партиципативные и Крипто. Каждый продукт отбирается по соотношению риска и доходности.",
    "home.faq.q4": "Как подать заявку на кредит?",
    "home.faq.a4": "Перейдите в раздел Кредит в личном кабинете, выберите тип займа, укажите сумму и срок. Заявка будет рассмотрена в течение 24 часов нашими консультантами.",
    "home.faq.q5": "Есть ли скрытые комиссии?",
    "home.faq.a5": "Никаких скрытых комиссий. Все тарифы чётко отображаются при каждой операции. Условия доступны в личном кабинете в любое время.",
    "home.faq.q6": "Как связаться со службой поддержки?",
    "home.faq.a6": "Используйте встроенный мессенджер в личном кабинете для прямой связи с личным банковским консультантом, доступным 24/7.",
    "home.cta.title": "Готовы начать?",
    "home.cta.sub": "Присоединяйтесь к тысячам пользователей, управляющих финансами с {bank}. Регистрация занимает менее 2 минут.",
    "home.footer.products": "Продукты",
    "home.footer.services": "Услуги",
    "home.footer.access": "Быстрый доступ",
    "home.footer.accounts": "Счета",
    "home.footer.transfers": "Переводы",
    "home.footer.cards": "Карты",
    "home.footer.savings": "Сбережения",
    "home.footer.investments": "Инвестиции",
    "home.footer.credits": "Кредиты",
    "home.footer.financing": "Финансирование",
    "home.footer.messages": "Сообщения",
    "home.footer.login": "Вход",
    "home.footer.signup": "Открыть счёт",
    "home.footer.support": "Поддержка 24/7",
    "home.footer.tagline": "Ваш онлайн-банк, доступный везде и в любое время.",
    "home.footer.copyright": "© {year} {bank} — Все права защищены",
    "home.footer.powered": "Работает на {bank}",
    "app.menu_label": "Главное меню",
    "app.nav.dashboard": "Дашборд",
    "app.nav.accounts": "Счета",
    "app.nav.transactions": "Транзакции",
    "app.nav.transfers": "Переводы",
    "app.nav.cards": "Карты",
    "app.nav.savings": "Сбережения",
    "app.nav.investments": "Инвестиции",
    "app.nav.credit": "Кредит",
    "app.nav.financing": "Финансирование",
    "app.nav.messages": "Сообщения",
    "app.nav.card_tracking": "Отслеживание карты",
    "app.nav.notifications": "Уведомления",
    "app.nav.settings": "Настройки",
    "app.nav.profile": "Профиль",
    "app.nav.mobile.home": "Главная",
    "app.nav.mobile.transfers": "Переводы",
    "app.nav.mobile.cards": "Карты",
    "app.nav.mobile.transactions": "Транзакции",
    "app.nav.more.invest": "Инвестировать",
    "app.user_role": "Клиент",
    "app.logout": "Выйти",
    "app.search": "Поиск…",
    "app.more_title": "Больше возможностей",
    "app.more_btn": "Ещё",
    "app.loading": "Загрузка…",
    "app.error_back": "Вернуться ко входу",
    "app.see_more_notifs": "Посмотреть ещё {count} уведомлений →",
    "page.accounts": "Мои счета",
    "page.transactions": "Транзакции",
    "page.transfers": "Новый перевод",
    "page.deposit": "Депозит",
    "page.cards": "Мои карты",
    "page.savings": "Сбережения",
    "page.investments": "Инвестиции",
    "page.credits": "Кредит",
    "page.financing": "Финансирование проектов",
    "page.messages": "Сообщения",
    "page.settings": "Настройки",
    "page.notifications": "Важные уведомления",
    "page.profile": "Мой профиль",
    "dash.greeting": "Привет, {name}",
    "dash.overview": "Обзор ваших финансов — {date}",
    "dash.total_balance": "Общий остаток",
    "dash.this_month": "+{amount} в этом месяце",
    "dash.view_iban": "Посмотреть мой IBAN",
    "dash.my_accounts": "Мои счета",
    "dash.primary_account": "Основной счёт",
    "dash.savings_account": "Сберегательный счёт",
    "dash.remunerated": "С начислением процентов",
    "dash.details": "Детали",
    "dash.transfer_btn": "Перевод",
    "dash.quick_actions": "Быстрые действия",
    "dash.quick.deposit": "Депозит",
    "deposit.title": "Информация о депозите",
    "dash.quick.transfer": "Перевод",
    "dash.quick.history": "История",
    "dash.quick.cards": "Мои карты",
    "dash.quick.profile": "Профиль",
    "dash.monthly_budget": "Месячный бюджет",
    "dash.income": "Поступления",
    "dash.expenses": "Расходы",
    "dash.spending_cat": "Расходы по категориям",
    "dash.no_spending": "Расходы не зарегистрированы.",
    "dash.recent_tx": "Последние транзакции",
    "dash.see_all": "Посмотреть все",
    "dash.no_tx": "Операций нет.",
    "dash.main_card": "Моя основная карта",
    "dash.manage": "Управлять",
    "dash.block": "Заблокировать",
    "dash.pin": "PIN-код",
    "dash.security": "Безопасность",
    "dash.more": "Ещё",
    "dash.no_card": "Карта не выпущена",
    "dash.monthly_activity": "Активность за месяц",
    "dash.operations": "Операции",
    "dash.holder": "Владелец",
    "dash.expires": "Срок",
    "dash.visa_label": "VISA",
    "dash.balance": "Доступный остаток",
    "tx.all": "Все",
    "tx.income": "Поступления",
    "tx.expenses": "Расходы",
    "tx.today": "Сегодня",
    "tx.yesterday": "Вчера",
    "tx.none": "Транзакций нет.",
    "transfer.title": "Быстрый перевод",
    "transfer.recipient": "Получатель",
    "transfer.recipient_ph": "Имя, IBAN, номер телефона…",
    "transfer.amount": "Сумма",
    "transfer.label": "Назначение",
    "transfer.reason": "Причина (необязательно)",
    "transfer.reason_ph": "Напр.: аренда, возврат долга…",
    "transfer.execution": "Исполнение",
    "transfer.immediate": "Немедленно",
    "transfer.delayed": "Отложено",
    "transfer.balance": "Доступный остаток:",
    "transfer.continue": "Продолжить",
    "transfer.confirm": "Подтвердить",
    "transfer.cancel": "Отменить",
    "transfer.code": "Код подтверждения",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Введите полученный код для подтверждения перевода",
    "transfer.pending.title": "Перевод ожидает подтверждения",
    "transfer.pending.amount": "Сумма:",
    "transfer.pending.to": "Кому:",
    "transfer.pending.hint": "Ваш код подтверждения будет отправлен по электронной почте или через колокольчик уведомлений. Код состоит из 6 цифр. Если вы его не получили, обратитесь в службу поддержки.",
    "transfer.pending.validate": "Подтвердить перевод",
    "transfer.saved": "Перевод сохранён. Запросите код у администратора для подтверждения.",
    "transfer.done": "Перевод выполнен",
    "transfer.validated": "Перевод подтверждён!",
    "transfer.progress.processing": "Обработка перевода…",
    "transfer.progress.completed": "Перевод выполнен!",
    "transfer.progress.default_message": "Ваш перевод обрабатывается. Пожалуйста, подождите.",
    "cards.none": "Карта не выпущена.\nОбратитесь к администратору.",
    "cards.holder": "Владелец",
    "cards.expires": "Срок",
    "cards.block": "Заблокировать",
    "cards.pin": "PIN-код",
    "cards.more": "Ещё",
    "cards.limits": "Лимиты карты",
    "cards.payment": "Оплата",
    "cards.withdrawal": "Снятие наличных",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Заблокирована",
    "cards.status_active": "Активна",
    "cards.toast.blocked": "Карта заблокирована",
    "cards.toast.unblocked": "Карта разблокирована",
    "cards.toast.physical_ordered": "Физическая карта заказана! Доставка за 5–10 рабочих дней.",
    "cards.toast.created": "Карта успешно создана!",
    "cards.none_sub": "Нет выпущенной карты. Создайте виртуальную карту сейчас.",
    "cards.generate_btn": "Создать мою карту",
    "cards.create.title": "Создать мою виртуальную карту",
    "cards.create.network": "Платёжная сеть",
    "cards.create.color": "Цвет карты",
    "cards.create.preview_name": "ВАШЕ ИМЯ",
    "cards.create.preview_expiry": "ММ/ГГ",
    "cards.create.loading": "Создание…",
    "cards.create.submit": "Создать карту",
    "cards.btn.unblock": "Разблокировать",
    "cards.btn.block": "Заблокировать",
    "cards.btn.info": "Инфо",
    "cards.btn.physical": "Физическая",
    "cards.btn.physical_done": "Заказана",
    "cards.limits_title": "Лимиты",
    "cards.reveal.title": "Информация о карте",
    "cards.reveal.number": "Полный номер",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Срок действия",
    "cards.reveal.warning": "Никогда не делитесь этой информацией. Закройте это окно после использования.",
    "cards.pin.title": "PIN-код",
    "cards.pin.message": "Этот PIN-код используется для всех ваших онлайн-платежей и снятий. Никогда никому его не сообщайте.",
    "cards.physical.title": "Заказать физическую карту",
    "cards.physical.fee_label": "Плата за доставку",
    "cards.physical.balance_label": "Ваш баланс",
    "cards.physical.insufficient": "Недостаточный баланс. Пожалуйста, пополните счёт перед заказом.",
    "cards.physical.info": "Плата будет списана с вашего счёта. Доставка за 5–10 рабочих дней.",
    "cards.physical.order_btn": "Заказать ({fee})",
    "cards.physical.ordering": "Заказываю…",
    "cards.physical.order_cta": "Заказать физическую карту",
    "cards.physical.choose_type": "Выберите вашу карту",
    "cards.physical.features": "Преимущества",
    "cards.buy_virtual_cta": "Купить виртуальную карту",
    "notif.none": "Уведомлений пока нет.",
    "notif.contact_support": "Связаться с поддержкой",
    "profile.change_photo": "Изменить фото",
    "profile.remove_photo": "Удалить фото",
    "profile.email": "Email",
    "profile.phone": "Телефон",
    "profile.country": "Страна",
    "profile.address": "Адрес",
    "profile.notifs_title": "Уведомления ({count})",
    "profile.no_notifs": "Уведомлений нет.",
    "profile.logout": "Выйти",
    "profile.photo_updated": "Фото профиля обновлено",
    "profile.photo_removed": "Фото удалено",
    "profile.photo_too_large": "Изображение слишком большое (макс. 1 МБ)",
    "profile.powered": "Работает на {bank}",
    "iban.title": "Мой IBAN",
    "iban.loading": "Загрузка…",
    "iban.bic": "Код BIC / SWIFT",
    "iban.share_info": "Поделитесь этими реквизитами, чтобы получить перевод на счёт.",
    "iban.copy": "Копировать",
    "iban.copied": "Скопировано в буфер обмена",
    "iban.copy_fail": "Не удалось скопировать",
    "iban.close": "Закрыть",
    "accounts.all": "Все ваши счета в {bank}.",
    "accounts.primary": "Основной счёт",
    "accounts.savings": "Сберегательный счёт",
    "accounts.primary_tag": "Основной",
    "accounts.secondary_tag": "Дополнительный",
    "accounts.objective": "Цель: {amount} · {pct}%",
    "accounts.copy": "Копировать",
    "accounts.loading": "Загрузка…",
    "accounts.iban_copied": "IBAN скопирован",
    "accounts.copy_fail": "Не удалось скопировать",
    "savings.total": "Общая сумма сбережений",
    "savings.target_total": "Общая цель",
    "savings.progress": "Прогресс",
    "savings.active": "Активные цели",
    "savings.new_goal": "Новая цель",
    "savings.none": "Целей сбережений пока нет.",
    "savings.none_sub": "Создайте первую цель, чтобы начать.",
    "savings.saved": "Накоплено:",
    "savings.goal": "Цель:",
    "savings.deadline": "Дедлайн: {date}",
    "savings.deposit": "Пополнить",
    "savings.withdraw": "Снять",
    "savings.loading": "Загрузка…",
    "savings.form.title": "Новая цель сбережений",
    "savings.form.cancel": "Отменить",
    "savings.form.create": "Создать",
    "savings.form.name": "Название цели",
    "savings.form.name_ph": "Поездка в Японию, Автомобиль, …",
    "savings.form.target": "Целевая сумма",
    "savings.form.initial": "Начальный взнос",
    "savings.form.deadline": "Дата окончания (необязательно)",
    "savings.form.color": "Цвет",
    "savings.deposit_title": "Пополнить \"{name}\"",
    "savings.deposit_amount": "Сумма пополнения",
    "savings.balance_hint": "Остаток на основном счёте: {amount}",
    "savings.withdraw_prompt": "Сколько снять с \"{name}\"?",
    "savings.close_confirm": "Закрыть цель \"{name}\"? Сбережения будут возвращены на основной счёт.",
    "savings.toast.created": "Цель создана",
    "savings.toast.deposited": "Пополнение выполнено",
    "savings.toast.withdrawn": "Снятие выполнено",
    "savings.toast.closed": "Цель закрыта",
    "invest.total": "Общая сумма инвестиций",
    "invest.profits": "Накопленная прибыль",
    "invest.count": "Инвестиции",
    "invest.products": "Доступные продукты",
    "invest.invest_btn": "Инвестировать",
    "invest.my": "Мои инвестиции",
    "invest.pending": "На рассмотрении",
    "invest.validated": "Подтверждено",
    "invest.refused": "Отказано",
    "invest.withdraw.btn": "Вывести",
    "invest.withdraw.title": "Вывести мои инвестиции",
    "invest.withdraw.intro": "Вы собираетесь вывести свою инвестицию «{product}». Начальный капитал и накопленная прибыль будут зачислены на ваш основной баланс.",
    "invest.withdraw.principal": "Начальный капитал",
    "invest.withdraw.profit": "Прибыль",
    "invest.withdraw.total": "Итого к получению",
    "invest.withdraw.confirm": "Подтвердить вывод",
    "invest.withdraw.warning": "Это действие необратимо. Инвестиция будет закрыта.",
    "invest.withdraw.success": "Инвестиция выведена — {amount} зачислено на баланс.",
    "invest.withdrawn": "Выведено",
    "invest.cta_sub": "Выберите подходящий тип инвестиции. Консультант поможет с первого запроса.",
    "invest.form.amount": "Сумма для инвестирования",
    "invest.form.balance": "Доступный остаток: {amount}",
    "invest.form.note": "Заметка для консультанта (необязательно)",
    "invest.form.note_ph": "Уточните ваши ожидания…",
    "invest.form.advisor_info": "Диалог с консультантом будет открыт автоматически.",
    "invest.form.cancel": "Отменить",
    "invest.form.submit": "Отправить запрос",
    "invest.toast.requested": "Запрос зарегистрирован — диалог с консультантом открыт.",
    "invest.p1.name": "Недвижимость",
    "invest.p1.desc": "SCPI, бумажная недвижимость, краудфандинг недвижимости",
    "invest.p2.name": "Краудфандинг",
    "invest.p2.desc": "Партиципативное финансирование отобранных проектов",
    "invest.p3.name": "Возобновляемая энергия",
    "invest.p3.desc": "Солнечная, ветровая и зелёные проекты",
    "invest.p4.name": "Фондовый рынок",
    "invest.p4.desc": "Акции, ETF и диверсифицированные фонды",
    "invest.p5.name": "Партиципативные",
    "invest.p5.desc": "Займы для МСП и местных предпринимателей",
    "invest.p6.name": "Крипто",
    "invest.p6.desc": "Bitcoin, Ethereum и мультивалютный кошелёк",
    "msg.online": "Онлайн",
    "msg.loading": "Загрузка…",
    "msg.none": "Сообщений нет. Начните разговор с консультантом.",
    "msg.placeholder": "Напишите сообщение…",
    "msg.attach.image": "Изображение",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Аудио",
    "msg.attach.file": "Файл / Документ",
    "msg.pdf_doc": "PDF-документ",
    "msg.file": "Файл",
    "msg.too_large": "Файл слишком большой (макс. {size}).",
    "msg.withdraw": "Удалить",
    "credits.cta_sub": "Подайте заявку онлайн — ответ в течение 24 часов.",
    "credits.learn_more": "Узнать больше",
    "credits.request": "Подать заявку",
    "credits.history": "Мои заявки",
    "credits.status.pending": "На рассмотрении",
    "credits.status.accepted": "Принято",
    "credits.status.refused": "Отказано",
    "credits.chars": "Характеристики",
    "credits.amount_range": "Сумма: {min}€ – {max}€",
    "credits.rate_range": "Процентная ставка: {min}% – {max}% в год",
    "credits.duration_range": "Срок: от {min} до {max} месяцев",
    "credits.deposit_min": "Начальный взнос: {amount}€ минимум",
    "credits.garantie": "Залог: {garantie}",
    "credits.eligibility": "Условия соответствия",
    "credits.elig.income": "Подтвердить регулярный и стабильный доход",
    "credits.elig.deposit": "Внести начальный взнос в размере {amount}€",
    "credits.elig.garantie": "Предоставить залог: {garantie}",
    "credits.form.title": "Заявка — {name}",
    "credits.form.cancel": "Отменить",
    "credits.form.submit": "Отправить заявку",
    "credits.form.amount": "Желаемая сумма (€)",
    "credits.form.duration": "Срок (месяцев)",
    "credits.form.reason": "Причина (необязательно)",
    "credits.form.reason_ph": "Опишите ваш проект…",
    "credits.form.close": "Закрыть",
    "credits.toast.submitted": "Заявка на кредит отправлена — вы получите ответ в течение 24 часов.",
    "credits.p1.name": "Потребительский кредит",
    "credits.p1.desc": "Для финансирования повседневных нужд или личных проектов.",
    "credits.p2.name": "Ипотечный кредит",
    "credits.p2.desc": "Для финансирования покупки или строительства недвижимости.",
    "credits.p3.name": "Автокредит / Мотокредит",
    "credits.p3.desc": "Купите транспортное средство без лишних хлопот.",
    "credits.p4.name": "Студенческий кредит",
    "credits.p4.desc": "Финансовая помощь для получения высшего образования.",
    "credits.p5.name": "Бизнес-кредит",
    "credits.p5.desc": "Для поддержки роста профессиональной деятельности.",
    "credits.p6.name": "Экспресс-кредит",
    "credits.p6.desc": "Быстрое решение для срочных нужд.",
    "financing.reimbursable": "Возвратное финансирование",
    "financing.reimbursable_desc": "Финансовая поддержка с фиксированными условиями для запуска или развития деятельности.",
    "financing.non_reimbursable": "Безвозвратное финансирование",
    "financing.non_reimbursable_desc": "Для проектов с социальным, образовательным или гуманитарным воздействием (НКО, ассоциации).",
    "financing.cta_sub": "Индивидуальные решения для воплощения ваших амбиций.",
    "financing.request": "Запросить финансирование",
    "financing.history": "Мои заявки",
    "financing.reimbursable_label": "Возвратное",
    "financing.non_reimbursable_label": "Безвозвратное",
    "financing.status.pending": "На рассмотрении",
    "financing.status.granted": "Предоставлено",
    "financing.status.refused": "Отказано",
    "financing.form.title": "Заявка — {name}",
    "financing.form.cancel": "Отменить",
    "financing.form.submit": "Отправить заявку",
    "financing.form.project": "Название проекта",
    "financing.form.project_ph": "Мой проект",
    "financing.form.amount": "Запрашиваемая сумма (€)",
    "financing.form.sector": "Сектор",
    "financing.form.sector_ph": "Технологии, сельское хозяйство, …",
    "financing.form.description": "Описание проекта",
    "financing.form.description_ph": "Представьте ваш проект, его цели и ожидаемый эффект…",
    "settings.security": "Безопасность",
    "settings.current_pw": "Текущий пароль",
    "settings.new_pw": "Новый пароль",
    "settings.confirm_pw": "Подтвердить",
    "settings.submit": "Изменить пароль",
    "settings.no_match": "Пароли не совпадают",
    "settings.changed": "Пароль изменён",
    "common.cancel": "Отменить",
    "common.confirm": "Подтвердить",
    "common.close": "Закрыть",
    "common.save": "Сохранить",
    "common.loading": "Загрузка…",
    "common.send": "Отправить",
    "common.total": "Итого",
    "admin.wallet.balance": "Доступный баланс",
    "admin.wallet.history": "История",
    "admin.wallet.no_history": "Нет транзакций",
    "admin.wallet.title": "Мой кошелёк",
    "admin.wallet.topup": "Пополнить",
    "beneficiary.pick": "Выбрать",
    "beneficiary.save_this": "Сохранить получателя",
    "beneficiary.saved": "Сохранённые получатели",
    "beneficiary.saved_ok": "Получатель сохранён",
    "card.color.amber": "Янтарный",
    "card.color.cyan": "Голубой",
    "card.color.emerald": "Изумрудный",
    "card.color.forest": "Лесной",
    "card.color.gold": "Золотой",
    "card.color.lavender": "Лавандовый",
    "card.color.night": "Ночной",
    "card.color.orange": "Оранжевый",
    "card.color.pink": "Розовый",
    "card.color.royal": "Королевский синий",
    "card.color.ruby": "Рубиновый",
    "card.color.slate": "Сланцевый",
    "card.color.steel": "Стальной",
    "card.color.terracotta": "Терракотовый",
    "card.color.violet": "Фиолетовый",
    "common.other": "Другое",
    "common.search": "Поиск",
    "content.translated": "Автоматически переведённый контент",
    "credits.monthly_payment": "Ориентировочный ежемесячный платёж",
    "credits.rate_annual": "/ год",
    "credits.rate_used": "Ориентировочная ставка:",
    "credits.simulation": "Расчёт ежемесячного платежа",
    "credits.total_cost": "Ориентировочная общая стоимость",
    "demo.banner": "Демо-режим — никаких реальных действий",
    "demo.disabled": "Действие недоступно в демо-режиме",
    "deposit.default_instructions": "Свяжитесь с вашим менеджером для получения инструкций по пополнению.",
    "error.back_home": "На главную",
    "error.bank_not_found": "Банк не найден",
    "error.unknown": "Неизвестная ошибка",
    "kyc.file_read_error": "Не удалось прочитать файл",
    "kyc.file_too_large": "Файл слишком большой (макс. 5 МБ)",
    "login.history": "История входов",
    "login.history.empty": "Нет записей о входах.",
    "notif.dismiss": "Закрыть",
    "notif.locked": "Сообщение банка",
    "tracking.error.not_found": "Номер отслеживания не найден. Проверьте и попробуйте снова.",
    "tracking.label": "Отслеживание",
    "tracking.status.delivered": "Доставлено",
    "tracking.status.preparation": "Подготовка",
    "tracking.status.shipped": "Отправлено",
    "tracking.status.transit": "В пути",
    "transfer.code.copied": "Скопировано",
    "transfer.code.copy": "Копировать",
    "transfer.code.received": "Ваш код подтверждения",
    "dash.welcome": "Добро пожаловать",
    "common.delete": "Удалить",
    "cards.delete.title": "Удалить карту",
    "cards.delete.confirm_msg": "Это действие необратимо. Карта будет удалена навсегда.",
    "cards.delete.physical_tooltip": "Физическая карта заказана — удаление невозможно",
    "cards.order.confirmed": "Заказ подтверждён!",
    "cards.order.confirmed_body": "Ваша физическая карта готовится. Используйте этот номер для отслеживания доставки.",
    "cards.order.tracking_no": "№ отслеживания",
    "cards.fee_deducted": "{amount} будет списано с вашего баланса",
    "cards.insufficient_short": "Недостаточный баланс ({available} доступно)",
    "transfer.section.beneficiary": "Получатель",
    "transfer.section.details": "Перевод",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Банк",
    "transfer.field.city": "Город",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Банк:",
    "transfer.progress.paused": "Ожидает проверки",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Переменная",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Карта удалена",
    "cards.feat.gold_exclusive": "Эксклюзивная золотая карта",
    "cards.feat.cashback_05": "Кэшбэк 0,5%",
    "cards.feat.buyer_protection": "Защита покупателя",
    "cards.feat.support_standard": "Стандартная поддержка",
    "cards.feat.plastic_premium": "Премиум пластиковая карта",
    "cards.feat.cashback_1": "Кэшбэк 1%",
    "cards.feat.travel_standard": "Стандартная туристическая страховка",
    "cards.feat.support_priority": "Приоритетная поддержка",
    "cards.feat.metal_brushed": "Карта из матового металла",
    "cards.feat.cashback_2_unlimited": "Безлимитный кэшбэк 2%",
    "cards.feat.travel_premium": "Премиум туристическая страховка",
    "cards.feat.vip_lounge": "Доступ в VIP-залы",
    "cards.feat.support_247": "Поддержка 24/7",
    "auth.demo.title": "Вход администратора",
    "auth.demo.subtitle": "Введите учётные данные администратора для предпросмотра демо",
    "credits.garantie.none": "Отсутствует",
    "credits.garantie.mortgage": "Ипотека на финансируемое имущество",
    "credits.garantie.vehicle": "Финансируемое транспортное средство",
    "credits.garantie.parental": "Родительская или банковская",
    "credits.garantie.professional": "Профессиональные активы или личное поручительство",
    "cards.reveal.pin_prompt": "Введите ваш 4-значный PIN-код для отображения информации о карте.",
    "cards.reveal.show": "Показать",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Два PIN-кода не совпадают",
    "auth.pin.digit_hint": "4 цифры",
  
  },
  be: {
    "notif.transfer_code.title": "Код пацверджання перакладу",
    "notif.transfer_code.body": "Скапіруйце гэты код для пацверджання перакладу.",
    "notif.transfer_code.expires_in": "Знікае праз {sec}с",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} мес.",
    "credits.n_months": "{n} мес.",
    "msg.read": "Прачытана",
    "msg.sent": "Адпраўлена",
    "msg.attach_aria": "Прымацаваць файл",
    "login.device.unknown": "Невядомы браўзер",
    "login.device.mobile": "Мабільны",
    "login.device.tablet": "Планшэт",
    "login.device.desktop": "Камп'ютар",
    "login.recent": "Нядаўна",
    "err.invalid_credentials": "Няправільныя ўліковыя дадзеныя",
    "err.bad_pin": "Няправільны PIN",
    "err.account_blocked": "Уліковы запіс заблакаваны",
    "err.bad_current_pw": "Цяперашні пароль няправільны",
    "err.insufficient_funds": "Недастаткова сродкаў, папоўніце рахунак.",
    "err.amount_above_savings": "Сума перавышае даступныя зберажэнні",
    "err.must_be_positive": "Значэнне павінна быць станоўчым",
    "err.invalid_format": "Няправільны фармат",
    "err.email_taken": "Email ужо выкарыстоўваецца",
    "err.card_not_found": "Карта не знойдзена",
    "err.physical_card_already_ordered": "Фізічная карта ўжо заказана",
    "err.beneficiary_not_found": "Атрымальнік не знойдзены",
    "err.invalid_code": "Няправільны код",
    "err.no_transfer_code": "Няма кода для гэтага пераводу",
    "err.link_expired": "Спасылка несапраўдная або пратэрмінаваная",
    "err.thread_not_found": "Размова не знойдзена",
    "err.empty_message": "Пустое паведамленне",
    "err.file_too_large": "Файл занадта вялікі (макс 20 МБ)",
    "err.forbidden": "Доступ забаронены",
    "err.bank_not_found": "Банк не знойдзены",
    "err.signups_closed": "Рэгістрацыя ў гэтым банку закрытая.",
    "err.access_suspended": "Доступ часова прыпынены. Калі ласка, звярніцеся ў ваш банк.",
    "err.notif_not_closable": "Гэтае апавяшчэнне нельга закрыць.",
    "auth.signup.tagline": "Далучайцеся да нас сёння",
    "auth.login.tagline": "Сардэчна запрашаем!",
    "auth.signup.tagline_sub": "Адкрыйце свой рахунак за 2 хвіліны і атрымайце доступ да ўсіх нашых прэміум-банкаўскіх паслуг.",
    "auth.login.tagline_sub": "Увайдзіце ў свой асабісты кабінет і кіруйце сваімі фінансамі ў поўнай бяспецы.",
    "auth.back": "Вярнуцца на галоўную",
    "auth.bullet.security": "Банкаўская бяспека ваеннага ўзроўню",
    "auth.bullet.transfers": "Імгненныя пераводы 24/7",
    "auth.bullet.investments": "Інтэграваныя інвестыцыі і зберажэнні",
    "auth.bullet.advisor": "Асабісты кансультант даступны 7 дзён/7",
    "auth.footer": "Працуе на {bank}",
    "auth.signup.title": "Стварыць рахунак",
    "auth.login.title": "Уваход",
    "auth.field.lastName": "Прозвішча",
    "auth.field.firstName": "Імя",
    "auth.field.country": "Краіна",
    "auth.field.country.placeholder": "— Абярыце вашу краіну —",
    "auth.field.phone": "Тэлефон",
    "auth.field.address": "Адрас",
    "auth.field.address.placeholder": "вул. Францыска Скарыны, 1, Мінск",
    "auth.field.email": "Email",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Пароль",
    "auth.submit.signup": "Стварыць мой рахунак",
    "auth.submit.login": "Увайсці",
    "auth.login.badge": "Увайдзіце ў свой акаўнт",
    "auth.card.tagline": "Ваш асабісты кабінет",
    "auth.submit.loading": "Загрузка…",
    "auth.pin.title": "ПІН-код",
    "auth.pin.subtitle": "Увядзіце свой 4-значны ПІН-код",
    "auth.pin.label": "ПІН-код",
    "auth.pin.confirm": "Пацвердзіць PIN-код",
    "auth.pin.submit": "Пацвердзіць",
    "auth.pin.back": "Назад да ўваходу",
    "auth.pin.length_error": "ПІН-код павінен мець 4 лічбы",
    "auth.switch.have_account": "Ужо маеце рахунак?",
    "auth.switch.login_link": "Уваход",
    "auth.switch.no_account": "Яшчэ не кліент?",
    "auth.error.select_country": "Абярыце вашу краіну",
    "kyc.title": "Верыфікацыя асобы (KYC)",
    "kyc.sub": "Загрузіце свае дакументы для пацверджання рахунку. Усе дакументы зашыфраваны і канфідэнцыяльны.",
    "kyc.id_type": "Тып дакумента, які пацвярджае асобу *",
    "kyc.id_card": "Пасведчанне асобы",
    "kyc.passport": "Пашпарт",
    "kyc.driver_license": "Пасведчанне кіроўцы",
    "kyc.recto_passport": "Лічбавы бок пашпарта *",
    "kyc.recto_id": "Лічбавы бок дакумента *",
    "kyc.verso": "Адваротны бок дакумента *",
    "kyc.selfie": "Сэлфі з дакументам у руцэ *",
    "kyc.address_proof": "Пацверджанне адраса *",
    "kyc.hint_doc": "Выява або PDF, макс. 5 МБ",
    "kyc.hint_selfie": "Выразнае фота, твар і дакумент бачны",
    "kyc.hint_address": "Рахунак, выпіска, даведка… (макс. 5 МБ)",
    "kyc.upload": "Загрузіць",
    "kyc.replace": "Замяніць",
    "pending.title": "Рахунак на разглядзе",
    "pending.body": "Ваш запыт атрыманы. Адміністратар праверыць вашы дакументы і актывуе рахунак у бліжэйшы час.",
    "pending.back": "Вярнуцца да ўваходу",
    "home.nav.features": "Магчымасці",
    "home.nav.security": "Бяспека",
    "home.nav.invest": "Інвестыцыі",
    "home.nav.credits": "Крэдыты",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Увайсці",
    "home.hero.tagline": "Анлайн-банкінг новага пакалення",
    "home.hero.title1": "Кіруйце сваімі грашыма",
    "home.hero.title2": "з розумам",
    "home.hero.sub": "Паўнавартасная банкаўская платформа — пераводы, зберажэнні, інвестыцыі і крэдыты са смартфона.",
    "home.hero.login": "Увайсці",
    "home.hero.trust.secure": "100% Бяспечна",
    "home.hero.trust.fast": "Хуткае адкрыццё",
    "home.hero.trust.encrypted": "Зашыфраваныя даныя",
    "home.hero.trust.support": "Падтрымка 24/7",
    "home.mockup.balance": "Даступны астатак",
    "home.mockup.currency": "EUR · Асноўны рахунак",
    "home.mockup.recharge": "Папоўніць",
    "home.mockup.send": "Адправіць",
    "home.mockup.save": "Захаваць",
    "home.mockup.salary": "Зарплата Сакавік",
    "home.mockup.rent": "Арэнда Красавік",
    "home.mockup.investment": "Інвестыцыя",
    "home.mockup.days2": "2 дні таму",
    "home.mockup.days3": "3 дні таму",
    "home.mockup.days5": "5 дзён таму",
    "home.stats.accounts": "Актыўных рахункаў",
    "home.stats.uptime": "Даступнасць",
    "home.stats.transfers": "Пераводаў",
    "home.stats.support": "Падтрымка",
    "home.features.label": "Магчымасці",
    "home.features.title": "Усё, што вам патрэбна",
    "home.features.sub": "Паўнавартасная платформа для спрашчэння кіравання фінансамі кожны дзень.",
    "home.feature.security": "Ваенная бяспека",
    "home.feature.security_body": "Шыфраванне AES-256, двухфактарная аўтэнтыфікацыя і кругласутачны маніторынг рахунку.",
    "home.feature.cards": "Віртуальныя і фізічныя карткі",
    "home.feature.cards_body": "Выпускайце карткі за секунды або замоўце сапраўдную фізічную картку з дастаўкай.",
    "home.feature.transfers": "Імгненныя пераводы",
    "home.feature.transfers_body": "Адпраўляйце і атрымлівайце грошы ўва ўвесь свет за секунды.",
    "home.feature.analytics": "Аналіз у рэжыме рэальнага часу",
    "home.feature.analytics_body": "Разумныя дашборды для адсочвання выдаткаў і планавання фінансаў.",
    "home.feature.investments": "Інтэграваныя інвестыцыі",
    "home.feature.investments_body": "Атрымайце доступ да адабраных інвестыцыйных прадуктаў непасрэдна з рахунку.",
    "home.feature.support": "Падтрымка 24/7",
    "home.feature.support_body": "Асабісты банкаўскі кансультант даступны ў любы час праз абаронены месэнджар.",
    "home.tpl.label": "Trust",
    "home.tpl.title": "They trust us",
    "home.mockup.balance_label": "Астатак",
    "home.mockup.virement": "Перавод",
    "home.mockup.salaire": "Зарплата",
    "home.security.label": "Бяспека",
    "home.security.title1": "Вашы фінансы",
    "home.security.title2": "пад абсалютнай абаронай",
    "home.security.sub": "Мы прымяняем самыя строгія стандарты банкаўскай бяспекі для абароны кожнага цэнта.",
    "home.security.aes.title": "Шыфраванне AES-256",
    "home.security.aes.body": "Усе канфідэнцыяльныя даныя зашыфраваны тымі ж алгарытмамі, якія выкарыстоўваюць буйнейшыя міжнародныя банкі.",
    "home.security.auth.title": "Узмоцненая аўтэнтыфікацыя",
    "home.security.auth.body": "Двайная праверка асобы пры кожным уваходзе і для кожнай адчувальнай аперацыі на рахунку.",
    "home.security.compliance.title": "Адпаведнасць PSD2/GDPR",
    "home.security.compliance.body": "Мы выконваем усе еўрапейскія нормы ў галіне плацежных паслуг і абароны даных.",
    "home.invest.label": "Інвестыцыі",
    "home.invest.title": "Памножце свае грошы",
    "home.invest.sub": "6 катэгорый інвестыцый, адабраных па патэнцыяле і надзейнасці.",
    "home.invest.yield": "Даходнасць",
    "home.credits.label": "Крэдыты",
    "home.credits.title": "Рашэнні для кожнага праекта",
    "home.credits.sub": "Канкурэнтныя стаўкі, хуткія адказы і персаналізаванае суправаджэнне для ўсіх вашых праектаў.",
    "home.credits.rate": "Стаўка",
    "home.faq.label": "FAQ",
    "home.faq.title": "Частыя пытанні",
    "home.faq.q1": "Як адкрыць рахунак?",
    "home.faq.a1": "Рэгістрацыя займае менш за 2 хвіліны. Проста ўвядзіце асабістыя даныя, абярыце пароль — і рахунак будзе актываваны пасля праверкі нашай камандай.",
    "home.faq.q2": "Ці абаронены мае даныя?",
    "home.faq.a2": "Так. Усе даныя зашыфраваны з дапамогай AES-256 у стане спакою і TLS 1.3 пры перадачы. Мы выконваем міжнародныя банкаўскія стандарты і нормы GDPR/PSD2.",
    "home.faq.q3": "Якія тыпы інвестыцый вы прапануеце?",
    "home.faq.a3": "Мы прапануем 6 катэгорый: Нерухомасць, Краўдфандынг, Аднаўляльная энергія, Фондавы рынак, Удзельніцкія і Крыпта. Кожны прадукт адбіраецца па суадносінах рызыкі і даходнасці.",
    "home.faq.q4": "Як падаць заяўку на крэдыт?",
    "home.faq.a4": "Перайдзіце ў раздзел Крэдыт у асабістым кабінеце, абярыце тып пазыкі, укажыце суму і тэрмін. Заяўка будзе разгледжана на працягу 24 гадзін нашымі кансультантамі.",
    "home.faq.q5": "Ці ёсць схаваныя камісіі?",
    "home.faq.a5": "Ніякіх схаваных камісій. Усе тарыфы выразна адлюстроўваюцца пры кожнай аперацыі. Умовы даступны ў асабістым кабінеце ў любы час.",
    "home.faq.q6": "Як звязацца са службай падтрымкі?",
    "home.faq.a6": "Выкарыстоўвайце ўбудаваны месэнджар у асабістым кабінеце для прамой сувязі з асабістым банкаўскім кансультантам, даступным 24/7.",
    "home.cta.title": "Гатовы пачаць?",
    "home.cta.sub": "Далучайцеся да тысяч карыстальнікаў, якія кіруюць сваімі фінансамі з {bank}. Рэгістрацыя займае менш за 2 хвіліны.",
    "home.footer.products": "Прадукты",
    "home.footer.services": "Паслугі",
    "home.footer.access": "Хуткі доступ",
    "home.footer.accounts": "Рахункі",
    "home.footer.transfers": "Пераводы",
    "home.footer.cards": "Карткі",
    "home.footer.savings": "Зберажэнні",
    "home.footer.investments": "Інвестыцыі",
    "home.footer.credits": "Крэдыты",
    "home.footer.financing": "Фінансаванне",
    "home.footer.messages": "Паведамленні",
    "home.footer.login": "Уваход",
    "home.footer.signup": "Адкрыць рахунак",
    "home.footer.support": "Падтрымка 24/7",
    "home.footer.tagline": "Ваш анлайн-банк, даступны ўсюды і ў любы час.",
    "home.footer.copyright": "© {year} {bank} — Усе правы абаронены",
    "home.footer.powered": "Працуе на {bank}",
    "app.menu_label": "Галоўнае меню",
    "app.nav.dashboard": "Дашборд",
    "app.nav.accounts": "Рахункі",
    "app.nav.transactions": "Транзакцыі",
    "app.nav.transfers": "Пераводы",
    "app.nav.cards": "Карткі",
    "app.nav.savings": "Зберажэнні",
    "app.nav.investments": "Інвестыцыі",
    "app.nav.credit": "Крэдыт",
    "app.nav.financing": "Фінансаванне",
    "app.nav.messages": "Паведамленні",
    "app.nav.card_tracking": "Адсочванне карты",
    "app.nav.notifications": "Апавяшчэнні",
    "app.nav.settings": "Налады",
    "app.nav.profile": "Профіль",
    "app.nav.mobile.home": "Галоўная",
    "app.nav.mobile.transfers": "Пераводы",
    "app.nav.mobile.cards": "Карткі",
    "app.nav.mobile.transactions": "Транзакцыі",
    "app.nav.more.invest": "Інвеставаць",
    "app.user_role": "Кліент",
    "app.logout": "Выйсці",
    "app.search": "Пошук…",
    "app.more_title": "Больш магчымасцей",
    "app.more_btn": "Яшчэ",
    "app.loading": "Загрузка…",
    "app.error_back": "Вярнуцца да ўваходу",
    "app.see_more_notifs": "Паглядзець яшчэ {count} апавяшчэнняў →",
    "page.accounts": "Мае рахункі",
    "page.transactions": "Транзакцыі",
    "page.transfers": "Новы перавод",
    "page.deposit": "Дэпазіт",
    "page.cards": "Мае карткі",
    "page.savings": "Зберажэнні",
    "page.investments": "Інвестыцыі",
    "page.credits": "Крэдыт",
    "page.financing": "Фінансаванне праектаў",
    "page.messages": "Паведамленні",
    "page.settings": "Налады",
    "page.notifications": "Важныя апавяшчэнні",
    "page.profile": "Мой профіль",
    "dash.greeting": "Прывітанне, {name}",
    "dash.overview": "Агляд вашых фінансаў — {date}",
    "dash.total_balance": "Агульны астатак",
    "dash.this_month": "+{amount} у гэтым месяцы",
    "dash.view_iban": "Паглядзець мой IBAN",
    "dash.my_accounts": "Мае рахункі",
    "dash.primary_account": "Асноўны рахунак",
    "dash.savings_account": "Ашчадны рахунак",
    "dash.remunerated": "З начысленнем працэнтаў",
    "dash.details": "Дэталі",
    "dash.transfer_btn": "Перавод",
    "dash.quick_actions": "Хуткія дзеянні",
    "dash.quick.deposit": "Дэпазіт",
    "deposit.title": "Інфармацыя аб дэпазіце",
    "dash.quick.transfer": "Перавод",
    "dash.quick.history": "Гісторыя",
    "dash.quick.cards": "Мае карткі",
    "dash.quick.profile": "Профіль",
    "dash.monthly_budget": "Месячны бюджэт",
    "dash.income": "Паступленні",
    "dash.expenses": "Выдаткі",
    "dash.spending_cat": "Выдаткі па катэгорыях",
    "dash.no_spending": "Выдаткі не зарэгістраваны.",
    "dash.recent_tx": "Апошнія транзакцыі",
    "dash.see_all": "Паглядзець усё",
    "dash.no_tx": "Аперацый няма.",
    "dash.main_card": "Мая асноўная картка",
    "dash.manage": "Кіраваць",
    "dash.block": "Заблакаваць",
    "dash.pin": "PIN-код",
    "dash.security": "Бяспека",
    "dash.more": "Яшчэ",
    "dash.no_card": "Картка не выпушчана",
    "dash.monthly_activity": "Актыўнасць за месяц",
    "dash.operations": "Аперацыі",
    "dash.holder": "Уладальнік",
    "dash.expires": "Тэрмін дзеяння",
    "dash.visa_label": "VISA",
    "dash.balance": "Даступны астатак",
    "tx.all": "Усе",
    "tx.income": "Паступленні",
    "tx.expenses": "Выдаткі",
    "tx.today": "Сёння",
    "tx.yesterday": "Учора",
    "tx.none": "Транзакцый няма.",
    "transfer.title": "Хуткі перавод",
    "transfer.recipient": "Атрымальнік",
    "transfer.recipient_ph": "Імя, IBAN, нумар тэлефона…",
    "transfer.amount": "Сума",
    "transfer.label": "Прызначэнне",
    "transfer.reason": "Прычына (неабавязкова)",
    "transfer.reason_ph": "Напр.: арэнда, вяртанне даўгу…",
    "transfer.execution": "Выкананне",
    "transfer.immediate": "Неадкладна",
    "transfer.delayed": "Адкладзена",
    "transfer.balance": "Даступны астатак:",
    "transfer.continue": "Працягнуць",
    "transfer.confirm": "Пацвердзіць",
    "transfer.cancel": "Адмяніць",
    "transfer.code": "Код пацверджання",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Увядзіце атрыманы код для пацверджання пераводу",
    "transfer.pending.title": "Перавод чакае пацверджання",
    "transfer.pending.amount": "Сума:",
    "transfer.pending.to": "Каму:",
    "transfer.pending.hint": "Ваш код пацверджання будзе адпраўлены па электроннай пошце або праз звяніца апавяшчэнняў. Код складаецца з 6 лічбаў. Калі вы яго не атрымалі, звяжыцеся са службай падтрымкі.",
    "transfer.pending.validate": "Пацвердзіць перавод",
    "transfer.saved": "Перавод захаваны. Запытайце код у адміністратара для пацверджання.",
    "transfer.done": "Перавод выкананы",
    "transfer.validated": "Перавод пацверджаны!",
    "transfer.progress.processing": "Апрацоўка пераводу…",
    "transfer.progress.completed": "Перавод выкананы!",
    "transfer.progress.default_message": "Ваш перавод апрацоўваецца. Калі ласка, пачакайце.",
    "cards.none": "Картка не выпушчана.\nЗвярніцеся да адміністратара.",
    "cards.holder": "Уладальнік",
    "cards.expires": "Тэрмін дзеяння",
    "cards.block": "Заблакаваць",
    "cards.pin": "PIN-код",
    "cards.more": "Яшчэ",
    "cards.limits": "Ліміты карткі",
    "cards.payment": "Аплата",
    "cards.withdrawal": "Зняцце наяўных",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Заблакіравана",
    "cards.status_active": "Актыўная",
    "cards.toast.blocked": "Картка заблакіравана",
    "cards.toast.unblocked": "Картка разблакіравана",
    "cards.toast.physical_ordered": "Фізічная картка заказана! Дастаўка за 5–10 рабочых дзён.",
    "cards.toast.created": "Картка паспяхова створана!",
    "cards.none_sub": "Няма выдадзенай карткі. Стварыце сваю віртуальную картку цяпер.",
    "cards.generate_btn": "Стварыць маю картку",
    "cards.create.title": "Стварыць маю віртуальную картку",
    "cards.create.network": "Плацёжная сетка",
    "cards.create.color": "Колер карткі",
    "cards.create.preview_name": "ВАША ІМЯ",
    "cards.create.preview_expiry": "ММ/ГГ",
    "cards.create.loading": "Стварэнне…",
    "cards.create.submit": "Стварыць картку",
    "cards.btn.unblock": "Разблакіраваць",
    "cards.btn.block": "Заблакіраваць",
    "cards.btn.info": "Інфа",
    "cards.btn.physical": "Фізічная",
    "cards.btn.physical_done": "Заказана",
    "cards.limits_title": "Ліміты",
    "cards.reveal.title": "Інфармацыя аб картцы",
    "cards.reveal.number": "Поўны нумар",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Тэрмін дзеяння",
    "cards.reveal.warning": "Ніколі не дзяліцеся гэтай інфармацыяй. Зачыніце гэта акно пасля выкарыстання.",
    "cards.pin.title": "PIN-код",
    "cards.pin.message": "Гэты PIN-код выкарыстоўваецца для ўсіх вашых анлайн-плацяжоў і зняццяў. Ніколі нікому яго не паведамляйце.",
    "cards.physical.title": "Замовіць фізічную картку",
    "cards.physical.fee_label": "Плата за дастаўку",
    "cards.physical.balance_label": "Ваш баланс",
    "cards.physical.insufficient": "Недастатковы баланс. Калі ласка, папоўніце рахунак перад заказам.",
    "cards.physical.info": "Плата будзе спісана з вашага рахунку. Дастаўка за 5–10 рабочых дзён.",
    "cards.physical.order_btn": "Замовіць ({fee})",
    "cards.physical.ordering": "Заказваю…",
    "cards.physical.order_cta": "Замовіць фізічную картку",
    "cards.physical.choose_type": "Выберыце сваю картку",
    "cards.physical.features": "Перавагі",
    "cards.buy_virtual_cta": "Купіць віртуальную карту",
    "notif.none": "Апавяшчэнняў пакуль няма.",
    "notif.contact_support": "Звязацца са службай падтрымкі",
    "profile.change_photo": "Змяніць фота",
    "profile.remove_photo": "Выдаліць фота",
    "profile.email": "Email",
    "profile.phone": "Тэлефон",
    "profile.country": "Краіна",
    "profile.address": "Адрас",
    "profile.notifs_title": "Апавяшчэнні ({count})",
    "profile.no_notifs": "Апавяшчэнняў няма.",
    "profile.logout": "Выйсці",
    "profile.photo_updated": "Фота профілю абноўлена",
    "profile.photo_removed": "Фота выдалена",
    "profile.photo_too_large": "Выява занадта вялікая (макс. 1 МБ)",
    "profile.powered": "Працуе на {bank}",
    "iban.title": "Мой IBAN",
    "iban.loading": "Загрузка…",
    "iban.bic": "Код BIC / SWIFT",
    "iban.share_info": "Падзяліцеся гэтымі рэквізітамі, каб атрымаць перавод на рахунак.",
    "iban.copy": "Капіраваць",
    "iban.copied": "Скапіравана ў буфер абмену",
    "iban.copy_fail": "Не ўдалося скапіраваць",
    "iban.close": "Закрыць",
    "accounts.all": "Усе вашы рахункі ў {bank}.",
    "accounts.primary": "Асноўны рахунак",
    "accounts.savings": "Ашчадны рахунак",
    "accounts.primary_tag": "Асноўны",
    "accounts.secondary_tag": "Дадатковы",
    "accounts.objective": "Мэта: {amount} · {pct}%",
    "accounts.copy": "Капіраваць",
    "accounts.loading": "Загрузка…",
    "accounts.iban_copied": "IBAN скапіраваны",
    "accounts.copy_fail": "Не ўдалося скапіраваць",
    "savings.total": "Агульная сума зберажэнняў",
    "savings.target_total": "Агульная мэта",
    "savings.progress": "Прагрэс",
    "savings.active": "Актыўныя мэты",
    "savings.new_goal": "Новая мэта",
    "savings.none": "Мэт зберажэнняў яшчэ няма.",
    "savings.none_sub": "Стварыце першую мэту, каб пачаць.",
    "savings.saved": "Назаплечана:",
    "savings.goal": "Мэта:",
    "savings.deadline": "Тэрмін: {date}",
    "savings.deposit": "Папоўніць",
    "savings.withdraw": "Зняць",
    "savings.loading": "Загрузка…",
    "savings.form.title": "Новая мэта зберажэнняў",
    "savings.form.cancel": "Адмяніць",
    "savings.form.create": "Стварыць",
    "savings.form.name": "Назва мэты",
    "savings.form.name_ph": "Падарожжа ў Японію, Аўтамабіль, …",
    "savings.form.target": "Мэтавая сума",
    "savings.form.initial": "Пачатковы ўнёсак",
    "savings.form.deadline": "Канчатковая дата (неабавязкова)",
    "savings.form.color": "Колер",
    "savings.deposit_title": "Папоўніць \"{name}\"",
    "savings.deposit_amount": "Сума папаўнення",
    "savings.balance_hint": "Астатак на асноўным рахунку: {amount}",
    "savings.withdraw_prompt": "Колькі зняць з \"{name}\"?",
    "savings.close_confirm": "Закрыць мэту \"{name}\"? Зберажэнні будуць вернуты на асноўны рахунак.",
    "savings.toast.created": "Мэта створана",
    "savings.toast.deposited": "Папаўненне выканана",
    "savings.toast.withdrawn": "Зняцце выканана",
    "savings.toast.closed": "Мэта закрыта",
    "invest.total": "Агульная сума інвестыцый",
    "invest.profits": "Назапашаны прыбытак",
    "invest.count": "Інвестыцыі",
    "invest.products": "Даступныя прадукты",
    "invest.invest_btn": "Інвеставаць",
    "invest.my": "Мае інвестыцыі",
    "invest.pending": "На разглядзе",
    "invest.validated": "Пацверджана",
    "invest.refused": "Адхілена",
    "invest.withdraw.btn": "Вывесці",
    "invest.withdraw.title": "Вывесці маю інвестыцыю",
    "invest.withdraw.intro": "Вы збіраецеся вывесці сваю інвестыцыю «{product}». Пачатковы капітал і назапашаны прыбытак будуць залічаны на ваш асноўны баланс.",
    "invest.withdraw.principal": "Пачатковы капітал",
    "invest.withdraw.profit": "Прыбытак",
    "invest.withdraw.total": "Усяго да атрымання",
    "invest.withdraw.confirm": "Пацвердзіць вывад",
    "invest.withdraw.warning": "Гэта дзеянне незваротнае.",
    "invest.withdraw.success": "Інвестыцыя выведзена — {amount} залічана на баланс.",
    "invest.withdrawn": "Выведзена",
    "invest.cta_sub": "Абярыце тып інвестыцыі, які вам падыходзіць. Кансультант дапаможа з першага запыту.",
    "invest.form.amount": "Сума для інвеставання",
    "invest.form.balance": "Даступны астатак: {amount}",
    "invest.form.note": "Нататка для кансультанта (неабавязкова)",
    "invest.form.note_ph": "Удакладніце вашы чаканні…",
    "invest.form.advisor_info": "Размова з кансультантам будзе адкрыта аўтаматычна.",
    "invest.form.cancel": "Адмяніць",
    "invest.form.submit": "Адправіць запыт",
    "invest.toast.requested": "Запыт зарэгістраваны — размова з кансультантам адкрыта.",
    "invest.p1.name": "Нерухомасць",
    "invest.p1.desc": "SCPI, папяровая нерухомасць, краўдфандынг нерухомасці",
    "invest.p2.name": "Краўдфандынг",
    "invest.p2.desc": "Удзельніцкае фінансаванне адабраных праектаў",
    "invest.p3.name": "Аднаўляльная энергія",
    "invest.p3.desc": "Сонечная, ветравая і зялёныя праекты",
    "invest.p4.name": "Фондавы рынак",
    "invest.p4.desc": "Акцыі, ETF і дыверсіфікаваныя фонды",
    "invest.p5.name": "Удзельніцкія",
    "invest.p5.desc": "Пазыкі для МСП і мясцовых прадпрымальнікаў",
    "invest.p6.name": "Крыпта",
    "invest.p6.desc": "Bitcoin, Ethereum і мультывалютны кашалёк",
    "msg.online": "Анлайн",
    "msg.loading": "Загрузка…",
    "msg.none": "Паведамленняў няма. Пачніце размову з кансультантам.",
    "msg.placeholder": "Напішыце паведамленне…",
    "msg.attach.image": "Выява",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Аўдыё",
    "msg.attach.file": "Файл / Дакумент",
    "msg.pdf_doc": "PDF-дакумент",
    "msg.file": "Файл",
    "msg.too_large": "Файл занадта вялікі (макс. {size}).",
    "msg.withdraw": "Выдаліць",
    "credits.cta_sub": "Падайце заяўку анлайн — адказ на працягу 24 гадзін.",
    "credits.learn_more": "Даведацца больш",
    "credits.request": "Падаць заяўку",
    "credits.history": "Мае заяўкі",
    "credits.status.pending": "На разглядзе",
    "credits.status.accepted": "Прынята",
    "credits.status.refused": "Адхілена",
    "credits.chars": "Характарыстыкі",
    "credits.amount_range": "Сума: {min}€ – {max}€",
    "credits.rate_range": "Працэнтная стаўка: {min}% – {max}% у год",
    "credits.duration_range": "Тэрмін: ад {min} да {max} месяцаў",
    "credits.deposit_min": "Пачатковы ўнёсак: {amount}€ мінімум",
    "credits.garantie": "Залог: {garantie}",
    "credits.eligibility": "Умовы адпаведнасці",
    "credits.elig.income": "Пацвердзіць рэгулярны і стабільны даход",
    "credits.elig.deposit": "Зрабіць пачатковы ўнёсак у памеры {amount}€",
    "credits.elig.garantie": "Прадставіць залог: {garantie}",
    "credits.form.title": "Заяўка — {name}",
    "credits.form.cancel": "Адмяніць",
    "credits.form.submit": "Адправіць заяўку",
    "credits.form.amount": "Жаданая сума (€)",
    "credits.form.duration": "Тэрмін (месяцаў)",
    "credits.form.reason": "Прычына (неабавязкова)",
    "credits.form.reason_ph": "Апішыце ваш праект…",
    "credits.form.close": "Закрыць",
    "credits.toast.submitted": "Заяўка на крэдыт адпраўлена — вы атрымаеце адказ на працягу 24 гадзін.",
    "credits.p1.name": "Спажывецкі крэдыт",
    "credits.p1.desc": "Для фінансавання штодзённых патрэб або асабістых праектаў.",
    "credits.p2.name": "Іпатэчны крэдыт",
    "credits.p2.desc": "Для фінансавання куплі або будаўніцтва нерухомасці.",
    "credits.p3.name": "Аўтакрэдыт / Матакрэдыт",
    "credits.p3.desc": "Купіце транспартны сродак без лішніх клопатаў.",
    "credits.p4.name": "Студэнцкі крэдыт",
    "credits.p4.desc": "Фінансавая дапамога для атрымання вышэйшай адукацыі.",
    "credits.p5.name": "Бізнес-крэдыт",
    "credits.p5.desc": "Для падтрымкі росту прафесійнай дзейнасці.",
    "credits.p6.name": "Экспрэс-крэдыт",
    "credits.p6.desc": "Хуткае рашэнне для тэрміновых патрэб.",
    "financing.reimbursable": "Зваротнае фінансаванне",
    "financing.reimbursable_desc": "Фінансавая падтрымка з фіксаванымі ўмовамі для запуску або развіцця дзейнасці.",
    "financing.non_reimbursable": "Бяззваротнае фінансаванне",
    "financing.non_reimbursable_desc": "Для праектаў з сацыяльным, адукацыйным або гуманітарным уздзеяннем (НКА, асацыяцыі).",
    "financing.cta_sub": "Індывідуальныя рашэнні для рэалізацыі вашых амбіцый.",
    "financing.request": "Запытаць фінансаванне",
    "financing.history": "Мае заяўкі",
    "financing.reimbursable_label": "Зваротнае",
    "financing.non_reimbursable_label": "Бяззваротнае",
    "financing.status.pending": "На разглядзе",
    "financing.status.granted": "Прадастаўлена",
    "financing.status.refused": "Адхілена",
    "financing.form.title": "Заяўка — {name}",
    "financing.form.cancel": "Адмяніць",
    "financing.form.submit": "Адправіць заяўку",
    "financing.form.project": "Назва праекта",
    "financing.form.project_ph": "Мой праект",
    "financing.form.amount": "Запытваемая сума (€)",
    "financing.form.sector": "Сектар",
    "financing.form.sector_ph": "Тэхналогіі, сельская гаспадарка, …",
    "financing.form.description": "Апісанне праекта",
    "financing.form.description_ph": "Прадставіце ваш праект, яго мэты і чаканы ўплыў…",
    "settings.security": "Бяспека",
    "settings.current_pw": "Бягучы пароль",
    "settings.new_pw": "Новы пароль",
    "settings.confirm_pw": "Пацвердзіць",
    "settings.submit": "Змяніць пароль",
    "settings.no_match": "Паролі не супадаюць",
    "settings.changed": "Пароль зменены",
    "common.cancel": "Адмяніць",
    "common.confirm": "Пацвердзіць",
    "common.close": "Закрыць",
    "common.save": "Захаваць",
    "common.loading": "Загрузка…",
    "common.send": "Адправіць",
    "common.total": "Разам",
    "admin.wallet.balance": "Доступный баланс",
    "admin.wallet.history": "История",
    "admin.wallet.no_history": "Нет транзакций",
    "admin.wallet.title": "Мой кошелёк",
    "admin.wallet.topup": "Пополнить",
    "beneficiary.pick": "Выбрать",
    "beneficiary.save_this": "Сохранить получателя",
    "beneficiary.saved": "Сохранённые получатели",
    "beneficiary.saved_ok": "Получатель сохранён",
    "card.color.amber": "Янтарный",
    "card.color.cyan": "Голубой",
    "card.color.emerald": "Изумрудный",
    "card.color.forest": "Лесной",
    "card.color.gold": "Золотой",
    "card.color.lavender": "Лавандовый",
    "card.color.night": "Ночной",
    "card.color.orange": "Оранжевый",
    "card.color.pink": "Розовый",
    "card.color.royal": "Королевский синий",
    "card.color.ruby": "Рубиновый",
    "card.color.slate": "Сланцевый",
    "card.color.steel": "Стальной",
    "card.color.terracotta": "Терракотовый",
    "card.color.violet": "Фиолетовый",
    "common.other": "Другое",
    "common.search": "Поиск",
    "content.translated": "Автоматически переведённый контент",
    "credits.monthly_payment": "Ориентировочный ежемесячный платёж",
    "credits.rate_annual": "/ год",
    "credits.rate_used": "Ориентировочная ставка:",
    "credits.simulation": "Расчёт ежемесячного платежа",
    "credits.total_cost": "Ориентировочная общая стоимость",
    "demo.banner": "Демо-режим — никаких реальных действий",
    "demo.disabled": "Действие недоступно в демо-режиме",
    "deposit.default_instructions": "Свяжитесь с вашим менеджером для получения инструкций по пополнению.",
    "error.back_home": "На главную",
    "error.bank_not_found": "Банк не найден",
    "error.unknown": "Неизвестная ошибка",
    "kyc.file_read_error": "Не удалось прочитать файл",
    "kyc.file_too_large": "Файл слишком большой (макс. 5 МБ)",
    "login.history": "История входов",
    "login.history.empty": "Нет записей о входах.",
    "notif.dismiss": "Закрыть",
    "notif.locked": "Сообщение банка",
    "tracking.error.not_found": "Номер отслеживания не найден. Проверьте и попробуйте снова.",
    "tracking.label": "Отслеживание",
    "tracking.status.delivered": "Доставлено",
    "tracking.status.preparation": "Подготовка",
    "tracking.status.shipped": "Отправлено",
    "tracking.status.transit": "В пути",
    "transfer.code.copied": "Скопировано",
    "transfer.code.copy": "Копировать",
    "transfer.code.received": "Ваш код подтверждения",
    "dash.welcome": "Сардэчна запрашаем",
    "common.delete": "Выдаліць",
    "cards.delete.title": "Выдаліць картку",
    "cards.delete.confirm_msg": "Гэта дзеянне неадваротнае. Картка будзе выдалена назаўжды.",
    "cards.delete.physical_tooltip": "Фізічная картка замоўлена — выдаленне немагчыма",
    "cards.order.confirmed": "Замова пацверджана!",
    "cards.order.confirmed_body": "Ваша фізічная картка рыхтуецца. Выкарыстоўвайце гэты нумар для адсочвання дастаўкі.",
    "cards.order.tracking_no": "№ адсочвання",
    "cards.fee_deducted": "{amount} будзе спісана з вашага балансу",
    "cards.insufficient_short": "Недастатковы баланс ({available} даступна)",
    "transfer.section.beneficiary": "Атрымальнік",
    "transfer.section.details": "Пераклад",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Банк",
    "transfer.field.city": "Горад",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Банк:",
    "transfer.progress.paused": "Чакае праверкі",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Зменны",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Карта выдалена",
    "cards.feat.gold_exclusive": "Эксклюзіўная залатая карта",
    "cards.feat.cashback_05": "Кэшбэк 0,5%",
    "cards.feat.buyer_protection": "Абарона пакупніка",
    "cards.feat.support_standard": "Стандартная падтрымка",
    "cards.feat.plastic_premium": "Прэміум пластыкавая карта",
    "cards.feat.cashback_1": "Кэшбэк 1%",
    "cards.feat.travel_standard": "Стандартнае турыстычнае страхаванне",
    "cards.feat.support_priority": "Прыярытэтная падтрымка",
    "cards.feat.metal_brushed": "Карта з матавага металу",
    "cards.feat.cashback_2_unlimited": "Безлімітны кэшбэк 2%",
    "cards.feat.travel_premium": "Прэміум турыстычнае страхаванне",
    "cards.feat.vip_lounge": "Доступ да VIP-салонаў",
    "cards.feat.support_247": "24/7 падтрымка",
    "auth.demo.title": "Уваход адміністратара",
    "auth.demo.subtitle": "Увядзіце дадзеныя адміністратара для прагляду дэма",
    "credits.garantie.none": "Адсутнічае",
    "credits.garantie.mortgage": "Іпатэка на фінансаваную нерухомасць",
    "credits.garantie.vehicle": "Фінансаваны транспартны сродак",
    "credits.garantie.parental": "Бацькоўская або банкаўская",
    "credits.garantie.professional": "Прафесійныя актывы або асабістая парука",
    "cards.reveal.pin_prompt": "Увядзіце ваш 4-значны PIN-код для адлюстравання інфармацыі па карце.",
    "cards.reveal.show": "Паказаць",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Два PIN-коды не супадаюць",
    "auth.pin.digit_hint": "4 лічбы",
  
  },
  hu: {
    "notif.transfer_code.title": "Átutalási visszaigazoló kód",
    "notif.transfer_code.body": "Másolja ki ezt a kódot az átutalás megerősítéséhez.",
    "notif.transfer_code.expires_in": "Eltűnik {sec} mp múlva",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} hónap",
    "credits.n_months": "{n} hónap",
    "msg.read": "Olvasott",
    "msg.sent": "Elküldve",
    "msg.attach_aria": "Fájl csatolása",
    "login.device.unknown": "Ismeretlen böngésző",
    "login.device.mobile": "Mobil",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Számítógép",
    "login.recent": "Legutóbbi",
    "err.invalid_credentials": "Érvénytelen hitelesítő adatok",
    "err.bad_pin": "Helytelen PIN",
    "err.account_blocked": "Fiók zárolva",
    "err.bad_current_pw": "A jelenlegi jelszó helytelen",
    "err.insufficient_funds": "Nincs elég pénz, töltse fel a számláját.",
    "err.amount_above_savings": "Az összeg meghaladja a rendelkezésre álló megtakarítást",
    "err.must_be_positive": "Az értéknek pozitívnak kell lennie",
    "err.invalid_format": "Érvénytelen formátum",
    "err.email_taken": "Az email már használatban van",
    "err.card_not_found": "Kártya nem található",
    "err.physical_card_already_ordered": "Fizikai kártya már megrendelve",
    "err.beneficiary_not_found": "Kedvezményezett nem található",
    "err.invalid_code": "Érvénytelen kód",
    "err.no_transfer_code": "Nincs kód ehhez az átutaláshoz",
    "err.link_expired": "Hivatkozás érvénytelen vagy lejárt",
    "err.thread_not_found": "Beszélgetés nem található",
    "err.empty_message": "Üres üzenet",
    "err.file_too_large": "Fájl túl nagy (max 20 MB)",
    "err.forbidden": "Hozzáférés megtagadva",
    "err.bank_not_found": "Bank nem található",
    "err.signups_closed": "A regisztrációk zárva vannak ehhez a bankhoz.",
    "err.access_suspended": "Hozzáférés ideiglenesen felfüggesztve. Kérjük, vegye fel a kapcsolatot a bankjával.",
    "err.notif_not_closable": "Ezt az értesítést nem lehet bezárni.",
    "auth.signup.tagline": "Csatlakozzon hozzánk még ma",
    "auth.login.tagline": "Üdvözöljük!",
    "auth.signup.tagline_sub": "Nyissa meg számláját 2 perc alatt, és érje el prémium banki szolgáltatásainkat.",
    "auth.login.tagline_sub": "Lépjen be személyes fiókjába, és kezelje pénzügyeit biztonságosan.",
    "auth.back": "Vissza a főoldalra",
    "auth.bullet.security": "Katonai szintű banki biztonság",
    "auth.bullet.transfers": "Azonnali átutalások 24/7",
    "auth.bullet.investments": "Integrált befektetések és megtakarítások",
    "auth.bullet.advisor": "Személyes tanácsadó elérhető heti 7 nap",
    "auth.footer": "Működteti: {bank}",
    "auth.signup.title": "Fiók létrehozása",
    "auth.login.title": "Bejelentkezés",
    "auth.field.lastName": "Vezetéknév",
    "auth.field.firstName": "Keresztnév",
    "auth.field.country": "Ország",
    "auth.field.country.placeholder": "— Válassza ki az országát —",
    "auth.field.phone": "Telefonszám",
    "auth.field.address": "Cím",
    "auth.field.address.placeholder": "Budapest, Fő utca 123.",
    "auth.field.email": "E-mail",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Jelszó",
    "auth.submit.signup": "Fiók létrehozása",
    "auth.submit.login": "Bejelentkezés",
    "auth.login.badge": "Jelentkezzen be fiókjába",
    "auth.card.tagline": "Az ön ügyfélterülete",
    "auth.submit.loading": "Betöltés…",
    "auth.pin.title": "PIN-kód",
    "auth.pin.subtitle": "Adja meg a 4 számjegyű PIN-kódját",
    "auth.pin.label": "PIN-kód",
    "auth.pin.confirm": "PIN-kód megerősítése",
    "auth.pin.submit": "Megerősítés",
    "auth.pin.back": "Vissza a bejelentkezéshez",
    "auth.pin.length_error": "A PIN-kódnak 4 számjegyűnek kell lennie",
    "auth.switch.have_account": "Már van fiókja?",
    "auth.switch.login_link": "Bejelentkezés",
    "auth.switch.no_account": "Még nem ügyfél?",
    "auth.error.select_country": "Kérjük, válassza ki az országát",
    "kyc.title": "Személyazonosság-ellenőrzés (KYC)",
    "kyc.sub": "Töltse fel dokumentumait fiókja hitelesítéséhez. Minden dokumentum titkosított és bizalmas.",
    "kyc.id_type": "Személyazonosító okmány típusa *",
    "kyc.id_card": "Személyi igazolvány",
    "kyc.passport": "Útlevél",
    "kyc.driver_license": "Jogosítvány",
    "kyc.recto_passport": "Útlevél előlapja *",
    "kyc.recto_id": "Okmány előlapja *",
    "kyc.verso": "Okmány hátlapja *",
    "kyc.selfie": "Szelfie a személyi okmánnyal *",
    "kyc.address_proof": "Lakcímigazolás *",
    "kyc.hint_doc": "Kép vagy PDF, max. 5 MB",
    "kyc.hint_selfie": "Éles fénykép, arc és dokumentum látható",
    "kyc.hint_address": "Számla, kivonat, igazolás… (max. 5 MB)",
    "kyc.upload": "Feltöltés",
    "kyc.replace": "Csere",
    "pending.title": "Fiók jóváhagyásra vár",
    "pending.body": "Kérelmét megkaptuk. Egy adminisztrátor hamarosan ellenőrzi adatait és aktiválja fiókját.",
    "pending.back": "Vissza a bejelentkezéshez",
    "home.nav.features": "Funkciók",
    "home.nav.security": "Biztonság",
    "home.nav.invest": "Befektetés",
    "home.nav.credits": "Hitelek",
    "home.nav.faq": "GYIK",
    "home.nav.login": "Bejelentkezés",
    "home.hero.tagline": "Következő generációs online bank",
    "home.hero.title1": "Kezelje pénzügyeit",
    "home.hero.title2": "okosan",
    "home.hero.sub": "Teljes banki platform — átutalások, megtakarítások, befektetések és hitelek okostelefonról.",
    "home.hero.login": "Bejelentkezés",
    "home.hero.trust.secure": "100% Biztonságos",
    "home.hero.trust.fast": "Gyors megnyitás",
    "home.hero.trust.encrypted": "Titkosított adatok",
    "home.hero.trust.support": "24/7 Szolgáltatás",
    "home.mockup.balance": "Rendelkezésre álló egyenleg",
    "home.mockup.currency": "EUR · Főszámla",
    "home.mockup.recharge": "Feltöltés",
    "home.mockup.send": "Küldés",
    "home.mockup.save": "Megtakarítás",
    "home.mockup.salary": "Márciusi fizetés",
    "home.mockup.rent": "Áprilisi bérleti díj",
    "home.mockup.investment": "Befektetés",
    "home.mockup.days2": "2 napja",
    "home.mockup.days3": "3 napja",
    "home.mockup.days5": "5 napja",
    "home.stats.accounts": "Aktív számlák",
    "home.stats.uptime": "Rendelkezésre állás",
    "home.stats.transfers": "Átutalások",
    "home.stats.support": "Támogatás",
    "home.features.label": "Funkciók",
    "home.features.title": "Minden, amire szüksége van",
    "home.features.sub": "Teljes platform, amelyet mindennapi pénzügyei egyszerűsítésére terveztünk.",
    "home.feature.security": "Katonai biztonság",
    "home.feature.security_body": "AES-256 titkosítás, kétfaktoros hitelesítés és 24/7-es számlafigyelés.",
    "home.feature.cards": "Virtuális és fizikai kártyák",
    "home.feature.cards_body": "Adjon ki kártyákat másodpercek alatt, vagy rendeljen valódi fizikai kártyát házhozszállítással.",
    "home.feature.transfers": "Expressz átutalások",
    "home.feature.transfers_body": "Küldjön és fogadjon pénzt a világ bármely pontjára másodpercek alatt.",
    "home.feature.analytics": "Valós idejű elemzés",
    "home.feature.analytics_body": "Intelligens irányítópultok kiadásai nyomon követéséhez és pénzügyei előrejelzéséhez.",
    "home.feature.investments": "Integrált befektetések",
    "home.feature.investments_body": "Hozzáférés kiválasztott befektetési termékekhez közvetlenül a fiókjából.",
    "home.feature.support": "Dedikált 24/7-es támogatás",
    "home.feature.support_body": "Személyes banki tanácsadó, elérhető bármikor biztonságos üzenetküldésen keresztül.",
    "home.tpl.label": "Bizalom",
    "home.tpl.title": "Bíznak bennünk",
    "home.mockup.balance_label": "Egyenleg",
    "home.mockup.virement": "Átutalás",
    "home.mockup.salaire": "Fizetés",
    "home.security.label": "Biztonság",
    "home.security.title1": "Pénzügyei",
    "home.security.title2": "teljes biztonságban",
    "home.security.sub": "A legszigorúbb banki biztonsági szabványokat alkalmazzuk minden cent védelme érdekében.",
    "home.security.aes.title": "AES-256 titkosítás",
    "home.security.aes.body": "Minden érzékeny adat ugyanazokkal az algoritmusokkal van titkosítva, amelyeket a nagy nemzetközi bankok használnak.",
    "home.security.auth.title": "Megerősített hitelesítés",
    "home.security.auth.body": "Kétszeri személyazonosság-ellenőrzés minden bejelentkezéskor és minden érzékeny műveletnél.",
    "home.security.compliance.title": "PSD2/GDPR megfelelőség",
    "home.security.compliance.body": "Betartjuk az összes európai fizetési szolgáltatásokra és adatvédelemre vonatkozó jogszabályt.",
    "home.invest.label": "Befektetések",
    "home.invest.title": "Gyarapítsa pénzét",
    "home.invest.sub": "6 befektetési kategória, kiválasztva potenciáljuk és megbízhatóságuk alapján.",
    "home.invest.yield": "Hozam",
    "home.credits.label": "Hitelek",
    "home.credits.title": "Minden projekthez megfelelő megoldás",
    "home.credits.sub": "Versenyképes kamatok, gyors válaszok és személyre szabott kísérés minden projektjéhez.",
    "home.credits.rate": "Kamatláb",
    "home.faq.label": "GYIK",
    "home.faq.title": "Gyakran ismételt kérdések",
    "home.faq.q1": "Hogyan nyithatok számlát?",
    "home.faq.a1": "A regisztráció kevesebb mint 2 percet vesz igénybe. Egyszerűen adja meg személyes adatait, válasszon jelszót, és fiókját csapatunk ellenőrzése után aktiváljuk.",
    "home.faq.q2": "Biztonságban vannak az adataim?",
    "home.faq.a2": "Igen. Minden adat AES-256-tal titkosított nyugalmi állapotban és TLS 1.3-mal átvitel közben. Betartjuk a nemzetközi banki szabványokat és a GDPR/PSD2 előírásokat.",
    "home.faq.q3": "Milyen befektetési lehetőségeket kínálnak?",
    "home.faq.a3": "6 kategóriát kínálunk: Ingatlan, Crowdfunding, Megújuló energia, Tőzsde, Részvételi és Kripto. Minden terméket kockázat/hozam aránya alapján választunk ki.",
    "home.faq.q4": "Hogyan igényelhetek hitelt?",
    "home.faq.a4": "Látogasson el ügyfélterülete Hitel szekciójába, válassza ki a kölcsön típusát, adja meg az összeget és a futamidőt. Kérelmét tanácsadóink 24 órán belül feldolgozzák.",
    "home.faq.q5": "Vannak rejtett díjak?",
    "home.faq.a5": "Nincsenek rejtett díjak. Minden díjszabás egyértelműen megjelenik minden tranzakciónál. A feltételek bármikor elérhetők az ügyfélterületen.",
    "home.faq.q6": "Hogyan vehetek fel kapcsolatot az ügyfélszolgálattal?",
    "home.faq.a6": "Használja az ügyfélterület beépített üzenetküldőjét, hogy közvetlenül kapcsolatba lépjen személyes banki tanácsadójával, aki 24/7 elérhető.",
    "home.cta.title": "Készen áll a kezdésre?",
    "home.cta.sub": "Csatlakozzon azokhoz a felhasználókhoz, akik {bank}-kal kezelik pénzügyeiket. A regisztráció kevesebb mint 2 percet vesz igénybe.",
    "home.footer.products": "Termékek",
    "home.footer.services": "Szolgáltatások",
    "home.footer.access": "Gyors hozzáférés",
    "home.footer.accounts": "Számlák",
    "home.footer.transfers": "Átutalások",
    "home.footer.cards": "Kártyák",
    "home.footer.savings": "Megtakarítás",
    "home.footer.investments": "Befektetések",
    "home.footer.credits": "Hitelek",
    "home.footer.financing": "Finanszírozás",
    "home.footer.messages": "Üzenetek",
    "home.footer.login": "Bejelentkezés",
    "home.footer.signup": "Számlanyitás",
    "home.footer.support": "24/7 Támogatás",
    "home.footer.tagline": "Az Ön online bankja, mindenhol és mindig elérhető.",
    "home.footer.copyright": "© {year} {bank} — Minden jog fenntartva",
    "home.footer.powered": "Működteti: {bank}",
    "app.menu_label": "Főmenü",
    "app.nav.dashboard": "Irányítópult",
    "app.nav.accounts": "Számlák",
    "app.nav.transactions": "Tranzakciók",
    "app.nav.transfers": "Átutalások",
    "app.nav.cards": "Kártyák",
    "app.nav.savings": "Megtakarítás",
    "app.nav.investments": "Befektetés",
    "app.nav.credit": "Hitel",
    "app.nav.financing": "Finanszírozás",
    "app.nav.messages": "Üzenetek",
    "app.nav.card_tracking": "Kártyakövetés",
    "app.nav.notifications": "Értesítések",
    "app.nav.settings": "Beállítások",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Kezdőlap",
    "app.nav.mobile.transfers": "Átutalások",
    "app.nav.mobile.cards": "Kártyák",
    "app.nav.mobile.transactions": "Tranzakciók",
    "app.nav.more.invest": "Befektetés",
    "app.user_role": "Ügyfél",
    "app.logout": "Kijelentkezés",
    "app.search": "Keresés…",
    "app.more_title": "További funkciók",
    "app.more_btn": "Több",
    "app.loading": "Betöltés…",
    "app.error_back": "Vissza a bejelentkezéshez",
    "app.see_more_notifs": "Még {count} értesítés megtekintése →",
    "page.accounts": "Saját számlák",
    "page.transactions": "Tranzakciók",
    "page.transfers": "Új átutalás",
    "page.deposit": "Befizetés",
    "page.cards": "Saját kártyák",
    "page.savings": "Megtakarítás",
    "page.investments": "Befektetés",
    "page.credits": "Hitel",
    "page.financing": "Projektfinanszírozás",
    "page.messages": "Üzenetek",
    "page.settings": "Beállítások",
    "page.notifications": "Fontos értesítések",
    "page.profile": "Saját profil",
    "dash.greeting": "Jó napot, {name}",
    "dash.overview": "Pénzügyi áttekintő — {date}",
    "dash.total_balance": "Teljes egyenleg",
    "dash.this_month": "+{amount} ebben a hónapban",
    "dash.view_iban": "IBAN megtekintése",
    "dash.my_accounts": "Saját számlák",
    "dash.primary_account": "Főszámla",
    "dash.savings_account": "Megtakarítási számla",
    "dash.remunerated": "Kamatozó",
    "dash.details": "Részletek",
    "dash.transfer_btn": "Átutalás",
    "dash.quick_actions": "Gyors műveletek",
    "dash.quick.deposit": "Befizetés",
    "deposit.title": "Befizetési információk",
    "dash.quick.transfer": "Átutalás",
    "dash.quick.history": "Előzmények",
    "dash.quick.cards": "Saját kártyák",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Havi költségvetés",
    "dash.income": "Bevételek",
    "dash.expenses": "Kiadások",
    "dash.spending_cat": "Kiadások kategóriánként",
    "dash.no_spending": "Nincs rögzített kiadás.",
    "dash.recent_tx": "Legutóbbi tranzakciók",
    "dash.see_all": "Összes megtekintése",
    "dash.no_tx": "Nincs tranzakció.",
    "dash.main_card": "Fő kártyám",
    "dash.manage": "Kezelés",
    "dash.block": "Letiltás",
    "dash.pin": "PIN-kód",
    "dash.security": "Biztonság",
    "dash.more": "Több",
    "dash.no_card": "Nincs kibocsátott kártya",
    "dash.monthly_activity": "Havi aktivitás",
    "dash.operations": "Műveletek",
    "dash.holder": "Kártyabirtokos",
    "dash.expires": "Lejárat",
    "dash.visa_label": "VISA",
    "dash.balance": "Rendelkezésre álló egyenleg",
    "tx.all": "Összes",
    "tx.income": "Bevételek",
    "tx.expenses": "Kiadások",
    "tx.today": "Ma",
    "tx.yesterday": "Tegnap",
    "tx.none": "Nincs tranzakció.",
    "transfer.title": "Gyors átutalás",
    "transfer.recipient": "Kedvezményezett",
    "transfer.recipient_ph": "Név, IBAN, telefonszám…",
    "transfer.amount": "Összeg",
    "transfer.label": "Közlemény",
    "transfer.reason": "Indok (opcionális)",
    "transfer.reason_ph": "Pl.: bérleti díj, visszatérítés…",
    "transfer.execution": "Végrehajtás",
    "transfer.immediate": "Azonnali",
    "transfer.delayed": "Halasztott",
    "transfer.balance": "Rendelkezésre álló egyenleg:",
    "transfer.continue": "Folytatás",
    "transfer.confirm": "Megerősítés",
    "transfer.cancel": "Mégse",
    "transfer.code": "Ellenőrző kód",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Adja meg a kapott kódot az átutalás jóváhagyásához",
    "transfer.pending.title": "Átutalás jóváhagyásra vár",
    "transfer.pending.amount": "Összeg:",
    "transfer.pending.to": "Célszámla:",
    "transfer.pending.hint": "Az ellenőrző kódot e-mailben vagy az értesítési csengő segítségével küldjük el. A kód 6 számjegyből áll. Ha nem kapja meg, kérjük, lépjen kapcsolatba az ügyfélszolgálattal.",
    "transfer.pending.validate": "Átutalás jóváhagyása",
    "transfer.saved": "Átutalás mentve. Kérje az ellenőrző kódot az adminisztrátortól a jóváhagyáshoz.",
    "transfer.done": "Átutalás teljesítve",
    "transfer.validated": "Átutalás jóváhagyva!",
    "transfer.progress.processing": "Átutalás feldolgozása…",
    "transfer.progress.completed": "Átutalás teljesítve!",
    "transfer.progress.default_message": "Átutalása feldolgozás alatt áll. Kérjük várjon.",
    "cards.none": "Nincs kibocsátott kártya.\nVegye fel a kapcsolatot az adminisztrátorával.",
    "cards.holder": "Kártyabirtokos",
    "cards.expires": "Lejárat",
    "cards.block": "Zárolás",
    "cards.pin": "PIN-kód",
    "cards.more": "Több",
    "cards.limits": "Kártyalimitek",
    "cards.payment": "Fizetés",
    "cards.withdrawal": "Készpénzfelvétel",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Zárolt",
    "cards.status_active": "Aktív",
    "cards.toast.blocked": "Kártya zárolva",
    "cards.toast.unblocked": "Kártya feloldva",
    "cards.toast.physical_ordered": "Fizikai kártya megrendelve! Szállítás 5–10 munkanapon belül.",
    "cards.toast.created": "Kártya sikeresen létrehozva!",
    "cards.none_sub": "Nincs kibocsátott kártya. Hozza létre virtuális kártyáját most.",
    "cards.generate_btn": "Kártyám létrehozása",
    "cards.create.title": "Virtuális kártyám létrehozása",
    "cards.create.network": "Fizetési hálózat",
    "cards.create.color": "Kártyaszín",
    "cards.create.preview_name": "AZ ÖN NEVE",
    "cards.create.preview_expiry": "HH/ÉÉ",
    "cards.create.loading": "Létrehozás…",
    "cards.create.submit": "Kártya létrehozása",
    "cards.btn.unblock": "Feloldás",
    "cards.btn.block": "Zárolás",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fizikai",
    "cards.btn.physical_done": "Megrendelve",
    "cards.limits_title": "Limitek",
    "cards.reveal.title": "Kártyaadatok",
    "cards.reveal.number": "Teljes szám",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Lejárat",
    "cards.reveal.warning": "Soha ne ossza meg ezeket az adatokat. Használat után zárja be ezt az ablakot.",
    "cards.pin.title": "PIN-kód",
    "cards.pin.message": "Ez a PIN-kód az összes online fizetéséhez és készpénzfelvételéhez szükséges. Soha ne ossza meg senkivel.",
    "cards.physical.title": "Fizikai kártya megrendelése",
    "cards.physical.fee_label": "Szállítási díj",
    "cards.physical.balance_label": "Az Ön egyenlege",
    "cards.physical.insufficient": "Elégtelen egyenleg. Kérjük, töltse fel fiókját a rendelés előtt.",
    "cards.physical.info": "A díj levonásra kerül a fiókjából. Szállítás 5–10 munkanapon belül.",
    "cards.physical.order_btn": "Megrendelés ({fee})",
    "cards.physical.ordering": "Megrendelés…",
    "cards.physical.order_cta": "Fizikai kártya rendelése",
    "cards.physical.choose_type": "Válassza ki a kártyáját",
    "cards.physical.features": "Előnyök",
    "cards.buy_virtual_cta": "Virtuális kártya vásárlása",
    "notif.none": "Jelenleg nincsenek értesítések.",
    "notif.contact_support": "Kapcsolat az ügyfélszolgálattal",
    "profile.change_photo": "Fotó módosítása",
    "profile.remove_photo": "Fotó eltávolítása",
    "profile.email": "E-mail",
    "profile.phone": "Telefonszám",
    "profile.country": "Ország",
    "profile.address": "Cím",
    "profile.notifs_title": "Értesítések ({count})",
    "profile.no_notifs": "Nincs értesítés.",
    "profile.logout": "Kijelentkezés",
    "profile.photo_updated": "Profilfotó frissítve",
    "profile.photo_removed": "Fotó eltávolítva",
    "profile.photo_too_large": "A kép túl nagy (max. 1 MB)",
    "profile.powered": "Működteti: {bank}",
    "iban.title": "Saját IBAN",
    "iban.loading": "Betöltés…",
    "iban.bic": "BIC / SWIFT kód",
    "iban.share_info": "Ossza meg ezeket az adatokat, hogy átutalást fogadhasson a számlájára.",
    "iban.copy": "Másolás",
    "iban.copied": "Vágólapra másolva",
    "iban.copy_fail": "Másolás sikertelen",
    "iban.close": "Bezárás",
    "accounts.all": "Összes számlája a {bank}-nál.",
    "accounts.primary": "Főszámla",
    "accounts.savings": "Megtakarítási számla",
    "accounts.primary_tag": "Elsődleges",
    "accounts.secondary_tag": "Másodlagos",
    "accounts.objective": "Cél: {amount} · {pct}%",
    "accounts.copy": "Másolás",
    "accounts.loading": "Betöltés…",
    "accounts.iban_copied": "IBAN másolva",
    "accounts.copy_fail": "Másolás sikertelen",
    "savings.total": "Összes megtakarítás",
    "savings.target_total": "Teljes cél",
    "savings.progress": "Haladás",
    "savings.active": "Aktív célok",
    "savings.new_goal": "Új cél",
    "savings.none": "Még nincsenek megtakarítási célok.",
    "savings.none_sub": "Hozza létre első célját a kezdéshez.",
    "savings.saved": "Megtakarítva:",
    "savings.goal": "Cél:",
    "savings.deadline": "Határidő: {date}",
    "savings.deposit": "Befizetés",
    "savings.withdraw": "Kifizetés",
    "savings.loading": "Betöltés…",
    "savings.form.title": "Új megtakarítási cél",
    "savings.form.cancel": "Mégse",
    "savings.form.create": "Létrehozás",
    "savings.form.name": "Cél neve",
    "savings.form.name_ph": "Japán utazás, Autó, …",
    "savings.form.target": "Célösszeg",
    "savings.form.initial": "Kezdeti befizetés",
    "savings.form.deadline": "Határidő (opcionális)",
    "savings.form.color": "Szín",
    "savings.deposit_title": "Befizetés erre: \"{name}\"",
    "savings.deposit_amount": "Befizetendő összeg",
    "savings.balance_hint": "Főszámla egyenlege: {amount}",
    "savings.withdraw_prompt": "Mennyit vegyen ki innen: \"{name}\"?",
    "savings.close_confirm": "Lezárja a(z) \"{name}\" célt? A megtakarítás visszakerül a főszámlára.",
    "savings.toast.created": "Cél létrehozva",
    "savings.toast.deposited": "Befizetés teljesítve",
    "savings.toast.withdrawn": "Kifizetés teljesítve",
    "savings.toast.closed": "Cél lezárva",
    "invest.total": "Összes befektetés",
    "invest.profits": "Halmozott nyereség",
    "invest.count": "Befektetések",
    "invest.products": "Elérhető termékek",
    "invest.invest_btn": "Befektetés",
    "invest.my": "Saját befektetések",
    "invest.pending": "Függőben",
    "invest.validated": "Jóváhagyva",
    "invest.refused": "Elutasítva",
    "invest.withdraw.btn": "Kivesz",
    "invest.withdraw.title": "Befektetésem visszavonása",
    "invest.withdraw.intro": "Most fogja visszavonni a „{product}\" befektetését. A kezdeti tőke és a felhalmozott nyereség jóváírásra kerül a fő egyenlegén.",
    "invest.withdraw.principal": "Kezdeti tőke",
    "invest.withdraw.profit": "Nyereség",
    "invest.withdraw.total": "Összesen átvenni",
    "invest.withdraw.confirm": "Visszavonás megerősítése",
    "invest.withdraw.warning": "Ez a művelet visszafordíthatatlan. A befektetés lezárásra kerül.",
    "invest.withdraw.success": "Befektetés visszavonva — {amount} jóváírva az egyenlegén.",
    "invest.withdrawn": "Visszavonva",
    "invest.cta_sub": "Válassza ki az Önnek megfelelő befektetési típust. Tanácsadónk az első kérelmétől fogva segítséget nyújt.",
    "invest.form.amount": "Befektetendő összeg",
    "invest.form.balance": "Rendelkezésre álló egyenleg: {amount}",
    "invest.form.note": "Megjegyzés tanácsadójának (opcionális)",
    "invest.form.note_ph": "Pontosítsa elvárásait…",
    "invest.form.advisor_info": "Automatikusan megnyílik egy beszélgetés a tanácsadójával.",
    "invest.form.cancel": "Mégse",
    "invest.form.submit": "Kérelem küldése",
    "invest.toast.requested": "Kérelem rögzítve — megnyílt egy beszélgetés a tanácsadójával.",
    "invest.p1.name": "Ingatlan",
    "invest.p1.desc": "SCPI, papíralapú ingatlan, ingatlan crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Kiválasztott projektek közösségi finanszírozása",
    "invest.p3.name": "Megújuló energia",
    "invest.p3.desc": "Napenergia, szélenergia és zöld projektek",
    "invest.p4.name": "Tőzsde",
    "invest.p4.desc": "Részvények, ETF-ek és diverzifikált alapok",
    "invest.p5.name": "Részvételi",
    "invest.p5.desc": "Hitelek kkv-knak és helyi vállalkozóknak",
    "invest.p6.name": "Kripto",
    "invest.p6.desc": "Bitcoin, Ethereum és többdevizás tárca",
    "msg.online": "Online",
    "msg.loading": "Betöltés…",
    "msg.none": "Nincs üzenet. Kezdje el a beszélgetést tanácsadójával.",
    "msg.placeholder": "Írja be üzenetét…",
    "msg.attach.image": "Kép",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Hang",
    "msg.attach.file": "Fájl / Dokumentum",
    "msg.pdf_doc": "PDF dokumentum",
    "msg.file": "Fájl",
    "msg.too_large": "A fájl túl nagy (max. {size}).",
    "msg.withdraw": "Visszavonás",
    "credits.cta_sub": "Igényeljen közvetlenül online — válasz 24 órán belül.",
    "credits.learn_more": "Tudjon meg többet",
    "credits.request": "Igénylés",
    "credits.history": "Saját igénylések",
    "credits.status.pending": "Függőben",
    "credits.status.accepted": "Elfogadva",
    "credits.status.refused": "Elutasítva",
    "credits.chars": "Jellemzők",
    "credits.amount_range": "Összeg: {min}€ – {max}€",
    "credits.rate_range": "Kamatláb: {min}% – {max}% évente",
    "credits.duration_range": "Futamidő: {min}–{max} hónap",
    "credits.deposit_min": "Kezdeti befizetés: minimum {amount}€",
    "credits.garantie": "Fedezet: {garantie}",
    "credits.eligibility": "Jogosultsági feltételek",
    "credits.elig.income": "Rendszeres és stabil jövedelem igazolása",
    "credits.elig.deposit": "Kezdeti befizetés: {amount}€",
    "credits.elig.garantie": "A fedezet bemutatása: {garantie}",
    "credits.form.title": "Igénylés — {name}",
    "credits.form.cancel": "Mégse",
    "credits.form.submit": "Igénylés küldése",
    "credits.form.amount": "Kívánt összeg (€)",
    "credits.form.duration": "Futamidő (hónap)",
    "credits.form.reason": "Indok (opcionális)",
    "credits.form.reason_ph": "Ismertesse projektjét…",
    "credits.form.close": "Bezárás",
    "credits.toast.submitted": "Hiteligénylés elküldve — 24 órán belül választ kap.",
    "credits.p1.name": "Személyi kölcsön",
    "credits.p1.desc": "Mindennapi szükségletek vagy személyes projektek finanszírozásához.",
    "credits.p2.name": "Lakáshitel",
    "credits.p2.desc": "Ingatlan vásárlásának vagy építésének finanszírozásához.",
    "credits.p3.name": "Autó- / motorhitel",
    "credits.p3.desc": "Vásároljon járművet gond nélkül.",
    "credits.p4.name": "Diákhitel",
    "credits.p4.desc": "Pénzügyi segítség felsőoktatási tanulmányokhoz.",
    "credits.p5.name": "Vállalkozói hitel",
    "credits.p5.desc": "Szakmai tevékenység növekedésének támogatásához.",
    "credits.p6.name": "Expressz kölcsön",
    "credits.p6.desc": "Gyors megoldás sürgős szükségletekre.",
    "financing.reimbursable": "Visszatérítendő finanszírozás",
    "financing.reimbursable_desc": "Pénzügyi támogatás rögzített feltételekkel tevékenység indításához vagy fejlesztéséhez.",
    "financing.non_reimbursable": "Vissza nem térítendő finanszírozás",
    "financing.non_reimbursable_desc": "Társadalmi, oktatási vagy humanitárius hatású projektek számára (NGO-k, szervezetek).",
    "financing.cta_sub": "Személyre szabott megoldások ambícióinak megvalósításához.",
    "financing.request": "Finanszírozás igénylése",
    "financing.history": "Saját igénylések",
    "financing.reimbursable_label": "Visszatérítendő",
    "financing.non_reimbursable_label": "Vissza nem térítendő",
    "financing.status.pending": "Függőben",
    "financing.status.granted": "Jóváhagyva",
    "financing.status.refused": "Elutasítva",
    "financing.form.title": "Igénylés — {name}",
    "financing.form.cancel": "Mégse",
    "financing.form.submit": "Igénylés küldése",
    "financing.form.project": "Projekt neve",
    "financing.form.project_ph": "Saját projektem",
    "financing.form.amount": "Igényelt összeg (€)",
    "financing.form.sector": "Szektor",
    "financing.form.sector_ph": "Tech, mezőgazdaság, …",
    "financing.form.description": "Projekt leírása",
    "financing.form.description_ph": "Mutassa be projektjét, céljait és várható hatását…",
    "settings.security": "Biztonság",
    "settings.current_pw": "Jelenlegi jelszó",
    "settings.new_pw": "Új jelszó",
    "settings.confirm_pw": "Megerősítés",
    "settings.submit": "Jelszó módosítása",
    "settings.no_match": "A jelszavak nem egyeznek",
    "settings.changed": "Jelszó módosítva",
    "common.cancel": "Mégse",
    "common.confirm": "Megerősítés",
    "common.close": "Bezárás",
    "common.save": "Mentés",
    "common.loading": "Betöltés…",
    "common.send": "Küldés",
    "common.total": "Összesen",
    "admin.wallet.balance": "Elérhető egyenleg",
    "admin.wallet.history": "Előzmények",
    "admin.wallet.no_history": "Nincs tranzakció",
    "admin.wallet.title": "Pénztárcám",
    "admin.wallet.topup": "Feltöltés",
    "beneficiary.pick": "Kiválaszt",
    "beneficiary.save_this": "Kedvezményezett mentése",
    "beneficiary.saved": "Mentett kedvezményezettek",
    "beneficiary.saved_ok": "Kedvezményezett elmentve",
    "card.color.amber": "Borostyán",
    "card.color.cyan": "Ciánkék",
    "card.color.emerald": "Smaragd",
    "card.color.forest": "Erdőzöld",
    "card.color.gold": "Arany",
    "card.color.lavender": "Levendula",
    "card.color.night": "Éjszaka",
    "card.color.orange": "Narancs",
    "card.color.pink": "Rózsaszín",
    "card.color.royal": "Királykék",
    "card.color.ruby": "Rubin",
    "card.color.slate": "Palaszürke",
    "card.color.steel": "Acél",
    "card.color.terracotta": "Terrakotta",
    "card.color.violet": "Lila",
    "common.other": "Egyéb",
    "common.search": "Keresés",
    "content.translated": "Automatikusan lefordított tartalom",
    "credits.monthly_payment": "Becsült havi törlesztő",
    "credits.rate_annual": "/ év",
    "credits.rate_used": "Becsült kamatláb:",
    "credits.simulation": "Havi törlesztő szimuláció",
    "credits.total_cost": "Becsült teljes költség",
    "demo.banner": "Demonstrációs mód — nincs valódi művelet",
    "demo.disabled": "Művelet nem elérhető demó módban",
    "deposit.default_instructions": "Vegye fel a kapcsolatot kezelőjével a befizetés részleteiért.",
    "error.back_home": "Vissza a főoldalra",
    "error.bank_not_found": "Bank nem található",
    "error.unknown": "Ismeretlen hiba",
    "kyc.file_read_error": "A fájl nem olvasható",
    "kyc.file_too_large": "A fájl túl nagy (max. 5 MB)",
    "login.history": "Bejelentkezési előzmények",
    "login.history.empty": "Nincs rögzített bejelentkezés.",
    "notif.dismiss": "Bezárás",
    "notif.locked": "Banki üzenet",
    "tracking.error.not_found": "A nyomkövetési szám nem található. Ellenőrizze és próbálja újra.",
    "tracking.label": "Nyomkövetés",
    "tracking.status.delivered": "Kézbesítve",
    "tracking.status.preparation": "Előkészítés alatt",
    "tracking.status.shipped": "Feladva",
    "tracking.status.transit": "Szállítás alatt",
    "transfer.code.copied": "Másolva",
    "transfer.code.copy": "Másolás",
    "transfer.code.received": "Az érvényesítési kódod",
    "dash.welcome": "Üdvözöljük",
    "common.delete": "Törlés",
    "cards.delete.title": "Kártya törlése",
    "cards.delete.confirm_msg": "Ez a művelet visszavonhatatlan. A kártya véglegesen törlődik.",
    "cards.delete.physical_tooltip": "Fizikai kártya megrendelve — törlés lehetetlen",
    "cards.order.confirmed": "Rendelés megerősítve!",
    "cards.order.confirmed_body": "Fizikai kártyája készítés alatt áll. Használja ezt a számot a szállítás nyomon követéséhez.",
    "cards.order.tracking_no": "Követési sz.",
    "cards.fee_deducted": "{amount} levonásra kerül az egyenlegéből",
    "cards.insufficient_short": "Elégtelen egyenleg ({available} elérhető)",
    "transfer.section.beneficiary": "Kedvezményezett",
    "transfer.section.details": "Átutalás",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bank",
    "transfer.field.city": "Város",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bank:",
    "transfer.progress.paused": "Érvényesítésre vár",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Változó",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Kártya törölve",
    "cards.feat.gold_exclusive": "Exkluzív aranykártya",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Vásárlóvédelem",
    "cards.feat.support_standard": "Normál támogatás",
    "cards.feat.plastic_premium": "Prémium műanyag kártya",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Normál utasbiztosítás",
    "cards.feat.support_priority": "Elsőbbségi támogatás",
    "cards.feat.metal_brushed": "Csiszolt fém kártya",
    "cards.feat.cashback_2_unlimited": "Korlátlan 2% cashback",
    "cards.feat.travel_premium": "Prémium utasbiztosítás",
    "cards.feat.vip_lounge": "VIP váró hozzáférés",
    "cards.feat.support_247": "24/7 segítség",
    "auth.demo.title": "Adminisztrátor bejelentkezés",
    "auth.demo.subtitle": "Adja meg az admin hitelesítő adatokat a demó előnézetéhez",
    "credits.garantie.none": "Nincs",
    "credits.garantie.mortgage": "Jelzálog a finanszírozott ingatlanon",
    "credits.garantie.vehicle": "Finanszírozott jármű",
    "credits.garantie.parental": "Szülői vagy banki",
    "credits.garantie.professional": "Szakmai eszközök vagy személyes garancia",
    "cards.reveal.pin_prompt": "Adja meg a 4 jegyű PIN kódját a kártyaadatok megjelenítéséhez.",
    "cards.reveal.show": "Megjelenítés",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "A két PIN kód nem egyezik",
    "auth.pin.digit_hint": "4 számjegy",
  
  },
  fi: {
    "notif.transfer_code.title": "Tilisiirron vahvistuskoodi",
    "notif.transfer_code.body": "Kopioi tämä koodi vahvistaaksesi tilisiirron.",
    "notif.transfer_code.expires_in": "Katoaa {sec}s kuluttua",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} kk",
    "credits.n_months": "{n} kk",
    "msg.read": "Luettu",
    "msg.sent": "Lähetetty",
    "msg.attach_aria": "Liitä tiedosto",
    "login.device.unknown": "Tuntematon selain",
    "login.device.mobile": "Mobiili",
    "login.device.tablet": "Tabletti",
    "login.device.desktop": "Tietokone",
    "login.recent": "Viimeisin",
    "err.invalid_credentials": "Virheelliset kirjautumistiedot",
    "err.bad_pin": "Virheellinen PIN",
    "err.account_blocked": "Tili lukittu",
    "err.bad_current_pw": "Nykyinen salasana on virheellinen",
    "err.insufficient_funds": "Riittämättömät varat, lataa tilisi.",
    "err.amount_above_savings": "Summa ylittää käytettävissä olevat säästöt",
    "err.must_be_positive": "Arvon on oltava positiivinen",
    "err.invalid_format": "Virheellinen muoto",
    "err.email_taken": "Sähköposti jo käytössä",
    "err.card_not_found": "Korttia ei löydy",
    "err.physical_card_already_ordered": "Fyysinen kortti on jo tilattu",
    "err.beneficiary_not_found": "Saajaa ei löydy",
    "err.invalid_code": "Virheellinen koodi",
    "err.no_transfer_code": "Ei koodia tälle siirrolle",
    "err.link_expired": "Linkki virheellinen tai vanhentunut",
    "err.thread_not_found": "Keskustelua ei löydy",
    "err.empty_message": "Tyhjä viesti",
    "err.file_too_large": "Tiedosto liian suuri (max 20 MB)",
    "err.forbidden": "Käyttö estetty",
    "err.bank_not_found": "Pankkia ei löydy",
    "err.signups_closed": "Rekisteröitymiset on suljettu tälle pankille.",
    "err.access_suspended": "Käyttö väliaikaisesti keskeytetty. Ole hyvä ja ota yhteyttä pankkiisi.",
    "err.notif_not_closable": "Tätä ilmoitusta ei voi sulkea.",
    "auth.signup.tagline": "Liity joukkoomme tänään",
    "auth.login.tagline": "Tervetuloa takaisin!",
    "auth.signup.tagline_sub": "Avaa tilisi 2 minuutissa ja käytä kaikkia premium-pankkipalvelujamme.",
    "auth.login.tagline_sub": "Kirjaudu henkilökohtaiseen tilaasi ja hallinnoi talouttasi turvallisesti.",
    "auth.back": "Takaisin etusivulle",
    "auth.bullet.security": "Sotilastason pankkiturvallisuus",
    "auth.bullet.transfers": "Välittömät siirrot 24/7",
    "auth.bullet.investments": "Integroidut sijoitukset ja säästöt",
    "auth.bullet.advisor": "Henkilökohtainen neuvoja saatavilla 7 päivää viikossa",
    "auth.footer": "Palvelun tarjoaa {bank}",
    "auth.signup.title": "Luo tili",
    "auth.login.title": "Kirjaudu sisään",
    "auth.field.lastName": "Sukunimi",
    "auth.field.firstName": "Etunimi",
    "auth.field.country": "Maa",
    "auth.field.country.placeholder": "— Valitse maasi —",
    "auth.field.phone": "Puhelinnumero",
    "auth.field.address": "Osoite",
    "auth.field.address.placeholder": "Esimerkkikatu 123, Helsinki",
    "auth.field.email": "Sähköposti",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Salasana",
    "auth.submit.signup": "Luo tilini",
    "auth.submit.login": "Kirjaudu sisään",
    "auth.login.badge": "Kirjaudu tilillesi",
    "auth.card.tagline": "Asiakasalueesi",
    "auth.submit.loading": "Ladataan…",
    "auth.pin.title": "PIN-koodi",
    "auth.pin.subtitle": "Anna 4-numeroinen PIN-koodisi",
    "auth.pin.label": "PIN-koodi",
    "auth.pin.confirm": "Vahvista PIN-koodi",
    "auth.pin.submit": "Vahvista",
    "auth.pin.back": "Takaisin kirjautumiseen",
    "auth.pin.length_error": "PIN-koodissa on oltava 4 numeroa",
    "auth.switch.have_account": "Onko sinulla jo tili?",
    "auth.switch.login_link": "Kirjaudu sisään",
    "auth.switch.no_account": "Ei vielä asiakkaana?",
    "auth.error.select_country": "Valitse maasi",
    "kyc.title": "Henkilöllisyyden varmennus (KYC)",
    "kyc.sub": "Lataa asiakirjasi tilisi vahvistamiseksi. Kaikki asiakirjat ovat salattuja ja luottamuksellisia.",
    "kyc.id_type": "Henkilöllisyysasiakirjan tyyppi *",
    "kyc.id_card": "Henkilökortti",
    "kyc.passport": "Passi",
    "kyc.driver_license": "Ajokortti",
    "kyc.recto_passport": "Passin etusivu *",
    "kyc.recto_id": "Asiakirjan etusivu *",
    "kyc.verso": "Asiakirjan takasivu *",
    "kyc.selfie": "Selfi henkilöllisyystodistuksen kanssa *",
    "kyc.address_proof": "Osoitetodistus *",
    "kyc.hint_doc": "Kuva tai PDF, enintään 5 Mt",
    "kyc.hint_selfie": "Selkeä kuva, kasvot ja asiakirja näkyvissä",
    "kyc.hint_address": "Lasku, tiliote, todistus… (enintään 5 Mt)",
    "kyc.upload": "Lataa",
    "kyc.replace": "Korvaa",
    "pending.title": "Tili odottaa hyväksyntää",
    "pending.body": "Hakemuksesi on vastaanotettu. Järjestelmänvalvoja tarkistaa tietosi ja aktivoi tilisi pian.",
    "pending.back": "Takaisin kirjautumiseen",
    "home.nav.features": "Ominaisuudet",
    "home.nav.security": "Turvallisuus",
    "home.nav.invest": "Sijoitukset",
    "home.nav.credits": "Lainat",
    "home.nav.faq": "UKK",
    "home.nav.login": "Kirjaudu sisään",
    "home.hero.tagline": "Seuraavan sukupolven verkkopankki",
    "home.hero.title1": "Hallinnoi rahojasi",
    "home.hero.title2": "älykkäästi",
    "home.hero.sub": "Täydellinen pankkialusta — siirrot, säästöt, sijoitukset ja lainat älypuhelimellasi.",
    "home.hero.login": "Kirjaudu sisään",
    "home.hero.trust.secure": "100% Turvallinen",
    "home.hero.trust.fast": "Nopea avaus",
    "home.hero.trust.encrypted": "Salatut tiedot",
    "home.hero.trust.support": "24/7 Palvelu",
    "home.mockup.balance": "Käytettävissä oleva saldo",
    "home.mockup.currency": "EUR · Päätili",
    "home.mockup.recharge": "Lataa",
    "home.mockup.send": "Lähetä",
    "home.mockup.save": "Säästä",
    "home.mockup.salary": "Maaliskuun palkka",
    "home.mockup.rent": "Huhtikuun vuokra",
    "home.mockup.investment": "Sijoitus",
    "home.mockup.days2": "2 päivää sitten",
    "home.mockup.days3": "3 päivää sitten",
    "home.mockup.days5": "5 päivää sitten",
    "home.stats.accounts": "Aktiiviset tilit",
    "home.stats.uptime": "Käytettävyys",
    "home.stats.transfers": "Siirrot",
    "home.stats.support": "Tuki",
    "home.features.label": "Ominaisuudet",
    "home.features.title": "Kaikki mitä tarvitset",
    "home.features.sub": "Täydellinen alusta, joka on suunniteltu yksinkertaistamaan päivittäistä taloudenhallintaasi.",
    "home.feature.security": "Sotilastason turvallisuus",
    "home.feature.security_body": "AES-256-salaus, kaksivaiheinen todennus ja 24/7-tilivalvonta.",
    "home.feature.cards": "Virtuaali- ja fyysiset kortit",
    "home.feature.cards_body": "Myönnä kortteja sekunneissa tai tilaa oikea fyysinen kortti toimitettuna kotiovellesi.",
    "home.feature.transfers": "Pikasisirrot",
    "home.feature.transfers_body": "Lähetä ja vastaanota rahaa kaikkialle maailmaan sekunneissa.",
    "home.feature.analytics": "Reaaliaikainen analyysi",
    "home.feature.analytics_body": "Älykkäät kojelaudat menojen seurantaan ja taloutesi ennakointiin.",
    "home.feature.investments": "Integroidut sijoitukset",
    "home.feature.investments_body": "Käytä valittuja sijoitustuotteita suoraan tililtäsi.",
    "home.feature.support": "Omistettu 24/7-tuki",
    "home.feature.support_body": "Henkilökohtainen pankkineuvoja saatavilla milloin tahansa turvallisen viestinnän kautta.",
    "home.tpl.label": "Luottamus",
    "home.tpl.title": "He luottavat meihin",
    "home.mockup.balance_label": "Saldo",
    "home.mockup.virement": "Siirto",
    "home.mockup.salaire": "Palkka",
    "home.security.label": "Turvallisuus",
    "home.security.title1": "Taloutesi",
    "home.security.title2": "täydellisessä turvassa",
    "home.security.sub": "Sovellamme tiukimpia pankkiturvallisuusstandardeja jokaisen sentin suojaamiseksi.",
    "home.security.aes.title": "AES-256-salaus",
    "home.security.aes.body": "Kaikki arkaluontoiset tiedot salataan samoilla algoritmeilla kuin suuret kansainväliset pankit käyttävät.",
    "home.security.auth.title": "Vahvistettu todennus",
    "home.security.auth.body": "Kaksinkertainen henkilöllisyyden varmennus jokaisella kirjautumiskerralla ja jokaisessa arkaluonteisessa toimenpiteessä.",
    "home.security.compliance.title": "PSD2/GDPR-vaatimustenmukaisuus",
    "home.security.compliance.body": "Noudatamme kaikkia eurooppalaisia maksupalveluita ja tietosuojaa koskevia asetuksia.",
    "home.invest.label": "Sijoitukset",
    "home.invest.title": "Kasvata rahojasi",
    "home.invest.sub": "6 sijoituskategoriaa, valittu niiden potentiaalin ja luotettavuuden perusteella.",
    "home.invest.yield": "Tuotto",
    "home.credits.label": "Lainat",
    "home.credits.title": "Ratkaisuja jokaiseen projektiin",
    "home.credits.sub": "Kilpailukykyiset korot, nopeat vastaukset ja yksilöllinen tuki kaikkiin projekteihisi.",
    "home.credits.rate": "Korko",
    "home.faq.label": "UKK",
    "home.faq.title": "Usein kysytyt kysymykset",
    "home.faq.q1": "Kuinka avaan tilin?",
    "home.faq.a1": "Rekisteröityminen vie alle 2 minuuttia. Täytä henkilötietosi, valitse salasana, ja tilisi aktivoidaan tiimimme vahvistuksen jälkeen.",
    "home.faq.q2": "Ovatko tietoni turvassa?",
    "home.faq.a2": "Kyllä. Kaikki tiedot salataan AES-256:lla levossa ja TLS 1.3:lla siirrossa. Noudatamme kansainvälisiä pankkistandardeja ja GDPR/PSD2-asetuksia.",
    "home.faq.q3": "Mitä sijoitusvaihtoehtoja tarjoatte?",
    "home.faq.a3": "Tarjoamme 6 kategoriaa: Kiinteistöt, Joukkorahoitus, Uusiutuva energia, Pörssi, Osallistava ja Krypto. Jokainen tuote valitaan riski/tuotto-suhteensa perusteella.",
    "home.faq.q4": "Kuinka haen lainaa?",
    "home.faq.a4": "Siirry asiakasalueesi Laina-osioon, valitse lainatyyppi, syötä summa ja kesto. Hakemuksesi käsitellään 24 tunnin sisällä neuvojiltamme.",
    "home.faq.q5": "Onko piilomaksuja?",
    "home.faq.a5": "Ei piilomaksuja. Kaikki hinnat näytetään selvästi jokaisen toimenpiteen yhteydessä. Ehdot ovat saatavilla asiakasalueellasi milloin tahansa.",
    "home.faq.q6": "Kuinka otan yhteyttä tukeen?",
    "home.faq.a6": "Käytä asiakasalueesi integroitua viestintää ottaaksesi suoraan yhteyttä henkilökohtaiseen pankkineuvoja, joka on saatavilla 24/7.",
    "home.cta.title": "Valmis aloittamaan?",
    "home.cta.sub": "Liity tuhansiin käyttäjiin, jotka hallitsevat talouttaan {bank}:n kanssa. Rekisteröityminen vie alle 2 minuuttia.",
    "home.footer.products": "Tuotteet",
    "home.footer.services": "Palvelut",
    "home.footer.access": "Pikakäyttö",
    "home.footer.accounts": "Tilit",
    "home.footer.transfers": "Siirrot",
    "home.footer.cards": "Kortit",
    "home.footer.savings": "Säästöt",
    "home.footer.investments": "Sijoitukset",
    "home.footer.credits": "Lainat",
    "home.footer.financing": "Rahoitus",
    "home.footer.messages": "Viestit",
    "home.footer.login": "Kirjaudu sisään",
    "home.footer.signup": "Avaa tili",
    "home.footer.support": "24/7 Tuki",
    "home.footer.tagline": "Verkkopankkisi, saatavilla kaikkialla ja aina.",
    "home.footer.copyright": "© {year} {bank} — Kaikki oikeudet pidätetään",
    "home.footer.powered": "Palvelun tarjoaa {bank}",
    "app.menu_label": "Päävalikko",
    "app.nav.dashboard": "Kojelauta",
    "app.nav.accounts": "Tilit",
    "app.nav.transactions": "Tapahtumat",
    "app.nav.transfers": "Siirrot",
    "app.nav.cards": "Kortit",
    "app.nav.savings": "Säästöt",
    "app.nav.investments": "Sijoitukset",
    "app.nav.credit": "Laina",
    "app.nav.financing": "Rahoitus",
    "app.nav.messages": "Viestit",
    "app.nav.card_tracking": "Kortin seuranta",
    "app.nav.notifications": "Ilmoitukset",
    "app.nav.settings": "Asetukset",
    "app.nav.profile": "Profiili",
    "app.nav.mobile.home": "Etusivu",
    "app.nav.mobile.transfers": "Siirrot",
    "app.nav.mobile.cards": "Kortit",
    "app.nav.mobile.transactions": "Tapahtumat",
    "app.nav.more.invest": "Sijoita",
    "app.user_role": "Asiakas",
    "app.logout": "Kirjaudu ulos",
    "app.search": "Hae…",
    "app.more_title": "Lisää ominaisuuksia",
    "app.more_btn": "Lisää",
    "app.loading": "Ladataan…",
    "app.error_back": "Takaisin kirjautumiseen",
    "app.see_more_notifs": "Näytä {count} muuta ilmoitusta →",
    "page.accounts": "Omat tilit",
    "page.transactions": "Tapahtumat",
    "page.transfers": "Uusi siirto",
    "page.deposit": "Talletus",
    "page.cards": "Omat kortit",
    "page.savings": "Säästöt",
    "page.investments": "Sijoitukset",
    "page.credits": "Laina",
    "page.financing": "Projektin rahoitus",
    "page.messages": "Viestit",
    "page.settings": "Asetukset",
    "page.notifications": "Tärkeät ilmoitukset",
    "page.profile": "Oma profiili",
    "dash.greeting": "Hei, {name}",
    "dash.overview": "Talousyhteenveto — {date}",
    "dash.total_balance": "Kokonaissaldo",
    "dash.this_month": "+{amount} tässä kuussa",
    "dash.view_iban": "Näytä IBAN-numeroni",
    "dash.my_accounts": "Omat tilit",
    "dash.primary_account": "Päätili",
    "dash.savings_account": "Säästötili",
    "dash.remunerated": "Korollinen",
    "dash.details": "Tiedot",
    "dash.transfer_btn": "Siirto",
    "dash.quick_actions": "Pikatoiminnot",
    "dash.quick.deposit": "Talletus",
    "deposit.title": "Talletustiedot",
    "dash.quick.transfer": "Siirto",
    "dash.quick.history": "Historia",
    "dash.quick.cards": "Omat kortit",
    "dash.quick.profile": "Profiili",
    "dash.monthly_budget": "Kuukausibudjetti",
    "dash.income": "Tulot",
    "dash.expenses": "Menot",
    "dash.spending_cat": "Menot kategorioittain",
    "dash.no_spending": "Ei kirjattuja menoja.",
    "dash.recent_tx": "Viimeisimmät tapahtumat",
    "dash.see_all": "Näytä kaikki",
    "dash.no_tx": "Ei tapahtumia.",
    "dash.main_card": "Pääkorttini",
    "dash.manage": "Hallinnoi",
    "dash.block": "Estä",
    "dash.pin": "PIN-koodi",
    "dash.security": "Turvallisuus",
    "dash.more": "Lisää",
    "dash.no_card": "Ei myönnettyä korttia",
    "dash.monthly_activity": "Kuukausitoiminta",
    "dash.operations": "Toimenpiteet",
    "dash.holder": "Kortinhaltija",
    "dash.expires": "Voimassa",
    "dash.visa_label": "VISA",
    "dash.balance": "Käytettävissä oleva saldo",
    "tx.all": "Kaikki",
    "tx.income": "Tulot",
    "tx.expenses": "Menot",
    "tx.today": "Tänään",
    "tx.yesterday": "Eilen",
    "tx.none": "Ei tapahtumia.",
    "transfer.title": "Pikasisirto",
    "transfer.recipient": "Saaja",
    "transfer.recipient_ph": "Nimi, IBAN, puhelinnumero…",
    "transfer.amount": "Summa",
    "transfer.label": "Viite",
    "transfer.reason": "Syy (valinnainen)",
    "transfer.reason_ph": "Esim.: vuokra, hyvitys…",
    "transfer.execution": "Suoritus",
    "transfer.immediate": "Välitön",
    "transfer.delayed": "Viivästetty",
    "transfer.balance": "Käytettävissä oleva saldo:",
    "transfer.continue": "Jatka",
    "transfer.confirm": "Vahvista",
    "transfer.cancel": "Peruuta",
    "transfer.code": "Vahvistuskoodi",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Syötä saamasi koodi siirron vahvistamiseksi",
    "transfer.pending.title": "Siirto odottaa vahvistusta",
    "transfer.pending.amount": "Summa:",
    "transfer.pending.to": "Saaja:",
    "transfer.pending.hint": "Vahvistuskoodisi lähetetään sähköpostitse tai ilmoituskellon kautta. Koodi koostuu 6 numerosta. Jos et saa sitä, ota yhteyttä tukeen.",
    "transfer.pending.validate": "Vahvista siirto",
    "transfer.saved": "Siirto tallennettu. Pyydä vahvistuskoodi järjestelmänvalvojalta.",
    "transfer.done": "Siirto suoritettu",
    "transfer.validated": "Siirto vahvistettu!",
    "transfer.progress.processing": "Siirtoa käsitellään…",
    "transfer.progress.completed": "Siirto suoritettu!",
    "transfer.progress.default_message": "Siirtoasi käsitellään. Odota hetki.",
    "cards.none": "Ei myönnettyä korttia.\nOta yhteyttä järjestelmänvalvojaasi.",
    "cards.holder": "Kortinhaltija",
    "cards.expires": "Voimassa",
    "cards.block": "Lukitse",
    "cards.pin": "PIN-koodi",
    "cards.more": "Lisää",
    "cards.limits": "Korttirajoitukset",
    "cards.payment": "Maksu",
    "cards.withdrawal": "Nosto",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Estetty",
    "cards.status_active": "Aktiivinen",
    "cards.toast.blocked": "Kortti estetty",
    "cards.toast.unblocked": "Kortin esto poistettu",
    "cards.toast.physical_ordered": "Fyysinen kortti tilattu! Toimitus 5–10 arkipäivän kuluessa.",
    "cards.toast.created": "Kortti luotu onnistuneesti!",
    "cards.none_sub": "Ei myönnettyä korttia. Luo virtuaalikorttisi nyt.",
    "cards.generate_btn": "Luo korttini",
    "cards.create.title": "Luo virtuaalikorttini",
    "cards.create.network": "Maksuverkko",
    "cards.create.color": "Kortin väri",
    "cards.create.preview_name": "NIMESI",
    "cards.create.preview_expiry": "KK/VV",
    "cards.create.loading": "Luodaan…",
    "cards.create.submit": "Luo kortti",
    "cards.btn.unblock": "Poista esto",
    "cards.btn.block": "Estä",
    "cards.btn.info": "Tiedot",
    "cards.btn.physical": "Fyysinen",
    "cards.btn.physical_done": "Tilattu",
    "cards.limits_title": "Rajat",
    "cards.reveal.title": "Kortin tiedot",
    "cards.reveal.number": "Koko numero",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Voimassaolo",
    "cards.reveal.warning": "Älä koskaan jaa näitä tietoja. Sulje tämä ikkuna käytön jälkeen.",
    "cards.pin.title": "PIN-koodi",
    "cards.pin.message": "Tätä PIN-koodia käytetään kaikkiin verkkomaksuihin ja nostoihin. Älä koskaan kerro sitä kenellekään.",
    "cards.physical.title": "Tilaa fyysinen kortti",
    "cards.physical.fee_label": "Toimitusmaksu",
    "cards.physical.balance_label": "Saldosi",
    "cards.physical.insufficient": "Riittämätön saldo. Lataa tilisi ennen tilaamista.",
    "cards.physical.info": "Maksu veloitetaan tililtäsi. Toimitus 5–10 arkipäivän kuluessa.",
    "cards.physical.order_btn": "Tilaa ({fee})",
    "cards.physical.ordering": "Tilataan…",
    "cards.physical.order_cta": "Tilaa fyysinen kortti",
    "cards.physical.choose_type": "Valitse korttisi",
    "cards.physical.features": "Edut",
    "cards.buy_virtual_cta": "Osta virtuaalikortti",
    "notif.none": "Ei ilmoituksia tällä hetkellä.",
    "notif.contact_support": "Ota yhteyttä tukeen",
    "profile.change_photo": "Vaihda kuva",
    "profile.remove_photo": "Poista kuva",
    "profile.email": "Sähköposti",
    "profile.phone": "Puhelinnumero",
    "profile.country": "Maa",
    "profile.address": "Osoite",
    "profile.notifs_title": "Ilmoitukset ({count})",
    "profile.no_notifs": "Ei ilmoituksia.",
    "profile.logout": "Kirjaudu ulos",
    "profile.photo_updated": "Profiilikuva päivitetty",
    "profile.photo_removed": "Kuva poistettu",
    "profile.photo_too_large": "Kuva on liian suuri (enintään 1 Mt)",
    "profile.powered": "Palvelun tarjoaa {bank}",
    "iban.title": "Oma IBAN",
    "iban.loading": "Ladataan…",
    "iban.bic": "BIC / SWIFT-koodi",
    "iban.share_info": "Jaa nämä tiedot vastaanottaaksesi siirron tilillesi.",
    "iban.copy": "Kopioi",
    "iban.copied": "Kopioitu leikepöydälle",
    "iban.copy_fail": "Kopiointi epäonnistui",
    "iban.close": "Sulje",
    "accounts.all": "Kaikki tilisi {bank}:ssa.",
    "accounts.primary": "Päätili",
    "accounts.savings": "Säästötili",
    "accounts.primary_tag": "Ensisijainen",
    "accounts.secondary_tag": "Toissijainen",
    "accounts.objective": "Tavoite: {amount} · {pct}%",
    "accounts.copy": "Kopioi",
    "accounts.loading": "Ladataan…",
    "accounts.iban_copied": "IBAN kopioitu",
    "accounts.copy_fail": "Kopiointi epäonnistui",
    "savings.total": "Säästöt yhteensä",
    "savings.target_total": "Kokonaistavoite",
    "savings.progress": "Edistyminen",
    "savings.active": "Aktiiviset tavoitteet",
    "savings.new_goal": "Uusi tavoite",
    "savings.none": "Ei säästötavoitteita vielä.",
    "savings.none_sub": "Luo ensimmäinen tavoitteesi aloittaaksesi.",
    "savings.saved": "Säästetty:",
    "savings.goal": "Tavoite:",
    "savings.deadline": "Määräaika: {date}",
    "savings.deposit": "Talleta",
    "savings.withdraw": "Nosta",
    "savings.loading": "Ladataan…",
    "savings.form.title": "Uusi säästötavoite",
    "savings.form.cancel": "Peruuta",
    "savings.form.create": "Luo",
    "savings.form.name": "Tavoitteen nimi",
    "savings.form.name_ph": "Japanin matka, Auto, …",
    "savings.form.target": "Tavoitesumma",
    "savings.form.initial": "Alkutalletus",
    "savings.form.deadline": "Määräaika (valinnainen)",
    "savings.form.color": "Väri",
    "savings.deposit_title": "Talleta kohteeseen \"{name}\"",
    "savings.deposit_amount": "Talletettava summa",
    "savings.balance_hint": "Päätilin saldo: {amount}",
    "savings.withdraw_prompt": "Kuinka paljon nostetaan kohteesta \"{name}\"?",
    "savings.close_confirm": "Sulje tavoite \"{name}\"? Säästöt palautetaan päätilille.",
    "savings.toast.created": "Tavoite luotu",
    "savings.toast.deposited": "Talletus suoritettu",
    "savings.toast.withdrawn": "Nosto suoritettu",
    "savings.toast.closed": "Tavoite suljettu",
    "invest.total": "Sijoitettu yhteensä",
    "invest.profits": "Kertyneet voitot",
    "invest.count": "Sijoitukset",
    "invest.products": "Saatavilla olevat tuotteet",
    "invest.invest_btn": "Sijoita",
    "invest.my": "Omat sijoitukset",
    "invest.pending": "Odottaa",
    "invest.validated": "Vahvistettu",
    "invest.refused": "Hylätty",
    "invest.withdraw.btn": "Nosta",
    "invest.withdraw.title": "Nosta sijoitukseni",
    "invest.withdraw.intro": "Olet nostamassa sijoituksesi \"{product}\". Alkuperäinen pääoma ja kertynyt voitto hyvitetään päätilillesi.",
    "invest.withdraw.principal": "Pääoma",
    "invest.withdraw.profit": "Voitto",
    "invest.withdraw.total": "Yhteensä saatavaa",
    "invest.withdraw.confirm": "Vahvista nosto",
    "invest.withdraw.warning": "Tämä toiminto on peruuttamaton. Sijoitus suljetaan.",
    "invest.withdraw.success": "Sijoitus nostettu — {amount} hyvitetty saldoosi.",
    "invest.withdrawn": "Nostettu",
    "invest.cta_sub": "Valitse sinulle sopiva sijoitustyyppi. Neuvoja avustaa sinua ensimmäisestä pyynnöstäsi alkaen.",
    "invest.form.amount": "Sijoitettava summa",
    "invest.form.balance": "Käytettävissä oleva saldo: {amount}",
    "invest.form.note": "Huomio neuvojalle (valinnainen)",
    "invest.form.note_ph": "Täsmennä odotuksesi…",
    "invest.form.advisor_info": "Neuvojan kanssa avataan automaattisesti keskustelu.",
    "invest.form.cancel": "Peruuta",
    "invest.form.submit": "Lähetä pyyntö",
    "invest.toast.requested": "Pyyntö kirjattu — neuvojan kanssa on avattu keskustelu.",
    "invest.p1.name": "Kiinteistöt",
    "invest.p1.desc": "SCPI, paperimuotoinen kiinteistö, kiinteistöjoukkorahoitus",
    "invest.p2.name": "Joukkorahoitus",
    "invest.p2.desc": "Valittujen projektien osallistava rahoitus",
    "invest.p3.name": "Uusiutuva energia",
    "invest.p3.desc": "Aurinko-, tuulivoima ja vihreät projektit",
    "invest.p4.name": "Pörssi",
    "invest.p4.desc": "Osakkeet, ETF-rahastot ja hajautetut rahastot",
    "invest.p5.name": "Osallistava",
    "invest.p5.desc": "Lainat pk-yrityksille ja paikallisille yrittäjille",
    "invest.p6.name": "Krypto",
    "invest.p6.desc": "Bitcoin, Ethereum ja monivaluuttainen lompakko",
    "msg.online": "Verkossa",
    "msg.loading": "Ladataan…",
    "msg.none": "Ei viestejä. Aloita keskustelu neuvoja kanssasi.",
    "msg.placeholder": "Kirjoita viestisi…",
    "msg.attach.image": "Kuva",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Ääni",
    "msg.attach.file": "Tiedosto / Asiakirja",
    "msg.pdf_doc": "PDF-asiakirja",
    "msg.file": "Tiedosto",
    "msg.too_large": "Tiedosto on liian suuri (enintään {size}).",
    "msg.withdraw": "Poista",
    "credits.cta_sub": "Hae suoraan verkossa — vastaus 24 tunnin sisällä.",
    "credits.learn_more": "Lue lisää",
    "credits.request": "Hae",
    "credits.history": "Omat hakemukset",
    "credits.status.pending": "Odottaa",
    "credits.status.accepted": "Hyväksytty",
    "credits.status.refused": "Hylätty",
    "credits.chars": "Ominaisuudet",
    "credits.amount_range": "Summa: {min}€ – {max}€",
    "credits.rate_range": "Korko: {min}% – {max}% vuodessa",
    "credits.duration_range": "Kesto: {min}–{max} kuukautta",
    "credits.deposit_min": "Alkutalletus: vähintään {amount}€",
    "credits.garantie": "Vakuus: {garantie}",
    "credits.eligibility": "Kelpoisuusehdot",
    "credits.elig.income": "Todista säännölliset ja vakaat tulot",
    "credits.elig.deposit": "Tee alkutalletus: {amount}€",
    "credits.elig.garantie": "Esitä vakuus: {garantie}",
    "credits.form.title": "Hakemus — {name}",
    "credits.form.cancel": "Peruuta",
    "credits.form.submit": "Lähetä hakemus",
    "credits.form.amount": "Toivottu summa (€)",
    "credits.form.duration": "Kesto (kuukausia)",
    "credits.form.reason": "Syy (valinnainen)",
    "credits.form.reason_ph": "Kuvaile projektiasi…",
    "credits.form.close": "Sulje",
    "credits.toast.submitted": "Lainahakemus lähetetty — saat vastauksen 24 tunnin sisällä.",
    "credits.p1.name": "Henkilökohtainen laina",
    "credits.p1.desc": "Arkipäivän tarpeiden tai henkilökohtaisten projektien rahoittamiseen.",
    "credits.p2.name": "Asuntolaina",
    "credits.p2.desc": "Kiinteistön oston tai rakentamisen rahoittamiseen.",
    "credits.p3.name": "Auto- / moottoripyörälaina",
    "credits.p3.desc": "Osta ajoneuvo huoletta.",
    "credits.p4.name": "Opintolaina",
    "credits.p4.desc": "Taloudellinen tuki korkeakouluopintoihin.",
    "credits.p5.name": "Yrityslaina",
    "credits.p5.desc": "Ammatillisen toiminnan kasvun tukemiseen.",
    "credits.p6.name": "Pikalaina",
    "credits.p6.desc": "Nopea ratkaisu kiireellisiin tarpeisiin.",
    "financing.reimbursable": "Takaisinmaksettava rahoitus",
    "financing.reimbursable_desc": "Taloudellinen tuki kiinteillä ehdoilla toiminnan käynnistämiseen tai kehittämiseen.",
    "financing.non_reimbursable": "Takaisinmaksuton rahoitus",
    "financing.non_reimbursable_desc": "Sosiaalisiin, koulutuksellisiin tai humanitaarisiin projekteihin (kansalaisjärjestöt, yhdistykset).",
    "financing.cta_sub": "Räätälöityjä ratkaisuja tavoitteidesi toteuttamiseen.",
    "financing.request": "Hae rahoitusta",
    "financing.history": "Omat hakemukset",
    "financing.reimbursable_label": "Takaisinmaksettava",
    "financing.non_reimbursable_label": "Takaisinmaksuton",
    "financing.status.pending": "Odottaa",
    "financing.status.granted": "Myönnetty",
    "financing.status.refused": "Hylätty",
    "financing.form.title": "Hakemus — {name}",
    "financing.form.cancel": "Peruuta",
    "financing.form.submit": "Lähetä hakemus",
    "financing.form.project": "Projektin nimi",
    "financing.form.project_ph": "Oma projektini",
    "financing.form.amount": "Haettu summa (€)",
    "financing.form.sector": "Toimiala",
    "financing.form.sector_ph": "Teknologia, maatalous, …",
    "financing.form.description": "Projektin kuvaus",
    "financing.form.description_ph": "Esittele projektisi, sen tavoitteet ja odotettu vaikutus…",
    "settings.security": "Turvallisuus",
    "settings.current_pw": "Nykyinen salasana",
    "settings.new_pw": "Uusi salasana",
    "settings.confirm_pw": "Vahvista",
    "settings.submit": "Vaihda salasana",
    "settings.no_match": "Salasanat eivät täsmää",
    "settings.changed": "Salasana vaihdettu",
    "common.cancel": "Peruuta",
    "common.confirm": "Vahvista",
    "common.close": "Sulje",
    "common.save": "Tallenna",
    "common.loading": "Ladataan…",
    "common.send": "Lähetä",
    "common.total": "Yhteensä",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Arv. kuukausierä",
    "credits.rate_annual": "/ vuosi",
    "credits.rate_used": "Arv. korko:",
    "credits.simulation": "Kuukausierän simulointi",
    "credits.total_cost": "Arv. kokonaiskustannus",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Tervetuloa",
    "common.delete": "Poista",
    "cards.delete.title": "Poista kortti",
    "cards.delete.confirm_msg": "Tämä toiminto on peruuttamaton. Kortti poistetaan pysyvästi.",
    "cards.delete.physical_tooltip": "Fyysinen kortti tilattu — poisto ei mahdollista",
    "cards.order.confirmed": "Tilaus vahvistettu!",
    "cards.order.confirmed_body": "Fyysinen korttisi valmistetaan. Käytä tätä numeroa toimituksen seurantaan.",
    "cards.order.tracking_no": "Seurantatunnus",
    "cards.fee_deducted": "{amount} vähennetään saldostasi",
    "cards.insufficient_short": "Riittämätön saldo ({available} saatavilla)",
    "transfer.section.beneficiary": "Saaja",
    "transfer.section.details": "Siirto",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Pankki",
    "transfer.field.city": "Kaupunki",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Pankki:",
    "transfer.progress.paused": "Odottaa vahvistusta",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Vaihtuva",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Kortti poistettu",
    "cards.feat.gold_exclusive": "Eksklusiivinen kultakortti",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Ostajan suoja",
    "cards.feat.support_standard": "Vakiotuki",
    "cards.feat.plastic_premium": "Premium muovikortti",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Vakiomatkavakuutus",
    "cards.feat.support_priority": "Etusijatuki",
    "cards.feat.metal_brushed": "Harjattu metallikortti",
    "cards.feat.cashback_2_unlimited": "Rajaton 2% cashback",
    "cards.feat.travel_premium": "Premium matkavakuutus",
    "cards.feat.vip_lounge": "VIP-aulan pääsy",
    "cards.feat.support_247": "24/7 tuki",
    "auth.demo.title": "Järjestelmänvalvojan kirjautuminen",
    "auth.demo.subtitle": "Anna järjestelmänvalvojan tunnukset esikatselua varten",
    "credits.garantie.none": "Ei vakuutta",
    "credits.garantie.mortgage": "Kiinnitys rahoitettavaan kohteeseen",
    "credits.garantie.vehicle": "Rahoitettava ajoneuvo",
    "credits.garantie.parental": "Vanhempien tai pankin takaus",
    "credits.garantie.professional": "Ammattivarat tai henkilökohtainen takaus",
    "cards.reveal.pin_prompt": "Syötä 4-numeroinen PIN-koodisi nähdäksesi kortin tiedot.",
    "cards.reveal.show": "Näytä",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Kaksi PIN-koodia eivät täsmää",
    "auth.pin.digit_hint": "4 numeroa",
  
  },
  et: {
    "notif.transfer_code.title": "Ülekande kinnituskood",
    "notif.transfer_code.body": "Kopeerige see kood ülekande kinnitamiseks.",
    "notif.transfer_code.expires_in": "Kaob {sec}s pärast",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} kuud",
    "credits.n_months": "{n} kuud",
    "msg.read": "Loetud",
    "msg.sent": "Saadetud",
    "msg.attach_aria": "Lisa fail",
    "login.device.unknown": "Tundmatu brauser",
    "login.device.mobile": "Mobiil",
    "login.device.tablet": "Tahvelarvuti",
    "login.device.desktop": "Arvuti",
    "login.recent": "Hiljutine",
    "err.invalid_credentials": "Vigased mandaadid",
    "err.bad_pin": "Vale PIN",
    "err.account_blocked": "Konto blokeeritud",
    "err.bad_current_pw": "Praegune parool on vale",
    "err.insufficient_funds": "Ebapiisavad vahendid, palun täiendage oma kontot.",
    "err.amount_above_savings": "Summa ületab saadaolevat säästu",
    "err.must_be_positive": "Väärtus peab olema positiivne",
    "err.invalid_format": "Vigane vorming",
    "err.email_taken": "E-post juba kasutusel",
    "err.card_not_found": "Kaarti ei leitud",
    "err.physical_card_already_ordered": "Füüsiline kaart juba tellitud",
    "err.beneficiary_not_found": "Saajat ei leitud",
    "err.invalid_code": "Vigane kood",
    "err.no_transfer_code": "Selle ülekande jaoks pole koodi",
    "err.link_expired": "Link vigane või aegunud",
    "err.thread_not_found": "Vestlust ei leitud",
    "err.empty_message": "Tühi sõnum",
    "err.file_too_large": "Fail liiga suur (max 20 MB)",
    "err.forbidden": "Juurdepääs keelatud",
    "err.bank_not_found": "Panka ei leitud",
    "err.signups_closed": "Selle panga registreerimised on suletud.",
    "err.access_suspended": "Juurdepääs on ajutiselt peatatud. Palun võtke ühendust oma pangaga.",
    "err.notif_not_closable": "Seda teavitust ei saa sulgeda.",
    "auth.signup.tagline": "Liitu meiega juba täna",
    "auth.login.tagline": "Tere tulemast tagasi!",
    "auth.signup.tagline_sub": "Ava konto 2 minutiga ja kasuta kõiki meie premium-pangateenuseid.",
    "auth.login.tagline_sub": "Logi sisse oma isiklikule kontole ja halda oma rahandust turvaliselt.",
    "auth.back": "Tagasi avalehele",
    "auth.bullet.security": "Sõjaväeline pangaturvalisus",
    "auth.bullet.transfers": "Kohesed ülekanded 24/7",
    "auth.bullet.investments": "Integreeritud investeeringud ja säästud",
    "auth.bullet.advisor": "Isiklik nõustaja saadaval 7 päeva nädalas",
    "auth.footer": "Toimib {bank}",
    "auth.signup.title": "Loo konto",
    "auth.login.title": "Logi sisse",
    "auth.field.lastName": "Perekonnanimi",
    "auth.field.firstName": "Eesnimi",
    "auth.field.country": "Riik",
    "auth.field.country.placeholder": "— Vali oma riik —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Aadress",
    "auth.field.address.placeholder": "Näidistänav 123, Tallinn",
    "auth.field.email": "E-post",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Parool",
    "auth.submit.signup": "Loo minu konto",
    "auth.submit.login": "Logi sisse",
    "auth.login.badge": "Logi oma kontole sisse",
    "auth.card.tagline": "Teie kliendiala",
    "auth.submit.loading": "Laadimine…",
    "auth.pin.title": "PIN-kood",
    "auth.pin.subtitle": "Sisestage oma 4-kohaline PIN-kood",
    "auth.pin.label": "PIN-kood",
    "auth.pin.confirm": "Kinnita PIN-kood",
    "auth.pin.submit": "Kinnita",
    "auth.pin.back": "Tagasi sisselogimisse",
    "auth.pin.length_error": "PIN-kood peab olema 4 numbrit",
    "auth.switch.have_account": "Kas sul on juba konto?",
    "auth.switch.login_link": "Logi sisse",
    "auth.switch.no_account": "Pole veel klient?",
    "auth.error.select_country": "Palun vali oma riik",
    "kyc.title": "Isikusamasuse kontroll (KYC)",
    "kyc.sub": "Laadi üles oma dokumendid konto kinnitamiseks. Kõik dokumendid on krüpteeritud ja konfidentsiaalsed.",
    "kyc.id_type": "Isikut tõendava dokumendi tüüp *",
    "kyc.id_card": "Isikutunnistus",
    "kyc.passport": "Pass",
    "kyc.driver_license": "Juhiluba",
    "kyc.recto_passport": "Passi esikülg *",
    "kyc.recto_id": "Dokumendi esikülg *",
    "kyc.verso": "Dokumendi tagakülg *",
    "kyc.selfie": "Selfie isikut tõendava dokumendiga *",
    "kyc.address_proof": "Elukohajärgne tõend *",
    "kyc.hint_doc": "Pilt või PDF, maksimaalselt 5 MB",
    "kyc.hint_selfie": "Selge foto, nägu ja dokument nähtavad",
    "kyc.hint_address": "Arve, väljavõte, tõend… (maks. 5 MB)",
    "kyc.upload": "Lae üles",
    "kyc.replace": "Asenda",
    "pending.title": "Konto ootab kinnitamist",
    "pending.body": "Sinu taotlus on vastu võetud. Administraator kontrollib sinu andmeid ja aktiveerib konto varsti.",
    "pending.back": "Tagasi sisselogimisele",
    "home.nav.features": "Funktsioonid",
    "home.nav.security": "Turvalisus",
    "home.nav.invest": "Investeerimine",
    "home.nav.credits": "Laenud",
    "home.nav.faq": "KKK",
    "home.nav.login": "Logi sisse",
    "home.hero.tagline": "Järgmise põlvkonna internetipank",
    "home.hero.title1": "Halda oma raha",
    "home.hero.title2": "targalt",
    "home.hero.sub": "Täielik pangaplatvorm — ülekanded, säästud, investeeringud ja laenud nutitelefonist.",
    "home.hero.login": "Logi sisse",
    "home.hero.trust.secure": "100% Turvaline",
    "home.hero.trust.fast": "Kiire avamine",
    "home.hero.trust.encrypted": "Krüpteeritud andmed",
    "home.hero.trust.support": "24/7 Teenindus",
    "home.mockup.balance": "Saadaolev saldo",
    "home.mockup.currency": "EUR · Põhikonto",
    "home.mockup.recharge": "Lae",
    "home.mockup.send": "Saada",
    "home.mockup.save": "Säästa",
    "home.mockup.salary": "Märtsi palk",
    "home.mockup.rent": "Aprilli üür",
    "home.mockup.investment": "Investeering",
    "home.mockup.days2": "2 päeva tagasi",
    "home.mockup.days3": "3 päeva tagasi",
    "home.mockup.days5": "5 päeva tagasi",
    "home.stats.accounts": "Aktiivsed kontod",
    "home.stats.uptime": "Käideldavus",
    "home.stats.transfers": "Ülekanded",
    "home.stats.support": "Tugi",
    "home.features.label": "Funktsioonid",
    "home.features.title": "Kõik, mida vajad",
    "home.features.sub": "Täielik platvorm, mis on loodud sinu igapäevase rahanduse haldamise lihtsustamiseks.",
    "home.feature.security": "Sõjaväeline turvalisus",
    "home.feature.security_body": "AES-256 krüpteerimine, kahefaktoriline autentimine ja 24/7 kontoseire.",
    "home.feature.cards": "Virtuaalsed ja füüsilised kaardid",
    "home.feature.cards_body": "Väljasta kaardid sekunditega või telli päris füüsiline kaart koduuksele.",
    "home.feature.transfers": "Kiirülekanded",
    "home.feature.transfers_body": "Saada ja võta vastu raha igal pool maailmas sekunditega.",
    "home.feature.analytics": "Reaalajaanalüüs",
    "home.feature.analytics_body": "Nutikad armatuurlauad kulutuste jälgimiseks ja rahanduse planeerimiseks.",
    "home.feature.investments": "Integreeritud investeeringud",
    "home.feature.investments_body": "Kasuta valitud investeerimistooteid otse oma kontolt.",
    "home.feature.support": "Pühendatud 24/7 tugi",
    "home.feature.support_body": "Isiklik panganduse nõustaja, kättesaadav igal ajal turvalise sõnumside kaudu.",
    "home.tpl.label": "Usaldus",
    "home.tpl.title": "Nad usaldavad meid",
    "home.mockup.balance_label": "Saldo",
    "home.mockup.virement": "Ülekanne",
    "home.mockup.salaire": "Palk",
    "home.security.label": "Turvalisus",
    "home.security.title1": "Sinu rahandus",
    "home.security.title2": "täielikus turvalisuses",
    "home.security.sub": "Rakendame kõige rangemateid pangaturvalisuse standardeid iga sendi kaitsmiseks.",
    "home.security.aes.title": "AES-256 krüpteerimine",
    "home.security.aes.body": "Kõik tundlikud andmed on krüpteeritud samade algoritmidega, mida kasutavad suured rahvusvahelised pangad.",
    "home.security.auth.title": "Tugevdatud autentimine",
    "home.security.auth.body": "Kahekordne isikusamasuse kontroll iga sisselogimise ja iga tundliku toimingu puhul.",
    "home.security.compliance.title": "PSD2/GDPR vastavus",
    "home.security.compliance.body": "Järgime kõiki Euroopa makseteenuseid ja andmekaitset käsitlevaid määrusi.",
    "home.invest.label": "Investeeringud",
    "home.invest.title": "Kasvatage oma raha",
    "home.invest.sub": "6 investeerimiskategooriat, valitud nende potentsiaali ja usaldusväärsuse alusel.",
    "home.invest.yield": "Tootlus",
    "home.credits.label": "Laenud",
    "home.credits.title": "Lahendused igale projektile",
    "home.credits.sub": "Konkurentsivõimelised intressimäärad, kiired vastused ja isikupärastatud tugi kõikide projektide jaoks.",
    "home.credits.rate": "Intressimäär",
    "home.faq.label": "KKK",
    "home.faq.title": "Korduma kippuvad küsimused",
    "home.faq.q1": "Kuidas avada konto?",
    "home.faq.a1": "Registreerimine võtab vähem kui 2 minutit. Sisesta lihtsalt oma isikuandmed, vali parool ja konto aktiveeritakse pärast meie meeskonna kinnitust.",
    "home.faq.q2": "Kas minu andmed on turvatud?",
    "home.faq.a2": "Jah. Kõik andmed on krüpteeritud AES-256-ga puhkeolekus ja TLS 1.3-ga edastamisel. Järgime rahvusvahelisi pangastandardeid ja GDPR/PSD2 regulatsioone.",
    "home.faq.q3": "Milliseid investeerimisvõimalusi pakute?",
    "home.faq.a3": "Pakume 6 kategooriat: Kinnisvara, Ühisrahastus, Taastuvenergia, Börs, Osalustav ja Krüpto. Iga toode on valitud riski/tootluse suhte alusel.",
    "home.faq.q4": "Kuidas taotleda laenu?",
    "home.faq.a4": "Mine kliendialal asuvasse Laenu sektsiooni, vali laenu tüüp, sisesta summa ja kestus. Sinu taotlus töödeldakse 24 tunni jooksul meie nõustajate poolt.",
    "home.faq.q5": "Kas on varjatud tasusid?",
    "home.faq.a5": "Varjatud tasusid pole. Kõik hinnad on selgelt kuvatud iga toimingu juures. Tingimused on klientide alal igal ajal kättesaadavad.",
    "home.faq.q6": "Kuidas võtta ühendust toega?",
    "home.faq.a6": "Kasuta kliendialal integreeritud sõnumit, et võtta otse ühendust oma isikliku panganduse nõustajaga, kes on saadaval 24/7.",
    "home.cta.title": "Valmis alustama?",
    "home.cta.sub": "Liitu tuhandete kasutajatega, kes haldavad oma rahandust {bank}-ga. Registreerimine võtab vähem kui 2 minutit.",
    "home.footer.products": "Tooted",
    "home.footer.services": "Teenused",
    "home.footer.access": "Kiirjuurdepääs",
    "home.footer.accounts": "Kontod",
    "home.footer.transfers": "Ülekanded",
    "home.footer.cards": "Kaardid",
    "home.footer.savings": "Säästud",
    "home.footer.investments": "Investeeringud",
    "home.footer.credits": "Laenud",
    "home.footer.financing": "Rahastamine",
    "home.footer.messages": "Sõnumid",
    "home.footer.login": "Logi sisse",
    "home.footer.signup": "Ava konto",
    "home.footer.support": "24/7 Tugi",
    "home.footer.tagline": "Sinu internetipank, kättesaadav kõikjal ja alati.",
    "home.footer.copyright": "© {year} {bank} — Kõik õigused kaitstud",
    "home.footer.powered": "Toimib {bank}",
    "app.menu_label": "Peamenüü",
    "app.nav.dashboard": "Armatuurlaud",
    "app.nav.accounts": "Kontod",
    "app.nav.transactions": "Tehingud",
    "app.nav.transfers": "Ülekanded",
    "app.nav.cards": "Kaardid",
    "app.nav.savings": "Säästud",
    "app.nav.investments": "Investeeringud",
    "app.nav.credit": "Laen",
    "app.nav.financing": "Rahastamine",
    "app.nav.messages": "Sõnumid",
    "app.nav.card_tracking": "Kaardi jälgimine",
    "app.nav.notifications": "Teavitused",
    "app.nav.settings": "Seaded",
    "app.nav.profile": "Profiil",
    "app.nav.mobile.home": "Avaleht",
    "app.nav.mobile.transfers": "Ülekanded",
    "app.nav.mobile.cards": "Kaardid",
    "app.nav.mobile.transactions": "Tehingud",
    "app.nav.more.invest": "Investeeri",
    "app.user_role": "Klient",
    "app.logout": "Logi välja",
    "app.search": "Otsi…",
    "app.more_title": "Rohkem funktsioone",
    "app.more_btn": "Rohkem",
    "app.loading": "Laadimine…",
    "app.error_back": "Tagasi sisselogimisele",
    "app.see_more_notifs": "Vaata veel {count} teavitust →",
    "page.accounts": "Minu kontod",
    "page.transactions": "Tehingud",
    "page.transfers": "Uus ülekanne",
    "page.deposit": "Sissemakse",
    "page.cards": "Minu kaardid",
    "page.savings": "Säästud",
    "page.investments": "Investeeringud",
    "page.credits": "Laen",
    "page.financing": "Projekti rahastamine",
    "page.messages": "Sõnumid",
    "page.settings": "Seaded",
    "page.notifications": "Olulised teavitused",
    "page.profile": "Minu profiil",
    "dash.greeting": "Tere, {name}",
    "dash.overview": "Rahanduse ülevaade — {date}",
    "dash.total_balance": "Kogu saldo",
    "dash.this_month": "+{amount} sel kuul",
    "dash.view_iban": "Vaata minu IBAN-i",
    "dash.my_accounts": "Minu kontod",
    "dash.primary_account": "Põhikonto",
    "dash.savings_account": "Säästukonto",
    "dash.remunerated": "Intressikandvate",
    "dash.details": "Üksikasjad",
    "dash.transfer_btn": "Ülekanne",
    "dash.quick_actions": "Kiirtoimingud",
    "dash.quick.deposit": "Sissemakse",
    "deposit.title": "Sissemakse teave",
    "dash.quick.transfer": "Ülekanne",
    "dash.quick.history": "Ajalugu",
    "dash.quick.cards": "Minu kaardid",
    "dash.quick.profile": "Profiil",
    "dash.monthly_budget": "Kuueelarve",
    "dash.income": "Tulud",
    "dash.expenses": "Kulud",
    "dash.spending_cat": "Kulud kategooriate kaupa",
    "dash.no_spending": "Kulutusi pole registreeritud.",
    "dash.recent_tx": "Hiljutised tehingud",
    "dash.see_all": "Vaata kõiki",
    "dash.no_tx": "Tehinguid pole.",
    "dash.main_card": "Minu põhikaart",
    "dash.manage": "Halda",
    "dash.block": "Blokeeri",
    "dash.pin": "PIN-kood",
    "dash.security": "Turvalisus",
    "dash.more": "Rohkem",
    "dash.no_card": "Kaarti pole väljastatud",
    "dash.monthly_activity": "Kuu aktiivsus",
    "dash.operations": "Toimingud",
    "dash.holder": "Kaardi omanik",
    "dash.expires": "Kehtib",
    "dash.visa_label": "VISA",
    "dash.balance": "Saadaolev saldo",
    "tx.all": "Kõik",
    "tx.income": "Tulud",
    "tx.expenses": "Kulud",
    "tx.today": "Täna",
    "tx.yesterday": "Eile",
    "tx.none": "Tehinguid pole.",
    "transfer.title": "Kiirülekanne",
    "transfer.recipient": "Saaja",
    "transfer.recipient_ph": "Nimi, IBAN, telefoninumber…",
    "transfer.amount": "Summa",
    "transfer.label": "Selgitus",
    "transfer.reason": "Põhjus (vabatahtlik)",
    "transfer.reason_ph": "Nt: üür, tagasimakse…",
    "transfer.execution": "Täitmine",
    "transfer.immediate": "Kohene",
    "transfer.delayed": "Edasilükatud",
    "transfer.balance": "Saadaolev saldo:",
    "transfer.continue": "Jätka",
    "transfer.confirm": "Kinnita",
    "transfer.cancel": "Tühista",
    "transfer.code": "Kinnituskood",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Sisesta saadud kood ülekande kinnitamiseks",
    "transfer.pending.title": "Ülekanne ootab kinnitust",
    "transfer.pending.amount": "Summa:",
    "transfer.pending.to": "Saaja:",
    "transfer.pending.hint": "Teie kinnituskood saadetakse e-posti teel või teavituskella kaudu. Kood koosneb 6 numbrist. Kui te seda ei saa, võtke ühendust toega.",
    "transfer.pending.validate": "Kinnita ülekanne",
    "transfer.saved": "Ülekanne salvestatud. Küsi kinnituskoodi administraatorilt.",
    "transfer.done": "Ülekanne tehtud",
    "transfer.validated": "Ülekanne kinnitatud!",
    "transfer.progress.processing": "Ülekande töötlemine…",
    "transfer.progress.completed": "Ülekanne sooritatud!",
    "transfer.progress.default_message": "Teie ülekannet töödeldakse. Palun oodake.",
    "cards.none": "Kaarti pole väljastatud.\nVõta ühendust oma administraatoriga.",
    "cards.holder": "Kaardi omanik",
    "cards.expires": "Kehtib",
    "cards.block": "Lukusta",
    "cards.pin": "PIN-kood",
    "cards.more": "Rohkem",
    "cards.limits": "Kaardi limiidid",
    "cards.payment": "Makse",
    "cards.withdrawal": "Sularahavõtmine",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Blokeeritud",
    "cards.status_active": "Aktiivne",
    "cards.toast.blocked": "Kaart blokeeritud",
    "cards.toast.unblocked": "Kaardi blokeering eemaldatud",
    "cards.toast.physical_ordered": "Füüsiline kaart tellitud! Tarneaeg 5–10 tööpäeva.",
    "cards.toast.created": "Kaart edukalt loodud!",
    "cards.none_sub": "Ühtegi kaarti pole väljastatud. Looge oma virtuaalkaart kohe.",
    "cards.generate_btn": "Loo minu kaart",
    "cards.create.title": "Loo minu virtuaalkaart",
    "cards.create.network": "Maksevõrk",
    "cards.create.color": "Kaardi värv",
    "cards.create.preview_name": "TEIE NIMI",
    "cards.create.preview_expiry": "KK/AA",
    "cards.create.loading": "Loomine…",
    "cards.create.submit": "Loo kaart",
    "cards.btn.unblock": "Eemalda blokeering",
    "cards.btn.block": "Blokeeri",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Füüsiline",
    "cards.btn.physical_done": "Tellitud",
    "cards.limits_title": "Limiidid",
    "cards.reveal.title": "Kaardi andmed",
    "cards.reveal.number": "Täielik number",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Aegumiskuupäev",
    "cards.reveal.warning": "Ärge jagage seda teavet kunagi. Sulgege see aken pärast kasutamist.",
    "cards.pin.title": "PIN-kood",
    "cards.pin.message": "Seda PIN-koodi kasutatakse kõigi teie veebimaksete ja sularahavõtmiste jaoks. Ärge kunagi kellelegi edastage.",
    "cards.physical.title": "Telli füüsiline kaart",
    "cards.physical.fee_label": "Tarnetasu",
    "cards.physical.balance_label": "Teie saldo",
    "cards.physical.insufficient": "Ebapiisav saldo. Palun laadige konto enne tellimist.",
    "cards.physical.info": "Tasu võetakse teie kontolt. Tarneaeg 5–10 tööpäeva.",
    "cards.physical.order_btn": "Telli ({fee})",
    "cards.physical.ordering": "Tellimine…",
    "cards.physical.order_cta": "Telli füüsiline kaart",
    "cards.physical.choose_type": "Vali oma kaart",
    "cards.physical.features": "Eelised",
    "cards.buy_virtual_cta": "Osta virtuaalkaart",
    "notif.none": "Praegu teavitusi pole.",
    "notif.contact_support": "Võtke ühendust toega",
    "profile.change_photo": "Muuda fotot",
    "profile.remove_photo": "Eemalda foto",
    "profile.email": "E-post",
    "profile.phone": "Telefon",
    "profile.country": "Riik",
    "profile.address": "Aadress",
    "profile.notifs_title": "Teavitused ({count})",
    "profile.no_notifs": "Teavitusi pole.",
    "profile.logout": "Logi välja",
    "profile.photo_updated": "Profiilipilt uuendatud",
    "profile.photo_removed": "Foto eemaldatud",
    "profile.photo_too_large": "Pilt on liiga suur (maks. 1 MB)",
    "profile.powered": "Toimib {bank}",
    "iban.title": "Minu IBAN",
    "iban.loading": "Laadimine…",
    "iban.bic": "BIC / SWIFT-kood",
    "iban.share_info": "Jaga neid andmeid, et saada ülekandeid oma kontole.",
    "iban.copy": "Kopeeri",
    "iban.copied": "Kopeeritud lõikelauale",
    "iban.copy_fail": "Kopeerimine ebaõnnestus",
    "iban.close": "Sulge",
    "accounts.all": "Kõik sinu kontod {bank}-s.",
    "accounts.primary": "Põhikonto",
    "accounts.savings": "Säästukonto",
    "accounts.primary_tag": "Esmane",
    "accounts.secondary_tag": "Teisene",
    "accounts.objective": "Eesmärk: {amount} · {pct}%",
    "accounts.copy": "Kopeeri",
    "accounts.loading": "Laadimine…",
    "accounts.iban_copied": "IBAN kopeeritud",
    "accounts.copy_fail": "Kopeerimine ebaõnnestus",
    "savings.total": "Kokku säästetud",
    "savings.target_total": "Kogu eesmärk",
    "savings.progress": "Edusamm",
    "savings.active": "Aktiivsed eesmärgid",
    "savings.new_goal": "Uus eesmärk",
    "savings.none": "Säästueesmärke pole veel.",
    "savings.none_sub": "Loo esimene eesmärk alustamiseks.",
    "savings.saved": "Säästetud:",
    "savings.goal": "Eesmärk:",
    "savings.deadline": "Tähtaeg: {date}",
    "savings.deposit": "Tee sissemakse",
    "savings.withdraw": "Võta välja",
    "savings.loading": "Laadimine…",
    "savings.form.title": "Uus säästueesmärk",
    "savings.form.cancel": "Tühista",
    "savings.form.create": "Loo",
    "savings.form.name": "Eesmärgi nimi",
    "savings.form.name_ph": "Jaapani reis, Auto, …",
    "savings.form.target": "Sihtsumma",
    "savings.form.initial": "Esialgne sissemakse",
    "savings.form.deadline": "Tähtaeg (vabatahtlik)",
    "savings.form.color": "Värv",
    "savings.deposit_title": "Sissemakse eesmärgile \"{name}\"",
    "savings.deposit_amount": "Sissemakse summa",
    "savings.balance_hint": "Põhikonto saldo: {amount}",
    "savings.withdraw_prompt": "Kui palju võtta välja eesmärgist \"{name}\"?",
    "savings.close_confirm": "Sulgeda eesmärk \"{name}\"? Säästud kantakse üle põhikontole.",
    "savings.toast.created": "Eesmärk loodud",
    "savings.toast.deposited": "Sissemakse tehtud",
    "savings.toast.withdrawn": "Väljavõtmine tehtud",
    "savings.toast.closed": "Eesmärk suletud",
    "invest.total": "Kokku investeeritud",
    "invest.profits": "Kumulatiivsed kasumid",
    "invest.count": "Investeeringud",
    "invest.products": "Saadaolevad tooted",
    "invest.invest_btn": "Investeeri",
    "invest.my": "Minu investeeringud",
    "invest.pending": "Ootel",
    "invest.validated": "Kinnitatud",
    "invest.refused": "Keeldutud",
    "invest.withdraw.btn": "Võta välja",
    "invest.withdraw.title": "Võta minu investeering välja",
    "invest.withdraw.intro": "Olete välja võtmas oma investeeringut „{product}\". Algkapital ja kogunenud kasum kantakse teie põhisaldole.",
    "invest.withdraw.principal": "Algkapital",
    "invest.withdraw.profit": "Kasum",
    "invest.withdraw.total": "Kokku saada",
    "invest.withdraw.confirm": "Kinnita väljavõtmine",
    "invest.withdraw.warning": "See toiming on pöördumatu. Investeering suletakse.",
    "invest.withdraw.success": "Investeering välja võetud — {amount} kantud saldole.",
    "invest.withdrawn": "Välja võetud",
    "invest.cta_sub": "Vali endale sobiv investeerimistüüp. Nõustaja abistab sind esimesest taotlusest alates.",
    "invest.form.amount": "Investeeritav summa",
    "invest.form.balance": "Saadaolev saldo: {amount}",
    "invest.form.note": "Märkus nõustajale (vabatahtlik)",
    "invest.form.note_ph": "Täpsusta oma ootused…",
    "invest.form.advisor_info": "Nõustajaga avatakse automaatselt vestlus.",
    "invest.form.cancel": "Tühista",
    "invest.form.submit": "Saada taotlus",
    "invest.toast.requested": "Taotlus registreeritud — nõustajaga on avatud vestlus.",
    "invest.p1.name": "Kinnisvara",
    "invest.p1.desc": "SCPI, paberil kinnisvara, kinnisvaraühisrahastus",
    "invest.p2.name": "Ühisrahastus",
    "invest.p2.desc": "Valitud projektide osaluspõhine rahastamine",
    "invest.p3.name": "Taastuvenergia",
    "invest.p3.desc": "Päikeseenergia, tuuleenergia ja rohelised projektid",
    "invest.p4.name": "Börs",
    "invest.p4.desc": "Aktsiad, ETF-id ja hajutatud fondid",
    "invest.p5.name": "Osaluspõhine",
    "invest.p5.desc": "Laenud VKEdele ja kohalikele ettevõtjatele",
    "invest.p6.name": "Krüpto",
    "invest.p6.desc": "Bitcoin, Ethereum ja mitmivaluutane rahakott",
    "msg.online": "Võrgus",
    "msg.loading": "Laadimine…",
    "msg.none": "Sõnumeid pole. Alusta vestlust oma nõustajaga.",
    "msg.placeholder": "Kirjuta oma sõnum…",
    "msg.attach.image": "Pilt",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Heli",
    "msg.attach.file": "Fail / Dokument",
    "msg.pdf_doc": "PDF-dokument",
    "msg.file": "Fail",
    "msg.too_large": "Fail on liiga suur (maks. {size}).",
    "msg.withdraw": "Kustuta",
    "credits.cta_sub": "Esita taotlus otse veebis — vastus 24 tunni jooksul.",
    "credits.learn_more": "Loe lähemalt",
    "credits.request": "Taotle",
    "credits.history": "Minu taotlused",
    "credits.status.pending": "Ootel",
    "credits.status.accepted": "Vastu võetud",
    "credits.status.refused": "Tagasi lükatud",
    "credits.chars": "Omadused",
    "credits.amount_range": "Summa: {min}€ – {max}€",
    "credits.rate_range": "Intressimäär: {min}% – {max}% aastas",
    "credits.duration_range": "Kestus: {min}–{max} kuud",
    "credits.deposit_min": "Esialgne sissemakse: minimaalselt {amount}€",
    "credits.garantie": "Tagatis: {garantie}",
    "credits.eligibility": "Kõlblikkuse tingimused",
    "credits.elig.income": "Tõesta regulaarsed ja stabiilsed tulud",
    "credits.elig.deposit": "Tee esialgne sissemakse: {amount}€",
    "credits.elig.garantie": "Esita tagatis: {garantie}",
    "credits.form.title": "Taotlus — {name}",
    "credits.form.cancel": "Tühista",
    "credits.form.submit": "Saada taotlus",
    "credits.form.amount": "Soovitud summa (€)",
    "credits.form.duration": "Kestus (kuudes)",
    "credits.form.reason": "Põhjus (vabatahtlik)",
    "credits.form.reason_ph": "Kirjelda oma projekti…",
    "credits.form.close": "Sulge",
    "credits.toast.submitted": "Laenu taotlus saadetud — saad vastuse 24 tunni jooksul.",
    "credits.p1.name": "Isiklik laen",
    "credits.p1.desc": "Igapäevaste vajaduste või isiklike projektide rahastamiseks.",
    "credits.p2.name": "Kodulaen",
    "credits.p2.desc": "Kinnisvara ostu või ehitamise rahastamiseks.",
    "credits.p3.name": "Auto- / motolaen",
    "credits.p3.desc": "Osta sõiduk muretult.",
    "credits.p4.name": "Õppelaen",
    "credits.p4.desc": "Rahaline toetus kõrgharidusõpinguteks.",
    "credits.p5.name": "Ärilaen",
    "credits.p5.desc": "Professionaalse tegevuse kasvu toetamiseks.",
    "credits.p6.name": "Kiirlaen",
    "credits.p6.desc": "Kiire lahendus kiireloomulistele vajadustele.",
    "financing.reimbursable": "Tagasimakstav rahastamine",
    "financing.reimbursable_desc": "Rahaline toetus fikseeritud tingimustega tegevuse käivitamiseks või arendamiseks.",
    "financing.non_reimbursable": "Tagastamatu rahastamine",
    "financing.non_reimbursable_desc": "Sotsiaalse, hariduse või humanitaarse mõjuga projektide jaoks (vabaühendused, ühingud).",
    "financing.cta_sub": "Kohandatud lahendused oma ambitsioonide teostamiseks.",
    "financing.request": "Taotle rahastamist",
    "financing.history": "Minu taotlused",
    "financing.reimbursable_label": "Tagasimakstav",
    "financing.non_reimbursable_label": "Tagastamatu",
    "financing.status.pending": "Ootel",
    "financing.status.granted": "Antud",
    "financing.status.refused": "Keeldutud",
    "financing.form.title": "Taotlus — {name}",
    "financing.form.cancel": "Tühista",
    "financing.form.submit": "Saada taotlus",
    "financing.form.project": "Projekti nimi",
    "financing.form.project_ph": "Minu projekt",
    "financing.form.amount": "Taotletav summa (€)",
    "financing.form.sector": "Sektor",
    "financing.form.sector_ph": "Tehnoloogia, põllumajandus, …",
    "financing.form.description": "Projekti kirjeldus",
    "financing.form.description_ph": "Tutvusta oma projekti, selle eesmärke ja oodatavat mõju…",
    "settings.security": "Turvalisus",
    "settings.current_pw": "Praegune parool",
    "settings.new_pw": "Uus parool",
    "settings.confirm_pw": "Kinnita",
    "settings.submit": "Muuda parooli",
    "settings.no_match": "Paroolid ei ühti",
    "settings.changed": "Parool muudetud",
    "common.cancel": "Tühista",
    "common.confirm": "Kinnita",
    "common.close": "Sulge",
    "common.save": "Salvesta",
    "common.loading": "Laadimine…",
    "common.send": "Saada",
    "common.total": "Kokku",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Hinnang. kuumakse",
    "credits.rate_annual": "/ aasta",
    "credits.rate_used": "Hinnang. määr:",
    "credits.simulation": "Igakuise makse simuleerimine",
    "credits.total_cost": "Hinnang. kogukulu",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Tere tulemast",
    "common.delete": "Kustuta",
    "cards.delete.title": "Kustuta kaart",
    "cards.delete.confirm_msg": "See toiming on pöördumatu. Kaart kustutatakse jäädavalt.",
    "cards.delete.physical_tooltip": "Füüsiline kaart tellitud — kustutamine võimatu",
    "cards.order.confirmed": "Tellimus kinnitatud!",
    "cards.order.confirmed_body": "Teie füüsilist kaarti valmistatakse. Kasutage seda numbrit tarne jälgimiseks.",
    "cards.order.tracking_no": "Jälgimisnumber",
    "cards.fee_deducted": "{amount} arvestatakse teie saldolt maha",
    "cards.insufficient_short": "Ebapiisav saldo ({available} saadaval)",
    "transfer.section.beneficiary": "Saaja",
    "transfer.section.details": "Ülekanne",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Pank",
    "transfer.field.city": "Linn",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Pank:",
    "transfer.progress.paused": "Ootab kinnitust",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Muutuv",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Kaart kustutatud",
    "cards.feat.gold_exclusive": "Eksklusiivne kuldkaart",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Ostja kaitse",
    "cards.feat.support_standard": "Standardtugi",
    "cards.feat.plastic_premium": "Premium plastkaart",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Standardreisikindlustus",
    "cards.feat.support_priority": "Prioriteetne tugi",
    "cards.feat.metal_brushed": "Harjatud metallkaart",
    "cards.feat.cashback_2_unlimited": "Piiramatu 2% cashback",
    "cards.feat.travel_premium": "Premium reisikindlustus",
    "cards.feat.vip_lounge": "VIP-saali pääs",
    "cards.feat.support_247": "24/7 abi",
    "auth.demo.title": "Administraatori sisselogimine",
    "auth.demo.subtitle": "Sisesta administraatori mandaadid demo eelvaateks",
    "credits.garantie.none": "Puudub",
    "credits.garantie.mortgage": "Hüpoteek finantseeritud kinnisvara peale",
    "credits.garantie.vehicle": "Finantseeritud sõiduk",
    "credits.garantie.parental": "Vanema või panga tagatis",
    "credits.garantie.professional": "Äritegevuse varad või isiklik tagatis",
    "cards.reveal.pin_prompt": "Sisestage oma 4-kohaline PIN-kood, et kuvada kaardi andmeid.",
    "cards.reveal.show": "Kuva",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Kaks PIN-koodi ei ühti",
    "auth.pin.digit_hint": "4 numbrit",
  
  },
  tr: {
    "notif.transfer_code.title": "Havale doğrulama kodu",
    "notif.transfer_code.body": "Havaleyi doğrulamak için bu kodu kopyalayın.",
    "notif.transfer_code.expires_in": "{sec}s sonra kaybolur",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} ay",
    "credits.n_months": "{n} ay",
    "msg.read": "Okundu",
    "msg.sent": "Gönderildi",
    "msg.attach_aria": "Dosya ekle",
    "login.device.unknown": "Bilinmeyen tarayıcı",
    "login.device.mobile": "Mobil",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Bilgisayar",
    "login.recent": "Son",
    "err.invalid_credentials": "Geçersiz kimlik bilgileri",
    "err.bad_pin": "Yanlış PIN",
    "err.account_blocked": "Hesap engellendi",
    "err.bad_current_pw": "Mevcut şifre yanlış",
    "err.insufficient_funds": "Yetersiz bakiye, hesabınızı yükleyin.",
    "err.amount_above_savings": "Tutar mevcut tasarrufu aşıyor",
    "err.must_be_positive": "Değer pozitif olmalıdır",
    "err.invalid_format": "Geçersiz format",
    "err.email_taken": "Email zaten kullanımda",
    "err.card_not_found": "Kart bulunamadı",
    "err.physical_card_already_ordered": "Fiziksel kart zaten sipariş edildi",
    "err.beneficiary_not_found": "Alıcı bulunamadı",
    "err.invalid_code": "Geçersiz kod",
    "err.no_transfer_code": "Bu transfer için kod yok",
    "err.link_expired": "Bağlantı geçersiz veya süresi dolmuş",
    "err.thread_not_found": "Konuşma bulunamadı",
    "err.empty_message": "Boş mesaj",
    "err.file_too_large": "Dosya çok büyük (maks 20 MB)",
    "err.forbidden": "Erişim reddedildi",
    "err.bank_not_found": "Banka bulunamadı",
    "err.signups_closed": "Bu banka için kayıtlar kapalıdır.",
    "err.access_suspended": "Erişim geçici olarak askıya alındı. Lütfen bankanızla iletişime geçin.",
    "err.notif_not_closable": "Bu bildirim kapatılamaz.",
    "auth.signup.tagline": "Bugün bize katılın",
    "auth.login.tagline": "Tekrar hoş geldiniz!",
    "auth.signup.tagline_sub": "Hesabınızı 2 dakikada açın ve tüm premium bankacılık hizmetlerimize erişin.",
    "auth.login.tagline_sub": "Kişisel alanınıza erişin ve finanslarınızı güvenle yönetin.",
    "auth.back": "Ana sayfaya dön",
    "auth.bullet.security": "Askeri düzeyde bankacılık güvenliği",
    "auth.bullet.transfers": "7/24 anlık transferler",
    "auth.bullet.investments": "Entegre yatırım ve tasarruf",
    "auth.bullet.advisor": "7/24 kişisel danışman desteği",
    "auth.footer": "{bank} tarafından desteklenmektedir",
    "auth.signup.title": "Hesap oluştur",
    "auth.login.title": "Giriş yap",
    "auth.field.lastName": "Soyad",
    "auth.field.firstName": "Ad",
    "auth.field.country": "Ülke",
    "auth.field.country.placeholder": "— Ülkenizi seçin —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Adres",
    "auth.field.address.placeholder": "Atatürk Cad. No:123, İstanbul",
    "auth.field.email": "E-posta",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Şifre",
    "auth.submit.signup": "Hesabımı oluştur",
    "auth.submit.login": "Giriş yap",
    "auth.login.badge": "Hesabınıza giriş yapın",
    "auth.card.tagline": "Müşteri alanınız",
    "auth.submit.loading": "Yükleniyor…",
    "auth.pin.title": "PIN Kodu",
    "auth.pin.subtitle": "4 haneli PIN Kodunuzu girin",
    "auth.pin.label": "PIN Kodu",
    "auth.pin.confirm": "PIN Kodunu Onayla",
    "auth.pin.submit": "Onayla",
    "auth.pin.back": "Girişe dön",
    "auth.pin.length_error": "PIN Kodu 4 haneli olmalıdır",
    "auth.switch.have_account": "Zaten hesabınız var mı?",
    "auth.switch.login_link": "Giriş yap",
    "auth.switch.no_account": "Henüz müşteri değil misiniz?",
    "auth.error.select_country": "Ülkenizi seçin",
    "kyc.title": "Kimlik Doğrulama (KYC)",
    "kyc.sub": "Hesabınızı doğrulamak için belgelerinizi yükleyin. Tüm belgeler şifreli ve gizlidir.",
    "kyc.id_type": "Kimlik belgesi türü *",
    "kyc.id_card": "Kimlik kartı",
    "kyc.passport": "Pasaport",
    "kyc.driver_license": "Sürücü belgesi",
    "kyc.recto_passport": "Pasaportunun ön yüzü *",
    "kyc.recto_id": "Kimliğin ön yüzü *",
    "kyc.verso": "Kimliğin arka yüzü *",
    "kyc.selfie": "Kimlikle selfie *",
    "kyc.address_proof": "İkametgah belgesi *",
    "kyc.hint_doc": "Resim veya PDF, maks. 5 MB",
    "kyc.hint_selfie": "Net fotoğraf, yüz ve belge görünür olmalı",
    "kyc.hint_address": "Fatura, ekstre, belge… (maks. 5 MB)",
    "kyc.upload": "Yükle",
    "kyc.replace": "Değiştir",
    "pending.title": "Hesap beklemede",
    "pending.body": "Talebiniz alındı. Bir yönetici dosyanızı inceleyecek ve hesabınızı kısa süre içinde etkinleştirecektir.",
    "pending.back": "Girişe dön",
    "home.nav.features": "Özellikler",
    "home.nav.security": "Güvenlik",
    "home.nav.invest": "Yatırım",
    "home.nav.credits": "Krediler",
    "home.nav.faq": "SSS",
    "home.nav.login": "Giriş yap",
    "home.hero.tagline": "Yeni nesil çevrimiçi bankacılık",
    "home.hero.title1": "Paranızı yönetin",
    "home.hero.title2": "zekice",
    "home.hero.sub": "Eksiksiz bir bankacılık platformu — akıllı telefonunuzdan transferler, tasarruf, yatırım ve krediler.",
    "home.hero.login": "Giriş yap",
    "home.hero.trust.secure": "100% Güvenli",
    "home.hero.trust.fast": "Hızlı açılış",
    "home.hero.trust.encrypted": "Şifreli veriler",
    "home.hero.trust.support": "7/24 Hizmet",
    "home.mockup.balance": "Kullanılabilir bakiye",
    "home.mockup.currency": "EUR · Ana hesap",
    "home.mockup.recharge": "Yükle",
    "home.mockup.send": "Gönder",
    "home.mockup.save": "Biriktir",
    "home.mockup.salary": "Mart Maaşı",
    "home.mockup.rent": "Nisan Kirası",
    "home.mockup.investment": "Yatırım",
    "home.mockup.days2": "2 gün önce",
    "home.mockup.days3": "3 gün önce",
    "home.mockup.days5": "5 gün önce",
    "home.stats.accounts": "Aktif hesaplar",
    "home.stats.uptime": "Çalışma süresi",
    "home.stats.transfers": "Transferler",
    "home.stats.support": "Destek",
    "home.features.label": "Özellikler",
    "home.features.title": "İhtiyacınız olan her şey",
    "home.features.sub": "Günlük finansal yönetiminizi kolaylaştırmak için tasarlanmış eksiksiz bir platform.",
    "home.feature.security": "Askeri güvenlik",
    "home.feature.security_body": "AES-256 şifreleme, iki faktörlü kimlik doğrulama ve hesabınızın 7/24 izlenmesi.",
    "home.feature.cards": "Sanal ve fiziksel kartlar",
    "home.feature.cards_body": "Saniyeler içinde kart oluşturun veya size teslim edilecek gerçek bir fiziksel kart sipariş edin.",
    "home.feature.transfers": "Hızlı transferler",
    "home.feature.transfers_body": "Dünyanın her yerine saniyeler içinde para gönderin ve alın.",
    "home.feature.analytics": "Gerçek zamanlı analiz",
    "home.feature.analytics_body": "Harcamalarınızı takip etmek ve finanslarınızı öngörmek için akıllı panolar.",
    "home.feature.investments": "Entegre yatırımlar",
    "home.feature.investments_body": "Hesabınızdan doğrudan seçilmiş yatırım ürünlerine erişin.",
    "home.feature.support": "7/24 özel destek",
    "home.feature.support_body": "Güvenli mesajlaşma yoluyla her zaman ulaşılabilir kişisel bankacılık danışmanı.",
    "home.tpl.label": "Güven",
    "home.tpl.title": "Bize güveniyorlar",
    "home.mockup.balance_label": "Bakiye",
    "home.mockup.virement": "Transfer",
    "home.mockup.salaire": "Maaş",
    "home.security.label": "Güvenlik",
    "home.security.title1": "Finanslarınız",
    "home.security.title2": "tam güvende",
    "home.security.sub": "Her kuruşunuzu korumak için en katı bankacılık güvenlik standartlarını uyguluyoruz.",
    "home.security.aes.title": "AES-256 Şifreleme",
    "home.security.aes.body": "Tüm hassas veriler, büyük uluslararası bankalar tarafından kullanılan algoritmalarla şifrelenmektedir.",
    "home.security.auth.title": "Güçlendirilmiş kimlik doğrulama",
    "home.security.auth.body": "Her girişte ve hesabınızdaki her hassas işlem için çift kimlik doğrulama.",
    "home.security.compliance.title": "PSD2/GDPR Uyumluluğu",
    "home.security.compliance.body": "Ödeme hizmetleri ve veri koruma konusundaki tüm Avrupa düzenlemelerine uyuyoruz.",
    "home.invest.label": "Yatırımlar",
    "home.invest.title": "Paranızı büyütün",
    "home.invest.sub": "Potansiyelleri ve güvenilirlikleri için seçilmiş 6 yatırım kategorisi.",
    "home.invest.yield": "Getiri",
    "home.credits.label": "Krediler",
    "home.credits.title": "Her projeye uygun çözümler",
    "home.credits.sub": "Tüm projeleriniz için rekabetçi oranlar, hızlı yanıtlar ve kişisel destek.",
    "home.credits.rate": "Oran",
    "home.faq.label": "SSS",
    "home.faq.title": "Sık sorulan sorular",
    "home.faq.q1": "Hesap nasıl açılır?",
    "home.faq.a1": "Kayıt işlemi 2 dakikadan az sürer. Kişisel bilgilerinizi girin, bir şifre seçin; hesabınız ekibimiz tarafından onaylandıktan sonra etkinleştirilecektir.",
    "home.faq.q2": "Verilerim güvende mi?",
    "home.faq.a2": "Evet. Tüm veriler beklemede AES-256 ve iletimde TLS 1.3 ile şifrelenmektedir. Uluslararası bankacılık standartlarına ve GDPR/PSD2 düzenlemelerine uymaktayız.",
    "home.faq.q3": "Hangi yatırım türlerini sunuyorsunuz?",
    "home.faq.a3": "6 kategori sunuyoruz: Gayrimenkul, Kitlesel Fonlama, Yenilenebilir Enerji, Borsa, Katılımcı ve Kripto. Her ürün risk/getiri oranına göre seçilmektedir.",
    "home.faq.q4": "Kredi başvurusu nasıl yapılır?",
    "home.faq.a4": "Müşteri alanınızdaki Kredi bölümüne gidin, kredi türünü seçin, tutar ve süreyi girin. Başvurunuz danışmanlarımız tarafından 24 saat içinde işleme alınır.",
    "home.faq.q5": "Gizli ücret var mı?",
    "home.faq.a5": "Gizli ücret yoktur. Tüm ücretler her işlemde açıkça görüntülenir. Koşullar müşteri alanınızda her zaman erişilebilir durumdadır.",
    "home.faq.q6": "Destek ile nasıl iletişime geçilir?",
    "home.faq.a6": "7/24 ulaşılabilir kişisel bankacılık danışmanınıza doğrudan ulaşmak için müşteri alanınızdaki entegre mesajlaşmayı kullanın.",
    "home.cta.title": "Başlamaya hazır mısınız?",
    "home.cta.sub": "{bank} ile finanslarını yöneten binlerce kullanıcıya katılın. Kayıt işlemi 2 dakikadan az sürer.",
    "home.footer.products": "Ürünler",
    "home.footer.services": "Hizmetler",
    "home.footer.access": "Hızlı erişim",
    "home.footer.accounts": "Hesaplar",
    "home.footer.transfers": "Transferler",
    "home.footer.cards": "Kartlar",
    "home.footer.savings": "Tasarruf",
    "home.footer.investments": "Yatırımlar",
    "home.footer.credits": "Krediler",
    "home.footer.financing": "Finansman",
    "home.footer.messages": "Mesajlar",
    "home.footer.login": "Giriş",
    "home.footer.signup": "Hesap aç",
    "home.footer.support": "7/24 Destek",
    "home.footer.tagline": "Çevrimiçi bankanız, her yerde ve her zaman.",
    "home.footer.copyright": "© {year} {bank} — Tüm hakları saklıdır",
    "home.footer.powered": "{bank} tarafından desteklenmektedir",
    "app.menu_label": "Ana menü",
    "app.nav.dashboard": "Kontrol paneli",
    "app.nav.accounts": "Hesaplar",
    "app.nav.transactions": "İşlemler",
    "app.nav.transfers": "Transferler",
    "app.nav.cards": "Kartlar",
    "app.nav.savings": "Tasarruf",
    "app.nav.investments": "Yatırım",
    "app.nav.credit": "Kredi",
    "app.nav.financing": "Finansman",
    "app.nav.messages": "Mesajlar",
    "app.nav.card_tracking": "Kart takibi",
    "app.nav.notifications": "Bildirimler",
    "app.nav.settings": "Ayarlar",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Ana sayfa",
    "app.nav.mobile.transfers": "Transferler",
    "app.nav.mobile.cards": "Kartlar",
    "app.nav.mobile.transactions": "İşlemler",
    "app.nav.more.invest": "Yatırım yap",
    "app.user_role": "Müşteri",
    "app.logout": "Çıkış yap",
    "app.search": "Ara…",
    "app.more_title": "Daha fazla özellik",
    "app.more_btn": "Daha fazla",
    "app.loading": "Yükleniyor…",
    "app.error_back": "Girişe dön",
    "app.see_more_notifs": "{count} bildirimi daha gör →",
    "page.accounts": "Hesaplarım",
    "page.transactions": "İşlemler",
    "page.transfers": "Yeni transfer",
    "page.deposit": "Para Yatırma",
    "page.cards": "Kartlarım",
    "page.savings": "Tasarruf",
    "page.investments": "Yatırım",
    "page.credits": "Kredi",
    "page.financing": "Proje finansmanı",
    "page.messages": "Mesajlar",
    "page.settings": "Ayarlar",
    "page.notifications": "Önemli bildirimler",
    "page.profile": "Profilim",
    "dash.greeting": "Merhaba, {name}",
    "dash.overview": "Finanslarınıza genel bakış — {date}",
    "dash.total_balance": "Toplam bakiye",
    "dash.this_month": "Bu ay +{amount}",
    "dash.view_iban": "IBAN'ımı görüntüle",
    "dash.my_accounts": "Hesaplarım",
    "dash.primary_account": "Ana hesap",
    "dash.savings_account": "Tasarruf hesabı",
    "dash.remunerated": "Faizli",
    "dash.details": "Detaylar",
    "dash.transfer_btn": "Transfer",
    "dash.quick_actions": "Hızlı işlemler",
    "dash.quick.deposit": "Para Yatır",
    "deposit.title": "Para yatırma bilgileri",
    "dash.quick.transfer": "Transfer",
    "dash.quick.history": "Geçmiş",
    "dash.quick.cards": "Kartlarım",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Aylık bütçe",
    "dash.income": "Gelir",
    "dash.expenses": "Giderler",
    "dash.spending_cat": "Kategoriye göre harcama",
    "dash.no_spending": "Kayıtlı harcama yok.",
    "dash.recent_tx": "Son işlemler",
    "dash.see_all": "Tümünü gör",
    "dash.no_tx": "İşlem bulunamadı.",
    "dash.main_card": "Ana kartım",
    "dash.manage": "Yönet",
    "dash.block": "Engelle",
    "dash.pin": "PIN kodu",
    "dash.security": "Güvenlik",
    "dash.more": "Daha fazla",
    "dash.no_card": "Kart verilmedi",
    "dash.monthly_activity": "Aylık aktivite",
    "dash.operations": "İşlemler",
    "dash.holder": "Kart sahibi",
    "dash.expires": "Son.",
    "dash.visa_label": "VISA",
    "dash.balance": "Kullanılabilir bakiye",
    "tx.all": "Tümü",
    "tx.income": "Gelir",
    "tx.expenses": "Giderler",
    "tx.today": "Bugün",
    "tx.yesterday": "Dün",
    "tx.none": "İşlem bulunamadı.",
    "transfer.title": "Hızlı transfer",
    "transfer.recipient": "Alıcı",
    "transfer.recipient_ph": "Ad, IBAN, telefon numarası…",
    "transfer.amount": "Tutar",
    "transfer.label": "Açıklama",
    "transfer.reason": "Neden (isteğe bağlı)",
    "transfer.reason_ph": "Örn: kira, geri ödeme…",
    "transfer.execution": "Yürütme",
    "transfer.immediate": "Anlık",
    "transfer.delayed": "Gecikmeli",
    "transfer.balance": "Kullanılabilir bakiye:",
    "transfer.continue": "Devam et",
    "transfer.confirm": "Onayla",
    "transfer.cancel": "İptal",
    "transfer.code": "Doğrulama kodu",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Transferi onaylamak için alınan kodu girin",
    "transfer.pending.title": "Transfer onay bekliyor",
    "transfer.pending.amount": "Tutar:",
    "transfer.pending.to": "Alıcı:",
    "transfer.pending.hint": "Doğrulama kodunuz e-posta veya bildirim zili aracılığıyla gönderilecektir. Kod 6 haneden oluşmaktadır. Almadıysanız lütfen destekle iletişime geçin.",
    "transfer.pending.validate": "Transferi onayla",
    "transfer.saved": "Transfer kaydedildi. Onaylamak için yöneticinizden kodu isteyin.",
    "transfer.done": "Transfer tamamlandı",
    "transfer.validated": "Transfer onaylandı!",
    "transfer.progress.processing": "Transfer işleniyor…",
    "transfer.progress.completed": "Transfer tamamlandı!",
    "transfer.progress.default_message": "Transferiniz işleniyor. Lütfen bekleyin.",
    "cards.none": "Kart verilmedi.\nYöneticinizle iletişime geçin.",
    "cards.holder": "Kart sahibi",
    "cards.expires": "Son.",
    "cards.block": "Kilitle",
    "cards.pin": "PIN kodu",
    "cards.more": "Daha fazla",
    "cards.limits": "Kart limitleri",
    "cards.payment": "Ödeme",
    "cards.withdrawal": "Para çekme",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Engellendi",
    "cards.status_active": "Aktif",
    "cards.toast.blocked": "Kart engellendi",
    "cards.toast.unblocked": "Kartın engeli kaldırıldı",
    "cards.toast.physical_ordered": "Fiziksel kart sipariş edildi! 5–10 iş günü içinde teslim edilecek.",
    "cards.toast.created": "Kart başarıyla oluşturuldu!",
    "cards.none_sub": "Kart bulunmuyor. Sanal kartınızı şimdi oluşturun.",
    "cards.generate_btn": "Kartımı oluştur",
    "cards.create.title": "Sanal kartımı oluştur",
    "cards.create.network": "Ödeme ağı",
    "cards.create.color": "Kart rengi",
    "cards.create.preview_name": "ADINIZ",
    "cards.create.preview_expiry": "AA/YY",
    "cards.create.loading": "Oluşturuluyor…",
    "cards.create.submit": "Kart oluştur",
    "cards.btn.unblock": "Engeli kaldır",
    "cards.btn.block": "Engelle",
    "cards.btn.info": "Bilgi",
    "cards.btn.physical": "Fiziksel",
    "cards.btn.physical_done": "Sipariş edildi",
    "cards.limits_title": "Limitler",
    "cards.reveal.title": "Kart bilgileri",
    "cards.reveal.number": "Tam numara",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Son kullanma tarihi",
    "cards.reveal.warning": "Bu bilgileri asla paylaşmayın. Kullandıktan sonra bu pencereyi kapatın.",
    "cards.pin.title": "PIN kodu",
    "cards.pin.message": "Bu PIN kodu tüm çevrimiçi ödemeleriniz ve para çekmeleriniz için kullanılır. Asla kimseyle paylaşmayın.",
    "cards.physical.title": "Fiziksel kart sipariş et",
    "cards.physical.fee_label": "Teslimat ücreti",
    "cards.physical.balance_label": "Bakiyeniz",
    "cards.physical.insufficient": "Yetersiz bakiye. Lütfen sipariş vermeden önce hesabınızı doldurun.",
    "cards.physical.info": "Ücret hesabınızdan düşülecektir. 5–10 iş günü içinde teslim edilecek.",
    "cards.physical.order_btn": "Sipariş ver ({fee})",
    "cards.physical.ordering": "Sipariş veriliyor…",
    "cards.physical.order_cta": "Fiziksel kart sipariş et",
    "cards.physical.choose_type": "Kartınızı seçin",
    "cards.physical.features": "Avantajlar",
    "cards.buy_virtual_cta": "Sanal kart satın al",
    "notif.none": "Şu an bildirim yok.",
    "notif.contact_support": "Desteğe başvurun",
    "profile.change_photo": "Fotoğrafı değiştir",
    "profile.remove_photo": "Fotoğrafı kaldır",
    "profile.email": "E-posta",
    "profile.phone": "Telefon",
    "profile.country": "Ülke",
    "profile.address": "Adres",
    "profile.notifs_title": "Bildirimler ({count})",
    "profile.no_notifs": "Bildirim yok.",
    "profile.logout": "Çıkış yap",
    "profile.photo_updated": "Profil fotoğrafı güncellendi",
    "profile.photo_removed": "Fotoğraf kaldırıldı",
    "profile.photo_too_large": "Resim çok büyük (maks. 1 MB)",
    "profile.powered": "{bank} tarafından desteklenmektedir",
    "iban.title": "IBAN'ım",
    "iban.loading": "Yükleniyor…",
    "iban.bic": "BIC / SWIFT kodu",
    "iban.share_info": "Hesabınıza para almak için bu bilgileri paylaşın.",
    "iban.copy": "Kopyala",
    "iban.copied": "Panoya kopyalandı",
    "iban.copy_fail": "Kopyalanamadı",
    "iban.close": "Kapat",
    "accounts.all": "{bank} nezdindeki tüm hesaplarınız.",
    "accounts.primary": "Ana hesap",
    "accounts.savings": "Tasarruf hesabı",
    "accounts.primary_tag": "Birincil",
    "accounts.secondary_tag": "İkincil",
    "accounts.objective": "Hedef: {amount} · {pct}%",
    "accounts.copy": "Kopyala",
    "accounts.loading": "Yükleniyor…",
    "accounts.iban_copied": "IBAN kopyalandı",
    "accounts.copy_fail": "Kopyalanamadı",
    "savings.total": "Toplam tasarruf",
    "savings.target_total": "Toplam hedef",
    "savings.progress": "İlerleme",
    "savings.active": "Aktif hedefler",
    "savings.new_goal": "Yeni hedef",
    "savings.none": "Henüz tasarruf hedefi yok.",
    "savings.none_sub": "Başlamak için ilk hedefinizi oluşturun.",
    "savings.saved": "Biriktirilen:",
    "savings.goal": "Hedef:",
    "savings.deadline": "Son tarih: {date}",
    "savings.deposit": "Yatır",
    "savings.withdraw": "Çek",
    "savings.loading": "Yükleniyor…",
    "savings.form.title": "Yeni tasarruf hedefi",
    "savings.form.cancel": "İptal",
    "savings.form.create": "Oluştur",
    "savings.form.name": "Hedef adı",
    "savings.form.name_ph": "Japonya gezisi, Araba, …",
    "savings.form.target": "Hedef tutar",
    "savings.form.initial": "İlk yatırım",
    "savings.form.deadline": "Son tarih (isteğe bağlı)",
    "savings.form.color": "Renk",
    "savings.deposit_title": "\"{name}\" hesabına yatır",
    "savings.deposit_amount": "Yatırılacak tutar",
    "savings.balance_hint": "Ana bakiye: {amount}",
    "savings.withdraw_prompt": "\"{name}\" hesabından ne kadar çekmek istiyorsunuz?",
    "savings.close_confirm": "\"{name}\" hedefi kapatılsın mı? Tasarruf ana hesabınıza aktarılacaktır.",
    "savings.toast.created": "Hedef oluşturuldu",
    "savings.toast.deposited": "Yatırma işlemi yapıldı",
    "savings.toast.withdrawn": "Çekme işlemi yapıldı",
    "savings.toast.closed": "Hedef kapatıldı",
    "invest.total": "Toplam yatırım",
    "invest.profits": "Kümülatif kârlar",
    "invest.count": "Yatırımlar",
    "invest.products": "Mevcut ürünler",
    "invest.invest_btn": "Yatırım yap",
    "invest.my": "Yatırımlarım",
    "invest.pending": "Beklemede",
    "invest.validated": "Onaylandı",
    "invest.refused": "Reddedildi",
    "invest.withdraw.btn": "Çek",
    "invest.withdraw.title": "Yatırımımı çek",
    "invest.withdraw.intro": "\"{product}\" yatırımınızı çekmek üzeresiniz. İlk anapara ve tahakkuk eden kâr ana bakiyenize aktarılacaktır.",
    "invest.withdraw.principal": "Anapara",
    "invest.withdraw.profit": "Kâr",
    "invest.withdraw.total": "Alınacak toplam",
    "invest.withdraw.confirm": "Çekimi onayla",
    "invest.withdraw.warning": "Bu işlem geri alınamaz. Yatırım kapatılacaktır.",
    "invest.withdraw.success": "Yatırım çekildi — {amount} bakiyenize eklendi.",
    "invest.withdrawn": "Çekildi",
    "invest.cta_sub": "Size uygun yatırım türünü seçin. Bir danışman ilk talebinizden itibaren size eşlik edecektir.",
    "invest.form.amount": "Yatırılacak tutar",
    "invest.form.balance": "Kullanılabilir bakiye: {amount}",
    "invest.form.note": "Danışmanınıza not (isteğe bağlı)",
    "invest.form.note_ph": "Beklentilerinizi belirtin…",
    "invest.form.advisor_info": "Danışmanınızla otomatik olarak bir görüşme açılacaktır.",
    "invest.form.cancel": "İptal",
    "invest.form.submit": "Talep gönder",
    "invest.toast.requested": "Talep kaydedildi — danışmanınızla bir görüşme açıldı.",
    "invest.p1.name": "Gayrimenkul",
    "invest.p1.desc": "SCPI, gayrimenkul kitlesel fonlaması",
    "invest.p2.name": "Kitlesel Fonlama",
    "invest.p2.desc": "Seçilmiş projelerin katılımcı finansmanı",
    "invest.p3.name": "Yenilenebilir Enerji",
    "invest.p3.desc": "Güneş, rüzgar ve yeşil projeler",
    "invest.p4.name": "Borsa",
    "invest.p4.desc": "Hisseler, ETF ve çeşitlendirilmiş fonlar",
    "invest.p5.name": "Katılımcı",
    "invest.p5.desc": "KOBİ'lere ve yerel girişimcilere krediler",
    "invest.p6.name": "Kripto",
    "invest.p6.desc": "Bitcoin, Ethereum ve çok para birimli cüzdan",
    "msg.online": "Çevrimiçi",
    "msg.loading": "Yükleniyor…",
    "msg.none": "Mesaj yok. Danışmanınızla konuşmayı başlatın.",
    "msg.placeholder": "Mesajınızı yazın…",
    "msg.attach.image": "Resim",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Ses",
    "msg.attach.file": "Dosya / Belge",
    "msg.pdf_doc": "PDF Belgesi",
    "msg.file": "Dosya",
    "msg.too_large": "Dosya çok büyük (maks. {size}).",
    "msg.withdraw": "Geri al",
    "credits.cta_sub": "Başvurunuzu doğrudan çevrimiçi yapın — 24 saat içinde yanıt.",
    "credits.learn_more": "Daha fazla bilgi",
    "credits.request": "Başvur",
    "credits.history": "Başvurularım",
    "credits.status.pending": "Beklemede",
    "credits.status.accepted": "Kabul edildi",
    "credits.status.refused": "Reddedildi",
    "credits.chars": "Özellikler",
    "credits.amount_range": "Tutar: {min}€ – {max}€",
    "credits.rate_range": "Faiz oranı: yıllık %{min} – %{max}",
    "credits.duration_range": "Süre: {min} – {max} ay",
    "credits.deposit_min": "Başlangıç yatırımı: {amount}€ minimum",
    "credits.garantie": "Teminat: {garantie}",
    "credits.eligibility": "Uygunluk koşulları",
    "credits.elig.income": "Düzenli ve istikrarlı gelir kanıtı",
    "credits.elig.deposit": "{amount}€ başlangıç yatırımı yapın",
    "credits.elig.garantie": "Teminatı sunun: {garantie}",
    "credits.form.title": "Başvuru — {name}",
    "credits.form.cancel": "İptal",
    "credits.form.submit": "Başvuruyu gönder",
    "credits.form.amount": "İstenen tutar (€)",
    "credits.form.duration": "Süre (ay)",
    "credits.form.reason": "Neden (isteğe bağlı)",
    "credits.form.reason_ph": "Projenizi açıklayın…",
    "credits.form.close": "Kapat",
    "credits.toast.submitted": "Kredi başvurusu gönderildi — 24 saat içinde yanıt alacaksınız.",
    "credits.p1.name": "Bireysel Kredi",
    "credits.p1.desc": "Günlük ihtiyaçlar veya kişisel projeler için.",
    "credits.p2.name": "Konut Kredisi",
    "credits.p2.desc": "Gayrimenkul satın alma veya inşaat finansmanı için.",
    "credits.p3.name": "Araç Kredisi",
    "credits.p3.desc": "Gönül rahatlığıyla araç satın alın.",
    "credits.p4.name": "Öğrenci Kredisi",
    "credits.p4.desc": "Yükseköğretim için finansal destek.",
    "credits.p5.name": "Ticari Kredi",
    "credits.p5.desc": "Profesyonel faaliyetin büyümesini desteklemek için.",
    "credits.p6.name": "Hızlı Kredi",
    "credits.p6.desc": "Acil ihtiyaçlar için hızlı çözüm.",
    "financing.reimbursable": "Geri ödemeli finansman",
    "financing.reimbursable_desc": "Bir faaliyeti başlatmak veya geliştirmek için sabit koşullarla finansal destek.",
    "financing.non_reimbursable": "Geri ödemesiz finansman",
    "financing.non_reimbursable_desc": "Sosyal, eğitimsel veya insani etkili projeler için (STK'lar, dernekler).",
    "financing.cta_sub": "Hedeflerinizi gerçeğe dönüştürmek için uyarlanmış çözümler.",
    "financing.request": "Finansman talep et",
    "financing.history": "Başvurularım",
    "financing.reimbursable_label": "Geri ödemeli",
    "financing.non_reimbursable_label": "Geri ödemesiz",
    "financing.status.pending": "Beklemede",
    "financing.status.granted": "Onaylandı",
    "financing.status.refused": "Reddedildi",
    "financing.form.title": "Başvuru — {name}",
    "financing.form.cancel": "İptal",
    "financing.form.submit": "Başvuruyu gönder",
    "financing.form.project": "Proje adı",
    "financing.form.project_ph": "Projem",
    "financing.form.amount": "Talep edilen tutar (€)",
    "financing.form.sector": "Sektör",
    "financing.form.sector_ph": "Teknoloji, tarım, …",
    "financing.form.description": "Proje açıklaması",
    "financing.form.description_ph": "Projenizi, hedeflerini ve beklenen etkisini tanıtın…",
    "settings.security": "Güvenlik",
    "settings.current_pw": "Mevcut şifre",
    "settings.new_pw": "Yeni şifre",
    "settings.confirm_pw": "Onayla",
    "settings.submit": "Şifreyi değiştir",
    "settings.no_match": "Şifreler eşleşmiyor",
    "settings.changed": "Şifre değiştirildi",
    "common.cancel": "İptal",
    "common.confirm": "Onayla",
    "common.close": "Kapat",
    "common.save": "Kaydet",
    "common.loading": "Yükleniyor…",
    "common.send": "Gönder",
    "common.total": "Toplam",
    "admin.wallet.balance": "Mevcut bakiye",
    "admin.wallet.history": "Geçmiş",
    "admin.wallet.no_history": "İşlem yok",
    "admin.wallet.title": "Cüzdanım",
    "admin.wallet.topup": "Yükle",
    "beneficiary.pick": "Seç",
    "beneficiary.save_this": "Bu alıcıyı kaydet",
    "beneficiary.saved": "Kayıtlı alıcılar",
    "beneficiary.saved_ok": "Alıcı kaydedildi",
    "card.color.amber": "Kehribar",
    "card.color.cyan": "Camgöbeği",
    "card.color.emerald": "Zümrüt",
    "card.color.forest": "Orman",
    "card.color.gold": "Altın",
    "card.color.lavender": "Lavanta",
    "card.color.night": "Gece",
    "card.color.orange": "Turuncu",
    "card.color.pink": "Pembe",
    "card.color.royal": "Kraliyet mavisi",
    "card.color.ruby": "Yakut",
    "card.color.slate": "Arduvaz",
    "card.color.steel": "Çelik",
    "card.color.terracotta": "Toprak rengi",
    "card.color.violet": "Mor",
    "common.other": "Diğer",
    "common.search": "Ara",
    "content.translated": "Otomatik çevrilmiş içerik",
    "credits.monthly_payment": "Tahmini aylık ödeme",
    "credits.rate_annual": "/ yıl",
    "credits.rate_used": "Tahmini faiz oranı:",
    "credits.simulation": "Aylık ödeme tahmini",
    "credits.total_cost": "Tahmini toplam maliyet",
    "demo.banner": "Demo modu — gerçek işlem yok",
    "demo.disabled": "Demo modunda işlem yapılamaz",
    "deposit.default_instructions": "Para yatırma detayları için yöneticinizle iletişime geçin.",
    "error.back_home": "Ana sayfaya dön",
    "error.bank_not_found": "Banka bulunamadı",
    "error.unknown": "Bilinmeyen hata",
    "kyc.file_read_error": "Dosya okunamıyor",
    "kyc.file_too_large": "Dosya çok büyük (maks. 5 MB)",
    "login.history": "Giriş geçmişi",
    "login.history.empty": "Kayıtlı giriş yok.",
    "notif.dismiss": "Kapat",
    "notif.locked": "Banka mesajı",
    "tracking.error.not_found": "Takip numarası bulunamadı. Lütfen kontrol edip tekrar deneyin.",
    "tracking.label": "Takip",
    "tracking.status.delivered": "Teslim edildi",
    "tracking.status.preparation": "Hazırlanıyor",
    "tracking.status.shipped": "Kargoya verildi",
    "tracking.status.transit": "Yolda",
    "transfer.code.copied": "Kopyalandı",
    "transfer.code.copy": "Kopyala",
    "transfer.code.received": "Doğrulama kodunuz",
    "dash.welcome": "Hoş geldiniz",
    "common.delete": "Sil",
    "cards.delete.title": "Kartı sil",
    "cards.delete.confirm_msg": "Bu işlem geri alınamaz. Kart kalıcı olarak silinecektir.",
    "cards.delete.physical_tooltip": "Fiziksel kart sipariş edildi — silme imkânsız",
    "cards.order.confirmed": "Sipariş onaylandı!",
    "cards.order.confirmed_body": "Fiziksel kartınız hazırlanıyor. Teslimatınızı takip etmek için bu numarayı kullanın.",
    "cards.order.tracking_no": "Takip numarası",
    "cards.fee_deducted": "{amount} bakiyenizden düşülecektir",
    "cards.insufficient_short": "Yetersiz bakiye ({available} mevcut)",
    "transfer.section.beneficiary": "Faydalanan",
    "transfer.section.details": "Havale",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banka",
    "transfer.field.city": "Şehir",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banka:",
    "transfer.progress.paused": "Doğrulama bekleniyor",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Değişken",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Kart silindi",
    "cards.feat.gold_exclusive": "Özel altın kart",
    "cards.feat.cashback_05": "Cashback %0,5",
    "cards.feat.buyer_protection": "Alıcı koruması",
    "cards.feat.support_standard": "Standart destek",
    "cards.feat.plastic_premium": "Premium plastik kart",
    "cards.feat.cashback_1": "Cashback %1",
    "cards.feat.travel_standard": "Standart seyahat sigortası",
    "cards.feat.support_priority": "Öncelikli destek",
    "cards.feat.metal_brushed": "Fırçalanmış metal kart",
    "cards.feat.cashback_2_unlimited": "Sınırsız %2 cashback",
    "cards.feat.travel_premium": "Premium seyahat sigortası",
    "cards.feat.vip_lounge": "VIP salon erişimi",
    "cards.feat.support_247": "7/24 destek",
    "auth.demo.title": "Yönetici girişi",
    "auth.demo.subtitle": "Demoyu önizlemek için yönetici bilgilerinizi girin",
    "credits.garantie.none": "Yok",
    "credits.garantie.mortgage": "Finanse edilen mülk üzerinde ipotek",
    "credits.garantie.vehicle": "Finanse edilen araç",
    "credits.garantie.parental": "Ebeveyn veya banka",
    "credits.garantie.professional": "Profesyonel varlıklar veya kişisel kefalet",
    "cards.reveal.pin_prompt": "Kart bilgilerini görüntülemek için 4 haneli PIN kodunuzu girin.",
    "cards.reveal.show": "Göster",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "İki PIN kodu eşleşmiyor",
    "auth.pin.digit_hint": "4 rakam",
  
  },
  el: {
    "notif.transfer_code.title": "Κωδικός επαλήθευσης μεταφοράς",
    "notif.transfer_code.body": "Αντιγράψτε αυτόν τον κωδικό για να επαληθεύσετε τη μεταφορά.",
    "notif.transfer_code.expires_in": "Εξαφανίζεται σε {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} μήνες",
    "credits.n_months": "{n} μήνες",
    "msg.read": "Αναγνώστηκε",
    "msg.sent": "Στάλθηκε",
    "msg.attach_aria": "Επισύναψη αρχείου",
    "login.device.unknown": "Άγνωστο πρόγραμμα περιήγησης",
    "login.device.mobile": "Κινητό",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Υπολογιστής",
    "login.recent": "Πρόσφατο",
    "err.invalid_credentials": "Μη έγκυρα διαπιστευτήρια",
    "err.bad_pin": "Λανθασμένο PIN",
    "err.account_blocked": "Λογαριασμός αποκλεισμένος",
    "err.bad_current_pw": "Ο τρέχων κωδικός είναι λανθασμένος",
    "err.insufficient_funds": "Ανεπαρκή κεφάλαια, φορτίστε τον λογαριασμό σας.",
    "err.amount_above_savings": "Το ποσό υπερβαίνει τις διαθέσιμες αποταμιεύσεις",
    "err.must_be_positive": "Η τιμή πρέπει να είναι θετική",
    "err.invalid_format": "Μη έγκυρη μορφή",
    "err.email_taken": "Το email χρησιμοποιείται ήδη",
    "err.card_not_found": "Η κάρτα δεν βρέθηκε",
    "err.physical_card_already_ordered": "Η φυσική κάρτα έχει ήδη παραγγελθεί",
    "err.beneficiary_not_found": "Ο δικαιούχος δεν βρέθηκε",
    "err.invalid_code": "Μη έγκυρος κωδικός",
    "err.no_transfer_code": "Κανένας κωδικός για αυτή τη μεταφορά",
    "err.link_expired": "Σύνδεσμος άκυρος ή έχει λήξει",
    "err.thread_not_found": "Η συνομιλία δεν βρέθηκε",
    "err.empty_message": "Κενό μήνυμα",
    "err.file_too_large": "Το αρχείο είναι πολύ μεγάλο (μέγ. 20 MB)",
    "err.forbidden": "Άρνηση πρόσβασης",
    "err.bank_not_found": "Η τράπεζα δεν βρέθηκε",
    "err.signups_closed": "Οι εγγραφές για αυτή την τράπεζα είναι κλειστές.",
    "err.access_suspended": "Η πρόσβαση έχει ανασταλεί προσωρινά. Παρακαλούμε επικοινωνήστε με την τράπεζά σας.",
    "err.notif_not_closable": "Αυτή η ειδοποίηση δεν μπορεί να κλείσει.",
    "auth.signup.tagline": "Εγγραφείτε σήμερα",
    "auth.login.tagline": "Καλώς ορίσατε!",
    "auth.signup.tagline_sub": "Ανοίξτε τον λογαριασμό σας σε 2 λεπτά και αποκτήστε πρόσβαση σε όλες τις premium τραπεζικές υπηρεσίες μας.",
    "auth.login.tagline_sub": "Αποκτήστε πρόσβαση στον προσωπικό σας χώρο και διαχειριστείτε τα οικονομικά σας με ασφάλεια.",
    "auth.back": "Επιστροφή στην αρχική",
    "auth.bullet.security": "Τραπεζική ασφάλεια στρατιωτικού επιπέδου",
    "auth.bullet.transfers": "Άμεσες μεταφορές 24/7",
    "auth.bullet.investments": "Ενσωματωμένες επενδύσεις και αποταμιεύσεις",
    "auth.bullet.advisor": "Προσωπικός σύμβουλος διαθέσιμος 7 ημέρες/7",
    "auth.footer": "Με την υποστήριξη του {bank}",
    "auth.signup.title": "Δημιουργία λογαριασμού",
    "auth.login.title": "Σύνδεση",
    "auth.field.lastName": "Επώνυμο",
    "auth.field.firstName": "Όνομα",
    "auth.field.country": "Χώρα",
    "auth.field.country.placeholder": "— Επιλέξτε τη χώρα σας —",
    "auth.field.phone": "Τηλέφωνο",
    "auth.field.address": "Διεύθυνση",
    "auth.field.address.placeholder": "Λεωφ. Αθηνών 123, Αθήνα",
    "auth.field.email": "Email",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Κωδικός πρόσβασης",
    "auth.submit.signup": "Δημιουργία λογαριασμού",
    "auth.submit.login": "Σύνδεση",
    "auth.login.badge": "Συνδεθείτε στον λογαριασμό σας",
    "auth.card.tagline": "Ο χώρος σας",
    "auth.submit.loading": "Φόρτωση…",
    "auth.pin.title": "Κωδικός PIN",
    "auth.pin.subtitle": "Εισαγάγετε τον 4ψήφιο κωδικό PIN",
    "auth.pin.label": "Κωδικός PIN",
    "auth.pin.confirm": "Επιβεβαίωση Κωδικού PIN",
    "auth.pin.submit": "Επιβεβαίωση",
    "auth.pin.back": "Επιστροφή στη σύνδεση",
    "auth.pin.length_error": "Ο κωδικός PIN πρέπει να έχει 4 ψηφία",
    "auth.switch.have_account": "Έχετε ήδη λογαριασμό;",
    "auth.switch.login_link": "Σύνδεση",
    "auth.switch.no_account": "Δεν είστε ακόμη πελάτης;",
    "auth.error.select_country": "Επιλέξτε τη χώρα σας",
    "kyc.title": "Επαλήθευση ταυτότητας (KYC)",
    "kyc.sub": "Ανεβάστε τα έγγραφά σας για να επικυρώσετε τον λογαριασμό σας. Όλα τα έγγραφα είναι κρυπτογραφημένα και εμπιστευτικά.",
    "kyc.id_type": "Τύπος εγγράφου ταυτότητας *",
    "kyc.id_card": "Δελτίο ταυτότητας",
    "kyc.passport": "Διαβατήριο",
    "kyc.driver_license": "Άδεια οδήγησης",
    "kyc.recto_passport": "Εμπρός όψη διαβατηρίου *",
    "kyc.recto_id": "Εμπρός όψη ταυτότητας *",
    "kyc.verso": "Πίσω όψη ταυτότητας *",
    "kyc.selfie": "Selfie με την ταυτότητα στο χέρι *",
    "kyc.address_proof": "Απόδειξη διεύθυνσης *",
    "kyc.hint_doc": "Εικόνα ή PDF, μέγ. 5 MB",
    "kyc.hint_selfie": "Καθαρή φωτογραφία, πρόσωπο και έγγραφο ορατά",
    "kyc.hint_address": "Λογαριασμός, κατάσταση, βεβαίωση… (μέγ. 5 MB)",
    "kyc.upload": "Μεταφόρτωση",
    "kyc.replace": "Αντικατάσταση",
    "pending.title": "Λογαριασμός υπό αναμονή",
    "pending.body": "Το αίτημά σας ελήφθη. Ένας διαχειριστής θα επαληθεύσει τον φάκελό σας και θα ενεργοποιήσει τον λογαριασμό σας σύντομα.",
    "pending.back": "Επιστροφή στη σύνδεση",
    "home.nav.features": "Χαρακτηριστικά",
    "home.nav.security": "Ασφάλεια",
    "home.nav.invest": "Επένδυση",
    "home.nav.credits": "Πιστώσεις",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Σύνδεση",
    "home.hero.tagline": "Ηλεκτρονική τραπεζική νέας γενιάς",
    "home.hero.title1": "Διαχειριστείτε τα χρήματά σας",
    "home.hero.title2": "με ευφυΐα",
    "home.hero.sub": "Μια πλήρης τραπεζική πλατφόρμα — μεταφορές, αποταμιεύσεις, επενδύσεις και πιστώσεις από το smartphone σας.",
    "home.hero.login": "Σύνδεση",
    "home.hero.trust.secure": "100% Ασφαλές",
    "home.hero.trust.fast": "Γρήγορη εγγραφή",
    "home.hero.trust.encrypted": "Κρυπτογραφημένα δεδομένα",
    "home.hero.trust.support": "Υπηρεσία 24/7",
    "home.mockup.balance": "Διαθέσιμο υπόλοιπο",
    "home.mockup.currency": "EUR · Κύριος λογαριασμός",
    "home.mockup.recharge": "Φόρτωση",
    "home.mockup.send": "Αποστολή",
    "home.mockup.save": "Αποταμίευση",
    "home.mockup.salary": "Μισθός Μαρτίου",
    "home.mockup.rent": "Ενοίκιο Απριλίου",
    "home.mockup.investment": "Επένδυση",
    "home.mockup.days2": "2 ημέρες πριν",
    "home.mockup.days3": "3 ημέρες πριν",
    "home.mockup.days5": "5 ημέρες πριν",
    "home.stats.accounts": "Ενεργοί λογαριασμοί",
    "home.stats.uptime": "Διαθεσιμότητα",
    "home.stats.transfers": "Μεταφορές",
    "home.stats.support": "Υποστήριξη",
    "home.features.label": "Χαρακτηριστικά",
    "home.features.title": "Όλα όσα χρειάζεστε",
    "home.features.sub": "Μια πλήρης πλατφόρμα σχεδιασμένη να απλοποιεί τη διαχείριση των οικονομικών σας καθημερινά.",
    "home.feature.security": "Στρατιωτική ασφάλεια",
    "home.feature.security_body": "Κρυπτογράφηση AES-256, έλεγχος ταυτότητας δύο παραγόντων και παρακολούθηση λογαριασμού 24/7.",
    "home.feature.cards": "Εικονικές και φυσικές κάρτες",
    "home.feature.cards_body": "Εκδώστε κάρτες σε δευτερόλεπτα ή παραγγείλτε μια πραγματική φυσική κάρτα στο σπίτι σας.",
    "home.feature.transfers": "Εξπρές μεταφορές",
    "home.feature.transfers_body": "Στείλτε και λάβετε χρήματα οπουδήποτε στον κόσμο σε δευτερόλεπτα.",
    "home.feature.analytics": "Ανάλυση σε πραγματικό χρόνο",
    "home.feature.analytics_body": "Έξυπνοι πίνακες ελέγχου για παρακολούθηση δαπανών και πρόβλεψη οικονομικών.",
    "home.feature.investments": "Ενσωματωμένες επενδύσεις",
    "home.feature.investments_body": "Αποκτήστε πρόσβαση σε επιλεγμένα επενδυτικά προϊόντα απευθείας από τον λογαριασμό σας.",
    "home.feature.support": "Αποκλειστική υποστήριξη 24/7",
    "home.feature.support_body": "Προσωπικός τραπεζικός σύμβουλος διαθέσιμος ανά πάσα στιγμή μέσω ασφαλούς μηνυμάτων.",
    "home.tpl.label": "Εμπιστοσύνη",
    "home.tpl.title": "Μας εμπιστεύονται",
    "home.mockup.balance_label": "Υπόλοιπο",
    "home.mockup.virement": "Μεταφορά",
    "home.mockup.salaire": "Μισθός",
    "home.security.label": "Ασφάλεια",
    "home.security.title1": "Τα οικονομικά σας",
    "home.security.title2": "σε απόλυτη ασφάλεια",
    "home.security.sub": "Εφαρμόζουμε τα αυστηρότερα πρότυπα τραπεζικής ασφάλειας για να προστατεύουμε κάθε σεντ.",
    "home.security.aes.title": "Κρυπτογράφηση AES-256",
    "home.security.aes.body": "Όλα τα ευαίσθητα δεδομένα κρυπτογραφούνται με τους ίδιους αλγορίθμους που χρησιμοποιούν οι μεγάλες διεθνείς τράπεζες.",
    "home.security.auth.title": "Ενισχυμένος έλεγχος ταυτότητας",
    "home.security.auth.body": "Διπλή επαλήθευση ταυτότητας σε κάθε σύνδεση και για κάθε ευαίσθητη λειτουργία στον λογαριασμό σας.",
    "home.security.compliance.title": "Συμμόρφωση PSD2/GDPR",
    "home.security.compliance.body": "Συμμορφωνόμαστε με όλους τους ευρωπαϊκούς κανονισμούς για υπηρεσίες πληρωμών και προστασία δεδομένων.",
    "home.invest.label": "Επενδύσεις",
    "home.invest.title": "Αναπτύξτε τα χρήματά σας",
    "home.invest.sub": "6 κατηγορίες επενδύσεων επιλεγμένες για τις δυνατότητές τους και την αξιοπιστία τους.",
    "home.invest.yield": "Απόδοση",
    "home.credits.label": "Πιστώσεις",
    "home.credits.title": "Λύσεις για κάθε έργο",
    "home.credits.sub": "Ανταγωνιστικά επιτόκια, γρήγορες απαντήσεις και εξατομικευμένη υποστήριξη για όλα τα έργα σας.",
    "home.credits.rate": "Επιτόκιο",
    "home.faq.label": "FAQ",
    "home.faq.title": "Συχνές ερωτήσεις",
    "home.faq.q1": "Πώς ανοίγω λογαριασμό;",
    "home.faq.a1": "Η εγγραφή διαρκεί λιγότερο από 2 λεπτά. Απλώς συμπληρώστε τα προσωπικά σας στοιχεία, επιλέξτε κωδικό πρόσβασης και ο λογαριασμός σας θα ενεργοποιηθεί μετά την επαλήθευση από την ομάδα μας.",
    "home.faq.q2": "Είναι ασφαλή τα δεδομένα μου;",
    "home.faq.a2": "Ναι. Όλα τα δεδομένα κρυπτογραφούνται με AES-256 σε ηρεμία και TLS 1.3 κατά τη μεταφορά. Συμμορφωνόμαστε με τα διεθνή τραπεζικά πρότυπα και τους κανονισμούς GDPR/PSD2.",
    "home.faq.q3": "Ποιους τύπους επενδύσεων προσφέρετε;",
    "home.faq.a3": "Προσφέρουμε 6 κατηγορίες: Ακίνητα, Crowdfunding, Ανανεώσιμες Πηγές Ενέργειας, Χρηματιστήριο, Συμμετοχικές και Crypto. Κάθε προϊόν επιλέγεται για τον λόγο κινδύνου/απόδοσης.",
    "home.faq.q4": "Πώς υποβάλλω αίτηση πίστωσης;",
    "home.faq.a4": "Μεταβείτε στην ενότητα Πίστωση του χώρου πελατών σας, επιλέξτε τον τύπο δανείου, εισαγάγετε το ποσό και τη διάρκεια. Η αίτησή σας επεξεργάζεται εντός 24 ωρών από τους συμβούλους μας.",
    "home.faq.q5": "Υπάρχουν κρυφές χρεώσεις;",
    "home.faq.a5": "Καμία κρυφή χρέωση. Όλες οι τιμές εμφανίζονται με σαφήνεια σε κάθε συναλλαγή. Οι όροι είναι προσβάσιμοι στον χώρο πελατών σας ανά πάσα στιγμή.",
    "home.faq.q6": "Πώς επικοινωνώ με την υποστήριξη;",
    "home.faq.a6": "Χρησιμοποιήστε την ενσωματωμένη αλληλογραφία στον χώρο πελατών σας για να επικοινωνήσετε απευθείας με τον προσωπικό σας τραπεζικό σύμβουλο, διαθέσιμο 24/7.",
    "home.cta.title": "Έτοιμοι να ξεκινήσετε;",
    "home.cta.sub": "Γίνετε μέλος χιλιάδων χρηστών που διαχειρίζονται τα οικονομικά τους με {bank}. Η εγγραφή διαρκεί λιγότερο από 2 λεπτά.",
    "home.footer.products": "Προϊόντα",
    "home.footer.services": "Υπηρεσίες",
    "home.footer.access": "Γρήγορη πρόσβαση",
    "home.footer.accounts": "Λογαριασμοί",
    "home.footer.transfers": "Μεταφορές",
    "home.footer.cards": "Κάρτες",
    "home.footer.savings": "Αποταμιεύσεις",
    "home.footer.investments": "Επενδύσεις",
    "home.footer.credits": "Πιστώσεις",
    "home.footer.financing": "Χρηματοδότηση",
    "home.footer.messages": "Μηνύματα",
    "home.footer.login": "Σύνδεση",
    "home.footer.signup": "Άνοιγμα λογαριασμού",
    "home.footer.support": "Υποστήριξη 24/7",
    "home.footer.tagline": "Η ηλεκτρονική τράπεζά σας, διαθέσιμη παντού και ανά πάσα στιγμή.",
    "home.footer.copyright": "© {year} {bank} — Όλα τα δικαιώματα διατηρούνται",
    "home.footer.powered": "Με την υποστήριξη του {bank}",
    "app.menu_label": "Κύριο μενού",
    "app.nav.dashboard": "Πίνακας ελέγχου",
    "app.nav.accounts": "Λογαριασμοί",
    "app.nav.transactions": "Συναλλαγές",
    "app.nav.transfers": "Μεταφορές",
    "app.nav.cards": "Κάρτες",
    "app.nav.savings": "Αποταμιεύσεις",
    "app.nav.investments": "Επένδυση",
    "app.nav.credit": "Πίστωση",
    "app.nav.financing": "Χρηματοδότηση",
    "app.nav.messages": "Μηνύματα",
    "app.nav.card_tracking": "Παρακολούθηση κάρτας",
    "app.nav.notifications": "Ειδοποιήσεις",
    "app.nav.settings": "Ρυθμίσεις",
    "app.nav.profile": "Προφίλ",
    "app.nav.mobile.home": "Αρχική",
    "app.nav.mobile.transfers": "Μεταφορές",
    "app.nav.mobile.cards": "Κάρτες",
    "app.nav.mobile.transactions": "Συναλλαγές",
    "app.nav.more.invest": "Επένδυση",
    "app.user_role": "Πελάτης",
    "app.logout": "Αποσύνδεση",
    "app.search": "Αναζήτηση…",
    "app.more_title": "Περισσότερες λειτουργίες",
    "app.more_btn": "Περισσότερα",
    "app.loading": "Φόρτωση…",
    "app.error_back": "Επιστροφή στη σύνδεση",
    "app.see_more_notifs": "Δείτε {count} περισσότερες ειδοποιήσεις →",
    "page.accounts": "Οι λογαριασμοί μου",
    "page.transactions": "Συναλλαγές",
    "page.transfers": "Νέα μεταφορά",
    "page.deposit": "Κατάθεση",
    "page.cards": "Οι κάρτες μου",
    "page.savings": "Αποταμιεύσεις",
    "page.investments": "Επένδυση",
    "page.credits": "Πίστωση",
    "page.financing": "Χρηματοδότηση έργων",
    "page.messages": "Μηνύματα",
    "page.settings": "Ρυθμίσεις",
    "page.notifications": "Σημαντικές ειδοποιήσεις",
    "page.profile": "Το προφίλ μου",
    "dash.greeting": "Γεια σας, {name}",
    "dash.overview": "Επισκόπηση οικονομικών — {date}",
    "dash.total_balance": "Συνολικό υπόλοιπο",
    "dash.this_month": "+{amount} αυτόν τον μήνα",
    "dash.view_iban": "Προβολή IBAN μου",
    "dash.my_accounts": "Οι λογαριασμοί μου",
    "dash.primary_account": "Κύριος λογαριασμός",
    "dash.savings_account": "Λογαριασμός αποταμίευσης",
    "dash.remunerated": "Έντοκος",
    "dash.details": "Λεπτομέρειες",
    "dash.transfer_btn": "Μεταφορά",
    "dash.quick_actions": "Γρήγορες ενέργειες",
    "dash.quick.deposit": "Κατάθεση",
    "deposit.title": "Πληροφορίες κατάθεσης",
    "dash.quick.transfer": "Μεταφορά",
    "dash.quick.history": "Ιστορικό",
    "dash.quick.cards": "Οι κάρτες μου",
    "dash.quick.profile": "Προφίλ",
    "dash.monthly_budget": "Μηνιαίος προϋπολογισμός",
    "dash.income": "Έσοδα",
    "dash.expenses": "Έξοδα",
    "dash.spending_cat": "Δαπάνες ανά κατηγορία",
    "dash.no_spending": "Δεν καταγράφηκαν δαπάνες.",
    "dash.recent_tx": "Πρόσφατες συναλλαγές",
    "dash.see_all": "Προβολή όλων",
    "dash.no_tx": "Δεν υπάρχουν συναλλαγές.",
    "dash.main_card": "Η κύρια κάρτα μου",
    "dash.manage": "Διαχείριση",
    "dash.block": "Αποκλεισμός",
    "dash.pin": "Κωδικός PIN",
    "dash.security": "Ασφάλεια",
    "dash.more": "Περισσότερα",
    "dash.no_card": "Δεν έχει εκδοθεί κάρτα",
    "dash.monthly_activity": "Μηνιαία δραστηριότητα",
    "dash.operations": "Λειτουργίες",
    "dash.holder": "Κάτοχος",
    "dash.expires": "Λήξη",
    "dash.visa_label": "VISA",
    "dash.balance": "Διαθέσιμο υπόλοιπο",
    "tx.all": "Όλες",
    "tx.income": "Έσοδα",
    "tx.expenses": "Έξοδα",
    "tx.today": "Σήμερα",
    "tx.yesterday": "Χθες",
    "tx.none": "Δεν υπάρχουν συναλλαγές.",
    "transfer.title": "Γρήγορη μεταφορά",
    "transfer.recipient": "Παραλήπτης",
    "transfer.recipient_ph": "Όνομα, IBAN, αριθμός τηλεφώνου…",
    "transfer.amount": "Ποσό",
    "transfer.label": "Περιγραφή",
    "transfer.reason": "Λόγος (προαιρετικό)",
    "transfer.reason_ph": "Π.χ.: ενοίκιο, επιστροφή…",
    "transfer.execution": "Εκτέλεση",
    "transfer.immediate": "Άμεση",
    "transfer.delayed": "Καθυστερημένη",
    "transfer.balance": "Διαθέσιμο υπόλοιπο:",
    "transfer.continue": "Συνέχεια",
    "transfer.confirm": "Επιβεβαίωση",
    "transfer.cancel": "Ακύρωση",
    "transfer.code": "Κωδικός επαλήθευσης",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Εισαγάγετε τον κωδικό που ελήφθη για επαλήθευση της μεταφοράς",
    "transfer.pending.title": "Μεταφορά σε αναμονή επαλήθευσης",
    "transfer.pending.amount": "Ποσό:",
    "transfer.pending.to": "Προς:",
    "transfer.pending.hint": "Ο κωδικός επαλήθευσής σας θα αποσταλεί μέσω email ή μέσω του κουδουνιού ειδοποιήσεων. Ο κωδικός αποτελείται από 6 ψηφία. Εάν δεν τον λάβετε, επικοινωνήστε με την υποστήριξη.",
    "transfer.pending.validate": "Επαλήθευση μεταφοράς",
    "transfer.saved": "Η μεταφορά αποθηκεύτηκε. Ζητήστε τον κωδικό από τον διαχειριστή σας για επαλήθευση.",
    "transfer.done": "Η μεταφορά ολοκληρώθηκε",
    "transfer.validated": "Η μεταφορά επαληθεύτηκε!",
    "transfer.progress.processing": "Επεξεργασία μεταφοράς…",
    "transfer.progress.completed": "Η μεταφορά ολοκληρώθηκε!",
    "transfer.progress.default_message": "Η μεταφορά σας βρίσκεται σε επεξεργασία. Παρακαλώ περιμένετε.",
    "cards.none": "Δεν έχει εκδοθεί κάρτα.\nΕπικοινωνήστε με τον διαχειριστή σας.",
    "cards.holder": "Κάτοχος",
    "cards.expires": "Λήξη",
    "cards.block": "Αποκλεισμός",
    "cards.pin": "Κωδικός PIN",
    "cards.more": "Περισσότερα",
    "cards.limits": "Όρια κάρτας",
    "cards.payment": "Πληρωμή",
    "cards.withdrawal": "Ανάληψη",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Αποκλεισμένη",
    "cards.status_active": "Ενεργή",
    "cards.toast.blocked": "Η κάρτα αποκλείστηκε",
    "cards.toast.unblocked": "Η κάρτα ξεμπλοκαρίστηκε",
    "cards.toast.physical_ordered": "Η φυσική κάρτα παραγγέλθηκε! Παράδοση σε 5–10 εργάσιμες ημέρες.",
    "cards.toast.created": "Η κάρτα δημιουργήθηκε με επιτυχία!",
    "cards.none_sub": "Δεν έχει εκδοθεί κάρτα. Δημιουργήστε την εικονική σας κάρτα τώρα.",
    "cards.generate_btn": "Δημιουργία κάρτας μου",
    "cards.create.title": "Δημιουργία εικονικής κάρτας μου",
    "cards.create.network": "Δίκτυο πληρωμών",
    "cards.create.color": "Χρώμα κάρτας",
    "cards.create.preview_name": "ΤΟ ΟΝΟΜΑ ΣΑΣ",
    "cards.create.preview_expiry": "ΜΜ/ΕΕ",
    "cards.create.loading": "Δημιουργία…",
    "cards.create.submit": "Δημιουργία κάρτας",
    "cards.btn.unblock": "Ξεμπλοκάρισμα",
    "cards.btn.block": "Αποκλεισμός",
    "cards.btn.info": "Πληροφορίες",
    "cards.btn.physical": "Φυσική",
    "cards.btn.physical_done": "Παραγγέλθηκε",
    "cards.limits_title": "Όρια",
    "cards.reveal.title": "Στοιχεία κάρτας",
    "cards.reveal.number": "Πλήρης αριθμός",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Λήξη",
    "cards.reveal.warning": "Ποτέ μην μοιράζεστε αυτές τις πληροφορίες. Κλείστε αυτό το παράθυρο μετά τη χρήση.",
    "cards.pin.title": "Κωδικός PIN",
    "cards.pin.message": "Αυτός ο κωδικός PIN χρησιμοποιείται για όλες τις διαδικτυακές πληρωμές και αναλήψεις σας. Ποτέ μην τον μοιράζεστε με κανέναν.",
    "cards.physical.title": "Παραγγελία φυσικής κάρτας",
    "cards.physical.fee_label": "Τέλος παράδοσης",
    "cards.physical.balance_label": "Το υπόλοιπό σας",
    "cards.physical.insufficient": "Ανεπαρκές υπόλοιπο. Παρακαλώ αναπληρώστε τον λογαριασμό σας πριν παραγγείλετε.",
    "cards.physical.info": "Το τέλος θα χρεωθεί από τον λογαριασμό σας. Παράδοση σε 5–10 εργάσιμες ημέρες.",
    "cards.physical.order_btn": "Παραγγελία ({fee})",
    "cards.physical.ordering": "Γίνεται παραγγελία…",
    "cards.physical.order_cta": "Παραγγείλτε φυσική κάρτα",
    "cards.physical.choose_type": "Επιλέξτε την κάρτα σας",
    "cards.physical.features": "Πλεονεκτήματα",
    "cards.buy_virtual_cta": "Αγορά εικονικής κάρτας",
    "notif.none": "Δεν υπάρχουν ειδοποιήσεις αυτή τη στιγμή.",
    "notif.contact_support": "Επικοινωνήστε με την υποστήριξη",
    "profile.change_photo": "Αλλαγή φωτογραφίας",
    "profile.remove_photo": "Αφαίρεση φωτογραφίας",
    "profile.email": "Email",
    "profile.phone": "Τηλέφωνο",
    "profile.country": "Χώρα",
    "profile.address": "Διεύθυνση",
    "profile.notifs_title": "Ειδοποιήσεις ({count})",
    "profile.no_notifs": "Δεν υπάρχουν ειδοποιήσεις.",
    "profile.logout": "Αποσύνδεση",
    "profile.photo_updated": "Η φωτογραφία προφίλ ενημερώθηκε",
    "profile.photo_removed": "Η φωτογραφία αφαιρέθηκε",
    "profile.photo_too_large": "Η εικόνα είναι πολύ μεγάλη (μέγ. 1 MB)",
    "profile.powered": "Με την υποστήριξη του {bank}",
    "iban.title": "Το IBAN μου",
    "iban.loading": "Φόρτωση…",
    "iban.bic": "Κωδικός BIC / SWIFT",
    "iban.share_info": "Μοιραστείτε αυτά τα στοιχεία για να λάβετε μεταφορά στον λογαριασμό σας.",
    "iban.copy": "Αντιγραφή",
    "iban.copied": "Αντιγράφηκε στο πρόχειρο",
    "iban.copy_fail": "Αδυναμία αντιγραφής",
    "iban.close": "Κλείσιμο",
    "accounts.all": "Όλοι οι λογαριασμοί σας στο {bank}.",
    "accounts.primary": "Κύριος λογαριασμός",
    "accounts.savings": "Λογαριασμός αποταμίευσης",
    "accounts.primary_tag": "Κύριος",
    "accounts.secondary_tag": "Δευτερεύων",
    "accounts.objective": "Στόχος: {amount} · {pct}%",
    "accounts.copy": "Αντιγραφή",
    "accounts.loading": "Φόρτωση…",
    "accounts.iban_copied": "Το IBAN αντιγράφηκε",
    "accounts.copy_fail": "Αδυναμία αντιγραφής",
    "savings.total": "Σύνολο αποταμιεύσεων",
    "savings.target_total": "Συνολικός στόχος",
    "savings.progress": "Πρόοδος",
    "savings.active": "Ενεργοί στόχοι",
    "savings.new_goal": "Νέος στόχος",
    "savings.none": "Δεν υπάρχουν στόχοι αποταμίευσης ακόμη.",
    "savings.none_sub": "Δημιουργήστε τον πρώτο σας στόχο για να ξεκινήσετε.",
    "savings.saved": "Αποταμιεύτηκε:",
    "savings.goal": "Στόχος:",
    "savings.deadline": "Προθεσμία: {date}",
    "savings.deposit": "Κατάθεση",
    "savings.withdraw": "Ανάληψη",
    "savings.loading": "Φόρτωση…",
    "savings.form.title": "Νέος στόχος αποταμίευσης",
    "savings.form.cancel": "Ακύρωση",
    "savings.form.create": "Δημιουργία",
    "savings.form.name": "Όνομα στόχου",
    "savings.form.name_ph": "Ταξίδι στην Ιαπωνία, Αυτοκίνητο, …",
    "savings.form.target": "Ποσό στόχου",
    "savings.form.initial": "Αρχική κατάθεση",
    "savings.form.deadline": "Προθεσμία (προαιρετικό)",
    "savings.form.color": "Χρώμα",
    "savings.deposit_title": "Κατάθεση στο \"{name}\"",
    "savings.deposit_amount": "Ποσό κατάθεσης",
    "savings.balance_hint": "Κύριο υπόλοιπο: {amount}",
    "savings.withdraw_prompt": "Πόσο να αναληφθεί από το \"{name}\";",
    "savings.close_confirm": "Κλείσιμο στόχου \"{name}\"; Οι αποταμιεύσεις θα επιστραφούν στον κύριο λογαριασμό σας.",
    "savings.toast.created": "Ο στόχος δημιουργήθηκε",
    "savings.toast.deposited": "Η κατάθεση έγινε",
    "savings.toast.withdrawn": "Η ανάληψη έγινε",
    "savings.toast.closed": "Ο στόχος έκλεισε",
    "invest.total": "Σύνολο επενδύσεων",
    "invest.profits": "Σωρευτικά κέρδη",
    "invest.count": "Επενδύσεις",
    "invest.products": "Διαθέσιμα προϊόντα",
    "invest.invest_btn": "Επένδυση",
    "invest.my": "Οι επενδύσεις μου",
    "invest.pending": "Σε αναμονή",
    "invest.validated": "Επαληθεύτηκε",
    "invest.refused": "Απορρίφθηκε",
    "invest.withdraw.btn": "Ανάληψη",
    "invest.withdraw.title": "Ανάληψη της επένδυσής μου",
    "invest.withdraw.intro": "Πρόκειται να αναλάβετε την επένδυσή σας «{product}». Το αρχικό κεφάλαιο και τα συσσωρευμένα κέρδη θα πιστωθούν στο κύριο υπόλοιπό σας.",
    "invest.withdraw.principal": "Αρχικό κεφάλαιο",
    "invest.withdraw.profit": "Κέρδη",
    "invest.withdraw.total": "Συνολικό ποσό",
    "invest.withdraw.confirm": "Επιβεβαίωση ανάληψης",
    "invest.withdraw.warning": "Αυτή η ενέργεια είναι μη αναστρέψιμη. Η επένδυση θα κλείσει.",
    "invest.withdraw.success": "Η επένδυση αναλήφθηκε — {amount} πιστώθηκαν στο υπόλοιπο.",
    "invest.withdrawn": "Αναλήφθηκε",
    "invest.cta_sub": "Επιλέξτε τον τύπο επένδυσης που σας ταιριάζει. Ένας σύμβουλος θα σας συνοδεύσει από το πρώτο σας αίτημα.",
    "invest.form.amount": "Ποσό επένδυσης",
    "invest.form.balance": "Διαθέσιμο υπόλοιπο: {amount}",
    "invest.form.note": "Σημείωση για τον σύμβουλό σας (προαιρετικό)",
    "invest.form.note_ph": "Διευκρινίστε τις προσδοκίες σας…",
    "invest.form.advisor_info": "Θα ανοίξει αυτόματα μια συνομιλία με τον σύμβουλό σας.",
    "invest.form.cancel": "Ακύρωση",
    "invest.form.submit": "Αποστολή αιτήματος",
    "invest.toast.requested": "Το αίτημα καταχωρήθηκε — μια συνομιλία ανοίχτηκε με τον σύμβουλό σας.",
    "invest.p1.name": "Ακίνητα",
    "invest.p1.desc": "SCPI, crowdfunding ακινήτων",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Συμμετοχική χρηματοδότηση επιλεγμένων έργων",
    "invest.p3.name": "Ανανεώσιμες Πηγές Ενέργειας",
    "invest.p3.desc": "Ηλιακή, αιολική ενέργεια και πράσινα έργα",
    "invest.p4.name": "Χρηματιστήριο",
    "invest.p4.desc": "Μετοχές, ETF και διαφοροποιημένα αμοιβαία κεφάλαια",
    "invest.p5.name": "Συμμετοχικές",
    "invest.p5.desc": "Δάνεια σε ΜΜΕ και τοπικούς επιχειρηματίες",
    "invest.p6.name": "Κρυπτονόμισμα",
    "invest.p6.desc": "Bitcoin, Ethereum και πορτοφόλι πολλαπλών νομισμάτων",
    "msg.online": "Σε σύνδεση",
    "msg.loading": "Φόρτωση…",
    "msg.none": "Δεν υπάρχουν μηνύματα. Ξεκινήστε τη συνομιλία με τον σύμβουλό σας.",
    "msg.placeholder": "Γράψτε το μήνυμά σας…",
    "msg.attach.image": "Εικόνα",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Ήχος",
    "msg.attach.file": "Αρχείο / Έγγραφο",
    "msg.pdf_doc": "Έγγραφο PDF",
    "msg.file": "Αρχείο",
    "msg.too_large": "Το αρχείο είναι πολύ μεγάλο (μέγ. {size}).",
    "msg.withdraw": "Απόσυρση",
    "credits.cta_sub": "Υποβάλετε το αίτημά σας απευθείας διαδικτυακά — απάντηση εντός 24 ωρών.",
    "credits.learn_more": "Μάθετε περισσότερα",
    "credits.request": "Αίτηση",
    "credits.history": "Τα αιτήματά μου",
    "credits.status.pending": "Σε αναμονή",
    "credits.status.accepted": "Αποδεκτό",
    "credits.status.refused": "Απορρίφθηκε",
    "credits.chars": "Χαρακτηριστικά",
    "credits.amount_range": "Ποσό: {min}€ – {max}€",
    "credits.rate_range": "Επιτόκιο: {min}% – {max}% ετησίως",
    "credits.duration_range": "Διάρκεια: {min} έως {max} μήνες",
    "credits.deposit_min": "Αρχική κατάθεση: {amount}€ ελάχιστο",
    "credits.garantie": "Εγγύηση: {garantie}",
    "credits.eligibility": "Προϋποθέσεις επιλεξιμότητας",
    "credits.elig.income": "Απόδειξη τακτικών και σταθερών εισοδημάτων",
    "credits.elig.deposit": "Καταβολή αρχικής κατάθεσης {amount}€",
    "credits.elig.garantie": "Προσκόμιση εγγύησης: {garantie}",
    "credits.form.title": "Αίτηση — {name}",
    "credits.form.cancel": "Ακύρωση",
    "credits.form.submit": "Αποστολή αιτήματος",
    "credits.form.amount": "Επιθυμητό ποσό (€)",
    "credits.form.duration": "Διάρκεια (μήνες)",
    "credits.form.reason": "Λόγος (προαιρετικό)",
    "credits.form.reason_ph": "Περιγράψτε το έργο σας…",
    "credits.form.close": "Κλείσιμο",
    "credits.toast.submitted": "Η αίτηση πίστωσης υποβλήθηκε — θα λάβετε απάντηση εντός 24 ωρών.",
    "credits.p1.name": "Προσωπικό Δάνειο",
    "credits.p1.desc": "Για τη χρηματοδότηση καθημερινών αναγκών ή προσωπικών έργων.",
    "credits.p2.name": "Στεγαστικό Δάνειο",
    "credits.p2.desc": "Για τη χρηματοδότηση αγοράς ή κατασκευής ακινήτου.",
    "credits.p3.name": "Δάνειο Αυτοκινήτου",
    "credits.p3.desc": "Αγοράστε όχημα με ηρεμία.",
    "credits.p4.name": "Φοιτητικό Δάνειο",
    "credits.p4.desc": "Οικονομική βοήθεια για τριτοβάθμια εκπαίδευση.",
    "credits.p5.name": "Επαγγελματικό Δάνειο",
    "credits.p5.desc": "Για την υποστήριξη της ανάπτυξης επαγγελματικής δραστηριότητας.",
    "credits.p6.name": "Εξπρές Δάνειο",
    "credits.p6.desc": "Γρήγορη λύση για επείγουσες ανάγκες.",
    "financing.reimbursable": "Επιστρεπτέα χρηματοδότηση",
    "financing.reimbursable_desc": "Οικονομική υποστήριξη με σταθερούς όρους για την έναρξη ή ανάπτυξη δραστηριότητας.",
    "financing.non_reimbursable": "Μη επιστρεπτέα χρηματοδότηση",
    "financing.non_reimbursable_desc": "Για έργα κοινωνικής, εκπαιδευτικής ή ανθρωπιστικής επίδρασης (ΜΚΟ, σύλλογοι).",
    "financing.cta_sub": "Προσαρμοσμένες λύσεις για την υλοποίηση των φιλοδοξιών σας.",
    "financing.request": "Αίτηση χρηματοδότησης",
    "financing.history": "Τα αιτήματά μου",
    "financing.reimbursable_label": "Επιστρεπτέα",
    "financing.non_reimbursable_label": "Μη επιστρεπτέα",
    "financing.status.pending": "Σε αναμονή",
    "financing.status.granted": "Εγκρίθηκε",
    "financing.status.refused": "Απορρίφθηκε",
    "financing.form.title": "Αίτηση — {name}",
    "financing.form.cancel": "Ακύρωση",
    "financing.form.submit": "Αποστολή αιτήματος",
    "financing.form.project": "Όνομα έργου",
    "financing.form.project_ph": "Το έργο μου",
    "financing.form.amount": "Αιτούμενο ποσό (€)",
    "financing.form.sector": "Τομέας",
    "financing.form.sector_ph": "Τεχνολογία, γεωργία, …",
    "financing.form.description": "Περιγραφή έργου",
    "financing.form.description_ph": "Παρουσιάστε το έργο σας, τους στόχους και την αναμενόμενη επίδρασή του…",
    "settings.security": "Ασφάλεια",
    "settings.current_pw": "Τρέχων κωδικός πρόσβασης",
    "settings.new_pw": "Νέος κωδικός πρόσβασης",
    "settings.confirm_pw": "Επιβεβαίωση",
    "settings.submit": "Αλλαγή κωδικού πρόσβασης",
    "settings.no_match": "Οι κωδικοί πρόσβασης δεν ταιριάζουν",
    "settings.changed": "Ο κωδικός πρόσβασης άλλαξε",
    "common.cancel": "Ακύρωση",
    "common.confirm": "Επιβεβαίωση",
    "common.close": "Κλείσιμο",
    "common.save": "Αποθήκευση",
    "common.loading": "Φόρτωση…",
    "common.send": "Αποστολή",
    "common.total": "Σύνολο",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Εκτ. μηνιαία δόση",
    "credits.rate_annual": "/ έτος",
    "credits.rate_used": "Εκτ. επιτόκιο:",
    "credits.simulation": "Εκτίμηση μηνιαίας δόσης",
    "credits.total_cost": "Εκτ. συνολικό κόστος",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Καλώς ήρθατε",
    "common.delete": "Διαγραφή",
    "cards.delete.title": "Διαγραφή κάρτας",
    "cards.delete.confirm_msg": "Αυτή η ενέργεια είναι μη αναστρέψιμη. Η κάρτα θα διαγραφεί οριστικά.",
    "cards.delete.physical_tooltip": "Φυσική κάρτα παραγγελθείσα — διαγραφή αδύνατη",
    "cards.order.confirmed": "Παραγγελία επιβεβαιώθηκε!",
    "cards.order.confirmed_body": "Η φυσική σας κάρτα ετοιμάζεται. Χρησιμοποιήστε αυτόν τον αριθμό για να παρακολουθήσετε την παράδοση.",
    "cards.order.tracking_no": "Αρ. παρακολούθησης",
    "cards.fee_deducted": "{amount} θα αφαιρεθεί από το υπόλοιπό σας",
    "cards.insufficient_short": "Ανεπαρκές υπόλοιπο ({available} διαθέσιμο)",
    "transfer.section.beneficiary": "Δικαιούχος",
    "transfer.section.details": "Μεταφορά",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Τράπεζα",
    "transfer.field.city": "Πόλη",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Τράπεζα:",
    "transfer.progress.paused": "Αναμένει επικύρωση",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Μεταβλητό",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Η κάρτα διαγράφηκε",
    "cards.feat.gold_exclusive": "Αποκλειστική χρυσή κάρτα",
    "cards.feat.cashback_05": "Επιστροφή χρημάτων 0,5%",
    "cards.feat.buyer_protection": "Προστασία αγοραστή",
    "cards.feat.support_standard": "Τυπική υποστήριξη",
    "cards.feat.plastic_premium": "Πλαστική κάρτα premium",
    "cards.feat.cashback_1": "Επιστροφή χρημάτων 1%",
    "cards.feat.travel_standard": "Τυπική ταξιδιωτική ασφάλεια",
    "cards.feat.support_priority": "Υποστήριξη προτεραιότητας",
    "cards.feat.metal_brushed": "Κάρτα από βουρτσισμένο μέταλλο",
    "cards.feat.cashback_2_unlimited": "Απεριόριστη επιστροφή χρημάτων 2%",
    "cards.feat.travel_premium": "Premium ταξιδιωτική ασφάλεια",
    "cards.feat.vip_lounge": "Πρόσβαση σε σαλόνια VIP",
    "cards.feat.support_247": "Υποστήριξη 24/7",
    "auth.demo.title": "Σύνδεση διαχειριστή",
    "auth.demo.subtitle": "Εισάγετε τα διαπιστευτήρια διαχειριστή για προεπισκόπηση",
    "credits.garantie.none": "Καμία",
    "credits.garantie.mortgage": "Υποθήκη επί του χρηματοδοτούμενου ακινήτου",
    "credits.garantie.vehicle": "Χρηματοδοτούμενο όχημα",
    "credits.garantie.parental": "Γονική ή τραπεζική",
    "credits.garantie.professional": "Επαγγελματικά περιουσιακά στοιχεία ή προσωπική εγγύηση",
    "cards.reveal.pin_prompt": "Εισαγάγετε τον 4ψήφιο κωδικό PIN σας για να εμφανίσετε τις πληροφορίες της κάρτας.",
    "cards.reveal.show": "Εμφάνιση",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Οι δύο κωδικοί PIN δεν ταιριάζουν",
    "auth.pin.digit_hint": "4 ψηφία",
  
  },
  lv: {
    "notif.transfer_code.title": "Pārskaitījuma apstiprināšanas kods",
    "notif.transfer_code.body": "Kopējiet šo kodu, lai apstiprinātu pārskaitījumu.",
    "notif.transfer_code.expires_in": "Pazūd pēc {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mēneši",
    "credits.n_months": "{n} mēneši",
    "msg.read": "Izlasīts",
    "msg.sent": "Nosūtīts",
    "msg.attach_aria": "Pievienot failu",
    "login.device.unknown": "Nezināms pārlūks",
    "login.device.mobile": "Mobilais",
    "login.device.tablet": "Planšete",
    "login.device.desktop": "Dators",
    "login.recent": "Nesens",
    "err.invalid_credentials": "Nederīgi pieteikšanās dati",
    "err.bad_pin": "Nepareizs PIN",
    "err.account_blocked": "Konts bloķēts",
    "err.bad_current_pw": "Pašreizējā parole nav pareiza",
    "err.insufficient_funds": "Nepietiekami līdzekļi, lūdzu, papildiniet kontu.",
    "err.amount_above_savings": "Summa pārsniedz pieejamos ietaupījumus",
    "err.must_be_positive": "Vērtībai jābūt pozitīvai",
    "err.invalid_format": "Nederīgs formāts",
    "err.email_taken": "E-pasts jau tiek izmantots",
    "err.card_not_found": "Karte nav atrasta",
    "err.physical_card_already_ordered": "Fiziska karte jau pasūtīta",
    "err.beneficiary_not_found": "Saņēmējs nav atrasts",
    "err.invalid_code": "Nederīgs kods",
    "err.no_transfer_code": "Šim pārskaitījumam nav koda",
    "err.link_expired": "Saite nederīga vai beigusies",
    "err.thread_not_found": "Saruna nav atrasta",
    "err.empty_message": "Tukšs ziņojums",
    "err.file_too_large": "Fails ir pārāk liels (maks 20 MB)",
    "err.forbidden": "Piekļuve liegta",
    "err.bank_not_found": "Banka nav atrasta",
    "err.signups_closed": "Reģistrācija šai bankai ir slēgta.",
    "err.access_suspended": "Piekļuve ir īslaicīgi apturēta. Lūdzu, sazinieties ar savu banku.",
    "err.notif_not_closable": "Šo paziņojumu nevar aizvērt.",
    "auth.signup.tagline": "Pievienojieties mums šodien",
    "auth.login.tagline": "Laipni lūgti atpakaļ!",
    "auth.signup.tagline_sub": "Atveriet kontu 2 minūtēs un piekļūstiet visiem mūsu premium bankas pakalpojumiem.",
    "auth.login.tagline_sub": "Piekļūstiet savam personīgajam apgabalam un pārvaldiet savus finansus droši.",
    "auth.back": "Atpakaļ uz sākumlapu",
    "auth.bullet.security": "Militārā līmeņa bankas drošība",
    "auth.bullet.transfers": "Tūlītēji pārskaitījumi 24/7",
    "auth.bullet.investments": "Integrēti ieguldījumi un uzkrājumi",
    "auth.bullet.advisor": "Personīgais padomnieks pieejams 7 dienas/7",
    "auth.footer": "Darbina {bank}",
    "auth.signup.title": "Izveidot kontu",
    "auth.login.title": "Pieteikties",
    "auth.field.lastName": "Uzvārds",
    "auth.field.firstName": "Vārds",
    "auth.field.country": "Valsts",
    "auth.field.country.placeholder": "— Izvēlieties savu valsti —",
    "auth.field.phone": "Tālrunis",
    "auth.field.address": "Adrese",
    "auth.field.address.placeholder": "Brīvības iela 123, Rīga",
    "auth.field.email": "E-pasts",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Parole",
    "auth.submit.signup": "Izveidot manu kontu",
    "auth.submit.login": "Pieteikties",
    "auth.login.badge": "Piesakieties savā kontā",
    "auth.card.tagline": "Jūsu klientu zona",
    "auth.submit.loading": "Ielādē…",
    "auth.pin.title": "PIN kods",
    "auth.pin.subtitle": "Ievadiet savu 4 ciparu PIN kodu",
    "auth.pin.label": "PIN kods",
    "auth.pin.confirm": "Apstiprināt PIN kodu",
    "auth.pin.submit": "Apstiprināt",
    "auth.pin.back": "Atpakaļ uz pieteikšanos",
    "auth.pin.length_error": "PIN kodam jābūt 4 cipariem",
    "auth.switch.have_account": "Jau ir konts?",
    "auth.switch.login_link": "Pieteikties",
    "auth.switch.no_account": "Vēl neesat klients?",
    "auth.error.select_country": "Izvēlieties savu valsti",
    "kyc.title": "Identitātes verifikācija (KYC)",
    "kyc.sub": "Augšupielādējiet savus dokumentus, lai apstiprinātu kontu. Visi dokumenti ir šifrēti un konfidenciāli.",
    "kyc.id_type": "Identitātes dokumenta veids *",
    "kyc.id_card": "Personas apliecība",
    "kyc.passport": "Pase",
    "kyc.driver_license": "Vadītāja apliecība",
    "kyc.recto_passport": "Pases priekšpuse *",
    "kyc.recto_id": "Dokumenta priekšpuse *",
    "kyc.verso": "Dokumenta aizmugure *",
    "kyc.selfie": "Selfija ar dokumentu rokā *",
    "kyc.address_proof": "Dzīvesvietas apliecinājums *",
    "kyc.hint_doc": "Attēls vai PDF, maks. 5 MB",
    "kyc.hint_selfie": "Skaidra fotogrāfija, seja un dokuments redzami",
    "kyc.hint_address": "Rēķins, izraksts, apliecinājums… (maks. 5 MB)",
    "kyc.upload": "Augšupielādēt",
    "kyc.replace": "Nomainīt",
    "pending.title": "Konts gaida apstiprinājumu",
    "pending.body": "Jūsu pieteikums ir saņemts. Administrators pārbaudīs jūsu lietu un drīzumā aktivizēs jūsu kontu.",
    "pending.back": "Atpakaļ uz pieteikšanos",
    "home.nav.features": "Funkcijas",
    "home.nav.security": "Drošība",
    "home.nav.invest": "Ieguldījumi",
    "home.nav.credits": "Kredīti",
    "home.nav.faq": "BUJ",
    "home.nav.login": "Pieteikties",
    "home.hero.tagline": "Jaunākās paaudzes tiešsaistes banka",
    "home.hero.title1": "Pārvaldiet savu naudu",
    "home.hero.title2": "gudri",
    "home.hero.sub": "Pilnvērtīga bankas platforma — pārskaitījumi, uzkrājumi, ieguldījumi un kredīti no viedtālruņa.",
    "home.hero.login": "Pieteikties",
    "home.hero.trust.secure": "100% Drošs",
    "home.hero.trust.fast": "Ātra atvēršana",
    "home.hero.trust.encrypted": "Šifrēti dati",
    "home.hero.trust.support": "Serviss 24/7",
    "home.mockup.balance": "Pieejamais atlikums",
    "home.mockup.currency": "EUR · Galvenais konts",
    "home.mockup.recharge": "Papildināt",
    "home.mockup.send": "Nosūtīt",
    "home.mockup.save": "Uzkrāt",
    "home.mockup.salary": "Marta alga",
    "home.mockup.rent": "Aprīļa īre",
    "home.mockup.investment": "Ieguldījums",
    "home.mockup.days2": "pirms 2 dienām",
    "home.mockup.days3": "pirms 3 dienām",
    "home.mockup.days5": "pirms 5 dienām",
    "home.stats.accounts": "Aktīvie konti",
    "home.stats.uptime": "Pieejamība",
    "home.stats.transfers": "Pārskaitījumi",
    "home.stats.support": "Atbalsts",
    "home.features.label": "Funkcijas",
    "home.features.title": "Viss, kas jums nepieciešams",
    "home.features.sub": "Pilnvērtīga platforma, kas izstrādāta, lai vienkāršotu jūsu ikdienas finanšu pārvaldību.",
    "home.feature.security": "Militārā drošība",
    "home.feature.security_body": "AES-256 šifrēšana, divfaktoru autentifikācija un konta uzraudzība 24/7.",
    "home.feature.cards": "Virtuālās un fiziskās kartes",
    "home.feature.cards_body": "Izsniedziet kartes sekundēs vai pasūtiet īstu fizisko karti ar piegādi uz mājām.",
    "home.feature.transfers": "Ātrākie pārskaitījumi",
    "home.feature.transfers_body": "Nosūtiet un saņemiet naudu jebkur pasaulē sekundēs.",
    "home.feature.analytics": "Reālaika analīze",
    "home.feature.analytics_body": "Viedas informācijas paneļi izdevumu izsekošanai un finanšu plānošanai.",
    "home.feature.investments": "Integrētie ieguldījumi",
    "home.feature.investments_body": "Piekļūstiet izvēlētajiem ieguldījumu produktiem tieši no sava konta.",
    "home.feature.support": "Specializēts atbalsts 24/7",
    "home.feature.support_body": "Personīgais bankas padomnieks, pieejams jebkurā laikā, izmantojot drošo ziņojumapmaiņu.",
    "home.tpl.label": "Uzticība",
    "home.tpl.title": "Viņi mums uzticas",
    "home.mockup.balance_label": "Atlikums",
    "home.mockup.virement": "Pārskaitījums",
    "home.mockup.salaire": "Alga",
    "home.security.label": "Drošība",
    "home.security.title1": "Jūsu finanses",
    "home.security.title2": "absolūtā drošībā",
    "home.security.sub": "Mēs piemērojam stingrākos bankas drošības standartus, lai aizsargātu katru centu.",
    "home.security.aes.title": "AES-256 šifrēšana",
    "home.security.aes.body": "Visi sensitīvie dati tiek šifrēti ar tiem pašiem algoritmiem, ko izmanto lielās starptautiskās bankas.",
    "home.security.auth.title": "Pastiprināta autentifikācija",
    "home.security.auth.body": "Divkārša identitātes verifikācija katrā pieteikšanās reizē un katrai sensitīvai darbībai jūsu kontā.",
    "home.security.compliance.title": "PSD2/GDPR atbilstība",
    "home.security.compliance.body": "Mēs ievērojam visus Eiropas noteikumus par maksājumu pakalpojumiem un datu aizsardzību.",
    "home.invest.label": "Ieguldījumi",
    "home.invest.title": "Palieliniet savu naudu",
    "home.invest.sub": "6 ieguldījumu kategorijas, izvēlētas to potenciāla un uzticamības dēļ.",
    "home.invest.yield": "Ienesīgums",
    "home.credits.label": "Kredīti",
    "home.credits.title": "Risinājumi katram projektam",
    "home.credits.sub": "Konkurētspējīgas likmes, ātras atbildes un personalizēts atbalsts visiem jūsu projektiem.",
    "home.credits.rate": "Likme",
    "home.faq.label": "BUJ",
    "home.faq.title": "Biežāk uzdotie jautājumi",
    "home.faq.q1": "Kā atvērt kontu?",
    "home.faq.a1": "Reģistrācija aizņem mazāk nekā 2 minūtes. Vienkārši aizpildiet personīgo informāciju, izvēlieties paroli, un jūsu konts tiks aktivizēts pēc mūsu komandas apstiprinājuma.",
    "home.faq.q2": "Vai mani dati ir drošībā?",
    "home.faq.a2": "Jā. Visi dati tiek šifrēti ar AES-256 miera stāvoklī un TLS 1.3 pārsūtīšanas laikā. Mēs ievērojam starptautiskos bankas standartus un GDPR/PSD2 noteikumus.",
    "home.faq.q3": "Kādus ieguldījumu veidus piedāvājat?",
    "home.faq.a3": "Mēs piedāvājam 6 kategorijas: Nekustamais īpašums, Crowdfunding, Atjaunojamā enerģija, Akciju tirgus, Līdzdalības un Kriptovalūtas. Katrs produkts tiek izvēlēts pēc riska/ienesīguma attiecības.",
    "home.faq.q4": "Kā pieteikties kredītam?",
    "home.faq.a4": "Dodieties uz Kredītu sadaļu klienta apgabalā, izvēlieties aizdevuma veidu, ievadiet summu un termiņu. Jūsu pieteikums tiks apstrādāts 24 stundu laikā no mūsu padomniekiem.",
    "home.faq.q5": "Vai ir slēptas maksas?",
    "home.faq.a5": "Nav slēptu maksu. Visas cenas ir skaidri parādītas katrai darbībai. Noteikumi ir pieejami jūsu klienta apgabalā jebkurā laikā.",
    "home.faq.q6": "Kā sazināties ar atbalstu?",
    "home.faq.a6": "Izmantojiet integrēto ziņojumapmaiņu klienta apgabalā, lai tieši sazinātos ar savu personīgo bankas padomnieku, kurš ir pieejams 24/7.",
    "home.cta.title": "Gatavs sākt?",
    "home.cta.sub": "Pievienojieties tūkstošiem lietotāju, kuri pārvalda savus finansus ar {bank}. Reģistrācija aizņem mazāk nekā 2 minūtes.",
    "home.footer.products": "Produkti",
    "home.footer.services": "Pakalpojumi",
    "home.footer.access": "Ātrā piekļuve",
    "home.footer.accounts": "Konti",
    "home.footer.transfers": "Pārskaitījumi",
    "home.footer.cards": "Kartes",
    "home.footer.savings": "Uzkrājumi",
    "home.footer.investments": "Ieguldījumi",
    "home.footer.credits": "Kredīti",
    "home.footer.financing": "Finansēšana",
    "home.footer.messages": "Ziņojumi",
    "home.footer.login": "Pieteikties",
    "home.footer.signup": "Atvērt kontu",
    "home.footer.support": "Atbalsts 24/7",
    "home.footer.tagline": "Jūsu tiešsaistes banka, pieejama visur un jebkurā laikā.",
    "home.footer.copyright": "© {year} {bank} — Visas tiesības aizsargātas",
    "home.footer.powered": "Darbina {bank}",
    "app.menu_label": "Galvenā izvēlne",
    "app.nav.dashboard": "Informācijas panelis",
    "app.nav.accounts": "Konti",
    "app.nav.transactions": "Darījumi",
    "app.nav.transfers": "Pārskaitījumi",
    "app.nav.cards": "Kartes",
    "app.nav.savings": "Uzkrājumi",
    "app.nav.investments": "Ieguldījumi",
    "app.nav.credit": "Kredīts",
    "app.nav.financing": "Finansēšana",
    "app.nav.messages": "Ziņojumi",
    "app.nav.card_tracking": "Kartes izsekošana",
    "app.nav.notifications": "Paziņojumi",
    "app.nav.settings": "Iestatījumi",
    "app.nav.profile": "Profils",
    "app.nav.mobile.home": "Sākums",
    "app.nav.mobile.transfers": "Pārskaitījumi",
    "app.nav.mobile.cards": "Kartes",
    "app.nav.mobile.transactions": "Darījumi",
    "app.nav.more.invest": "Ieguldīt",
    "app.user_role": "Klients",
    "app.logout": "Izrakstīties",
    "app.search": "Meklēt…",
    "app.more_title": "Vairāk funkciju",
    "app.more_btn": "Vairāk",
    "app.loading": "Ielādē…",
    "app.error_back": "Atpakaļ uz pieteikšanos",
    "app.see_more_notifs": "Skatīt vēl {count} paziņojumus →",
    "page.accounts": "Mani konti",
    "page.transactions": "Darījumi",
    "page.transfers": "Jauns pārskaitījums",
    "page.deposit": "Iemaksa",
    "page.cards": "Manas kartes",
    "page.savings": "Uzkrājumi",
    "page.investments": "Ieguldījumi",
    "page.credits": "Kredīts",
    "page.financing": "Projektu finansēšana",
    "page.messages": "Ziņojumi",
    "page.settings": "Iestatījumi",
    "page.notifications": "Svarīgi paziņojumi",
    "page.profile": "Mans profils",
    "dash.greeting": "Sveiki, {name}",
    "dash.overview": "Finanšu pārskats — {date}",
    "dash.total_balance": "Kopējais atlikums",
    "dash.this_month": "+{amount} šomēnes",
    "dash.view_iban": "Skatīt manu IBAN",
    "dash.my_accounts": "Mani konti",
    "dash.primary_account": "Galvenais konts",
    "dash.savings_account": "Uzkrājumu konts",
    "dash.remunerated": "Ar procentiem",
    "dash.details": "Detaļas",
    "dash.transfer_btn": "Pārskaitījums",
    "dash.quick_actions": "Ātrās darbības",
    "dash.quick.deposit": "Iemaksa",
    "deposit.title": "Informācija par iemaksu",
    "dash.quick.transfer": "Pārskaitījums",
    "dash.quick.history": "Vēsture",
    "dash.quick.cards": "Manas kartes",
    "dash.quick.profile": "Profils",
    "dash.monthly_budget": "Mēneša budžets",
    "dash.income": "Ieņēmumi",
    "dash.expenses": "Izdevumi",
    "dash.spending_cat": "Izdevumi pēc kategorijām",
    "dash.no_spending": "Nav reģistrētu izdevumu.",
    "dash.recent_tx": "Pēdējie darījumi",
    "dash.see_all": "Skatīt visus",
    "dash.no_tx": "Nav darījumu.",
    "dash.main_card": "Mana galvenā karte",
    "dash.manage": "Pārvaldīt",
    "dash.block": "Bloķēt",
    "dash.pin": "PIN kods",
    "dash.security": "Drošība",
    "dash.more": "Vairāk",
    "dash.no_card": "Karte nav izsniegta",
    "dash.monthly_activity": "Mēneša aktivitāte",
    "dash.operations": "Darbības",
    "dash.holder": "Turētājs",
    "dash.expires": "Der.",
    "dash.visa_label": "VISA",
    "dash.balance": "Pieejamais atlikums",
    "tx.all": "Visi",
    "tx.income": "Ieņēmumi",
    "tx.expenses": "Izdevumi",
    "tx.today": "Šodien",
    "tx.yesterday": "Vakar",
    "tx.none": "Nav darījumu.",
    "transfer.title": "Ātrais pārskaitījums",
    "transfer.recipient": "Saņēmējs",
    "transfer.recipient_ph": "Vārds, IBAN, tālruņa numurs…",
    "transfer.amount": "Summa",
    "transfer.label": "Apraksts",
    "transfer.reason": "Iemesls (pēc izvēles)",
    "transfer.reason_ph": "Piem.: īre, atmaksa…",
    "transfer.execution": "Izpilde",
    "transfer.immediate": "Tūlītējs",
    "transfer.delayed": "Atlikts",
    "transfer.balance": "Pieejamais atlikums:",
    "transfer.continue": "Turpināt",
    "transfer.confirm": "Apstiprināt",
    "transfer.cancel": "Atcelt",
    "transfer.code": "Verifikācijas kods",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Ievadiet saņemto kodu, lai apstiprinātu pārskaitījumu",
    "transfer.pending.title": "Pārskaitījums gaida apstiprinājumu",
    "transfer.pending.amount": "Summa:",
    "transfer.pending.to": "Uz:",
    "transfer.pending.hint": "Jūsu verifikācijas kods tiks nosūtīts pa e-pastu vai izmantojot paziņojumu zvaniņu. Kods sastāv no 6 cipariem. Ja to nesaņemat, lūdzu, sazinieties ar atbalsta dienestu.",
    "transfer.pending.validate": "Apstiprināt pārskaitījumu",
    "transfer.saved": "Pārskaitījums saglabāts. Lūdziet administratoram kodu apstiprināšanai.",
    "transfer.done": "Pārskaitījums pabeigts",
    "transfer.validated": "Pārskaitījums apstiprināts!",
    "transfer.progress.processing": "Pārskaitījuma apstrāde…",
    "transfer.progress.completed": "Pārskaitījums veikts!",
    "transfer.progress.default_message": "Jūsu pārskaitījums tiek apstrādāts. Lūdzu, uzgaidiet.",
    "cards.none": "Karte nav izsniegta.\nSazinieties ar administratoru.",
    "cards.holder": "Turētājs",
    "cards.expires": "Der.",
    "cards.block": "Bloķēt",
    "cards.pin": "PIN kods",
    "cards.more": "Vairāk",
    "cards.limits": "Kartes ierobežojumi",
    "cards.payment": "Maksājums",
    "cards.withdrawal": "Izņemšana",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Bloķēta",
    "cards.status_active": "Aktīva",
    "cards.toast.blocked": "Karte bloķēta",
    "cards.toast.unblocked": "Karte atbloķēta",
    "cards.toast.physical_ordered": "Fiziskā karte pasūtīta! Piegāde 5–10 darba dienu laikā.",
    "cards.toast.created": "Karte veiksmīgi izveidota!",
    "cards.none_sub": "Nav izsniegtas kartes. Izveidojiet savu virtuālo karti tagad.",
    "cards.generate_btn": "Izveidot manu karti",
    "cards.create.title": "Izveidot manu virtuālo karti",
    "cards.create.network": "Maksājumu tīkls",
    "cards.create.color": "Kartes krāsa",
    "cards.create.preview_name": "JŪSU VĀRDS",
    "cards.create.preview_expiry": "MM/GG",
    "cards.create.loading": "Izveide…",
    "cards.create.submit": "Izveidot karti",
    "cards.btn.unblock": "Atbloķēt",
    "cards.btn.block": "Bloķēt",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fiziskā",
    "cards.btn.physical_done": "Pasūtīta",
    "cards.limits_title": "Limiti",
    "cards.reveal.title": "Kartes informācija",
    "cards.reveal.number": "Pilns numurs",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Derīguma termiņš",
    "cards.reveal.warning": "Nekad nekopīgojiet šo informāciju. Pēc lietošanas aizveriet šo logu.",
    "cards.pin.title": "PIN kods",
    "cards.pin.message": "Šis PIN kods tiek izmantots visiem jūsu tiešsaistes maksājumiem un izņemšanām. Nekad nevienam to neatklājiet.",
    "cards.physical.title": "Pasūtīt fizisko karti",
    "cards.physical.fee_label": "Piegādes maksa",
    "cards.physical.balance_label": "Jūsu atlikums",
    "cards.physical.insufficient": "Nepietiekams atlikums. Lūdzu, papildiniet kontu pirms pasūtīšanas.",
    "cards.physical.info": "Maksa tiks norakstīta no jūsu konta. Piegāde 5–10 darba dienu laikā.",
    "cards.physical.order_btn": "Pasūtīt ({fee})",
    "cards.physical.ordering": "Pasūta…",
    "cards.physical.order_cta": "Pasūtīt fizisko karti",
    "cards.physical.choose_type": "Izvēlieties savu karti",
    "cards.physical.features": "Priekšrocības",
    "cards.buy_virtual_cta": "Pirkt virtuālo karti",
    "notif.none": "Pašlaik nav paziņojumu.",
    "notif.contact_support": "Sazināties ar atbalstu",
    "profile.change_photo": "Mainīt fotogrāfiju",
    "profile.remove_photo": "Noņemt fotogrāfiju",
    "profile.email": "E-pasts",
    "profile.phone": "Tālrunis",
    "profile.country": "Valsts",
    "profile.address": "Adrese",
    "profile.notifs_title": "Paziņojumi ({count})",
    "profile.no_notifs": "Nav paziņojumu.",
    "profile.logout": "Izrakstīties",
    "profile.photo_updated": "Profila fotogrāfija atjaunināta",
    "profile.photo_removed": "Fotogrāfija noņemta",
    "profile.photo_too_large": "Attēls pārāk liels (maks. 1 MB)",
    "profile.powered": "Darbina {bank}",
    "iban.title": "Mans IBAN",
    "iban.loading": "Ielādē…",
    "iban.bic": "BIC / SWIFT kods",
    "iban.share_info": "Kopīgojiet šos datus, lai saņemtu pārskaitījumu savā kontā.",
    "iban.copy": "Kopēt",
    "iban.copied": "Nokopēts starpliktuvē",
    "iban.copy_fail": "Nevar nokopēt",
    "iban.close": "Aizvērt",
    "accounts.all": "Visi jūsu konti {bank}.",
    "accounts.primary": "Galvenais konts",
    "accounts.savings": "Uzkrājumu konts",
    "accounts.primary_tag": "Primārais",
    "accounts.secondary_tag": "Sekundārais",
    "accounts.objective": "Mērķis: {amount} · {pct}%",
    "accounts.copy": "Kopēt",
    "accounts.loading": "Ielādē…",
    "accounts.iban_copied": "IBAN nokopēts",
    "accounts.copy_fail": "Nevar nokopēt",
    "savings.total": "Kopējie uzkrājumi",
    "savings.target_total": "Kopējais mērķis",
    "savings.progress": "Progress",
    "savings.active": "Aktīvie mērķi",
    "savings.new_goal": "Jauns mērķis",
    "savings.none": "Pagaidām nav uzkrājumu mērķu.",
    "savings.none_sub": "Izveidojiet savu pirmo mērķi, lai sāktu.",
    "savings.saved": "Uzkrāts:",
    "savings.goal": "Mērķis:",
    "savings.deadline": "Termiņš: {date}",
    "savings.deposit": "Iemaksāt",
    "savings.withdraw": "Izņemt",
    "savings.loading": "Ielādē…",
    "savings.form.title": "Jauns uzkrājumu mērķis",
    "savings.form.cancel": "Atcelt",
    "savings.form.create": "Izveidot",
    "savings.form.name": "Mērķa nosaukums",
    "savings.form.name_ph": "Japānas ceļojums, Automašīna, …",
    "savings.form.target": "Mērķa summa",
    "savings.form.initial": "Sākotnējā iemaksa",
    "savings.form.deadline": "Termiņš (pēc izvēles)",
    "savings.form.color": "Krāsa",
    "savings.deposit_title": "Iemaksāt \"{name}\"",
    "savings.deposit_amount": "Iemaksājamā summa",
    "savings.balance_hint": "Galvenais atlikums: {amount}",
    "savings.withdraw_prompt": "Cik izņemt no \"{name}\"?",
    "savings.close_confirm": "Aizvērt mērķi \"{name}\"? Uzkrājumi tiks atgriezti jūsu galvenajā kontā.",
    "savings.toast.created": "Mērķis izveidots",
    "savings.toast.deposited": "Iemaksa veikta",
    "savings.toast.withdrawn": "Izņemšana veikta",
    "savings.toast.closed": "Mērķis aizvērts",
    "invest.total": "Kopējie ieguldījumi",
    "invest.profits": "Kumulatīvā peļņa",
    "invest.count": "Ieguldījumi",
    "invest.products": "Pieejamie produkti",
    "invest.invest_btn": "Ieguldīt",
    "invest.my": "Mani ieguldījumi",
    "invest.pending": "Gaida",
    "invest.validated": "Apstiprināts",
    "invest.refused": "Noraidīts",
    "invest.withdraw.btn": "Izņemt",
    "invest.withdraw.title": "Izņemt manu ieguldījumu",
    "invest.withdraw.intro": "Jūs gatavojaties izņemt savu ieguldījumu „{product}\". Sākotnējais kapitāls un uzkrātā peļņa tiks ieskaitīti jūsu galvenajā atlikumā.",
    "invest.withdraw.principal": "Sākotnējais kapitāls",
    "invest.withdraw.profit": "Peļņa",
    "invest.withdraw.total": "Kopā saņemt",
    "invest.withdraw.confirm": "Apstiprināt izņemšanu",
    "invest.withdraw.warning": "Šī darbība ir neatgriezeniska.",
    "invest.withdraw.success": "Ieguldījums izņemts — {amount} ieskaitīti atlikumā.",
    "invest.withdrawn": "Izņemts",
    "invest.cta_sub": "Izvēlieties jums piemēroto ieguldījumu veidu. Padomnieks pavadīs jūs no pirmā pieteikuma.",
    "invest.form.amount": "Ieguldāmā summa",
    "invest.form.balance": "Pieejamais atlikums: {amount}",
    "invest.form.note": "Piezīme padomniekam (pēc izvēles)",
    "invest.form.note_ph": "Precizējiet savas vēlmes…",
    "invest.form.advisor_info": "Ar jūsu padomnieku tiks automātiski atvērta saruna.",
    "invest.form.cancel": "Atcelt",
    "invest.form.submit": "Nosūtīt pieteikumu",
    "invest.toast.requested": "Pieteikums reģistrēts — ar jūsu padomnieku tika atvērta saruna.",
    "invest.p1.name": "Nekustamais īpašums",
    "invest.p1.desc": "SCPI, nekustamā īpašuma crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Izvēlētu projektu līdzdalības finansēšana",
    "invest.p3.name": "Atjaunojamā enerģija",
    "invest.p3.desc": "Saules, vēja un zaļie projekti",
    "invest.p4.name": "Akciju tirgus",
    "invest.p4.desc": "Akcijas, ETF un diversificēti fondi",
    "invest.p5.name": "Līdzdalības",
    "invest.p5.desc": "Aizdevumi MVU un vietējiem uzņēmējiem",
    "invest.p6.name": "Kriptovalūtas",
    "invest.p6.desc": "Bitcoin, Ethereum un daudzvalūtu maks",
    "msg.online": "Tiešsaistē",
    "msg.loading": "Ielādē…",
    "msg.none": "Nav ziņojumu. Sāciet sarunu ar savu padomnieku.",
    "msg.placeholder": "Rakstiet savu ziņojumu…",
    "msg.attach.image": "Attēls",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Fails / Dokuments",
    "msg.pdf_doc": "PDF dokuments",
    "msg.file": "Fails",
    "msg.too_large": "Fails pārāk liels (maks. {size}).",
    "msg.withdraw": "Atsaukt",
    "credits.cta_sub": "Iesniedziet pieteikumu tiešsaistē — atbilde 24 stundu laikā.",
    "credits.learn_more": "Uzzināt vairāk",
    "credits.request": "Pieteikties",
    "credits.history": "Mani pieteikumi",
    "credits.status.pending": "Gaida",
    "credits.status.accepted": "Pieņemts",
    "credits.status.refused": "Noraidīts",
    "credits.chars": "Raksturojums",
    "credits.amount_range": "Summa: {min}€ – {max}€",
    "credits.rate_range": "Procentu likme: {min}% – {max}% gadā",
    "credits.duration_range": "Ilgums: {min} līdz {max} mēnešiem",
    "credits.deposit_min": "Sākotnējā iemaksa: {amount}€ minimums",
    "credits.garantie": "Garantija: {garantie}",
    "credits.eligibility": "Atbilstības nosacījumi",
    "credits.elig.income": "Apliecināt regulārus un stabilos ienākumus",
    "credits.elig.deposit": "Veikt sākotnējo iemaksu {amount}€",
    "credits.elig.garantie": "Iesniegt garantiju: {garantie}",
    "credits.form.title": "Pieteikums — {name}",
    "credits.form.cancel": "Atcelt",
    "credits.form.submit": "Nosūtīt pieteikumu",
    "credits.form.amount": "Vēlamā summa (€)",
    "credits.form.duration": "Ilgums (mēneši)",
    "credits.form.reason": "Iemesls (pēc izvēles)",
    "credits.form.reason_ph": "Aprakstiet savu projektu…",
    "credits.form.close": "Aizvērt",
    "credits.toast.submitted": "Kredīta pieteikums iesniegts — atbilde tiks saņemta 24 stundu laikā.",
    "credits.p1.name": "Personīgais aizdevums",
    "credits.p1.desc": "Ikdienas vajadzību vai personīgo projektu finansēšanai.",
    "credits.p2.name": "Hipotekārais aizdevums",
    "credits.p2.desc": "Nekustamā īpašuma iegādes vai celtniecības finansēšanai.",
    "credits.p3.name": "Auto aizdevums",
    "credits.p3.desc": "Iegādājieties transportlīdzekli bez rūpēm.",
    "credits.p4.name": "Studentu aizdevums",
    "credits.p4.desc": "Finansiāls atbalsts augstākajai izglītībai.",
    "credits.p5.name": "Uzņēmējdarbības aizdevums",
    "credits.p5.desc": "Profesionālās darbības izaugsmes atbalstam.",
    "credits.p6.name": "Ātrais aizdevums",
    "credits.p6.desc": "Ātrais risinājums steidzamām vajadzībām.",
    "financing.reimbursable": "Atmaksājamais finansējums",
    "financing.reimbursable_desc": "Finansiāls atbalsts ar fiksētiem nosacījumiem darbības uzsākšanai vai attīstīšanai.",
    "financing.non_reimbursable": "Neatmaksājamais finansējums",
    "financing.non_reimbursable_desc": "Projektiem ar sociālu, izglītojošu vai humānu ietekmi (NVO, biedrības).",
    "financing.cta_sub": "Pielāgoti risinājumi jūsu ambīciju īstenošanai.",
    "financing.request": "Pieteikties finansējumam",
    "financing.history": "Mani pieteikumi",
    "financing.reimbursable_label": "Atmaksājams",
    "financing.non_reimbursable_label": "Neatmaksājams",
    "financing.status.pending": "Gaida",
    "financing.status.granted": "Piešķirts",
    "financing.status.refused": "Noraidīts",
    "financing.form.title": "Pieteikums — {name}",
    "financing.form.cancel": "Atcelt",
    "financing.form.submit": "Nosūtīt pieteikumu",
    "financing.form.project": "Projekta nosaukums",
    "financing.form.project_ph": "Mans projekts",
    "financing.form.amount": "Pieprasītā summa (€)",
    "financing.form.sector": "Nozare",
    "financing.form.sector_ph": "Tehnoloģijas, lauksaimniecība, …",
    "financing.form.description": "Projekta apraksts",
    "financing.form.description_ph": "Iepazīstiniet ar savu projektu, tā mērķiem un paredzamo ietekmi…",
    "settings.security": "Drošība",
    "settings.current_pw": "Pašreizējā parole",
    "settings.new_pw": "Jaunā parole",
    "settings.confirm_pw": "Apstiprināt",
    "settings.submit": "Mainīt paroli",
    "settings.no_match": "Paroles nesakrīt",
    "settings.changed": "Parole mainīta",
    "common.cancel": "Atcelt",
    "common.confirm": "Apstiprināt",
    "common.close": "Aizvērt",
    "common.save": "Saglabāt",
    "common.loading": "Ielādē…",
    "common.send": "Nosūtīt",
    "common.total": "Kopā",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Aptuv. ikmēneša maksājums",
    "credits.rate_annual": "/ gads",
    "credits.rate_used": "Aptuv. likme:",
    "credits.simulation": "Ikmēneša maksājuma simulācija",
    "credits.total_cost": "Aptuv. kopējās izmaksas",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Laipni lūdzam",
    "common.delete": "Dzēst",
    "cards.delete.title": "Dzēst karti",
    "cards.delete.confirm_msg": "Šī darbība ir neatgriezeniska. Karte tiks dzēsta neatgriezeniski.",
    "cards.delete.physical_tooltip": "Fiziskā karte pasūtīta — dzēšana neiespējama",
    "cards.order.confirmed": "Pasūtījums apstiprināts!",
    "cards.order.confirmed_body": "Jūsu fiziskā karte tiek sagatavota. Izmantojiet šo numuru, lai izsekotu piegādi.",
    "cards.order.tracking_no": "Izsekošanas nr.",
    "cards.fee_deducted": "{amount} tiks atskaitīts no jūsu atlikuma",
    "cards.insufficient_short": "Nepietiekams atlikums ({available} pieejams)",
    "transfer.section.beneficiary": "Saņēmējs",
    "transfer.section.details": "Pārskaitījums",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banka",
    "transfer.field.city": "Pilsēta",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banka:",
    "transfer.progress.paused": "Gaida validāciju",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Mainīgs",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Karte izdzēsta",
    "cards.feat.gold_exclusive": "Ekskluzīva zelta karte",
    "cards.feat.cashback_05": "Naudas atmaksa 0,5%",
    "cards.feat.buyer_protection": "Pircēja aizsardzība",
    "cards.feat.support_standard": "Standarta atbalsts",
    "cards.feat.plastic_premium": "Premium plastmasas karte",
    "cards.feat.cashback_1": "Naudas atmaksa 1%",
    "cards.feat.travel_standard": "Standarta ceļojumu apdrošināšana",
    "cards.feat.support_priority": "Prioritārs atbalsts",
    "cards.feat.metal_brushed": "Slīpēta metāla karte",
    "cards.feat.cashback_2_unlimited": "Neierobežota 2% naudas atmaksa",
    "cards.feat.travel_premium": "Premium ceļojumu apdrošināšana",
    "cards.feat.vip_lounge": "Piekļuve VIP zonām",
    "cards.feat.support_247": "24/7 atbalsts",
    "auth.demo.title": "Administratora pieteikšanās",
    "auth.demo.subtitle": "Ievadiet administratora datus, lai priekšskatītu demo",
    "credits.garantie.none": "Nav",
    "credits.garantie.mortgage": "Hipotēka uz finansēto īpašumu",
    "credits.garantie.vehicle": "Finansētais transportlīdzeklis",
    "credits.garantie.parental": "Vecāku vai bankas galvojums",
    "credits.garantie.professional": "Profesionālie aktīvi vai personiskais galvojums",
    "cards.reveal.pin_prompt": "Ievadiet savu 4 ciparu PIN kodu, lai parādītu kartes informāciju.",
    "cards.reveal.show": "Parādīt",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Divi PIN kodi nesakrīt",
    "auth.pin.digit_hint": "4 cipari",
  
  },
  lt: {
    "notif.transfer_code.title": "Pavedimo patvirtinimo kodas",
    "notif.transfer_code.body": "Nukopijuokite šį kodą pavedimui patvirtinti.",
    "notif.transfer_code.expires_in": "Išnyks po {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mėn.",
    "credits.n_months": "{n} mėn.",
    "msg.read": "Perskaityta",
    "msg.sent": "Išsiųsta",
    "msg.attach_aria": "Pridėti failą",
    "login.device.unknown": "Nežinoma naršyklė",
    "login.device.mobile": "Mobilus",
    "login.device.tablet": "Planšetė",
    "login.device.desktop": "Kompiuteris",
    "login.recent": "Neseniai",
    "err.invalid_credentials": "Negaliojantys prisijungimo duomenys",
    "err.bad_pin": "Neteisingas PIN",
    "err.account_blocked": "Paskyra užblokuota",
    "err.bad_current_pw": "Dabartinis slaptažodis neteisingas",
    "err.insufficient_funds": "Nepakanka lėšų, papildykite savo sąskaitą.",
    "err.amount_above_savings": "Suma viršija prieinamas santaupas",
    "err.must_be_positive": "Reikšmė turi būti teigiama",
    "err.invalid_format": "Negaliojantis formatas",
    "err.email_taken": "El. paštas jau naudojamas",
    "err.card_not_found": "Kortelė nerasta",
    "err.physical_card_already_ordered": "Fizinė kortelė jau užsakyta",
    "err.beneficiary_not_found": "Gavėjas nerastas",
    "err.invalid_code": "Negaliojantis kodas",
    "err.no_transfer_code": "Šiam pavedimui nėra kodo",
    "err.link_expired": "Nuoroda negalioja arba pasibaigė",
    "err.thread_not_found": "Pokalbis nerastas",
    "err.empty_message": "Tuščia žinutė",
    "err.file_too_large": "Failas per didelis (maks 20 MB)",
    "err.forbidden": "Prieiga uždrausta",
    "err.bank_not_found": "Bankas nerastas",
    "err.signups_closed": "Registracija šiame banke uždaryta.",
    "err.access_suspended": "Prieiga laikinai sustabdyta. Prašome susisiekti su savo banku.",
    "err.notif_not_closable": "Šio pranešimo negalima uždaryti.",
    "auth.signup.tagline": "Prisijunkite prie mūsų šiandien",
    "auth.login.tagline": "Sveiki sugrįžę!",
    "auth.signup.tagline_sub": "Atidarykite sąskaitą per 2 minutes ir gaukite prieigą prie visų mūsų aukščiausios klasės bankinių paslaugų.",
    "auth.login.tagline_sub": "Pasiekite savo asmeninę erdvę ir valdykite savo finansus saugiai.",
    "auth.back": "Grįžti į pagrindinį puslapį",
    "auth.bullet.security": "Karinio lygio bankinė apsauga",
    "auth.bullet.transfers": "Momentiniai pervedimai 24/7",
    "auth.bullet.investments": "Integruotos investicijos ir taupymas",
    "auth.bullet.advisor": "Asmeninis patarėjas pasiekiamas 7 dienas/7",
    "auth.footer": "Sukurta su {bank}",
    "auth.signup.title": "Sukurti sąskaitą",
    "auth.login.title": "Prisijungimas",
    "auth.field.lastName": "Pavardė",
    "auth.field.firstName": "Vardas(-ai)",
    "auth.field.country": "Šalis",
    "auth.field.country.placeholder": "— Pasirinkite šalį —",
    "auth.field.phone": "Telefonas",
    "auth.field.address": "Adresas",
    "auth.field.address.placeholder": "Gedimino pr. 123, Vilnius",
    "auth.field.email": "El. paštas",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Slaptažodis",
    "auth.submit.signup": "Sukurti mano sąskaitą",
    "auth.submit.login": "Prisijungti",
    "auth.login.badge": "Prisijunkite prie savo paskyros",
    "auth.card.tagline": "Jūsų kliento zona",
    "auth.submit.loading": "Kraunama…",
    "auth.pin.title": "PIN kodas",
    "auth.pin.subtitle": "Įveskite savo 4 skaitmenų PIN kodą",
    "auth.pin.label": "PIN kodas",
    "auth.pin.confirm": "Patvirtinti PIN kodą",
    "auth.pin.submit": "Patvirtinti",
    "auth.pin.back": "Grįžti į prisijungimą",
    "auth.pin.length_error": "PIN kodą turi sudaryti 4 skaitmenys",
    "auth.switch.have_account": "Jau turite sąskaitą?",
    "auth.switch.login_link": "Prisijungimas",
    "auth.switch.no_account": "Dar nesate klientas?",
    "auth.error.select_country": "Pasirinkite savo šalį",
    "kyc.title": "Tapatybės patvirtinimas (KYC)",
    "kyc.sub": "Įkelkite savo dokumentus, kad patvirtintumėte sąskaitą. Visi dokumentai yra užšifruoti ir konfidencialūs.",
    "kyc.id_type": "Asmens dokumento tipas *",
    "kyc.id_card": "Asmens tapatybės kortelė",
    "kyc.passport": "Pasas",
    "kyc.driver_license": "Vairuotojo pažymėjimas",
    "kyc.recto_passport": "Paso priekis *",
    "kyc.recto_id": "Dokumento priekis *",
    "kyc.verso": "Dokumento galas *",
    "kyc.selfie": "Asmenukė su dokumentu rankoje *",
    "kyc.address_proof": "Gyvenamosios vietos įrodymas *",
    "kyc.hint_doc": "Vaizdas arba PDF, maks. 5 MB",
    "kyc.hint_selfie": "Aiški nuotrauka, veidas ir dokumentas matomi",
    "kyc.hint_address": "Sąskaita, išrašas, pažyma… (maks. 5 MB)",
    "kyc.upload": "Įkelti",
    "kyc.replace": "Pakeisti",
    "pending.title": "Sąskaita laukia patvirtinimo",
    "pending.body": "Jūsų prašymas gautas. Administratorius patikrins jūsų bylą ir netrukus aktyvuos jūsų sąskaitą.",
    "pending.back": "Grįžti į prisijungimą",
    "home.nav.features": "Funkcijos",
    "home.nav.security": "Saugumas",
    "home.nav.invest": "Investicijos",
    "home.nav.credits": "Kreditai",
    "home.nav.faq": "DUK",
    "home.nav.login": "Prisijungti",
    "home.hero.tagline": "Naujos kartos internetinis bankas",
    "home.hero.title1": "Valdykite savo pinigus",
    "home.hero.title2": "protingai",
    "home.hero.sub": "Išsami bankinė platforma — pervedimai, taupymas, investicijos ir kreditai iš jūsų išmaniojo telefono.",
    "home.hero.login": "Prisijungti",
    "home.hero.trust.secure": "100% Saugus",
    "home.hero.trust.fast": "Greitas atidarymas",
    "home.hero.trust.encrypted": "Užšifruoti duomenys",
    "home.hero.trust.support": "Aptarnavimas 24/7",
    "home.mockup.balance": "Turimas likutis",
    "home.mockup.currency": "EUR · Pagrindinė sąskaita",
    "home.mockup.recharge": "Papildyti",
    "home.mockup.send": "Siųsti",
    "home.mockup.save": "Taupyti",
    "home.mockup.salary": "Kovo atlyginimas",
    "home.mockup.rent": "Balandžio nuoma",
    "home.mockup.investment": "Investicija",
    "home.mockup.days2": "prieš 2 dienas",
    "home.mockup.days3": "prieš 3 dienas",
    "home.mockup.days5": "prieš 5 dienas",
    "home.stats.accounts": "Aktyvios sąskaitos",
    "home.stats.uptime": "Prieinamumas",
    "home.stats.transfers": "Pervedimai",
    "home.stats.support": "Palaikymas",
    "home.features.label": "Funkcijos",
    "home.features.title": "Viskas, ko jums reikia",
    "home.features.sub": "Išsami platforma, skirta supaprastinti kasdienį finansų valdymą.",
    "home.feature.security": "Karinis saugumas",
    "home.feature.security_body": "AES-256 šifravimas, dviejų veiksnių autentifikacija ir sąskaitos stebėjimas 24/7.",
    "home.feature.cards": "Virtualios ir fizinės kortelės",
    "home.feature.cards_body": "Išduokite korteles per kelias sekundes arba užsakykite tikrą fizinę kortelę su pristatymu.",
    "home.feature.transfers": "Spartūs pervedimai",
    "home.feature.transfers_body": "Siųskite ir gaukite pinigus visame pasaulyje per kelias sekundes.",
    "home.feature.analytics": "Analizė realiuoju laiku",
    "home.feature.analytics_body": "Išmaniosios prietaisų suvestinės, leidžiančios sekti išlaidas ir planuoti finansus.",
    "home.feature.investments": "Integruotos investicijos",
    "home.feature.investments_body": "Pasiekite atrinktus investicinius produktus tiesiogiai iš savo sąskaitos.",
    "home.feature.support": "Specialus palaikymas 24/7",
    "home.feature.support_body": "Asmeninis banko patarėjas pasiekiamas bet kuriuo metu per saugų pokalbių kanalą.",
    "home.tpl.label": "Pasitikėjimas",
    "home.tpl.title": "Jie mumis pasitiki",
    "home.mockup.balance_label": "Likutis",
    "home.mockup.virement": "Pervedimas",
    "home.mockup.salaire": "Atlyginimas",
    "home.security.label": "Saugumas",
    "home.security.title1": "Jūsų finansai",
    "home.security.title2": "visiškai saugūs",
    "home.security.sub": "Taikome griežčiausius bankinės saugos standartus, kad apsaugotume kiekvieną centą.",
    "home.security.aes.title": "AES-256 šifravimas",
    "home.security.aes.body": "Visi neskelbtini duomenys šifruojami tais pačiais algoritmais, kuriuos naudoja stambiausios tarptautinės bankininkystės institucijos.",
    "home.security.auth.title": "Sustiprintas autentifikavimas",
    "home.security.auth.body": "Dvigubas tapatybės patikrinimas kiekvieną kartą prisijungiant ir atliekant svarbias operacijas.",
    "home.security.compliance.title": "PSD2/BDAR atitiktis",
    "home.security.compliance.body": "Laikomės visų Europos mokėjimo paslaugų ir duomenų apsaugos reglamentų.",
    "home.invest.label": "Investicijos",
    "home.invest.title": "Padidinkite savo pinigus",
    "home.invest.sub": "6 investicijų kategorijos, atrinktos dėl savo potencialo ir patikimumo.",
    "home.invest.yield": "Grąža",
    "home.credits.label": "Kreditai",
    "home.credits.title": "Sprendimai kiekvienam projektui",
    "home.credits.sub": "Konkurencingos palūkanos, greiti atsakymai ir asmeninis palydėjimas visiems jūsų projektams.",
    "home.credits.rate": "Norma",
    "home.faq.label": "DUK",
    "home.faq.title": "Dažnai užduodami klausimai",
    "home.faq.q1": "Kaip atidaryti sąskaitą?",
    "home.faq.a1": "Registracija trunka mažiau nei 2 minutes. Tiesiog užpildykite savo asmeninius duomenis, pasirinkite slaptažodį ir jūsų sąskaita bus aktyvuota po mūsų komandos patvirtinimo.",
    "home.faq.q2": "Ar mano duomenys saugūs?",
    "home.faq.a2": "Taip. Visi duomenys šifruojami AES-256 ramybės būsenoje ir TLS 1.3 perdavimo metu. Laikomės tarptautinių bankininkystės standartų ir BDAR/PSD2 reglamentų.",
    "home.faq.q3": "Kokius investicijų tipus siūlote?",
    "home.faq.a3": "Siūlome 6 kategorijas: Nekilnojamasis turtas, Sutelktinis finansavimas, Atsinaujinanti energija, Vertybiniai popieriai, Dalyvavimo ir Kriptovaliutos. Kiekvienas produktas atrinktas pagal rizikos ir grąžos santykį.",
    "home.faq.q4": "Kaip kreiptis dėl kredito?",
    "home.faq.a4": "Eikite į kliento srities Kreditų skyrių, pasirinkite paskolos tipą, įveskite sumą ir trukmę. Jūsų prašymą mūsų patarėjai apdoroja per 24 val.",
    "home.faq.q5": "Ar yra paslėptų mokesčių?",
    "home.faq.a5": "Jokių paslėptų mokesčių. Visi tarifai aiškiai rodomi su kiekviena operacija. Sąlygos pasiekiamos jūsų kliento srityje bet kuriuo metu.",
    "home.faq.q6": "Kaip susisiekti su palaikymo tarnyba?",
    "home.faq.a6": "Naudokite integruotą žinučių siuntimą savo kliento srityje, kad tiesiogiai susisiektumėte su asmeniniu banko patarėju, pasiekiamu 24/7.",
    "home.cta.title": "Pasiruošę pradėti?",
    "home.cta.sub": "Prisijunkite prie tūkstančių vartotojų, valdančių savo finansus su {bank}. Registracija trunka mažiau nei 2 minutes.",
    "home.footer.products": "Produktai",
    "home.footer.services": "Paslaugos",
    "home.footer.access": "Greita prieiga",
    "home.footer.accounts": "Sąskaitos",
    "home.footer.transfers": "Pervedimai",
    "home.footer.cards": "Kortelės",
    "home.footer.savings": "Taupymas",
    "home.footer.investments": "Investicijos",
    "home.footer.credits": "Kreditai",
    "home.footer.financing": "Finansavimas",
    "home.footer.messages": "Žinutės",
    "home.footer.login": "Prisijungimas",
    "home.footer.signup": "Atidaryti sąskaitą",
    "home.footer.support": "Palaikymas 24/7",
    "home.footer.tagline": "Jūsų internetinis bankas, pasiekiamas visur ir bet kuriuo metu.",
    "home.footer.copyright": "© {year} {bank} — Visos teisės saugomos",
    "home.footer.powered": "Sukurta su {bank}",
    "app.menu_label": "Pagrindinis meniu",
    "app.nav.dashboard": "Suvestinė",
    "app.nav.accounts": "Sąskaitos",
    "app.nav.transactions": "Operacijos",
    "app.nav.transfers": "Pervedimai",
    "app.nav.cards": "Kortelės",
    "app.nav.savings": "Taupymas",
    "app.nav.investments": "Investicijos",
    "app.nav.credit": "Kreditas",
    "app.nav.financing": "Finansavimas",
    "app.nav.messages": "Žinutės",
    "app.nav.card_tracking": "Kortelės sekimas",
    "app.nav.notifications": "Pranešimai",
    "app.nav.settings": "Nustatymai",
    "app.nav.profile": "Profilis",
    "app.nav.mobile.home": "Pradžia",
    "app.nav.mobile.transfers": "Pervedimai",
    "app.nav.mobile.cards": "Kortelės",
    "app.nav.mobile.transactions": "Operacijos",
    "app.nav.more.invest": "Investuoti",
    "app.user_role": "Klientas",
    "app.logout": "Atsijungti",
    "app.search": "Ieškoti…",
    "app.more_title": "Daugiau funkcijų",
    "app.more_btn": "Daugiau",
    "app.loading": "Kraunama…",
    "app.error_back": "Grįžti į prisijungimą",
    "app.see_more_notifs": "Žiūrėti {count} daugiau pranešimų →",
    "page.accounts": "Mano sąskaitos",
    "page.transactions": "Operacijos",
    "page.transfers": "Naujas pervedimas",
    "page.deposit": "Įmoka",
    "page.cards": "Mano kortelės",
    "page.savings": "Taupymas",
    "page.investments": "Investicijos",
    "page.credits": "Kreditas",
    "page.financing": "Projektų finansavimas",
    "page.messages": "Žinutės",
    "page.settings": "Nustatymai",
    "page.notifications": "Svarbūs pranešimai",
    "page.profile": "Mano profilis",
    "dash.greeting": "Sveiki, {name}",
    "dash.overview": "Jūsų finansų apžvalga — {date}",
    "dash.total_balance": "Bendras likutis",
    "dash.this_month": "+{amount} šį mėnesį",
    "dash.view_iban": "Žiūrėti mano IBAN",
    "dash.my_accounts": "Mano sąskaitos",
    "dash.primary_account": "Pagrindinė sąskaita",
    "dash.savings_account": "Taupomoji sąskaita",
    "dash.remunerated": "Palūkanomis apmokama",
    "dash.details": "Detalės",
    "dash.transfer_btn": "Pervedimas",
    "dash.quick_actions": "Greiti veiksmai",
    "dash.quick.deposit": "Įmoka",
    "deposit.title": "Informacija apie įmoką",
    "dash.quick.transfer": "Pervedimas",
    "dash.quick.history": "Istorija",
    "dash.quick.cards": "Mano kortelės",
    "dash.quick.profile": "Profilis",
    "dash.monthly_budget": "Mėnesio biudžetas",
    "dash.income": "Pajamos",
    "dash.expenses": "Išlaidos",
    "dash.spending_cat": "Išlaidos pagal kategoriją",
    "dash.no_spending": "Jokių išlaidų neužregistruota.",
    "dash.recent_tx": "Naujausios operacijos",
    "dash.see_all": "Žiūrėti visas",
    "dash.no_tx": "Jokių operacijų.",
    "dash.main_card": "Mano pagrindinė kortelė",
    "dash.manage": "Valdyti",
    "dash.block": "Blokuoti",
    "dash.pin": "PIN kodas",
    "dash.security": "Saugumas",
    "dash.more": "Daugiau",
    "dash.no_card": "Kortelė neišduota",
    "dash.monthly_activity": "Mėnesio veikla",
    "dash.operations": "Operacijos",
    "dash.holder": "Turėtojas",
    "dash.expires": "Galioja iki",
    "dash.visa_label": "VISA",
    "dash.balance": "Turimas likutis",
    "tx.all": "Visos",
    "tx.income": "Pajamos",
    "tx.expenses": "Išlaidos",
    "tx.today": "Šiandien",
    "tx.yesterday": "Vakar",
    "tx.none": "Jokių operacijų.",
    "transfer.title": "Greitas pervedimas",
    "transfer.recipient": "Gavėjas",
    "transfer.recipient_ph": "Vardas, IBAN, telefono numeris…",
    "transfer.amount": "Suma",
    "transfer.label": "Aprašas",
    "transfer.reason": "Priežastis (neprivaloma)",
    "transfer.reason_ph": "Pvz.: nuoma, grąžinimas…",
    "transfer.execution": "Vykdymas",
    "transfer.immediate": "Nedelsiant",
    "transfer.delayed": "Atidėtas",
    "transfer.balance": "Turimas likutis:",
    "transfer.continue": "Tęsti",
    "transfer.confirm": "Patvirtinti",
    "transfer.cancel": "Atšaukti",
    "transfer.code": "Patvirtinimo kodas",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Įveskite gautą kodą pervedimui patvirtinti",
    "transfer.pending.title": "Pervedimas laukia patvirtinimo",
    "transfer.pending.amount": "Suma:",
    "transfer.pending.to": "Kam:",
    "transfer.pending.hint": "Jūsų patvirtinimo kodas bus išsiųstas el. paštu arba per pranešimų varpelį. Kodą sudaro 6 skaitmenys. Jei jo negausite, kreipkitės į palaikymo tarnybą.",
    "transfer.pending.validate": "Patvirtinti pervedimą",
    "transfer.saved": "Pervedimas išsaugotas. Paprašykite administratoriaus patvirtinimo kodo.",
    "transfer.done": "Pervedimas atliktas",
    "transfer.validated": "Pervedimas patvirtintas!",
    "transfer.progress.processing": "Apdorojamas pervedimas…",
    "transfer.progress.completed": "Pervedimas atliktas!",
    "transfer.progress.default_message": "Jūsų pervedimas apdorojamas. Prašome palaukti.",
    "cards.none": "Kortelė neišduota.\nSusisiekite su administratoriumi.",
    "cards.holder": "Turėtojas",
    "cards.expires": "Galioja iki",
    "cards.block": "Užblokuoti",
    "cards.pin": "PIN kodas",
    "cards.more": "Daugiau",
    "cards.limits": "Kortelės limitai",
    "cards.payment": "Mokėjimas",
    "cards.withdrawal": "Išėmimas",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Užblokuota",
    "cards.status_active": "Aktyvi",
    "cards.toast.blocked": "Kortelė užblokuota",
    "cards.toast.unblocked": "Kortelė atblokuota",
    "cards.toast.physical_ordered": "Fizinė kortelė užsakyta! Pristatymas per 5–10 darbo dienų.",
    "cards.toast.created": "Kortelė sėkmingai sukurta!",
    "cards.none_sub": "Kortelė neišduota. Sukurkite savo virtualią kortelę dabar.",
    "cards.generate_btn": "Sukurti mano kortelę",
    "cards.create.title": "Sukurti mano virtualią kortelę",
    "cards.create.network": "Mokėjimų tinklas",
    "cards.create.color": "Kortelės spalva",
    "cards.create.preview_name": "JŪSŲ VARDAS",
    "cards.create.preview_expiry": "MM/MM",
    "cards.create.loading": "Kuriama…",
    "cards.create.submit": "Sukurti kortelę",
    "cards.btn.unblock": "Atblokuoti",
    "cards.btn.block": "Užblokuoti",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fizinė",
    "cards.btn.physical_done": "Užsakyta",
    "cards.limits_title": "Limitai",
    "cards.reveal.title": "Kortelės informacija",
    "cards.reveal.number": "Pilnas numeris",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Galiojimo data",
    "cards.reveal.warning": "Niekada nesidalinkite šia informacija. Uždarykite šį langą po naudojimo.",
    "cards.pin.title": "PIN kodas",
    "cards.pin.message": "Šis PIN kodas naudojamas visiems jūsų internetiniams mokėjimams ir išėmimams. Niekada niekam jo neatskleiskite.",
    "cards.physical.title": "Užsakyti fizinę kortelę",
    "cards.physical.fee_label": "Pristatymo mokestis",
    "cards.physical.balance_label": "Jūsų balansas",
    "cards.physical.insufficient": "Nepakankamas balansas. Prieš užsakydami papildykite savo sąskaitą.",
    "cards.physical.info": "Mokestis bus nurašytas nuo jūsų sąskaitos. Pristatymas per 5–10 darbo dienų.",
    "cards.physical.order_btn": "Užsakyti ({fee})",
    "cards.physical.ordering": "Užsakoma…",
    "cards.physical.order_cta": "Užsakyti fizinę kortelę",
    "cards.physical.choose_type": "Pasirinkite savo kortelę",
    "cards.physical.features": "Pranašumai",
    "cards.buy_virtual_cta": "Pirkti virtualią kortelę",
    "notif.none": "Šiuo metu pranešimų nėra.",
    "notif.contact_support": "Susisiekite su palaikymo tarnyba",
    "profile.change_photo": "Keisti nuotrauką",
    "profile.remove_photo": "Šalinti nuotrauką",
    "profile.email": "El. paštas",
    "profile.phone": "Telefonas",
    "profile.country": "Šalis",
    "profile.address": "Adresas",
    "profile.notifs_title": "Pranešimai ({count})",
    "profile.no_notifs": "Jokių pranešimų.",
    "profile.logout": "Atsijungti",
    "profile.photo_updated": "Profilio nuotrauka atnaujinta",
    "profile.photo_removed": "Nuotrauka pašalinta",
    "profile.photo_too_large": "Vaizdas per didelis (maks. 1 MB)",
    "profile.powered": "Sukurta su {bank}",
    "iban.title": "Mano IBAN",
    "iban.loading": "Kraunama…",
    "iban.bic": "BIC / SWIFT kodas",
    "iban.share_info": "Pasidalykite šiais duomenimis, kad gautumėte pervedimą į savo sąskaitą.",
    "iban.copy": "Kopijuoti",
    "iban.copied": "Nukopijuota į iškarpinę",
    "iban.copy_fail": "Nepavyko nukopijuoti",
    "iban.close": "Uždaryti",
    "accounts.all": "Visos jūsų sąskaitos {bank}.",
    "accounts.primary": "Pagrindinė sąskaita",
    "accounts.savings": "Taupomoji sąskaita",
    "accounts.primary_tag": "Pagrindinė",
    "accounts.secondary_tag": "Antrinė",
    "accounts.objective": "Tikslas: {amount} · {pct}%",
    "accounts.copy": "Kopijuoti",
    "accounts.loading": "Kraunama…",
    "accounts.iban_copied": "IBAN nukopijuotas",
    "accounts.copy_fail": "Nepavyko nukopijuoti",
    "savings.total": "Iš viso sutaupyta",
    "savings.target_total": "Bendras tikslas",
    "savings.progress": "Pažanga",
    "savings.active": "Aktyvūs tikslai",
    "savings.new_goal": "Naujas tikslas",
    "savings.none": "Taupymo tikslų kol kas nėra.",
    "savings.none_sub": "Sukurkite savo pirmą tikslą, kad pradėtumėte.",
    "savings.saved": "Sutaupyta:",
    "savings.goal": "Tikslas:",
    "savings.deadline": "Terminas: {date}",
    "savings.deposit": "Įnešti",
    "savings.withdraw": "Išimti",
    "savings.loading": "Kraunama…",
    "savings.form.title": "Naujas taupymo tikslas",
    "savings.form.cancel": "Atšaukti",
    "savings.form.create": "Sukurti",
    "savings.form.name": "Tikslo pavadinimas",
    "savings.form.name_ph": "Kelionė į Japoniją, Automobilis, …",
    "savings.form.target": "Tikslinis dydis",
    "savings.form.initial": "Pradinis įnašas",
    "savings.form.deadline": "Terminas (neprivaloma)",
    "savings.form.color": "Spalva",
    "savings.deposit_title": "Įnešti į \"{name}\"",
    "savings.deposit_amount": "Įnešama suma",
    "savings.balance_hint": "Pagrindinis likutis: {amount}",
    "savings.withdraw_prompt": "Kiek išimti iš \"{name}\"?",
    "savings.close_confirm": "Uždaryti tikslą \"{name}\"? Sutaupyti pinigai bus grąžinti į jūsų pagrindinę sąskaitą.",
    "savings.toast.created": "Tikslas sukurtas",
    "savings.toast.deposited": "Įnašas atliktas",
    "savings.toast.withdrawn": "Išėmimas atliktas",
    "savings.toast.closed": "Tikslas uždarytas",
    "invest.total": "Iš viso investuota",
    "invest.profits": "Sukauptas pelnas",
    "invest.count": "Investicijos",
    "invest.products": "Prieinami produktai",
    "invest.invest_btn": "Investuoti",
    "invest.my": "Mano investicijos",
    "invest.pending": "Laukia",
    "invest.validated": "Patvirtinta",
    "invest.refused": "Atmesta",
    "invest.withdraw.btn": "Atsiimti",
    "invest.withdraw.title": "Atsiimti mano investiciją",
    "invest.withdraw.intro": "Ketinate atsiimti savo investiciją „{product}\". Pradinis kapitalas ir sukauptas pelnas bus įskaityti į jūsų pagrindinį likutį.",
    "invest.withdraw.principal": "Pradinis kapitalas",
    "invest.withdraw.profit": "Pelnas",
    "invest.withdraw.total": "Iš viso gauti",
    "invest.withdraw.confirm": "Patvirtinti atsiėmimą",
    "invest.withdraw.warning": "Šio veiksmo negalima atšaukti. Investicija bus uždaryta.",
    "invest.withdraw.success": "Investicija atsiimta — {amount} įskaityti į likutį.",
    "invest.withdrawn": "Atsiimta",
    "invest.cta_sub": "Pasirinkite jums tinkamą investicijų tipą. Patarėjas padės nuo pat pirmojo prašymo.",
    "invest.form.amount": "Investuojama suma",
    "invest.form.balance": "Turimas likutis: {amount}",
    "invest.form.note": "Pastaba patarėjui (neprivaloma)",
    "invest.form.note_ph": "Nurodykite savo lūkesčius…",
    "invest.form.advisor_info": "Su jūsų patarėju bus automatiškai atidarytas pokalbis.",
    "invest.form.cancel": "Atšaukti",
    "invest.form.submit": "Siųsti prašymą",
    "invest.toast.requested": "Prašymas užregistruotas — pokalbis su jūsų patarėju atidarytas.",
    "invest.p1.name": "Nekilnojamasis turtas",
    "invest.p1.desc": "SCPI, popierinis nekilnojamasis turtas, sutelktinis finansavimas",
    "invest.p2.name": "Sutelktinis finansavimas",
    "invest.p2.desc": "Dalyvavimo finansavimas atrinktų projektų",
    "invest.p3.name": "Atsinaujinanti energija",
    "invest.p3.desc": "Saulės, vėjo ir žalieji projektai",
    "invest.p4.name": "Vertybiniai popieriai",
    "invest.p4.desc": "Akcijos, ETF ir diversifikuoti fondai",
    "invest.p5.name": "Dalyvavimo",
    "invest.p5.desc": "Paskolos MVĮ ir vietos verslininkams",
    "invest.p6.name": "Kriptovaliutos",
    "invest.p6.desc": "Bitcoin, Ethereum ir daugiavaliutė piniginė",
    "msg.online": "Prisijungęs",
    "msg.loading": "Kraunama…",
    "msg.none": "Jokių žinučių. Pradėkite pokalbį su savo patarėju.",
    "msg.placeholder": "Rašykite savo žinutę…",
    "msg.attach.image": "Vaizdas",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Garso įrašas",
    "msg.attach.file": "Failas / Dokumentas",
    "msg.pdf_doc": "PDF dokumentas",
    "msg.file": "Failas",
    "msg.too_large": "Failas per didelis (maks. {size}).",
    "msg.withdraw": "Ištrinti",
    "credits.cta_sub": "Pateikite prašymą tiesiogiai internetu — atsakymas per 24 val.",
    "credits.learn_more": "Sužinoti daugiau",
    "credits.request": "Kreiptis",
    "credits.history": "Mano prašymai",
    "credits.status.pending": "Laukia",
    "credits.status.accepted": "Priimta",
    "credits.status.refused": "Atmesta",
    "credits.chars": "Charakteristikos",
    "credits.amount_range": "Suma: {min}€ – {max}€",
    "credits.rate_range": "Palūkanų norma: {min}% – {max}% per metus",
    "credits.duration_range": "Trukmė: {min} iki {max} mėnesių",
    "credits.deposit_min": "Pradinis įnašas: {amount}€ minimum",
    "credits.garantie": "Garantija: {garantie}",
    "credits.eligibility": "Tinkamumo sąlygos",
    "credits.elig.income": "Pagrįsti reguliarias ir stabilias pajamas",
    "credits.elig.deposit": "Atlikti pradinį įnašą {amount}€",
    "credits.elig.garantie": "Pateikti garantiją: {garantie}",
    "credits.form.title": "Prašymas — {name}",
    "credits.form.cancel": "Atšaukti",
    "credits.form.submit": "Siųsti prašymą",
    "credits.form.amount": "Pageidaujama suma (€)",
    "credits.form.duration": "Trukmė (mėnesiai)",
    "credits.form.reason": "Priežastis (neprivaloma)",
    "credits.form.reason_ph": "Apibūdinkite savo projektą…",
    "credits.form.close": "Uždaryti",
    "credits.toast.submitted": "Kredito prašymas išsiųstas — atsakymą gausite per 24 val.",
    "credits.p1.name": "Asmeninė paskola",
    "credits.p1.desc": "Kasdienių poreikių ar asmeninių projektų finansavimui.",
    "credits.p2.name": "Hipotekos paskola",
    "credits.p2.desc": "Nekilnojamojo turto pirkimo ar statybos finansavimui.",
    "credits.p3.name": "Automobilio / Motociklo paskola",
    "credits.p3.desc": "Pirkite transporto priemonę ramiai.",
    "credits.p4.name": "Studijų paskola",
    "credits.p4.desc": "Finansinė pagalba aukštojo mokslo studijoms.",
    "credits.p5.name": "Verslo paskola",
    "credits.p5.desc": "Profesinės veiklos augimo palaikymui.",
    "credits.p6.name": "Ekspresų paskola",
    "credits.p6.desc": "Greitas sprendimas skubiems poreikiams.",
    "financing.reimbursable": "Grąžintinas finansavimas",
    "financing.reimbursable_desc": "Finansinė parama fiksuotomis sąlygomis veiklai pradėti ar plėtoti.",
    "financing.non_reimbursable": "Negrąžintinas finansavimas",
    "financing.non_reimbursable_desc": "Projektams, turintiems socialinį, edukacinį ar humanitarinį poveikį (NVO, asociacijos).",
    "financing.cta_sub": "Pritaikyti sprendimai jūsų ambicijoms įgyvendinti.",
    "financing.request": "Prašyti finansavimo",
    "financing.history": "Mano prašymai",
    "financing.reimbursable_label": "Grąžintinas",
    "financing.non_reimbursable_label": "Negrąžintinas",
    "financing.status.pending": "Laukia",
    "financing.status.granted": "Suteikta",
    "financing.status.refused": "Atmesta",
    "financing.form.title": "Prašymas — {name}",
    "financing.form.cancel": "Atšaukti",
    "financing.form.submit": "Siųsti prašymą",
    "financing.form.project": "Projekto pavadinimas",
    "financing.form.project_ph": "Mano projektas",
    "financing.form.amount": "Prašoma suma (€)",
    "financing.form.sector": "Sektorius",
    "financing.form.sector_ph": "Technologijos, žemės ūkis, …",
    "financing.form.description": "Projekto aprašymas",
    "financing.form.description_ph": "Pristatykite savo projektą, jo tikslus ir laukiamą poveikį…",
    "settings.security": "Saugumas",
    "settings.current_pw": "Dabartinis slaptažodis",
    "settings.new_pw": "Naujas slaptažodis",
    "settings.confirm_pw": "Patvirtinti",
    "settings.submit": "Keisti slaptažodį",
    "settings.no_match": "Slaptažodžiai nesutampa",
    "settings.changed": "Slaptažodis pakeistas",
    "common.cancel": "Atšaukti",
    "common.confirm": "Patvirtinti",
    "common.close": "Uždaryti",
    "common.save": "Išsaugoti",
    "common.loading": "Kraunama…",
    "common.send": "Siųsti",
    "common.total": "Viso",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Apytiksl. mėn. mokėjimas",
    "credits.rate_annual": "/ metai",
    "credits.rate_used": "Apytiksl. palūkanos:",
    "credits.simulation": "Mėnesinės įmokos skaičiavimas",
    "credits.total_cost": "Apytiksl. bendra kaina",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Sveiki atvykę",
    "common.delete": "Ištrinti",
    "cards.delete.title": "Ištrinti kortelę",
    "cards.delete.confirm_msg": "Šis veiksmas yra negrįžtamas. Kortelė bus ištrinta visam laikui.",
    "cards.delete.physical_tooltip": "Fizinė kortelė užsakyta — ištrynimas neįmanomas",
    "cards.order.confirmed": "Užsakymas patvirtintas!",
    "cards.order.confirmed_body": "Jūsų fizinė kortelė ruošiama. Naudokite šį numerį pristatymui sekti.",
    "cards.order.tracking_no": "Sekimo nr.",
    "cards.fee_deducted": "{amount} bus atskaityta nuo jūsų balanso",
    "cards.insufficient_short": "Nepakankamas balansas ({available} prieinama)",
    "transfer.section.beneficiary": "Gavėjas",
    "transfer.section.details": "Pervedimas",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bankas",
    "transfer.field.city": "Miestas",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bankas:",
    "transfer.progress.paused": "Laukia patvirtinimo",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Kintamas",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Kortelė ištrinta",
    "cards.feat.gold_exclusive": "Išskirtinė auksinė kortelė",
    "cards.feat.cashback_05": "Pinigų grąžinimas 0,5%",
    "cards.feat.buyer_protection": "Pirkėjo apsauga",
    "cards.feat.support_standard": "Standartinis palaikymas",
    "cards.feat.plastic_premium": "Premium plastikinė kortelė",
    "cards.feat.cashback_1": "Pinigų grąžinimas 1%",
    "cards.feat.travel_standard": "Standartinis kelionių draudimas",
    "cards.feat.support_priority": "Prioritetinis palaikymas",
    "cards.feat.metal_brushed": "Šveistos metalo kortelė",
    "cards.feat.cashback_2_unlimited": "Neribotas 2% pinigų grąžinimas",
    "cards.feat.travel_premium": "Premium kelionių draudimas",
    "cards.feat.vip_lounge": "VIP zonų prieiga",
    "cards.feat.support_247": "24/7 pagalba",
    "auth.demo.title": "Administratoriaus prisijungimas",
    "auth.demo.subtitle": "Įveskite administratoriaus duomenis demonstracijai peržiūrėti",
    "credits.garantie.none": "Nėra",
    "credits.garantie.mortgage": "Hipoteka finansuojamam turtui",
    "credits.garantie.vehicle": "Finansuojama transporto priemonė",
    "credits.garantie.parental": "Tėvų arba banko",
    "credits.garantie.professional": "Profesiniai aktyvai arba asmeninė laidavimo garantija",
    "cards.reveal.pin_prompt": "Įveskite 4 skaitmenų PIN kodą, kad matytumėte kortelės informaciją.",
    "cards.reveal.show": "Rodyti",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Du PIN kodai nesutampa",
    "auth.pin.digit_hint": "4 skaitmenys",
  
  },
  ga: {
    "notif.transfer_code.title": "Cód bailíochtaithe aistrithe",
    "notif.transfer_code.body": "Cóipeáil an cód seo chun an t-aistriú a bhailíochtú.",
    "notif.transfer_code.expires_in": "Imíonn i gceann {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mí",
    "credits.n_months": "{n} mí",
    "msg.read": "Léite",
    "msg.sent": "Seolta",
    "msg.attach_aria": "Ceangail comhad",
    "login.device.unknown": "Brabhsálaí anaithnid",
    "login.device.mobile": "Soghluaiste",
    "login.device.tablet": "Táibléad",
    "login.device.desktop": "Ríomhaire",
    "login.recent": "Le déanaí",
    "err.invalid_credentials": "Dintiúir neamhbhailí",
    "err.bad_pin": "PIN mícheart",
    "err.account_blocked": "Cuntas blocáilte",
    "err.bad_current_pw": "Tá an pasfhocal reatha mícheart",
    "err.insufficient_funds": "Cistí neamhleor, athluchtaigh do chuntas le do thoil.",
    "err.amount_above_savings": "Sáraíonn an méid an coigilteas atá ar fáil",
    "err.must_be_positive": "Caithfidh an luach a bheith dearfach",
    "err.invalid_format": "Formáid neamhbhailí",
    "err.email_taken": "Tá an ríomhphost in úsáid cheana",
    "err.card_not_found": "Cárta gan aimsiú",
    "err.physical_card_already_ordered": "Cárta fisiciúil ordaithe cheana",
    "err.beneficiary_not_found": "Tairbhí gan aimsiú",
    "err.invalid_code": "Cód neamhbhailí",
    "err.no_transfer_code": "Gan cód don aistriú seo",
    "err.link_expired": "Nasc neamhbhailí nó imithe in éag",
    "err.thread_not_found": "Comhrá gan aimsiú",
    "err.empty_message": "Teachtaireacht folamh",
    "err.file_too_large": "Comhad ró-mhór (uas 20 MB)",
    "err.forbidden": "Diúltaíodh rochtain",
    "err.bank_not_found": "Banc gan aimsiú",
    "err.signups_closed": "Tá clárúcháin dúnta don bhanc seo.",
    "err.access_suspended": "Rochtain ar fionraí go sealadach. Déan teagmháil le do bhanc, le do thoil.",
    "err.notif_not_closable": "Ní féidir an fógra seo a dhúnadh.",
    "auth.signup.tagline": "Glac linn inniu",
    "auth.login.tagline": "Fáilte ar ais!",
    "auth.signup.tagline_sub": "Oscail do chuntas in 2 nóiméad agus faigh rochtain ar ár seirbhísí baincéireachta préimhe.",
    "auth.login.tagline_sub": "Rochtain ar do spás pearsanta agus bainistigh do chuid airgeadais go slán.",
    "auth.back": "Ar ais go dtí an leathanach baile",
    "auth.bullet.security": "Slándáil baincéireachta ar leibhéal míleata",
    "auth.bullet.transfers": "Aistrithe láithreacha 24/7",
    "auth.bullet.investments": "Infheistíochtaí agus coigilteas comhtháite",
    "auth.bullet.advisor": "Comhairleoir pearsanta ar fáil 7 lá/7",
    "auth.footer": "Cumhachtaithe ag {bank}",
    "auth.signup.title": "Cruthaigh cuntas",
    "auth.login.title": "Logáil isteach",
    "auth.field.lastName": "Sloinne",
    "auth.field.firstName": "Céadainm(-neacha)",
    "auth.field.country": "Tír",
    "auth.field.country.placeholder": "— Roghnaigh do thír —",
    "auth.field.phone": "Fón",
    "auth.field.address": "Seoladh",
    "auth.field.address.placeholder": "123 Sráid an Phríomha, Baile Átha Cliath",
    "auth.field.email": "Ríomhphost",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Pasfhocal",
    "auth.submit.signup": "Cruthaigh mo chuntas",
    "auth.submit.login": "Logáil isteach",
    "auth.login.badge": "Logáil isteach ar do chuntas",
    "auth.card.tagline": "Do limistéar cliaint",
    "auth.submit.loading": "Ag lódáil…",
    "auth.pin.title": "Cód PIN",
    "auth.pin.subtitle": "Cuir isteach do Chód PIN 4 dhigit",
    "auth.pin.label": "Cód PIN",
    "auth.pin.confirm": "Deimhnigh Cód PIN",
    "auth.pin.submit": "Deimhnigh",
    "auth.pin.back": "Ar ais chuig logáil isteach",
    "auth.pin.length_error": "Caithfidh 4 dhigit a bheith sa Chód PIN",
    "auth.switch.have_account": "Cuntas agat cheana?",
    "auth.switch.login_link": "Logáil isteach",
    "auth.switch.no_account": "Níl tú i do chliant fós?",
    "auth.error.select_country": "Roghnaigh do thír",
    "kyc.title": "Fíorú Céannachta (KYC)",
    "kyc.sub": "Uaslódáil do chuid doiciméad chun do chuntas a bhailíochtú. Tá gach doiciméad criptithe agus rúnda.",
    "kyc.id_type": "Cineál doiciméid aitheantais *",
    "kyc.id_card": "Cárta aitheantais",
    "kyc.passport": "Pas",
    "kyc.driver_license": "Ceadúnas tiomána",
    "kyc.recto_passport": "Aghaidh an phas *",
    "kyc.recto_id": "Aghaidh an doiciméid *",
    "kyc.verso": "Cúl an doiciméid *",
    "kyc.selfie": "Féinphictiúr le doiciméad i láimh *",
    "kyc.address_proof": "Cruthúnas seolta *",
    "kyc.hint_doc": "Íomhá nó PDF, uasmhéid 5 MB",
    "kyc.hint_selfie": "Grianghraf soiléir, aghaidh agus doiciméad le feiceáil",
    "kyc.hint_address": "Bille, ráiteas, teastas… (uasmhéid 5 MB)",
    "kyc.upload": "Uaslódáil",
    "kyc.replace": "Ionadaigh",
    "pending.title": "Cuntas ar feitheamh",
    "pending.body": "Fuarthas do iarratas. Déanfaidh riarthóir do chomhad a fhíorú agus gníomhachtóidh sé do chuntas go luath.",
    "pending.back": "Ar ais go dtí logáil isteach",
    "home.nav.features": "Gnéithe",
    "home.nav.security": "Slándáil",
    "home.nav.invest": "Infheistíocht",
    "home.nav.credits": "Creidmheasanna",
    "home.nav.faq": "CCanna",
    "home.nav.login": "Logáil isteach",
    "home.hero.tagline": "Baincéireacht ar líne den chéad ghlúin eile",
    "home.hero.title1": "Bainistigh do chuid airgid",
    "home.hero.title2": "go cliste",
    "home.hero.sub": "Ardán baincéireachta iomlán — aistrithe, coigilteas, infheistíochtaí agus creidmheasanna ó do fhón cliste.",
    "home.hero.login": "Logáil isteach",
    "home.hero.trust.secure": "100% Slán",
    "home.hero.trust.fast": "Oscailt thapa",
    "home.hero.trust.encrypted": "Sonraí criptithe",
    "home.hero.trust.support": "Seirbhís 24/7",
    "home.mockup.balance": "Iarmhéid ar fáil",
    "home.mockup.currency": "EUR · Príomhcuntas",
    "home.mockup.recharge": "Athluchtaigh",
    "home.mockup.send": "Seol",
    "home.mockup.save": "Coigil",
    "home.mockup.salary": "Tuarastal Márta",
    "home.mockup.rent": "Cíos Aibreán",
    "home.mockup.investment": "Infheistíocht",
    "home.mockup.days2": "2 lá ó shin",
    "home.mockup.days3": "3 lá ó shin",
    "home.mockup.days5": "5 lá ó shin",
    "home.stats.accounts": "Cuntais ghníomhacha",
    "home.stats.uptime": "Infhaighteacht",
    "home.stats.transfers": "Aistrithe",
    "home.stats.support": "Tacaíocht",
    "home.features.label": "Gnéithe",
    "home.features.title": "Gach rud a theastaíonn uait",
    "home.features.sub": "Ardán iomlán deartha chun bainistíocht do chuid airgeadais a shimpliú gach lá.",
    "home.feature.security": "Slándáil mhíleata",
    "home.feature.security_body": "Criptiú AES-256, fíordheimhniú dhá fhachtóir agus faireachán cuntais 24/7.",
    "home.feature.cards": "Cártaí fíorúla agus fisiciúla",
    "home.feature.cards_body": "Eisigh cártaí i gcúpla soicind nó ordaigh cárta fisiciúil fíor a sheachadtar chugat.",
    "home.feature.transfers": "Aistrithe meara",
    "home.feature.transfers_body": "Seol agus faigh airgead ar fud an domhain i gcúpla soicind.",
    "home.feature.analytics": "Anailís fíor-ama",
    "home.feature.analytics_body": "Deaisclanna cliste chun do chuid caiteachais a rianú agus do chuid airgeadais a réamh-mheas.",
    "home.feature.investments": "Infheistíochtaí comhtháite",
    "home.feature.investments_body": "Rochtain ar tháirgí infheistíochta roghnaithe go díreach ó do chuntas.",
    "home.feature.support": "Tacaíocht thiomnaithe 24/7",
    "home.feature.support_body": "Comhairleoir baincéireachta pearsanta ar fáil ag am ar bith trí theachtaireachtaí slána.",
    "home.tpl.label": "Trust",
    "home.tpl.title": "They trust us",
    "home.mockup.balance_label": "Iarmhéid",
    "home.mockup.virement": "Aistriú",
    "home.mockup.salaire": "Tuarastal",
    "home.security.label": "Slándáil",
    "home.security.title1": "Do chuid airgeadais",
    "home.security.title2": "ar shlándáil iomlán",
    "home.security.sub": "Cuirimid na caighdeáin slándála baincéireachta is déine i bhfeidhm chun gach cent a chosaint.",
    "home.security.aes.title": "Criptiú AES-256",
    "home.security.aes.body": "Tá gach sonra íogair criptithe leis na halgartaim chéanna a úsáideann na mórbhainc idirnáisiúnta.",
    "home.security.auth.title": "Fíordheimhniú feabhsaithe",
    "home.security.auth.body": "Fíorú céannachta dúbailte ag gach logáil isteach agus do gach oibríocht íogair ar do chuntas.",
    "home.security.compliance.title": "Comhlíonadh PSD2/GDPR",
    "home.security.compliance.body": "Comhlíonaimid gach rialachán Eorpach maidir le seirbhísí íocaíochta agus cosaint sonraí.",
    "home.invest.label": "Infheistíochtaí",
    "home.invest.title": "Fás do chuid airgid",
    "home.invest.sub": "6 chatagóir infheistíochta roghnaithe dá gcumas agus dá iontaofacht.",
    "home.invest.yield": "Toradh",
    "home.credits.label": "Creidmheasanna",
    "home.credits.title": "Réitigh do gach tionscadal",
    "home.credits.sub": "Rátaí iomaíocha, freagraí tapa agus tacaíocht phearsanaithe do gach ceann de do thionscadail.",
    "home.credits.rate": "Ráta",
    "home.faq.label": "CCanna",
    "home.faq.title": "Ceisteanna coitianta",
    "home.faq.q1": "Conas cuntas a oscailt?",
    "home.faq.a1": "Tógann clárú níos lú ná 2 nóiméad. Líon isteach do chuid faisnéise pearsanta, roghnaigh pasfhocal agus gníomhachtófar do chuntas tar éis bailíochtaithe ag ár bhfoireann.",
    "home.faq.q2": "An bhfuil mo chuid sonraí slán?",
    "home.faq.a2": "Tá. Tá gach sonra criptithe le AES-256 ar stóráil agus TLS 1.3 i dtáirgeadh. Comhlíonaimid caighdeáin baincéireachta idirnáisiúnta agus rialacháin GDPR/PSD2.",
    "home.faq.q3": "Cén cineál infheistíochtaí a chuireann sibh ar fáil?",
    "home.faq.a3": "Cuirimid 6 chatagóir ar fáil: Eastát Réadach, Slua-mhaoiniú, Fuinneamh In-athnuaite, Margadh Stoc, Rannpháirtíoch agus Criptea. Roghnítear gach táirge dá cóimheas riosca/toradh.",
    "home.faq.q4": "Conas iarratas a dhéanamh ar chreidmheas?",
    "home.faq.a4": "Téigh go rannán Creidmheasa d'achar cliant, roghnaigh an cineál iasachta, cuir isteach an méid agus an fad. Próiseálann ár gcomhairleoirí d'iarratas laistigh de 24u.",
    "home.faq.q5": "An bhfuil táillí folaithe ann?",
    "home.faq.a5": "Gan táillí folaithe. Taispeántar gach ráta go soiléir le gach oibríocht. Tá téarmaí ar fáil i d'achar cliant ag am ar bith.",
    "home.faq.q6": "Conas teagmháil a dhéanamh leis an tacaíocht?",
    "home.faq.a6": "Úsáid an teachtaireachtaí comhtháite i d'achar cliant chun teagmháil dhíreach a dhéanamh le do chomhairleoir baincéireachta pearsanta, ar fáil 24/7.",
    "home.cta.title": "Réidh le tosú?",
    "home.cta.sub": "Glac le na mílte úsáideoir a bhainistíonn a gcuid airgeadais le {bank}. Tógann clárú níos lú ná 2 nóiméad.",
    "home.footer.products": "Táirgí",
    "home.footer.services": "Seirbhísí",
    "home.footer.access": "Rochtain thapa",
    "home.footer.accounts": "Cuntais",
    "home.footer.transfers": "Aistrithe",
    "home.footer.cards": "Cártaí",
    "home.footer.savings": "Coigilteas",
    "home.footer.investments": "Infheistíochtaí",
    "home.footer.credits": "Creidmheasanna",
    "home.footer.financing": "Maoiniú",
    "home.footer.messages": "Teachtaireachtaí",
    "home.footer.login": "Logáil isteach",
    "home.footer.signup": "Oscail cuntas",
    "home.footer.support": "Tacaíocht 24/7",
    "home.footer.tagline": "Do bhanc ar líne, ar fáil i ngach áit agus ag am ar bith.",
    "home.footer.copyright": "© {year} {bank} — Gach ceart ar cosaint",
    "home.footer.powered": "Cumhachtaithe ag {bank}",
    "app.menu_label": "Príomhroghchlár",
    "app.nav.dashboard": "Deais",
    "app.nav.accounts": "Cuntais",
    "app.nav.transactions": "Idirbhearta",
    "app.nav.transfers": "Aistrithe",
    "app.nav.cards": "Cártaí",
    "app.nav.savings": "Coigilteas",
    "app.nav.investments": "Infheistíocht",
    "app.nav.credit": "Creidmheas",
    "app.nav.financing": "Maoiniú",
    "app.nav.messages": "Teachtaireachtaí",
    "app.nav.card_tracking": "Rianú cárta",
    "app.nav.notifications": "Fógraí",
    "app.nav.settings": "Socruithe",
    "app.nav.profile": "Próifíl",
    "app.nav.mobile.home": "Baile",
    "app.nav.mobile.transfers": "Aistrithe",
    "app.nav.mobile.cards": "Cártaí",
    "app.nav.mobile.transactions": "Idirbhearta",
    "app.nav.more.invest": "Infheistigh",
    "app.user_role": "Cliant",
    "app.logout": "Logáil amach",
    "app.search": "Cuardaigh…",
    "app.more_title": "Tuilleadh gnéithe",
    "app.more_btn": "Tuilleadh",
    "app.loading": "Ag lódáil…",
    "app.error_back": "Ar ais go dtí logáil isteach",
    "app.see_more_notifs": "Féach {count} fógra eile →",
    "page.accounts": "Mo chuntais",
    "page.transactions": "Idirbhearta",
    "page.transfers": "Aistriú nua",
    "page.deposit": "Taisce",
    "page.cards": "Mo chártaí",
    "page.savings": "Coigilteas",
    "page.investments": "Infheistíocht",
    "page.credits": "Creidmheas",
    "page.financing": "Maoiniú tionscadail",
    "page.messages": "Teachtaireachtaí",
    "page.settings": "Socruithe",
    "page.notifications": "Fógraí tábhachtacha",
    "page.profile": "Mo phróifíl",
    "dash.greeting": "Dia duit, {name}",
    "dash.overview": "Forbhreathnú ar d'airgeadais — {date}",
    "dash.total_balance": "Iarmhéid iomlán",
    "dash.this_month": "+{amount} an mhí seo",
    "dash.view_iban": "Féach mo IBAN",
    "dash.my_accounts": "Mo chuntais",
    "dash.primary_account": "Príomhcuntas",
    "dash.savings_account": "Cuntas coigiltis",
    "dash.remunerated": "Úsmhar",
    "dash.details": "Sonraí",
    "dash.transfer_btn": "Aistriú",
    "dash.quick_actions": "Gníomhartha tapa",
    "dash.quick.deposit": "Taisce",
    "deposit.title": "Faisnéis taisce",
    "dash.quick.transfer": "Aistriú",
    "dash.quick.history": "Stair",
    "dash.quick.cards": "Mo chártaí",
    "dash.quick.profile": "Próifíl",
    "dash.monthly_budget": "Buiséad míosúil",
    "dash.income": "Ioncam",
    "dash.expenses": "Caiteachas",
    "dash.spending_cat": "Caiteachas de réir catagóire",
    "dash.no_spending": "Níl aon chaiteachas taifeadta.",
    "dash.recent_tx": "Idirbhearta le déanaí",
    "dash.see_all": "Féach uile",
    "dash.no_tx": "Gan idirbhearta.",
    "dash.main_card": "Mo phríomhchárta",
    "dash.manage": "Bainistigh",
    "dash.block": "Bloc",
    "dash.pin": "Cód PIN",
    "dash.security": "Slándáil",
    "dash.more": "Tuilleadh",
    "dash.no_card": "Níl aon chárta eisithe",
    "dash.monthly_activity": "Gníomhaíocht mhíosúil",
    "dash.operations": "Oibríochtaí",
    "dash.holder": "Sealbhóir",
    "dash.expires": "Éag.",
    "dash.visa_label": "VISA",
    "dash.balance": "Iarmhéid ar fáil",
    "tx.all": "Uile",
    "tx.income": "Ioncam",
    "tx.expenses": "Caiteachas",
    "tx.today": "Inniu",
    "tx.yesterday": "Inné",
    "tx.none": "Gan idirbhearta.",
    "transfer.title": "Aistriú tapa",
    "transfer.recipient": "Faighteoir",
    "transfer.recipient_ph": "Ainm, IBAN, uimhir fóin…",
    "transfer.amount": "Méid",
    "transfer.label": "Lipéad",
    "transfer.reason": "Cúis (roghnach)",
    "transfer.reason_ph": "Mar shampla: cíos, aisíocaíocht…",
    "transfer.execution": "Forghníomhú",
    "transfer.immediate": "Láithreach",
    "transfer.delayed": "Moillithe",
    "transfer.balance": "Iarmhéid ar fáil:",
    "transfer.continue": "Lean ar aghaidh",
    "transfer.confirm": "Deimhnigh",
    "transfer.cancel": "Cealaigh",
    "transfer.code": "Cód bailíochtaithe",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Cuir isteach an cód a fuarthas chun an t-aistriú a bhailíochtú",
    "transfer.pending.title": "Aistriú ar feitheamh bailíochtaithe",
    "transfer.pending.amount": "Méid:",
    "transfer.pending.to": "Chuig:",
    "transfer.pending.hint": "Seolfar do chód bailíochtaithe trí r-phost nó trí chloch na bhfógraí. Tá 6 digit sa chód. Mura bhfaighidh tú é, téigh i dteagmháil leis an tacaíocht.",
    "transfer.pending.validate": "Bailíochtaigh aistriú",
    "transfer.saved": "Aistriú sábháilte. Iarr ar do riarthóir an cód bailíochtaithe.",
    "transfer.done": "Aistriú críochnaithe",
    "transfer.validated": "Aistriú bailíochtaithe!",
    "transfer.progress.processing": "Aistriú á phróiseáil…",
    "transfer.progress.completed": "Aistriú críochnaithe!",
    "transfer.progress.default_message": "Tá d'aistriú á phróiseáil. Fan le do thoil.",
    "cards.none": "Níl aon chárta eisithe.\nDéan teagmháil le do riarthóir.",
    "cards.holder": "Sealbhóir",
    "cards.expires": "Éag.",
    "cards.block": "Glasáil",
    "cards.pin": "Cód PIN",
    "cards.more": "Tuilleadh",
    "cards.limits": "Teorainneacha cárta",
    "cards.payment": "Íocaíocht",
    "cards.withdrawal": "Aistarraingt",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Blocáilte",
    "cards.status_active": "Gníomhach",
    "cards.toast.blocked": "Cárta blocáilte",
    "cards.toast.unblocked": "Cárta dí-bhlocáilte",
    "cards.toast.physical_ordered": "Cárta fisiciúil ordaithe! Seachadadh laistigh de 5–10 lá oibre.",
    "cards.toast.created": "Cárta ginte go rathúil!",
    "cards.none_sub": "Níl aon chárta eisithe. Gin do chárta fíorúil anois.",
    "cards.generate_btn": "Gin mo chárta",
    "cards.create.title": "Gin mo chárta fíorúil",
    "cards.create.network": "Líonra íocaíochta",
    "cards.create.color": "Dath an chárta",
    "cards.create.preview_name": "D'AINM",
    "cards.create.preview_expiry": "MM/BB",
    "cards.create.loading": "Ag giniúint…",
    "cards.create.submit": "Gin cárta",
    "cards.btn.unblock": "Dí-bhlocáil",
    "cards.btn.block": "Blocáil",
    "cards.btn.info": "Eolas",
    "cards.btn.physical": "Fisiciúil",
    "cards.btn.physical_done": "Ordaithe",
    "cards.limits_title": "Teorainneacha",
    "cards.reveal.title": "Faisnéis cárta",
    "cards.reveal.number": "Uimhir iomlán",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Éaga",
    "cards.reveal.warning": "Ná roinn an fhaisnéis seo riamh. Dún an fhuinneog seo tar éis úsáide.",
    "cards.pin.title": "Cód PIN",
    "cards.pin.message": "Úsáidtear an cód PIN seo le haghaidh do chuid íocaíochtaí ar líne agus aistarraingteacha go léir. Ná roinn é le haon duine riamh.",
    "cards.physical.title": "Ordaigh cárta fisiciúil",
    "cards.physical.fee_label": "Táille seachadta",
    "cards.physical.balance_label": "Do shláine",
    "cards.physical.insufficient": "Iarmhéid neamhleor. Muirearú do chuntas sula n-ordaíonn tú.",
    "cards.physical.info": "Bainfear an táille as do chuntas. Seachadadh laistigh de 5–10 lá oibre.",
    "cards.physical.order_btn": "Ordaigh ({fee})",
    "cards.physical.ordering": "Ag ordú…",
    "cards.physical.order_cta": "Ordaigh cárta fisiciúil",
    "cards.physical.choose_type": "Roghnaigh do chárta",
    "cards.physical.features": "Buntáistí",
    "cards.buy_virtual_cta": "Ceannaigh cárta fíorúil",
    "notif.none": "Gan fógraí faoi láthair.",
    "notif.contact_support": "Déan teagmháil le tacaíocht",
    "profile.change_photo": "Athraigh grianghraf",
    "profile.remove_photo": "Bain grianghraf",
    "profile.email": "Ríomhphost",
    "profile.phone": "Fón",
    "profile.country": "Tír",
    "profile.address": "Seoladh",
    "profile.notifs_title": "Fógraí ({count})",
    "profile.no_notifs": "Gan fógraí.",
    "profile.logout": "Logáil amach",
    "profile.photo_updated": "Grianghraf próifíle nuashonraithe",
    "profile.photo_removed": "Grianghraf bainte",
    "profile.photo_too_large": "Íomhá rómhór (uasmhéid 1 MB)",
    "profile.powered": "Cumhachtaithe ag {bank}",
    "iban.title": "Mo IBAN",
    "iban.loading": "Ag lódáil…",
    "iban.bic": "Cód BIC / SWIFT",
    "iban.share_info": "Comhroinn na sonraí seo chun aistriú a fháil chuig do chuntas.",
    "iban.copy": "Cóipeáil",
    "iban.copied": "Cóipeáilte go dtí an gearrthaisce",
    "iban.copy_fail": "Ní féidir cóipeáil",
    "iban.close": "Dún",
    "accounts.all": "Do chuntais ar fad le {bank}.",
    "accounts.primary": "Príomhcuntas",
    "accounts.savings": "Cuntas coigiltis",
    "accounts.primary_tag": "Príomhúil",
    "accounts.secondary_tag": "Tánaisteach",
    "accounts.objective": "Sprioc: {amount} · {pct}%",
    "accounts.copy": "Cóipeáil",
    "accounts.loading": "Ag lódáil…",
    "accounts.iban_copied": "IBAN cóipeáilte",
    "accounts.copy_fail": "Ní féidir cóipeáil",
    "savings.total": "Iomlán coigilte",
    "savings.target_total": "Sprioc iomlán",
    "savings.progress": "Dul chun cinn",
    "savings.active": "Spriocanna gníomhacha",
    "savings.new_goal": "Sprioc nua",
    "savings.none": "Gan spriocanna coigiltis fós.",
    "savings.none_sub": "Cruthaigh do chéad sprioc chun tosú.",
    "savings.saved": "Coigilte:",
    "savings.goal": "Sprioc:",
    "savings.deadline": "Spriocdháta: {date}",
    "savings.deposit": "Taiscigh",
    "savings.withdraw": "Tarraing siar",
    "savings.loading": "Ag lódáil…",
    "savings.form.title": "Sprioc coigiltis nua",
    "savings.form.cancel": "Cealaigh",
    "savings.form.create": "Cruthaigh",
    "savings.form.name": "Ainm na sprice",
    "savings.form.name_ph": "Turas go Seapáin, Carr, …",
    "savings.form.target": "Méid sprice",
    "savings.form.initial": "Taisce tosaigh",
    "savings.form.deadline": "Spriocdháta (roghnach)",
    "savings.form.color": "Dath",
    "savings.deposit_title": "Taiscigh go \"{name}\"",
    "savings.deposit_amount": "Méid le taiscigh",
    "savings.balance_hint": "Príomhiarmhéid: {amount}",
    "savings.withdraw_prompt": "Cé mhéid le tarraing siar ó \"{name}\"?",
    "savings.close_confirm": "Dún sprioc \"{name}\"? Cuirfear na coigiltis ar ais chuig do phríomhcuntas.",
    "savings.toast.created": "Sprioc cruthaithe",
    "savings.toast.deposited": "Taisce déanta",
    "savings.toast.withdrawn": "Aistarraingt déanta",
    "savings.toast.closed": "Sprioc dúnta",
    "invest.total": "Iomlán infheistithe",
    "invest.profits": "Brabúis carntha",
    "invest.count": "Infheistíochtaí",
    "invest.products": "Táirgí ar fáil",
    "invest.invest_btn": "Infheistigh",
    "invest.my": "Mo chuid infheistíochtaí",
    "invest.pending": "Ar feitheamh",
    "invest.validated": "Bailíochtaithe",
    "invest.refused": "Diúltaithe",
    "invest.withdraw.btn": "Tarraing siar",
    "invest.withdraw.title": "Tarraing siar m'infheistíocht",
    "invest.withdraw.intro": "Tá tú ar tí d'infheistíocht „{product}\" a tharraingt siar. Cuirfear an phríomhshuim agus an brabús carntha le do phríomhiarmhéid.",
    "invest.withdraw.principal": "Príomhshuim",
    "invest.withdraw.profit": "Brabús",
    "invest.withdraw.total": "Iomlán le fáil",
    "invest.withdraw.confirm": "Deimhnigh an tarraingt siar",
    "invest.withdraw.warning": "Tá an gníomh seo do-aisiompaithe.",
    "invest.withdraw.success": "Infheistíocht tarraingthe siar — {amount} curtha le do iarmhéid.",
    "invest.withdrawn": "Tarraingthe siar",
    "invest.cta_sub": "Roghnaigh an cineál infheistíochta a oireann duit. Cabhróidh comhairleoir leat ó do chéad iarratas.",
    "invest.form.amount": "Méid le hinfheistiú",
    "invest.form.balance": "Iarmhéid ar fáil: {amount}",
    "invest.form.note": "Nóta do do chomhairleoir (roghnach)",
    "invest.form.note_ph": "Sonraigh do chuid ionchais…",
    "invest.form.advisor_info": "Osclófar comhrá go huathoibríoch le do chomhairleoir.",
    "invest.form.cancel": "Cealaigh",
    "invest.form.submit": "Seol iarratas",
    "invest.toast.requested": "Iarratas cláraithe — osclaíodh comhrá le do chomhairleoir.",
    "invest.p1.name": "Eastát Réadach",
    "invest.p1.desc": "SCPI, páipéar-chloch, slua-mhaoiniú réadmhaoine",
    "invest.p2.name": "Slua-mhaoiniú",
    "invest.p2.desc": "Maoiniú rannpháirtíoch tionscadal roghnaithe",
    "invest.p3.name": "Fuinneamh In-athnuaite",
    "invest.p3.desc": "Grian, gaoithe agus tionscadail ghlas",
    "invest.p4.name": "Margadh Stoc",
    "invest.p4.desc": "Scaireanna, ETF agus cistí éagsúlaithe",
    "invest.p5.name": "Rannpháirtíoch",
    "invest.p5.desc": "Iasachtaí do FBManna agus fiontraithe áitiúla",
    "invest.p6.name": "Criptea",
    "invest.p6.desc": "Bitcoin, Ethereum agus sparán ilairgeadra",
    "msg.online": "Ar líne",
    "msg.loading": "Ag lódáil…",
    "msg.none": "Gan teachtaireachtaí. Tosaigh an comhrá le do chomhairleoir.",
    "msg.placeholder": "Scríobh do theachtaireacht…",
    "msg.attach.image": "Íomhá",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Fuaim",
    "msg.attach.file": "Comhad / Doiciméad",
    "msg.pdf_doc": "Doiciméad PDF",
    "msg.file": "Comhad",
    "msg.too_large": "Comhad rómhór (uasmhéid {size}).",
    "msg.withdraw": "Scrios",
    "credits.cta_sub": "Déan iarratas ar líne go díreach — freagra laistigh de 24u.",
    "credits.learn_more": "Faigh tuilleadh eolais",
    "credits.request": "Iarr",
    "credits.history": "Mo chuid iarratas",
    "credits.status.pending": "Ar feitheamh",
    "credits.status.accepted": "Glactha",
    "credits.status.refused": "Diúltaithe",
    "credits.chars": "Saintréithe",
    "credits.amount_range": "Méid: {min}€ – {max}€",
    "credits.rate_range": "Ráta úis: {min}% – {max}% sa bhliain",
    "credits.duration_range": "Fad: {min} go {max} mí",
    "credits.deposit_min": "Taisce tosaigh: {amount}€ ar a laghad",
    "credits.garantie": "Ráthaíocht: {garantie}",
    "credits.eligibility": "Critéir incháilitheachta",
    "credits.elig.income": "Ioncam rialta agus cobhsaí a chruthú",
    "credits.elig.deposit": "Taisce tosaigh {amount}€ a dhéanamh",
    "credits.elig.garantie": "An ráthaíocht a chur i láthair: {garantie}",
    "credits.form.title": "Iarratas — {name}",
    "credits.form.cancel": "Cealaigh",
    "credits.form.submit": "Seol iarratas",
    "credits.form.amount": "Méid atá uait (€)",
    "credits.form.duration": "Fad (míonna)",
    "credits.form.reason": "Cúis (roghnach)",
    "credits.form.reason_ph": "Cur síos ar do thionscadal…",
    "credits.form.close": "Dún",
    "credits.toast.submitted": "Iarratas creidmheasa seolta — gheobhaidh tú freagra laistigh de 24u.",
    "credits.p1.name": "Iasacht Phearsanta",
    "credits.p1.desc": "Chun riachtanais laethúla nó tionscadail phearsanta a mhaoiniú.",
    "credits.p2.name": "Morgáiste",
    "credits.p2.desc": "Chun ceannach nó tógáil maoine réadaí a mhaoiniú.",
    "credits.p3.name": "Iasacht Ghluaisteáin / Mótarbhaoic",
    "credits.p3.desc": "Ceannaigh feithicil gan strus.",
    "credits.p4.name": "Iasacht Mac Léinn",
    "credits.p4.desc": "Cúnamh airgeadais do chúrsaí ardoideachais.",
    "credits.p5.name": "Iasacht Gnó",
    "credits.p5.desc": "Chun fás gníomhaíochta gairmiúla a thacú.",
    "credits.p6.name": "Iasacht Mear",
    "credits.p6.desc": "Réiteach tapa do riachtanais phráinneacha.",
    "financing.reimbursable": "Maoiniú inaisíoctha",
    "financing.reimbursable_desc": "Tacaíocht airgeadais le coinníollacha seasta chun gníomhaíocht a sheoladh nó a fhorbairt.",
    "financing.non_reimbursable": "Maoiniú neamh-inaisíoctha",
    "financing.non_reimbursable_desc": "Do thionscadail le tionchar sóisialta, oideachasúil nó daonnachtúil (ENRanna, cumainn).",
    "financing.cta_sub": "Réitigh saincheaptha chun do chuid uaillmhianta a bhaint amach.",
    "financing.request": "Iarr maoiniú",
    "financing.history": "Mo chuid iarratas",
    "financing.reimbursable_label": "Inaisíoctha",
    "financing.non_reimbursable_label": "Neamh-inaisíoctha",
    "financing.status.pending": "Ar feitheamh",
    "financing.status.granted": "Deonaithe",
    "financing.status.refused": "Diúltaithe",
    "financing.form.title": "Iarratas — {name}",
    "financing.form.cancel": "Cealaigh",
    "financing.form.submit": "Seol iarratas",
    "financing.form.project": "Ainm an tionscadail",
    "financing.form.project_ph": "Mo thionscadal",
    "financing.form.amount": "Méid iartha (€)",
    "financing.form.sector": "Earnáil",
    "financing.form.sector_ph": "Teicneolaíocht, talmhaíocht, …",
    "financing.form.description": "Cur síos ar an tionscadal",
    "financing.form.description_ph": "Cuir i láthair do thionscadal, a chuspóirí agus a thionchar ionchais…",
    "settings.security": "Slándáil",
    "settings.current_pw": "Pasfhocal reatha",
    "settings.new_pw": "Pasfhocal nua",
    "settings.confirm_pw": "Deimhnigh",
    "settings.submit": "Athraigh pasfhocal",
    "settings.no_match": "Ní hionann na pasfhocail",
    "settings.changed": "Pasfhocal athraithe",
    "common.cancel": "Cealaigh",
    "common.confirm": "Deimhnigh",
    "common.close": "Dún",
    "common.save": "Sábháil",
    "common.loading": "Ag lódáil…",
    "common.send": "Seol",
    "common.total": "Iomlán",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Suim mhíosúil mheasta",
    "credits.rate_annual": "/ bliain",
    "credits.rate_used": "Ráta measta:",
    "credits.simulation": "Meastachán suim mhíosúil",
    "credits.total_cost": "Costas iomlán measta",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Fáilte",
    "common.delete": "Scrios",
    "cards.delete.title": "Scrios an cárta",
    "cards.delete.confirm_msg": "Tá an gníomh seo do-aisiompaithe. Scriosfar an cárta go buan.",
    "cards.delete.physical_tooltip": "Cárta fisiciúil ordaithe — scrios dodhéanta",
    "cards.order.confirmed": "Ordú deimhnithe!",
    "cards.order.confirmed_body": "Tá do chárta fisiciúil á ullmhú. Úsáid an uimhir seo chun do sheachadadh a rianú.",
    "cards.order.tracking_no": "Uimh. rianaithe",
    "cards.fee_deducted": "Beidh {amount} tógtha as d'iarmhéid",
    "cards.insufficient_short": "Iarmhéid neamhleor ({available} ar fáil)",
    "transfer.section.beneficiary": "Tairbhí",
    "transfer.section.details": "Aistriú",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banc",
    "transfer.field.city": "Cathair",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banc:",
    "transfer.progress.paused": "Ag fanacht le bailíochtú",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Athraitheach",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Cárta scriosta",
    "cards.feat.gold_exclusive": "Cárta óir eisiach",
    "cards.feat.cashback_05": "Aisíoc 0.5%",
    "cards.feat.buyer_protection": "Cosaint cheannaitheora",
    "cards.feat.support_standard": "Tacaíocht chaighdeánach",
    "cards.feat.plastic_premium": "Cárta plaisteach premium",
    "cards.feat.cashback_1": "Aisíoc 1%",
    "cards.feat.travel_standard": "Árachas taistil caighdeánach",
    "cards.feat.support_priority": "Tacaíocht thosaíochta",
    "cards.feat.metal_brushed": "Cárta miotail scuabtha",
    "cards.feat.cashback_2_unlimited": "Aisíoc 2% gan teorainn",
    "cards.feat.travel_premium": "Árachas taistil premium",
    "cards.feat.vip_lounge": "Rochtain ar thabhairt VIP",
    "cards.feat.support_247": "Tacaíocht 24/7",
    "auth.demo.title": "Logáil isteach an riarthóra",
    "auth.demo.subtitle": "Cuir isteach do chreidiúnais riarthóra chun an taispeántas a réamhamharc",
    "credits.garantie.none": "Dada",
    "credits.garantie.mortgage": "Morgáiste ar an maoin á maoiniú",
    "credits.garantie.vehicle": "Feithicil á maoiniú",
    "credits.garantie.parental": "Tuismitheora nó baincéireachta",
    "credits.garantie.professional": "Sócmhainní gairmiúla nó bannaí pearsanta",
    "cards.reveal.pin_prompt": "Iontráil do chód PIN 4 dhigit chun faisnéis na cárta a thaispeáint.",
    "cards.reveal.show": "Taispeáin",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Ní hionann an dá chód PIN",
    "auth.pin.digit_hint": "4 uimhir",
  
  },
  mt: {
    "notif.transfer_code.title": "Kodiċi ta' validazzjoni tat-trasferiment",
    "notif.transfer_code.body": "Ikkupja dan il-kodiċi biex tivvalida t-trasferiment.",
    "notif.transfer_code.expires_in": "Jisparixxi f'{sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} xhur",
    "credits.n_months": "{n} xhur",
    "msg.read": "Inqara",
    "msg.sent": "Mibgħut",
    "msg.attach_aria": "Annetti fajl",
    "login.device.unknown": "Browser mhux magħruf",
    "login.device.mobile": "Mobbli",
    "login.device.tablet": "Tablet",
    "login.device.desktop": "Kompjuter",
    "login.recent": "Reċenti",
    "err.invalid_credentials": "Kredenzjali invalidi",
    "err.bad_pin": "PIN żbaljat",
    "err.account_blocked": "Kont imblukkat",
    "err.bad_current_pw": "Il-password attwali hija żbaljata",
    "err.insufficient_funds": "Fondi insuffiċjenti, jekk jogħġbok agħmel top-up tal-kont.",
    "err.amount_above_savings": "L-ammont jaqbeż it-tfaddil disponibbli",
    "err.must_be_positive": "Il-valur għandu jkun pożittiv",
    "err.invalid_format": "Format invalidu",
    "err.email_taken": "Email diġà tintuża",
    "err.card_not_found": "Karta ma nstabitx",
    "err.physical_card_already_ordered": "Karta fiżika diġà ordnata",
    "err.beneficiary_not_found": "Benefiċjarju ma nstabx",
    "err.invalid_code": "Kodiċi invalidu",
    "err.no_transfer_code": "L-ebda kodiċi għal dan it-trasferiment",
    "err.link_expired": "Link invalidu jew skadut",
    "err.thread_not_found": "Konversazzjoni ma nstabitx",
    "err.empty_message": "Messaġġ vojt",
    "err.file_too_large": "Fajl kbir wisq (massimu 20 MB)",
    "err.forbidden": "Aċċess miċħud",
    "err.bank_not_found": "Bank ma nstabx",
    "err.signups_closed": "Ir-reġistrazzjonijiet huma magħluqa għal dan il-bank.",
    "err.access_suspended": "Aċċess sospiż temporanjament. Jekk jogħġbok ikkuntattja l-bank tiegħek.",
    "err.notif_not_closable": "Din in-notifika ma tistax tingħalaq.",
    "auth.signup.tagline": "Ingħaqad magħna llum",
    "auth.login.tagline": "Merħba lura!",
    "auth.signup.tagline_sub": "Iftaħ il-kont tiegħek fi 2 minuti u ikkollja l-aċċess għas-servizzi bankarji premium tagħna kollha.",
    "auth.login.tagline_sub": "Aċċessa l-ispazju personali tiegħek u immaniġġja l-finanzi tiegħek b'mod sigur.",
    "auth.back": "Lura għall-paġna ewlenija",
    "auth.bullet.security": "Sigurtà bankarja ta' livell militari",
    "auth.bullet.transfers": "Trasferimenti istantanji 24/7",
    "auth.bullet.investments": "Investimenti u tfaddil integrati",
    "auth.bullet.advisor": "Konsulent personali disponibbli 7 ijiem/7",
    "auth.footer": "Imħaddem minn {bank}",
    "auth.signup.title": "Oħloq kont",
    "auth.login.title": "Idħol",
    "auth.field.lastName": "Kunjom",
    "auth.field.firstName": "Isem(-ijiet)",
    "auth.field.country": "Pajjiż",
    "auth.field.country.placeholder": "— Agħżel il-pajjiż tiegħek —",
    "auth.field.phone": "Telefon",
    "auth.field.address": "Indirizz",
    "auth.field.address.placeholder": "123 Triq ir-Repubblika, Valletta",
    "auth.field.email": "Email",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Password",
    "auth.submit.signup": "Oħloq il-kont tiegħi",
    "auth.submit.login": "Idħol",
    "auth.login.badge": "Idħol fil-kont tiegħek",
    "auth.card.tagline": "L-ispazju tal-klijent tiegħek",
    "auth.submit.loading": "Qed jillowdja…",
    "auth.pin.title": "Kodiċi PIN",
    "auth.pin.subtitle": "Daħħal il-Kodiċi PIN ta’ 4 ċifri tiegħek",
    "auth.pin.label": "Kodiċi PIN",
    "auth.pin.confirm": "Ikkonferma l-Kodiċi PIN",
    "auth.pin.submit": "Ikkonferma",
    "auth.pin.back": "Lura għ-login",
    "auth.pin.length_error": "Il-Kodiċi PIN irid ikun ta’ 4 ċifri",
    "auth.switch.have_account": "Diġà għandek kont?",
    "auth.switch.login_link": "Idħol",
    "auth.switch.no_account": "Għadek mhux klijent?",
    "auth.error.select_country": "Agħżel il-pajjiż tiegħek",
    "kyc.title": "Verifika tal-Identità (KYC)",
    "kyc.sub": "Itella' d-dokumenti tiegħek biex tivvalida l-kont tiegħek. Id-dokumenti kollha huma kriptati u kunfidenzjali.",
    "kyc.id_type": "Tip ta' dokument tal-identità *",
    "kyc.id_card": "Karta tal-identità",
    "kyc.passport": "Passaport",
    "kyc.driver_license": "Liċenzja tas-sewqan",
    "kyc.recto_passport": "Wiċċ il-passaport *",
    "kyc.recto_id": "Wiċċ id-dokument *",
    "kyc.verso": "Dahar id-dokument *",
    "kyc.selfie": "Selfie bid-dokument f'idek *",
    "kyc.address_proof": "Prova tal-indirizz *",
    "kyc.hint_doc": "Immaġni jew PDF, maks 5 MB",
    "kyc.hint_selfie": "Ritratt ċar, wiċċ u dokument viżibbli",
    "kyc.hint_address": "Kont, stqarrija, ċertifikat… (maks 5 MB)",
    "kyc.upload": "Itella'",
    "kyc.replace": "Ibdel",
    "pending.title": "Kont pendenti",
    "pending.body": "It-talba tiegħek ġiet riċevuta. Amministratur se jivverifika l-fajl tiegħek u se jattiva l-kont tiegħek dalwaqt.",
    "pending.back": "Lura biex tidħol",
    "home.nav.features": "Karatteristiċi",
    "home.nav.security": "Sigurtà",
    "home.nav.invest": "Investiment",
    "home.nav.credits": "Krediti",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Idħol",
    "home.hero.tagline": "Bankarija online tal-ġenerazzjoni li jmiss",
    "home.hero.title1": "Immaniġġja l-flus tiegħek",
    "home.hero.title2": "b'intelliġenza",
    "home.hero.sub": "Pjattaforma bankarja sħiħa — trasferimenti, tfaddil, investimenti u krediti mill-ismartphone tiegħek.",
    "home.hero.login": "Idħol",
    "home.hero.trust.secure": "100% Sigur",
    "home.hero.trust.fast": "Ftuħ rapidu",
    "home.hero.trust.encrypted": "Dejta kriptata",
    "home.hero.trust.support": "Servizz 24/7",
    "home.mockup.balance": "Bilanċ disponibbli",
    "home.mockup.currency": "EUR · Kont prinċipali",
    "home.mockup.recharge": "Irrikarika",
    "home.mockup.send": "Ibgħat",
    "home.mockup.save": "Iffranka",
    "home.mockup.salary": "Salarju ta' Marzu",
    "home.mockup.rent": "Kera ta' April",
    "home.mockup.investment": "Investiment",
    "home.mockup.days2": "2 ijiem ilu",
    "home.mockup.days3": "3 ijiem ilu",
    "home.mockup.days5": "5 ijiem ilu",
    "home.stats.accounts": "Kontijiet attivi",
    "home.stats.uptime": "Disponibbiltà",
    "home.stats.transfers": "Trasferimenti",
    "home.stats.support": "Appoġġ",
    "home.features.label": "Karatteristiċi",
    "home.features.title": "Kulma għandek bżonn",
    "home.features.sub": "Pjattaforma sħiħa ddisinjata biex tissimplifika l-ġestjoni tal-finanzi tiegħek kuljum.",
    "home.feature.security": "Sigurtà militari",
    "home.feature.security_body": "Kriptaġġ AES-256, awtentikazzjoni b'żewġ fatturi u monitoraġġ tal-kont 24/7.",
    "home.feature.cards": "Karti virtwali u fiżiċi",
    "home.feature.cards_body": "Oħroġ karti f'sekondi jew ordna karta fiżika vera livrata d-dar tiegħek.",
    "home.feature.transfers": "Trasferimenti espressi",
    "home.feature.transfers_body": "Ibgħat u irċievi flus mad-dinja kollha f'sekondi.",
    "home.feature.analytics": "Analiżi f'ħin reali",
    "home.feature.analytics_body": "Dashboards intelliġenti biex isegwu l-infiq tiegħek u jantiċipaw il-finanzi tiegħek.",
    "home.feature.investments": "Investimenti integrati",
    "home.feature.investments_body": "Aċċessa prodotti ta' investiment magħżula direttament mill-kont tiegħek.",
    "home.feature.support": "Appoġġ dedikat 24/7",
    "home.feature.support_body": "Konsulent bankarju personali disponibbli fi kwalunkwe ħin permezz ta' messaġġerija sigura.",
    "home.tpl.label": "Trust",
    "home.tpl.title": "They trust us",
    "home.mockup.balance_label": "Bilanċ",
    "home.mockup.virement": "Trasferiment",
    "home.mockup.salaire": "Salarju",
    "home.security.label": "Sigurtà",
    "home.security.title1": "Il-finanzi tiegħek",
    "home.security.title2": "f'sigurtà assoluta",
    "home.security.sub": "Napplikaw l-ogħla standards ta' sigurtà bankarja biex nipproteġu kull ċenteżmu.",
    "home.security.aes.title": "Kriptaġġ AES-256",
    "home.security.aes.body": "Id-dejta sensittiva kollha hija kriptata bl-istess algoritmi użati mill-akbar banek internazzjonali.",
    "home.security.auth.title": "Awtentikazzjoni msaħħa",
    "home.security.auth.body": "Verifika doppja tal-identità f'kull dħul u għal kull operazzjoni sensittiva fuq il-kont tiegħek.",
    "home.security.compliance.title": "Konformità PSD2/GDPR",
    "home.security.compliance.body": "Nikkonformaw mar-regolamenti Ewropej kollha dwar servizzi ta' ħlas u protezzjoni tad-dejta.",
    "home.invest.label": "Investimenti",
    "home.invest.title": "Żid il-flus tiegħek",
    "home.invest.sub": "6 kategoriji ta' investiment magħżula għall-potenzjal u l-affidabbiltà tagħhom.",
    "home.invest.yield": "Rendiment",
    "home.credits.label": "Krediti",
    "home.credits.title": "Soluzzjonijiet għal kull proġett",
    "home.credits.sub": "Rati kompetittivi, risposti rapidi u appoġġ personalizzat għall-proġetti tiegħek kollha.",
    "home.credits.rate": "Rata",
    "home.faq.label": "FAQ",
    "home.faq.title": "Mistoqsijiet frekwenti",
    "home.faq.q1": "Kif niftaħ kont?",
    "home.faq.a1": "Ir-reġistrazzjoni tieħu inqas minn 2 minuti. Sempliċement imla l-informazzjoni personali tiegħek, agħżel password u l-kont tiegħek jiġi attivat wara l-validazzjoni mit-tim tagħna.",
    "home.faq.q2": "Id-dejta tiegħi hija sigura?",
    "home.faq.a2": "Iva. Id-dejta kollha hija kriptata b'AES-256 fl-irqad u TLS 1.3 fit-tranzitu. Nikkonformaw mal-istandards bankarji internazzjonali u r-regolamenti GDPR/PSD2.",
    "home.faq.q3": "X'tipi ta' investimenti toffru?",
    "home.faq.a3": "Noffru 6 kategoriji: Proprjetà Immobbli, Crowdfunding, Enerġija Rinnovabbli, Borża, Parteċipattiv u Kripto. Kull prodott huwa magħżul għar-rapport riskju/rendiment tiegħu.",
    "home.faq.q4": "Kif napplika għal kreditu?",
    "home.faq.a4": "Mur fit-taqsima tal-Kreditu tal-ispazju tal-klijent tiegħek, agħżel it-tip ta' self, daħħal l-ammont u d-durata. L-applikazzjoni tiegħek tiġi proċessata fi 24 siegħa mill-konsulenti tagħna.",
    "home.faq.q5": "Hemm tariffi moħbija?",
    "home.faq.a5": "L-ebda tariffa moħbija. It-tariffi kollha huma murija b'mod ċar ma' kull operazzjoni. Il-kondizzjonijiet huma aċċessibbli fl-ispazju tal-klijent tiegħek fi kwalunkwe ħin.",
    "home.faq.q6": "Kif nikkuntattja l-appoġġ?",
    "home.faq.a6": "Uża l-messaġġerija integrata fl-ispazju tal-klijent tiegħek biex tikkuntattja direttament lill-konsulent bankarju personali tiegħek, disponibbli 24/7.",
    "home.cta.title": "Lest tibda?",
    "home.cta.sub": "Ingħaqad mal-eluf ta' utenti li jimmaniġġjaw il-finanzi tagħhom ma' {bank}. Ir-reġistrazzjoni tieħu inqas minn 2 minuti.",
    "home.footer.products": "Prodotti",
    "home.footer.services": "Servizzi",
    "home.footer.access": "Aċċess rapidu",
    "home.footer.accounts": "Kontijiet",
    "home.footer.transfers": "Trasferimenti",
    "home.footer.cards": "Karti",
    "home.footer.savings": "Tfaddil",
    "home.footer.investments": "Investimenti",
    "home.footer.credits": "Krediti",
    "home.footer.financing": "Finanzjament",
    "home.footer.messages": "Messaġġi",
    "home.footer.login": "Idħol",
    "home.footer.signup": "Iftaħ kont",
    "home.footer.support": "Appoġġ 24/7",
    "home.footer.tagline": "Il-bank online tiegħek, disponibbli kullimkien u fi kwalunkwe ħin.",
    "home.footer.copyright": "© {year} {bank} — Id-drittijiet kollha riżervati",
    "home.footer.powered": "Imħaddem minn {bank}",
    "app.menu_label": "Menu ewlieni",
    "app.nav.dashboard": "Dashboard",
    "app.nav.accounts": "Kontijiet",
    "app.nav.transactions": "Transazzjonijiet",
    "app.nav.transfers": "Trasferimenti",
    "app.nav.cards": "Karti",
    "app.nav.savings": "Tfaddil",
    "app.nav.investments": "Investiment",
    "app.nav.credit": "Kreditu",
    "app.nav.financing": "Finanzjament",
    "app.nav.messages": "Messaġġi",
    "app.nav.card_tracking": "Traċċar tal-kard",
    "app.nav.notifications": "Notifiki",
    "app.nav.settings": "Settings",
    "app.nav.profile": "Profil",
    "app.nav.mobile.home": "Dar",
    "app.nav.mobile.transfers": "Trasferimenti",
    "app.nav.mobile.cards": "Karti",
    "app.nav.mobile.transactions": "Transazzjonijiet",
    "app.nav.more.invest": "Investi",
    "app.user_role": "Klijent",
    "app.logout": "Oħroġ",
    "app.search": "Fittex…",
    "app.more_title": "Aktar karatteristiċi",
    "app.more_btn": "Aktar",
    "app.loading": "Qed jillowdja…",
    "app.error_back": "Lura biex tidħol",
    "app.see_more_notifs": "Ara {count} notifika oħra →",
    "page.accounts": "Il-kontijiet tiegħi",
    "page.transactions": "Transazzjonijiet",
    "page.transfers": "Trasferiment ġdid",
    "page.deposit": "Depożitu",
    "page.cards": "Il-karti tiegħi",
    "page.savings": "Tfaddil",
    "page.investments": "Investiment",
    "page.credits": "Kreditu",
    "page.financing": "Finanzjament ta' proġetti",
    "page.messages": "Messaġġi",
    "page.settings": "Settings",
    "page.notifications": "Notifiki importanti",
    "page.profile": "Il-profil tiegħi",
    "dash.greeting": "Bonġu, {name}",
    "dash.overview": "Ħarsa ġenerali tal-finanzi tiegħek — {date}",
    "dash.total_balance": "Bilanċ totali",
    "dash.this_month": "+{amount} dan ix-xahar",
    "dash.view_iban": "Ara l-IBAN tiegħi",
    "dash.my_accounts": "Il-kontijiet tiegħi",
    "dash.primary_account": "Kont prinċipali",
    "dash.savings_account": "Kont tat-tfaddil",
    "dash.remunerated": "Bi mgħax",
    "dash.details": "Dettalji",
    "dash.transfer_btn": "Trasferiment",
    "dash.quick_actions": "Azzjonijiet rapidi",
    "dash.quick.deposit": "Depożitu",
    "deposit.title": "Informazzjoni dwar id-depożitu",
    "dash.quick.transfer": "Trasferiment",
    "dash.quick.history": "Storja",
    "dash.quick.cards": "Il-karti tiegħi",
    "dash.quick.profile": "Profil",
    "dash.monthly_budget": "Baġit mensili",
    "dash.income": "Dħul",
    "dash.expenses": "Nefqa",
    "dash.spending_cat": "Nefqa skont il-kategorija",
    "dash.no_spending": "L-ebda nefqa rreġistrata.",
    "dash.recent_tx": "Transazzjonijiet reċenti",
    "dash.see_all": "Ara kollox",
    "dash.no_tx": "L-ebda transazzjonijiet.",
    "dash.main_card": "Il-karta prinċipali tiegħi",
    "dash.manage": "Immaniġġja",
    "dash.block": "Ibblokka",
    "dash.pin": "Kodiċi PIN",
    "dash.security": "Sigurtà",
    "dash.more": "Aktar",
    "dash.no_card": "L-ebda karta maħruġa",
    "dash.monthly_activity": "Attività mensili",
    "dash.operations": "Operazzjonijiet",
    "dash.holder": "Detentur",
    "dash.expires": "Skad.",
    "dash.visa_label": "VISA",
    "dash.balance": "Bilanċ disponibbli",
    "tx.all": "Kollha",
    "tx.income": "Dħul",
    "tx.expenses": "Nefqa",
    "tx.today": "Illum",
    "tx.yesterday": "Il-bieraħ",
    "tx.none": "L-ebda transazzjonijiet.",
    "transfer.title": "Trasferiment rapidu",
    "transfer.recipient": "Destinatarju",
    "transfer.recipient_ph": "Isem, IBAN, numru tat-telefon…",
    "transfer.amount": "Ammont",
    "transfer.label": "Tikketta",
    "transfer.reason": "Raġuni (fakultattiv)",
    "transfer.reason_ph": "Eż.: kera, rimborż…",
    "transfer.execution": "Eżekuzzjoni",
    "transfer.immediate": "Immedjat",
    "transfer.delayed": "Differit",
    "transfer.balance": "Bilanċ disponibbli:",
    "transfer.continue": "Kompli",
    "transfer.confirm": "Ikkonferma",
    "transfer.cancel": "Ikkanċella",
    "transfer.code": "Kodiċi ta' validazzjoni",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Daħħal il-kodiċi riċevut biex tivvalida t-trasferiment",
    "transfer.pending.title": "Trasferiment pendenti ta' validazzjoni",
    "transfer.pending.amount": "Ammont:",
    "transfer.pending.to": "Lejn:",
    "transfer.pending.hint": "Il-kodiċi ta' validazzjoni tiegħek jintbagħat bl-e-mail jew permezz tal-qanpiena tan-notifiki. Il-kodiċi huwa magħmul minn 6 ċifri. Jekk ma tirċevihx, jekk jogħġbok ikkuntattja s-support.",
    "transfer.pending.validate": "Ivvalida t-trasferiment",
    "transfer.saved": "Trasferiment salvat. Itlob lill-amministratur il-kodiċi ta' validazzjoni.",
    "transfer.done": "Trasferiment lest",
    "transfer.validated": "Trasferiment validat!",
    "transfer.progress.processing": "Qed jiġi pproċessat it-trasferiment…",
    "transfer.progress.completed": "Trasferiment lest!",
    "transfer.progress.default_message": "It-trasferiment tiegħek qed jiġi pproċessat. Jekk jogħġbok stenna.",
    "cards.none": "L-ebda karta maħruġa.\nIkkuntattja lill-amministratur tiegħek.",
    "cards.holder": "Detentur",
    "cards.expires": "Skad.",
    "cards.block": "Agħlaq",
    "cards.pin": "Kodiċi PIN",
    "cards.more": "Aktar",
    "cards.limits": "Limiti tal-karta",
    "cards.payment": "Ħlas",
    "cards.withdrawal": "Irtirar",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Imblukkata",
    "cards.status_active": "Attiva",
    "cards.toast.blocked": "Karta mblukkata",
    "cards.toast.unblocked": "Karta mhux imblukkata",
    "cards.toast.physical_ordered": "Karta fiżika ordnata! Kunsinna fi żmien 5–10 ijiem tax-xogħol.",
    "cards.toast.created": "Karta ġġenerata b'suċċess!",
    "cards.none_sub": "L-ebda karta maħruġa. Iġġenera l-karta virtwali tiegħek issa.",
    "cards.generate_btn": "Iġġenera l-karta tiegħi",
    "cards.create.title": "Iġġenera l-karta virtwali tiegħi",
    "cards.create.network": "Netwerk ta' ħlas",
    "cards.create.color": "Kulur tal-karta",
    "cards.create.preview_name": "ISMEK",
    "cards.create.preview_expiry": "XX/SS",
    "cards.create.loading": "Qed tiġġenera…",
    "cards.create.submit": "Iġġenera karta",
    "cards.btn.unblock": "Ibblukka",
    "cards.btn.block": "Imblukka",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fiżika",
    "cards.btn.physical_done": "Ordnata",
    "cards.limits_title": "Limiti",
    "cards.reveal.title": "Informazzjoni tal-karta",
    "cards.reveal.number": "Numru sħiħ",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Skadenza",
    "cards.reveal.warning": "Qatt taqsam din l-informazzjoni. Agħlaq din it-tieqa wara l-użu.",
    "cards.pin.title": "Kodiċi PIN",
    "cards.pin.message": "Dan il-kodiċi PIN jintuża għall-ħlasijiet online u l-irtirar tiegħek kollu. Qatt taqsmuh ma' ħadd.",
    "cards.physical.title": "Ordna karta fiżika",
    "cards.physical.fee_label": "Tariffa ta' kunsinna",
    "cards.physical.balance_label": "Il-bilanċ tiegħek",
    "cards.physical.insufficient": "Bilanċ insuffiċjenti. Jekk jogħġbok irrikarika l-kont tiegħek qabel tordna.",
    "cards.physical.info": "It-tariffa tiġi debitata mill-kont tiegħek. Kunsinna fi żmien 5–10 ijiem tax-xogħol.",
    "cards.physical.order_btn": "Ordna ({fee})",
    "cards.physical.ordering": "Qed tordna…",
    "cards.physical.order_cta": "Ordna karta fiżika",
    "cards.physical.choose_type": "Agħżel il-karta tiegħek",
    "cards.physical.features": "Vantaġġi",
    "cards.buy_virtual_cta": "Ixtri karta virtwali",
    "notif.none": "L-ebda notifika bħalissa.",
    "notif.contact_support": "Ikkuntattja l-appoġġ",
    "profile.change_photo": "Ibdel ir-ritratt",
    "profile.remove_photo": "Neħħi r-ritratt",
    "profile.email": "Email",
    "profile.phone": "Telefon",
    "profile.country": "Pajjiż",
    "profile.address": "Indirizz",
    "profile.notifs_title": "Notifiki ({count})",
    "profile.no_notifs": "L-ebda notifiki.",
    "profile.logout": "Oħroġ",
    "profile.photo_updated": "Ritratt tal-profil aġġornat",
    "profile.photo_removed": "Ritratt imneħħi",
    "profile.photo_too_large": "Immaġni kbira wisq (maks 1 MB)",
    "profile.powered": "Imħaddem minn {bank}",
    "iban.title": "L-IBAN tiegħi",
    "iban.loading": "Qed jillowdja…",
    "iban.bic": "Kodiċi BIC / SWIFT",
    "iban.share_info": "Aqsam dawn id-dettalji biex tirċievi trasferiment fil-kont tiegħek.",
    "iban.copy": "Ikkopja",
    "iban.copied": "Ikkopjat fil-clipboard",
    "iban.copy_fail": "Ma jistax jikkopja",
    "iban.close": "Agħlaq",
    "accounts.all": "Il-kontijiet tiegħek kollha ma' {bank}.",
    "accounts.primary": "Kont prinċipali",
    "accounts.savings": "Kont tat-tfaddil",
    "accounts.primary_tag": "Prinċipali",
    "accounts.secondary_tag": "Sekondarju",
    "accounts.objective": "Mira: {amount} · {pct}%",
    "accounts.copy": "Ikkopja",
    "accounts.loading": "Qed jillowdja…",
    "accounts.iban_copied": "IBAN ikkopjat",
    "accounts.copy_fail": "Ma jistax jikkopja",
    "savings.total": "Total iffrankjat",
    "savings.target_total": "Mira totali",
    "savings.progress": "Progress",
    "savings.active": "Miri attivi",
    "savings.new_goal": "Mira ġdida",
    "savings.none": "L-ebda miri tat-tfaddil għalissa.",
    "savings.none_sub": "Oħloq l-ewwel mira tiegħek biex tibda.",
    "savings.saved": "Iffrankjat:",
    "savings.goal": "Mira:",
    "savings.deadline": "Skadenza: {date}",
    "savings.deposit": "Depożitu",
    "savings.withdraw": "Irtira",
    "savings.loading": "Qed jillowdja…",
    "savings.form.title": "Mira ġdida tat-tfaddil",
    "savings.form.cancel": "Ikkanċella",
    "savings.form.create": "Oħloq",
    "savings.form.name": "Isem il-mira",
    "savings.form.name_ph": "Vjaġġ lejn il-Ġappun, Karozza, …",
    "savings.form.target": "Ammont ta' mira",
    "savings.form.initial": "Depożitu inizjali",
    "savings.form.deadline": "Skadenza (fakultattiv)",
    "savings.form.color": "Kulur",
    "savings.deposit_title": "Depożita f'\"{name}\"",
    "savings.deposit_amount": "Ammont biex tiddepożita",
    "savings.balance_hint": "Bilanċ prinċipali: {amount}",
    "savings.withdraw_prompt": "Kemm tirtira minn \"{name}\"?",
    "savings.close_confirm": "Agħlaq il-mira \"{name}\"? It-tfaddil jiġi rritornat fil-kont prinċipali tiegħek.",
    "savings.toast.created": "Mira maħluqa",
    "savings.toast.deposited": "Depożitu magħmul",
    "savings.toast.withdrawn": "Irtirar magħmul",
    "savings.toast.closed": "Mira magħluqa",
    "invest.total": "Total investit",
    "invest.profits": "Profitti akkumulati",
    "invest.count": "Investimenti",
    "invest.products": "Prodotti disponibbli",
    "invest.invest_btn": "Investi",
    "invest.my": "L-investimenti tiegħi",
    "invest.pending": "Pendenti",
    "invest.validated": "Validat",
    "invest.refused": "Rifjutat",
    "invest.withdraw.btn": "Irtira",
    "invest.withdraw.title": "Irtira l-investiment tiegħi",
    "invest.withdraw.intro": "Int dwar li tirtira l-investiment tiegħek „{product}\". Il-kapital inizjali u l-profitt akkumulat se jiġu kkreditati fil-bilanċ prinċipali tiegħek.",
    "invest.withdraw.principal": "Kapital inizjali",
    "invest.withdraw.profit": "Profitt",
    "invest.withdraw.total": "Total biex tirċievi",
    "invest.withdraw.confirm": "Ikkonferma l-irtirar",
    "invest.withdraw.warning": "Din l-azzjoni hija irriversibbli.",
    "invest.withdraw.success": "Investiment irtirat — {amount} ikkreditati fil-bilanċ.",
    "invest.withdrawn": "Irtirat",
    "invest.cta_sub": "Agħżel it-tip ta' investiment li jaqbel miegħek. Konsulent jgħinek mill-ewwel talba tiegħek.",
    "invest.form.amount": "Ammont biex tinvesti",
    "invest.form.balance": "Bilanċ disponibbli: {amount}",
    "invest.form.note": "Nota għall-konsulent tiegħek (fakultattiv)",
    "invest.form.note_ph": "Speċifika l-aspettattivi tiegħek…",
    "invest.form.advisor_info": "Konversazzjoni tinfetaħ awtomatikament mal-konsulent tiegħek.",
    "invest.form.cancel": "Ikkanċella",
    "invest.form.submit": "Ibgħat it-talba",
    "invest.toast.requested": "Talba rreġistrata — konversazzjoni infetħet mal-konsulent tiegħek.",
    "invest.p1.name": "Proprjetà Immobbli",
    "invest.p1.desc": "SCPI, karta tal-gebel, crowdfunding immobbli",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Finanzjament parteċipattiv ta' proġetti magħżula",
    "invest.p3.name": "Enerġija Rinnovabbli",
    "invest.p3.desc": "Solari, riħ u proġetti ekoloġiċi",
    "invest.p4.name": "Borża",
    "invest.p4.desc": "Azzjonijiet, ETF u fondi diversifikati",
    "invest.p5.name": "Parteċipattiv",
    "invest.p5.desc": "Self lil SMEs u intraprendituri lokali",
    "invest.p6.name": "Kripto",
    "invest.p6.desc": "Bitcoin, Ethereum u portafoll multi-valuta",
    "msg.online": "Online",
    "msg.loading": "Qed jillowdja…",
    "msg.none": "L-ebda messaġġi. Ibda l-konversazzjoni mal-konsulent tiegħek.",
    "msg.placeholder": "Ikteb il-messaġġ tiegħek…",
    "msg.attach.image": "Immaġni",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Awdjo",
    "msg.attach.file": "Fajl / Dokument",
    "msg.pdf_doc": "Dokument PDF",
    "msg.file": "Fajl",
    "msg.too_large": "Fajl kbir wisq (maks {size}).",
    "msg.withdraw": "Ħassar",
    "credits.cta_sub": "Applika direttament online — risposta fi 24 siegħa.",
    "credits.learn_more": "Isir af aktar",
    "credits.request": "Applika",
    "credits.history": "It-talbiet tiegħi",
    "credits.status.pending": "Pendenti",
    "credits.status.accepted": "Aċċettat",
    "credits.status.refused": "Rifjutat",
    "credits.chars": "Karatteristiċi",
    "credits.amount_range": "Ammont: {min}€ – {max}€",
    "credits.rate_range": "Rata tal-imgħax: {min}% – {max}% fis-sena",
    "credits.duration_range": "Durata: {min} sa {max} xhar",
    "credits.deposit_min": "Depożitu inizjali: {amount}€ minimu",
    "credits.garantie": "Garanzija: {garantie}",
    "credits.eligibility": "Kriterji tal-eliġibbiltà",
    "credits.elig.income": "Tipprova dħul regolari u stabbli",
    "credits.elig.deposit": "Agħmel depożitu inizjali ta' {amount}€",
    "credits.elig.garantie": "Ippreżenta l-garanzija: {garantie}",
    "credits.form.title": "Applikazzjoni — {name}",
    "credits.form.cancel": "Ikkanċella",
    "credits.form.submit": "Ibgħat l-applikazzjoni",
    "credits.form.amount": "Ammont mixtieq (€)",
    "credits.form.duration": "Durata (xhur)",
    "credits.form.reason": "Raġuni (fakultattiv)",
    "credits.form.reason_ph": "Iddeskrivi l-proġett tiegħek…",
    "credits.form.close": "Agħlaq",
    "credits.toast.submitted": "Applikazzjoni tal-kreditu mibgħuta — tirċievi risposta fi 24 siegħa.",
    "credits.p1.name": "Self Personali",
    "credits.p1.desc": "Biex tiffinanzja l-bżonnijiet ta' kuljum jew proġetti personali.",
    "credits.p2.name": "Ipoteka",
    "credits.p2.desc": "Biex tiffinanzja x-xiri jew il-kostruzzjoni ta' proprjetà immobbli.",
    "credits.p3.name": "Self għall-Karozza / Moto",
    "credits.p3.desc": "Ixtri vettura bis-serħan tal-moħħ.",
    "credits.p4.name": "Self għall-Istudenti",
    "credits.p4.desc": "Għajnuna finanzjarja għall-edukazzjoni terzjarja.",
    "credits.p5.name": "Self Professjonali",
    "credits.p5.desc": "Biex tappoġġja t-tkabbir ta' attività professjonali.",
    "credits.p6.name": "Self Espress",
    "credits.p6.desc": "Soluzzjoni rapida għal bżonnijiet urġenti.",
    "financing.reimbursable": "Finanzjament rimborżabbli",
    "financing.reimbursable_desc": "Appoġġ finanzjarju b'kondizzjonijiet fissi biex tniedi jew tiżviluppa attività.",
    "financing.non_reimbursable": "Finanzjament mhux rimborżabbli",
    "financing.non_reimbursable_desc": "Għal proġetti b'impatt soċjali, edukattiv jew umanitarju (NGOs, assoċjazzjonijiet).",
    "financing.cta_sub": "Soluzzjonijiet adattati biex tagħmel l-ambizzjonijiet tiegħek realtà.",
    "financing.request": "Itlob finanzjament",
    "financing.history": "It-talbiet tiegħi",
    "financing.reimbursable_label": "Rimborżabbli",
    "financing.non_reimbursable_label": "Mhux rimborżabbli",
    "financing.status.pending": "Pendenti",
    "financing.status.granted": "Mogħti",
    "financing.status.refused": "Rifjutat",
    "financing.form.title": "Applikazzjoni — {name}",
    "financing.form.cancel": "Ikkanċella",
    "financing.form.submit": "Ibgħat l-applikazzjoni",
    "financing.form.project": "Isem il-proġett",
    "financing.form.project_ph": "Il-proġett tiegħi",
    "financing.form.amount": "Ammont mitlub (€)",
    "financing.form.sector": "Settur",
    "financing.form.sector_ph": "Teknoloġija, agrikoltura, …",
    "financing.form.description": "Deskrizzjoni tal-proġett",
    "financing.form.description_ph": "Ippreżenta l-proġett tiegħek, l-għanijiet tiegħu u l-impatt mistenni tiegħu…",
    "settings.security": "Sigurtà",
    "settings.current_pw": "Password attwali",
    "settings.new_pw": "Password ġdida",
    "settings.confirm_pw": "Ikkonferma",
    "settings.submit": "Ibdel il-password",
    "settings.no_match": "Il-passwords ma jaqblux",
    "settings.changed": "Password inbidlet",
    "common.cancel": "Ikkanċella",
    "common.confirm": "Ikkonferma",
    "common.close": "Agħlaq",
    "common.save": "Salva",
    "common.loading": "Qed jillowdja…",
    "common.send": "Ibgħat",
    "common.total": "Totali",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Pagament mensili stmat.",
    "credits.rate_annual": "/ sena",
    "credits.rate_used": "Rata stmata:",
    "credits.simulation": "Stima ta' pagament mensili",
    "credits.total_cost": "Spiża totali stmata.",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Merħba",
    "common.delete": "Ħassar",
    "cards.delete.title": "Ħassar il-karta",
    "cards.delete.confirm_msg": "Din l-azzjoni hija irreversibbli. Il-karta se tiġi mħassra b'mod permanenti.",
    "cards.delete.physical_tooltip": "Karta fiżika ordnata — tħassir impossibbli",
    "cards.order.confirmed": "Ordni kkonfermata!",
    "cards.order.confirmed_body": "Il-karta fiżika tiegħek qed tiġi ppreparata. Uża dan in-numru biex isegwi l-kunsinna tiegħek.",
    "cards.order.tracking_no": "Nru. ta' ttraċċar",
    "cards.fee_deducted": "{amount} se jitnaqqas mill-bilanċ tiegħek",
    "cards.insufficient_short": "Bilanċ insuffiċjenti ({available} disponibbli)",
    "transfer.section.beneficiary": "Benefiċjarju",
    "transfer.section.details": "Trasferiment",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bank",
    "transfer.field.city": "Belt",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bank:",
    "transfer.progress.paused": "Qed jistenna l-validazzjoni",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Varjabbli",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Karta tħassret",
    "cards.feat.gold_exclusive": "Karta tad-deheb esklussiva",
    "cards.feat.cashback_05": "Cashback 0.5%",
    "cards.feat.buyer_protection": "Protezzjoni tax-xerrej",
    "cards.feat.support_standard": "Appoġġ standard",
    "cards.feat.plastic_premium": "Karta tal-plastik premium",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Assigurazzjoni tal-vjaġġar standard",
    "cards.feat.support_priority": "Appoġġ ta' prijorità",
    "cards.feat.metal_brushed": "Karta tal-metall imxarrġa",
    "cards.feat.cashback_2_unlimited": "Cashback 2% bla limitu",
    "cards.feat.travel_premium": "Assigurazzjoni tal-vjaġġar premium",
    "cards.feat.vip_lounge": "Aċċess għal-lounges VIP",
    "cards.feat.support_247": "Għajnuna 24/7",
    "auth.demo.title": "Login tal-amministratur",
    "auth.demo.subtitle": "Daħħal il-kredenzjali tal-amministratur biex tara d-demo",
    "credits.garantie.none": "Ebda",
    "credits.garantie.mortgage": "Ipoteka fuq il-proprjetà ffinanzjata",
    "credits.garantie.vehicle": "Vettura ffinanzjata",
    "credits.garantie.parental": "Ġenituri jew bankarja",
    "credits.garantie.professional": "Assi professjonali jew garanzija personali",
    "cards.reveal.pin_prompt": "Daħħal il-kodiċi PIN tiegħek ta' 4 ċifri biex turi l-informazzjoni tal-kard.",
    "cards.reveal.show": "Uri",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Iż-żewġ kodiċi PIN ma jaqblux",
    "auth.pin.digit_hint": "4 ċifri",
  
  },
  qu: {
    "notif.transfer_code.title": "Apaykuyta sutichana yupay",
    "notif.transfer_code.body": "Kay yupayta uqllay apaykuyta sutichanapaq.",
    "notif.transfer_code.expires_in": "{sec}s qhipata chinkapun",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} killakuna",
    "credits.n_months": "{n} killakuna",
    "msg.read": "Ñawinchasqa",
    "msg.sent": "Apachisqa",
    "msg.attach_aria": "Tinkichiy archivota",
    "login.device.unknown": "Mana riqsisqa navegador",
    "login.device.mobile": "Mobil",
    "login.device.tablet": "Tableta",
    "login.device.desktop": "Ordenador",
    "login.recent": "Qhipan",
    "err.invalid_credentials": "Mana allin credenciales",
    "err.bad_pin": "Mana allin PIN",
    "err.account_blocked": "Cuenta wisq'asqa",
    "err.bad_current_pw": "Kunan contraseña mana allinchu",
    "err.insufficient_funds": "Mana achkha qullqi, cuentanchikta hunt'achiy.",
    "err.amount_above_savings": "Chay qullqi waqaychasqamanta aswan hatun",
    "err.must_be_positive": "Chay numiruqa positivo kanan",
    "err.invalid_format": "Mana allin formato",
    "err.email_taken": "Email ñam apaykachasqa",
    "err.card_not_found": "Tarjeta mana tarisqa",
    "err.physical_card_already_ordered": "Físico tarjeta ñam mañasqa",
    "err.beneficiary_not_found": "Beneficiario mana tarisqa",
    "err.invalid_code": "Mana allin código",
    "err.no_transfer_code": "Kay transferenciapaq mana códigochu",
    "err.link_expired": "Mana allin enlace utaq pisipasqa",
    "err.thread_not_found": "Conversación mana tarisqa",
    "err.empty_message": "Ch'usaq willay",
    "err.file_too_large": "Archivo nishu hatun (hatun 20 MB)",
    "err.forbidden": "Mana yaykunachu",
    "err.bank_not_found": "Banco mana tarisqa",
    "err.signups_closed": "Kay bancopaq qillqakuy wisq'asqa.",
    "err.access_suspended": "Yaykuy ratullapaq tatichisqa. Ama hinachu, bankoykiman willamuy.",
    "err.notif_not_closable": "Kay willayqa mana wisq'ay atinchu.",
    "auth.signup.tagline": "Kunan p'unchaw ñuqaykuwan tinkuy",
    "auth.login.tagline": "Allin hamusqayki!",
    "auth.signup.tagline_sub": "Cuentaykita iskay minutopi kichariy, llapan premium banco serviciokunaman haykuy.",
    "auth.login.tagline_sub": "Sapayki kaq kuskaykiman haykuy, qullqikunata allin waqaychayman pusay.",
    "auth.back": "Qallariyman kutiy",
    "auth.bullet.security": "Sinchi banco amachay",
    "auth.bullet.transfers": "P'unchawpas tutapas instante virementokuna",
    "auth.bullet.investments": "Inversionkuna huñusqa ahorrowan",
    "auth.bullet.advisor": "Sapaykiman yanapaq qanchis p'unchaw semanapi",
    "auth.footer": "{bank} kallpachan",
    "auth.signup.title": "Cuentata paqarichiy",
    "auth.login.title": "Haykuy",
    "auth.field.lastName": "Apellido",
    "auth.field.firstName": "Sutikuna",
    "auth.field.country": "Suyu",
    "auth.field.country.placeholder": "— Suyuykita akllay —",
    "auth.field.phone": "Telefono",
    "auth.field.address": "Tiyana",
    "auth.field.address.placeholder": "123 Hawkaypata Ñan, Paris",
    "auth.field.email": "Email",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Yaykuna rimay",
    "auth.submit.signup": "Cuentayta paqarichiy",
    "auth.submit.login": "Haykuy",
    "auth.login.badge": "Haykuy quentaykiman",
    "auth.card.tagline": "Qhawaq masi kay",
    "auth.submit.loading": "Aysarichkan…",
    "auth.pin.title": "PIN yupay",
    "auth.pin.subtitle": "4 yupaymanta PIN yupaykita churay",
    "auth.pin.label": "PIN yupay",
    "auth.pin.confirm": "PIN Código nisqata takyachiy",
    "auth.pin.submit": "Takyachiy",
    "auth.pin.back": "Yaykunaman kutiy",
    "auth.pin.length_error": "PIN yupayqa 4 yupayniyuq kanan",
    "auth.switch.have_account": "Cuentaykiñachu kan?",
    "auth.switch.login_link": "Haykuy",
    "auth.switch.no_account": "Manaraqchu cliente kanki?",
    "auth.error.select_country": "Suyuykita akllay",
    "kyc.title": "Kikin kay rikuchiy (KYC)",
    "kyc.sub": "Documentokunaykita apachimuy cuentaykita allinchanapaq. Llapan documentokunaqa pakasqa, mana pillapas yachananpaq.",
    "kyc.id_type": "Identificacion qillqaq laya *",
    "kyc.id_card": "Identidad carnet",
    "kyc.passport": "Pasaporte",
    "kyc.driver_license": "Manejana licencia",
    "kyc.recto_passport": "Pasaporte ñawpaq uya *",
    "kyc.recto_id": "Carnet ñawpaq uya *",
    "kyc.verso": "Carnet qhipa uya *",
    "kyc.selfie": "Carnetwan makipi hap'isqa selfie *",
    "kyc.address_proof": "Tiyanaykimanta qillqa *",
    "kyc.hint_doc": "Siq'i icha PDF, 5 Mb sayanankama",
    "kyc.hint_selfie": "Sumaq foto, uya hinaspa documento rikukunan",
    "kyc.hint_address": "Factura, recibo, qillqa… (5 Mb sayanankama)",
    "kyc.upload": "Apachiy",
    "kyc.replace": "Hukwan tikray",
    "pending.title": "Cuenta suyaypi",
    "pending.body": "Mañakuyniyki chaskisqa karqa. Huk administrator dossierniykita qhawanqa, hinaspa cuentaykita pisi pachapi ruwachinqa.",
    "pending.back": "Haykunaman kutiy",
    "home.nav.features": "Ruwaykuna",
    "home.nav.security": "Amachay",
    "home.nav.invest": "Inversion",
    "home.nav.credits": "Manukuna",
    "home.nav.faq": "Tapuykuna",
    "home.nav.login": "Haykuy",
    "home.hero.tagline": "Musuq mit'a online banco",
    "home.hero.title1": "Qullqiykita kamachiy",
    "home.hero.title2": "yachaywan",
    "home.hero.sub": "Hunt'asqa banco platforma — virementokuna, ahorro, inversionkuna, manukuna celularniykimanta.",
    "home.hero.login": "Haykuy",
    "home.hero.trust.secure": "100% Amachasqa",
    "home.hero.trust.fast": "Utqay kichay",
    "home.hero.trust.encrypted": "Datos pakasqa",
    "home.hero.trust.support": "Servicio 24/7",
    "home.mockup.balance": "Qullqi atikuq",
    "home.mockup.currency": "EUR · Kuraq cuenta",
    "home.mockup.recharge": "Hunt'achiy",
    "home.mockup.send": "Apachiy",
    "home.mockup.save": "Waqaychay",
    "home.mockup.salary": "Marzo kullqi",
    "home.mockup.rent": "Abril alquiler",
    "home.mockup.investment": "Inversion",
    "home.mockup.days2": "iskay p'unchaw qhipata",
    "home.mockup.days3": "kimsa p'unchaw qhipata",
    "home.mockup.days5": "pichqa p'unchaw qhipata",
    "home.stats.accounts": "Kawsaq cuentakuna",
    "home.stats.uptime": "Kachkay",
    "home.stats.transfers": "Virementokuna",
    "home.stats.support": "Yanapay",
    "home.features.label": "Ruwaykuna",
    "home.features.title": "Llapan munasqaykimanta",
    "home.features.sub": "Hunt'asqa platforma sapa p'unchaw qullqiykita allin kamachinaykipaq.",
    "home.feature.security": "Sinchi amachay",
    "home.feature.security_body": "AES-256 pakana, iskay rikuchiq autenticacion, hinaspa 24/7 cuentaykita qhawapay.",
    "home.feature.cards": "Virtual ñataq fisica cartakuna",
    "home.feature.cards_body": "Pisi segundopi cartakunata paqarichiy icha checaq fisica cartata wasiykiman apamuchiy.",
    "home.feature.transfers": "Utqay virementokuna",
    "home.feature.transfers_body": "Maypipas pacha muyuypi qullqita apachiy ñataq chaskiy pisi segundopi.",
    "home.feature.analytics": "Kunan pacha analisis",
    "home.feature.analytics_body": "Yachay tableaukuna gastoykita qhawanapaq, hinaspa qullqiykita musyanapaq.",
    "home.feature.investments": "Huñusqa inversionkuna",
    "home.feature.investments_body": "Akllasqa inversion productokunaman cuentaykimanta direktamente haykuy.",
    "home.feature.support": "Sapallayki yanapay 24/7",
    "home.feature.support_body": "Sapa kaq banco yanapaq amachasqa mensajeriawan mayqin pachapas atikun.",
    "home.tpl.label": "Trust",
    "home.tpl.title": "They trust us",
    "home.mockup.balance_label": "Qullqi",
    "home.mockup.virement": "Qullqi kachay",
    "home.mockup.salaire": "Kullqi",
    "home.security.label": "Amachay",
    "home.security.title1": "Qullqiykikuna",
    "home.security.title2": "hunt'aq amachaypi",
    "home.security.sub": "Aswan sinchi banco amachay estandarkunata churayku sapa centimota waqaychanapaq.",
    "home.security.aes.title": "AES-256 pakana",
    "home.security.aes.body": "Llapan ancha datos kaqkuna chay kikin algoritmokunawan pakasqa, hatun internacional bancokuna hina.",
    "home.security.auth.title": "Sinchichasqa autenticacion",
    "home.security.auth.body": "Sapa haykunapi iskay rikuchiy, sapa ancha ruwaypi cuentaykipi.",
    "home.security.compliance.title": "DSP2/RGPD hunt'achiy",
    "home.security.compliance.body": "Llapan europeo kamachikuyta hunt'achiyku pagokuna serviciopi, hinaspa datos amachaypi.",
    "home.invest.label": "Inversionkuna",
    "home.invest.title": "Qullqiykita miray",
    "home.invest.sub": "6 inversion qatiykuna akllasqa atiyninwan, hinaspa hampuyninwan.",
    "home.invest.yield": "Mirachiy",
    "home.credits.label": "Manukuna",
    "home.credits.title": "Sapa proyectopaq tinkuq",
    "home.credits.sub": "Allin tasakuna, utqay kutichiy, hinaspa sapaykipaq yanapay llapan proyectoykipaq.",
    "home.credits.rate": "Tasa",
    "home.faq.label": "FAQ",
    "home.faq.title": "Achkha tapuykuna",
    "home.faq.q1": "¿Imayna cuentata kichariyman?",
    "home.faq.a1": "Inscripcion 2 minutomanta pisi. Sapayki willaykita qillqay, yaykuna rimayta akllay, hinaspa cuentaykiqa equipopuq qhawasqan qhipa kichakunqa.",
    "home.faq.q2": "¿Datosniykunaqa amachasqachu?",
    "home.faq.a2": "Arí. Llapan datos AES-256-wan pakasqa samanapi, TLS 1.3-wan apaytapi. Llapan internacional banco norma, hinaspa RGPD/DSP2 hunt'achiyku.",
    "home.faq.q3": "¿Ima laya inversionkunata qukuychu?",
    "home.faq.a3": "6 qatiykunata quykuyku: Wasi, Crowdfunding, Musuq energiyakuna, Bolsa, Tantasqa, hinaspa Crypto. Sapa producto riesgo/mirachiy chimpapurakuyninpaq akllasqa.",
    "home.faq.q4": "¿Imayna manuta mañakuyman?",
    "home.faq.a4": "Cliente kuskaykipi Manu seccionman riy, manu layata akllay, qullqi qhawa, hinaspa pacha qillqay. Dossierniyki 24 horapi yanapaqkunawan ruwakunqa.",
    "home.faq.q5": "¿Pakasqa gastokuna kanchu?",
    "home.faq.a5": "Mana ima pakasqa gasto. Llapan precio sapa ruwaypi sut'inta rikuchikun. Condicionkunaqa cliente kuskaykipi mayqin pachapas atikun.",
    "home.faq.q6": "¿Imayna soporteman tinkuyman?",
    "home.faq.a6": "Cliente kuskaykipi huñusqa mensajeria llamk'achiy, sapaykipaq banco yanapaqman tinkunaykipaq, 24/7 atikun.",
    "home.cta.title": "¿Qallariyman wakichasqachu?",
    "home.cta.sub": "{bank}-wan qullqinta kamachiq waranqa waranqa runakunawan tinkuy. Inscripcion 2 minutomanta pisi.",
    "home.footer.products": "Productokuna",
    "home.footer.services": "Servicio",
    "home.footer.access": "Utqay haykuy",
    "home.footer.accounts": "Cuentakuna",
    "home.footer.transfers": "Virementokuna",
    "home.footer.cards": "Cartakuna",
    "home.footer.savings": "Ahorro",
    "home.footer.investments": "Inversionkuna",
    "home.footer.credits": "Manukuna",
    "home.footer.financing": "Qullqi yanapay",
    "home.footer.messages": "Mensajekuna",
    "home.footer.login": "Haykuy",
    "home.footer.signup": "Cuentata kichariy",
    "home.footer.support": "Yanapay 24/7",
    "home.footer.tagline": "Online bancoyki, maypipas mayqin pachapas atikun.",
    "home.footer.copyright": "© {year} {bank} — Llapan derechokuna waqaychasqa",
    "home.footer.powered": "{bank} kallpachan",
    "app.menu_label": "Kuraq menu",
    "app.nav.dashboard": "Kamachiy tabla",
    "app.nav.accounts": "Cuentakuna",
    "app.nav.transactions": "Transaccionkuna",
    "app.nav.transfers": "Virementokuna",
    "app.nav.cards": "Cartakuna",
    "app.nav.savings": "Ahorro",
    "app.nav.investments": "Inversion",
    "app.nav.credit": "Manu",
    "app.nav.financing": "Qullqi yanapay",
    "app.nav.messages": "Mensajekuna",
    "app.nav.card_tracking": "Tarjeta qatikuy",
    "app.nav.notifications": "Willaykuna",
    "app.nav.settings": "Churaykuna",
    "app.nav.profile": "Sutiyki kay",
    "app.nav.mobile.home": "Wasi",
    "app.nav.mobile.transfers": "Virementokuna",
    "app.nav.mobile.cards": "Cartakuna",
    "app.nav.mobile.transactions": "Transaccionkuna",
    "app.nav.more.invest": "Inversionay",
    "app.user_role": "Cliente",
    "app.logout": "Lluqsiy",
    "app.search": "Maskay…",
    "app.more_title": "Astawan ruwaykuna",
    "app.more_btn": "Astawan",
    "app.loading": "Aysarichkan…",
    "app.error_back": "Haykunaman kutiy",
    "app.see_more_notifs": "Huk {count} willaykunata qhaway →",
    "page.accounts": "Cuentaykuna",
    "page.transactions": "Transaccionkuna",
    "page.transfers": "Musuq virement",
    "page.deposit": "Churay",
    "page.cards": "Cartaykuna",
    "page.savings": "Ahorro",
    "page.investments": "Inversion",
    "page.credits": "Manu",
    "page.financing": "Proyecto qullqi yanapay",
    "page.messages": "Mensajekuna",
    "page.settings": "Churaykuna",
    "page.notifications": "Kuraq willaykuna",
    "page.profile": "Sutiy kay",
    "dash.greeting": "Allin p'unchay, {name}",
    "dash.overview": "Qullqiyki qhaway — {date}",
    "dash.total_balance": "Llapan qullqi",
    "dash.this_month": "+{amount} kay killapi",
    "dash.view_iban": "IBAN-yta qhaway",
    "dash.my_accounts": "Cuentaykuna",
    "dash.primary_account": "Kuraq cuenta",
    "dash.savings_account": "Ahorro cuenta",
    "dash.remunerated": "Pagasqa",
    "dash.details": "Sapanchasqa",
    "dash.transfer_btn": "Qullqi kachay",
    "dash.quick_actions": "Utqay ruwaykuna",
    "dash.quick.deposit": "Churay",
    "deposit.title": "Churay willakuy",
    "dash.quick.transfer": "Qullqi kachay",
    "dash.quick.history": "Ñawpaq",
    "dash.quick.cards": "Cartakuna",
    "dash.quick.profile": "Sutiyki",
    "dash.monthly_budget": "Killa presupuesto",
    "dash.income": "Haykuqkuna",
    "dash.expenses": "Gastokuna",
    "dash.spending_cat": "Gasto qatiykunapi",
    "dash.no_spending": "Mana ima gastopas qillqasqa.",
    "dash.recent_tx": "Qaylla transaccionkuna",
    "dash.see_all": "Llapanta qhaway",
    "dash.no_tx": "Mana ima ruwaypas.",
    "dash.main_card": "Kuraq cartay",
    "dash.manage": "Kamachiy",
    "dash.block": "Wisq'ay",
    "dash.pin": "PIN qillqa",
    "dash.security": "Amachay",
    "dash.more": "Astawan",
    "dash.no_card": "Mana ima carta paqarichisqa",
    "dash.monthly_activity": "Killa ruway",
    "dash.operations": "Ruwaykuna",
    "dash.holder": "Hap'iq",
    "dash.expires": "Tukuy.",
    "dash.visa_label": "VISA",
    "dash.balance": "Qullqi atikuq",
    "tx.all": "Llapan",
    "tx.income": "Haykuqkuna",
    "tx.expenses": "Lluqsiqkuna",
    "tx.today": "Kunan p'unchaw",
    "tx.yesterday": "Qayna",
    "tx.none": "Mana ima transaccionpas.",
    "transfer.title": "Utqay virement",
    "transfer.recipient": "Chaskiq",
    "transfer.recipient_ph": "Suti, IBAN, telefono numero…",
    "transfer.amount": "Qullqi qhawa",
    "transfer.label": "Suti",
    "transfer.reason": "Motivo (mana imapaqpas)",
    "transfer.reason_ph": "Ej: alquiler, kutichiy…",
    "transfer.execution": "Ruway",
    "transfer.immediate": "Kunan",
    "transfer.delayed": "Qhipaman",
    "transfer.balance": "Qullqi atikuq:",
    "transfer.continue": "Qatiy",
    "transfer.confirm": "Sut'inchay",
    "transfer.cancel": "Saqiy",
    "transfer.code": "Validacion codigo",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Chaskisqa codigota qillqay virementota allinchanapaq",
    "transfer.pending.title": "Virement validacionta suyachkan",
    "transfer.pending.amount": "Qullqi qhawa:",
    "transfer.pending.to": "Maymanqa:",
    "transfer.pending.hint": "Validacion codigonki e-mail channinmi ichakuqtin notificacion campanapi apachisqa kanqa. Codigo 6 numeroyuqmi. Mana chayamuptenqa, soporteta willankuy.",
    "transfer.pending.validate": "Virementota allinchay",
    "transfer.saved": "Virement qillqasqa. Validacionpaq codigota administratorniykimanta mañakuy.",
    "transfer.done": "Virement ruwasqa",
    "transfer.validated": "Virement allinchasqa!",
    "transfer.progress.processing": "Virement ruwakuchkan…",
    "transfer.progress.completed": "Virement ruwasqa!",
    "transfer.progress.default_message": "Virementniyki ruwakuchkanmi. Ama hina kaspa suyay.",
    "cards.none": "Mana ima carta paqarichisqa.\nAdministratorniykiwan tinkuy.",
    "cards.holder": "Hap'iq",
    "cards.expires": "Tukuy.",
    "cards.block": "Wisq'ay",
    "cards.pin": "PIN qillqa",
    "cards.more": "Astawan",
    "cards.limits": "Carta sayaykuna",
    "cards.payment": "Paguy",
    "cards.withdrawal": "Hurquy",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Sayt'asqa",
    "cards.status_active": "Ch'uya",
    "cards.toast.blocked": "Carta sayt'asqa",
    "cards.toast.unblocked": "Cartap sayt'asqan qichasqa",
    "cards.toast.physical_ordered": "Hap'iq carta mañasqa! 5–10 llamk'ay p'unchawpi apamunqa.",
    "cards.toast.created": "Carta allinmi rurasqa!",
    "cards.none_sub": "Mana carta qusqachu. Kunan kikiyki virtual cartaykita rura.",
    "cards.generate_btn": "Noqap cartayta rura",
    "cards.create.title": "Noqap virtual cartayta rura",
    "cards.create.network": "Paguy llayt'a",
    "cards.create.color": "Cartap simiynin",
    "cards.create.preview_name": "SUTIYKI",
    "cards.create.preview_expiry": "MM/WW",
    "cards.create.loading": "Rurashayki…",
    "cards.create.submit": "Carta rura",
    "cards.btn.unblock": "Qicha",
    "cards.btn.block": "Sayt'a",
    "cards.btn.info": "Willakuy",
    "cards.btn.physical": "Hap'iq",
    "cards.btn.physical_done": "Mañasqa",
    "cards.limits_title": "Saywakunam",
    "cards.reveal.title": "Cartap willakuynin",
    "cards.reveal.number": "Tukuy nímiru",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Tukupuynin",
    "cards.reveal.warning": "Manapuni kay willakuyta hukchachu. Kay qhatu wisq'aspa huñusqayta qipa.",
    "cards.pin.title": "PIN qillqa",
    "cards.pin.message": "Kay PIN qillqaqa tukuy llayt'api paguyniykipaq hurqunapaqpis. Manapuni pipas willaychu.",
    "cards.physical.title": "Hap'iq carta mañay",
    "cards.physical.fee_label": "Apamuy paguy",
    "cards.physical.balance_label": "Qullqikiyki",
    "cards.physical.insufficient": "Mana allin qullqichu. Ama mañaspa awpaqta qullqikita yapay.",
    "cards.physical.info": "Paguyqa cuentaykirayku qichasqa kanqa. 5–10 llamk'ay p'unchawpi apamunqa.",
    "cards.physical.order_btn": "Mañay ({fee})",
    "cards.physical.ordering": "Mañashayki…",
    "cards.physical.order_cta": "Mañay huk físico tarjeta",
    "cards.physical.choose_type": "Akllakuy qam tarjetaykita",
    "cards.physical.features": "Allin kayninkuna",
    "cards.buy_virtual_cta": "Virtual tarjetata rantiy",
    "notif.none": "Mana ima willaypas kunan kashan.",
    "notif.contact_support": "Yanapakuyta qayariy",
    "profile.change_photo": "Fotota tikray",
    "profile.remove_photo": "Fotota hurquy",
    "profile.email": "Email",
    "profile.phone": "Telefono",
    "profile.country": "Suyu",
    "profile.address": "Tiyana",
    "profile.notifs_title": "Willaykuna ({count})",
    "profile.no_notifs": "Mana ima willaypas.",
    "profile.logout": "Lluqsiy",
    "profile.photo_updated": "Sutiyki fotonchaki musuqchasqa",
    "profile.photo_removed": "Foto hurqusqa",
    "profile.photo_too_large": "Siq'i sinchi hatun (1 Mb sayanankama)",
    "profile.powered": "{bank} kallpachan",
    "iban.title": "IBAN-y",
    "iban.loading": "Aysarichkan…",
    "iban.bic": "BIC / SWIFT codigo",
    "iban.share_info": "Kay willayta rakiy cuentaykipi virementota chaskinaykipaq.",
    "iban.copy": "Kupiay",
    "iban.copied": "Tabla apaqpi kupiasqa",
    "iban.copy_fail": "Mana kupiay atisqachu",
    "iban.close": "Wichq'ay",
    "accounts.all": "Llapan {bank}-pi kichasqa cuentaykikuna.",
    "accounts.primary": "Kuraq cuenta",
    "accounts.savings": "Ahorro cuenta",
    "accounts.primary_tag": "Kuraq",
    "accounts.secondary_tag": "Iskay kaq",
    "accounts.objective": "Tukukuy: {amount} · {pct}%",
    "accounts.copy": "Kupiay",
    "accounts.loading": "Aysarichkan…",
    "accounts.iban_copied": "IBAN kupiasqa",
    "accounts.copy_fail": "Mana kupiay atisqachu",
    "savings.total": "Llapan waqaychasqa",
    "savings.target_total": "Llapan tukukuy",
    "savings.progress": "Ñawpaqman puriy",
    "savings.active": "Kawsaq tukukuykuna",
    "savings.new_goal": "Musuq tukukuy",
    "savings.none": "Manaraq ahorropaq tukukuy.",
    "savings.none_sub": "Ñawpaq tukukuyniykita paqarichiy qallarinaykipaq.",
    "savings.saved": "Waqaychasqa:",
    "savings.goal": "Tukukuy:",
    "savings.deadline": "Pacha tukukuy: {date}",
    "savings.deposit": "Churay",
    "savings.withdraw": "Hurquy",
    "savings.loading": "Aysarichkan…",
    "savings.form.title": "Musuq ahorro tukukuy",
    "savings.form.cancel": "Saqiy",
    "savings.form.create": "Paqarichiy",
    "savings.form.name": "Tukukuy suti",
    "savings.form.name_ph": "Japon puriy, Carro, …",
    "savings.form.target": "Tukukuy qullqi",
    "savings.form.initial": "Qallariy churay",
    "savings.form.deadline": "Pacha tukukuy (mana imapaqpas)",
    "savings.form.color": "Llimp'i",
    "savings.deposit_title": "\"{name}\"-man churay",
    "savings.deposit_amount": "Churanapaq qullqi qhawa",
    "savings.balance_hint": "Kuraq qullqi: {amount}",
    "savings.withdraw_prompt": "¿Hayk'a \"{name}\"-manta hurquy?",
    "savings.close_confirm": "¿\"{name}\" tukukuyta wisq'ay? Ahorroqa kuraq cuentaykiman kutichikunqa.",
    "savings.toast.created": "Tukukuy paqarichisqa",
    "savings.toast.deposited": "Churay ruwasqa",
    "savings.toast.withdrawn": "Hurquy ruwasqa",
    "savings.toast.closed": "Tukukuy wisq'asqa",
    "invest.total": "Llapan churaykusqa",
    "invest.profits": "Huñusqa miraykuna",
    "invest.count": "Inversionkuna",
    "invest.products": "Atikuq productokuna",
    "invest.invest_btn": "Churaykuy",
    "invest.my": "Inversionniykuna",
    "invest.pending": "Suyaypi",
    "invest.validated": "Allinchasqa",
    "invest.refused": "Mana munasqa",
    "invest.withdraw.btn": "Hurquy",
    "invest.withdraw.title": "Inversionniyta hurquy",
    "invest.withdraw.intro": "\"{product}\" inversionniykita hurqunki. Qallariy kapital, ganawnchikkunawan kuska, hatun saldoykiman yapakunqa.",
    "invest.withdraw.principal": "Qallariy kapital",
    "invest.withdraw.profit": "Ganaway",
    "invest.withdraw.total": "Llapan chaskina",
    "invest.withdraw.confirm": "Hurqunaykipaq sutinchay",
    "invest.withdraw.warning": "Kay rurayta mana kutichinkichu.",
    "invest.withdraw.success": "Inversion hurqurqa — {amount} saldoykiman yapakurqa.",
    "invest.withdrawn": "Hurqusqa",
    "invest.cta_sub": "Munasqayki inversion layata akllay. Huk yanapaq mañakuyniykimantapacha yanapasunki.",
    "invest.form.amount": "Churaykunapaq qullqi qhawa",
    "invest.form.balance": "Qullqi atikuq: {amount}",
    "invest.form.note": "Yanapaqpaq qillqa (mana imapaqpas)",
    "invest.form.note_ph": "Suyasqaykita willay…",
    "invest.form.advisor_info": "Huk rimanakuy yanapaqniykiwan kichakunqa.",
    "invest.form.cancel": "Saqiy",
    "invest.form.submit": "Mañakuyta apachiy",
    "invest.toast.requested": "Mañakuy qillqasqa — huk rimanakuy yanapaqniykiwan kicharisqa karqa.",
    "invest.p1.name": "Wasichakuy",
    "invest.p1.desc": "SCPI, rumi-papel, wasichakuy crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Akllasqa proyectokunaman tantasqa qullqi yanapay",
    "invest.p3.name": "Musuq energiya",
    "invest.p3.desc": "Inti, wayra, q'umir proyecto",
    "invest.p4.name": "Bolsa",
    "invest.p4.desc": "Acciones, ETF, askha fondo",
    "invest.p5.name": "Tantasqa",
    "invest.p5.desc": "PME-man manu, ñuqaykupaq llaqta empresariokuna",
    "invest.p6.name": "Crypto",
    "invest.p6.desc": "Bitcoin, Ethereum, askha qullqi portafolio",
    "msg.online": "Onlinepi",
    "msg.loading": "Aysarichkan…",
    "msg.none": "Mana ima mensajepas. Yanapaqniykiwan rimanakuyta qallariy.",
    "msg.placeholder": "Mensajeykita qillqay…",
    "msg.attach.image": "Siq'i",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Archivo / Documento",
    "msg.pdf_doc": "PDF documento",
    "msg.file": "Archivo",
    "msg.too_large": "Archivo sinchi hatun ({size} sayanankama).",
    "msg.withdraw": "Hurquy",
    "credits.cta_sub": "Mañakuyniykita directamente online ruway — 24 horapi kutichiy.",
    "credits.learn_more": "Astawan yachay",
    "credits.request": "Mañakuy",
    "credits.history": "Mañakuyniykuna",
    "credits.status.pending": "Suyaypi",
    "credits.status.accepted": "Chaskisqa",
    "credits.status.refused": "Mana munasqa",
    "credits.chars": "Caracteristicakuna",
    "credits.amount_range": "Qullqi qhawa: {min}€ – {max}€",
    "credits.rate_range": "Interes tasa: {min}% – {max}% sapa wata",
    "credits.duration_range": "Pacha: {min} – {max} killa",
    "credits.deposit_min": "Qallariy churay: {amount}€ pisi",
    "credits.garantie": "Garantia: {garantie}",
    "credits.eligibility": "Atiy condicionkuna",
    "credits.elig.income": "Hunt'a, allin haykuqkunata rikuchiy",
    "credits.elig.deposit": "{amount}€-pi qallariy churayta ruway",
    "credits.elig.garantie": "Garantiata rikuchiy: {garantie}",
    "credits.form.title": "Mañakuy — {name}",
    "credits.form.cancel": "Saqiy",
    "credits.form.submit": "Mañakuyta apachiy",
    "credits.form.amount": "Munasqa qullqi qhawa (€)",
    "credits.form.duration": "Pacha (killa)",
    "credits.form.reason": "Motivo (mana imapaqpas)",
    "credits.form.reason_ph": "Proyectoykita willay…",
    "credits.form.close": "Wichq'ay",
    "credits.toast.submitted": "Manu mañakuy apachisqa — 24 horapi kutichita chaskinki.",
    "credits.p1.name": "Sapanchasqa manu",
    "credits.p1.desc": "Sapa p'unchaw munasqakunata utaq sapaykipaq proyectota yanapanapaq.",
    "credits.p2.name": "Wasi manu",
    "credits.p2.desc": "Wasi rantinapaq utaq wasi ruwanapaq.",
    "credits.p3.name": "Carro / Moto manu",
    "credits.p3.desc": "Vehiculota allin sunquwan rantiy.",
    "credits.p4.name": "Estudiante manu",
    "credits.p4.desc": "Kuraq estudiokunapaq qullqi yanapay.",
    "credits.p5.name": "Profesional manu",
    "credits.p5.desc": "Profesional ruwana wiñayta yanapanapaq.",
    "credits.p6.name": "Utqay manu",
    "credits.p6.desc": "Utqay munasqakunapaq utqay solucion.",
    "financing.reimbursable": "Kutichinapaq qullqi yanapay",
    "financing.reimbursable_desc": "Tikray mana kaq condicionkunawan qullqi yanapay, huk ruwayta qallarinapaq utaq wiñachinapaq.",
    "financing.non_reimbursable": "Mana kutichinapaq qullqi yanapay",
    "financing.non_reimbursable_desc": "Social, yachachiy utaq runa khuyay proyectopaq (ONG, asociacionkuna).",
    "financing.cta_sub": "Suyaykiqniykita hunt'achinapaq tinkuq soluciones.",
    "financing.request": "Qullqi yanapayta mañakuy",
    "financing.history": "Mañakuyniykuna",
    "financing.reimbursable_label": "Kutichinapaq",
    "financing.non_reimbursable_label": "Mana kutichinapaq",
    "financing.status.pending": "Suyaypi",
    "financing.status.granted": "Quyusqa",
    "financing.status.refused": "Mana munasqa",
    "financing.form.title": "Mañakuy — {name}",
    "financing.form.cancel": "Saqiy",
    "financing.form.submit": "Mañakuyta apachiy",
    "financing.form.project": "Proyecto suti",
    "financing.form.project_ph": "Proyectoy",
    "financing.form.amount": "Mañakusqa qullqi qhawa (€)",
    "financing.form.sector": "Sector",
    "financing.form.sector_ph": "Tech, agricultura, …",
    "financing.form.description": "Proyecto willay",
    "financing.form.description_ph": "Proyectoykita willay, tukukuyninkunata, suyasqa impactonta…",
    "settings.security": "Amachay",
    "settings.current_pw": "Kunan yaykuna rimay",
    "settings.new_pw": "Musuq yaykuna rimay",
    "settings.confirm_pw": "Sut'inchay",
    "settings.submit": "Yaykuna rimayta tikray",
    "settings.no_match": "Yaykuna rimaykuna mana chimpapurachu",
    "settings.changed": "Yaykuna rimay tikrasqa",
    "common.cancel": "Saqiy",
    "common.confirm": "Sut'inchay",
    "common.close": "Wichq'ay",
    "common.save": "Waqaychay",
    "common.loading": "Aysarichkan…",
    "common.send": "Apachiy",
    "common.total": "Llapan",
    "admin.wallet.balance": "Saldo disponible",
    "admin.wallet.history": "Historial",
    "admin.wallet.no_history": "Sin transacciones",
    "admin.wallet.title": "Mi billetera",
    "admin.wallet.topup": "Recargar",
    "beneficiary.pick": "Seleccionar",
    "beneficiary.save_this": "Guardar este beneficiario",
    "beneficiary.saved": "Beneficiarios guardados",
    "beneficiary.saved_ok": "Beneficiario guardado",
    "card.color.amber": "Ámbar",
    "card.color.cyan": "Cian",
    "card.color.emerald": "Esmeralda",
    "card.color.forest": "Bosque",
    "card.color.gold": "Oro",
    "card.color.lavender": "Lavanda",
    "card.color.night": "Noche",
    "card.color.orange": "Naranja",
    "card.color.pink": "Rosa",
    "card.color.royal": "Azul real",
    "card.color.ruby": "Rubí",
    "card.color.slate": "Pizarra",
    "card.color.steel": "Acero",
    "card.color.terracotta": "Terracota",
    "card.color.violet": "Violeta",
    "common.other": "Otro",
    "common.search": "Buscar",
    "content.translated": "Contenido traducido automáticamente",
    "credits.monthly_payment": "Cuota mensual estimada",
    "credits.rate_annual": "/ año",
    "credits.rate_used": "Tasa estimada:",
    "credits.simulation": "Simulación de cuota mensual",
    "credits.total_cost": "Coste total estimado",
    "demo.banner": "Modo demostración — ninguna acción real",
    "demo.disabled": "Acción no disponible en modo demo",
    "deposit.default_instructions": "Contacte a su gestor para los detalles del depósito.",
    "error.back_home": "Volver al inicio",
    "error.bank_not_found": "Banco no encontrado",
    "error.unknown": "Error desconocido",
    "kyc.file_read_error": "No se puede leer el archivo",
    "kyc.file_too_large": "Archivo demasiado grande (máx. 5 MB)",
    "login.history": "Historial de conexiones",
    "login.history.empty": "Ninguna conexión registrada.",
    "notif.dismiss": "Cerrar",
    "notif.locked": "Mensaje del banco",
    "tracking.error.not_found": "Número de seguimiento no encontrado. Verifique e intente de nuevo.",
    "tracking.label": "Seguimiento",
    "tracking.status.delivered": "Entregado",
    "tracking.status.preparation": "En preparación",
    "tracking.status.shipped": "Enviado",
    "tracking.status.transit": "En tránsito",
    "transfer.code.copied": "Copiado",
    "transfer.code.copy": "Copiar",
    "transfer.code.received": "Su código de validación",
    "dash.welcome": "Allin hamunayki",
    "common.delete": "Qichuy",
    "cards.delete.title": "Tarjeta qichuy",
    "cards.delete.confirm_msg": "Kay ruwayqa mana kutirichinapaqchu. Tarjeta wiñaypaq qichusqa kanqa.",
    "cards.delete.physical_tooltip": "Tarjeta fisika tapusqa — mana qichunapaqchu",
    "cards.order.confirmed": "Pedido confirmado!",
    "cards.order.confirmed_body": "Card fisikuyki allichasqa kachkan. Kay numerota llamk'achiy waykuchinaykita qhawanankupaq.",
    "cards.order.tracking_no": "Qhaway numero",
    "cards.fee_deducted": "Saldoykimanta {amount} iskaytasqa kanqa",
    "cards.insufficient_short": "Saldo mana aypaqchu ({available} lliu)",
    "transfer.section.beneficiary": "Ñawpaq tiyaq",
    "transfer.section.details": "Riqsinakuy",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banco",
    "transfer.field.city": "Llaqta",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banco:",
    "transfer.progress.paused": "Rurasqata suyaspa",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "T'ikrakuq",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",
    "cards.deleted": "Tarjeta chinkachisqa",
    "cards.feat.gold_exclusive": "Quri tarjeta sapaq",
    "cards.feat.cashback_05": "Cashback 0,5%",
    "cards.feat.buyer_protection": "Rantiq amachay",
    "cards.feat.support_standard": "Yanapaq qichunalla",
    "cards.feat.plastic_premium": "Premium plastiku tarjeta",
    "cards.feat.cashback_1": "Cashback 1%",
    "cards.feat.travel_standard": "Puriy seguro estandarpaq",
    "cards.feat.support_priority": "Ñawpaqniyuq yanapay",
    "cards.feat.metal_brushed": "Pichasqa metal tarjeta",
    "cards.feat.cashback_2_unlimited": "Cashback 2% mana atisqa",
    "cards.feat.travel_premium": "Premium puriy seguro",
    "cards.feat.vip_lounge": "VIP huch'uy wasi yaykuy",
    "cards.feat.support_247": "Yanapay 24/7",
    "auth.demo.title": "Kamachiq yaykuy",
    "auth.demo.subtitle": "Yaykuy admin sutiykita demo qhawanapaq",
    "credits.garantie.none": "Mana imapas",
    "credits.garantie.mortgage": "Qullqisqa wasimanta hipoteca",
    "credits.garantie.vehicle": "Qullqisqa antawa",
    "credits.garantie.parental": "Yayaymanta utaq bancomanta",
    "credits.garantie.professional": "Llamk'ay kapitalninkunamanta utaq sapan garantía",
    "cards.reveal.pin_prompt": "4 yupayniyuq PIN codigniykita yaykuchiy tarjetapa willakuyninkunata qawanaykipaq.",
    "cards.reveal.show": "Rikuchiy",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Iskay PIN-kuna mana tupanchu",
    "auth.pin.digit_hint": "4 yupana",
  
  },

  gn: {
    "notif.transfer_code.title": "Mba'epuru ñemoneĩha pápeha",
    "notif.transfer_code.body": "Ehupika ko papapy mba'epuru ñemoneĩ hag̃ua.",
    "notif.transfer_code.expires_in": "Okañy {sec}s rire",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} jasy",
    "credits.n_months": "{n} jasy",
    "msg.read": "Ojeleéma",
    "msg.sent": "Oñemondóma",
    "msg.attach_aria": "Embojoaju marandurenda",
    "login.device.unknown": "Navegador ojekuaa'ỹva",
    "login.device.mobile": "Pumbyry'i",
    "login.device.tablet": "Tableta",
    "login.device.desktop": "Ordenador",
    "login.recent": "Ko'aga ramo",
    "err.invalid_credentials": "Credenciales hekopegua'ỹ",
    "err.bad_pin": "PIN hekopegua'ỹ",
    "err.account_blocked": "Tetepy ojejoko",
    "err.bad_current_pw": "Pe contraseña ko'aĝagua hekopegua'ỹ",
    "err.insufficient_funds": "Ndaipóri pirapire, eñongatu nde cuentape.",
    "err.amount_above_savings": "Pe sambyhy ohasa pe ñongatupy oĩva",
    "err.must_be_positive": "Pe valor tekotevẽ ha'e positivo",
    "err.invalid_format": "Formato hekopegua'ỹ",
    "err.email_taken": "Email ojepuruma",
    "err.card_not_found": "Tarjeta ndojejuhúi",
    "err.physical_card_already_ordered": "Tarjeta físico ojejerure aréma",
    "err.beneficiary_not_found": "Beneficiario ndojejuhúi",
    "err.invalid_code": "Código hekopegua'ỹ",
    "err.no_transfer_code": "Ndaipóri código ko transferencia rehegua",
    "err.link_expired": "Juajuha hekopegua'ỹ térã ohasáma",
    "err.thread_not_found": "Ñomongeta ndojejuhúi",
    "err.empty_message": "Marandu nandi",
    "err.file_too_large": "Marandurenda tuicheterei (max 20 MB)",
    "err.forbidden": "Ndojeheja'asyiry",
    "err.bank_not_found": "Banco ndojejuhúi",
    "err.signups_closed": "Ñemboheraguapy oñemboty ko banco rehegua.",
    "err.access_suspended": "Jeike sapy'aite oñembyaty. Eñe'ẽmína ne banco ndive.",
    "err.notif_not_closable": "Ko marandu ndaikatúi oñemboty.",
    "auth.signup.tagline": "Eñembojoaju oréndive ko árape",
    "auth.login.tagline": "Tereg̃uahẽ porãite!",
    "auth.signup.tagline_sub": "Eipe'a ne kuénta mokõi aravo'ípe ha eike opaite ore servicio bancario premiumpe.",
    "auth.login.tagline_sub": "Eike ne kuárto personalpe ha eñangareko ne viru rehe seguridad pavẽme.",
    "auth.back": "Ejevy henda guasúpe",
    "auth.bullet.security": "Seguridad bancaria militar-icha",
    "auth.bullet.transfers": "Virement pya'e 24h/24",
    "auth.bullet.investments": "Inversión ha ahorro oñembojoajupyre",
    "auth.bullet.advisor": "Conseillero personal oĩ 7 ára semanápe",
    "auth.footer": "{bank} ombopu'aka",
    "auth.signup.title": "Ejapo peteĩ kuenta",
    "auth.login.title": "Eike",
    "auth.field.lastName": "Téra ryepy",
    "auth.field.firstName": "Téra",
    "auth.field.country": "Tetã",
    "auth.field.country.placeholder": "— Eiporavo ne tetã —",
    "auth.field.phone": "Pumbyry",
    "auth.field.address": "Tendaite",
    "auth.field.address.placeholder": "123 Paix Rape, París",
    "auth.field.email": "Email",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Ñe'ẽñemi",
    "auth.submit.signup": "Ejapo che kuenta",
    "auth.submit.login": "Eike",
    "auth.login.badge": "Eike zure kontura",
    "auth.card.tagline": "Ne peteĩ reko aty",
    "auth.submit.loading": "Oñembohenda…",
    "auth.pin.title": "PIN papapy",
    "auth.pin.subtitle": "Emoinge nde PIN papapy 4 papapýva",
    "auth.pin.label": "PIN papapy",
    "auth.pin.confirm": "PIN Código moneĩ",
    "auth.pin.submit": "Emoneĩ",
    "auth.pin.back": "Eguahẽjey jeike-pe",
    "auth.pin.length_error": "PIN papapy oguerekova’erã 4 papapy",
    "auth.switch.have_account": "¿Reguerekómapa kuenta?",
    "auth.switch.login_link": "Eike",
    "auth.switch.no_account": "¿Naneclienteíri gueteri?",
    "auth.error.select_country": "Eiporavo ne tetã",
    "kyc.title": "Ñehesa'ỹijo identidad (KYC)",
    "kyc.sub": "Emondo ne kuatia umi ne kuenta omyendyha. Opaite umi kuatia oñemonge'õ ha ñemoñemíme.",
    "kyc.id_type": "Identidad kuatia mba'éichagua *",
    "kyc.id_card": "Identidad carnet",
    "kyc.passport": "Pasaporte",
    "kyc.driver_license": "Mbo'epyre permiso",
    "kyc.recto_passport": "Pasaporte tenondegua *",
    "kyc.recto_id": "Carnet tenondegua *",
    "kyc.verso": "Carnet hapykuegua *",
    "kyc.selfie": "Selfie carnet ndive po pópe *",
    "kyc.address_proof": "Tendaite ñehechauka *",
    "kyc.hint_doc": "Ta'anga térã PDF, 5 Mo peve",
    "kyc.hint_selfie": "Ta'anga porã, rova ha kuatia ojehecha porã",
    "kyc.hint_address": "Factura, recibo, kuatia… (5 Mo peve)",
    "kyc.upload": "Emondo",
    "kyc.replace": "Embyekovia",
    "pending.title": "Kuenta oha'arõhápe",
    "pending.body": "Ne ñembojerure ojerrecibimi. Peteĩ administrador omaña ne kuatia rehe ha omyendýta ne kuenta sa'iete arive.",
    "pending.back": "Ejevy ñemboikepe",
    "home.nav.features": "Mba'éichapa omba'apo",
    "home.nav.security": "Seguridad",
    "home.nav.invest": "Inversión",
    "home.nav.credits": "Crédito",
    "home.nav.faq": "Porandu",
    "home.nav.login": "Eike",
    "home.hero.tagline": "Banco online generación pyahu",
    "home.hero.title1": "Eñangareko ne virúre",
    "home.hero.title2": "akãhatãme",
    "home.hero.sub": "Plataforma bancaria oĩmbáva — virement, ahorro, inversión, crédito ne smartphone-gui.",
    "home.hero.login": "Eike",
    "home.hero.trust.secure": "100% Seguro",
    "home.hero.trust.fast": "Jeipe'a pya'e",
    "home.hero.trust.encrypted": "Datos ñemoñemĩva",
    "home.hero.trust.support": "Servicio 24/7",
    "home.mockup.balance": "Viru oĩva",
    "home.mockup.currency": "EUR · Kuenta tuichavéva",
    "home.mockup.recharge": "Embohenyhẽ",
    "home.mockup.send": "Emondo",
    "home.mockup.save": "Eñongatu",
    "home.mockup.salary": "Marzo repy",
    "home.mockup.rent": "Abril alquiler",
    "home.mockup.investment": "Inversión",
    "home.mockup.days2": "ohasáma mokõi ára",
    "home.mockup.days3": "ohasáma mbohapy ára",
    "home.mockup.days5": "ohasáma po ára",
    "home.stats.accounts": "Kuenta oikóva",
    "home.stats.uptime": "Oĩ pukukue",
    "home.stats.transfers": "Virement",
    "home.stats.support": "Pytyvõ",
    "home.features.label": "Mba'apokuéra",
    "home.features.title": "Opaite reikotevẽva",
    "home.features.sub": "Plataforma oĩmbáva oñembosako'i ne viru ñangareko hag̃ua ára ko'ẽme.",
    "home.feature.security": "Seguridad militar",
    "home.feature.security_body": "AES-256 ñemoñemĩ, autenticación mokõihárupi, ha ñemañangareko 24/7 ne kuentápe.",
    "home.feature.cards": "Carta virtual ha física",
    "home.feature.cards_body": "Ejapo carta sa'i segundo-pe térã eñembyetýrõ carta física añetegua ógape.",
    "home.feature.transfers": "Virement pya'e",
    "home.feature.transfers_body": "Emondo ha embou viru opa rupi yvy ape ári sa'i segundo-pe.",
    "home.feature.analytics": "Ñehesa'ỹijo ko'ag̃aitéva",
    "home.feature.analytics_body": "Tableau iñarandúva eñangareko hag̃ua ne gasto rehe ha eikuaa hag̃ua ne viru.",
    "home.feature.investments": "Inversión oñembojoajupyre",
    "home.feature.investments_body": "Eike inversión producto oñeiporavova'ekuépe ne kuentágui.",
    "home.feature.support": "Pytyvõ ne tee 24/7",
    "home.feature.support_body": "Conseillero bancario personal oĩ opa árape mensajería seguro rupive.",
    "home.tpl.label": "Trust",
    "home.tpl.title": "They trust us",
    "home.mockup.balance_label": "Viru",
    "home.mockup.virement": "Jegueraha",
    "home.mockup.salaire": "Repy",
    "home.security.label": "Seguridad",
    "home.security.title1": "Ne virukuéra",
    "home.security.title2": "seguridad pavẽme",
    "home.security.sub": "Roiporu seguridad bancaria estándar hatãvéva oñeñongatu hag̃ua opaite centimo.",
    "home.security.aes.title": "AES-256 ñemoñemĩ",
    "home.security.aes.body": "Opaite datos ojeporúva oñeñemoñemĩ algoritmo umi banco internacional tuicháva oiporúva ndive.",
    "home.security.auth.title": "Autenticación mbaretevéva",
    "home.security.auth.body": "Mokõihárupi identidad ñehecha sapa eikévo, ha sapa mba'apo guasu ne kuentápe.",
    "home.security.compliance.title": "DSP2/RGPD jehupyty",
    "home.security.compliance.body": "Rojapo opaite leikuéra europea servicio jehepyme'ẽ rehegua ha datos jeñongatu rehegua.",
    "home.invest.label": "Inversión",
    "home.invest.title": "Embohetave ne viru",
    "home.invest.sub": "6 categoría inversión oñeiporavóva ipotĩ ha hekoroñetépe.",
    "home.invest.yield": "Hetave",
    "home.credits.label": "Crédito",
    "home.credits.title": "Soluciones omoñe'ẽva sapa proyecto",
    "home.credits.sub": "Tasa porã, ñembohovái pya'e, ha pytyvõ ne tee opaite ne proyecto.",
    "home.credits.rate": "Tasa",
    "home.faq.label": "FAQ",
    "home.faq.title": "Porandu oñeporanduvéva",
    "home.faq.q1": "¿Mba'éichapa aipe'a peteĩ kuenta?",
    "home.faq.a1": "Inscripción ohasa mokõi aravo'i guive. Emoĩ ne datos personalkuéra, eiporavo peteĩ ñe'ẽñemi ha ne kuenta omyendýta ore equipo ohecharire.",
    "home.faq.q2": "¿Ñembyaty datos seguro?",
    "home.faq.a2": "Heẽ. Opaite datos oñeñemoñemĩ AES-256-pe oñepytyvõhápe ha TLS 1.3-pe ogueru-jajeguávo. Rojapo norma bancaria internacional ha RGPD/DSP2 leikuéra.",
    "home.faq.q3": "¿Mba'eichagua inversión peñongatu?",
    "home.faq.a3": "Roñongatu 6 categoría: Imobiliaria, Crowdfunding, Energía pyahu, Bolsa, Participativo ha Crypto. Sapa producto oñeiporavo iriesgo/hetavehára rehe.",
    "home.faq.q4": "¿Mba'éichapa ajerure crédito?",
    "home.faq.a4": "Tereho Crédito secciónpe ne espacio cliente, eiporavo préstamo mba'eichagua, emoĩ viru ha pukukue. Ne expediente oñembosako'íta 24 hora-pe ore conseillero rupive.",
    "home.faq.q5": "¿Oĩpa gastos oñekañỹva?",
    "home.faq.a5": "Ndaipóri gasto oñekañỹva. Opaite hepy ojehecha porã sapa mba'apópe. Condiciones oñehupyty ne espacio clientepe opa árape.",
    "home.faq.q6": "¿Mba'éichapa apytyvõ rohenduka?",
    "home.faq.a6": "Eiporu mensajería oĩva ne espacio clientepe eñehenduka hag̃ua ne conseillero bancario personalpe, oĩ 24h/24 ha 7 ára semanápe.",
    "home.cta.title": "¿Eñembosako'íma reñepyrũ hag̃ua?",
    "home.cta.sub": "Eñembojoaju heta usuario oñangarekóva iviru rehe {bank} ndive. Inscripción ohasa mokõi aravo'i guive.",
    "home.footer.products": "Producto",
    "home.footer.services": "Servicio",
    "home.footer.access": "Jeike pya'e",
    "home.footer.accounts": "Kuenta",
    "home.footer.transfers": "Virement",
    "home.footer.cards": "Carta",
    "home.footer.savings": "Ahorro",
    "home.footer.investments": "Inversión",
    "home.footer.credits": "Crédito",
    "home.footer.financing": "Financiamiento",
    "home.footer.messages": "Marandu",
    "home.footer.login": "Eike",
    "home.footer.signup": "Eipe'a kuenta",
    "home.footer.support": "Pytyvõ 24/7",
    "home.footer.tagline": "Ne banco online, oĩ opa rupi ha opa árape.",
    "home.footer.copyright": "© {year} {bank} — Opaite derecho oñeñongatu",
    "home.footer.powered": "{bank} ombopu'aka",
    "app.menu_label": "Menú tuichavéva",
    "app.nav.dashboard": "Tablero",
    "app.nav.accounts": "Kuenta",
    "app.nav.transactions": "Transacción",
    "app.nav.transfers": "Virement",
    "app.nav.cards": "Carta",
    "app.nav.savings": "Ahorro",
    "app.nav.investments": "Inversión",
    "app.nav.credit": "Crédito",
    "app.nav.financing": "Financiamiento",
    "app.nav.messages": "Marandu",
    "app.nav.card_tracking": "Tarjeta jehecha",
    "app.nav.notifications": "Momarandu",
    "app.nav.settings": "Ñembosako'i",
    "app.nav.profile": "Mba'éichapa che",
    "app.nav.mobile.home": "Óga",
    "app.nav.mobile.transfers": "Virement",
    "app.nav.mobile.cards": "Carta",
    "app.nav.mobile.transactions": "Transacción",
    "app.nav.more.invest": "Inversión",
    "app.user_role": "Cliente",
    "app.logout": "Ejejupi",
    "app.search": "Eheka…",
    "app.more_title": "Hetave mba'apo",
    "app.more_btn": "Hetave",
    "app.loading": "Oñembohenda…",
    "app.error_back": "Ejevy ñemboikepe",
    "app.see_more_notifs": "Ehecha {count} momarandu hetave →",
    "page.accounts": "Che kuenta",
    "page.transactions": "Transacción",
    "page.transfers": "Virement pyahu",
    "page.deposit": "Mba'epyre",
    "page.cards": "Che carta",
    "page.savings": "Ahorro",
    "page.investments": "Inversión",
    "page.credits": "Crédito",
    "page.financing": "Proyecto financiamiento",
    "page.messages": "Marandu",
    "page.settings": "Ñembosako'i",
    "page.notifications": "Momarandu tuichavéva",
    "page.profile": "Che kuatia",
    "dash.greeting": "Mba'éichapa, {name}",
    "dash.overview": "Ñehecha ne viru rehegua — {date}",
    "dash.total_balance": "Viru opaite",
    "dash.this_month": "+{amount} ko jasýpe",
    "dash.view_iban": "Ehecha che IBAN",
    "dash.my_accounts": "Che kuenta",
    "dash.primary_account": "Kuenta tuichavéva",
    "dash.savings_account": "Ahorro kuenta",
    "dash.remunerated": "Ojehepyme'ẽva",
    "dash.details": "Detalle",
    "dash.transfer_btn": "Jegueraha",
    "dash.quick_actions": "Mba'apo pya'e",
    "dash.quick.deposit": "Mba'epyre",
    "deposit.title": "Mba'epyre marandu",
    "dash.quick.transfer": "Jegueraha",
    "dash.quick.history": "Tembiasakue",
    "dash.quick.cards": "Che carta",
    "dash.quick.profile": "Mba'éichapa che",
    "dash.monthly_budget": "Presupuesto mensual",
    "dash.income": "Ojeguahẽva",
    "dash.expenses": "Gasto",
    "dash.spending_cat": "Gasto categoría rupive",
    "dash.no_spending": "Ndaipóri gasto oñemboguapy.",
    "dash.recent_tx": "Transacción ag̃aguáva",
    "dash.see_all": "Ehecha opaite",
    "dash.no_tx": "Ndaipóri mba'apo.",
    "dash.main_card": "Che carta tuichavéva",
    "dash.manage": "Eñangareko",
    "dash.block": "Eñembotyma",
    "dash.pin": "PIN código",
    "dash.security": "Seguridad",
    "dash.more": "Hetave",
    "dash.no_card": "Ndaipóri carta osẽva",
    "dash.monthly_activity": "Mba'apo jasy pukukue",
    "dash.operations": "Mba'apo",
    "dash.holder": "Ipy'aguasu",
    "dash.expires": "Opa.",
    "dash.visa_label": "VISA",
    "dash.balance": "Viru oĩva",
    "tx.all": "Opaite",
    "tx.income": "Ojeguahẽva",
    "tx.expenses": "Osẽva",
    "tx.today": "Ko árape",
    "tx.yesterday": "Kuehe",
    "tx.none": "Ndaipóri transacción.",
    "transfer.title": "Virement pya'e",
    "transfer.recipient": "Ohupytýva",
    "transfer.recipient_ph": "Téra, IBAN, pumbyry papapy…",
    "transfer.amount": "Mboheta",
    "transfer.label": "Téra",
    "transfer.reason": "Motivo (ndoikoteveíva)",
    "transfer.reason_ph": "Téc.: alquiler, jehepyme'ẽjevy…",
    "transfer.execution": "Mba'apo",
    "transfer.immediate": "Sapy'aite",
    "transfer.delayed": "Upérire",
    "transfer.balance": "Viru oĩva:",
    "transfer.continue": "Eku'e",
    "transfer.confirm": "Emoneĩ",
    "transfer.cancel": "Eheja",
    "transfer.code": "Código validación",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Emoĩ ne código reguahẽva eñemoneĩ hag̃ua virement",
    "transfer.pending.title": "Virement oha'arõ validación",
    "transfer.pending.amount": "Mboheta:",
    "transfer.pending.to": "Ohohápe:",
    "transfer.pending.hint": "Ne código validación-pe reikuaa ha'e oñemondo e-mail rehe térã notificación campana rehe. Código 6 número orekóva. Nde ndoikeséiramo, japo contacto soporte-pe.",
    "transfer.pending.validate": "Emoneĩ virement",
    "transfer.saved": "Virement oñemboguapýma. Ejerure código ne administrátor-pe emoneĩ hag̃ua.",
    "transfer.done": "Virement ojejapóma",
    "transfer.validated": "Virement oñemoneĩma!",
    "transfer.progress.processing": "Ojeprocesa virement…",
    "transfer.progress.completed": "Virement ojejapóma!",
    "transfer.progress.default_message": "Nde virement ojeprocesa hína. Eha'arõmi.",
    "cards.none": "Ndaipóri carta osẽva.\nEñehenduka ne administrador-pe.",
    "cards.holder": "Ipy'aguasu",
    "cards.expires": "Opa.",
    "cards.block": "Eñembotyma",
    "cards.pin": "PIN código",
    "cards.more": "Hetave",
    "cards.limits": "Carta jepyrũmby",
    "cards.payment": "Jehepyme'ẽ",
    "cards.withdrawal": "Ñenohẽ",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Mboty'aha",
    "cards.status_active": "Jeheraite",
    "cards.toast.blocked": "Karapi mboty'aha",
    "cards.toast.unblocked": "Karapi jojáva",
    "cards.toast.physical_ordered": "Karapi teko'a oñeporandu! Omboguatase 5–10 arapypegua aramonte.",
    "cards.toast.created": "Karapi ojejapóva porãite!",
    "cards.none_sub": "Ndaipóri karapi ojejapóva. Ejapo nde karapi virtual ko'ág̃a.",
    "cards.generate_btn": "Ejapo che karapípe",
    "cards.create.title": "Ejapo che karapi virtual",
    "cards.create.network": "Red jehepyme'ẽrã",
    "cards.create.color": "Sa'y karapi",
    "cards.create.preview_name": "NDERÉRA",
    "cards.create.preview_expiry": "MM/AA",
    "cards.create.loading": "Ojejapóva…",
    "cards.create.submit": "Ejapo karapi",
    "cards.btn.unblock": "Ejoja",
    "cards.btn.block": "Emboty",
    "cards.btn.info": "Marandu",
    "cards.btn.physical": "Teko'a",
    "cards.btn.physical_done": "Oñeporandu",
    "cards.limits_title": "Tape'ã",
    "cards.reveal.title": "Marandu karapi rehegua",
    "cards.reveal.number": "Papapy opaichagua",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Opáva",
    "cards.reveal.warning": "Ani emuña ko marandu avave ndive. Ejokua ko ovetã oipurúvo rire.",
    "cards.pin.title": "PIN código",
    "cards.pin.message": "Ko PIN código ojeporu nde jehepyme'ẽ ha ñenohẽ opaichagua peg̃uarã. Ani embou avave ndive.",
    "cards.physical.title": "Eporandu karapi teko'a",
    "cards.physical.fee_label": "Jehapykuerã jehepyme'ẽ",
    "cards.physical.balance_label": "Nde peteĩ",
    "cards.physical.insufficient": "Nde peteĩ'ỹva. Emombareteve nde cuenta eporandu mboyve.",
    "cards.physical.info": "Jehepyme'ẽrã ojegueropo nde cuentagui. Omboguatase 5–10 arapypegua aramonte.",
    "cards.physical.order_btn": "Eporandu ({fee})",
    "cards.physical.ordering": "Oñeporandu…",
    "cards.physical.order_cta": "Jerure peteĩ tarjeta física",
    "cards.physical.choose_type": "Eiporavo nde tarjetápe",
    "cards.physical.features": "Mbaéporãvéva",
    "cards.buy_virtual_cta": "Jogua peteĩ tarjeta virtual",
    "notif.none": "Ndaipóri momarandu ko'ag̃aite.",
    "notif.contact_support": "Ñomongeta pytyvõrä rehe",
    "profile.change_photo": "Embyekovia ta'anga",
    "profile.remove_photo": "Enohẽ ta'anga",
    "profile.email": "Email",
    "profile.phone": "Pumbyry",
    "profile.country": "Tetã",
    "profile.address": "Tendaite",
    "profile.notifs_title": "Momarandu ({count})",
    "profile.no_notifs": "Ndaipóri momarandu.",
    "profile.logout": "Ejejupi",
    "profile.photo_updated": "Ne ta'anga personal oñemyatyrõ",
    "profile.photo_removed": "Ta'anga oñenohẽ",
    "profile.photo_too_large": "Ta'anga tuichaiterei (1 Mo peve)",
    "profile.powered": "{bank} ombopu'aka",
    "iban.title": "Che IBAN",
    "iban.loading": "Oñembohenda…",
    "iban.bic": "BIC / SWIFT código",
    "iban.share_info": "Emyasãi ko'ã datos ehupyty hag̃ua virement ne kuentápe.",
    "iban.copy": "Embohasakopy",
    "iban.copied": "Oñembohasáma kuatia ryepýpe",
    "iban.copy_fail": "Ndaikatúi ojembohasa",
    "iban.close": "Embotyke",
    "accounts.all": "Opaite ne kuenta ne reipe'akue {bank}-pe.",
    "accounts.primary": "Kuenta tuichavéva",
    "accounts.savings": "Ahorro kuenta",
    "accounts.primary_tag": "Tuichavéva",
    "accounts.secondary_tag": "Mokõiha",
    "accounts.objective": "Objetivo: {amount} · {pct}%",
    "accounts.copy": "Embohasakopy",
    "accounts.loading": "Oñembohenda…",
    "accounts.iban_copied": "IBAN oñembohasa",
    "accounts.copy_fail": "Ndaikatúi ojembohasa",
    "savings.total": "Opaite oñeñongatu",
    "savings.target_total": "Objetivo opaite",
    "savings.progress": "Tape jejapo",
    "savings.active": "Objetivo oikóva",
    "savings.new_goal": "Objetivo pyahu",
    "savings.none": "Ndaipóri ahorro objetivo ag̃aite.",
    "savings.none_sub": "Ejapo ne ñepyrũrã objetivo eñepyrũ hag̃ua.",
    "savings.saved": "Oñeñongatu:",
    "savings.goal": "Objetivo:",
    "savings.deadline": "Ára paha: {date}",
    "savings.deposit": "Emoinge",
    "savings.withdraw": "Enohẽ",
    "savings.loading": "Oñembohenda…",
    "savings.form.title": "Ahorro objetivo pyahu",
    "savings.form.cancel": "Eheja",
    "savings.form.create": "Ejapo",
    "savings.form.name": "Objetivo téra",
    "savings.form.name_ph": "Japón jeho, mba'yruguata, …",
    "savings.form.target": "Mboheta ohupytýrã",
    "savings.form.initial": "Ñepyrũrã ñemoĩ",
    "savings.form.deadline": "Ára paha (ndoikoteveíva)",
    "savings.form.color": "Color",
    "savings.deposit_title": "Emoinge \"{name}\"-pe",
    "savings.deposit_amount": "Mboheta emoĩva'erã",
    "savings.balance_hint": "Viru tuichavéva: {amount}",
    "savings.withdraw_prompt": "¿Mboýpa enohẽ \"{name}\"-gui?",
    "savings.close_confirm": "¿Embotyke objetivo \"{name}\"? Ahorro ojejevýta ne kuenta tuichavévape.",
    "savings.toast.created": "Objetivo ojejapóma",
    "savings.toast.deposited": "Ñemoinge ojejapóma",
    "savings.toast.withdrawn": "Ñenohẽ ojejapóma",
    "savings.toast.closed": "Objetivo oñembotyma",
    "invest.total": "Opaite ojeinvertíva",
    "invest.profits": "Hepyme'ẽ omyatyrõpyre",
    "invest.count": "Inversión",
    "invest.products": "Producto oĩva",
    "invest.invest_btn": "Einvertí",
    "invest.my": "Che inversión",
    "invest.pending": "Oha'arõhápe",
    "invest.validated": "Oñemoneĩma",
    "invest.refused": "Oñembotove",
    "invest.withdraw.btn": "Egueru jey",
    "invest.withdraw.title": "Che inversion agueru jey",
    "invest.withdraw.intro": "Reguerújeýta nde inversion \"{product}\". Kapital ñepyrũ ha ganansia oñembyatypa va'ekue oñembojoja ne saldo guasúpe.",
    "invest.withdraw.principal": "Kapital ñepyrũ",
    "invest.withdraw.profit": "Ganansia",
    "invest.withdraw.total": "Mbaeñembyatypa pe rerekóva",
    "invest.withdraw.confirm": "Emboaje rerujey",
    "invest.withdraw.warning": "Ko mba'apo ndaikatuvéima ojejapo jey.",
    "invest.withdraw.success": "Inversion ojeguerujey — {amount} oikéma saldope.",
    "invest.withdrawn": "Ojeguerujey",
    "invest.cta_sub": "Eiporavo inversión mba'eichagua oikóva ndéve g̃uarã. Peteĩ conseillero ne pytyvõta ne ñembojerure guive.",
    "invest.form.amount": "Mboheta einvertítava",
    "invest.form.balance": "Viru oĩva: {amount}",
    "invest.form.note": "Kuatia ne conseillero peg̃uarã (ndoikoteveíva)",
    "invest.form.note_ph": "Emoĩ ne ñeha'arõ…",
    "invest.form.advisor_info": "Peteĩ ñemongeta oñembojuajúta ne conseillero ndive.",
    "invest.form.cancel": "Eheja",
    "invest.form.submit": "Emondo ñembojerure",
    "invest.toast.requested": "Ñembojerure oñemboguapýma — peteĩ ñemongeta oñembojuaju ne conseillero ndive.",
    "invest.p1.name": "Imobiliaria",
    "invest.p1.desc": "SCPI, ita-kuatia, imobiliario crowdfunding",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Financiamiento participativo proyecto oñeiporavóva",
    "invest.p3.name": "Energía pyahu",
    "invest.p3.desc": "Kuarahy, yvytu ha proyecto hovyũ",
    "invest.p4.name": "Bolsa",
    "invest.p4.desc": "Acción, ETF ha fondo iñambuéva",
    "invest.p5.name": "Participativo",
    "invest.p5.desc": "Préstamo PME-pe ha empresario tetãpýpe",
    "invest.p6.name": "Crypto",
    "invest.p6.desc": "Bitcoin, Ethereum ha cartera multi-viru",
    "msg.online": "Online",
    "msg.loading": "Oñembohenda…",
    "msg.none": "Ndaipóri marandu. Eñepyrũ ñemongeta ne conseillero ndive.",
    "msg.placeholder": "Ehai ne marandu…",
    "msg.attach.image": "Ta'anga",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Audio",
    "msg.attach.file": "Archivo / Kuatia",
    "msg.pdf_doc": "Kuatia PDF",
    "msg.file": "Archivo",
    "msg.too_large": "Archivo tuichaiterei ({size} peve).",
    "msg.withdraw": "Enohẽ",
    "credits.cta_sub": "Ejapo ne ñembojerure directamente online — ñembohovái 24 hora-pe.",
    "credits.learn_more": "Eikuaave",
    "credits.request": "Ejerure",
    "credits.history": "Che ñembojerure",
    "credits.status.pending": "Oha'arõhápe",
    "credits.status.accepted": "Oñemoneĩ",
    "credits.status.refused": "Oñembotove",
    "credits.chars": "Característica",
    "credits.amount_range": "Mboheta: {min}€ – {max}€",
    "credits.rate_range": "Tasa interés: {min}% – {max}% sapa ary",
    "credits.duration_range": "Pukukue: {min}-gui {max} jasy peve",
    "credits.deposit_min": "Ñepyrũrã ñemoinge: {amount}€ michĩvéva",
    "credits.garantie": "Garantía: {garantie}",
    "credits.eligibility": "Condiciones eikéta hag̃ua",
    "credits.elig.income": "Ehechauka virúre ojeguahẽva mantéva ha mbaretéva",
    "credits.elig.deposit": "Ejapo ñepyrũrã ñemoinge {amount}€",
    "credits.elig.garantie": "Ehechauka garantía: {garantie}",
    "credits.form.title": "Ñembojerure — {name}",
    "credits.form.cancel": "Eheja",
    "credits.form.submit": "Emondo ñembojerure",
    "credits.form.amount": "Mboheta reikotevẽva (€)",
    "credits.form.duration": "Pukukue (jasy)",
    "credits.form.reason": "Motivo (ndoikoteveíva)",
    "credits.form.reason_ph": "Emombe'u ne proyecto…",
    "credits.form.close": "Embotyke",
    "credits.toast.submitted": "Crédito ñembojerure oñemondóma — rehupytýta ñembohovái 24 hora-pe.",
    "credits.p1.name": "Préstamo Personal",
    "credits.p1.desc": "Eipuru hag̃ua ára ko'ẽme tekotevẽvape térã proyecto personalpe.",
    "credits.p2.name": "Préstamo Imobiliaria",
    "credits.p2.desc": "Ejogua térã ejapo hag̃ua peteĩ óga.",
    "credits.p3.name": "Préstamo Mba'yru / Moto",
    "credits.p3.desc": "Ejogua mba'yru py'aguapýpe.",
    "credits.p4.name": "Préstamo Mbo'epyre",
    "credits.p4.desc": "Viru pytyvõ estudio superior-pe g̃uarã.",
    "credits.p5.name": "Préstamo Profesional",
    "credits.p5.desc": "Eñembotuicha hag̃ua ne mba'apo profesional.",
    "credits.p6.name": "Préstamo Pya'e",
    "credits.p6.desc": "Solución pya'e tekotevẽme g̃uarã.",
    "financing.reimbursable": "Financiamiento ojejejevýva",
    "financing.reimbursable_desc": "Pytyvõ financiero condiciones mantéva ndive eñepyrũ térã eñembotuicha hag̃ua peteĩ mba'apo.",
    "financing.non_reimbursable": "Financiamiento ndojejevýiva",
    "financing.non_reimbursable_desc": "Proyecto impacto social, mbo'epyre térã yvyporakuái peg̃uarã (ONG, asociación).",
    "financing.cta_sub": "Solución oñembosako'íva eguahẽ hag̃ua ne suyãme.",
    "financing.request": "Ejerure financiamiento",
    "financing.history": "Che ñembojerure",
    "financing.reimbursable_label": "Ojejevýva",
    "financing.non_reimbursable_label": "Ndojejevýiva",
    "financing.status.pending": "Oha'arõhápe",
    "financing.status.granted": "Oñeme'ẽma",
    "financing.status.refused": "Oñembotove",
    "financing.form.title": "Ñembojerure — {name}",
    "financing.form.cancel": "Eheja",
    "financing.form.submit": "Emondo ñembojerure",
    "financing.form.project": "Proyecto téra",
    "financing.form.project_ph": "Che proyecto",
    "financing.form.amount": "Mboheta ojejerure (€)",
    "financing.form.sector": "Sector",
    "financing.form.sector_ph": "Tech, agricultura, …",
    "financing.form.description": "Proyecto mombe'u",
    "financing.form.description_ph": "Emombe'u ne proyecto, ijobjetivokuéra ha ipoder oñeha'arõva…",
    "settings.security": "Seguridad",
    "settings.current_pw": "Ñe'ẽñemi ko'ag̃agua",
    "settings.new_pw": "Ñe'ẽñemi pyahu",
    "settings.confirm_pw": "Emoneĩ",
    "settings.submit": "Embyekovia ñe'ẽñemi",
    "settings.no_match": "Ñe'ẽñemi nomoirũi ojupe",
    "settings.changed": "Ñe'ẽñemi oñemyatyrõ",
    "common.cancel": "Eheja",
    "common.confirm": "Emoneĩ",
    "common.close": "Embotyke",
    "common.save": "Eñongatu",
    "common.loading": "Oñembohenda…",
    "common.send": "Emondo",
    "common.total": "Opaite",
    "admin.wallet.balance": "Saldo disponível",
    "admin.wallet.history": "Histórico",
    "admin.wallet.no_history": "Sem transações",
    "admin.wallet.title": "Minha carteira",
    "admin.wallet.topup": "Recarregar",
    "beneficiary.pick": "Selecionar",
    "beneficiary.save_this": "Guardar este beneficiário",
    "beneficiary.saved": "Beneficiários guardados",
    "beneficiary.saved_ok": "Beneficiário guardado",
    "card.color.amber": "Âmbar",
    "card.color.cyan": "Ciano",
    "card.color.emerald": "Esmeralda",
    "card.color.forest": "Floresta",
    "card.color.gold": "Ouro",
    "card.color.lavender": "Lavanda",
    "card.color.night": "Noite",
    "card.color.orange": "Laranja",
    "card.color.pink": "Rosa",
    "card.color.royal": "Azul real",
    "card.color.ruby": "Rubi",
    "card.color.slate": "Ardósia",
    "card.color.steel": "Aço",
    "card.color.terracotta": "Terracota",
    "card.color.violet": "Violeta",
    "common.other": "Outro",
    "common.search": "Pesquisar",
    "content.translated": "Conteúdo traduzido automaticamente",
    "credits.monthly_payment": "Prestação mensal estimada",
    "credits.rate_annual": "/ ano",
    "credits.rate_used": "Taxa estimativa:",
    "credits.simulation": "Simulação de prestação mensal",
    "credits.total_cost": "Custo total estimado",
    "demo.banner": "Modo demonstração — nenhuma ação real",
    "demo.disabled": "Ação não disponível em modo demo",
    "deposit.default_instructions": "Contacte o seu gestor para as modalidades de depósito.",
    "error.back_home": "Voltar ao início",
    "error.bank_not_found": "Banco não encontrado",
    "error.unknown": "Erro desconhecido",
    "kyc.file_read_error": "Leitura do arquivo impossível",
    "kyc.file_too_large": "Arquivo muito grande (máx. 5 MB)",
    "login.history": "Histórico de conexões",
    "login.history.empty": "Nenhuma conexão registrada.",
    "notif.dismiss": "Fechar",
    "notif.locked": "Mensagem do banco",
    "tracking.error.not_found": "Número de rastreio não encontrado. Verifique e tente novamente.",
    "tracking.label": "Rastreio",
    "tracking.status.delivered": "Entregue",
    "tracking.status.preparation": "Em preparação",
    "tracking.status.shipped": "Enviado",
    "tracking.status.transit": "Em trânsito",
    "transfer.code.copied": "Copiado",
    "transfer.code.copy": "Copiar",
    "transfer.code.received": "O seu código de validação",
    "dash.welcome": "Eguahé porã",
    "common.delete": "Mbogueje",
    "cards.delete.title": "Tarjeta mbogueje",
    "cards.delete.confirm_msg": "Ko mba'e apopy ndojereivéi. Tarjeta opa arape mboguejéta.",
    "cards.delete.physical_tooltip": "Tarjeta física roguerohory — ndaikatuvéi mbogueje",
    "cards.order.confirmed": "Pedido confirmado!",
    "cards.order.confirmed_body": "Nde tarjeta fisika omoheña oĩ. Ejeporu ko número pegua nde entrega rehekávo.",
    "cards.order.tracking_no": "Jeheka número",
    "cards.fee_deducted": "{amount} ojeisá ne saldo pegua",
    "cards.insufficient_short": "Saldo'y'm ({available} oĩ)",
    "transfer.section.beneficiary": "Mba'e jeporu",
    "transfer.section.details": "Mondaho",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Banco",
    "transfer.field.city": "Táva",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Banco:",
    "transfer.progress.paused": "Oĩ jeheka'ápe",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Iñambuéva",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",

    "cards.deleted": "Karta oñembogue",

    "cards.feat.gold_exclusive": "Karta yvyguy oñe'ẽva",

    "cards.feat.cashback_05": "Cashback 0,5%",

    "cards.feat.buyer_protection": "Mboguakaha jeguahúpe",

    "cards.feat.support_standard": "Pytyvõ tee",

    "cards.feat.plastic_premium": "Karta yvy'eo premium",

    "cards.feat.cashback_1": "Cashback 1%",

    "cards.feat.travel_standard": "Aseguro guata teemi",

    "cards.feat.support_priority": "Pytyvõ tenondegua",

    "cards.feat.metal_brushed": "Karta metal pererĩme",

    "cards.feat.cashback_2_unlimited": "Cashback 2% jepe'a",

    "cards.feat.travel_premium": "Aseguro guata premium",

    "cards.feat.vip_lounge": "VIP koty jeike",

    "cards.feat.support_247": "Pytyvõ 24/7",

    "auth.demo.title": "Sãmbyhyhára jeike",

    "auth.demo.subtitle": "Eike admin réra demo ehecha hag̃ua",
    "credits.garantie.none": "Mba'eveichagua",
    "credits.garantie.mortgage": "Hipoteka óga rehegua oñefinansiáva",
    "credits.garantie.vehicle": "Mba'yru oñefinansiáva",
    "credits.garantie.parental": "Túva térã bancaria",
    "credits.garantie.professional": "Mba'apo mba'e térã garante personal",
    "cards.reveal.pin_prompt": "Emoinge nde código PIN 4 papapy rehegua ehechauka hag̃ua kuatia'atã marandu.",
    "cards.reveal.show": "Ehechauka",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "Mokõi PIN-kódi ndojuajéi",
    "auth.pin.digit_hint": "4 papapy",
  
  },

  ht: {
    "notif.transfer_code.title": "Kòd validasyon transfè",
    "notif.transfer_code.body": "Kopye kòd sa a pou valide transfè a.",
    "notif.transfer_code.expires_in": "Disparèt nan {sec}s",
    /* ── i18n additions: banking pages + error translations ── */
    "credits.duration_chip": "{min}–{max} mwa",
    "credits.n_months": "{n} mwa",
    "msg.read": "Li",
    "msg.sent": "Voye",
    "msg.attach_aria": "Tache yon fichye",
    "login.device.unknown": "Navigatè enkoni",
    "login.device.mobile": "Mobil",
    "login.device.tablet": "Tablèt",
    "login.device.desktop": "Òdinatè",
    "login.recent": "Resan",
    "err.invalid_credentials": "Idantifyan envalid",
    "err.bad_pin": "PIN ki pa kòrèk",
    "err.account_blocked": "Kont bloke",
    "err.bad_current_pw": "Modpas aktyèl la pa kòrèk",
    "err.insufficient_funds": "Fon ki pa sifizan, tanpri ranpli kont ou.",
    "err.amount_above_savings": "Montan an depase ekonomi disponib la",
    "err.must_be_positive": "Valè a dwe pozitif",
    "err.invalid_format": "Fòma envalid",
    "err.email_taken": "Imèl deja itilize",
    "err.card_not_found": "Kat la pa jwenn",
    "err.physical_card_already_ordered": "Kat fizik deja kòmande",
    "err.beneficiary_not_found": "Benefisyè pa jwenn",
    "err.invalid_code": "Kòd envalid",
    "err.no_transfer_code": "Pa gen kòd pou transfè sa a",
    "err.link_expired": "Lyen envalid oswa ekspire",
    "err.thread_not_found": "Konvèsasyon pa jwenn",
    "err.empty_message": "Mesaj vid",
    "err.file_too_large": "Fichye twò gwo (maks 20 MB)",
    "err.forbidden": "Aksè refize",
    "err.bank_not_found": "Bank pa jwenn",
    "err.signups_closed": "Enskripsyon fèmen pou bank sa a.",
    "err.access_suspended": "Aksè sispann tanporèman. Tanpri kontakte labank ou.",
    "err.notif_not_closable": "Yo paka fèmen notifikasyon sa a.",
    "auth.signup.tagline": "Vin jwenn nou jodi a",
    "auth.login.tagline": "Byenvini ankò!",
    "auth.signup.tagline_sub": "Ouvri kont ou nan 2 minit epi jwenn aksè ak tout sèvis bankè premium nou yo.",
    "auth.login.tagline_sub": "Aksè espas pèsonèl ou epi jere finans ou an sekirite.",
    "auth.back": "Retounen lakay",
    "auth.bullet.security": "Sekirite bankè nivo militè",
    "auth.bullet.transfers": "Virman enstantane 24h/24",
    "auth.bullet.investments": "Envestisman ak ekonomi entegre",
    "auth.bullet.advisor": "Konseye pèsonèl disponib 7 jou sou 7",
    "auth.footer": "Pwopoulse pa {bank}",
    "auth.signup.title": "Kreye yon kont",
    "auth.login.title": "Koneksyon",
    "auth.field.lastName": "Non fanmi",
    "auth.field.firstName": "Prenon",
    "auth.field.country": "Peyi",
    "auth.field.country.placeholder": "— Chwazi peyi ou —",
    "auth.field.phone": "Telefòn",
    "auth.field.address": "Adrès",
    "auth.field.address.placeholder": "123 Ri Lapè, Pari",
    "auth.field.email": "Imèl",
    "auth.field.email.placeholder": "@mail",
    "auth.field.password": "Modpas",
    "auth.submit.signup": "Kreye kont mwen",
    "auth.submit.login": "Konekte",
    "auth.login.badge": "Konekte nan kont ou",
    "auth.card.tagline": "Espas kliyan ou",
    "auth.submit.loading": "Chajman…",
    "auth.pin.title": "Kòd PIN",
    "auth.pin.subtitle": "Antre Kòd PIN 4 chif ou",
    "auth.pin.label": "Kòd PIN",
    "auth.pin.confirm": "Konfime Kòd PIN",
    "auth.pin.submit": "Konfime",
    "auth.pin.back": "Tounen nan koneksyon",
    "auth.pin.length_error": "Kòd PIN dwe gen 4 chif",
    "auth.switch.have_account": "Ou gen yon kont deja?",
    "auth.switch.login_link": "Koneksyon",
    "auth.switch.no_account": "Ou poko kliyan?",
    "auth.error.select_country": "Chwazi peyi ou",
    "kyc.title": "Verifikasyon idantite (KYC)",
    "kyc.sub": "Telechaje dokiman ou yo pou valide kont ou. Tout dokiman yo chifre epi konfidansyèl.",
    "kyc.id_type": "Tip pyès idantite *",
    "kyc.id_card": "Kat idantite",
    "kyc.passport": "Paspò",
    "kyc.driver_license": "Pèmi kondui",
    "kyc.recto_passport": "Devan paspò a *",
    "kyc.recto_id": "Devan pyès la *",
    "kyc.verso": "Dèyè pyès la *",
    "kyc.selfie": "Selfi ak pyès idantite a nan men *",
    "kyc.address_proof": "Prèv adrès *",
    "kyc.hint_doc": "Imaj oswa PDF, maksimòm 5 Mo",
    "kyc.hint_selfie": "Foto klè, figi ak dokiman vizib",
    "kyc.hint_address": "Bòdwo, releve, atestasyon… (maksimòm 5 Mo)",
    "kyc.upload": "Telechaje",
    "kyc.replace": "Ranplase",
    "pending.title": "Kont annatant",
    "pending.body": "Demann ou an byen resevwa. Yon administratè ap verifye dosye ou epi aktive kont ou talè.",
    "pending.back": "Retounen sou koneksyon",
    "home.nav.features": "Fonksyonalite",
    "home.nav.security": "Sekirite",
    "home.nav.invest": "Envestisman",
    "home.nav.credits": "Kredi",
    "home.nav.faq": "FAQ",
    "home.nav.login": "Konekte",
    "home.hero.tagline": "Bank an liy nouvèl jenerasyon",
    "home.hero.title1": "Jere lajan ou",
    "home.hero.title2": "ak entèlijans",
    "home.hero.sub": "Yon platfòm bankè konplè — virman, ekonomi, envestisman ak kredi depi smatfòn ou.",
    "home.hero.login": "Konekte",
    "home.hero.trust.secure": "100% an sekirite",
    "home.hero.trust.fast": "Ouvèti rapid",
    "home.hero.trust.encrypted": "Done chifre",
    "home.hero.trust.support": "Sèvis 24/7",
    "home.mockup.balance": "Balans disponib",
    "home.mockup.currency": "EUR · Kont prensipal",
    "home.mockup.recharge": "Rechaje",
    "home.mockup.send": "Voye",
    "home.mockup.save": "Ekonomize",
    "home.mockup.salary": "Salè Mas",
    "home.mockup.rent": "Lwaye Avril",
    "home.mockup.investment": "Envestisman",
    "home.mockup.days2": "gen 2 jou",
    "home.mockup.days3": "gen 3 jou",
    "home.mockup.days5": "gen 5 jou",
    "home.stats.accounts": "Kont aktif",
    "home.stats.uptime": "Disponibilite",
    "home.stats.transfers": "Virman",
    "home.stats.support": "Sipò",
    "home.features.label": "Fonksyonalite",
    "home.features.title": "Tout sa ou bezwen",
    "home.features.sub": "Yon platfòm konplè ki fèt pou senpilifye jesyon finans ou chak jou.",
    "home.feature.security": "Sekirite militè",
    "home.feature.security_body": "Chifreman AES-256, otantifikasyon de faktè ak siveyans 24/7 kont ou.",
    "home.feature.cards": "Kat vityèl ak fizik",
    "home.feature.cards_body": "Emèt kat nan kèk segonn oswa kòmande yon vrè kat fizik delivre lakay ou.",
    "home.feature.transfers": "Virman ekspres",
    "home.feature.transfers_body": "Voye ak resevwa lajan toupatou nan lemonn nan kèk segonn.",
    "home.feature.analytics": "Analiz an tan reyèl",
    "home.feature.analytics_body": "Tablodbò entèlijan pou swiv depans ou epi prevwa finans ou.",
    "home.feature.investments": "Envestisman entegre",
    "home.feature.investments_body": "Aksè a pwodwi envestisman seleksyone dirèkteman depi kont ou.",
    "home.feature.support": "Sipò dedye 24/7",
    "home.feature.support_body": "Yon konseye bankè pèsonèl disponib nenpòt lè atravè mesajri sekirize.",
    "home.tpl.label": "Trust",
    "home.tpl.title": "They trust us",
    "home.mockup.balance_label": "Balans",
    "home.mockup.virement": "Virman",
    "home.mockup.salaire": "Salè",
    "home.security.label": "Sekirite",
    "home.security.title1": "Finans ou",
    "home.security.title2": "an sekirite absoli",
    "home.security.sub": "Nou aplike estanda sekirite bankè ki pi strik yo pou pwoteje chak santim.",
    "home.security.aes.title": "Chifreman AES-256",
    "home.security.aes.body": "Tout done sansib yo chifre ak menm algoritm yo gwo bank entènasyonal yo itilize.",
    "home.security.auth.title": "Otantifikasyon ranfòse",
    "home.security.auth.body": "Doub verifikasyon idantite chak fwa ou konekte ak pou chak operasyon sansib sou kont ou.",
    "home.security.compliance.title": "Konfòmite DSP2/RGPD",
    "home.security.compliance.body": "Nou respekte tout règleman ewopeyen yo nan zafè sèvis peman ak pwoteksyon done.",
    "home.invest.label": "Envestisman",
    "home.invest.title": "Fè lajan ou pwodi",
    "home.invest.sub": "6 kategori envestisman seleksyone pou potansyèl yo ak fyabilite yo.",
    "home.invest.yield": "Randman",
    "home.credits.label": "Kredi",
    "home.credits.title": "Solisyon adapte pou chak pwojè",
    "home.credits.sub": "To konpetitif, repons rapid ak yon akonpayman pèsonalize pou tout pwojè ou yo.",
    "home.credits.rate": "To",
    "home.faq.label": "FAQ",
    "home.faq.title": "Kesyon frekan",
    "home.faq.q1": "Kijan pou ouvri yon kont?",
    "home.faq.a1": "Enskripsyon an pran mwens pase 2 minit. Ou jis bezwen ranpli enfòmasyon pèsonèl ou, chwazi yon modpas epi kont ou ap aktive apre ekip nou valide l.",
    "home.faq.q2": "Èske done mwen yo an sekirite?",
    "home.faq.a2": "Wi. Tout done yo chifre ak AES-256 nan repo a ak TLS 1.3 nan transpò a. Nou respekte nòm bankè entènasyonal yo ak règleman RGPD/DSP2 yo.",
    "home.faq.q3": "Ki tip envestisman ou pwopoze?",
    "home.faq.a3": "Nou pwopoze 6 kategori: Imobilye, Crowdfunding, Enèji renouvlab, Bous, Patisipatif ak Crypto. Chak pwodwi seleksyone pou rapò risk/randman li.",
    "home.faq.q4": "Kijan pou mande yon kredi?",
    "home.faq.a4": "Ale nan seksyon Kredi nan espas kliyan ou, chwazi tip prè a, antre montan an ak dire a. Dosye ou trete nan 24h pa konseye nou yo.",
    "home.faq.q5": "Èske gen frè kache?",
    "home.faq.a5": "Pa gen okenn frè kache. Tout tarif yo afiche klèman pandan chak operasyon. Kondisyon yo aksesib nan espas kliyan ou nenpòt lè.",
    "home.faq.q6": "Kijan pou kontakte sipò?",
    "home.faq.a6": "Itilize mesajri ki entegre nan espas kliyan ou pou kontakte dirèkteman konseye bankè pèsonèl ou, disponib 24h/24 ak 7 jou sou 7.",
    "home.cta.title": "Pare pou kòmanse?",
    "home.cta.sub": "Vin jwenn dè milye itilizatè ki jere finans yo ak {bank}. Enskripsyon an pran mwens pase 2 minit.",
    "home.footer.products": "Pwodwi",
    "home.footer.services": "Sèvis",
    "home.footer.access": "Aksè rapid",
    "home.footer.accounts": "Kont",
    "home.footer.transfers": "Virman",
    "home.footer.cards": "Kat",
    "home.footer.savings": "Ekonomi",
    "home.footer.investments": "Envestisman",
    "home.footer.credits": "Kredi",
    "home.footer.financing": "Finansman",
    "home.footer.messages": "Mesaj",
    "home.footer.login": "Koneksyon",
    "home.footer.signup": "Ouvri yon kont",
    "home.footer.support": "Sipò 24/7",
    "home.footer.tagline": "Bank an liy ou, disponib toupatou epi nenpòt lè.",
    "home.footer.copyright": "© {year} {bank} — Tout dwa rezève",
    "home.footer.powered": "Pwopoulse pa {bank}",
    "app.menu_label": "Meni prensipal",
    "app.nav.dashboard": "Tablodbò",
    "app.nav.accounts": "Kont",
    "app.nav.transactions": "Tranzaksyon",
    "app.nav.transfers": "Virman",
    "app.nav.cards": "Kat",
    "app.nav.savings": "Ekonomi",
    "app.nav.investments": "Envestisman",
    "app.nav.credit": "Kredi",
    "app.nav.financing": "Finansman",
    "app.nav.messages": "Mesaj",
    "app.nav.card_tracking": "Swiv kat",
    "app.nav.notifications": "Notifikasyon",
    "app.nav.settings": "Paramèt",
    "app.nav.profile": "Pwofil",
    "app.nav.mobile.home": "Akèy",
    "app.nav.mobile.transfers": "Virman",
    "app.nav.mobile.cards": "Kat",
    "app.nav.mobile.transactions": "Tranzaksyon",
    "app.nav.more.invest": "Envesti",
    "app.user_role": "Kliyan",
    "app.logout": "Dekoneksyon",
    "app.search": "Chèche…",
    "app.more_title": "Plis fonksyonalite",
    "app.more_btn": "Plis",
    "app.loading": "Chajman…",
    "app.error_back": "Retounen sou koneksyon",
    "app.see_more_notifs": "Wè {count} lòt notifikasyon yo →",
    "page.accounts": "Kont mwen yo",
    "page.transactions": "Tranzaksyon",
    "page.transfers": "Nouvo virman",
    "page.deposit": "Depo",
    "page.cards": "Kat mwen yo",
    "page.savings": "Ekonomi",
    "page.investments": "Envestisman",
    "page.credits": "Kredi",
    "page.financing": "Finansman pwojè",
    "page.messages": "Mesaj",
    "page.settings": "Paramèt",
    "page.notifications": "Notifikasyon enpòtan",
    "page.profile": "Pwofil mwen",
    "dash.greeting": "Bonjou, {name}",
    "dash.overview": "Apèsi sou finans ou — {date}",
    "dash.total_balance": "Balans total",
    "dash.this_month": "+{amount} mwa sa a",
    "dash.view_iban": "Wè IBAN mwen",
    "dash.my_accounts": "Kont mwen yo",
    "dash.primary_account": "Kont prensipal",
    "dash.savings_account": "Kont ekonomi",
    "dash.remunerated": "Remunere",
    "dash.details": "Detay",
    "dash.transfer_btn": "Virman",
    "dash.quick_actions": "Aksyon rapid",
    "dash.quick.deposit": "Depo",
    "deposit.title": "Enfòmasyon depo",
    "dash.quick.transfer": "Virman",
    "dash.quick.history": "Istorik",
    "dash.quick.cards": "Kat mwen yo",
    "dash.quick.profile": "Pwofil",
    "dash.monthly_budget": "Bidjè mansyèl",
    "dash.income": "Antre",
    "dash.expenses": "Depans",
    "dash.spending_cat": "Depans pa kategori",
    "dash.no_spending": "Pa gen depans anrejistre.",
    "dash.recent_tx": "Tranzaksyon resan",
    "dash.see_all": "Wè tout",
    "dash.no_tx": "Pa gen operasyon.",
    "dash.main_card": "Kat prensipal mwen",
    "dash.manage": "Jere",
    "dash.block": "Bloke",
    "dash.pin": "Kòd PIN",
    "dash.security": "Sekirite",
    "dash.more": "Plis",
    "dash.no_card": "Pa gen kat emèt",
    "dash.monthly_activity": "Aktivite mwa a",
    "dash.operations": "Operasyon",
    "dash.holder": "Tititè",
    "dash.expires": "Eksp.",
    "dash.visa_label": "VISA",
    "dash.balance": "Balans disponib",
    "tx.all": "Tout",
    "tx.income": "Antre",
    "tx.expenses": "Soti",
    "tx.today": "Jodi a",
    "tx.yesterday": "Yè",
    "tx.none": "Pa gen tranzaksyon.",
    "transfer.title": "Virman rapid",
    "transfer.recipient": "Destinatè",
    "transfer.recipient_ph": "Non, IBAN, nimewo telefòn…",
    "transfer.amount": "Montan",
    "transfer.label": "Libele",
    "transfer.reason": "Motif (opsyonèl)",
    "transfer.reason_ph": "Eg.: lwaye, ranbousman…",
    "transfer.execution": "Egzekisyon",
    "transfer.immediate": "Imedya",
    "transfer.delayed": "Difere",
    "transfer.balance": "Balans disponib:",
    "transfer.continue": "Kontinye",
    "transfer.confirm": "Konfime",
    "transfer.cancel": "Anile",
    "transfer.code": "Kòd validasyon",
    "transfer.code_ph": "● ● ● ● ● ●",
    "transfer.code_hint": "Antre kòd ou resevwa a pou valide virman an",
    "transfer.pending.title": "Virman annatant validasyon",
    "transfer.pending.amount": "Montan:",
    "transfer.pending.to": "Vè:",
    "transfer.pending.hint": "Kòd validasyon ou a ap voye pa imel oswa atravè klòch notifikasyon. Kòd la gen 6 chif. Si ou pa resevwa li, tanpri kontakte sipò.",
    "transfer.pending.validate": "Valide virman an",
    "transfer.saved": "Virman anrejistre. Mande kòd la bay administratè ou pou valide.",
    "transfer.done": "Virman fèt",
    "transfer.validated": "Virman valide!",
    "transfer.progress.processing": "Y ap trete virman an…",
    "transfer.progress.completed": "Virman fèt!",
    "transfer.progress.default_message": "Y ap trete virman ou a. Tanpri tann.",
    "cards.none": "Pa gen kat emèt.\nKontakte administratè ou.",
    "cards.holder": "Tititè",
    "cards.expires": "Eksp.",
    "cards.block": "Bloke",
    "cards.pin": "Kòd PIN",
    "cards.more": "Plis",
    "cards.limits": "Limit kat la",
    "cards.payment": "Peman",
    "cards.withdrawal": "Retrè",
        "cards.daily": "Daily",
        "cards.weekly": "Weekly",
        "cards.monthly": "Monthly",
    "cards.blocked_label": "Bloke",
    "cards.status_active": "Aktif",
    "cards.toast.blocked": "Kat bloke",
    "cards.toast.unblocked": "Kat debloke",
    "cards.toast.physical_ordered": "Kat fizik kòmande! Livrezon nan 5–10 jou travay.",
    "cards.toast.created": "Kat jenere avèk siksè!",
    "cards.none_sub": "Pa gen kat ki emèt. Jenere kat vityèl ou kounye a.",
    "cards.generate_btn": "Jenere kat mwen",
    "cards.create.title": "Jenere kat vityèl mwen",
    "cards.create.network": "Rezo peman",
    "cards.create.color": "Koulè kat la",
    "cards.create.preview_name": "NON OU",
    "cards.create.preview_expiry": "MM/AA",
    "cards.create.loading": "Jenere…",
    "cards.create.submit": "Jenere kat",
    "cards.btn.unblock": "Debloke",
    "cards.btn.block": "Bloke",
    "cards.btn.info": "Info",
    "cards.btn.physical": "Fizik",
    "cards.btn.physical_done": "Kòmande",
    "cards.limits_title": "Limit",
    "cards.reveal.title": "Enfòmasyon kat la",
    "cards.reveal.number": "Nimewo konplè",
    "cards.reveal.cvv": "CVV",
    "cards.reveal.expiry": "Ekspire",
    "cards.reveal.warning": "Piga janm pataje enfòmasyon sa yo. Fèmen fenèt sa a apre itilizasyon.",
    "cards.pin.title": "Kòd PIN",
    "cards.pin.message": "Kòd PIN sa a itilize pou tout peman anliy ou ak retrè ou yo. Piga janm pataje l ak pèsòn.",
    "cards.physical.title": "Kòmande kat fizik",
    "cards.physical.fee_label": "Frè livrezon",
    "cards.physical.balance_label": "Balans ou",
    "cards.physical.insufficient": "Balans ensifizan. Tanpri rechaje kont ou anvan ou kòmande.",
    "cards.physical.info": "Frè a pral debite nan kont ou. Livrezon nan 5–10 jou travay.",
    "cards.physical.order_btn": "Kòmande ({fee})",
    "cards.physical.ordering": "Kòmande…",
    "cards.physical.order_cta": "Kòmande yon kat fizik",
    "cards.physical.choose_type": "Chwazi kat ou",
    "cards.physical.features": "Avantaj",
    "cards.buy_virtual_cta": "Achte yon kat vityèl",
    "notif.none": "Pa gen notifikasyon kounye a.",
    "notif.contact_support": "Kontakte sèvis sipò",
    "profile.change_photo": "Chanje foto",
    "profile.remove_photo": "Retire foto",
    "profile.email": "Imèl",
    "profile.phone": "Telefòn",
    "profile.country": "Peyi",
    "profile.address": "Adrès",
    "profile.notifs_title": "Notifikasyon ({count})",
    "profile.no_notifs": "Pa gen notifikasyon.",
    "profile.logout": "Dekoneksyon",
    "profile.photo_updated": "Foto pwofil mete ajou",
    "profile.photo_removed": "Foto retire",
    "profile.photo_too_large": "Imaj twò gwo (maksimòm 1 Mo)",
    "profile.powered": "Pwopoulse pa {bank}",
    "iban.title": "IBAN mwen",
    "iban.loading": "Chajman…",
    "iban.bic": "Kòd BIC / SWIFT",
    "iban.share_info": "Pataje kowòdone sa yo pou resevwa yon virman sou kont ou.",
    "iban.copy": "Kopye",
    "iban.copied": "Kopye nan presspapye",
    "iban.copy_fail": "Pa kapab kopye",
    "iban.close": "Fèmen",
    "accounts.all": "Tout kont ou yo ouvri kay {bank}.",
    "accounts.primary": "Kont prensipal",
    "accounts.savings": "Kont ekonomi",
    "accounts.primary_tag": "Prensipal",
    "accounts.secondary_tag": "Segondè",
    "accounts.objective": "Objektif: {amount} · {pct}%",
    "accounts.copy": "Kopye",
    "accounts.loading": "Chajman…",
    "accounts.iban_copied": "IBAN kopye",
    "accounts.copy_fail": "Pa kapab kopye",
    "savings.total": "Total ekonomize",
    "savings.target_total": "Objektif total",
    "savings.progress": "Pwogrè",
    "savings.active": "Objektif aktif",
    "savings.new_goal": "Nouvo objektif",
    "savings.none": "Pa gen objektif ekonomi pou kounye a.",
    "savings.none_sub": "Kreye premye objektif ou pou kòmanse.",
    "savings.saved": "Ekonomize:",
    "savings.goal": "Objektif:",
    "savings.deadline": "Dat limit: {date}",
    "savings.deposit": "Depoze",
    "savings.withdraw": "Retire",
    "savings.loading": "Chajman…",
    "savings.form.title": "Nouvo objektif ekonomi",
    "savings.form.cancel": "Anile",
    "savings.form.create": "Kreye",
    "savings.form.name": "Non objektif la",
    "savings.form.name_ph": "Vwayaj Japon, Machin, …",
    "savings.form.target": "Montan vize",
    "savings.form.initial": "Depo inisyal",
    "savings.form.deadline": "Dat limit (opsyonèl)",
    "savings.form.color": "Koulè",
    "savings.deposit_title": "Depoze sou \"{name}\"",
    "savings.deposit_amount": "Montan pou depoze",
    "savings.balance_hint": "Balans prensipal: {amount}",
    "savings.withdraw_prompt": "Konbyen pou retire sou \"{name}\"?",
    "savings.close_confirm": "Fèmen objektif \"{name}\"? Ekonomi an ap retounen sou kont prensipal ou.",
    "savings.toast.created": "Objektif kreye",
    "savings.toast.deposited": "Depo fèt",
    "savings.toast.withdrawn": "Retrè fèt",
    "savings.toast.closed": "Objektif fèmen",
    "invest.total": "Total envesti",
    "invest.profits": "Benefis kimile",
    "invest.count": "Envestisman",
    "invest.products": "Pwodwi disponib",
    "invest.invest_btn": "Envesti",
    "invest.my": "Envestisman mwen yo",
    "invest.pending": "Annatant",
    "invest.validated": "Valide",
    "invest.refused": "Refize",
    "invest.withdraw.btn": "Retire",
    "invest.withdraw.title": "Retire envestisman mwen",
    "invest.withdraw.intro": "Ou pral retire envestisman ou \"{product}\". Kapital orijinal la ak benefis ki akimile yo ap antre nan balans prensipal ou.",
    "invest.withdraw.principal": "Kapital orijinal",
    "invest.withdraw.profit": "Benefis",
    "invest.withdraw.total": "Total pou resevwa",
    "invest.withdraw.confirm": "Konfime retrè a",
    "invest.withdraw.warning": "Aksyon sa a pa kapab anile.",
    "invest.withdraw.success": "Envestisman retire — {amount} antre nan balans ou.",
    "invest.withdrawn": "Retire",
    "invest.cta_sub": "Chwazi tip envestisman ki konvenab pou ou. Yon konseye ap akonpaye ou depi demann ou.",
    "invest.form.amount": "Montan pou envesti",
    "invest.form.balance": "Balans disponib: {amount}",
    "invest.form.note": "Nòt pou konseye ou (opsyonèl)",
    "invest.form.note_ph": "Presize sa ou atann…",
    "invest.form.advisor_info": "Yon konvèsasyon ap ouvri otomatikman ak konseye ou.",
    "invest.form.cancel": "Anile",
    "invest.form.submit": "Voye demann nan",
    "invest.toast.requested": "Demann anrejistre — yon konvèsasyon te ouvri ak konseye ou.",
    "invest.p1.name": "Imobilye",
    "invest.p1.desc": "SCPI, papye-wòch, crowdfunding imobilye",
    "invest.p2.name": "Crowdfunding",
    "invest.p2.desc": "Finansman patisipatif pwojè seleksyone",
    "invest.p3.name": "Enèji renouvlab",
    "invest.p3.desc": "Solè, eolyen ak pwojè vèt",
    "invest.p4.name": "Bous",
    "invest.p4.desc": "Aksyon, ETF ak fon divèsifye",
    "invest.p5.name": "Patisipatif",
    "invest.p5.desc": "Prè pou PME ak antreprenè lokal",
    "invest.p6.name": "Crypto",
    "invest.p6.desc": "Bitcoin, Ethereum ak pòtfèy milti-monnen",
    "msg.online": "An liy",
    "msg.loading": "Chajman…",
    "msg.none": "Pa gen mesaj. Kòmanse konvèsasyon an ak konseye ou.",
    "msg.placeholder": "Ekri mesaj ou…",
    "msg.attach.image": "Imaj",
    "msg.attach.pdf": "PDF",
    "msg.attach.audio": "Odyo",
    "msg.attach.file": "Fichye / Dokiman",
    "msg.pdf_doc": "Dokiman PDF",
    "msg.file": "Fichye",
    "msg.too_large": "Fichye twò gwo (maksimòm {size}).",
    "msg.withdraw": "Retire",
    "credits.cta_sub": "Fè demann ou dirèkteman an liy — repons nan 24h.",
    "credits.learn_more": "Aprann plis",
    "credits.request": "Mande",
    "credits.history": "Demann mwen yo",
    "credits.status.pending": "Annatant",
    "credits.status.accepted": "Aksepte",
    "credits.status.refused": "Refize",
    "credits.chars": "Karakteristik",
    "credits.amount_range": "Montan: {min}€ – {max}€",
    "credits.rate_range": "To enterè: {min}% – {max}% pa ane",
    "credits.duration_range": "Dire: {min} a {max} mwa",
    "credits.deposit_min": "Depo inisyal: {amount}€ minimòm",
    "credits.garantie": "Garanti: {garantie}",
    "credits.eligibility": "Kondisyon elijiblite",
    "credits.elig.income": "Jistifye revni regilye epi stab",
    "credits.elig.deposit": "Fè yon depo inisyal {amount}€",
    "credits.elig.garantie": "Prezante garanti a: {garantie}",
    "credits.form.title": "Demann — {name}",
    "credits.form.cancel": "Anile",
    "credits.form.submit": "Voye demann nan",
    "credits.form.amount": "Montan vle (€)",
    "credits.form.duration": "Dire (mwa)",
    "credits.form.reason": "Motif (opsyonèl)",
    "credits.form.reason_ph": "Presize pwojè ou…",
    "credits.form.close": "Fèmen",
    "credits.toast.submitted": "Demann kredi voye — ou ap resevwa yon repons nan 24h.",
    "credits.p1.name": "Prè Pèsonèl",
    "credits.p1.desc": "Pou finanse bezwen chak jou oswa pwojè pèsonèl.",
    "credits.p2.name": "Prè Imobilye",
    "credits.p2.desc": "Pou finanse acha oswa konstriksyon yon byen imobilye.",
    "credits.p3.name": "Prè Oto / Moto",
    "credits.p3.desc": "Achte yon machin nan serenite.",
    "credits.p4.name": "Prè Etidyan",
    "credits.p4.desc": "Yon èd finansye pou etid siperyè.",
    "credits.p5.name": "Prè Pwofesyonèl",
    "credits.p5.desc": "Pou sipòte kwasans yon aktivite pwofesyonèl.",
    "credits.p6.name": "Prè Ekspres",
    "credits.p6.desc": "Yon solisyon rapid pou bezwen ijan.",
    "financing.reimbursable": "Finansman ranbousab",
    "financing.reimbursable_desc": "Yon sipò finansye ak kondisyon fiks pou lanse oswa devlope yon aktivite.",
    "financing.non_reimbursable": "Finansman ki pa ranbousab",
    "financing.non_reimbursable_desc": "Pou pwojè ak enpak sosyal, edikatif oswa imanitè (ONG, asosyasyon).",
    "financing.cta_sub": "Solisyon adapte pou konkretize ambisyon ou yo.",
    "financing.request": "Mande yon finansman",
    "financing.history": "Demann mwen yo",
    "financing.reimbursable_label": "Ranbousab",
    "financing.non_reimbursable_label": "Pa ranbousab",
    "financing.status.pending": "Annatant",
    "financing.status.granted": "Akòde",
    "financing.status.refused": "Refize",
    "financing.form.title": "Demann — {name}",
    "financing.form.cancel": "Anile",
    "financing.form.submit": "Voye demann nan",
    "financing.form.project": "Non pwojè a",
    "financing.form.project_ph": "Pwojè mwen",
    "financing.form.amount": "Montan mande (€)",
    "financing.form.sector": "Sektè",
    "financing.form.sector_ph": "Tech, agrikilti, …",
    "financing.form.description": "Deskripsyon pwojè a",
    "financing.form.description_ph": "Prezante pwojè ou, objektif li yo ak enpak li atann…",
    "settings.security": "Sekirite",
    "settings.current_pw": "Modpas aktyèl",
    "settings.new_pw": "Nouvo modpas",
    "settings.confirm_pw": "Konfime",
    "settings.submit": "Chanje modpas la",
    "settings.no_match": "Modpas yo pa koresponn",
    "settings.changed": "Modpas modifye",
    "common.cancel": "Anile",
    "common.confirm": "Konfime",
    "common.close": "Fèmen",
    "common.save": "Anrejistre",
    "common.loading": "Chajman…",
    "common.send": "Voye",
    "common.total": "Total",
    "admin.wallet.balance": "Available balance",
    "admin.wallet.history": "History",
    "admin.wallet.no_history": "No transactions",
    "admin.wallet.title": "My Wallet",
    "admin.wallet.topup": "Top up",
    "beneficiary.pick": "Select",
    "beneficiary.save_this": "Save this beneficiary",
    "beneficiary.saved": "Saved beneficiaries",
    "beneficiary.saved_ok": "Beneficiary saved",
    "card.color.amber": "Amber",
    "card.color.cyan": "Cyan",
    "card.color.emerald": "Emerald",
    "card.color.forest": "Forest",
    "card.color.gold": "Gold",
    "card.color.lavender": "Lavender",
    "card.color.night": "Night",
    "card.color.orange": "Orange",
    "card.color.pink": "Pink",
    "card.color.royal": "Royal Blue",
    "card.color.ruby": "Ruby",
    "card.color.slate": "Slate",
    "card.color.steel": "Steel",
    "card.color.terracotta": "Terracotta",
    "card.color.violet": "Violet",
    "common.other": "Other",
    "common.search": "Search",
    "content.translated": "Auto-translated content",
    "credits.monthly_payment": "Pèman mansyèl estimé",
    "credits.rate_annual": "/ ane",
    "credits.rate_used": "To estimé:",
    "credits.simulation": "Estimasyon pèman chak mwa",
    "credits.total_cost": "Koste total estimé",
    "demo.banner": "Demo mode — no real actions",
    "demo.disabled": "Action not available in demo mode",
    "deposit.default_instructions": "Contact your manager for deposit details.",
    "error.back_home": "Back to home",
    "error.bank_not_found": "Bank not found",
    "error.unknown": "Unknown error",
    "kyc.file_read_error": "Unable to read file",
    "kyc.file_too_large": "File too large (max 5 MB)",
    "login.history": "Login history",
    "login.history.empty": "No login records found.",
    "notif.dismiss": "Dismiss",
    "notif.locked": "Bank message",
    "tracking.error.not_found": "Tracking number not found. Please verify and try again.",
    "tracking.label": "Tracking",
    "tracking.status.delivered": "Delivered",
    "tracking.status.preparation": "In preparation",
    "tracking.status.shipped": "Shipped",
    "tracking.status.transit": "In transit",
    "transfer.code.copied": "Copied",
    "transfer.code.copy": "Copy",
    "transfer.code.received": "Your validation code",
    "dash.welcome": "Byenveni",
    "common.delete": "Efase",
    "cards.delete.title": "Efase kat la",
    "cards.delete.confirm_msg": "Aksyon sa a pa ka defèt. Kat la pral efase pou toujou.",
    "cards.delete.physical_tooltip": "Kat fizik kòmande — efasaj enposib",
    "cards.order.confirmed": "Kòmand konfime!",
    "cards.order.confirmed_body": "Kat fizik ou ap prepare. Sèvi ak nimewo sa a pou swiv livrezon ou.",
    "cards.order.tracking_no": "Nimewo swivi",
    "cards.fee_deducted": "{amount} pral dedwi nan balans ou",
    "cards.insufficient_short": "Balans ensifizan ({available} disponib)",
    "transfer.section.beneficiary": "Benefisyè",
    "transfer.section.details": "Transfere",
    "transfer.field.bic": "BIC / SWIFT",
    "transfer.field.bank": "Bank",
    "transfer.field.city": "Vil",
    "transfer.iban_label": "IBAN:",
    "transfer.bank_label": "Bank:",
    "transfer.progress.paused": "Ap tann validasyon",
    "cards.pin_label": "PIN",
    "home.invest.p.realestate": "Real estate",
    "home.invest.p.realestate_desc": "SCPI, real estate crowdfunding, property-paper",
    "home.invest.p.crowdfunding": "Crowdfunding",
    "home.invest.p.crowdfunding_desc": "Participative financing of selected projects",
    "home.invest.p.renewable": "Renewable energy",
    "home.invest.p.renewable_desc": "Solar, wind and green projects",
    "home.invest.p.stocks": "Stock market",
    "home.invest.p.stocks_desc": "Stocks, ETFs and diversified funds",
    "home.invest.p.participatory": "Peer-to-peer",
    "home.invest.p.participatory_desc": "Loans to SMEs and local entrepreneurs",
    "home.invest.p.crypto": "Crypto",
    "home.invest.p.crypto_desc": "Bitcoin, Ethereum and multi-currency wallet",
    "home.invest.p.variable": "Varyab",
    "home.credits.p.personal": "Personal Loan",
    "home.credits.p.realestate": "Mortgage Loan",
    "home.credits.p.auto": "Auto/Motorcycle Loan",
    "home.credits.p.student": "Student Loan",
    "home.credits.p.pro": "Business Loan",
    "home.credits.p.express": "Express Loan",
    "home.credits.rate.from": "from {rate}/year",

    "cards.deleted": "Kat efase",

    "cards.feat.gold_exclusive": "Kat lò eksklizif",

    "cards.feat.cashback_05": "Cashback 0,5%",

    "cards.feat.buyer_protection": "Pwoteksyon achtè",

    "cards.feat.support_standard": "Sipò estanda",

    "cards.feat.plastic_premium": "Kat plastik premium",

    "cards.feat.cashback_1": "Cashback 1%",

    "cards.feat.travel_standard": "Asirans vwayaj estanda",

    "cards.feat.support_priority": "Sipò priyoritè",

    "cards.feat.metal_brushed": "Kat metal bwose",

    "cards.feat.cashback_2_unlimited": "Cashback 2% san limit",

    "cards.feat.travel_premium": "Asirans vwayaj premium",

    "cards.feat.vip_lounge": "Aksè salon VIP",

    "cards.feat.support_247": "Asistans 24/7",

    "auth.demo.title": "Konekte administratè",

    "auth.demo.subtitle": "Antre kredansyèl admin pou wè demo a",
    "credits.garantie.none": "Okenn",
    "credits.garantie.mortgage": "Ipotèk sou pwopriyete ki finanse a",
    "credits.garantie.vehicle": "Veyikil ki finanse a",
    "credits.garantie.parental": "Parantal oswa bankè",
    "credits.garantie.professional": "Byen pwofesyonèl oswa garanti pèsonèl",
    "cards.reveal.pin_prompt": "Antre kòd PIN 4 chif ou a pou afiche enfòmasyon kat la.",
    "cards.reveal.show": "Afiche",
    "transfer.field.iban": "IBAN",
    "auth.pin.mismatch": "De de kòd PIN yo pa menm",
    "auth.pin.digit_hint": "4 chif",
  
  },

};

/* ─────────────────────────────────────────────────────────────────────────── */
/* useTranslation — returns t(key, vars?) with fr fallback                    */
/* ─────────────────────────────────────────────────────────────────────────── */
export function useTranslation(locale) {
  const lang = T[locale] || T.fr;
  const t = (key, vars = {}) => {
    let str = lang[key] ?? T.fr[key] ?? key;
    Object.entries(vars).forEach(([k, v]) => { str = str.replace(`{${k}}`, String(v)); });
    return str;
  };
  return { t };
}

export const TRANSLATIONS = T;
